import { useState, useEffect, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Search, Plus, Tag, Pause, ClipboardList, Receipt, Undo2, RefreshCcw,
  Banknote, User, SearchCheck, BarChart3, Gift, Scale, Coins, Trash2,
  ShoppingCart, Package, X, ArrowLeft, ArrowRight, ArrowLeftRight, Building, Cake,
  PartyPopper, Camera, Lightbulb, CreditCard, Smartphone, Star,
  Medal, Building2, ChevronLeft, ChevronRight, DollarSign, Wallet, Phone, Mail, IdCard,
  AlertTriangle, BookOpen, Store, CircleDot, MapPin, Calendar, Printer, Wrench, Users, Image, CheckCircle,
  FileText, Filter, RefreshCw, ChevronDown, Clock, Eye, Pencil, Box, ShoppingBag, Ribbon, MoreHorizontal, MoreVertical, Percent, TrendingUp, TrendingDown, Download, Send, Inbox, Info, Gem, ClipboardCheck, BadgeCheck, ScrollText, Check, BadgePercent, ExternalLink, Landmark, Globe
} from 'lucide-react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import Loading from '../components/Loading';
import logService, { LOG_MODULES, LOG_ACTIONS } from '../services/logService';
import { API_URL, API_HOST } from '../config/api';
import useEscapeClose from '../hooks/useEscapeClose';
import { handlePrint } from '../utils/printHelper';
import { normalizePhoneDigits, formatPhoneDisplay, isPhoneComplete } from '../utils/phoneFormat';
import { computeSaleItemPricing } from '../utils/saleItemPricing';
import { computeKasaOzeti, formatKasaSummaryValue, eTicaretGelirRows, eTicaretNetRows } from '../utils/kasaOzeti';
import { formatPaymentMethod, getItemsDiscountSum, getSaleDisplayAmount } from '../utils/saleDisplay';
import EndOfDayGenel from '../components/EndOfDayGenel';
import PriceInput, { numberToText } from '../components/PriceInput';
import './RetailSales.css';
import FislerMobileHome from './FislerMobileHome';
import ZoomablePhoto from '../components/ZoomablePhoto';

const IMAGE_HOST = API_HOST;

// Bu tarihten İTİBAREN oluşturulan satışların BİLGİ FİŞİ'nde kalem fiyatları, ekrandaki
// Fiş Detayı / Fiş Düzenleme / Değişim / İade ile AYNI kaynaktan (computeSaleItemPricing)
// çekilir. Daha eski fişler eski davranışla yazdırılır (geriye dönük hiçbir değişiklik olmaz).
const UNIFIED_RECEIPT_PRICING_CUTOFF = new Date('2026-09-09T00:00:00');

// Ürün resmi tam URL (ör. Shopify CDN) veya yerelde saklanan bir yol olabilir
const getImageUrl = (image) => {
  if (!image) return null;
  return image.startsWith('http') ? image : `${IMAGE_HOST}${image}`;
};

// Türkçe karakter uyumlu küçük harfe çevirme (ı/I ve i/İ dönüşümü için)
const turkishToLower = (str) => {
  if (!str) return '';
  return str.toLocaleLowerCase('tr-TR');
};

// Türkçe karakter uyumlu metin içerme kontrolü
const turkishIncludes = (text, search) => {
  return turkishToLower(text).includes(turkishToLower(search));
};

// Havale/EFT hesap adı gösterilirken (Şirket)/(Şahsi)/(E-Ticaret) etiketini gizle
// — hesap adı zaten türü belirtiyor (ör. "HAVALE ŞİRKET"), tekrar göstermeye gerek yok.
// Kayıtlı veri (raporlama/eşleşme için) değişmiyor, sadece ekrana yazdırılan metin temizleniyor.
const stripBankAccountType = (label) => (label || '').replace(/\s*\((Şirket|Şahsi|Şahıs|E-Ticaret)\)\s*$/i, '');

// Fiyat formatlama: 1.234.567,89 formatında (binlik nokta, kuruş virgül)
const formatPrice = (value) => {
  const num = Number(value) || 0;
  return num.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// mobileApp: iOS uygulamasının WebView'ı (/mobile/fisler, MobileFisler.js) — kasa ekranı çizilmez,
// yalnızca "Tüm Satış Fişleri" listesi telefona göre FislerMobileHome ile gösterilir; fiş detay/düzenleme
// modalları ve tüm hesaplar web ile ortaktır. Sepet gerektiren işlemler (iade, değişim) ve tarayıcı
// yazdırması telefonda gizlenir.
const RetailSales = ({ user, selectedStore, mobileApp = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const barcodeInputRef = useRef(null);
  const eskiEtiketInputRef = useRef(null);
  const searchDebounceRef = useRef(null);
  const prevTerminalCartsCountRef = useRef(-1); // Terminal sepeti bildirim sesi için (-1 = ilk yükleme)
  const creditOptionsRef = useRef(null); // Açık hesap seçenekleri için scroll ref

  // Ürün toplamını hesapla (items verisinden)
  const getItemsSum = (sale) => {
    if (!sale.items || sale.items.length === 0) return null;
    return sale.items.reduce((sum, item) => {
      const qty = Math.abs(Number(item.quantity) || 1);
      const price = Number(item.discounted_unit_price || item.unit_price) || 0;
      return sum + (price * qty);
    }, 0);
  };


  // Satışın gerçek ödenen tutarını hesapla (indirim uygulanmış)
  const getSalePaidAmount = (sale) => {
    const total = Number(sale.total) || 0;
    const subtotal = Number(sale.subtotal) || 0;
    const discount = Number(sale.discount) || 0;
    const scrapAmount = Number(sale.scrap_total_amount) || 0;
    const scrapDebt = Number(sale.scrap_debt_discount) || 0;
    // total == subtotal ve indirim varsa, indirim total'e uygulanmamış olabilir
    if (discount > 0 && subtotal > 0 && Math.abs(total - subtotal) < 1) {
      // İndirim ürün fiyatlarına dağıtılmış mı kontrol et (discount_amount toplamı sepet indirimine yakınsa)
      const itemsDiscSum = getItemsDiscountSum(sale);
      if (itemsDiscSum > 0 && Math.abs(itemsDiscSum - discount) < 1) {
        return total;
      }
      return total - discount - scrapAmount - scrapDebt;
    }
    return total;
  };


  // Mağaza metal türünü belirle (store_id 1 = altın, diğerleri = gümüş)
  const currentStoreId = user?.role === 'admin' ? (selectedStore?.id || user?.store_id) : user?.store_id;
  const isGoldStore = currentStoreId === 1;
  const storeMetalType = isGoldStore ? 'gold' : 'silver';

  const [cart, setCart] = useState([]);
  const [selectedPreviewItem, setSelectedPreviewItem] = useState(null); // Resim önizleme için seçili ürün
  const [showProductDetailModal, setShowProductDetailModal] = useState(false); // Ürün detay modalı
  const [detailStoreStocks, setDetailStoreStocks] = useState([]); // Ürün detay modalı mağaza stokları
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [eskiEtiketQuery, setEskiEtiketQuery] = useState('');
  const [receiptNote, setReceiptNote] = useState('');
  const [showReceiptNoteInput, setShowReceiptNoteInput] = useState(false);

  // Döviz/Altın fiyatları
  const [prices, setPrices] = useState(null);
  const [priceAnimations, setPriceAnimations] = useState({}); // Fiyat değişim animasyonları
  const [scraperWarning, setScraperWarning] = useState(null); // Scraper durumu uyarısı
  const priceWarningShown = useRef({}); // Fiyat uyarısı spam engelleyici
  const [showRatesMenu, setShowRatesMenu] = useState(false);
  const [visibleRates, setVisibleRates] = useState(() => {
    const saved = localStorage.getItem('visibleRates');
    return saved ? JSON.parse(saved) : {
      gold: true, silver: true, usd: true, eur: true, gbp: true, silverUsd: true
    };
  });

  const [eskiEtiketScanEnabled, setEskiEtiketScanEnabled] = useState(() => {
    const saved = localStorage.getItem('eskiEtiketScanEnabled');
    return saved !== null ? saved === 'true' : false;
  });

  // Favori ürünler
  const [showFavoritesModal, setShowFavoritesModal] = useState(false);
  const [favoriteProducts, setFavoriteProducts] = useState([]);
  const [loadingFavorites, setLoadingFavorites] = useState(false);

  // Mağaza seçimi (admin için)
  const [showStoreSelectModal, setShowStoreSelectModal] = useState(false);
  const [selectedStoreId, setSelectedStoreId] = useState(null);
  const [pendingPaymentMethod, setPendingPaymentMethod] = useState(null);
  const [pendingDebtPayments, setPendingDebtPayments] = useState([]);

  // Müşteri state'leri
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [showCustomerSearch, setShowCustomerSearch] = useState(false);
  const [customerSearchQuery, setCustomerSearchQuery] = useState('');
  const [customerSearchResults, setCustomerSearchResults] = useState([]);
  const [newCustomer, setNewCustomer] = useState({ name: '', phone: '', email: '', address: '', tax_number: '', birthday: '', sms_consent: false, kvkk_consent: false });

  // Kayıtsız devam et: görünür buton yok, müşteri ekranı görse fark etmesin diye
  // "Müşteri Ara" başlığına ~1.2sn basılı tutunca tetiklenen gizli kısayol.
  const anonymousHoldTimer = useRef(null);
  const startAnonymousHold = () => {
    if (anonymousHoldTimer.current) return;
    anonymousHoldTimer.current = setTimeout(() => {
      anonymousHoldTimer.current = null;
      setSelectedCustomer({ id: null, name: 'Kayıtsız Müşteri', phone: '-', isAnonymous: true });
      setShowCustomerSearch(false);
      setCustomerSearchQuery('');
      toast.info('Kayıtsız müşteri olarak devam ediliyor');
    }, 1200);
  };
  const cancelAnonymousHold = () => {
    if (anonymousHoldTimer.current) {
      clearTimeout(anonymousHoldTimer.current);
      anonymousHoldTimer.current = null;
    }
  };
  const [showKvkkModal, setShowKvkkModal] = useState(false);
  const [phoneMatchCustomers, setPhoneMatchCustomers] = useState([]); // Telefon ile eşleşen müşteriler

  // Diğer modaller
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [discountType, setDiscountType] = useState('percent');
  const [appliedDiscounts, setAppliedDiscounts] = useState([]); // [{id, type, value, label}]

  // Mağaza bazlı maksimum indirim limiti
  const [maxDiscountLimit, setMaxDiscountLimit] = useState(null);

  // İndirim onay sistemi
  const [discountApprovalId, setDiscountApprovalId] = useState(null);
  const [discountApprovalStatus, setDiscountApprovalStatus] = useState(null); // 'pending', 'approved', 'rejected'
  const [approvedDiscountOverride, setApprovedDiscountOverride] = useState(null); // null veya onaylanan yüzde (ör: 25)
  const [isRequestingApproval, setIsRequestingApproval] = useState(false);
  const [pendingDiscountRequest, setPendingDiscountRequest] = useState(null); // { type, value }

  // Ürün bazlı indirim modalı
  const [showItemDiscountModal, setShowItemDiscountModal] = useState(false);
  const [selectedItemForDiscount, setSelectedItemForDiscount] = useState(null);
  const [itemDiscountAmount, setItemDiscountAmount] = useState(0);
  const [itemDiscountType, setItemDiscountType] = useState('percent');

  // Hızlı fiyat değiştirme modalı (toplama çift tıklama)
  const [showQuickPriceModal, setShowQuickPriceModal] = useState(false);
  const [quickTargetPrice, setQuickTargetPrice] = useState('');

  // Fark (Adjustment) modalı
  const [showAdjustmentModal, setShowAdjustmentModal] = useState(false);
  const [adjustmentItems, setAdjustmentItems] = useState([]);
  const [adjustmentForm, setAdjustmentForm] = useState({
    amount: '',
    reason: '',
    description: ''
  });
  const [editingAdjustmentIndex, setEditingAdjustmentIndex] = useState(null);

  // Ürün notu modalı
  const [showItemNoteModal, setShowItemNoteModal] = useState(false);
  const [selectedItemForNote, setSelectedItemForNote] = useState(null);
  const [itemNoteText, setItemNoteText] = useState('');

  // Kişiye özel ürün modalı
  const [showCustomProductModal, setShowCustomProductModal] = useState(false);
  const [customProductPrice, setCustomProductPrice] = useState('');
  const [customProductNote, setCustomProductNote] = useState('');

  // Özel tanımlı ürün gramaj giriş modalı
  const [showSpecialWeightModal, setShowSpecialWeightModal] = useState(false);
  const [specialWeightProduct, setSpecialWeightProduct] = useState(null);
  const [specialWeightInput, setSpecialWeightInput] = useState('');

  // Ödeme seçenekleri modal
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showPaymentCreditOptions, setShowPaymentCreditOptions] = useState(false);

  const [showHoldModal, setShowHoldModal] = useState(false);
  const [heldSales, setHeldSales] = useState([]);
  const [terminalCarts, setTerminalCarts] = useState([]);

  // İade ve Son Satış için
  const [preRefundCustomer, setPreRefundCustomer] = useState(null); // İade/değişim öncesi müşteri
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [showLastSaleModal, setShowLastSaleModal] = useState(false);
  const [salesModalMode, setSalesModalMode] = useState('view'); // 'view', 'refund', 'exchange'
  const [recentSales, setRecentSales] = useState([]);
  const [recentSalesSearch, setRecentSalesSearch] = useState('');
  const [recentSalesFilters, setRecentSalesFilters] = useState({
    startDate: '',
    endDate: '',
    paymentMethod: '',
    minAmount: '',
    maxAmount: '',
    exactAmount: '',
    receiptType: ''
  });
  const [showSalesFilters, setShowSalesFilters] = useState(false);
  const [recentSalesOffset, setRecentSalesOffset] = useState(0);
  const [hasMoreRecentSales, setHasMoreRecentSales] = useState(true);
  const [loadingMoreSales, setLoadingMoreSales] = useState(false);
  const recentSalesListRef = useRef(null);
  const recentSalesSentinelRef = useRef(null);
  const RECENT_SALES_PAGE_SIZE = 50;
  const [selectedSale, setSelectedSale] = useState(null);
  const [refundItems, setRefundItems] = useState([]);
  const [refundPaymentMethod, setRefundPaymentMethod] = useState('Nakit İade');
  const [refundCustomerDebts, setRefundCustomerDebts] = useState([]); // Müşterinin borçları (iade için)
  const [refundOriginalPayments, setRefundOriginalPayments] = useState([]); // Orijinal ödeme detayları
  const [refundAmounts, setRefundAmounts] = useState({}); // Her ödeme yöntemi için iade tutarları
  const [previousRefunds, setPreviousRefunds] = useState([]); // Bu satışa ait önceki iadeler
  const [refundAdjustments, setRefundAdjustments] = useState([]); // Fark kalemleri için iade
  const [useCustomRefundPayments, setUseCustomRefundPayments] = useState(false); // Farklı ödeme yöntemi kullan
  const [customRefundPayments, setCustomRefundPayments] = useState({ nakit: '', krediKarti: '', havale: '', hediyeKarti: '', hurda: '' }); // Özel iade ödeme tutarları
  const [showCrossStoreSearch, setShowCrossStoreSearch] = useState(false);
  const [crossStoreReceiptId, setCrossStoreReceiptId] = useState('');
  const [crossStoreSale, setCrossStoreSale] = useState(null);
  const [crossStoreLoading, setCrossStoreLoading] = useState(false);
  const [crossStoreError, setCrossStoreError] = useState('');

  // Tüm Fişler Modal
  const [showAllSalesModal, setShowAllSalesModal] = useState(mobileApp); // mobileApp: liste her zaman açık
  const [allSales, setAllSales] = useState([]);
  const [allSalesFilter, setAllSalesFilter] = useState('all'); // 'all', '1', '2', '3' (store ids)
  const [allSalesLoading, setAllSalesLoading] = useState(false);
  const [allSalesOffset, setAllSalesOffset] = useState(0);
  const [hasMoreAllSales, setHasMoreAllSales] = useState(true);
  const [loadingMoreAllSales, setLoadingMoreAllSales] = useState(false);
  const [allSalesTotalCounts, setAllSalesTotalCounts] = useState({ all: 0, store1: 0, store2: 0, store3: 0 });
  const [allSalesSearch, setAllSalesSearch] = useState('');
  const [allSalesDateFilter, setAllSalesDateFilter] = useState('all');
  const [allSalesTypeFilter, setAllSalesTypeFilter] = useState('all');
  const allSalesSearchTimeout = useRef(null);
  const ALL_SALES_PAGE_SIZE = 50;

  // Değişim modu tespiti - sepette iade ürünü varsa değişim modundayız
  const isExchangeMode = cart.some(item => item.isRefund);

  // Değişim için
  const [showExchangeModal, setShowExchangeModal] = useState(false);
  const [exchangeItems, setExchangeItems] = useState([]);
  const [exchangeAdjustments, setExchangeAdjustments] = useState([]);
  const [exchangeSaleInfo, setExchangeSaleInfo] = useState(null);

  // Değişim Fişi için
  const [showExchangeReceiptModal, setShowExchangeReceiptModal] = useState(false);
  const [exchangeReceiptData, setExchangeReceiptData] = useState(null);

  // Satış sonrası fiş seçim modalı
  const [showReceiptChoiceModal, setShowReceiptChoiceModal] = useState(false);
  const [receiptChoiceData, setReceiptChoiceData] = useState(null);

  // İade/Değişim Detay Modal
  const [showRefundDetailModal, setShowRefundDetailModal] = useState(false);
  const [selectedRefundDetail, setSelectedRefundDetail] = useState(null);

  // Borç Tahsilat Detay Modal
  const [showDebtPaymentDetailModal, setShowDebtPaymentDetailModal] = useState(false);
  const [selectedDebtPaymentDetail, setSelectedDebtPaymentDetail] = useState(null);
  const [debtPaymentActionsOpen, setDebtPaymentActionsOpen] = useState(false);

  // Borç Tahsilat Düzenleme Modal
  const [showEditDebtPaymentModal, setShowEditDebtPaymentModal] = useState(false);
  const [editingDebtPayment, setEditingDebtPayment] = useState(null);
  const [debtPaymentEditData, setDebtPaymentEditData] = useState({
    amount: '',
    payment_method: 'Nakit',
    notes: '',
    created_at: ''
  });

  // Hurda Alımı Detay Modal
  const [showScrapPurchaseDetailModal, setShowScrapPurchaseDetailModal] = useState(false);
  const [selectedScrapPurchaseDetail, setSelectedScrapPurchaseDetail] = useState(null);

  // Hurda Alımı Düzenleme Modal
  const [showEditScrapPurchaseModal, setShowEditScrapPurchaseModal] = useState(false);
  const [editingScrapPurchase, setEditingScrapPurchase] = useState(null);
  const [scrapPurchaseEditData, setScrapPurchaseEditData] = useState({
    metal_type: 'gold',
    purity: '22',
    gross_weight: '',
    waste_percent: '0',
    pure_weight: '',
    unit_price: '',
    total: '',
    payment_method: 'Nakit',
    notes: '',
    created_at: ''
  });

  // Masraf Detay Modal
  const [showExpenseDetailModal, setShowExpenseDetailModal] = useState(false);
  const [selectedExpenseDetail, setSelectedExpenseDetail] = useState(null);

  // Değişim/İade düzenleme için (Admin)
  const [showExchangeEditModal, setShowExchangeEditModal] = useState(false);
  const [editingExchange, setEditingExchange] = useState(null);
  const [exchangeEditData, setExchangeEditData] = useState({
    customer_id: null,
    customer_name: '',
    staff_ids: [],
    staff_names: [],
    payment_method: '',
    payment_details: [],
    created_at: '',
    notes: ''
  });
  const [editingReturnItems, setEditingReturnItems] = useState([]);
  const [editingNewItems, setEditingNewItems] = useState([]);
  const [savingExchangeEdit, setSavingExchangeEdit] = useState(false);

  // Satış düzenleme için
  const [showEditSaleModal, setShowEditSaleModal] = useState(false);
  const [editingSale, setEditingSale] = useState(null);
  const [editingItems, setEditingItems] = useState([]);
  const [refundEditItems, setRefundEditItems] = useState([]); // İade düzenleme: orijinal satış ürünleri + checkbox
  const [editingAdjustments, setEditingAdjustments] = useState([]); // Fark kalemleri düzenleme
  const [editDiscount, setEditDiscount] = useState(0); // Sepet indirimi düzenleme
  const [savingEdit, setSavingEdit] = useState(false);
  const [editModalKey, setEditModalKey] = useState(0); // Modal yeniden mount için key

  // Admin kapsamlı düzenleme için
  const [showAdminEditModal, setShowAdminEditModal] = useState(false);
  const [adminEditSelectedStaff, setAdminEditSelectedStaff] = useState([]);
  const [adminEditData, setAdminEditData] = useState({
    customer_id: null,
    customer_name: '',
    staff_id: null,
    staff_name: '',
    payment_method: '',
    payment_details: [],
    created_at: '',
    notes: ''
  });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingSale, setDeletingSale] = useState(null);
  // Borç güncelleme modalı (GRAM/GUMUS borçlar için)
  const [showDebtUpdateModal, setShowDebtUpdateModal] = useState(false);
  const [debtUpdateData, setDebtUpdateData] = useState({ amount: '', amount_tl: '', gold_rate: '', debt_type: '' });
  const [pendingAdminSave, setPendingAdminSave] = useState(null); // Borç onayı bekleyen kaydetme

  // Genel Onay Modal (window.confirm yerine)
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmModalConfig, setConfirmModalConfig] = useState({
    title: 'Onay',
    message: '',
    confirmText: 'Evet',
    cancelText: 'İptal',
    onConfirm: null,
    type: 'warning' // 'warning', 'danger', 'info'
  });

  const [adminCustomerSearch, setAdminCustomerSearch] = useState('');
  const [adminCustomerResults, setAdminCustomerResults] = useState([]);
  const [showAdminCustomerDropdown, setShowAdminCustomerDropdown] = useState(false);
  const [adminProductSearch, setAdminProductSearch] = useState('');
  const [adminProductResults, setAdminProductResults] = useState([]);
  const [showAdminProductDropdown, setShowAdminProductDropdown] = useState(false);

  // Sepet fiyat düzenleme için
  const [editingPriceItemId, setEditingPriceItemId] = useState(null);
  const [editingPriceValue, setEditingPriceValue] = useState('');
  const [editingQtyItemId, setEditingQtyItemId] = useState(null);
  const [editingQtyValue, setEditingQtyValue] = useState('');

  // Satış sorumlusu
  const [showStaffModal, setShowStaffModal] = useState(false);
  const [salesStaffList, setSalesStaffList] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState([]); // Çoklu seçim için array

  // Gün Sonu Raporu
  const [showEndOfDayModal, setShowEndOfDayModal] = useState(false);
  const [endOfDayReport, setEndOfDayReport] = useState(null);
  const [endOfDayLoading, setEndOfDayLoading] = useState(false);
  const [sendingDailyReport, setSendingDailyReport] = useState(false);
  const [endOfDayTab, setEndOfDayTab] = useState('genel');
  const [expandedCashiers, setExpandedCashiers] = useState({});
  const [expandedSales, setExpandedSales] = useState({});
  const [showPrintMenu, setShowPrintMenu] = useState(false);
  const [showPdfMenu, setShowPdfMenu] = useState(false);
  const [expandedPaymentType, setExpandedPaymentType] = useState(null);
  const [expandedExpenseItem, setExpandedExpenseItem] = useState(null);
  const [expandedControlItem, setExpandedControlItem] = useState(null);
  const [eodDate, setEodDate] = useState(new Date());
  const [eodCashierFilter, setEodCashierFilter] = useState([]);

  // Nakit Ödeme Modal
  const [showCashModal, setShowCashModal] = useState(false);

  // Nakit İade Onay Modal
  const [showCashRefundConfirmModal, setShowCashRefundConfirmModal] = useState(false);
  // Havale İade Onay Modal
  const [showTransferRefundConfirmModal, setShowTransferRefundConfirmModal] = useState(false);
  // Hurda Borca Al Seçenek Modal (Gram/Nakit)
  const [showScrapDebtTypeModal, setShowScrapDebtTypeModal] = useState(false);
  const [receivedAmount, setReceivedAmount] = useState('');
  const [selectedCurrency, setSelectedCurrency] = useState('TRY');
  const [partialPayments, setPartialPayments] = useState([]); // Kısmi ödemeler listesi
  const [showCreditOptions, setShowCreditOptions] = useState(false); // Açık hesap seçenekleri
  const [pendingHavalePayment, setPendingHavalePayment] = useState(null); // Karışık ödemede havale için bekleyen ödeme
  const [pendingCollectionHavalePayment, setPendingCollectionHavalePayment] = useState(null); // Tahsilat havale için bekleyen ödeme
  const [selectedCollectionBankAccount, setSelectedCollectionBankAccount] = useState(null); // Tahsilat için seçilen banka hesabı

  // Nakit Basit Ödeme Modal (para üstü hesaplama)
  const [showNakitModal, setShowNakitModal] = useState(false);
  const [nakitReceivedAmount, setNakitReceivedAmount] = useState('');

  // Kredi Kartı Ödeme Modal
  const [showCardModal, setShowCardModal] = useState(false);

  // Sanal Pos (iyzico) Ödeme Modal
  const [showVirtualPosModal, setShowVirtualPosModal] = useState(false);

  // Satış Detay Modal
  const [showSaleDetailModal, setShowSaleDetailModal] = useState(false);
  const [selectedSaleDetail, setSelectedSaleDetail] = useState(null);
  const [saleExchangeInfo, setSaleExchangeInfo] = useState(null); // Bu satış bir değişimin parçasıysa bilgisi
  const [saleDetailActionsOpen, setSaleDetailActionsOpen] = useState(false);
  const [saleDeleteAllowed, setSaleDeleteAllowed] = useState(false);

  // Kampanya sistemi
  const [activeCampaigns, setActiveCampaigns] = useState([]);
  const [appliedCampaign, setAppliedCampaign] = useState(null);
  const [campaignDiscount, setCampaignDiscount] = useState(0);
  const [campaignSuggestions, setCampaignSuggestions] = useState([]);
  const [showCampaignInfo, setShowCampaignInfo] = useState(false);
  const [showCampaignsModal, setShowCampaignsModal] = useState(false);

  // Ürün Resmi Popup
  const [showImagePopup, setShowImagePopup] = useState(false);
  const [popupImageUrl, setPopupImageUrl] = useState('');
  const [popupImageName, setPopupImageName] = useState('');

  // Yardım Modalı
  const [showHelpModal, setShowHelpModal] = useState(false);

  // Ürün ve Fiyat Sorgula
  const [showProductLookupModal, setShowProductLookupModal] = useState(false);
  const [lookupSearchQuery, setLookupSearchQuery] = useState('');
  const [lookupSearchResults, setLookupSearchResults] = useState([]);
  const [selectedLookupProduct, setSelectedLookupProduct] = useState(null);
  const [lookupFilters, setLookupFilters] = useState({ category: '', metal_type: '', brand: '' });
  const [productStoreStocks, setProductStoreStocks] = useState([]);
  const [stores, setStores] = useState([]);

  // Bakiye
  const [giftCardCode, setGiftCardCode] = useState('');
  const [giftCardInfo, setGiftCardInfo] = useState(null);
  const [giftCardError, setGiftCardError] = useState('');
  const [appliedGiftCard, setAppliedGiftCard] = useState(null);
  const [giftCardDiscount, setGiftCardDiscount] = useState(0);

  // Hurda Borcu (Scrap Debt) - Müşterinin hurda alımından alacağı
  const [customerScrapDebtCards, setCustomerScrapDebtCards] = useState([]);
  const [appliedScrapDebtCard, setAppliedScrapDebtCard] = useState(null);

  // Müşteriye Özel Hediye Kartları
  const [customerGiftCards, setCustomerGiftCards] = useState([]);
  const [appliedCustomerGiftCard, setAppliedCustomerGiftCard] = useState(null);

  // Hurda Alacağı Kalan Bakiye Modal
  const [showScrapBalanceModal, setShowScrapBalanceModal] = useState(false);
  const [scrapBalanceInfo, setScrapBalanceInfo] = useState(null);

  // Mobile Tab Navigation
  const [mobileActiveTab, setMobileActiveTab] = useState('cart'); // 'cart', 'checkout', 'actions'

  // Hurda Alım ile Satış
  const [showScrapOptionsModal, setShowScrapOptionsModal] = useState(false);
  const [showScrapFormModal, setShowScrapFormModal] = useState(false);
  const [scrapCart, setScrapCart] = useState([]); // Çoklu hurda sepeti
  const [scrapBonusPercent, setScrapBonusPercent] = useState(0); // Genel bonus yüzdesi
  const [scrapFormData, setScrapFormData] = useState({
    metal_type: 'gold',
    purity: '22',
    custom_purity_rate: null, // Tek seferlik özel oran
    gross_weight: '',
    waste_percent: 0,
    price_per_gram: '',
    bonus_percent: 0,
    description: ''
  });

  // Hurda ayar oranları - varsayılan değerler
  const defaultScrapPurityRates = {
    gold: { '24': 0.985, '22': 0.900, '21': 0.800, '18': 0.700, '14': 0.500, '10': 0.350, '9': 0.300, '8': 0.250 },
    silver: { '999': 0.750, '925': 0.650, '900': 0.600, '850': 0.550, '800': 0.500, '750': 0.450, '700': 0.400 }
  };

  // localStorage'dan kayıtlı oranları al veya varsayılanları kullan
  const [scrapPurityRates, setScrapPurityRates] = useState(defaultScrapPurityRates);

  // Ayar oranı düzenleme state'leri
  const [isEditingPurityRate, setIsEditingPurityRate] = useState(false);
  const [editingPurityRateValue, setEditingPurityRateValue] = useState('');

  // Ayar oranını güncelle (tek seferlik - localStorage'a kaydetmez)
  // formType: 'scrap' veya 'collection'
  const updatePurityRate = (metalType, purity, newRate, formType = 'scrap') => {
    const rate = parseFloat(newRate) / 1000; // 985 -> 0.985
    if (isNaN(rate) || rate < 0 || rate > 1) return;

    // Sadece ilgili formData'daki custom_purity_rate'i güncelle (kalıcı değil)
    if (formType === 'collection') {
      setCollectionScrapData(prev => ({ ...prev, custom_purity_rate: rate }));
    } else {
      setScrapFormData(prev => ({ ...prev, custom_purity_rate: rate }));
    }
  };

  // Hızlı hurda seçenekleri (Çeyrek, Yarım, Tam)
  const quickScrapOptions = [
    { id: 'ceyrek', name: 'Çeyrek', weight: 1.75, purity: '22' },
    { id: 'yarim', name: 'Yarım', weight: 3.5, purity: '22' },
    { id: 'tam', name: 'Tam', weight: 7, purity: '22' }
  ];

  // Dinamik alış kuru hesaplama - manuel alış kuru varsa onu kullan, yoksa anlık kur
  const getAlisRate = (currency) => {
    const customRate = customRates[currency];
    let liveAlis;
    if (currency === 'GOLD') {
      liveAlis = parseFloat(prices?.altin_alis) || 0;
    } else if (currency === 'SILVER') {
      liveAlis = parseFloat(prices?.gumus_alis) || 0;
    } else {
      liveAlis = parseFloat(prices?.[`${currency}_alis`]) || parseFloat(prices?.[currency]) || 0;
    }

    // Önce makas farkı yüzdesini kontrol et (dinamik hesaplama)
    if (customRate?.alis_percentage != null && customRate.alis_percentage !== '' && !isNaN(customRate.alis_percentage)) {
      return liveAlis * (1 - parseFloat(customRate.alis_percentage) / 100);
    }
    // Sonra manuel sabit alış kurunu kontrol et
    if (customRate?.rate_alis != null && parseFloat(customRate.rate_alis) > 0) {
      return parseFloat(customRate.rate_alis);
    }
    // Manuel alış kuru yoksa anlık alış kurunu kullan
    return liveAlis;
  };

  // Dinamik satış kuru hesaplama - manuel satış kuru varsa onu kullan, yoksa anlık kur
  const getSatisRate = (currency) => {
    const customRate = customRates[currency];
    let liveSatis;
    if (currency === 'GOLD') {
      liveSatis = parseFloat(prices?.altin_satis) || 0;
    } else if (currency === 'SILVER') {
      liveSatis = parseFloat(prices?.gumus_satis) || 0;
    } else {
      liveSatis = parseFloat(prices?.[`${currency}_satis`]) || parseFloat(prices?.[currency]) || 0;
    }

    // Önce makas farkı yüzdesini kontrol et (dinamik hesaplama)
    if (customRate?.satis_percentage != null && customRate.satis_percentage !== '' && !isNaN(customRate.satis_percentage)) {
      return liveSatis * (1 + parseFloat(customRate.satis_percentage) / 100);
    }
    // Sonra manuel sabit satış kurunu kontrol et
    if (customRate?.rate_satis != null && parseFloat(customRate.rate_satis) > 0) {
      return parseFloat(customRate.rate_satis);
    }
    // Manuel satış kuru yoksa anlık satış kurunu kullan
    return liveSatis;
  };

  // Hızlı hurda seçimi - form alanlarını doldur
  const handleQuickScrapSelect = (option) => {
    const goldPrice = getAlisRate('GOLD');

    setScrapFormData(prev => ({
      ...prev,
      metal_type: 'gold',
      purity: option.purity,
      custom_purity_rate: null,
      gross_weight: option.weight.toString(),
      price_per_gram: goldPrice.toFixed(2),
      description: option.name
    }));
    toast.info(`${option.name} bilgileri forma dolduruldu`);
  };

  // Hurda sepetine manuel ekleme
  const addScrapToCart = () => {
    const grossWeight = parseFloat(scrapFormData.gross_weight) || 0;
    const wasteGram = parseFloat(scrapFormData.waste_gram) || 0;
    const pricePerGram = parseFloat(scrapFormData.price_per_gram) || 0;

    if (grossWeight <= 0 || pricePerGram <= 0) {
      toast.warning('Lütfen gram ve fiyat bilgilerini girin');
      return;
    }

    const netWeight = grossWeight - wasteGram;
    // Önce custom_purity_rate'i kontrol et (tek seferlik), yoksa varsayılanı kullan
    const purityRate = scrapFormData.custom_purity_rate || scrapPurityRates[scrapFormData.metal_type]?.[scrapFormData.purity] || defaultScrapPurityRates[scrapFormData.metal_type]?.[scrapFormData.purity] || 0;
    const pureWeight = netWeight * purityRate;
    const calculatedTotal = pureWeight * pricePerGram;
    // Manuel değer varsa onu kullan
    const total = customScrapTotal !== null ? customScrapTotal : calculatedTotal;
    const metalName = scrapFormData.metal_type === 'gold' ? 'Altın' : 'Gümüş';

    const bonusPercent = parseFloat(scrapFormData.bonus_percent) || 0;
    const bonusAmount = total * (bonusPercent / 100);

    const newScrap = {
      id: Date.now(),
      name: scrapFormData.description || `${metalName} ${scrapFormData.purity} Ayar`,
      metal_type: scrapFormData.metal_type,
      purity: scrapFormData.purity,
      gross_weight: grossWeight,
      waste_gram: wasteGram,
      pure_weight: pureWeight,
      price_per_gram: pricePerGram,
      unit_total: total,
      total: total,
      quantity: 1,
      bonus_percent: bonusPercent,
      bonus_amount: bonusAmount,
      isQuickOption: false
    };

    setScrapCart([...scrapCart, newScrap]);
    // Bonus yüzdesini genel değere de yansıt
    if (bonusPercent > 0) {
      setScrapBonusPercent(bonusPercent);
    }
    // Manuel toplam değeri sıfırla
    setCustomScrapTotal(null);
    setIsEditingScrapTotal(false);
    toast.success('Hurda sepete eklendi');

    // Formu sıfırla - fiyatı koruyarak (aynı kurla devam etmek için)
    const currentMetalType = scrapFormData.metal_type;
    const currentPrice = currentMetalType === 'gold'
      ? getAlisRate('GOLD')
      : getAlisRate('SILVER');

    setScrapFormData({
      ...scrapFormData,
      gross_weight: '',
      waste_gram: '',
      custom_purity_rate: null, // Tek seferlik oranı sıfırla
      price_per_gram: currentPrice > 0 ? currentPrice.toString() : '',
      bonus_percent: 0,
      description: ''
    });
  };

  // Hurda miktarını güncelle
  const updateScrapQuantity = (id, delta) => {
    setScrapCart(prev => prev.map(item => {
      if (item.id === id) {
        const newQty = Math.max(1, item.quantity + delta);
        return { ...item, quantity: newQty, total: item.unit_total * newQty };
      }
      return item;
    }));
  };

  // Hurda miktarını manuel ayarla
  const setScrapQuantity = (id, qty) => {
    const newQty = Math.max(1, parseInt(qty) || 1);
    setScrapCart(prev => prev.map(item => {
      if (item.id === id) {
        return { ...item, quantity: newQty, total: item.unit_total * newQty };
      }
      return item;
    }));
  };

  // Hurda sil
  const removeScrapFromCart = (id) => {
    setScrapCart(prev => prev.filter(item => item.id !== id));
  };

  // Hurda sepetini temizle
  const clearScrapCart = () => {
    setScrapCart([]);
    setScrapBonusPercent(0);
    setCustomScrapTotal(null);
    setIsEditingScrapTotal(false);
    setEditingScrapTotalValue('');
  };

  // Toplam hurda tutarı
  const scrapDiscount = scrapCart.reduce((sum, item) => sum + item.total, 0);

  // Sarf Malzemeleri Modal
  const [showConsumablesModal, setShowConsumablesModal] = useState(false);
  const [consumableCategory, setConsumableCategory] = useState(null);
  const [consumableProducts, setConsumableProducts] = useState([]);
  const [loadingConsumables, setLoadingConsumables] = useState(false);
  const [selectedSaleForConsumable, setSelectedSaleForConsumable] = useState(null);
  const [selectedConsumables, setSelectedConsumables] = useState({}); // { productId: { product, quantity } }
  const [savingConsumables, setSavingConsumables] = useState(false);
  const [consumablesEditMode, setConsumablesEditMode] = useState(false); // true = düzenleme, false = yeni ekleme
  const [existingConsumables, setExistingConsumables] = useState([]); // Mevcut sarf kayıtları

  // Transferler Modal
  const [showTransfersModal, setShowTransfersModal] = useState(false);
  const [transferMode, setTransferMode] = useState(null); // 'transfer', 'receive-transfer', vb.
  const [pendingTransferCount, setPendingTransferCount] = useState(0);
  const [rejectedRequestCount, setRejectedRequestCount] = useState(0);
  const previousRejectedIdsRef = useRef(null);
  // En geç teslim süresi gelmiş/geçmiş tamirat sayısı ("Tamirat" butonu rozeti için)
  const [repairOverdueCount, setRepairOverdueCount] = useState(0);
  const [incomingRequestCount, setIncomingRequestCount] = useState(0);
  const [transferTargetStore, setTransferTargetStore] = useState('');
  const [transferItems, setTransferItems] = useState([]);
  const [transferBarcode, setTransferBarcode] = useState('');
  const [transferNotes, setTransferNotes] = useState('');
  const [savingTransfer, setSavingTransfer] = useState(false);
  const transferBarcodeRef = useRef(null);
  const [transferStaffId, setTransferStaffId] = useState('');
  const [transferStaffName, setTransferStaffName] = useState('');
  const [receiveStaffId, setReceiveStaffId] = useState('');
  const [receiveStaffName, setReceiveStaffName] = useState('');
  const [showReceiveStaffModal, setShowReceiveStaffModal] = useState(false);
  const [pendingReceiveAction, setPendingReceiveAction] = useState(null); // 'bulk' veya 'single'
  const [sentTransfers, setSentTransfers] = useState([]);
  const [loadingSentTransfers, setLoadingSentTransfers] = useState(false);
  const [selectedTransferDetail, setSelectedTransferDetail] = useState(null);

  // Transfer Talebi
  const [requestSourceStore, setRequestSourceStore] = useState('');
  const [requestItems, setRequestItems] = useState([]);
  const [requestProductSearch, setRequestProductSearch] = useState('');
  const [requestSearchResults, setRequestSearchResults] = useState([]);
  const [requestNotes, setRequestNotes] = useState('');
  const [savingRequest, setSavingRequest] = useState(false);
  const [searchingRequestProducts, setSearchingRequestProducts] = useState(false);

  // Gönderilen Transfer Talepleri
  const [sentRequests, setSentRequests] = useState([]);
  const [loadingSentRequests, setLoadingSentRequests] = useState(false);
  const [selectedRequestDetail, setSelectedRequestDetail] = useState(null);

  // Transfer Geçmişi
  const [transferHistory, setTransferHistory] = useState([]);
  const [loadingTransferHistory, setLoadingTransferHistory] = useState(false);
  const [selectedHistoryDetail, setSelectedHistoryDetail] = useState(null);
  const [cancellingTransfer, setCancellingTransfer] = useState(false);
  const [cancellingRequest, setCancellingRequest] = useState(false);
  const [editingTransfer, setEditingTransfer] = useState(false);
  const [editingTransferItems, setEditingTransferItems] = useState([]);
  const [savingTransferEdit, setSavingTransferEdit] = useState(false);

  // Gelen Transfer Talepleri
  const [incomingRequests, setIncomingRequests] = useState([]);
  const [loadingIncomingRequests, setLoadingIncomingRequests] = useState(false);
  const [selectedIncomingRequestDetail, setSelectedIncomingRequestDetail] = useState(null);
  const [incomingRequestDetailItems, setIncomingRequestDetailItems] = useState([]);
  const [processingRequest, setProcessingRequest] = useState(false);
  const [incomingRequestBarcodeInput, setIncomingRequestBarcodeInput] = useState('');
  const incomingRequestBarcodeRef = useRef(null);
  const [incomingRequestStaffId, setIncomingRequestStaffId] = useState('');
  const [incomingRequestStaffName, setIncomingRequestStaffName] = useState('');
  const [showRejectRequestModal, setShowRejectRequestModal] = useState(false);
  const [rejectRequestReason, setRejectRequestReason] = useState('');
  const [rejectRequestStaffId, setRejectRequestStaffId] = useState(null);
  const [rejectRequestStaffName, setRejectRequestStaffName] = useState('');
  const [incomingRequestsTab, setIncomingRequestsTab] = useState('pending'); // 'pending' | 'history'
  const [incomingRequestsHistory, setIncomingRequestsHistory] = useState([]);
  const [loadingIncomingHistory, setLoadingIncomingHistory] = useState(false);
  const [selectedHistoryIncomingRequest, setSelectedHistoryIncomingRequest] = useState(null);
  const [showApproveStaffModal, setShowApproveStaffModal] = useState(false);

  // Transfer Teslim Alma
  const [pendingTransfers, setPendingTransfers] = useState([]);
  const [selectedTransferForReceive, setSelectedTransferForReceive] = useState(null);
  const [transferDetailItems, setTransferDetailItems] = useState([]);
  const [loadingPendingTransfers, setLoadingPendingTransfers] = useState(false);
  const [receiveBarcodeInput, setReceiveBarcodeInput] = useState('');
  const receiveBarcodeInputRef = useRef(null);

  // Hızlı Barkod Teslim (fiş seçmeden)
  const [quickTransferBarcode, setQuickTransferBarcode] = useState('');
  const [quickTransferItems, setQuickTransferItems] = useState([]);
  const [showQuickTransferSummary, setShowQuickTransferSummary] = useState(false);
  const [quickTransferSummaryData, setQuickTransferSummaryData] = useState(null);
  const [savingQuickTransfer, setSavingQuickTransfer] = useState(false);
  const quickTransferBarcodeRef = useRef(null);
  const [lastScannedTransferProduct, setLastScannedTransferProduct] = useState(null);

  // Tamirat Modülü
  const [showRepairOptionsModal, setShowRepairOptionsModal] = useState(false);
  const [showRepairModal, setShowRepairModal] = useState(false);
  const [repairCustomers, setRepairCustomers] = useState([]);
  const [repairCustomerSearch, setRepairCustomerSearch] = useState('');
  const [showRepairCustomerDropdown, setShowRepairCustomerDropdown] = useState(false);
  const [repairCustomerMode, setRepairCustomerMode] = useState('select'); // 'select' veya 'new'
  const repairCustomerDropdownRef = useRef(null);
  const repairFileInputRef = useRef(null);
  const repairCameraInputRef = useRef(null);
  const [repairUploading, setRepairUploading] = useState(false);
  const [repairPhoneMatchCustomers, setRepairPhoneMatchCustomers] = useState([]);

  // Terminal fotoğraf çekimi için state'ler
  const [showRepairTerminalModal, setShowRepairTerminalModal] = useState(false);
  const [repairTerminals, setRepairTerminals] = useState([]);
  const [loadingRepairTerminals, setLoadingRepairTerminals] = useState(false);
  const [selectedRepairTerminal, setSelectedRepairTerminal] = useState(null);
  const [repairWaitingForPhoto, setRepairWaitingForPhoto] = useState(false);
  const [repairPhotoRequestId, setRepairPhotoRequestId] = useState(null);
  const [repairTerminalPhotoItemIndex, setRepairTerminalPhotoItemIndex] = useState(0);
  const repairPhotoCheckIntervalRef = useRef(null);
  // Telefon eşleşmesinden seçilen müşteri mi (badge metni için)
  const [repairCustomerSelectedFromMatch, setRepairCustomerSelectedFromMatch] = useState(false);
  // Tamirat "Ücret Al" (kapora) ödeme modalı için
  const [showRepairPaymentModal, setShowRepairPaymentModal] = useState(false);
  const [repairPaymentMethod, setRepairPaymentMethod] = useState('');
  const [repairPaymentAmount, setRepairPaymentAmount] = useState('');
  const [repairPayments, setRepairPayments] = useState([]);
  const [repairBankAccounts, setRepairBankAccounts] = useState([]);
  const [selectedRepairBankAccount, setSelectedRepairBankAccount] = useState(null);

  const [repairFormData, setRepairFormData] = useState({
    customer_id: '',
    customer_name: '',
    customer_phone: '',
    items: [{
      item_type: storeMetalType,
      item_description: '',
      problem_description: '',
      photos: []
    }],
    estimated_price: '',
    price_type: 'normal',
    deposit_amount: '',
    deposit_payment_method: '',
    deposit_payment_details: [],
    estimated_delivery: '',
    deadline: '',
    notes: '',
    staff_name: ''
  });

  // Doğum Günü Kampanyası
  const [birthdayCampaign, setBirthdayCampaign] = useState(null);

  // Özel Gün Kampanyası
  const [specialDayCampaign, setSpecialDayCampaign] = useState(null);

  // Hediye Kampanyası
  const [showGiftCampaignModal, setShowGiftCampaignModal] = useState(false);
  const [eligibleGiftCampaign, setEligibleGiftCampaign] = useState(null);
  const [giftCampaignProduct, setGiftCampaignProduct] = useState(null);

  // Seçili müşterinin borç bilgisi
  const [selectedCustomerDebt, setSelectedCustomerDebt] = useState(null);

  // Tahsilat modülü için
  const [showCollectionModal, setShowCollectionModal] = useState(false);
  const [collectionCustomerSearchQuery, setCollectionCustomerSearchQuery] = useState('');
  const [collectionCustomerSearchResults, setCollectionCustomerSearchResults] = useState([]);
  const [collectionSelectedCustomer, setCollectionSelectedCustomer] = useState(null);
  const [showCollectionDebtModal, setShowCollectionDebtModal] = useState(false);
  const [collectionCustomerDebts, setCollectionCustomerDebts] = useState([]);
  const [loadingCollectionDebts, setLoadingCollectionDebts] = useState(false);
  const [showCollectionPaymentModal, setShowCollectionPaymentModal] = useState(false);
  const [selectedCollectionDebt, setSelectedCollectionDebt] = useState(null);
  const [selectedCollectionDebtGroup, setSelectedCollectionDebtGroup] = useState(null); // Grup ödeme için
  const [collectionPaymentMethod, setCollectionPaymentMethod] = useState('');
  const [collectionPaymentAmount, setCollectionPaymentAmount] = useState('');
  const [collectionMixedPayments, setCollectionMixedPayments] = useState([]); // Karışık ödemeler için
  const [collectionPaymentInputMode, setCollectionPaymentInputMode] = useState('tl'); // 'tl' veya 'gram' - altın borcu için
  const [collectionDebtSummary, setCollectionDebtSummary] = useState({});
  const [collectionStaffId, setCollectionStaffId] = useState('');
  const [collectionStaffName, setCollectionStaffName] = useState('');
  const [showCollectionStaffModal, setShowCollectionStaffModal] = useState(false);

  // Tahsilat - Son Tahsilatlar Geçmişi
  const [showCollectionHistory, setShowCollectionHistory] = useState(false);
  const [collectionHistory, setCollectionHistory] = useState([]);
  const [loadingCollectionHistory, setLoadingCollectionHistory] = useState(false);
  const [collectionHistorySearch, setCollectionHistorySearch] = useState('');

  // Tahsilat - Düzenleme
  const [editingCollectionPayment, setEditingCollectionPayment] = useState(null);
  const [editCollectionItems, setEditCollectionItems] = useState([]);
  const [editCollectionNotes, setEditCollectionNotes] = useState('');
  const [editCollectionDate, setEditCollectionDate] = useState('');

  // Tahsilat - İşlem durumu (çift tıklama koruması)
  const [collectionProcessing, setCollectionProcessing] = useState(false);
  // Tahsilat - Hurda ile ödeme
  const [collectionScrapMode, setCollectionScrapMode] = useState(false);
  const [collectionScrapData, setCollectionScrapData] = useState({
    metal_type: isGoldStore ? 'gold' : 'silver',
    gross_weight: '',
    waste_percent: '',
    waste_gram: '',
    purity: '',
    custom_purity_rate: null, // Tek seferlik özel oran
    unit_price: ''
  });

  // Hurda manuel toplam düzenleme
  const [isEditingScrapTotal, setIsEditingScrapTotal] = useState(false);
  const [editingScrapTotalValue, setEditingScrapTotalValue] = useState('');
  const [customScrapTotal, setCustomScrapTotal] = useState(null);
  const [isEditingCollectionScrapTotal, setIsEditingCollectionScrapTotal] = useState(false);
  const [editingCollectionScrapTotalValue, setEditingCollectionScrapTotalValue] = useState('');
  const [customCollectionScrapTotal, setCustomCollectionScrapTotal] = useState(null);

  // Efektif hurda tutarı (manuel değer varsa onu kullan)
  const effectiveScrapTotal = customScrapTotal !== null ? customScrapTotal : scrapDiscount;

  // Hurda bilgisi - yüzdelik tüm hurdalar için genel (scrapBonusPercent)
  const appliedScrap = scrapCart.length > 0 ? {
    items: scrapCart,
    total: effectiveScrapTotal,
    bonus_percent: scrapBonusPercent,
    bonus_amount: effectiveScrapTotal * (scrapBonusPercent / 100)
  } : null;

  // Masraflar modülü için
  const [showExpensesModal, setShowExpensesModal] = useState(false);
  const [showExpenseForm, setShowExpenseForm] = useState(false);
  const [expenses, setExpenses] = useState([]);
  const [loadingExpenses, setLoadingExpenses] = useState(false);
  const [showAddExpenseForm, setShowAddExpenseForm] = useState(false);
  const [newExpense, setNewExpense] = useState({
    description: '',
    amount: '',
    category: 'Genel',
    payment_method: 'Nakit',
    notes: ''
  });
  const [expenseCategories] = useState([
    'Genel', 'Kırtasiye', 'Temizlik', 'Yemek', 'Ulaşım', 'Kargo', 'Tamir/Bakım', 'Fatura', 'Diğer'
  ]);

  // Masraf düzenleme için (admin)
  const [showEditExpenseModal, setShowEditExpenseModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [showDeleteExpenseConfirm, setShowDeleteExpenseConfirm] = useState(false);
  const [deletingExpenseId, setDeletingExpenseId] = useState(null);

  // Geçmiş masraflar (admin)
  const [showPastExpenses, setShowPastExpenses] = useState(false);
  const [pastExpenses, setPastExpenses] = useState([]);
  const [loadingPastExpenses, setLoadingPastExpenses] = useState(false);
  const [pastExpenseSearch, setPastExpenseSearch] = useState('');
  const [pastExpenseFilters, setPastExpenseFilters] = useState({
    start_date: '',
    end_date: '',
    store_id: '',
    category: ''
  });

  // Özel Kur Modalı - localStorage ile senkronize
  const [showRateModal, setShowRateModal] = useState(false);
  const [customRates, setCustomRates] = useState(() => {
    const saved = localStorage.getItem('sharedCustomRates');
    return saved ? JSON.parse(saved) : {};
  });
  const [rateModalCurrency, setRateModalCurrency] = useState('USD');
  const [rateModalValue, setRateModalValue] = useState('');
  const [rateModalAlisValue, setRateModalAlisValue] = useState('');
  const [rateModalSatisValue, setRateModalSatisValue] = useState('');
  const [rateModalAlisPercentage, setRateModalAlisPercentage] = useState(''); // Alış makas farkı
  const [rateModalSatisPercentage, setRateModalSatisPercentage] = useState(''); // Satış makas farkı
  const [rateModalInputMode, setRateModalInputMode] = useState('manual'); // eski uyumluluk
  const [rateModalAlisMode, setRateModalAlisMode] = useState('manual'); // 'manual' veya 'percentage'
  const [rateModalSatisMode, setRateModalSatisMode] = useState('manual'); // 'manual' veya 'percentage'
  const [rateModalAlisDolar, setRateModalAlisDolar] = useState('');
  const [rateModalSatisDolar, setRateModalSatisDolar] = useState('');


  // Banka Hesapları (Havale/EFT)
  const [showBankAccountsModal, setShowBankAccountsModal] = useState(false);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [selectedBankAccount, setSelectedBankAccount] = useState(null);
  const [showBankAccountDetails, setShowBankAccountDetails] = useState(false);
  const [showAddBankAccountForm, setShowAddBankAccountForm] = useState(false);
  const [editingBankAccount, setEditingBankAccount] = useState(null);
  const [newBankAccount, setNewBankAccount] = useState({
    account_name: '',
    holder_name: '',
    bank_name: '',
    iban: '',
    account_type: 'company',
    currency: 'TRY',
    qr_code_image: '',
    notes: ''
  });

  // Stok Yetersizliği ve Transfer Talebi
  const [showStockRequestModal, setShowStockRequestModal] = useState(false);
  const [pendingStockRequests, setPendingStockRequests] = useState([]);
  const [pendingPaymentData, setPendingPaymentData] = useState(null); // Ödeme bekletme verisi

  // Log Görüntüleme (Sadece Admin)
  const [showPosLogsModal, setShowPosLogsModal] = useState(false);
  const [posLogs, setPosLogs] = useState([]);
  const [posLogsLoading, setPosLogsLoading] = useState(false);
  const [posLogsFilter, setPosLogsFilter] = useState({
    action: '',
    status: '',
    user_id: '',
    start_date: '',
    end_date: ''
  });
  const [posLogsPage, setPosLogsPage] = useState(1);
  const [posLogsTotalPages, setPosLogsTotalPages] = useState(1);
  const [posLogsStats, setPosLogsStats] = useState(null);
  const [posLogsUsers, setPosLogsUsers] = useState([]);
  const [selectedLogDetail, setSelectedLogDetail] = useState(null);

  // Modal kapanış animasyonu için state
  const [closingModal, setClosingModal] = useState(null);

  // Modal kapanış handler'ı (callback opsiyonel)
  const handleCloseModal = (modalName, setShowModal, callback) => {
    setClosingModal(modalName);
    setTimeout(() => {
      setShowModal(false);
      setClosingModal(null);
      if (callback) callback();
    }, 250);
  };

  // ESC tuşu ile modal kapatma
  useEscapeClose([
    // Confirm modals (highest priority)
    [showConfirmModal, () => setShowConfirmModal(false)],
    [showDeleteConfirm, () => setShowDeleteConfirm(false)],
    [showDeleteExpenseConfirm, () => setShowDeleteExpenseConfirm(false)],
    [showCashRefundConfirmModal, () => setShowCashRefundConfirmModal(false)],
    [showTransferRefundConfirmModal, () => setShowTransferRefundConfirmModal(false)],
    // Detail modals
    [showSaleDetailModal, () => setShowSaleDetailModal(false)],
    [showRefundDetailModal, () => setShowRefundDetailModal(false)],
    [showDebtPaymentDetailModal, () => setShowDebtPaymentDetailModal(false)],
    [showScrapPurchaseDetailModal, () => setShowScrapPurchaseDetailModal(false)],
    [showExpenseDetailModal, () => setShowExpenseDetailModal(false)],
    // Edit modals
    [showEditSaleModal, () => closeEditSaleModal()],
    [showAdminEditModal, () => closeAdminEditModal()],
    [showExchangeEditModal, () => closeExchangeEditModal()],
    [showEditDebtPaymentModal, () => setShowEditDebtPaymentModal(false)],
    [showEditScrapPurchaseModal, () => setShowEditScrapPurchaseModal(false)],
    [showEditExpenseModal, () => setShowEditExpenseModal(false)],
    [showDebtUpdateModal, () => setShowDebtUpdateModal(false)],
    // Specific feature modals
    [showCustomProductModal, () => setShowCustomProductModal(false)],
    [showQuickPriceModal, () => setShowQuickPriceModal(false)],
    [showItemDiscountModal, () => setShowItemDiscountModal(false)],
    [showItemNoteModal, () => setShowItemNoteModal(false)],
    [showSpecialWeightModal, () => setShowSpecialWeightModal(false)],
    [showDiscountModal, () => { setDiscountAmount(0); setShowDiscountModal(false); }],
    [showKvkkModal, () => setShowKvkkModal(false)],
    [showStaffModal, () => setShowStaffModal(false)],
    [showReceiveStaffModal, () => setShowReceiveStaffModal(false)],
    [showCollectionStaffModal, () => setShowCollectionStaffModal(false)],
    [showCashModal, () => setShowCashModal(false)],
    [showNakitModal, () => setShowNakitModal(false)],
    [showCardModal, () => setShowCardModal(false)],
    [showVirtualPosModal, () => setShowVirtualPosModal(false)],
    [showScrapDebtTypeModal, () => setShowScrapDebtTypeModal(false)],
    [showScrapBalanceModal, () => setShowScrapBalanceModal(false)],
    [showReceiptChoiceModal, () => setShowReceiptChoiceModal(false)],
    [showExchangeReceiptModal, () => setShowExchangeReceiptModal(false)],
    [showStoreSelectModal, () => setShowStoreSelectModal(false)],
    [showStockRequestModal, () => setShowStockRequestModal(false)],
    [showGiftCampaignModal, () => setShowGiftCampaignModal(false)],
    [showRepairTerminalModal, () => setShowRepairTerminalModal(false)],
    [showRepairPaymentModal, () => setShowRepairPaymentModal(false)],
    [showCollectionPaymentModal, () => setShowCollectionPaymentModal(false)],
    [showCollectionDebtModal, () => setShowCollectionDebtModal(false)],
    [showBankAccountsModal, () => setShowBankAccountsModal(false)],
    [showImagePopup, () => setShowImagePopup(false)],
    // Main modals
    [showPaymentModal, () => setShowPaymentModal(false)],
    [showRefundModal, () => setShowRefundModal(false)],
    [showLastSaleModal, () => setShowLastSaleModal(false)],
    [showAllSalesModal, () => setShowAllSalesModal(false)],
    [showExchangeModal, () => setShowExchangeModal(false)],
    [showCustomerModal, () => setShowCustomerModal(false)],
    [showFavoritesModal, () => setShowFavoritesModal(false)],
    [showCampaignsModal, () => setShowCampaignsModal(false)],
    [showScrapOptionsModal, () => setShowScrapOptionsModal(false)],
    [showScrapFormModal, () => setShowScrapFormModal(false)],
    [showConsumablesModal, () => setShowConsumablesModal(false)],
    [showApproveStaffModal, () => setShowApproveStaffModal(false)],
    [showTransfersModal, () => closeTransferModal()],
    [showRepairOptionsModal, () => setShowRepairOptionsModal(false)],
    [showRepairModal, () => setShowRepairModal(false)],
    [showCollectionModal, () => setShowCollectionModal(false)],
    [showExpensesModal, () => { setNewExpense({ description: '', amount: '', category: 'Genel', payment_method: 'Nakit', notes: '' }); setShowAddExpenseForm(false); setShowExpensesModal(false); }],
    [showEndOfDayModal, () => setShowEndOfDayModal(false)],
    [showHoldModal, () => setShowHoldModal(false)],
    [showRateModal, () => setShowRateModal(false)],
    [showProductDetailModal, () => setShowProductDetailModal(false)],
    [showProductLookupModal, () => setShowProductLookupModal(false)],
    [showHelpModal, () => setShowHelpModal(false)],
    [showPosLogsModal, () => setShowPosLogsModal(false)],
  ]);

  // Eski etiket ayarını localStorage'dan canlı dinle
  useEffect(() => {
    const handleStorage = () => {
      const saved = localStorage.getItem('eskiEtiketScanEnabled');
      setEskiEtiketScanEnabled(saved === 'true');
    };
    window.addEventListener('storage', handleStorage);
    // Sayfa fokus aldığında da kontrol et (aynı tab'ta settings değişirse)
    const handleFocus = () => {
      const saved = localStorage.getItem('eskiEtiketScanEnabled');
      setEskiEtiketScanEnabled(saved === 'true');
    };
    window.addEventListener('focus', handleFocus);
    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  // Kasiyer bazlı indirim limiti: seçili kasiyerlerden en yüksek limiti al
  useEffect(() => {
    if (user?.role === 'admin') {
      setMaxDiscountLimit(null);
      return;
    }
    if (selectedStaff.length === 0) {
      setMaxDiscountLimit(null);
      return;
    }
    // Seçili kasiyerlerden max_discount_percent değerlerini al
    const limits = selectedStaff
      .map(s => s.max_discount_percent)
      .filter(v => v !== null && v !== undefined);
    if (limits.length === 0) {
      // Hiçbir kasiyerde limit tanımlı değilse, limit yok
      setMaxDiscountLimit(null);
    } else {
      // En yüksek limiti baz al
      setMaxDiscountLimit(Math.max(...limits));
    }
  }, [selectedStaff, user?.role]);

  // Log context ayarla
  useEffect(() => {
    logService.setContext(user, selectedStore);
  }, [user, selectedStore]);

  // Transfer modu değiştiğinde son taranan ürünü sıfırla
  useEffect(() => {
    setLastScannedTransferProduct(null);
  }, [transferMode]);

  // Bekleyen transfer ve gelen talep sayılarını çek (sayfa yüklendiğinde + modal menüye dönünce)
  const fetchTransferCounts = () => {
    const storeId = selectedStore?.id || user?.store_id;
    if (!storeId) return;
    fetch(`${API_URL}/transfers/pending/${storeId}`)
      .then(res => res.ok ? res.json() : [])
      .then(data => setPendingTransferCount(Array.isArray(data) ? data.length : 0))
      .catch(() => setPendingTransferCount(0));
    fetch(`${API_URL}/transfer-requests/incoming/${storeId}`)
      .then(res => res.ok ? res.json() : [])
      .then(data => setIncomingRequestCount(Array.isArray(data) ? data.length : 0))
      .catch(() => setIncomingRequestCount(0));
    // Gönderilen taleplerden reddedilenleri çek; daha önce görülmemiş olanlar için bildirim göster
    fetch(`${API_URL}/transfer-requests/outgoing/${storeId}`)
      .then(res => res.ok ? res.json() : [])
      .then(data => {
        if (!Array.isArray(data)) return;
        const rejected = data.filter(r => r.status === 'rejected');
        const ackKey = `ackRejectedTransferRequests_${storeId}`;
        let ackIds = [];
        try {
          const raw = localStorage.getItem(ackKey);
          if (raw) ackIds = JSON.parse(raw) || [];
        } catch (e) { ackIds = []; }
        const unseen = rejected.filter(r => !ackIds.includes(r.id));
        const unseenIds = unseen.map(r => r.id);
        // İlk yükleme dışında, daha önce listede olmayan yeni reddetmeler için toast
        if (previousRejectedIdsRef.current !== null) {
          const newlyRejected = unseen.filter(r => !previousRejectedIdsRef.current.includes(r.id));
          newlyRejected.forEach(req => {
            const targetName = req.to_store_name || 'Hedef mağaza';
            toast.warning(`Transfer talebiniz reddedildi: ${targetName}`, { autoClose: 8000 });
          });
        }
        previousRejectedIdsRef.current = unseenIds;
        setRejectedRequestCount(unseen.length);
      })
      .catch(() => {});
  };

  useEffect(() => {
    fetchTransferCounts();
  }, [selectedStore, user]);

  useEffect(() => {
    if (showTransfersModal && !transferMode) {
      fetchTransferCounts();
    }
  }, [showTransfersModal, transferMode]);

  // Reddedilen talepler dahil sayıları periyodik olarak güncelle (30 sn)
  useEffect(() => {
    const storeId = selectedStore?.id || user?.store_id;
    if (!storeId) return;
    const interval = setInterval(fetchTransferCounts, 30000);
    return () => clearInterval(interval);
  }, [selectedStore, user]);

  // Süresi gelmiş/geçmiş tamirat sayısını çek (Tamirat butonu rozeti için)
  const fetchRepairOverdueCount = () => {
    const params = currentStoreId ? `?storeId=${currentStoreId}` : '';
    fetch(`${API_URL}/repairs/overdue-count${params}`)
      .then(res => res.ok ? res.json() : { count: 0 })
      .then(data => setRepairOverdueCount(data.count || 0))
      .catch(() => setRepairOverdueCount(0));
  };

  useEffect(() => {
    fetchRepairOverdueCount();
    const interval = setInterval(fetchRepairOverdueCount, 30000);
    return () => clearInterval(interval);
  }, [currentStoreId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Terminal fotoğraf kontrolü cleanup
  useEffect(() => {
    return () => {
      if (repairPhotoCheckIntervalRef.current) {
        clearInterval(repairPhotoCheckIntervalRef.current);
      }
    };
  }, []);

  // Barkod inputuna sürekli focus - modal açık değilken ve başka input seçili değilken
  useEffect(() => {
    const focusBarcode = () => {
      // Modal overlay açıksa focus verme
      if (document.querySelector('.modal-overlay')) return;
      // Aktif element bir input/textarea/select ise ve barkod inputu değilse dokunma
      const active = document.activeElement;
      if (active && active !== document.body && active !== barcodeInputRef.current) {
        const tag = active.tagName?.toLowerCase();
        if (tag === 'input' || tag === 'textarea' || tag === 'select' || active.isContentEditable) return;
      }
      barcodeInputRef.current?.focus();
    };
    focusBarcode();
    const interval = setInterval(focusBarcode, 500);
    return () => clearInterval(interval);
  }, []);

  // customRates değiştiğinde localStorage'a kaydet
  useEffect(() => {
    if (Object.keys(customRates).length > 0) {
      localStorage.setItem('sharedCustomRates', JSON.stringify(customRates));
    }
  }, [customRates]);

  // Açık hesap seçenekleri açıldığında otomatik scroll
  useEffect(() => {
    if (showCreditOptions && creditOptionsRef.current) {
      setTimeout(() => {
        creditOptionsRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 100);
    }
  }, [showCreditOptions]);

  // Sayfa odaklandığında localStorage'dan customRates'i güncelle
  useEffect(() => {
    const handleFocus = () => {
      const saved = localStorage.getItem('sharedCustomRates');
      if (saved) {
        const parsed = JSON.parse(saved);
        setCustomRates(prev => {
          if (JSON.stringify(prev) !== JSON.stringify(parsed)) {
            return parsed;
          }
          return prev;
        });
      }
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  // Genel onay modal'ı gösterme fonksiyonu (window.confirm yerine)
  const showConfirmation = (config) => {
    setConfirmModalConfig({
      title: config.title || 'Onay',
      message: config.message || '',
      confirmText: config.confirmText || 'Evet',
      cancelText: config.cancelText || 'İptal',
      onConfirm: config.onConfirm || (() => {}),
      onCancel: config.onCancel || null,
      type: config.type || 'warning'
    });
    setShowConfirmModal(true);
  };

  // Ürün arama (mağaza tipine göre filtreleme)
  // Eski etiket barkod okutma - barkodun başına 27 ekleyerek arar ve sepete ekler
  const handleEskiEtiketScan = async (barcode) => {
    if (!barcode.trim()) return;
    const modifiedBarcode = '27' + barcode.trim();
    try {
      const storeType = getStoreTypeForCustomer();
      const storeTypeParam = storeType ? `?storeType=${encodeURIComponent(storeType)}` : '';
      const response = await fetch(`${API_URL}/products/search/${encodeURIComponent(modifiedBarcode)}${storeTypeParam}`);
      const data = await response.json();
      if (data.length > 0) {
        addToCart(data[0]);
      } else {
        playCartSound('error');
        toast.error('Ürün bulunamadı!');
      }
    } catch (error) {
      console.error('Eski etiket arama hatası:', error);
      playCartSound('error');
      toast.error('Arama sırasında hata oluştu!');
    }
    setEskiEtiketQuery('');
    eskiEtiketInputRef.current?.focus();
  };

  const searchProduct = async (query) => {
    if (!query || query.length < 1) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    try {
      const storeType = getStoreTypeForCustomer();
      const storeTypeParam = storeType ? `?storeType=${encodeURIComponent(storeType)}` : '';
      const response = await fetch(`${API_URL}/products/search/${encodeURIComponent(query)}${storeTypeParam}`);
      const data = await response.json();
      setSearchResults(data);
      setShowSearchResults(data.length > 0);

      // Otomatik ekleme kaldırıldı - sadece Enter ile ekleme yapılacak
      // Barkod tam eşleşse bile liste gösterilecek, Enter'a basınca eklenecek
    } catch (error) {
      console.error('Ürün arama hatası:', error);
    }
  };

  // Favori ürünleri getir
  const fetchFavorites = async () => {
    setLoadingFavorites(true);
    try {
      const storeId = selectedStore?.id || user?.store_id || 1;
      const response = await fetch(`${API_URL}/products/favorites?store_id=${storeId}`);
      const data = await response.json();
      setFavoriteProducts(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Favori ürünler yüklenemedi:', error);
      setFavoriteProducts([]);
    } finally {
      setLoadingFavorites(false);
    }
  };

  // Favori toggle (satış ekranından) - Mağaza bazlı
  const toggleFavoriteFromSales = async (product) => {
    const newValue = !(product.is_favorite === 1 || product.is_favorite === true);
    const storeId = selectedStore?.id || user?.store_id || 1;
    try {
      const response = await fetch(`${API_URL}/products/${product.id}/favorite`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_favorite: newValue, store_id: storeId })
      });
      const data = await response.json();
      if (data.success) {
        toast.success(newValue ? `⭐ Favorilere eklendi` : `Favorilerden çıkarıldı`);
        fetchFavorites();
      }
    } catch (error) {
      console.error('Favori toggle hatası:', error);
    }
  };

  // Favori modalını aç
  const openFavoritesModal = () => {
    fetchFavorites();
    setShowFavoritesModal(true);
  };

  // Seçili mağazaya göre müşteri tipini belirle
  const getStoreTypeForCustomer = () => {
    const storeId = selectedStore?.id || user?.store_id;
    if (storeId === 1) return 'Altın';
    if (storeId === 2 || storeId === 3 || storeId === 4) return 'Gümüş';
    return '';
  };

  // Müşteri arama
  const searchCustomer = async (query) => {
    try {
      const storeType = getStoreTypeForCustomer();
      let response;
      if (!query || query.length < 2) {
        const params = new URLSearchParams({ limit: '50' });
        if (storeType) params.append('storeType', storeType);
        response = await fetch(`${API_URL}/customers?${params}`);
      } else {
        const storeParam = storeType ? `?storeType=${storeType}` : '';
        response = await fetch(`${API_URL}/customers/search/${query}${storeParam}`);
      }
      const data = await response.json();
      const customers = data.customers || data;

      // Bakiye bilgilerini paralel olarak çek
      try {
        const summaryRes = await fetch(`${API_URL}/customers/debts/summary`);
        const summaryData = await summaryRes.json();
        const balanceMap = {};
        summaryData.forEach(d => { balanceMap[d.customer_id] = d; });
        // Müşterilere bakiye bilgisi ekle
        customers.forEach(c => {
          const bal = balanceMap[c.id];
          if (bal) {
            c._tlBalance = Number(bal.total_tl_debt || 0) - Number(bal.total_tl_credit || 0);
            c._goldBalance = Number(bal.total_gold_debt || 0);
            c._silverBalance = Number(bal.total_silver_debt || 0);
          }
        });
      } catch (e) { /* bakiye alınamazsa sorun değil */ }

      setCustomerSearchResults(customers);
    } catch (error) {
      console.error('Müşteri arama hatası:', error);
    }
  };

  // Modal açıldığında müşterileri yükle
  useEffect(() => {
    if (showCustomerSearch) {
      searchCustomer('');
    }
  }, [showCustomerSearch]);

  // Müşteri seçildiğinde doğum günü kampanyasını kontrol et
  const checkBirthdayCampaign = async (customer) => {
    if (!customer?.id) {
      setBirthdayCampaign(null);
      return;
    }

    try {
      // Önce müşterinin geçmiş alışverişi var mı kontrol et
      const salesResponse = await fetch(`${API_URL}/customers/${customer.id}/sales`);
      const salesData = await salesResponse.json();

      // Müşterinin geçmişte en az bir alışverişi olmalı (array döner)
      const hasPastPurchases = Array.isArray(salesData) && salesData.length > 0;

      if (!hasPastPurchases) {
        setBirthdayCampaign(null);
        return; // Geçmiş alışveriş yoksa doğum günü kampanyası uygulanmaz
      }

      const response = await fetch(`${API_URL}/campaigns/applicable/${customer.id}`);
      const campaigns = await response.json();

      // Doğum günü kampanyası var mı kontrol et
      const birthdayCamp = campaigns.find(c => c.campaign_type === 'birthday');

      if (birthdayCamp) {
        setBirthdayCampaign(birthdayCamp);

        // Kampanya indirimini parse et
        const rewards = typeof birthdayCamp.rewards === 'string'
          ? JSON.parse(birthdayCamp.rewards)
          : birthdayCamp.rewards || {};

        // Otomatik kampanya uygula
        setAppliedCampaign(birthdayCamp);

        toast.success(
          `🎂 ${customer.name} için doğum günü kampanyası aktif! ${rewards.discount_type === 'fixed' ? `₺${rewards.discount_amount}` : `%${rewards.discount_percent}`} indirim uygulandı.`,
          { autoClose: 7500 }
        );
      } else {
        setBirthdayCampaign(null);
      }
    } catch (error) {
      console.error('Doğum günü kampanyası kontrol hatası:', error);
      setBirthdayCampaign(null);
    }
  };

  // Müşteri seçimi değiştiğinde doğum günü kampanyasını kontrol et
  useEffect(() => {
    if (selectedCustomer) {
      checkBirthdayCampaign(selectedCustomer);
    } else {
      setBirthdayCampaign(null);
      setSelectedCustomerDebt(null); // Müşteri kaldırıldığında borç bilgisini temizle
      // Hurda alacağı bilgilerini temizle
      setCustomerScrapDebtCards([]);
      setAppliedScrapDebtCard(null);
            // Eğer uygulanan kampanya doğum günü kampanyasıysa, kaldır
      if (appliedCampaign?.campaign_type === 'birthday') {
        setAppliedCampaign(null);
        setCampaignDiscount(0);
      }
    }
  }, [selectedCustomer]);

  // Müşteri seçildiğinde borç kontrolü yap
  const selectCustomerWithDebtCheck = async (customer) => {
    // Onaylı indirim varken gerçek müşteri seçiliyse değiştirme
    if (isCartLockedByApproval && selectedCustomer && !selectedCustomer.isAnonymous) {
      toast.warning('Onaylı indirim varken müşteri değiştirilemez!');
      return;
    }
    setSelectedCustomer(customer);
    setShowCustomerSearch(false);
    setCustomerSearchQuery('');
    setCustomerSearchResults([]);

    // Müşterinin borcunu kontrol et
    try {
      const response = await fetch(`${API_URL}/customers/debts/summary`);
      const data = await response.json();
      const customerDebt = data.find(d => d.customer_id === customer.id);

      if (customerDebt) {
        setSelectedCustomerDebt(customerDebt);

        // TL net bakiye (borç - alacak), gram borçlar ayrı
        const tlNetDebt = Math.max(0, Number(customerDebt.total_tl_debt || 0) - Number(customerDebt.total_tl_credit || 0));
        const goldGram = Number(customerDebt.total_gold_debt || 0);
        const silverGram = Number(customerDebt.total_silver_debt || 0);
        const goldTL = goldGram * getCurrentGoldPrice();
        const silverTL = silverGram * getCurrentSilverPrice();
        const totalDebt = tlNetDebt + goldTL + silverTL;

        if (totalDebt > 0.01) {
          const debtParts = [];
          if (tlNetDebt > 0) {
            debtParts.push(`₺${tlNetDebt.toLocaleString('tr-TR', {maximumFractionDigits: 0})} TL`);
          }
          if (goldGram > 0) {
            debtParts.push(`${goldGram.toFixed(2)} gr altın (₺${goldTL.toLocaleString('tr-TR', {maximumFractionDigits: 0})})`);
          }
          if (silverGram > 0) {
            debtParts.push(`${silverGram.toFixed(2)} gr gümüş (₺${silverTL.toLocaleString('tr-TR', {maximumFractionDigits: 0})})`);
          }

          toast.info(
            `${customer.name} - Toplam Borç: ₺${totalDebt.toLocaleString('tr-TR', {maximumFractionDigits: 0})} (${debtParts.join(' + ')})`,
            { autoClose: 7500 }
          );
        }
      } else {
        setSelectedCustomerDebt(null);
      }
    } catch (error) {
      console.error('Borç kontrol hatası:', error);
      setSelectedCustomerDebt(null);
    }

    // Hurda borcu (alacak) kontrolü - müşterinin hurda alımından alacağı var mı?
    try {
      const scrapResponse = await fetch(`${API_URL}/gift-cards`);
      const allCards = await scrapResponse.json();
      // Sadece bu müşteriye ait, aktif ve scrap_purchase kaynaklı kartları filtrele
      const customerScrapCards = (Array.isArray(allCards) ? allCards : []).filter(card =>
        card.source === 'scrap_purchase' &&
        card.status === 'active' &&
        card.customer_id === customer.id &&
        Number(card.remaining_amount) > 0
      );

      setCustomerScrapDebtCards(customerScrapCards);

      if (customerScrapCards.length > 0 && !isExchangeMode) {
        // Gram bazlı kartların güncel TL karşılığını hesapla - debt_type kontrolü
        const calculateCurrentTLValue = (card) => {
          const debtType = card.debt_type || 'TL';
          if (debtType === 'GRAM') {
            // Altın gram - remaining_amount gram cinsinden, güncel satış kuruyla çarp
            const currentGoldRate = getSatisRate('GOLD');
            return Number(card.remaining_amount) * currentGoldRate;
          } else if (debtType === 'GUMUS') {
            // Gümüş gram - remaining_amount gram cinsinden, güncel satış kuruyla çarp
            const currentSilverRate = getSatisRate('SILVER');
            return Number(card.remaining_amount) * currentSilverRate;
          }
          // TL bazlı - direkt remaining_amount kullan
          return Number(card.remaining_amount);
        };

        const totalCredit = customerScrapCards.reduce((sum, c) => sum + calculateCurrentTLValue(c), 0);

        // Gram bilgisi varsa göster (remaining_amount gram cinsinden)
        const gramInfo = customerScrapCards.filter(c => c.debt_type === 'GRAM' || c.debt_type === 'GUMUS');
        const gramText = gramInfo.length > 0
          ? gramInfo.map(c => `${Number(c.remaining_amount).toFixed(2)}gr ${c.debt_type === 'GRAM' ? 'Altın' : 'Gümüş'}`).join(', ')
          : null;

        // Tıklanabilir toast bildirimi
        toast.success(
          ({ closeToast }) => (
            <div
              style={{ cursor: 'pointer' }}
              onClick={() => {
                applyScrapDebtCards(customerScrapCards, totalCredit);
                closeToast();
              }}
            >
              <strong>🏷️ Hurda İndirimi Bulundu!</strong><br />
              {gramText && <><span style={{ fontSize: '0.85em' }}>({gramText})</span><br /></>}
              {customer.name} adlı müşterinin <strong>₺{totalCredit.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</strong> tutarında hurda indirimi var.<br />
              <span style={{ fontSize: '0.85em', color: '#a7f3d0' }}>Uygulamak için tıklayın</span>
            </div>
          ),
          { autoClose: 15000, closeOnClick: false }
        );
      }
    } catch (error) {
      console.error('Hurda borcu kontrol hatası:', error);
      setCustomerScrapDebtCards([]);
    }

    // Müşteri TL bakiye (alacak) kontrolü - customer_balance_transactions üzerinden
    try {
      const balanceRes = await fetch(`${API_URL}/customers/${customer.id}/debts`);
      const balanceData = await balanceRes.json();
      const tlBalance = balanceData.tl_balance ?? balanceData.balance ?? 0;

      if (tlBalance < -0.01 && !isExchangeMode) {
        const creditAmount = Math.abs(tlBalance);
        toast.success(
          ({ closeToast }) => (
            <div
              style={{ cursor: 'pointer' }}
              onClick={() => {
                applyCustomerCredit(creditAmount);
                closeToast();
              }}
            >
              <strong>Müşteri Bakiyesi Bulundu!</strong><br />
              {customer.name} adlı müşterinin <strong>₺{creditAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</strong> bakiyesi var.<br />
              <span style={{ fontSize: '0.85em', color: '#a7f3d0' }}>Uygulamak için tıklayın</span>
            </div>
          ),
          { autoClose: 12000, closeOnClick: false }
        );
      }
    } catch (error) {
      console.error('Müşteri bakiye kontrol hatası:', error);
    }
    setCustomerGiftCards([]);
  };

  // Müşteri bakiyesi uygula (customer_balance_transactions üzerinden)
  const applyCustomerCredit = (creditAmount) => {
    if (!creditAmount || creditAmount <= 0) return;
    if (isExchangeMode) return;
    setAppliedCustomerGiftCard({ creditAmount });
    toast.success(`₺${creditAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} bakiye sepete uygulandı.`, { autoClose: 4500 });
  };
  const removeCustomerGiftCard = () => {
    setAppliedCustomerGiftCard(null);
    toast.info('Müşteri bakiyesi kaldırıldı', { autoClose: 3000 });
  };

  // Hurda indirimini uygula
  const applyScrapDebtCards = (cards, totalCredit) => {
    if (!cards || cards.length === 0) return;
    if (isExchangeMode) {
      toast.warning('Değişim işleminde hurda indirimi kullanılamaz!');
      return;
    }

    // Gram bazlı kartları işaretle
    const gramCards = cards.filter(c => c.amount_gram && (c.amount_unit === 'GRAM' || c.amount_unit === 'GUMUS'));
    const gramInfo = gramCards.map(c => ({
      gram: parseFloat(c.amount_gram),
      unit: c.amount_unit,
      code: c.code
    }));

    // Kartları ve toplam alacağı kaydet - gerçek indirim tutarı dinamik hesaplanacak
    const mainCard = {
      cards: cards,
      totalCredit: totalCredit, // Anlık hesaplanan TL değeri
      codes: cards.map(c => c.code).join(', '),
      gramInfo: gramInfo // Gram bilgileri - kur değişince yeniden hesaplanabilir
    };

    setAppliedScrapDebtCard(mainCard);

    // Gram bilgisi varsa göster
    const gramText = gramInfo.length > 0
      ? ` (${gramInfo.map(g => `${g.gram.toFixed(2)}gr ${g.unit === 'GRAM' ? 'Altın' : 'Gümüş'}`).join(', ')})`
      : '';

    // İlk mesajı göster - gerçek tutar sepete ürün eklenince hesaplanacak
    toast.success(`🏷️ Hurda indirimi aktif edildi!${gramText} ₺${totalCredit.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} tutarında indirim sepete uygulanacak.`, { autoClose: 6000 });
  };

  // Hurda indirimi kaldır
  const removeScrapDebtCard = () => {
    setAppliedScrapDebtCard(null);
    toast.info('Hurda indirimi kaldırıldı', { autoClose: 3000 });
  };

  // Yeni müşteri oluştur
  const createCustomer = async () => {
    if (!newCustomer.name) {
      toast.warning('Müşteri adı gerekli!');
      return;
    }

    if (newCustomer.phone && !isPhoneComplete(newCustomer.phone)) {
      toast.warning('Telefon numarası 11 haneli olmalı (0 dahil)');
      return;
    }

    // KVKK onayı kontrolü
    if (!newCustomer.kvkk_consent) {
      toast.warning('Devam etmek için KVKK metnini onaylamanız gerekmektedir!');
      return;
    }

    try {
      const storeType = getStoreTypeForCustomer();
      const customerData = { ...newCustomer, store_type: storeType };
      const response = await fetch(`${API_URL}/customers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(customerData)
      });
      const data = await response.json();
      if (data.success) {
        setSelectedCustomer(data.customer);
        setShowCustomerModal(false);
        setNewCustomer({ name: '', phone: '', email: '', address: '', tax_number: '', birthday: '', sms_consent: false, kvkk_consent: false });
        setPhoneMatchCustomers([]);
      } else {
        toast.error(data.message || 'Müşteri oluşturulamadı!');
      }
    } catch (error) {
      console.error('Müşteri oluşturma hatası:', error);
      toast.error('Müşteri oluşturulamadı!');
    }
  };

  // Telefon numarasına göre müşteri ara
  const searchCustomerByPhone = async (phone) => {
    const cleanPhone = phone.replace(/\D/g, ''); // Sadece rakamları al
    if (cleanPhone.length < 3) {
      setPhoneMatchCustomers([]);
      return;
    }

    try {
      const storeType = getStoreTypeForCustomer();
      const params = new URLSearchParams({ search: cleanPhone });
      if (storeType) params.append('storeType', storeType);
      const response = await fetch(`${API_URL}/customers?${params}`);
      const data = await response.json();
      const customers = data.customers || data || [];
      if (Array.isArray(customers)) {
        // Telefon numarasına göre filtrele
        const matches = customers.filter(c =>
          c.phone && c.phone.replace(/\D/g, '').includes(cleanPhone)
        );
        setPhoneMatchCustomers(matches.slice(0, 5)); // Max 5 sonuç
      }
    } catch (error) {
      console.error('Telefon arama hatası:', error);
      setPhoneMatchCustomers([]);
    }
  };

  // Eşleşen müşteriyi seç
  const selectPhoneMatchCustomer = (customer) => {
    setShowCustomerModal(false);
    setNewCustomer({ name: '', phone: '', email: '', address: '', tax_number: '', birthday: '', sms_consent: false, kvkk_consent: false });
    setPhoneMatchCustomers([]);
    toast.success(`${customer.name} müşterisi seçildi`);
    // "Müşteri ara"daki gibi borç/alacak/hurda/bakiye uyarılarını da çalıştır
    selectCustomerWithDebtCheck(customer);
  };

  // Müşteri kaldır
  const removeSelectedCustomer = () => {
    if (isCartLockedByApproval && selectedCustomer && !selectedCustomer.isAnonymous) {
      toast.warning('Onaylı indirim varken müşteri kaldırılamaz!');
      return;
    }
    setSelectedCustomer(null);
    setSelectedCustomerDebt(null);
    // Hurda alacağı bilgilerini temizle
    setCustomerScrapDebtCards([]);
    setAppliedScrapDebtCard(null);
    // Müşteriye özel bakiye bilgilerini temizle
    setCustomerGiftCards([]);
    setAppliedCustomerGiftCard(null);
      };

  // ===== TRANSFER FONKSİYONLARI =====

  // Transfer ile ilgili TÜM state'leri sıfırla (sayfa yenilenmiş gibi temiz).
  // Hem modal kapatılırken hem de menüye geri dönülürken kullanılır.
  const resetAllTransferState = () => {
    setTransferMode(null);
    setTransferItems([]);
    setTransferBarcode('');
    setTransferNotes('');
    setTransferTargetStore('');
    setSelectedTransferDetail(null);
    setRequestItems([]);
    setRequestProductSearch('');
    setRequestSearchResults([]);
    setRequestNotes('');
    setRequestSourceStore('');
    setSelectedRequestDetail(null);
    setSelectedIncomingRequestDetail(null);
    setLastScannedTransferProduct(null);
    setTransferStaffId('');
    setTransferStaffName('');
    setEditingTransfer(false);
    setEditingTransferItems([]);
    // Gelen transfer talepleri
    setIncomingRequestDetailItems([]);
    setIncomingRequestBarcodeInput('');
    setIncomingRequestStaffId('');
    setIncomingRequestStaffName('');
    setIncomingRequestsTab('pending');
    setSelectedHistoryIncomingRequest(null);
    // Transfer teslim alma
    setSelectedTransferForReceive(null);
    setTransferDetailItems([]);
    setReceiveBarcodeInput('');
    // Hızlı barkod teslim
    setQuickTransferBarcode('');
    setQuickTransferItems([]);
    setShowQuickTransferSummary(false);
    setQuickTransferSummaryData(null);
  };

  // Transfer modalını gerçekten kapat (temizle)
  const doCloseTransferModal = () => {
    setClosingModal('transfers');
    setTimeout(() => {
      setShowTransfersModal(false);
      setClosingModal(null);
      resetAllTransferState();
    }, 250);
  };

  // Transfer modalını kapat (onay ile)
  const closeTransferModal = () => {
    if (transferItems.length > 0) {
      showConfirmation({
        title: 'Kaydedilmemiş Transfer',
        message: 'Kaydedilmemiş transfer ürünleri var. Çıkmak istediğinize emin misiniz?',
        confirmText: 'Evet, Çık',
        cancelText: 'Hayır, Kal',
        type: 'warning',
        onConfirm: () => {
          if (requestItems.length > 0) {
            showConfirmation({
              title: 'Kaydedilmemiş Talep',
              message: 'Kaydedilmemiş talep ürünleri var. Çıkmak istediğinize emin misiniz?',
              confirmText: 'Evet, Çık',
              cancelText: 'Hayır, Kal',
              type: 'warning',
              onConfirm: doCloseTransferModal
            });
          } else {
            doCloseTransferModal();
          }
        }
      });
      return;
    }
    if (requestItems.length > 0) {
      showConfirmation({
        title: 'Kaydedilmemiş Talep',
        message: 'Kaydedilmemiş talep ürünleri var. Çıkmak istediğinize emin misiniz?',
        confirmText: 'Evet, Çık',
        cancelText: 'Hayır, Kal',
        type: 'warning',
        onConfirm: doCloseTransferModal
      });
      return;
    }
    doCloseTransferModal();
  };

  // Transfer için barkod okut
  const handleTransferBarcode = async (e) => {
    if (e.key !== 'Enter' || !transferBarcode.trim()) return;

    const barcode = transferBarcode.trim();
    setTransferBarcode('');

    const storeId = selectedStore?.id || user?.store_id;
    if (!storeId) {
      playCartSound('error');
      toast.error('Mağaza seçili değil');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/transfers/product/barcode/${barcode}?store_id=${storeId}`);

      if (response.ok) {
        const product = await response.json();

        if (!product.stock_quantity || product.stock_quantity <= 0) {
          playCartSound('error');
          toast.error('Bu ürün seçili mağazada stokta yok');
          return;
        }

        setLastScannedTransferProduct({ product_id: product.id, name: product.name, barcode: product.barcode, image: product.image });

        // Ürün zaten listede mi?
        const existingIndex = transferItems.findIndex(item => item.product_id === product.id);

        if (existingIndex >= 0) {
          // Miktar artır ve en üste taşı
          const existingItem = transferItems[existingIndex];
          if (existingItem.quantity < product.stock_quantity) {
            const newItems = [...transferItems];
            newItems.splice(existingIndex, 1);
            newItems.unshift({ ...existingItem, quantity: existingItem.quantity + 1 });
            setTransferItems(newItems);
            playCartSound('success');
          } else {
            playCartSound('error');
            toast.warning('Stokta bu kadar ürün yok');
          }
        } else {
          // Yeni ürünü en üste ekle
          setTransferItems([{
            product_id: product.id,
            product_name: product.name,
            barcode: product.barcode,
            image: product.image,
            quantity: 1,
            stock_quantity: product.stock_quantity
          }, ...transferItems]);
          playCartSound('success');
        }
      } else {
        playCartSound('error');
        toast.error('Ürün bulunamadı');
      }
    } catch (error) {
      console.error('Barkod arama hatası:', error);
      playCartSound('error');
      toast.error('Ürün aranamadı');
    }
  };

  // Transfer ürün miktarını değiştir
  const updateTransferItemQuantity = (index, newQty) => {
    const newItems = [...transferItems];
    const maxQty = newItems[index].stock_quantity;

    if (newQty > maxQty) {
      toast.warning(`Maksimum ${maxQty} adet transfer edilebilir`);
      newQty = maxQty;
    }

    if (newQty < 1) {
      newItems.splice(index, 1);
    } else {
      newItems[index].quantity = newQty;
    }
    setTransferItems(newItems);
  };

  // Transfer ürününü kaldır
  const removeTransferItem = (index) => {
    const newItems = [...transferItems];
    newItems.splice(index, 1);
    setTransferItems(newItems);
  };

  // Transfer oluştur
  const createTransfer = async () => {
    const storeId = selectedStore?.id || user?.store_id;
    if (!storeId) {
      toast.error('Mağaza seçili değil');
      return;
    }

    if (transferItems.length === 0) {
      toast.error('En az bir ürün ekleyin');
      return;
    }

    if (!transferTargetStore) {
      toast.error('Hedef mağaza seçin');
      return;
    }

    if (!transferStaffId) {
      toast.error('Lütfen kasiyer seçin');
      return;
    }

    setSavingTransfer(true);

    try {
      const response = await fetch(`${API_URL}/transfers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          from_store_id: storeId,
          to_store_id: transferTargetStore,
          transfer_type: 'store',
          items: transferItems.map(item => ({
            product_id: item.product_id,
            quantity: item.quantity
          })),
          notes: transferNotes,
          created_by: user?.id,
          created_by_name: transferStaffName
        })
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(`Transfer oluşturuldu: ${result.transfer_code}`);
        doCloseTransferModal();
      } else {
        const err = await response.json();
        toast.error(err.error || 'Transfer oluşturulamadı');
      }
    } catch (error) {
      console.error('Transfer hatası:', error);
      toast.error('Transfer oluşturulurken hata oluştu');
    } finally {
      setSavingTransfer(false);
    }
  };

  // Gönderilen transferleri getir
  const fetchSentTransfers = async () => {
    const storeId = selectedStore?.id || user?.store_id;
    if (!storeId) return;

    setLoadingSentTransfers(true);
    try {
      const response = await fetch(`${API_URL}/transfers?from_store_id=${storeId}&limit=50`);
      if (response.ok) {
        const data = await response.json();
        setSentTransfers(data);
      }
    } catch (error) {
      console.error('Gönderilen transferler yüklenemedi:', error);
      toast.error('Transferler yüklenemedi');
    } finally {
      setLoadingSentTransfers(false);
    }
  };

  // Transfer detayını getir
  const fetchTransferDetail = async (transferId) => {
    try {
      const response = await fetch(`${API_URL}/transfers/${transferId}`);
      if (response.ok) {
        const data = await response.json();
        setSelectedTransferDetail(data);
      }
    } catch (error) {
      console.error('Transfer detayı yüklenemedi:', error);
      toast.error('Transfer detayı yüklenemedi');
    }
  };

  // Transfer talebi için ürün ara
  const searchRequestProducts = async (query) => {
    if (!query || query.length < 2) {
      setRequestSearchResults([]);
      return;
    }

    if (!requestSourceStore) {
      toast.warning('Önce kaynak mağaza seçin');
      return;
    }

    setSearchingRequestProducts(true);
    try {
      const response = await fetch(`${API_URL}/products/search?q=${encodeURIComponent(query)}&store_id=${requestSourceStore}&limit=10`);
      if (response.ok) {
        const data = await response.json();
        setRequestSearchResults(data.filter(p => p.stock_quantity > 0));
      }
    } catch (error) {
      console.error('Ürün arama hatası:', error);
    } finally {
      setSearchingRequestProducts(false);
    }
  };

  // Transfer talebine ürün ekle
  const addRequestItem = (product) => {
    setLastScannedTransferProduct({ name: product.name, barcode: product.barcode, image: product.image });
    const existingIndex = requestItems.findIndex(item => item.product_id === product.id);

    if (existingIndex >= 0) {
      const newItems = [...requestItems];
      if (newItems[existingIndex].quantity < product.stock_quantity) {
        newItems[existingIndex].quantity += 1;
        setRequestItems(newItems);
      } else {
        toast.warning('Stokta bu kadar ürün yok');
      }
    } else {
      setRequestItems([...requestItems, {
        product_id: product.id,
        product_name: product.name,
        barcode: product.barcode,
        image: product.image,
        quantity: 1,
        stock_quantity: product.stock_quantity
      }]);
    }
    setRequestProductSearch('');
    setRequestSearchResults([]);
  };

  // Transfer talebi için barkod ile ürün ara (Enter'a basınca)
  const handleRequestBarcodeSearch = async (e) => {
    if (e.key !== 'Enter' || !requestProductSearch.trim()) return;

    if (!requestSourceStore) {
      playCartSound('error');
      toast.warning('Önce kaynak mağaza seçin');
      return;
    }

    const barcode = requestProductSearch.trim();

    try {
      const response = await fetch(`${API_URL}/transfers/product/barcode/${barcode}?store_id=${requestSourceStore}`);

      if (response.ok) {
        const product = await response.json();

        if (!product.stock_quantity || product.stock_quantity <= 0) {
          playCartSound('error');
          toast.error('Bu ürün seçili mağazada stokta yok');
          return;
        }

        setLastScannedTransferProduct({ name: product.name, barcode: product.barcode, image: product.image });

        // Ürün zaten listede mi?
        const existingIndex = requestItems.findIndex(item => item.product_id === product.id);

        if (existingIndex >= 0) {
          const newItems = [...requestItems];
          if (newItems[existingIndex].quantity < product.stock_quantity) {
            newItems[existingIndex].quantity += 1;
            setRequestItems(newItems);
            playCartSound('success');
            toast.success(`${product.name} miktarı artırıldı`);
          } else {
            playCartSound('error');
            toast.warning('Stokta bu kadar ürün yok');
          }
        } else {
          setRequestItems([...requestItems, {
            product_id: product.id,
            product_name: product.name,
            barcode: product.barcode,
            image: product.image,
            quantity: 1,
            stock_quantity: product.stock_quantity
          }]);
          playCartSound('success');
          toast.success(`${product.name} eklendi`);
        }
        setRequestProductSearch('');
        setRequestSearchResults([]);
      } else {
        playCartSound('error');
        toast.error('Ürün bulunamadı');
      }
    } catch (error) {
      console.error('Barkod arama hatası:', error);
      playCartSound('error');
      toast.error('Ürün aranamadı');
    }
  };

  // Transfer talebi ürün miktarını güncelle
  const updateRequestItemQuantity = (index, newQty) => {
    const newItems = [...requestItems];
    const maxQty = newItems[index].stock_quantity;

    if (newQty > maxQty) {
      toast.warning(`Maksimum ${maxQty} adet talep edilebilir`);
      newQty = maxQty;
    }

    if (newQty < 1) {
      newItems.splice(index, 1);
    } else {
      newItems[index].quantity = newQty;
    }
    setRequestItems(newItems);
  };

  // Transfer talebi ürününü kaldır
  const removeRequestItem = (index) => {
    const newItems = [...requestItems];
    newItems.splice(index, 1);
    setRequestItems(newItems);
  };

  // Transfer talebi oluştur
  const createTransferRequest = async () => {
    const storeId = selectedStore?.id || user?.store_id;
    if (!storeId) {
      toast.error('Mağaza seçili değil');
      return;
    }

    if (requestItems.length === 0) {
      toast.error('En az bir ürün ekleyin');
      return;
    }

    if (!requestSourceStore) {
      toast.error('Kaynak mağaza seçin');
      return;
    }

    if (!transferStaffId) {
      toast.error('Lütfen kasiyer seçin');
      return;
    }

    setSavingRequest(true);

    try {
      const response = await fetch(`${API_URL}/transfer-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          from_store_id: storeId,
          to_store_id: requestSourceStore,
          items: requestItems.map(item => ({
            product_id: item.product_id,
            quantity: item.quantity
          })),
          notes: requestNotes,
          created_by: user?.id,
          staff_id: transferStaffId || null,
          staff_name: transferStaffName || null
        })
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(`Transfer talebi oluşturuldu: ${result.request_code || 'Başarılı'}`);
        // Reset form
        setTransferMode(null);
        setRequestItems([]);
        setRequestProductSearch('');
        setRequestNotes('');
        setRequestSourceStore('');
      } else {
        const err = await response.json();
        toast.error(err.message || 'Transfer talebi oluşturulamadı');
      }
    } catch (error) {
      console.error('Transfer talebi hatası:', error);
      toast.error('Transfer talebi oluşturulurken hata oluştu');
    } finally {
      setSavingRequest(false);
    }
  };

  // Gönderilen transfer taleplerini getir
  const fetchSentRequests = async () => {
    const storeId = selectedStore?.id || user?.store_id;
    if (!storeId) return;

    setLoadingSentRequests(true);
    try {
      const response = await fetch(`${API_URL}/transfer-requests/outgoing/${storeId}`);
      if (response.ok) {
        const data = await response.json();
        if (Array.isArray(data)) {
          setSentRequests(data);
        }
      }
    } catch (error) {
      console.error('Gönderilen talepler yüklenemedi:', error);
      toast.error('Talepler yüklenemedi');
    } finally {
      setLoadingSentRequests(false);
    }
  };

  // Transfer talebi detayını getir
  const fetchRequestDetail = async (requestId) => {
    try {
      const response = await fetch(`${API_URL}/transfer-requests/${requestId}`);
      if (response.ok) {
        const data = await response.json();
        setSelectedRequestDetail(data);
      }
    } catch (error) {
      console.error('Talep detayı yüklenemedi:', error);
      toast.error('Talep detayı yüklenemedi');
    }
  };

  // Transfer iptal et
  const cancelTransfer = (transferId) => {
    showConfirmation({
      title: 'Transfer İptali',
      message: 'Bu transferi iptal etmek istediğinize emin misiniz? Stoklar geri yüklenecektir.',
      confirmText: 'İptal Et',
      cancelText: 'Vazgeç',
      type: 'warning',
      onConfirm: async () => {
        setCancellingTransfer(true);
        try {
          const response = await fetch(`${API_URL}/transfers/${transferId}/cancel`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' }
          });
          if (response.ok) {
            toast.success('Transfer iptal edildi');
            setSelectedTransferDetail(prev => prev ? { ...prev, status: 'cancelled' } : null);
            fetchSentTransfers();
          } else {
            const data = await response.json();
            toast.error(data.error || 'Transfer iptal edilemedi');
          }
        } catch (error) {
          console.error('Transfer iptal hatası:', error);
          toast.error('Transfer iptal edilemedi');
        } finally {
          setCancellingTransfer(false);
        }
      }
    });
  };

  // Transfer düzenleme başlat
  const startEditingTransfer = async (transfer) => {
    try {
      const productIds = transfer.items.map(i => i.product_id);
      const response = await fetch(`${API_URL}/store-stocks/bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ store_id: transfer.from_store_id, product_ids: productIds })
      });
      const stockMap = await response.json();
      const itemsWithStock = transfer.items.map(i => ({
        ...i,
        source_stock: stockMap[i.product_id] || 0,
        original_quantity: i.quantity
      }));
      setEditingTransferItems(itemsWithStock);
      setEditingTransfer(true);
    } catch (error) {
      console.error('Stok bilgisi alınamadı:', error);
      setEditingTransferItems(transfer.items.map(i => ({ ...i, source_stock: null, original_quantity: i.quantity })));
      setEditingTransfer(true);
    }
  };

  // Transfer düzenleme kaydet
  const saveTransferEdit = async (transferId) => {
    setSavingTransferEdit(true);
    try {
      const items = editingTransferItems.map(item => ({
        item_id: item.id,
        quantity: item.quantity
      }));

      const response = await fetch(`${API_URL}/transfers/${transferId}/edit`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('Transfer güncellendi');
        setEditingTransfer(false);
        setEditingTransferItems([]);
        fetchSentTransfers();
        if (data.transfer) {
          setSelectedTransferDetail(data.transfer);
        }
      } else {
        toast.error(data.error || 'Transfer güncellenemedi');
      }
    } catch (error) {
      console.error('Transfer güncelleme hatası:', error);
      toast.error('Transfer güncellenemedi');
    } finally {
      setSavingTransferEdit(false);
    }
  };

  // Transfer talebi iptal et
  const cancelTransferRequest = (requestId) => {
    showConfirmation({
      title: 'Transfer Talebi İptali',
      message: 'Bu transfer talebini iptal etmek istediğinize emin misiniz?',
      confirmText: 'İptal Et',
      cancelText: 'Vazgeç',
      type: 'warning',
      onConfirm: async () => {
        setCancellingRequest(true);
        try {
          const response = await fetch(`${API_URL}/transfer-requests/${requestId}/cancel`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' }
          });
          if (response.ok) {
            toast.success('Transfer talebi iptal edildi');
            setSelectedRequestDetail(prev => prev ? { ...prev, status: 'cancelled' } : null);
            fetchSentRequests();
          } else {
            const data = await response.json();
            toast.error(data.error || 'Talep iptal edilemedi');
          }
        } catch (error) {
          console.error('Transfer talebi iptal hatası:', error);
          toast.error('Talep iptal edilemedi');
        } finally {
          setCancellingRequest(false);
        }
      }
    });
  };

  // Transfer geçmişini getir
  const fetchTransferHistory = async () => {
    const storeId = selectedStore?.id || user?.store_id;
    if (!storeId) return;

    setLoadingTransferHistory(true);
    try {
      const response = await fetch(`${API_URL}/transfers/history/${storeId}`);
      if (response.ok) {
        const data = await response.json();
        if (Array.isArray(data)) {
          setTransferHistory(data);
        }
      }
    } catch (error) {
      console.error('Transfer geçmişi yüklenemedi:', error);
      toast.error('Transfer geçmişi yüklenemedi');
    } finally {
      setLoadingTransferHistory(false);
    }
  };

  // Gelen transfer taleplerini getir
  const fetchIncomingRequests = async () => {
    const storeId = selectedStore?.id || user?.store_id;
    if (!storeId) return;

    setLoadingIncomingRequests(true);
    try {
      const response = await fetch(`${API_URL}/transfer-requests/incoming/${storeId}`);
      if (response.ok) {
        const data = await response.json();
        if (Array.isArray(data)) {
          setIncomingRequests(data);
        }
      }
    } catch (error) {
      console.error('Gelen talepler yüklenemedi:', error);
      toast.error('Talepler yüklenemedi');
    } finally {
      setLoadingIncomingRequests(false);
    }
  };

  // Gelen taleplerin geçmişini getir (pending olmayanlar)
  const fetchIncomingRequestsHistory = async () => {
    const storeId = selectedStore?.id || user?.store_id;
    if (!storeId) return;
    setLoadingIncomingHistory(true);
    try {
      const response = await fetch(`${API_URL}/transfer-requests/incoming/${storeId}/history?limit=100`);
      if (response.ok) {
        const data = await response.json();
        setIncomingRequestsHistory(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Gelen talep geçmişi yüklenemedi:', error);
      toast.error('Geçmiş yüklenemedi');
    } finally {
      setLoadingIncomingHistory(false);
    }
  };

  // Gelen talep detayını getir (talep seçildiğinde)
  const fetchIncomingRequestDetail = async (request) => {
    setSelectedIncomingRequestDetail(request);
    try {
      const response = await fetch(`${API_URL}/transfer-requests/${request.id}`);
      if (response.ok) {
        const data = await response.json();
        if (data.items) {
          setIncomingRequestDetailItems(data.items.map(item => ({
            ...item,
            approved_quantity: 0
          })));
        }
        setTimeout(() => incomingRequestBarcodeRef.current?.focus(), 100);
      }
    } catch (error) {
      console.error('Talep detayı yüklenemedi:', error);
      toast.error('Talep detayı yüklenemedi');
    }
  };

  // Gelen talep ürün miktarını güncelle
  const updateIncomingRequestItemQuantity = (index, newQty) => {
    const newItems = [...incomingRequestDetailItems];
    const item = newItems[index];
    const maxQty = Math.min(item.requested_quantity, item.available_stock || 0);
    if (newQty < 0) newQty = 0;
    if (newQty > maxQty) {
      if (maxQty < item.requested_quantity) {
        toast.warning(`Stok yetersiz! Mevcut stok: ${item.available_stock || 0}`);
      }
      newQty = maxQty;
    }
    item.approved_quantity = newQty;
    setIncomingRequestDetailItems(newItems);
  };

  // Gelen talep için barkod okutma (form submit)
  const handleIncomingRequestBarcode = (e) => {
    e.preventDefault();
    const barcode = incomingRequestBarcodeInput.trim();
    if (!barcode) return;

    const itemIndex = incomingRequestDetailItems.findIndex(item => item.barcode === barcode);

    if (itemIndex === -1) {
      playCartSound('error');
      toast.error('Bu barkod talep listesinde bulunamadı!');
      setIncomingRequestBarcodeInput('');
      return;
    }

    const item = incomingRequestDetailItems[itemIndex];

    if (item.approved_quantity >= item.requested_quantity) {
      playCartSound('error');
      toast.warning(`Bu ürünün tamamı zaten okutuldu (${item.requested_quantity}/${item.requested_quantity})`);
      setIncomingRequestBarcodeInput('');
      return;
    }

    if (item.approved_quantity >= (item.available_stock || 0)) {
      playCartSound('error');
      toast.warning(`Stok yetersiz! Mevcut stok: ${item.available_stock || 0}`);
      setIncomingRequestBarcodeInput('');
      return;
    }

    const updatedItems = [...incomingRequestDetailItems];
    updatedItems[itemIndex] = { ...item, approved_quantity: item.approved_quantity + 1 };
    setIncomingRequestDetailItems(updatedItems);

    playCartSound('success');
    toast.success(`${item.product_name} eklendi (${updatedItems[itemIndex].approved_quantity}/${item.requested_quantity})`);
    setIncomingRequestBarcodeInput('');
  };

  // Tümünü ekle (stok ve talep miktarına göre)
  const fillAllIncomingRequestApproval = () => {
    const updatedItems = incomingRequestDetailItems.map(item => ({
      ...item,
      approved_quantity: Math.min(item.requested_quantity, item.available_stock || 0)
    }));
    setIncomingRequestDetailItems(updatedItems);
    const totalApproved = updatedItems.reduce((sum, item) => sum + item.approved_quantity, 0);
    toast.success(`Tüm ürünler eklendi (${totalApproved} adet)`);
  };

  // Gelen talebi onayla ve transfer oluştur (API üzerinden)
  const startApproveIncomingRequest = () => {
    if (!selectedIncomingRequestDetail) return;
    const approvedItems = incomingRequestDetailItems.filter(item => item.approved_quantity > 0);
    if (approvedItems.length === 0) {
      toast.warning('En az bir ürün okutun veya manuel ekleyin!');
      return;
    }
    setShowApproveStaffModal(true);
  };

  const approveIncomingRequest = async (staffId, staffName) => {
    if (!selectedIncomingRequestDetail) return;

    const totalApproved = incomingRequestDetailItems.filter(item => item.approved_quantity > 0).reduce((sum, item) => sum + item.approved_quantity, 0);

    setProcessingRequest(true);
    try {
      const response = await fetch(`${API_URL}/transfer-requests/${selectedIncomingRequestDetail.id}/approve`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          approved_by: user?.id,
          staff_id: staffId,
          staff_name: staffName,
          items: incomingRequestDetailItems.map(item => ({
            product_id: item.product_id,
            approved_quantity: item.approved_quantity || 0
          }))
        })
      });

      if (response.ok) {
        playCartSound('success');
        toast.success(`Transfer onaylandı ve gönderildi (${totalApproved} adet)`);
        setSelectedIncomingRequestDetail(null);
        setIncomingRequestDetailItems([]);
        setIncomingRequestBarcodeInput('');
        await fetchIncomingRequests();
      } else {
        const errorData = await response.json().catch(() => ({}));
        toast.error(errorData.error || 'Onaylama başarısız');
      }
    } catch (error) {
      console.error('Onaylama hatası:', error);
      toast.error('Onaylama başarısız');
    } finally {
      setProcessingRequest(false);
    }
  };

  // Gelen talebi reddet → modalı aç
  const rejectIncomingRequest = () => {
    if (!selectedIncomingRequestDetail) return;
    setRejectRequestReason('');
    setRejectRequestStaffId(null);
    setRejectRequestStaffName('');
    setShowRejectRequestModal(true);
  };

  // Modaldan reddi onayla
  const confirmRejectIncomingRequest = async () => {
    if (!selectedIncomingRequestDetail) return;
    if (!rejectRequestReason.trim()) {
      toast.warning('Lütfen reddetme sebebini yazın');
      return;
    }
    if (!rejectRequestStaffId) {
      toast.warning('Lütfen kasiyer seçin');
      return;
    }

    setProcessingRequest(true);
    try {
      const response = await fetch(`${API_URL}/transfer-requests/${selectedIncomingRequestDetail.id}/reject`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          rejection_reason: rejectRequestReason.trim(),
          rejected_by: user?.id,
          rejected_staff_id: rejectRequestStaffId,
          rejected_staff_name: rejectRequestStaffName
        })
      });

      if (response.ok) {
        toast.success('Talep reddedildi');
        setShowRejectRequestModal(false);
        setSelectedIncomingRequestDetail(null);
        setIncomingRequestDetailItems([]);
        setIncomingRequestBarcodeInput('');
        await fetchIncomingRequests();
      } else {
        toast.error('Reddetme başarısız');
      }
    } catch (error) {
      console.error('Talep reddetme hatası:', error);
      toast.error('Reddetme başarısız');
    } finally {
      setProcessingRequest(false);
    }
  };

  // ===== TRANSFER TESLİM ALMA FONKSİYONLARI =====

  // Bekleyen transferleri getir
  const fetchPendingTransfers = async () => {
    const storeId = selectedStore?.id || user?.store_id;
    if (!storeId) return;

    setLoadingPendingTransfers(true);
    try {
      const response = await fetch(`${API_URL}/transfers/pending/${storeId}`);
      if (response.ok) {
        const data = await response.json();
        setPendingTransfers(data);
      }
    } catch (error) {
      console.error('Transfer getirme hatası:', error);
    } finally {
      setLoadingPendingTransfers(false);
    }
  };

  // Transfer detayını göster
  const selectTransferForReceive = async (transfer) => {
    setSelectedTransferForReceive(transfer);
    setReceiveBarcodeInput('');
    setLastScannedTransferProduct(null);

    try {
      const response = await fetch(`${API_URL}/transfers/${transfer.id}`);
      if (response.ok) {
        const data = await response.json();
        setSelectedTransferForReceive(data);
        // already_received: DB'den gelen daha önce teslim alınan miktar
        // received_quantity: bu seansta barkod ile okutulan yeni miktar (0'dan başlar)
        const itemsWithReceived = (data.items || []).map(item => ({
          ...item,
          already_received: item.received_quantity || 0,
          received_quantity: 0
        }));
        setTransferDetailItems(itemsWithReceived);
        // Barkod inputuna focus
        setTimeout(() => {
          receiveBarcodeInputRef.current?.focus();
        }, 100);
      }
    } catch (error) {
      console.error('Transfer detay hatası:', error);
    }
  };

  // Transfer için barkod okutma
  const handleReceiveBarcodeSubmit = (e) => {
    e.preventDefault();
    const barcode = receiveBarcodeInput.trim();
    if (!barcode) return;

    // Barkoda göre ürün bul
    const itemIndex = transferDetailItems.findIndex(item => item.barcode === barcode);

    if (itemIndex === -1) {
      playCartSound('error');
      toast.error('Bu barkod transferde bulunamadı!');
      setReceiveBarcodeInput('');
      return;
    }

    const item = transferDetailItems[itemIndex];
    const remaining = item.quantity - (item.already_received || 0);

    setLastScannedTransferProduct({ name: item.product_name, barcode: item.barcode, image: item.image });

    // Zaten tamamı teslim alındı mı kontrol et
    if (item.received_quantity >= remaining) {
      playCartSound('error');
      toast.warning(`Bu ürünün tamamı zaten teslim alındı (${(item.already_received || 0) + item.received_quantity}/${item.quantity})`);
      setReceiveBarcodeInput('');
      return;
    }

    // Teslim alınan miktarı artır ve en üste taşı
    const updatedItems = [...transferDetailItems];
    const [updatedItem] = updatedItems.splice(itemIndex, 1);
    updatedItem.received_quantity += 1;
    updatedItems.unshift(updatedItem);
    setTransferDetailItems(updatedItems);

    playCartSound('success');
    const totalSoFar = (item.already_received || 0) + updatedItem.received_quantity;
    toast.success(`${item.product_name} teslim alındı (${totalSoFar}/${item.quantity})`);
    setReceiveBarcodeInput('');
  };

  // Hızlı barkod ile transfer ürünü ara ve listeye ekle (henüz teslim almadan)
  const handleQuickTransferBarcode = async (e) => {
    if (e.key !== 'Enter' || !quickTransferBarcode.trim()) return;

    const barcode = quickTransferBarcode.trim();
    setQuickTransferBarcode('');

    const storeId = selectedStore?.id || user?.store_id;
    if (!storeId) {
      playCartSound('error');
      toast.error('Mağaza seçilmedi');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/transfers/find-by-barcode?barcode=${barcode}&store_id=${storeId}`);
      const data = await response.json();

      if (response.ok && data.found) {
        playCartSound('success');

        setQuickTransferItems(prev => {
          const key = `${data.transfer.id}-${data.product.id}`;
          const existingIndex = prev.findIndex(item => item.key === key);

          if (existingIndex >= 0) {
            const updated = [...prev];
            const item = updated[existingIndex];
            if (item.scannedQty < item.expectedQty) {
              updated[existingIndex] = {
                ...item,
                scannedQty: item.scannedQty + 1
              };
              toast.success(`${data.product.name} (${updated[existingIndex].scannedQty}/${item.expectedQty})`);
            } else {
              toast.warning(`Bu ürünün tamamı zaten okutuldu (${item.expectedQty}/${item.expectedQty})`);
            }
            return updated;
          }

          toast.success(`${data.product.name} eklendi - ${data.transfer.code}`);
          return [...prev, {
            key,
            transfer: data.transfer,
            product: data.product,
            expectedQty: data.expectedQty,
            scannedQty: 1,
            alreadyReceived: data.alreadyReceived
          }];
        });
      } else {
        playCartSound('error');
        toast.error(data.error || 'Ürün bekleyen transferlerde bulunamadı');
      }
    } catch (error) {
      console.error('Hızlı barkod arama hatası:', error);
      playCartSound('error');
      toast.error('Arama başarısız');
    }

    setTimeout(() => {
      quickTransferBarcodeRef.current?.focus();
    }, 100);
  };

  // Hızlı teslim için özet hazırla
  const prepareQuickTransferSummary = async () => {
    if (quickTransferItems.length === 0) {
      toast.warning('Önce ürün okutun!');
      return;
    }

    toast.info('Özet hazırlanıyor...');

    // Transferlere göre grupla
    const transferGroups = {};
    for (const item of quickTransferItems) {
      const tid = item.transfer.id;
      if (!transferGroups[tid]) {
        transferGroups[tid] = {
          transfer: item.transfer,
          scannedItems: [],
          missingItems: []
        };
      }
      transferGroups[tid].scannedItems.push(item);
    }

    // Her transfer için eksik ürünleri bul
    try {
      for (const tid of Object.keys(transferGroups)) {
        const response = await fetch(`${API_URL}/transfers/${tid}`);
        if (response.ok) {
          const transferData = await response.json();
          const scannedProductIds = transferGroups[tid].scannedItems.map(i => i.product.id);

          const missing = (transferData.items || []).filter(item => {
            const remaining = item.quantity - (item.received_quantity || 0);
            return remaining > 0 && !scannedProductIds.includes(item.product_id);
          }).map(item => ({
            product_id: item.product_id,
            product_name: item.product_name,
            barcode: item.barcode,
            image: item.image,
            expectedQty: item.quantity - (item.received_quantity || 0)
          }));

          transferGroups[tid].missingItems = missing;
        }
      }
    } catch (error) {
      console.error('Transfer detay hatası:', error);
      toast.error('Özet hazırlanamadı');
      return;
    }

    setQuickTransferSummaryData(Object.values(transferGroups));
    setShowQuickTransferSummary(true);
  };

  // Toplu teslim al - kasiyer seçim modalını göster
  const confirmQuickTransfer = async () => {
    if (!quickTransferItems.length) return;

    setReceiveStaffId('');
    setReceiveStaffName('');
    setPendingReceiveAction('bulk');
    setShowReceiveStaffModal(true);
  };

  // Toplu teslim al - asıl işlem (kasiyer seçildikten sonra)
  const doConfirmQuickTransfer = async () => {
    setSavingQuickTransfer(true);
    const storeId = selectedStore?.id || user?.store_id;

    try {
      const response = await fetch(`${API_URL}/transfers/bulk-receive`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          store_id: storeId,
          received_by: user?.id,
          received_by_name: receiveStaffName,
          items: quickTransferItems.map(item => ({
            transfer_id: item.transfer.id,
            product_id: item.product.id,
            quantity: item.scannedQty
          }))
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast.success(`${data.totalReceived} ürün teslim alındı!`);
        setQuickTransferItems([]);
        setShowQuickTransferSummary(false);
        setQuickTransferSummaryData(null);
        fetchPendingTransfers();
      } else {
        toast.error(data.error || 'Teslim alma başarısız');
      }
    } catch (error) {
      console.error('Toplu teslim hatası:', error);
      toast.error('Teslim alma başarısız');
    }

    setSavingQuickTransfer(false);
  };

  // Hızlı teslim listesinden ürün çıkar
  const removeQuickTransferItem = (key) => {
    setQuickTransferItems(prev => prev.filter(item => item.key !== key));
  };

  // Hızlı teslim ürün miktarını değiştir
  const updateQuickTransferItemQty = (key, newQty) => {
    setQuickTransferItems(prev => prev.map(item => {
      if (item.key === key) {
        const maxQty = item.expectedQty;
        const qty = Math.max(1, Math.min(newQty, maxQty));
        return { ...item, scannedQty: qty };
      }
      return item;
    }));
  };

  // Transfer teslim al (asıl işlem)
  const doReceiveTransfer = async () => {
    const totalReceived = transferDetailItems.reduce((sum, item) => sum + (item.received_quantity || 0), 0);
    try {
      const response = await fetch(`${API_URL}/transfers/${selectedTransferForReceive.id}/receive`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          received_by: user?.id,
          received_by_name: receiveStaffName,
          items: transferDetailItems.map(item => ({
            product_id: item.product_id,
            received_quantity: item.received_quantity || 0
          }))
        })
      });

      if (response.ok) {
        const data = await response.json();

        // Eksi stoklu ürünler varsa uyarı göster
        if (data.hasNegativeStock && data.negativeStockItems?.length > 0) {
          const itemsList = data.negativeStockItems.map(item =>
            `• ${item.product_name} (${item.barcode || 'Barkod yok'})\n  Önceki stok: ${item.stock_before}, Şu anki stok: ${item.stock_after}`
          ).join('\n');

          toast.warning(
            <div>
              <strong>Dikkat: Müşteriye Satılmış Ürünler!</strong>
              <br /><br />
              Aşağıdaki ürünler daha önce müşteriye satılmış ve stok eksi durumdaydı:
              <br /><br />
              <pre style={{ fontSize: '12px', whiteSpace: 'pre-wrap' }}>{itemsList}</pre>
              <br />
              Bu ürünler müşteriye teslim edilmeyi bekliyor olabilir.
            </div>,
            {
              autoClose: false,
              closeOnClick: false,
              draggable: false,
              style: { width: '400px' }
            }
          );
        }

        if (data.allFullyReceived) {
          toast.success(`Transfer teslim alındı! (${totalReceived} ürün)`);
        } else {
          toast.success(`Kısmi teslim alındı (${totalReceived} ürün). Kalan ürünler bekliyor.`);
        }
        setSelectedTransferForReceive(null);
        setTransferDetailItems([]);
        setReceiveBarcodeInput('');
        await fetchPendingTransfers();

        if (data.allFullyReceived && pendingTransfers.length <= 1) {
          setTransferMode(null);
        }
      } else {
        const error = await response.json();
        toast.error(error.error || 'Teslim alma başarısız');
      }
    } catch (error) {
      console.error('Teslim alma hatası:', error);
      toast.error('Teslim alma başarısız');
    }
  };

  // Transfer teslim al (onay ile)
  const receiveTransfer = () => {
    if (!selectedTransferForReceive) return;

    // En az bir ürün teslim alınmış mı kontrol et
    const totalReceived = transferDetailItems.reduce((sum, item) => sum + (item.received_quantity || 0), 0);
    if (totalReceived === 0) {
      toast.warning('Lütfen önce barkod okutarak ürünleri teslim alın!');
      receiveBarcodeInputRef.current?.focus();
      return;
    }

    // Kasiyer seçim modalını göster
    setReceiveStaffId('');
    setReceiveStaffName('');
    setPendingReceiveAction('single');
    setShowReceiveStaffModal(true);
  };

  // Kasiyer seçildikten sonra teslim al
  const handleReceiveStaffConfirm = () => {
    if (!receiveStaffId) {
      toast.error('Lütfen teslim alan kasiyer seçin');
      return;
    }
    setShowReceiveStaffModal(false);

    if (pendingReceiveAction === 'bulk') {
      doConfirmQuickTransfer();
    } else {
      // Eksik teslim varsa onay iste
      const totalReceived = transferDetailItems.reduce((sum, item) => sum + (item.received_quantity || 0), 0);
      const totalRemaining = transferDetailItems.reduce((sum, item) => sum + item.quantity - (item.already_received || 0), 0);
      if (totalReceived < totalRemaining) {
        showConfirmation({
          title: 'Eksik Teslim Alma',
          message: `Dikkat: Kalan ${totalRemaining} üründen ${totalReceived} tanesi teslim alındı. Eksik teslim almak istediğinize emin misiniz?`,
          confirmText: 'Evet, Teslim Al',
          cancelText: 'Hayır, İptal',
          type: 'warning',
          onConfirm: doReceiveTransfer
        });
        return;
      }
      doReceiveTransfer();
    }
  };

  // ===== TADİLAT MODÜLÜ FONKSİYONLARI =====

  // Tamirat modalını aç
  const openRepairModal = async () => {
    // Kasiyer listesini yükle
    try {
      const staffRes = await fetch(`${API_URL}/sales-staff${currentStoreId ? `?store_id=${currentStoreId}` : ''}`);
      const staffData = await staffRes.json();
      if (Array.isArray(staffData)) setSalesStaffList(staffData);
    } catch (e) { /* ignore */ }

    // Eğer sepette seçili müşteri varsa, onu otomatik olarak seç
    if (selectedCustomer) {
      setRepairFormData(prev => ({
        ...prev,
        customer_id: selectedCustomer.id,
        customer_name: selectedCustomer.name,
        customer_phone: selectedCustomer.phone || ''
      }));
      setRepairCustomerMode('select');
    } else {
      resetRepairForm();
    }
    setShowRepairModal(true);
    fetchRepairCustomers();
  };

  // Tamirat formunu sıfırla
  const resetRepairForm = () => {
    setRepairFormData({
      customer_id: '',
      customer_name: '',
      customer_phone: '',
      items: [{
        item_type: storeMetalType,
        item_description: '',
        problem_description: '',
        photos: []
      }],
      estimated_price: '',
      price_type: 'normal',
      deposit_amount: '',
      deposit_payment_method: '',
      deposit_payment_details: [],
      estimated_delivery: '',
      deadline: '',
      notes: '',
      staff_name: ''
    });
    setRepairCustomerSearch('');
    setRepairCustomerMode('new');
    setRepairCustomerSelectedFromMatch(false);
    setRepairPhoneMatchCustomers([]);
  };

  // Tamirat ürünü ekle
  const addRepairItem = () => {
    setRepairFormData(prev => ({
      ...prev,
      items: [...prev.items, {
        item_type: storeMetalType,
        item_description: '',
        problem_description: '',
        photos: []
      }]
    }));
  };

  // Tamirat ürünü sil
  const removeRepairItem = (index) => {
    if (repairFormData.items.length <= 1) {
      toast.warning('En az bir ürün olmalı!');
      return;
    }
    setRepairFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  // Tamirat ürünü güncelle
  const updateRepairItem = (index, field, value) => {
    setRepairFormData(prev => ({
      ...prev,
      items: prev.items.map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  // Tamirat için müşteri listesini getir
  const fetchRepairCustomers = async (search = '') => {
    try {
      const storeType = getStoreTypeForCustomer();
      const params = new URLSearchParams({ page: '1', limit: '100' });
      if (search) params.append('search', search);
      if (storeType) params.append('storeType', storeType);
      const response = await fetch(`${API_URL}/customers?${params}`);
      const data = await response.json();
      setRepairCustomers(data.customers || data);
    } catch (error) {
      console.error('Müşteri yükleme hatası:', error);
    }
  };

  // Tamirat müşteri seç
  const selectRepairCustomer = (customer) => {
    setRepairFormData(prev => ({
      ...prev,
      customer_id: customer.id,
      customer_name: customer.name,
      customer_phone: customer.phone || ''
    }));
    setRepairCustomerSearch('');
    setShowRepairCustomerDropdown(false);
  };

  // Tamirat telefon numarası ile müşteri ara
  const searchRepairCustomerByPhone = async (phone) => {
    const cleanPhone = phone.replace(/\D/g, '');
    if (cleanPhone.length < 9) {
      setRepairPhoneMatchCustomers([]);
      return;
    }
    try {
      const response = await fetch(`${API_URL}/customers?search=${cleanPhone}`);
      const data = await response.json();
      const customers = data.customers || data || [];
      if (Array.isArray(customers)) {
        const matches = customers.filter(c => c.phone && c.phone.replace(/\D/g, '').includes(cleanPhone));
        setRepairPhoneMatchCustomers(matches.slice(0, 5));
      }
    } catch (error) {
      setRepairPhoneMatchCustomers([]);
    }
  };

  // Tamirat için yeni müşteri oluştur
  const createRepairCustomer = async () => {
    if (!repairFormData.customer_name) {
      toast.warning('Müşteri adı gerekli!');
      return;
    }
    if (repairFormData.customer_id) {
      toast.info('Müşteri zaten kaydedildi!');
      return;
    }
    if (repairFormData.customer_phone && !isPhoneComplete(repairFormData.customer_phone)) {
      toast.warning('Telefon numarası 11 haneli olmalı (0 dahil)');
      return;
    }

    try {
      // Önce aynı telefonla mevcut müşteri var mı bak; varsa onu seç, yenisini açma.
      const storeType = getStoreTypeForCustomer();
      const cleanPhone = (repairFormData.customer_phone || '').replace(/\D/g, '');
      if (cleanPhone.length >= 9) {
        try {
          const searchRes = await fetch(`${API_URL}/customers?search=${cleanPhone}`);
          const searchData = await searchRes.json();
          const list = searchData.customers || searchData || [];
          if (Array.isArray(list)) {
            const match = list.find(c => {
              if (!c.phone) return false;
              const cPhone = c.phone.replace(/\D/g, '');
              if (!cPhone.includes(cleanPhone) && !cleanPhone.includes(cPhone)) return false;
              if (!storeType) return true;
              return (c.store_type || '') === storeType;
            });
            if (match) {
              setRepairFormData(prev => ({
                ...prev,
                customer_id: match.id,
                customer_name: match.name,
                customer_phone: match.phone || prev.customer_phone
              }));
              setRepairPhoneMatchCustomers([]);
              toast.info(`Mevcut müşteri seçildi: ${match.name}`);
              return;
            }
          }
        } catch (e) { /* aramada hata olursa yeni kayıt akışına devam */ }
      }

      const response = await fetch(`${API_URL}/customers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: repairFormData.customer_name,
          phone: repairFormData.customer_phone || '',
          store_type: storeType,
          kvkk_consent: true
        })
      });

      const data = await response.json();
      if (data.success && data.customer) {
        setRepairFormData(prev => ({
          ...prev,
          customer_id: data.customer.id
        }));
        setRepairPhoneMatchCustomers([]);
        toast.success('Müşteri kaydedildi!');
      } else {
        toast.error(data.message || data.error || 'Müşteri kaydedilemedi!');
      }
    } catch (error) {
      console.error('Müşteri kayıt hatası:', error);
      toast.error('Müşteri kaydedilemedi!');
    }
  };

  // ── Tamirat "Ücret Al" (kapora) ödeme akışı ──────────────────────────
  const fetchRepairBankAccounts = async () => {
    try {
      const response = await fetch(`${API_URL}/bank-accounts?active_only=true`);
      const data = await response.json();
      if (data.success) setRepairBankAccounts(data.accounts || []);
    } catch (e) {}
  };

  // Toplam alınan ücret
  const getRepairPaymentsTotal = () => repairPayments.reduce((sum, p) => sum + p.amount, 0);

  // Kalan: alınan ücret toplam tamirat ücretini geçemez
  const getRepairDepositRemaining = () => {
    const total = Number(repairFormData.estimated_price) || 0;
    return Math.max(0, total - getRepairPaymentsTotal());
  };

  // Ödeme ekle (alınan ücret toplam ücreti geçmeyecek şekilde sınırlanır)
  const addRepairPayment = (methodOverride, bankAccountOverride) => {
    const amount = parseFloat(repairPaymentAmount);
    if (!amount || amount <= 0) {
      toast.warning('Lütfen tutar girin!');
      return;
    }
    const method = methodOverride || repairPaymentMethod;
    if (!method) {
      toast.warning('Lütfen ödeme yöntemi seçin!');
      return;
    }
    const bankAccount = bankAccountOverride || selectedRepairBankAccount;
    if (method === 'Havale/EFT' && !bankAccount) {
      toast.warning('Lütfen banka hesabı seçin!');
      return;
    }

    // Alınan ücret toplam tamirat ücretini geçemez
    const remaining = getRepairDepositRemaining();
    if (remaining <= 0) {
      toast.warning('Alınan ücret toplam tamirat ücretine ulaştı!');
      return;
    }
    const cappedAmount = Math.min(amount, remaining);
    if (cappedAmount < amount) {
      toast.info('Alınan ücret toplam tamirat ücretini geçemez, tutar sınırlandı.');
    }

    const payment = { id: Date.now(), method, amount: cappedAmount };
    if (method === 'Havale/EFT' && bankAccount) payment.bankAccount = bankAccount;

    setRepairPayments(prev => [...prev, payment]);
    setRepairPaymentAmount('');
    setRepairPaymentMethod(method);
    setSelectedRepairBankAccount(null);
  };

  const removeRepairPayment = (id) => {
    setRepairPayments(prev => prev.filter(p => p.id !== id));
  };

  // Ücret alma modalını aç (mevcut kapora ödemelerini modala yükle)
  const openDepositPaymentModal = () => {
    if (!(Number(repairFormData.estimated_price) > 0)) {
      toast.warning('Önce tamirat ücretini girin!');
      return;
    }
    setRepairPaymentMethod('');
    setRepairPaymentAmount('');
    const existing = (repairFormData.deposit_payment_details || []).map((p, idx) => ({
      id: p.id || (Date.now() + idx),
      method: p.method,
      amount: Number(p.amount) || 0,
      bankAccount: p.bank_account ? { id: p.bank_account_id, account_name: p.bank_account, account_type: p.bank_account_type || 'personal' } : undefined
    }));
    setRepairPayments(existing);
    setSelectedRepairBankAccount(null);
    fetchRepairBankAccounts();
    setShowRepairPaymentModal(true);
  };

  // Alınan ücreti kaydet → repairFormData'ya yaz
  const completeDepositPayment = () => {
    const total = getRepairPaymentsTotal();
    if (total <= 0) {
      toast.warning('Lütfen en az bir ödeme ekleyin!');
      return;
    }
    const primaryMethod = repairPayments.length === 1
      ? repairPayments[0].method
      : (repairPayments.length > 1 ? 'Karışık' : null);
    const details = repairPayments.map(p => ({
      method: p.method,
      amount: p.amount,
      ...(p.bankAccount ? { bank_account: p.bankAccount.account_name, bank_account_type: p.bankAccount.account_type } : {})
    }));
    setRepairFormData(prev => ({
      ...prev,
      deposit_amount: total.toFixed(2),
      deposit_payment_method: primaryMethod,
      deposit_payment_details: details
    }));
    setShowRepairPaymentModal(false);
  };

  // Alınan ücreti temizle
  const clearDepositPayment = () => {
    setRepairFormData(prev => ({
      ...prev,
      deposit_amount: '',
      deposit_payment_method: '',
      deposit_payment_details: []
    }));
  };

  // Tamirat fotoğraf yükleme
  const handleRepairPhotoUpload = async (e, itemIndex) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setRepairUploading(true);

    try {
      const formData = new FormData();
      files.forEach(file => formData.append('photos', file));

      const response = await fetch(`${API_URL}/repairs/upload-photos`, {
        method: 'POST',
        body: formData
      });

      const data = await response.json();
      if (data.success && Array.isArray(data.photos)) {
        setRepairFormData(prev => ({
          ...prev,
          items: prev.items.map((item, i) =>
            i === itemIndex ? { ...item, photos: [...item.photos, ...data.photos] } : item
          )
        }));
      } else {
        toast.error(data.error || 'Fotoğraf yüklenemedi!');
      }
    } catch (error) {
      console.error('Fotoğraf yükleme hatası:', error);
      toast.error('Fotoğraf yüklenemedi!');
    } finally {
      setRepairUploading(false);
      e.target.value = '';
    }
  };

  // Tamirat fotoğrafını kaldır
  const removeRepairPhoto = (itemIndex, photoIndex) => {
    setRepairFormData(prev => ({
      ...prev,
      items: prev.items.map((item, i) =>
        i === itemIndex ? { ...item, photos: item.photos.filter((_, pi) => pi !== photoIndex) } : item
      )
    }));
  };

  // Terminal listesini getir (tamirat için)
  const fetchRepairTerminals = async () => {
    setLoadingRepairTerminals(true);
    try {
      const response = await fetch(`${API_URL}/terminal-users?store_id=${currentStoreId}&active=true`);
      const data = await response.json();
      setRepairTerminals(data || []);
    } catch (error) {
      console.error('Terminal yükleme hatası:', error);
      setRepairTerminals([]);
    } finally {
      setLoadingRepairTerminals(false);
    }
  };

  // Terminal seçim modalını aç (tamirat için)
  const openRepairTerminalModal = (itemIndex = 0) => {
    setRepairTerminalPhotoItemIndex(itemIndex);
    fetchRepairTerminals();
    setShowRepairTerminalModal(true);
    setSelectedRepairTerminal(null);
  };

  // Terminale fotoğraf çekim isteği gönder (tamirat için)
  const sendRepairPhotoRequest = async (terminal) => {
    try {
      const currentItem = repairFormData.items[repairTerminalPhotoItemIndex];
      const response = await fetch(`${API_URL}/terminal-photo-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          terminal_user_id: terminal.id,
          request_type: 'repair_photo',
          description: currentItem?.item_description || 'Tamirat ürünü'
        })
      });

      const data = await response.json();
      if (data.success) {
        setRepairPhotoRequestId(data.request_id);
        setSelectedRepairTerminal(terminal);
        setShowRepairTerminalModal(false);
        setRepairWaitingForPhoto(true);
        toast.info(`${terminal.name} terminaline fotoğraf isteği gönderildi!`);

        // Fotoğraf geldi mi kontrol et
        startRepairPhotoCheck(data.request_id);
      } else {
        toast.error(data.message || 'İstek gönderilemedi!');
      }
    } catch (error) {
      console.error('Fotoğraf isteği hatası:', error);
      toast.error('İstek gönderilirken hata oluştu!');
    }
  };

  // Fotoğraf kontrolünü başlat (tamirat için)
  const startRepairPhotoCheck = (requestId) => {
    if (repairPhotoCheckIntervalRef.current) {
      clearInterval(repairPhotoCheckIntervalRef.current);
    }

    repairPhotoCheckIntervalRef.current = setInterval(async () => {
      try {
        const response = await fetch(`${API_URL}/terminal-photo-requests/${requestId}`);
        const data = await response.json();

        if (data.status === 'completed' && data.photo_url) {
          clearInterval(repairPhotoCheckIntervalRef.current);
          setRepairWaitingForPhoto(false);
          setRepairPhotoRequestId(null);

          // Fotoğrafı doğru ürüne ekle
          setRepairFormData(prev => ({
            ...prev,
            items: prev.items.map((item, i) =>
              i === repairTerminalPhotoItemIndex
                ? { ...item, photos: [...(item.photos || []), data.photo_url] }
                : item
            )
          }));

          toast.success('Fotoğraf terminalden alındı!');
        } else if (data.status === 'rejected') {
          clearInterval(repairPhotoCheckIntervalRef.current);
          setRepairWaitingForPhoto(false);
          setRepairPhotoRequestId(null);
          toast.warning('Terminal fotoğraf isteğini reddetti.');
        } else if (data.status === 'expired') {
          clearInterval(repairPhotoCheckIntervalRef.current);
          setRepairWaitingForPhoto(false);
          setRepairPhotoRequestId(null);
          toast.warning('Fotoğraf isteğinin süresi doldu.');
        }
      } catch (error) {
        console.error('Fotoğraf kontrol hatası:', error);
      }
    }, 2000);

    // 2 dakika sonra timeout
    setTimeout(() => {
      if (repairPhotoCheckIntervalRef.current) {
        clearInterval(repairPhotoCheckIntervalRef.current);
        if (repairWaitingForPhoto) {
          setRepairWaitingForPhoto(false);
          setRepairPhotoRequestId(null);
          toast.warning('Fotoğraf isteği zaman aşımına uğradı.');
        }
      }
    }, 120000);
  };

  // Fotoğraf beklemeyi iptal et (tamirat için)
  const cancelRepairPhotoWait = async () => {
    if (repairPhotoCheckIntervalRef.current) {
      clearInterval(repairPhotoCheckIntervalRef.current);
    }

    if (repairPhotoRequestId) {
      try {
        await fetch(`${API_URL}/terminal-photo-requests/${repairPhotoRequestId}/cancel`, {
          method: 'PUT'
        });
      } catch (error) {
        console.error('İptal hatası:', error);
      }
    }

    setRepairWaitingForPhoto(false);
    setRepairPhotoRequestId(null);
    setSelectedRepairTerminal(null);
    toast.info('Fotoğraf isteği iptal edildi.');
  };

  // Tamirat müşteri teslim fişi yazdır (Repairs sayfasındaki ile aynı)
  const printRepairCustomerReceipt = async (repairData) => {
    const repairCode = repairData.repair_code;
    const estimatedDelivery = repairData.estimated_delivery
      ? new Date(repairData.estimated_delivery).toLocaleDateString('tr-TR', { year: 'numeric', month: 'long', day: 'numeric' })
      : '-';
    const createdAt = new Date(repairData.created_at || new Date()).toLocaleDateString('tr-TR', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' });

    // QR kodu al
    let qrCodeData = null;
    try {
      const qrResponse = await fetch(`${API_URL}/public/repair-qr/${repairCode}`);
      const qrResult = await qrResponse.json();
      if (qrResult.success) {
        qrCodeData = qrResult.qrCode;
      }
    } catch (e) {
      console.error('QR kod alınamadı:', e);
    }

    // Geçerli ürünleri filtrele
    let repairItems = [];
    if (repairData.items) {
      try {
        repairItems = typeof repairData.items === 'string' ? JSON.parse(repairData.items) : repairData.items;
      } catch (e) {
        repairItems = [];
      }
    }
    if (!repairItems || repairItems.length === 0) {
      repairItems = [{
        item_type: repairData.item_type,
        item_description: repairData.item_description,
        problem_description: repairData.problem_description
      }];
    }
    const validItems = repairItems.filter(i => i.item_description?.trim());

    const itemsHtml = `
      <table style="width: 100%; border-collapse: collapse; font-size: 11px; font-weight: bold;">
        ${validItems.map((item, index) => `
          <tr style="border-bottom: 1px solid #ccc;">
            <td style="padding: 4px 0; width: 18px; vertical-align: top; color: #000;">${index + 1}.</td>
            <td style="padding: 4px 0; color: #000;">${item.item_description}${item.problem_description ? ` <span style="color: #555;">- ${item.problem_description}</span>` : ''}</td>
          </tr>
        `).join('')}
      </table>
    `;

    const repairHtml = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Müşteri Teslim Fişi - ${repairCode}</title>
        <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.6/dist/JsBarcode.all.min.js"><\/script>
        <style>
          @page { size: 80mm auto; margin: 5mm; }
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body {
            font-family: Arial, Helvetica, sans-serif;
            font-size: 11px;
            font-weight: 600;
            width: 80mm;
            padding: 5mm;
            background: #fff;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .header {
            text-align: center;
            border-bottom: 1px dashed #000;
            padding-bottom: 6px;
            margin-bottom: 6px;
          }
          .logo-img {
            width: 100px;
            height: auto;
            margin-bottom: 4px;
            filter: invert(1);
          }
          .subtitle { font-size: 11px; color: #000; font-weight: bold; }
          .section { margin: 6px 0; }
          .section-title {
            font-weight: bold;
            font-size: 11px;
            color: #000;
            border-bottom: 1px solid #000;
            padding-bottom: 2px;
            margin-bottom: 4px;
          }
          .row {
            display: flex;
            justify-content: space-between;
            padding: 2px 0;
            font-size: 11px;
            color: #000;
          }
          .row .label { color: #000; font-weight: bold; }
          .row .value { font-weight: bold; text-align: right; max-width: 55%; color: #000; }
          .divider { border-top: 1px dashed #000; margin: 4px 0; }
          .footer {
            text-align: center;
            margin-top: 6px;
            padding-top: 6px;
            border-top: 1px dashed #000;
            font-size: 9px;
            color: #000;
            font-weight: bold;
            line-height: 1.4;
          }
          @media print {
            body { width: 80mm; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          ${currentStoreId === 1
            ? `<img src="/Siyah - Beyaz - 1.png" alt="Akik Gold Jewellery" class="logo-img" />`
            : `<div style="margin: 0 auto 2px auto; text-align: center;">
                <img src="/akikfisligi.png" alt="Akik Gümüş" style="width: 110px; height: auto; filter: contrast(3);" />
              </div>`
          }
          ${currentStoreId === 1
            ? `<div style="font-family: Arial, sans-serif; font-size: 10px; color: #000; line-height: 1.4; margin-bottom: 5px;">Osmangazi Mah. Ahmet Şireci Bulvarı No: 14D<br/>Şehitkamil / Gaziantep<br/>Tel: 0342 230 00 64<br/>www.akikjewellery.com | info@akikjewellery.com</div>`
            : `<div style="font-family: Arial, sans-serif; font-size: 11px; font-weight: bold; color: #000; margin-bottom: 3px;">${currentStoreId === 3 ? '(YEŞİLSU ŞUBESİ)' : '(OSMANGAZİ ŞUBESİ)'}</div>
               <div style="font-family: Arial, sans-serif; font-size: 10px; color: #000; line-height: 1.4; margin-bottom: 5px;">${currentStoreId === 3 ? 'Bey Mah. Atatürk Blv. 43/A<br/>Şahinbey / Gaziantep' : 'Osmangazi Mah. Ahmet Şireci Bulvarı No: 16/F<br/>Şehitkamil / Gaziantep'}<br/>Tel: 0342 230 00 64</div>`
          }
          <div class="subtitle">Tamirat Fişi</div>
        </div>

        <div style="display: flex; gap: 6px; align-items: flex-start; margin: 5px 0; border: 1px solid #000; border-radius: 4px; padding: 5px;">
          <div style="flex-shrink: 0; text-align: center;">
            <div style="font-size: 9px; font-weight: bold; color: #000;">FİŞ NO</div>
            <div style="font-size: 16px; font-weight: bold; letter-spacing: 2px; color: #000;">${repairCode}</div>
            <div style="margin: 2px 0 0 0;"><svg id="barcode-receipt"></svg></div>
          </div>
          <div style="flex: 1; border-left: 1px dashed #000; padding-left: 6px; font-weight: bold; color: #000;">
            <div style="font-size: 12px; margin-bottom: 2px;">${repairData.customer_name}</div>
            <div style="font-size: 12px; margin-bottom: 2px;">${repairData.customer_phone || '-'}</div>
            <div style="font-size: 12px; color: #000;">Teslim Kodu: <span style="letter-spacing: 1px;">${repairCode}</span></div>
          </div>
        </div>

        <div class="section">
          <div class="section-title">ÜRÜNLER (${validItems.length} adet)</div>
          ${itemsHtml}
        </div>

        <div class="section">
          <div class="section-title">TARİH BİLGİLERİ</div>
          <div class="row">
            <span class="label">Teslim Alma:</span>
            <span class="value">${createdAt}</span>
          </div>
          <div class="row">
            <span class="label">Tahmini Teslim:</span>
            <span class="value">${estimatedDelivery}</span>
          </div>
          ${repairData.staff_name ? `
          <div class="row">
            <span class="label">Kasiyer:</span>
            <span class="value">${repairData.staff_name.split(' ').map(word => word.charAt(0).toUpperCase() + '***').join(' ')}</span>
          </div>
          ` : ''}
        </div>

        ${repairData.price_type === 'free' ? `
        <div class="section">
          <div class="section-title">ÜCRET BİLGİLERİ</div>
          <div class="row">
            <span class="label">Ücret:</span>
            <span class="value" style="font-weight: bold;">ÜCRETSİZ</span>
          </div>
        </div>
        ` : repairData.price_type === 'pending' ? `
        <div class="section">
          <div class="section-title">ÜCRET BİLGİLERİ</div>
          <div class="row">
            <span class="label">Ücret:</span>
            <span class="value" style="font-weight: bold;">HESAPLANACAK</span>
          </div>
        </div>
        ` : repairData.estimated_price > 0 ? `
        <div class="section">
          <div class="section-title">ÜCRET BİLGİLERİ</div>
          <div class="row">
            <span class="label">Ücret:</span>
            <span class="value">₺${Number(repairData.estimated_price).toLocaleString('tr-TR')}</span>
          </div>
          ${repairData.deposit_amount > 0 ? `
          <div class="row">
            <span class="label">Alınan Ücret:</span>
            <span class="value">₺${Number(repairData.deposit_amount).toLocaleString('tr-TR')}</span>
          </div>
          <div class="divider"></div>
          <div class="row" style="font-weight: bold;">
            <span class="label">Kalan Tutar:</span>
            <span class="value">₺${Number(repairData.estimated_price - repairData.deposit_amount).toLocaleString('tr-TR')}</span>
          </div>
          ` : ''}
        </div>
        ` : ''}

        ${repairData.notes ? `
        <div class="section">
          <div class="section-title">NOTLAR</div>
          <div style="font-size: 11px; padding: 2px 0; color: #000; font-weight: bold;">${repairData.notes}</div>
        </div>
        ` : ''}

        ${qrCodeData ? `
        <div style="display: flex; align-items: center; gap: 8px; margin: 6px 0; padding: 4px 0;">
          <img src="${qrCodeData}" alt="QR Kod" style="width: 50px; height: 50px; flex-shrink: 0;" />
          <div style="font-size: 9px; color: #000; line-height: 1.4;">Tamirat durumunuzu takip etmek için QR kodu okutabilirsiniz.</div>
        </div>
        ` : ''}

        <div class="footer">
          <div>TEŞEKKÜR EDERİZ YİNE BEKLERİZ</div>
          <div>BİLGİ FİŞİDİR MALİ DEĞERİ YOKTUR</div>
          <div style="margin-top: 2px;">15 GÜN İÇERİSİNDE TESLİM ALINMAYAN ÜRÜNLERDEN FİRMAMIZ SORUMLU DEĞİLDİR</div>
          <div style="margin-top: 2px;">ÜRÜNLERİNİZİ YALNIZCA BU FİŞLE TESLİM ALABİLİRSİNİZ FİŞİNİZİ SAKLAYINIZ</div>
        </div>

        <script>
          window.onload = function() {
            try {
              JsBarcode("#barcode-receipt", "${repairCode}", {
                format: "CODE128", width: 1.2, height: 30,
                displayValue: false, margin: 0, background: "transparent"
              });
            } catch(e) { console.error('Barkod hatası:', e); }
            var directPrint = localStorage.getItem('directPrintEnabled') === 'true';
            var printed = false;
            function doPrint() {
              if (printed) return;
              printed = true;
              window.print();
              if (directPrint) { setTimeout(function() { window.close(); }, 1500); }
            }
            var imgs = document.querySelectorAll('img');
            var loaded = 0;
            var total = imgs.length;
            if (total === 0) { setTimeout(doPrint, 800); }
            else {
              function check() { loaded++; if (loaded >= total) setTimeout(doPrint, 400); }
              imgs.forEach(function(img) {
                if (img.complete) { check(); }
                else { img.onload = check; img.onerror = check; }
              });
              setTimeout(doPrint, 4000);
            }
          }
        </script>
      </body>
      </html>
    `;
    handlePrint(repairHtml, 'receipt');
  };

  // Tamirat iç kullanım fişi (barkodlu) - Repairs sayfasındaki ile aynı
  const printRepairInternalReceipt = (repairData) => {
    const repairCode = repairData.repair_code;
    const createdAt = new Date(repairData.created_at || new Date()).toLocaleDateString('tr-TR', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' });

    let repairItems = [];
    if (repairData.items) {
      try {
        repairItems = typeof repairData.items === 'string' ? JSON.parse(repairData.items) : repairData.items;
      } catch (e) {
        repairItems = [];
      }
    }
    if (!repairItems || repairItems.length === 0) {
      repairItems = [{
        item_type: repairData.item_type,
        item_description: repairData.item_description,
        problem_description: repairData.problem_description
      }];
    }
    const validItems = repairItems.filter(item => item.item_description?.trim());

    const itemsHtml = `
      <table style="width: 100%; border-collapse: collapse; font-size: 11px; font-weight: bold;">
        ${validItems.map((item, index) => `
          <tr style="border-bottom: 1px solid #ccc;">
            <td style="padding: 4px 0; width: 18px; vertical-align: top; color: #000;">${index + 1}.</td>
            <td style="padding: 4px 0; color: #000;">${item.item_description}${item.problem_description ? ` <span style="color: #555;">- ${item.problem_description}</span>` : ''}</td>
          </tr>
        `).join('')}
      </table>
    `;

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Tamirat Fişi - ${repairCode}</title>
        <style>
          @page { size: 80mm auto; margin: 0; }
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body {
            font-family: Arial, Helvetica, sans-serif;
            font-size: 12px;
            font-weight: 600;
            width: 80mm;
            margin: 0 auto;
            padding: 5mm;
            background: #fff;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .header {
            text-align: center;
            border-bottom: 2px solid #000;
            padding-bottom: 10px;
            margin-bottom: 15px;
          }
          .header-title {
            font-size: 16px;
            font-weight: bold;
            margin-bottom: 5px;
            padding: 5px;
          }
          .section-title {
            font-weight: bold;
            font-size: 13px;
            color: #000;
            border-bottom: 2px solid #000;
            padding-bottom: 3px;
            margin-bottom: 10px;
          }
          .footer {
            text-align: center;
            margin-top: 15px;
            padding-top: 10px;
            border-top: 2px dashed #000;
            font-size: 11px;
            color: #000;
            font-weight: bold;
          }
          @media print {
            body { width: 80mm; }
          }
        </style>
        <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.6/dist/JsBarcode.all.min.js"><\/script>
      </head>
      <body>
        <div class="header">
          ${currentStoreId === 1
            ? `<img src="/Siyah - Beyaz - 1.png" alt="Akik Gold Jewellery" style="width: 100px; height: auto; margin-bottom: 4px; filter: invert(1);" />`
            : `<div style="margin: 0 auto 2px auto; text-align: center;">
                <img src="/akikfisligi.png" alt="Akik Gümüş" style="width: 110px; height: auto; filter: contrast(3);" />
              </div>`
          }
          ${currentStoreId === 1
            ? `<div style="font-family: Arial, sans-serif; font-size: 10px; color: #000; line-height: 1.4; margin-bottom: 5px;">Osmangazi Mah. Ahmet Şireci Bulvarı No: 14D<br/>Şehitkamil / Gaziantep<br/>Tel: 0342 230 00 64<br/>www.akikjewellery.com | info@akikjewellery.com</div>`
            : `<div style="font-family: Arial, sans-serif; font-size: 11px; font-weight: bold; color: #000; margin-bottom: 3px;">${currentStoreId === 3 ? '(YEŞİLSU ŞUBESİ)' : '(OSMANGAZİ ŞUBESİ)'}</div>
               <div style="font-family: Arial, sans-serif; font-size: 10px; color: #000; line-height: 1.4; margin-bottom: 5px;">${currentStoreId === 3 ? 'Bey Mah. Atatürk Blv. 43/A<br/>Şahinbey / Gaziantep' : 'Osmangazi Mah. Ahmet Şireci Bulvarı No: 16/F<br/>Şehitkamil / Gaziantep'}<br/>Tel: 0342 230 00 64</div>`
          }
          <div class="header-title">TAMİRAT FİŞİ</div>
        </div>

        <div style="display: flex; gap: 6px; align-items: flex-start; margin: 5px 0; border: 1px solid #000; border-radius: 4px; padding: 5px;">
          <div style="flex-shrink: 0; text-align: center;">
            <div style="font-size: 9px; font-weight: bold; color: #000;">FİŞ NO</div>
            <div style="font-size: 16px; font-weight: bold; letter-spacing: 2px; color: #000;">${repairCode}</div>
            <div style="margin: 2px 0 0 0;"><svg id="barcode-repair-internal"></svg></div>
          </div>
          <div style="flex: 1; border-left: 1px dashed #000; padding-left: 6px; font-weight: bold; color: #000;">
            <div style="font-size: 12px; margin-bottom: 2px;">${repairData.customer_name}</div>
            <div style="font-size: 12px; margin-bottom: 2px;">${repairData.customer_phone || '-'}</div>
            <div style="font-size: 12px; margin-bottom: 2px;">${createdAt}</div>
            ${repairData.staff_name ? `<div style="font-size: 12px;">Kasiyer: ${repairData.staff_name.split(' ').map(word => word.charAt(0).toUpperCase() + '***').join(' ')}</div>` : ''}
          </div>
        </div>

        <div class="section">
          <div class="section-title">ÜRÜNLER (${validItems.length} adet)</div>
          ${itemsHtml}
        </div>

        ${repairData.price_type === 'free' ? `
        <div class="section" style="margin-top: 8px;">
          <div class="section-title">ÜCRET BİLGİLERİ</div>
          <div class="row"><span class="label">Ücret:</span><span class="value" style="font-weight: bold;">ÜCRETSİZ</span></div>
        </div>
        ` : repairData.price_type === 'pending' ? `
        <div class="section" style="margin-top: 8px;">
          <div class="section-title">ÜCRET BİLGİLERİ</div>
          <div class="row"><span class="label">Ücret:</span><span class="value" style="font-weight: bold;">HESAPLANACAK</span></div>
        </div>
        ` : (Number(repairData.estimated_price) > 0 || Number(repairData.deposit_amount) > 0) ? `
        <div class="section" style="margin-top: 8px;">
          <div class="section-title">ÜCRET BİLGİLERİ</div>
          ${Number(repairData.estimated_price) > 0 ? `<div class="row"><span class="label">Ücret:</span><span class="value">₺${Number(repairData.estimated_price).toLocaleString('tr-TR')}</span></div>` : ''}
          ${Number(repairData.deposit_amount) > 0 ? `<div class="row"><span class="label">Alınan Ücret:</span><span class="value">₺${Number(repairData.deposit_amount).toLocaleString('tr-TR')}</span></div>` : ''}
          ${Number(repairData.estimated_price) > 0 && Number(repairData.deposit_amount) > 0 ? `<div class="divider"></div><div class="row" style="font-weight: bold;"><span class="label">Kalan Tutar:</span><span class="value">₺${(Number(repairData.estimated_price) - Number(repairData.deposit_amount)).toLocaleString('tr-TR')}</span></div>` : ''}
        </div>
        ` : ''}

        ${repairData.notes ? `
        <div class="section" style="margin-top: 8px;">
          <div class="section-title">NOTLAR</div>
          <div style="font-size: 11px; padding: 2px 0; color: #000; font-weight: bold;">${repairData.notes}</div>
        </div>
        ` : ''}

        <div class="footer">
          <div>Bu fiş tamirat takibi için kullanılır.</div>
          <div style="margin-top: 5px;">Yazdırma: ${new Date().toLocaleString('tr-TR')}</div>
        </div>

        <script>
          window.onload = function() {
            try {
              JsBarcode("#barcode-repair-internal", "${repairCode}", {
                format: "CODE128", width: 1.2, height: 30,
                displayValue: false, margin: 0, background: "transparent"
              });
            } catch(e) { console.error('Barkod hatası:', e); }
            var directPrint = localStorage.getItem('directPrintEnabled') === 'true';
            var printed = false;
            function doPrint() {
              if (printed) return;
              printed = true;
              window.print();
              if (directPrint) { setTimeout(function() { window.close(); }, 1500); }
            }
            var imgs = document.querySelectorAll('img');
            var loaded = 0;
            var total = imgs.length;
            if (total === 0) { setTimeout(doPrint, 800); }
            else {
              function check() { loaded++; if (loaded >= total) setTimeout(doPrint, 400); }
              imgs.forEach(function(img) {
                if (img.complete) { check(); }
                else { img.onload = check; img.onerror = check; }
              });
              setTimeout(doPrint, 4000);
            }
          }
        </script>
      </body>
      </html>
    `;
    handlePrint(html, 'receipt');
  };

  // Tamirat kaydını oluştur
  const handleRepairSubmit = async () => {
    // Validasyon
    if (!repairFormData.customer_name) {
      toast.warning('Müşteri adı gerekli!');
      return;
    }

    const cleanPhoneRepair = (repairFormData.customer_phone || '').replace(/\D/g, '');
    if (cleanPhoneRepair.length > 0 && cleanPhoneRepair.length !== 10 && cleanPhoneRepair.length !== 11) {
      toast.warning('Numara hatalı');
      return;
    }

    if (!repairFormData.staff_name) {
      toast.warning('Kasiyer seçimi zorunludur!');
      return;
    }

    // En az bir ürün açıklaması olmalı
    const hasValidItem = repairFormData.items.some(item => item.item_description.trim());
    if (!hasValidItem) {
      toast.warning('En az bir ürün açıklaması gerekli!');
      return;
    }

    try {
      // customer_id boşsa ama isim doluysa: önce telefonla eşleşen mevcut müşteriyi bul,
      // yoksa otomatik müşteri kaydı oluştur. (Duplikat kayıt oluşturmayı engeller.)
      let customerIdToUse = repairFormData.customer_id;
      // Telefona kayıtlı mevcut cari bulunursa fişte de o carinin adı/telefonu kullanılsın
      // (kullanıcının elle yazdığı isim değil). Eşleşme yoksa yazılan isim geçerli.
      let customerNameToUse = repairFormData.customer_name;
      let customerPhoneToUse = repairFormData.customer_phone;
      if (!customerIdToUse && repairFormData.customer_name?.trim()) {
        const storeType = getStoreTypeForCustomer();
        const cleanPhone = (repairFormData.customer_phone || '').replace(/\D/g, '');
        // 1) Telefonla mevcut müşteri ara (aynı mağaza tipi)
        if (cleanPhone.length >= 9) {
          try {
            const searchRes = await fetch(`${API_URL}/customers?search=${cleanPhone}`);
            const searchData = await searchRes.json();
            const list = searchData.customers || searchData || [];
            if (Array.isArray(list)) {
              const match = list.find(c => {
                if (!c.phone) return false;
                const cPhone = c.phone.replace(/\D/g, '');
                if (!cPhone.includes(cleanPhone) && !cleanPhone.includes(cPhone)) return false;
                if (!storeType) return true;
                return (c.store_type || '') === storeType;
              });
              if (match) {
                customerIdToUse = match.id;
                // Eşleşen carinin adı/telefonu esas alınır; elle yazılan isim göz ardı edilir
                customerNameToUse = match.name;
                customerPhoneToUse = match.phone || customerPhoneToUse;
              }
            }
          } catch (searchErr) {
            console.error('Müşteri telefon arama hatası:', searchErr);
          }
        }
        // 2) Eşleşme yoksa yeni müşteri oluştur
        if (!customerIdToUse) {
          try {
            const createRes = await fetch(`${API_URL}/customers`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                name: repairFormData.customer_name,
                phone: repairFormData.customer_phone || '',
                store_type: storeType,
                kvkk_consent: true
              })
            });
            const createData = await createRes.json();
            if (createData.success && createData.customer) {
              customerIdToUse = createData.customer.id;
            } else if (createData.duplicate && createData.existing_customer) {
              // Aynı telefon+mağaza tipinde zaten cari var - mükerrer açmak yerine onu kullan
              customerIdToUse = createData.existing_customer.id;
            }
          } catch (createErr) {
            console.error('Otomatik müşteri oluşturma hatası:', createErr);
          }
        }
      }

      const response = await fetch(`${API_URL}/repairs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // store_id şart: gönderilmezse tamirat NULL mağaza ile kaydedilip her mağazada görünüyordu
        body: JSON.stringify({ ...repairFormData, customer_id: customerIdToUse || null, customer_name: customerNameToUse, customer_phone: customerPhoneToUse, store_id: currentStoreId })
      });

      const data = await response.json();
      if (data.success) {
        const repairCode = data.repair_code || '-';
        const itemCount = repairFormData.items.filter(i => i.item_description.trim()).length;
        logService.log('repairs', 'create', `Yeni tamirat kaydı oluşturuldu - ${repairFormData.customer_name} (${itemCount} ürün)`, {
          entity_type: 'repair',
          entity_id: data.id,
          entity_code: repairCode,
          details: {
            musteri: repairFormData.customer_name,
            urun_sayisi: itemCount,
            urunler: repairFormData.items.map(i => i.item_description).filter(Boolean).join(', '),
            fis_no: repairCode
          },
          status: 'success'
        });
        toast.success(`Tamirat kaydı oluşturuldu! Fiş No: ${repairCode} (${itemCount} ürün)`);
        setShowRepairModal(false);

        // Repair detayını backend'den çek (tam veri ile fiş yazdırmak için)
        const savedFormData = { ...repairFormData, repair_code: repairCode, created_at: new Date().toISOString() };
        resetRepairForm();

        const directPrint = localStorage.getItem('directPrintEnabled') === 'true';
        if (directPrint) {
          // Otomatik: 1x müşteri teslim fişi + 1x tamirat fişi
          printRepairCustomerReceipt(savedFormData);
          setTimeout(() => printRepairInternalReceipt(savedFormData), 3000);
        } else {
          printRepairCustomerReceipt(savedFormData);
        }
      } else {
        toast.error(data.error || 'Tamirat kaydı oluşturulamadı!');
      }
    } catch (error) {
      console.error('Tamirat kayıt hatası:', error);
      toast.error('Tamirat kaydı oluşturulamadı!');
    }
  };

  // Filtrelenmiş tamirat müşterileri
  const filteredRepairCustomers = repairCustomers.filter(c =>
    turkishIncludes(c.name, repairCustomerSearch) ||
    c.phone?.includes(repairCustomerSearch)
  );

  // ===== TAHSİLAT MODÜLÜ FONKSİYONLARI =====

  // Borçları türe göre grupla ve topla
  const groupedCollectionDebts = useMemo(() => {
    if (!collectionCustomerDebts || collectionCustomerDebts.length === 0) return [];

    const groups = {};

    collectionCustomerDebts.forEach(debt => {
      const type = debt.debt_type || 'TL';
      if (!groups[type]) {
        groups[type] = {
          debt_type: type,
          debts: [],
          total_amount: 0,
          total_amount_tl: 0,
          debt_ids: []
        };
      }
      groups[type].debts.push(debt);
      groups[type].debt_ids.push(debt.id);
      groups[type].total_amount += parseFloat(debt.amount) || 0;
      groups[type].total_amount_tl += parseFloat(debt.amount_tl) || 0;
    });

    // Array'e çevir ve sırala (TL, GRAM, GUMUS)
    const order = ['TL', 'GRAM', 'GUMUS'];
    return Object.values(groups).sort((a, b) => order.indexOf(a.debt_type) - order.indexOf(b.debt_type));
  }, [collectionCustomerDebts]);

  // Tahsilat modal açıldığında borçlu müşterileri yükle
  const openCollectionModal = async () => {
    setShowCollectionModal(true);
    setCollectionCustomerSearchQuery('');
    await fetchCollectionDebtSummary();
    await searchCollectionCustomer('');
  };

  // Borç özetini getir
  const fetchCollectionDebtSummary = async () => {
    try {
      const response = await fetch(`${API_URL}/customers/debts/summary`);
      const data = await response.json();
      const summaryObj = {};
      data.forEach(d => {
        summaryObj[d.customer_id] = d;
      });
      setCollectionDebtSummary(summaryObj);
    } catch (error) {
      console.error('Borç özeti yükleme hatası:', error);
    }
  };

  // Tahsilat için müşteri arama
  const searchCollectionCustomer = async (query) => {
    try {
      let response;
      if (!query || query.length < 2) {
        response = await fetch(`${API_URL}/customers?limit=50&sortByDebt=true`);
      } else {
        response = await fetch(`${API_URL}/customers/search/${query}`);
      }
      const data = await response.json();
      const allCustomers = data.customers || data;

      // Mağaza türüne göre müşterileri filtrele
      const filteredCustomers = allCustomers.filter(customer => {
        const customerStoreType = (customer.store_type || '').toLowerCase();
        if (isGoldStore) {
          return customerStoreType === 'gold' || customerStoreType === 'altın';
        } else {
          return customerStoreType === 'silver' || customerStoreType === 'gümüş';
        }
      });

      setCollectionCustomerSearchResults(filteredCustomers);
    } catch (error) {
      console.error('Müşteri arama hatası:', error);
    }
  };

  // Borç listesini netleştir: iade edilmiş satışları hariç tut
  const filterActiveDebts = (allDebtsArr) => {
    const arr = Array.isArray(allDebtsArr) ? allDebtsArr : [];
    // TL: sale_id'li kayıtlar satış bazında netleşir; sale_id'siz kayıtlar (manuel,
    // migration, mağaza bakiye aktarımları vb.) ayrı ayrı tutulur ki grup tahsilatında
    // her birinin debt_id'si ayrı sent edilsin (aksi halde fazla ödeme kaybolur).
    const tlEntries = arr.filter(d => (d.debt_type || d.currency_type || 'TL') === 'TL');
    const saleGroups = {};
    const nonSaleDebts = [];
    let nonSaleCollections = 0;

    tlEntries.forEach(d => {
      const amt = parseFloat(d.amount_tl) || 0;
      if (d.sale_id) {
        const key = `sale_${d.sale_id}`;
        if (!saleGroups[key]) saleGroups[key] = { baseEntry: d, netTl: 0 };
        saleGroups[key].netTl += amt;
        if (amt > 0) {
          const currentBase = saleGroups[key].baseEntry;
          if (parseFloat(currentBase.amount_tl) <= 0 || d.transaction_type === 'sale') {
            saleGroups[key].baseEntry = d;
          }
        }
      } else {
        if (amt > 0) {
          nonSaleDebts.push({ entry: d, remaining: amt });
        } else {
          nonSaleCollections += amt; // negatif değer
        }
      }
    });

    // Tüm fazla alacakları (sale_id'siz tahsilatlar + iade fazlası) topla. Önce
    // sale gruplarına, sonra sale_id'siz borçlara eskiden yeniye dağıt. Böylece
    // bir mağaza-bakiye-aktarımı kaybolmaz ve modal doğru toplamı gösterir.
    let surplus = Math.abs(nonSaleCollections);
    for (const key of Object.keys(saleGroups)) {
      if (saleGroups[key].netTl < -0.01) {
        surplus += Math.abs(saleGroups[key].netTl);
        saleGroups[key].netTl = 0;
      }
    }
    if (surplus > 0.01) {
      const saleKeys = Object.keys(saleGroups)
        .filter(k => saleGroups[k].netTl > 0.01)
        .sort((a, b) => new Date(saleGroups[a].baseEntry.created_at || 0) - new Date(saleGroups[b].baseEntry.created_at || 0));
      for (const key of saleKeys) {
        if (surplus <= 0.01) break;
        const deduct = Math.min(surplus, saleGroups[key].netTl);
        saleGroups[key].netTl -= deduct;
        surplus -= deduct;
      }
      if (surplus > 0.01) {
        const sortedNonSale = [...nonSaleDebts].sort((a, b) =>
          new Date(a.entry.created_at || 0) - new Date(b.entry.created_at || 0)
        );
        for (const nsd of sortedNonSale) {
          if (surplus <= 0.01) break;
          const deduct = Math.min(surplus, nsd.remaining);
          nsd.remaining -= deduct;
          surplus -= deduct;
        }
      }
    }

    const activeTl = [];
    Object.values(saleGroups)
      .filter(g => g.netTl > 0.01)
      .forEach(g => activeTl.push({ ...g.baseEntry, amount_tl: g.netTl, amount: g.netTl }));
    nonSaleDebts
      .filter(nsd => nsd.remaining > 0.01)
      .forEach(nsd => activeTl.push({ ...nsd.entry, amount_tl: nsd.remaining, amount: nsd.remaining }));
    // GRAM/GUMUS: para birimi bazında TOPLAM net hesapla
    const metalTypes = ['GRAM', 'GUMUS'];
    const activeMetal = [];
    for (const metalType of metalTypes) {
      const entries = arr.filter(d => (d.debt_type || d.currency_type) === metalType);
      if (entries.length === 0) continue;
      const netGram = entries.reduce((sum, d) => sum + (parseFloat(d.amount) || 0), 0);
      const netTl = entries.reduce((sum, d) => sum + (parseFloat(d.amount_tl) || 0), 0);
      if (netGram > 0.0001) {
        const baseEntry = entries.find(d => parseFloat(d.amount) > 0 && d.transaction_type === 'sale') || entries[0];
        activeMetal.push({ ...baseEntry, amount: netGram, amount_tl: netTl });
      }
    }
    return [...activeTl, ...activeMetal];
  };

  // Müşterinin borçlarını getir (sadece aktif/ödenecek borçlar)
  const fetchCollectionCustomerDebts = async (customer) => {
    setCollectionSelectedCustomer(customer);
    setShowCollectionModal(false);

    try {
      const response = await fetch(`${API_URL}/customers/${customer.id}/debts`);
      const data = await response.json();
      const activeDebts = filterActiveDebts(data.success ? data.debts : data);
      setCollectionCustomerDebts(activeDebts);
      setLoadingCollectionDebts(false);

      // Borç tiplerini kontrol et
      const debtTypes = [...new Set(activeDebts.map(d => d.debt_type || 'TL'))];

      if (activeDebts.length === 0) {
        // Borç yoksa borç listesini göster (boş mesaj)
        setShowCollectionDebtModal(true);
      } else if (debtTypes.length === 1) {
        // Tek tip borç varsa direkt ödeme modalını aç
        // Tüm borçları grupla
        const totalAmount = activeDebts.reduce((sum, d) => sum + (parseFloat(d.amount) || 0), 0);
        const totalAmountTl = activeDebts.reduce((sum, d) => sum + (parseFloat(d.amount_tl) || 0), 0);
        const groupDebt = {
          id: 'group_' + debtTypes[0],
          debt_type: debtTypes[0],
          amount: totalAmount,
          amount_tl: totalAmountTl,
          debt_ids: activeDebts.map(d => d.id),
          is_group: true,
          isGroup: true
        };
        setSelectedCollectionDebtGroup({ debt_type: debtTypes[0], total_amount: totalAmount, total_amount_tl: totalAmountTl, debts: activeDebts, debt_ids: activeDebts.map(d => d.id) });
        openCollectionPaymentModal(groupDebt);
      } else {
        // Birden fazla borç tipi varsa borç listesini göster
        setShowCollectionDebtModal(true);
      }
    } catch (error) {
      console.error('Borç yükleme hatası:', error);
      setCollectionCustomerDebts([]);
      setShowCollectionDebtModal(true);
    }
  };

  // Tamirat sayfasından "Tahsilat yapınız" uyarısıyla gelindiğinde ilgili müşterinin
  // tahsilat penceresini otomatik aç
  useEffect(() => {
    const openCollectionCustomerId = location.state?.openCollectionCustomerId;
    if (openCollectionCustomerId) {
      fetchCollectionCustomerDebts({
        id: openCollectionCustomerId,
        name: location.state?.openCollectionCustomerName,
        phone: location.state?.openCollectionCustomerPhone,
      });
      // Aynı kayıt geri/yenile'de tekrar açılmasın diye state'i temizle
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Ürün Yönetimi sayfasındaki transfer linkleriyle gelindiğinde ilgili
  // transfer modalını otomatik aç (Products.js artık kendi transfer
  // modallarını değil, buradaki tek/güncel tasarımı kullanıyor)
  useEffect(() => {
    const openTransferMode = location.state?.openTransferMode;
    if (openTransferMode) {
      setShowTransfersModal(true);
      setTransferMode(openTransferMode);
      if (openTransferMode === 'sent-transfers') fetchSentTransfers();
      else if (openTransferMode === 'sent-requests') fetchSentRequests();
      else if (openTransferMode === 'incoming-requests') fetchIncomingRequests();
      else if (openTransferMode === 'receive-transfer') fetchPendingTransfers();
      else if (openTransferMode === 'history') fetchTransferHistory();
      else if (openTransferMode === 'transfer-request') {
        // Ürün Yönetimi'ndeki stok uyarısından "talep oluştur" ile geldiyse
        // ürünü ve kaynak mağazayı ön dolu getir
        if (location.state?.prefillRequestFromStoreId) {
          setRequestSourceStore(location.state.prefillRequestFromStoreId);
        }
        if (location.state?.prefillRequestProduct) {
          setRequestItems([location.state.prefillRequestProduct]);
        }
      }
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Ödeme modalını aç
  const openCollectionPaymentModal = async (debt) => {
    // Banka hesaplarını yükle (Havale/EFT için)
    fetchBankAccounts();

    setSelectedCollectionDebt(debt);

    setCollectionPaymentAmount('');

    setCollectionPaymentMethod('');
    setCollectionMixedPayments([]); // Karışık ödemeleri sıfırla
    setSelectedCollectionBankAccount(null); // Seçili banka hesabını sıfırla
    setCollectionPaymentInputMode('tl'); // Giriş modunu TL olarak sıfırla
    // Hurda modunu sıfırla
    setCollectionScrapMode(false);
    setCollectionScrapData({
      metal_type: isGoldStore ? 'gold' : 'silver',
      gross_weight: '',
      waste_percent: '',
      waste_gram: '',
      purity: isGoldStore ? '22' : '925',
      unit_price: getAlisRate(isGoldStore ? 'GOLD' : 'SILVER').toFixed(2)
    });
    setShowCollectionPaymentModal(true);
  };

  // Grup ödeme modalını aç (toplanmış borçlar için)
  const openGroupCollectionPaymentModal = (group) => {
    // Banka hesaplarını yükle (Havale/EFT için)
    fetchBankAccounts();

    // Grup bilgilerini tek bir debt objesi gibi hazırla
    const groupDebt = {
      id: group.debt_ids.join(','), // Virgülle ayrılmış ID'ler
      debt_type: group.debt_type,
      amount: group.total_amount,
      amount_tl: group.total_amount_tl,
      debt_ids: group.debt_ids, // Tüm borç ID'leri
      isGroup: true // Grup olduğunu belirt
    };

    setSelectedCollectionDebt(groupDebt);
    setSelectedCollectionDebtGroup(group);

    setCollectionPaymentAmount('');

    setCollectionPaymentMethod('');
    setCollectionMixedPayments([]); // Karışık ödemeleri sıfırla
    setSelectedCollectionBankAccount(null); // Seçili banka hesabını sıfırla
    setCollectionPaymentInputMode('tl'); // Giriş modunu TL olarak sıfırla
    setCollectionScrapMode(false);
    setCollectionScrapData({
      metal_type: isGoldStore ? 'gold' : 'silver',
      gross_weight: '',
      waste_percent: '',
      waste_gram: '',
      purity: isGoldStore ? '22' : '925',
      unit_price: getAlisRate(isGoldStore ? 'GOLD' : 'SILVER').toFixed(2)
    });
    setShowCollectionPaymentModal(true);
  };

  // Tahsilat hurda tutarını hesapla
  const calculateCollectionScrapValue = () => {
    const grossWeight = parseFloat(collectionScrapData.gross_weight) || 0;
    const wasteGram = parseFloat(collectionScrapData.waste_gram) || 0;
    const unitPrice = parseFloat(collectionScrapData.unit_price) || 0;
    const purity = collectionScrapData.purity;

    if (grossWeight <= 0 || unitPrice <= 0) return { pureWeight: 0, totalValue: 0 };

    // Fire hesaplama (sadece gram)
    const netWeight = grossWeight - wasteGram;

    // Ayara göre has hesaplama - localStorage'dan alınan oranları kullan
    const metalType = collectionScrapData.metal_type || (isGoldStore ? 'gold' : 'silver');
    // Manuel düzenlenen oran (custom_purity_rate) varsa önce onu kullan
    const purityFactor = collectionScrapData.custom_purity_rate || scrapPurityRates[metalType]?.[purity] || defaultScrapPurityRates[metalType]?.[purity] || (metalType === 'gold' ? 0.900 : 0.925);

    const pureWeight = netWeight * purityFactor;
    // Manuel değer varsa onu kullan
    const totalValue = customCollectionScrapTotal !== null ? customCollectionScrapTotal : pureWeight * unitPrice;

    return { pureWeight, totalValue, netWeight, wasteGram };
  };

  // Sistemdeki güncel altın gram fiyatını al (satış kuru - ürün satarken kullanılır)
  const getCurrentGoldPrice = () => {
    const customRate = customRates['GOLD'];
    const liveSatis = parseFloat(prices?.altin_satis) || 0;

    // Önce makas farkı yüzdesini kontrol et (dinamik hesaplama)
    if (customRate?.satis_percentage != null && customRate.satis_percentage !== '' && !isNaN(customRate.satis_percentage)) {
      return liveSatis * (1 + parseFloat(customRate.satis_percentage) / 100);
    }
    // Sonra manuel sabit satış kurunu kontrol et
    if (customRate?.rate_satis != null && parseFloat(customRate.rate_satis) > 0) {
      return parseFloat(customRate.rate_satis);
    }
    return liveSatis;
  };

  // Sistemdeki güncel gümüş gram fiyatını al (satış kuru - ürün satarken kullanılır)
  const getCurrentSilverPrice = () => {
    const customRate = customRates['SILVER'];
    const liveSatis = parseFloat(prices?.gumus_satis) || 0;

    // Önce makas farkı yüzdesini kontrol et (dinamik hesaplama)
    if (customRate?.satis_percentage != null && customRate.satis_percentage !== '' && !isNaN(customRate.satis_percentage)) {
      return liveSatis * (1 + parseFloat(customRate.satis_percentage) / 100);
    }
    // Sonra manuel sabit satış kurunu kontrol et
    if (customRate?.rate_satis != null && parseFloat(customRate.rate_satis) > 0) {
      return parseFloat(customRate.rate_satis);
    }
    return liveSatis;
  };

  // Gram borcun güncel TL değerini hesapla (altın)
  const calculateCurrentGramValue = (gramAmount) => {
    const currentGoldPrice = getCurrentGoldPrice();
    return (parseFloat(gramAmount) || 0) * currentGoldPrice;
  };

  // Gram borcun güncel TL değerini hesapla (gümüş)
  const calculateCurrentSilverGramValue = (gramAmount) => {
    const currentSilverPrice = getCurrentSilverPrice();
    return (parseFloat(gramAmount) || 0) * currentSilverPrice;
  };

  // Tahsilat - Ödeme Ekle (Karışık ödeme için)
  const addCollectionPayment = (methodOverride, bankAccountOverride) => {
    const method = methodOverride || collectionPaymentMethod;
    if (!method && !collectionScrapMode) {
      toast.warning('Lütfen önce ödeme yöntemi seçin!');
      return;
    }
    const inputValue = parseFloat(collectionPaymentAmount);
    if (isNaN(inputValue) || inputValue <= 0) {
      toast.warning('Geçerli bir tutar girin!');
      return;
    }

    // Gram modu ve altın/gümüş borcu için TL'ye çevir
    let tlAmount = inputValue;
    let gramAmount = null;
    const isGramDebt = selectedCollectionDebt?.debt_type === 'GRAM' || selectedCollectionDebt?.debt_type === 'GUMUS';

    if (collectionPaymentInputMode === 'gram' && isGramDebt) {
      // Kur hesapla
      let rate = 0;
      if (selectedCollectionDebt?.debt_type === 'GRAM') {
        rate = getCurrentGoldPrice();
      } else if (selectedCollectionDebt?.debt_type === 'GUMUS') {
        rate = getCurrentSilverPrice();
      }
      if (rate <= 0) {
        toast.warning('Kur bilgisi alınamadı!');
        return;
      }
      tlAmount = inputValue * rate;
      gramAmount = inputValue;
    }

    // Borç tutarını hesapla
    let maxAmount = parseFloat(selectedCollectionDebt?.amount_tl) || 0;
    if (selectedCollectionDebt?.debt_type === 'GRAM') {
      maxAmount = calculateCurrentGramValue(selectedCollectionDebt.amount);
    } else if (selectedCollectionDebt?.debt_type === 'GUMUS') {
      maxAmount = calculateCurrentSilverGramValue(selectedCollectionDebt.amount);
    }

    // Mevcut ödemelerin toplamı
    const currentTotal = collectionMixedPayments.reduce((sum, p) => sum + p.amount, 0);
    const remaining = maxAmount - currentTotal;

    // Altın/gümüş borçlarda kurun belirsizliği nedeniyle bakiyeden fazla tahsilata izin verilmez.
    // TL borçta müşteri isterse fazla ödeyebilir - fazlası müşteri alacağı olarak bakiyede kalır.
    if (isGramDebt && tlAmount > remaining * 1.01) { // %1 tolerans
      toast.warning(`Kalan borç tutarı: ₺${remaining.toLocaleString('tr-TR', {maximumFractionDigits: 2})}`);
      return;
    }
    if (!isGramDebt && tlAmount > remaining * 1.01 && remaining > 0.01) {
      toast.info(`Kalan borç ₺${remaining.toLocaleString('tr-TR', {maximumFractionDigits: 2})} - fazlası (₺${(tlAmount - remaining).toLocaleString('tr-TR', {maximumFractionDigits: 2})}) müşteri alacağı olarak kaydedilecek.`);
    }

    // Havale/EFT seçiliyse banka hesabı seçili olmalı
    if (method === 'Havale/EFT') {
      const bankAccount = bankAccountOverride || selectedCollectionBankAccount;
      if (!bankAccount) {
        toast.warning('Lütfen bir banka hesabı seçin!');
        return;
      }
      const accountType = bankAccount.account_type === 'company' ? 'Şirket' : bankAccount.account_type === 'ecommerce' ? 'E-Ticaret' : 'Şahsi';
      const newPayment = {
        id: Date.now(),
        method: 'Havale/EFT',
        amount: tlAmount,
        gramAmount: gramAmount,
        debtType: selectedCollectionDebt?.debt_type,
        bankAccount: `${bankAccount.account_name} (${accountType})`,
        bankAccountId: bankAccount.id
      };
      setCollectionMixedPayments(prev => [...prev, newPayment]);
      setCollectionPaymentAmount('');
      setCollectionPaymentMethod('');
      setSelectedCollectionBankAccount(null);
      setTimeout(() => {
        const input = document.querySelector('.collection-payment-input');
        if (input) input.focus();
      }, 50);
      if (gramAmount) {
        toast.success(`${gramAmount.toFixed(selectedCollectionDebt?.debt_type === 'GUMUS' ? 2 : 3)} gr (₺${tlAmount.toLocaleString('tr-TR', {minimumFractionDigits: 2})}) Havale/EFT - ${bankAccount.account_name} eklendi`);
      } else {
        toast.success(`₺${tlAmount.toLocaleString('tr-TR', {minimumFractionDigits: 2})} Havale/EFT - ${bankAccount.account_name} eklendi`);
      }
      return;
    }

    const newPayment = {
      id: Date.now(),
      method: method,
      amount: tlAmount,
      gramAmount: gramAmount,
      debtType: selectedCollectionDebt?.debt_type
    };

    // Hurda ödemesinde scrap detaylarını da kaydet
    if (collectionScrapMode && method === 'Hurda') {
      const scrapCalc = calculateCollectionScrapValue();
      const metalType = collectionScrapData.metal_type || (isGoldStore ? 'gold' : 'silver');
      newPayment.method = `Hurda (${metalType === 'gold' ? 'Altın' : 'Gümüş'})`;
      newPayment.isScrap = true;
      newPayment.scrapDetails = {
        metal_type: metalType,
        purity: collectionScrapData.purity,
        gross_weight: parseFloat(collectionScrapData.gross_weight),
        waste_percent: parseFloat(collectionScrapData.waste_percent) || 0,
        waste_gram: parseFloat(collectionScrapData.waste_gram) || 0,
        pure_weight: scrapCalc.pureWeight,
        unit_price: parseFloat(collectionScrapData.unit_price),
        total_amount: scrapCalc.totalValue
      };
    }

    setCollectionMixedPayments(prev => [...prev, newPayment]);
    setCollectionPaymentAmount('');
    setCollectionPaymentMethod('');
    setCollectionScrapMode(false);
    setTimeout(() => {
      const input = document.querySelector('.collection-payment-input');
      if (input) input.focus();
    }, 50);

    if (gramAmount) {
      toast.success(`${gramAmount.toFixed(selectedCollectionDebt?.debt_type === 'GUMUS' ? 2 : 3)} gr (₺${tlAmount.toLocaleString('tr-TR', {minimumFractionDigits: 2})}) ${method} eklendi`);
    } else {
      toast.success(`₺${tlAmount.toLocaleString('tr-TR', {minimumFractionDigits: 2})} ${method} eklendi`);
    }
  };

  // Tahsilat - Ödeme Kaldır
  const removeCollectionPayment = (paymentId) => {
    setCollectionMixedPayments(prev => prev.filter(p => p.id !== paymentId));
  };

  // Tahsilat - Toplam ödeme tutarı
  const getCollectionPaymentsTotal = () => {
    return collectionMixedPayments.reduce((sum, p) => sum + p.amount, 0);
  };

  // Tahsilat - Kalan borç tutarı
  const getCollectionRemainingDebt = () => {
    let maxAmount = parseFloat(selectedCollectionDebt?.amount_tl) || 0;
    if (selectedCollectionDebt?.debt_type === 'GRAM') {
      maxAmount = calculateCurrentGramValue(selectedCollectionDebt.amount);
    } else if (selectedCollectionDebt?.debt_type === 'GUMUS') {
      maxAmount = calculateCurrentSilverGramValue(selectedCollectionDebt.amount);
    }
    return maxAmount - getCollectionPaymentsTotal();
  };

  // Son tahsilatları getir
  const fetchCollectionHistory = async () => {
    setLoadingCollectionHistory(true);
    try {
      const response = await fetch(`${API_URL}/debt-payments?storeId=${currentStoreId}`);
      const data = await response.json();
      setCollectionHistory(data);
    } catch (error) {
      console.error('Tahsilat geçmişi yükleme hatası:', error);
      toast.error('Tahsilat geçmişi yüklenemedi!');
    } finally {
      setLoadingCollectionHistory(false);
    }
  };

  // Tahsilat iptal et (sadece admin)
  const cancelCollectionPayment = (payment) => {
    if (user?.role !== 'admin') { toast.error('Bu işlem sadece admin yetkisiyle yapılabilir'); return; }
    showConfirmation({
      title: 'Tahsilat İptali',
      message: `${payment.customer_name} müşterisine ait ₺${Number(payment.amount || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} tutarındaki tahsilat iptal edilecek ve borç geri yüklenecektir.\n\nDevam etmek istiyor musunuz?`,
      confirmText: 'Evet, İptal Et',
      cancelText: 'Vazgeç',
      type: 'warning',
      onConfirm: async () => {
        try {
          // Grup tahsilatlarında tüm kayıtları sil (payment_ids virgülle ayrılmış ID'ler)
          const ids = payment.payment_ids ? String(payment.payment_ids).split(',') : [String(payment.id)];
          let allOk = true;
          for (const id of ids) {
            const response = await fetch(`${API_URL}/debt-payments/${id.trim()}`, { method: 'DELETE' });
            if (!response.ok) {
              const result = await response.json();
              toast.error(result.error || 'Tahsilat iptal edilemedi!');
              allOk = false;
              break;
            }
          }
          if (allOk) {
            toast.success('Tahsilat iptal edildi ve borç geri yüklendi!');
            fetchCollectionHistory();
            fetchCollectionDebtSummary();
            // Müşteri borç listesini de yenile
            if (collectionSelectedCustomer && payment.customer_id === collectionSelectedCustomer.id) {
              try {
                const debtsRes = await fetch(`${API_URL}/customers/${payment.customer_id}/debts`);
                const debtsData = await debtsRes.json();
                setCollectionCustomerDebts(filterActiveDebts(debtsData.success ? debtsData.debts : debtsData));
              } catch (e) { /* ignore */ }
            }
            // Tahsilat banner güncelle
            if (selectedCustomer && payment.customer_id === selectedCustomer.id) {
              try {
                const summaryResp = await fetch(`${API_URL}/customers/debts/summary`);
                const summaryData = await summaryResp.json();
                setSelectedCustomerDebt(summaryData.find(d => d.customer_id === selectedCustomer.id) || null);
              } catch (e) { /* ignore */ }
            }
          }
        } catch (error) {
          console.error('Tahsilat iptal hatası:', error);
          toast.error('Tahsilat iptal edilemedi!');
        }
      }
    });
  };

  // Tahsilat düzenleme modalını aç (sadece admin)
  const openEditCollectionPayment = async (payment) => {
    if (user?.role !== 'admin') { toast.error('Bu işlem sadece admin yetkisiyle yapılabilir'); return; }
    await fetchBankAccounts();
    setEditingCollectionPayment(payment);
    const currentAmount = parseFloat(payment.amount) || 0;
    // payment_details varsa onu kullan, yoksa tek kalem oluştur
    let details = [];
    if (payment.payment_details) {
      try {
        details = typeof payment.payment_details === 'string' ? JSON.parse(payment.payment_details) : payment.payment_details;
      } catch { details = []; }
    }
    if (!details || details.length === 0) {
      // payment_method'dan method çıkar
      let method = payment.payment_method || 'Nakit';
      if (method.includes('Nakit')) method = 'Nakit';
      else if (method.includes('Kredi')) method = 'Kredi Kartı';
      else if (method.includes('Havale') || method.includes('EFT')) method = 'Havale/EFT';
      details = [{ id: Date.now(), amount: currentAmount, method }];
    } else {
      // payment_details toplamı güncel amount ile uyuşmuyorsa, tek kalemli ise amount'u kullan
      const detailsTotal = details.reduce((sum, d) => sum + (parseFloat(d.amount) || 0), 0);
      if (details.length === 1 && Math.abs(detailsTotal - currentAmount) > 0.01) {
        details = [{ ...details[0], amount: currentAmount }];
      }
    }
    setEditCollectionItems(details.map(d => ({ ...d, amount: parseFloat(d.amount) || 0 })));
    setEditCollectionNotes(payment.notes || '');
    // Tarih formatla
    let formattedDate = '';
    try {
      if (payment.created_at) {
        const date = new Date(payment.created_at);
        if (!isNaN(date.getTime())) {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          const hours = String(date.getHours()).padStart(2, '0');
          const minutes = String(date.getMinutes()).padStart(2, '0');
          formattedDate = `${year}-${month}-${day}T${hours}:${minutes}`;
        }
      }
    } catch (e) {}
    setEditCollectionDate(formattedDate);
  };

  // Tahsilat düzenlemeyi kaydet
  const saveEditCollectionPayment = async () => {
    if (!editingCollectionPayment) return;
    const validItems = editCollectionItems.filter(item => item.amount > 0);
    if (validItems.length === 0) {
      toast.error('En az bir ödeme kalemi olmalı');
      return;
    }
    const newTotal = validItems.reduce((sum, item) => sum + item.amount, 0);
    const methodText = validItems.length > 1 ? 'Karışık' : validItems[0].method;
    // amount yalnızca kullanıcı tutarı gerçekten değiştirdiğinde gönderilir.
    // Aksi halde grup tahsilatlarda (örn. 23500 = 18766 + 4734 split) backend tek
    // cdp'nin amount'unu toplam grup tutarına çekiyor → duplicate / şişmiş satır.
    const oldAmount = parseFloat(editingCollectionPayment.amount) || 0;
    const amountChanged = Math.abs(newTotal - oldAmount) > 0.01;
    const body = {
      payment_method: methodText,
      payment_details: JSON.stringify(validItems.map(item => ({ amount: item.amount, method: item.method }))),
      notes: editCollectionNotes,
      created_at: editCollectionDate ? new Date(editCollectionDate).toISOString() : undefined
    };
    if (amountChanged) {
      body.amount = newTotal;
      body.old_amount = oldAmount;
    }
    try {
      const response = await fetch(`${API_URL}/debt-payments/${editingCollectionPayment.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const result = await response.json();
      if (response.ok && result.success) {
        toast.success('Tahsilat güncellendi!');
        setEditingCollectionPayment(null);
        fetchCollectionHistory();
        fetchCollectionDebtSummary();
        if (collectionSelectedCustomer && editingCollectionPayment.customer_id === collectionSelectedCustomer.id) {
          try {
            const debtsRes = await fetch(`${API_URL}/customers/${editingCollectionPayment.customer_id}/debts`);
            const debtsData = await debtsRes.json();
            setCollectionCustomerDebts(filterActiveDebts(debtsData.success ? debtsData.debts : debtsData));
          } catch (e) { /* ignore */ }
        }
        if (selectedCustomer && editingCollectionPayment.customer_id === selectedCustomer.id) {
          try {
            const summaryResp = await fetch(`${API_URL}/customers/debts/summary`);
            const summaryData = await summaryResp.json();
            setSelectedCustomerDebt(summaryData.find(d => d.customer_id === selectedCustomer.id) || null);
          } catch (e) { /* ignore */ }
        }
      } else {
        toast.error(result.error || 'Tahsilat güncellenemedi!');
      }
    } catch (error) {
      console.error('Tahsilat düzenleme hatası:', error);
      toast.error('Tahsilat güncellenemedi!');
    }
  };

  // Borcu ödendi olarak işaretle (kısmi ödeme destekli)
  const markCollectionDebtAsPaid = async (staffOverride) => {
    if (!selectedCollectionDebt || collectionProcessing) return;
    setCollectionProcessing(true);

    // staffOverride varsa state'i güncelle
    if (staffOverride) {
      setCollectionStaffId(staffOverride.id);
      setCollectionStaffName(staffOverride.name);
    }
    const effectiveStaffId = staffOverride?.id || collectionStaffId;
    const effectiveStaffName = staffOverride?.name || collectionStaffName;

    // Kasiyer seçimi zorunlu
    if (!effectiveStaffId) {
      toast.error('Tahsilat yapabilmek için kasiyer seçmelisiniz!', { autoClose: 6000 });
      setShowCollectionStaffModal(true);
      setCollectionProcessing(false);
      return;
    }

    let amount;
    let scrapPurchaseId = null;
    let createdScrapIds = [];
    let scrapCalcResult = null;
    let paymentMethodText = '';
    let paymentsToProcess = [];

    if (collectionMixedPayments.length > 0) {
      // Karışık ödeme modu
      amount = getCollectionPaymentsTotal();
      paymentsToProcess = [...collectionMixedPayments];

      // Hurda ödemelerini scrap_purchases tablosuna kaydet
      const scrapPayments = paymentsToProcess.filter(p => p.isScrap && p.scrapDetails);
      for (const sp of scrapPayments) {
        try {
          const scrapResponse = await fetch(`${API_URL}/scrap-purchases`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              customer_id: collectionSelectedCustomer?.id || null,
              customer_name: collectionSelectedCustomer?.name || 'Tahsilat Müşterisi',
              customer_phone: collectionSelectedCustomer?.phone || '',
              staff_id: effectiveStaffId || user?.id || null,
              staff_name: effectiveStaffName || user?.name || null,
              store_id: currentStoreId,
              metal_type: sp.scrapDetails.metal_type,
              purity: sp.scrapDetails.purity,
              gross_weight: sp.scrapDetails.gross_weight,
              waste_percent: sp.scrapDetails.waste_percent,
              waste_gram: sp.scrapDetails.waste_gram,
              pure_weight: sp.scrapDetails.pure_weight,
              unit_price: sp.scrapDetails.unit_price,
              total_amount: sp.scrapDetails.total_amount,
              notes: `Tahsilat (Karışık) - Borç ID: ${selectedCollectionDebt.id}`,
              payment_method: 'Tahsilat Mahsup',
              paid_cash: 0,
              paid_transfer: 0,
              paid_credit: sp.scrapDetails.total_amount
            })
          });
          const scrapData = await scrapResponse.json();
          if (scrapData.success) {
            scrapPurchaseId = scrapData.id;
            createdScrapIds.push(scrapData.id);
          }
        } catch (error) {
          console.error('Karışık ödeme hurda kaydetme hatası:', error);
        }
      }

      // Ödeme yöntemlerini birleştir
      const methodCounts = {};
      paymentsToProcess.forEach(p => {
        methodCounts[p.method] = (methodCounts[p.method] || 0) + p.amount;
      });
      const descriptiveText = Object.entries(methodCounts)
        .map(([method, amt]) => `${method}: ₺${amt.toLocaleString('tr-TR', {minimumFractionDigits: 0})}`)
        .join(', ');
      const uniqueMethods = Object.keys(methodCounts);
      // Birden fazla farklı ödeme yöntemi varsa 'Karışık' olarak kaydet (gün sonu raporu doğru dağıtsın)
      paymentMethodText = uniqueMethods.length > 1 ? 'Karışık' : (uniqueMethods[0] || descriptiveText);

      if (amount <= 0) {
        toast.warning('Lütfen en az bir ödeme ekleyin!');
        setCollectionProcessing(false);
        return;
      }
    } else {
      amount = parseFloat(collectionPaymentAmount);
      if (isNaN(amount) || amount <= 0) {
        toast.warning('Geçerli bir tutar girin!');
        setCollectionProcessing(false);
        return;
      }
      paymentMethodText = collectionPaymentMethod;
    }

    // GRAM/GUMUS borçlarda fazla ödeme kabul edilmez (kur belirsiz);
    // TL borçlarda fazla ödeme alacak olarak işlenir, üst sınır yok.
    const isGramDebtType = selectedCollectionDebt.debt_type === 'GRAM' || selectedCollectionDebt.debt_type === 'GUMUS';
    if (isGramDebtType) {
      let maxAmount = parseFloat(selectedCollectionDebt.amount_tl) || 0;
      if (selectedCollectionDebt.debt_type === 'GRAM') {
        maxAmount = calculateCurrentGramValue(selectedCollectionDebt.amount);
      } else {
        maxAmount = calculateCurrentSilverGramValue(selectedCollectionDebt.amount);
      }
      if (amount > maxAmount * 1.01) {
        toast.warning(`Gram borçlarda fazla ödeme yapılamaz. Borç: ₺${maxAmount.toLocaleString('tr-TR', {maximumFractionDigits: 2})}`);
        setCollectionProcessing(false);
        return;
      }
    }

    try {
      let response, data;

      const finalPaymentMethod = paymentMethodText;

      // Gram borçlar için toplam gram miktarını hesapla
      let totalGramAmount = null;
      const isGramDebt = selectedCollectionDebt?.debt_type === 'GRAM' || selectedCollectionDebt?.debt_type === 'GUMUS';
      if (isGramDebt && collectionMixedPayments.length > 0) {
        const gramPayments = collectionMixedPayments.filter(p => p.gramAmount);
        if (gramPayments.length > 0) {
          totalGramAmount = gramPayments.reduce((sum, p) => sum + p.gramAmount, 0);
        }
      }
      // Tek ödeme gram modunda ise
      if (isGramDebt && collectionPaymentInputMode === 'gram' && !collectionMixedPayments.length) {
        totalGramAmount = parseFloat(collectionPaymentAmount) || null;
      }

      // Grup ödemesi mi yoksa tek borç ödemesi mi?
      if (selectedCollectionDebt.isGroup && selectedCollectionDebt.debt_ids) {
        // Grup ödemesi
        response = await fetch(`${API_URL}/debts/group/pay`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            debtIds: selectedCollectionDebt.debt_ids,
            customerId: collectionSelectedCustomer?.id,
            paymentMethod: finalPaymentMethod,
            paymentAmount: amount,
            gramAmount: totalGramAmount,
            scrapPurchaseId: scrapPurchaseId,
            storeId: currentStoreId,
            staffId: effectiveStaffId || user?.id,
            staffName: effectiveStaffName || user?.name,
            paymentDetails: paymentsToProcess.length > 0 ? paymentsToProcess : null
          })
        });
        data = await response.json();
      } else {
        // Tek borç ödemesi
        response = await fetch(`${API_URL}/debts/${selectedCollectionDebt.id}/pay`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            customerId: collectionSelectedCustomer?.id,
            paymentMethod: finalPaymentMethod,
            paymentAmount: amount,
            gramAmount: totalGramAmount,
            scrapPurchaseId: scrapPurchaseId,
            storeId: currentStoreId,
            staffId: effectiveStaffId || user?.id,
            staffName: effectiveStaffName || user?.name,
            paymentDetails: paymentsToProcess.length > 0 ? paymentsToProcess : null
          })
        });
        data = await response.json();
      }
      if (data.success) {
        // Fiş yazdırma için bilgileri sakla
        const receiptData = {
          customerName: collectionSelectedCustomer?.name || 'Bilinmiyor',
          customerPhone: collectionSelectedCustomer?.phone || '',
          debtDescription: selectedCollectionDebt.description || (selectedCollectionDebt.sale_id ? `Satış #${selectedCollectionDebt.sale_id}` : 'Borç Tahsilatı'),
          originalAmount: data.previousBalance ?? selectedCollectionDebt.amount_tl,
          paidAmount: amount,
          remainingAmount: Math.max(0, data.remaining || 0),
          isFullyPaid: data.isFullyPaid,
          paymentMethod: finalPaymentMethod,
          paymentDetails: paymentsToProcess.length > 0 ? paymentsToProcess : null,
          staffName: effectiveStaffName || user?.name || 'Personel',
          date: new Date()
        };

        // Mesaj göster
        if (data.overpayment && data.overpayment > 0.01) {
          toast.success(`₺${amount.toLocaleString('tr-TR', {minimumFractionDigits: 2})} ${paymentMethodText} ile alındı. Borç kapatıldı, ₺${data.overpayment.toLocaleString('tr-TR', {minimumFractionDigits: 2})} müşteri alacaklı.`);
        } else if (data.isFullyPaid) {
          toast.success(`₺${amount.toLocaleString('tr-TR', {minimumFractionDigits: 2})} ${paymentMethodText} ile ödendi! Borç tamamen kapatıldı.`);
        } else {
          toast.success(`₺${amount.toLocaleString('tr-TR', {minimumFractionDigits: 2})} ${paymentMethodText} ile tahsil edildi. Kalan borç: ₺${data.remaining?.toFixed(2)}`);
        }

        // ÖNCELİKLİ: Modal'ı kapat ve state'i sıfırla (ikincil işlemlerden önce)
        setShowCollectionPaymentModal(false);
        setSelectedCollectionDebt(null);
        setCollectionPaymentAmount('');
        setCollectionMixedPayments([]);
        setSelectedCollectionBankAccount(null);
        setCollectionScrapMode(false);
        setCollectionScrapData({
          metal_type: isGoldStore ? 'gold' : 'silver',
          gross_weight: '',
          waste_percent: '',
          waste_gram: '',
          purity: isGoldStore ? '22' : '925',
          unit_price: ''
        });

        // İkincil işlemler (modal zaten kapandı, hata olsa bile sorun yok)
        try {
          // Borç listesini sunucudan yeniden çek
          const debtsResponse = await fetch(`${API_URL}/customers/${collectionSelectedCustomer.id}/debts`);
          const updatedDebts = await debtsResponse.json();
          setCollectionCustomerDebts(filterActiveDebts(updatedDebts.success ? updatedDebts.debts : updatedDebts));

          // Eğer tüm borçlar ödendiyse borç modalını kapat
          const activeDebts = filterActiveDebts(updatedDebts.success ? updatedDebts.debts : updatedDebts);
          if (activeDebts.length === 0) {
            setShowCollectionDebtModal(false);
            setCollectionSelectedCustomer(null);
          }
        } catch (debtsErr) {
          console.error('Borç listesi güncelleme hatası:', debtsErr);
        }

        // Log kaydı - Borç tahsilatı (Detaylı)
        logService.log(
          LOG_MODULES.DEBTS,
          LOG_ACTIONS.DEBT_PAYMENT,
          `Tahsilat - ${collectionSelectedCustomer?.name || 'Bilinmiyor'} - ₺${amount.toLocaleString('tr-TR', {minimumFractionDigits: 2})} - ${paymentMethodText}`,
          {
            entity_type: 'customer',
            entity_id: collectionSelectedCustomer?.id,
            status: 'success',
            details: {
              customer: {
                id: collectionSelectedCustomer?.id,
                name: collectionSelectedCustomer?.name || 'Bilinmiyor',
                phone: collectionSelectedCustomer?.phone || null
              },
              debt: {
                id: selectedCollectionDebt.id,
                description: selectedCollectionDebt.description || `Satış #${selectedCollectionDebt.sale_id}`,
                debt_type: selectedCollectionDebt.debt_type || 'TL',
                original_amount: selectedCollectionDebt.amount_tl
              },
              payment: {
                amount: amount,
                payment_method: paymentMethodText,
                is_scrap_payment: collectionScrapMode,
                scrap_details: collectionScrapMode && scrapCalcResult ? {
                  metal_type: collectionScrapData.metal_type || (isGoldStore ? 'gold' : 'silver'),
                  purity: collectionScrapData.purity,
                  gross_weight: collectionScrapData.gross_weight,
                  pure_weight: scrapCalcResult.pureWeight
                } : null
              },
              result: {
                is_fully_paid: data.isFullyPaid,
                remaining_amount: data.remaining || 0
              },
              staff: {
                id: user?.id,
                name: user?.name
              }
            }
          }
        );

        await fetchCollectionDebtSummary();

        // Seçili müşterinin borç bilgisini güncelle (tahsilat banner)
        if (selectedCustomer && collectionSelectedCustomer && selectedCustomer.id === collectionSelectedCustomer.id) {
          try {
            const summaryResp = await fetch(`${API_URL}/customers/debts/summary`);
            const summaryData = await summaryResp.json();
            const updatedDebt = summaryData.find(d => d.customer_id === selectedCustomer.id);
            setSelectedCustomerDebt(updatedDebt || null);
          } catch (e) {
            console.error('Borç banner güncelleme hatası:', e);
          }
        }

        // Tahsilat fişi yazdırma seçeneği
        const savedReceiptData = receiptData;
        showConfirmation({
          title: 'Tahsilat Fişi Yazdır',
          message: 'Tahsilat fişi kaç adet yazdırılsın?',
          confirmText: 'Tahsilat Fişi Yazdır (2 Adet)',
          cancelText: 'Kapat (1 Adet)',
          type: 'info',
          onConfirm: () => {
            // 2 adet tahsilat fişi yazdır
            printCollectionReceipt(savedReceiptData);
            setTimeout(() => printCollectionReceipt(savedReceiptData), 1500);
          },
          onCancel: () => {
            // 1 adet tahsilat fişi yazdır
            printCollectionReceipt(savedReceiptData);
          }
        });
      } else {
        // API başarısız döndü - hata göster
        toast.error(data.error || data.message || 'Tahsilat işlemi başarısız oldu!');
        // Oluşturulan hurda kayıtlarını temizle (borç ödemesi başarısız olduğu için)
        for (const scId of createdScrapIds) {
          try {
            await fetch(`${API_URL}/scrap-purchases/${scId}`, { method: 'DELETE' });
          } catch (cleanupErr) {
            console.error('Hurda kaydı temizleme hatası:', cleanupErr);
          }
        }
      }
    } catch (error) {
      console.error('Borç güncelleme hatası:', error);
      toast.error('İşlem başarısız: ' + (error.message || 'Bilinmeyen hata'));
      // Oluşturulan hurda kayıtlarını temizle
      for (const scId of createdScrapIds) {
        try {
          await fetch(`${API_URL}/scrap-purchases/${scId}`, { method: 'DELETE' });
        } catch (cleanupErr) {
          console.error('Hurda kaydı temizleme hatası:', cleanupErr);
        }
      }
    } finally {
      setCollectionProcessing(false);
    }
  };

  // Tahsilat fişi yazdır (80mm termal yazıcı formatı)
  const printCollectionReceipt = (receiptData) => {
    const formatDate = (date) => {
      return new Date(date).toLocaleString('tr-TR', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      });
    };
    const formatMoney = (amount) => {
      return Number(amount || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const receiptNo = `THS-${Date.now().toString().slice(-8)}`;

    const collectionHtml = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Tahsilat Fişi - ${receiptNo}</title>
        <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.6/dist/JsBarcode.all.min.js"><\/script>
        <style>
          @page { size: 80mm auto; margin: 5mm; }
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body {
            font-family: Arial, Helvetica, sans-serif;
            font-size: 12px;
            font-weight: 600;
            width: 80mm;
            padding: 5mm;
            background: #fff;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .header { text-align: center; padding-bottom: 10px; margin-bottom: 10px; }
          .logo-img { width: 120px; height: auto; margin-bottom: 8px; filter: invert(1); }
          .subtitle { font-size: 13px; font-weight: bold; margin-top: 5px; }
          .date-line { text-align: center; font-size: 13px; color: #000; font-weight: 600; margin-bottom: 5px; }
          .receipt-no { text-align: center; font-size: 11px; font-weight: bold; margin-bottom: 10px; }
          .divider { border-top: 1px dashed #333; margin: 8px 0; }
          .row { display: flex; justify-content: space-between; font-size: 11px; padding: 3px 0; }
          .row-grand-total {
            display: flex; justify-content: space-between;
            font-size: 16px; font-weight: bold; padding: 8px 0;
            border-top: 2px solid #000; border-bottom: 2px solid #000; margin: 5px 0;
          }
          .section-label { font-size: 10px; font-weight: bold; color: #333; margin-bottom: 4px; }
          .customer-section { font-size: 11px; margin: 5px 0; }
          .staff-section { font-family: Arial, sans-serif; font-size: 10px; color: #000; margin: 5px 0; }
          .status-box {
            text-align: center; padding: 8px; margin: 8px 0;
            font-weight: bold; font-size: 12px; border-radius: 4px;
          }
          .status-paid { background: none; color: #000; border: 1px solid #000; }
          .status-remaining { background: none; color: #000; border: 1px solid #000; }
          .barcode-section { text-align: center; margin: 10px 0; }
          .barcode-number { font-size: 10px; color: #000; margin-top: 2px; }
          .footer { text-align: center; margin-top: 10px; padding-top: 10px; font-size: 10px; color: #000; }
          @media print { body { width: 80mm; } .no-print { display: none; } }
        </style>
      </head>
      <body>
        <div class="header">
          ${currentStoreId === 1
            ? '<img src="/Siyah - Beyaz - 1.png" alt="Akik Gold Jewellery" class="logo-img" />'
            : '<div style="margin: 0 auto 2px auto; text-align: center;"><img src="/akikfisligi.png" alt="Akik Gümüş" style="width: 140px; height: auto; filter: contrast(3);" /></div>'
          }
          ${currentStoreId === 1
            ? '<div style="font-family: Arial, sans-serif; font-size: 10px; color: #000; line-height: 1.4; margin-bottom: 5px;">Osmangazi Mah. Ahmet Şireci Bulvarı No: 14D<br/>Şehitkamil / Gaziantep<br/>Tel: 0342 230 00 64<br/>www.akikjewellery.com | info@akikjewellery.com</div>'
            : `<div style="font-family: Arial, sans-serif; font-size: 11px; font-weight: bold; color: #000; margin-bottom: 3px;">${currentStoreId === 3 ? '(YEŞİLSU ŞUBESİ)' : '(OSMANGAZİ ŞUBESİ)'}</div>
               <div style="font-family: Arial, sans-serif; font-size: 10px; color: #000; line-height: 1.4; margin-bottom: 5px;">${currentStoreId === 3 ? 'Bey Mah. Atatürk Blv. 43/A<br/>Şahinbey / Gaziantep' : 'Osmangazi Mah. Ahmet Şireci Bulvarı No: 16/F<br/>Şehitkamil / Gaziantep'}<br/>Tel: 0342 230 00 64</div>`
          }
          <div class="subtitle">TAHSİLAT FİŞİ</div>
        </div>

        <div class="date-line">${formatDate(receiptData.date)}</div>
        <div class="receipt-no">İşlem No: ${receiptNo}</div>

        <div class="divider"></div>

        <div class="customer-section">
          <span style="color: #000; font-weight: bold;">Müşteri:</span> ${receiptData.customerName}
          ${receiptData.customerPhone ? `<br/><span style="color: #000; font-weight: bold;">Tel:</span> ${receiptData.customerPhone}` : ''}
        </div>

        <div class="divider"></div>

        <div class="row"><span>Açıklama:</span><span>${receiptData.debtDescription}</span></div>
        <div class="row"><span>Borç Tutarı:</span><span>₺${formatMoney(receiptData.originalAmount)}</span></div>

        <div class="row-grand-total">
          <span>ÖDENEN:</span>
          <span>₺${formatMoney(receiptData.paidAmount)}</span>
        </div>

        ${!receiptData.isFullyPaid ? `
        <div class="status-box status-remaining">
          KALAN BORÇ: ₺${formatMoney(receiptData.remainingAmount)}
        </div>
        ` : `
        <div class="status-box status-paid">
          BORÇ TAMAMEN ÖDENDİ ✓
        </div>
        `}

        ${(() => {
          const details = receiptData.paymentDetails;
          const methodLabel = (p) => {
            const m = p.method || 'Ödeme';
            const bank = p.bankAccount || p.bank_account;
            return (m === 'Havale/EFT' || m === 'Havale') && bank ? `${m} (${bank})` : m;
          };
          if (!Array.isArray(details) || details.length === 0) {
            return `<div class="row"><span>Ödeme Yöntemi:</span><span>${receiptData.paymentMethod}</span></div>`;
          }
          if (details.length === 1) {
            return `<div class="row"><span>Ödeme Yöntemi:</span><span>${methodLabel(details[0])}</span></div>`;
          }
          return details.map(p => `<div class="row"><span>${methodLabel(p)}:</span><span>₺${formatMoney(p.amount)}</span></div>`).join('');
        })()}
        <div class="staff-section">Personel: ${receiptData.staffName}</div>

        <div class="divider"></div>

        <div class="barcode-section">
          <svg id="barcode-receipt"></svg>
          <div class="barcode-number">${receiptNo}</div>
        </div>

        <div class="footer">
          <div style="font-weight: bold;">Ödemeniz için teşekkür ederiz.</div>
          <div style="font-size: 10px; color: #000; margin-top: 5px;">Bu fiş bilgi amaçlıdır.</div>
        </div>

        <script>
          window.onload = function() {
            try {
              JsBarcode("#barcode-receipt", "${receiptNo}", {
                format: "CODE128", width: 1.5, height: 40,
                displayValue: false, margin: 0, background: "transparent"
              });
            } catch(e) { console.error('Barkod hatası:', e); }
            var directPrint = localStorage.getItem('directPrintEnabled') === 'true';
            setTimeout(function() {
              window.print();
              if (directPrint) { setTimeout(function() { window.close(); }, 500); }
            }, 300);
          }
        </script>
      </body>
      </html>
    `;
    handlePrint(collectionHtml, 'receipt');
  };

  // Satış fişi yazdır (80mm termal yazıcı formatı)
  const printSaleReceipt = (data) => {
    const formatDate = (date) => {
      return new Date(date).toLocaleString('tr-TR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    };

    const formatMoney = (amount) => {
      return Number(amount || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    // Kasiyer maskeleme: "Ahmet Kaya" → "A**** K***"
    const maskName = (name) => {
      if (!name) return '';
      return name.split(' ').map(word => {
        if (word.length <= 1) return word;
        return word[0] + '*'.repeat(word.length - 1);
      }).join(' ');
    };

    // Ödeme yöntemi Türkçe karşılıkları
    const paymentMethodLabel = (method) => {
      const labels = {
        'Nakit': 'Nakit',
        'Kredi Kartı': 'Kredi Kartı',
        'Havale': 'Havale/EFT',
        'Çoklu Ödeme': 'Çoklu Ödeme',
        'Açık Hesap': 'Açık Hesap',
        'Hurda Borca Al': 'Hurda Borca Al'
      };
      return labels[method] || method;
    };

    // Sepet indirimini orantılı dağıtım için taban (yalnızca ESKİ fişlerde, kalem bazında
    // indirimli fiyat yoksa fallback olarak kullanılır).
    const printItemsBase = (data.items || []).reduce((sum, it) => {
      const op = Number(it.original_price || it.price || 0);
      return sum + (op * (it.quantity || 1));
    }, 0);
    const printAdjBase = (data.adjustmentItems || []).reduce((sum, adj) => sum + Number(adj.amount || 0), 0);
    const printTotalDiscount = Number(data.totalDiscount || 0);
    const printDiscountRatio = ((printItemsBase + printAdjBase) > 0 && printTotalDiscount > 0)
      ? ((printItemsBase + printAdjBase) - printTotalDiscount) / (printItemsBase + printAdjBase)
      : 1;

    // YENİ fişler: kalem birim fiyatı ekrandaki 4 modalla AYNI kaynaktan (computeSaleItemPricing).
    const receiptDate = data.date ? new Date(data.date) : new Date();
    let unifiedUnitPrices = null;
    if (receiptDate >= UNIFIED_RECEIPT_PRICING_CUTOFF && Array.isArray(data.items) && data.items.length > 0) {
      const mapped = data.items.map(it => ({
        quantity: it.quantity || 1,
        original_unit_price: Number(it.original_price || it.price || 0),
        discounted_unit_price: Number(it.discountedUnitPrice ?? it.discounted_price ?? it.price ?? 0)
      }));
      const p = computeSaleItemPricing({ subtotal: data.subtotal, discount: data.totalDiscount, items: mapped }, []);
      unifiedUnitPrices = mapped.map(m => p.itemUnitPrice(m));
    }

    // Ürün satırları HTML (tablo formatı)
    const itemsHtml = data.items.map((item, idx) => {
      const qty = item.quantity || 1;
      const originalPrice = Number(item.original_price || item.price || 0);
      // Kalem bazında saklı indirimli birim fiyat varsa DOĞRUDAN onu kullan; Fiş Detayı,
      // Değişim ve İade ekranları da aynısını yapıyor. Yoksa sepet indirimini orantılı dağıt.
      const storedDiscountedUnit = Number(item.discountedUnitPrice || item.discounted_price || 0);
      const effectiveUnitPrice = unifiedUnitPrices
        ? unifiedUnitPrices[idx]
        : (storedDiscountedUnit > 0
            ? storedDiscountedUnit
            : (printDiscountRatio < 1 ? originalPrice * printDiscountRatio : Number(item.price || 0)));

      return `
        <tr>
          <td class="col-name">${item.name}${item.barcode ? `<div style="color:#000;">(${item.barcode})</div>` : ''}${item.note ? `<div class="item-note">${item.note}</div>` : ''}</td>
          <td class="col-qty">${qty}</td>
          <td class="col-price">₺${formatMoney(originalPrice)}</td>
          <td class="col-final">₺${formatMoney(effectiveUnitPrice * qty)}</td>
        </tr>
      `;
    }).join('');

    // Fark (adjustment) satırları - tablo formatında (indirim payı dahil)
    let adjustmentHtml = '';
    if (data.adjustmentItems && data.adjustmentItems.length > 0) {
      adjustmentHtml = data.adjustmentItems.map(adj => {
        const originalAmt = Number(adj.amount || 0);
        const discShare = Number(adj.discountShare || 0);
        const netAmt = discShare > 0 ? originalAmt - discShare : originalAmt;
        const discPct = (discShare > 0.01 && originalAmt > 0) ? ((discShare / originalAmt) * 100) : 0;
        const discPctStr = discPct > 0 ? (Number.isInteger(discPct) ? discPct : parseFloat(discPct.toFixed(2))) : 0;
        return `
          <tr>
            <td class="col-name">${adj.description || adj.reason || 'Fark'}</td>
            <td class="col-qty">1</td>
            <td class="col-price">₺${formatMoney(originalAmt)}</td>
            <td class="col-final">₺${formatMoney(netAmt)}</td>
          </tr>
        `;
      }).join('');
    }

    // İndirim satırları
    let discountRows = '';
    if (data.campaignDiscount > 0) {
      discountRows += `<div class="row"><span>Kampanya İndirimi:</span><span>-₺${formatMoney(data.campaignDiscount)}</span></div>`;
    }
    if (data.giftCardDiscount > 0) {
      discountRows += `<div class="row"><span>Bakiye:</span><span>-₺${formatMoney(data.giftCardDiscount)}</span></div>`;
    }
    // Hurda artık ödeme bölümünde gösteriliyor, indirim olarak değil
    if (data.scrapDebtDiscount > 0) {
      discountRows += `<div class="row"><span>Hurda Alacak İnd.:</span><span>-₺${formatMoney(data.scrapDebtDiscount)}</span></div>`;
    }
    // Müşteri bakiyesi indirim değil ödeme yöntemi olarak gösterilecek
    // Manuel indirim (kampanya/kart dışı)
    const manualDiscount = data.totalDiscount - (data.campaignDiscount || 0) - (data.giftCardDiscount || 0) - (data.scrapDebtDiscount || 0);
    if (manualDiscount > 0) {
      discountRows += `<div class="row"><span>İndirim:</span><span>-₺${formatMoney(manualDiscount)}</span></div>`;
    }

    // Müşteriye iade tutarı hesapla (hurda tutarı satış tutarını aştığında)
    const customerRefundAmount = (data.scrapDiscount > 0 && data.total < 0)
      ? Math.abs(data.total)
      : (data.scrapDiscount > 0 && data.total === 0 && data.subtotal > 0)
        ? Math.max(0, data.scrapDiscount - (data.subtotal - (data.totalDiscount || 0) + (data.adjustmentTotal || 0)))
        : 0;

    // Ödeme detayları
    let paymentHtml = '';
    if (customerRefundAmount > 0) {
      // Hurda tutarı satış tutarını aşıyor - müşteriye iade yapıldı
      paymentHtml = `<div class="row"><span>Müşteriye Nakit İade:</span><span>₺${formatMoney(customerRefundAmount)}</span></div>`;
    } else if (data.paymentDetails && data.paymentDetails.length > 0) {
      paymentHtml = data.paymentDetails.map(p => {
        const methodText = (p.method === 'Havale/EFT' || p.method === 'Havale') && (p.bank_account || p.bankAccount)
          ? `${paymentMethodLabel(p.method)} (${stripBankAccountType(p.bank_account || p.bankAccount)})`
          : paymentMethodLabel(p.method);
        let line = `<div class="row"><span>${methodText}</span>`;
        if (p.currency && p.currency !== 'TRY') {
          line += `<span>${Number(p.amount).toLocaleString('tr-TR', {minimumFractionDigits: 2})} ${p.currency} (₺${formatMoney(p.amountTL || p.amount_tl)})</span>`;
        } else {
          line += `<span>₺${formatMoney(p.amountTL || p.amount_tl || p.amount)}</span>`;
        }
        line += `</div>`;
        return line;
      }).join('');
    } else {
      paymentHtml = `<div class="row"><span>${paymentMethodLabel(data.paymentMethod)}</span><span>₺${formatMoney(data.total)}</span></div>`;
    }

    const maskedStaff = maskName(data.staffNames);
    const receiptNumber = String(data.receiptNumber);

    const saleHtml = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Satış Fişi - ${receiptNumber}</title>
        <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.6/dist/JsBarcode.all.min.js"><\/script>
        <style>
          @page { size: 80mm auto; margin: 5mm; }
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body {
            font-family: Arial, Helvetica, sans-serif;
            font-size: 12px;
            font-weight: 600;
            width: 80mm;
            padding: 5mm;
            background: #fff;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .header {
            text-align: center;
            padding-bottom: 4px;
            margin-bottom: 4px;
          }
          .logo-img {
            width: 120px;
            height: auto;
            margin-bottom: 8px;
            filter: invert(1);
          }
          .date-line {
            text-align: center;
            font-size: 13px;
            color: #000;
            font-weight: 600;
            margin-bottom: 5px;
          }
          .receipt-no {
            text-align: center;
            font-size: 11px;
            font-weight: bold;
            margin-bottom: 10px;
          }
          .divider {
            border-top: 1px dashed #333;
            margin: 8px 0;
          }
          .items-table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 4px;
          }
          .items-table th {
            font-size: 10px;
            font-weight: bold;
            text-align: center;
            padding: 3px 1px;
            border-bottom: 1px solid #000;
            white-space: nowrap;
          }
          .items-table th:first-child {
            text-align: left;
          }
          .items-table td {
            font-size: 11px;
            padding: 4px 1px;
            vertical-align: top;
            text-align: center;
            border-bottom: 1px dotted #ddd;
          }
          .col-name {
            text-align: left !important;
            font-weight: bold;
            font-size: 11px;
            max-width: 90px;
            word-wrap: break-word;
          }
          .col-qty { white-space: nowrap; }
          .col-price { white-space: nowrap; }
          .col-disc { white-space: nowrap; font-weight: bold; }
          .col-final { white-space: nowrap; font-weight: bold; }
          .item-note {
            font-size: 9px;
            color: #000;
            margin-top: 1px;
            font-style: italic;
          }
          .totals-section {
            margin: 8px 0;
          }
          .row {
            display: flex;
            justify-content: space-between;
            font-size: 11px;
            padding: 3px 0;
          }
          .row-grand-total {
            display: flex;
            justify-content: space-between;
            font-size: 16px;
            font-weight: bold;
            padding: 8px 0;
            border-top: 2px solid #000;
            border-bottom: 2px solid #000;
            margin: 5px 0;
          }
          .payment-section {
            margin: 8px 0;
          }
          .section-label {
            font-family: Arial, sans-serif;
            font-size: 13px;
            font-weight: 600;
            color: #000;
            margin-bottom: 4px;
          }
          .customer-section {
            font-size: 11px;
            margin: 5px 0;
          }
          .staff-section {
            font-family: Arial, sans-serif;
            font-size: 10px;
            color: #000;
            margin: 5px 0;
          }
          .barcode-section {
            text-align: center;
            margin: 10px 0;
          }
          .barcode-number {
            font-size: 10px;
            color: #000;
            margin-top: 2px;
          }
          .footer {
            text-align: center;
            margin-top: 10px;
            padding-top: 10px;
            font-size: 10px;
            color: #000;
          }
          .footer .thanks {
            font-weight: bold;
            margin-bottom: 5px;
          }
          .footer .exchange-note {
            font-size: 10px;
            color: #000;
            margin-top: 5px;
          }
          @media print {
            body { width: 80mm; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          ${data.storeId === 1
            ? `<img src="/Siyah - Beyaz - 1.png" alt="Akik Gold Jewellery" class="logo-img" />`
            : `<div style="margin: 0 auto 2px auto; text-align: center;">
                <img src="/akikfisligi.png" alt="Akik Gümüş" style="width: 170px; height: auto; filter: contrast(3);" />
              </div>`
          }
          ${data.storeId === 1
            ? `<div style="font-family: Arial, sans-serif; font-size: 10px; color: #000; line-height: 1.4; margin-bottom: 2px;">Osmangazi Mah. Ahmet Şireci Bulvarı No: 14D<br/>Şehitkamil / Gaziantep<br/>Tel: 0342 230 00 64<br/>www.akikjewellery.com | info@akikjewellery.com</div>`
            : `<div style="font-family: Arial, sans-serif; font-size: 11px; font-weight: bold; color: #000; margin-bottom: 3px;">${data.storeId === 3 ? '(YEŞİLSU ŞUBESİ)' : '(OSMANGAZİ ŞUBESİ)'}</div>
               <div style="font-family: Arial, sans-serif; font-size: 10px; color: #000; line-height: 1.4; margin-bottom: 2px;">${data.storeId === 3 ? 'Bey Mah. Atatürk Blv. 43/A<br/>Şahinbey / Gaziantep' : 'Osmangazi Mah. Ahmet Şireci Bulvarı No: 16/F<br/>Şehitkamil / Gaziantep'}<br/>Tel: 0342 230 00 64</div>`
          }
        </div>

        <div style="text-align: center; font-family: Arial, sans-serif; font-size: 14px; font-weight: bold; color: #000; margin: 6px 0; letter-spacing: 1px;">BİLGİ FİŞİ</div>

        <div class="date-line">${formatDate(data.date)}</div>

        <div class="divider"></div>

        <table class="items-table">
          <thead>
            <tr>
              <th>Ürün</th>
              <th>Adet</th>
              <th>Fiyat</th>
              <th>Tutar</th>
            </tr>
          </thead>
          <tbody>
            ${itemsHtml}
            ${adjustmentHtml}
          </tbody>
        </table>

        <div class="divider"></div>

        <div class="totals-section">
          ${discountRows ? `<div class="row"><span>Ara Toplam:</span><span>₺${formatMoney(data.subtotal + (data.adjustmentTotal || 0))}</span></div>` : ''}
          ${discountRows}
          ${data.scrapDiscount > 0 ? '' : ''}
        </div>

        <div class="row-grand-total">
          <span>TOPLAM:</span>
          <span>₺${formatMoney(Math.max(0, data.scrapDiscount > 0 ? data.total + data.scrapDiscount : data.total))}</span>
        </div>

        <div class="payment-section">
          <div class="section-label">ÖDEME:</div>
          ${paymentHtml}
          ${data.scrapDiscount > 0 ? `<div class="row"><span>Hurda Alımı:</span><span>₺${formatMoney(data.scrapDiscount)}</span></div>` : ''}
        </div>

        ${data.customer ? `
        <div class="customer-section">
          <span style="color: #000; font-weight: bold;">Müşteri:</span> ${data.customer.name}
        </div>
        ` : ''}

        <div class="staff-section">Kasiyer: ${maskedStaff}</div>

        ${data.receiptNote ? `
        <div style="text-align: center; margin: 6px 0; padding: 4px 0; border-top: 1px dashed #000; border-bottom: 1px dashed #000;">
          <div style="font-size: 11px; font-weight: bold;">Fiş Açıklaması: ${data.receiptNote}</div>
        </div>
        ` : ''}

        <div class="divider"></div>

        <div class="barcode-section">
          <svg id="barcode-sale"></svg>
          <div class="barcode-number">${receiptNumber}</div>
        </div>

        <div class="footer">
          <div class="thanks">TEŞEKKÜR EDERİZ YİNE BEKLERİZ</div>
          <div class="exchange-note" style="font-weight: bold;">ÜRÜNLERDE İADE YOKTUR</div>
          <div class="exchange-note" style="font-weight: bold;">BİLGİ FİŞİDİR MALİ DEĞERİ YOKTUR</div>
          <div class="exchange-note" style="font-weight: bold;">DEĞİŞİM SÜRECİ 7 GÜN İÇERİSİNDE YAPILMAKTADIR</div>
        </div>

        <script>
          window.onload = function() {
            try {
              JsBarcode("#barcode-sale", "${receiptNumber}", {
                format: "CODE128", width: 1.5, height: 40,
                displayValue: false, margin: 0, background: "transparent"
              });
            } catch(e) { console.error('Barkod hatası:', e); }
            var directPrint = localStorage.getItem('directPrintEnabled') === 'true';
            setTimeout(function() {
              window.print();
              if (directPrint) { setTimeout(function() { window.close(); }, 500); }
            }, 300);
          }
        </script>
      </body>
      </html>
    `;
    handlePrint(saleHtml, 'receipt');
  };

  // Hediye / Değişim fişi yazdır (fiyatsız)
  // Hediye/Değişim Fişi - Akik Gold Jewellery (Gold, storeId === 1)
  const printGiftExchangeReceiptGold = (data) => {
    const formatDate = (date) => {
      return new Date(date).toLocaleString('tr-TR', {
        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
      });
    };

    const maskCashierName = (name) => {
      if (!name) return '';
      return name.split(',').map(n =>
        n.trim().split(/\s+/).map(w => w.charAt(0).toUpperCase() + '***').join(' ')
      ).join(', ');
    };

    const formatMoney = (val) => Number(val || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    const cashierDisplay = maskCashierName(data.staffNames || '');

    // Müşteriye nakit iade tutarı (hurda tutarı satış tutarını aştığında)
    const customerRefundAmount = (data.scrapDiscount > 0 && data.total < 0)
      ? Math.abs(data.total)
      : (data.scrapDiscount > 0 && data.total === 0 && data.subtotal > 0)
        ? Math.max(0, data.scrapDiscount - (data.subtotal - (data.totalDiscount || 0) + (data.adjustmentTotal || 0)))
        : 0;

    const giftGoldHtml = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Hediye / Değişim Fişi</title>
        <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.6/dist/JsBarcode.all.min.js"><\/script>
        <style>
          @page { size: 80mm auto; margin: 5mm; }
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body {
            font-family: Arial, Helvetica, sans-serif;
            font-size: 12px;
            font-weight: 600;
            width: 80mm;
            padding: 5mm;
            background: #fff;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .header { text-align: center; padding-bottom: 4px; margin-bottom: 4px; }
          .logo-img { width: 120px; height: auto; margin-bottom: 8px; filter: invert(1); }
          .subtitle { font-size: 14px; font-weight: bold; margin-top: 5px; margin-bottom: 4px; letter-spacing: 1px; }
          .date-line { text-align: center; font-size: 13px; color: #000; font-weight: 600; margin-bottom: 5px; }
          .divider { border-top: 1px dashed #333; margin: 8px 0; }
          .item { display: flex; justify-content: space-between; align-items: center; padding: 4px 0; border-bottom: 1px dotted #ddd; }
          .item:last-child { border-bottom: none; }
          .item-name { font-size: 11px; font-weight: bold; flex: 1; }
          .item-qty { font-size: 11px; color: #000; font-weight: bold; white-space: nowrap; margin-left: 8px; }
          .customer-section { font-size: 11px; margin: 5px 0; }
          .cashier-section { font-size: 11px; margin: 5px 0; }
          .barcode-section { text-align: center; margin: 10px 0; }
          .barcode-number { font-size: 10px; color: #000; margin-top: 2px; }
          .footer { text-align: center; margin-top: 10px; padding-top: 10px; font-size: 10px; color: #000; line-height: 1.6; }
          @media print { body { width: 80mm; } .no-print { display: none; } }
        </style>
      </head>
      <body>
        <div class="header">
          <img src="/Siyah - Beyaz - 1.png" alt="Akik Gold Jewellery" class="logo-img" />
          <div style="font-family: Arial, sans-serif; font-size: 10px; color: #000; line-height: 1.4; margin-bottom: 2px;">Osmangazi Mah. Ahmet Şireci Bulvarı No: 14D<br/>Şehitkamil / Gaziantep<br/>Tel: 0342 230 00 64<br/>www.akikjewellery.com | info@akikjewellery.com</div>
          <div class="subtitle">HEDİYE / DEĞİŞİM FİŞİ</div>
        </div>

        <div class="date-line">${formatDate(data.date)}</div>

        <div class="divider"></div>

        ${cashierDisplay ? `
        <div class="cashier-section">
          <span style="color: #000; font-weight: bold;">Kasiyer:</span> ${cashierDisplay}
        </div>
        ` : ''}

        ${customerRefundAmount > 0 ? `
        <div class="divider"></div>
        <div style="font-size: 12px; font-weight: bold; text-align: center; padding: 6px 0; color: #000;">
          Müşteriye Nakit İade: ₺${formatMoney(customerRefundAmount)}
        </div>
        ` : ''}

        <div class="divider"></div>

        ${data.receiptNumber ? `
        <div class="barcode-section">
          <svg id="barcode-gift"></svg>
          <div class="barcode-number">${data.receiptNumber}</div>
        </div>
        ` : ''}

        <div class="footer">
          <div style="font-weight: bold;">TEŞEKKÜR EDERİZ YİNE BEKLERİZ</div>
          <div style="font-weight: bold;">ÜRÜNLERDE İADE YOKTUR</div>
          <div style="font-weight: bold;">BİLGİ FİŞİDİR MALİ DEĞERİ YOKTUR</div>
          <div style="font-weight: bold;">DEĞİŞİM SÜRECİ 7 GÜN İÇERİSİNDE YAPILMAKTADIR</div>
        </div>

        <script>
          window.onload = function() {
            ${data.receiptNumber ? `
            try {
              JsBarcode("#barcode-gift", "${data.receiptNumber}", {
                format: "CODE128", width: 1.5, height: 40,
                displayValue: false, margin: 0, background: "transparent"
              });
            } catch(e) { console.error('Barkod hatası:', e); }
            ` : ''}
            var directPrint = localStorage.getItem('directPrintEnabled') === 'true';
            setTimeout(function() {
              window.print();
              if (directPrint) { setTimeout(function() { window.close(); }, 500); }
            }, 300);
          }
        <\/script>
      </body>
      </html>
    `;
    handlePrint(giftGoldHtml, 'receipt');
  };

  // Hediye/Değişim Fişi - Akik Gümüş (Silver, storeId !== 1)
  const printGiftExchangeReceiptSilver = (data) => {
    const formatDate = (date) => {
      return new Date(date).toLocaleString('tr-TR', {
        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
      });
    };

    const maskCashierName = (name) => {
      if (!name) return '';
      return name.split(',').map(n =>
        n.trim().split(/\s+/).map(w => w.charAt(0).toUpperCase() + '***').join(' ')
      ).join(', ');
    };

    const formatMoney = (val) => Number(val || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    const cashierDisplay = maskCashierName(data.staffNames || '');

    // Müşteriye nakit iade tutarı (hurda tutarı satış tutarını aştığında)
    const customerRefundAmount = (data.scrapDiscount > 0 && data.total < 0)
      ? Math.abs(data.total)
      : (data.scrapDiscount > 0 && data.total === 0 && data.subtotal > 0)
        ? Math.max(0, data.scrapDiscount - (data.subtotal - (data.totalDiscount || 0) + (data.adjustmentTotal || 0)))
        : 0;

    const giftSilverHtml = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Hediye / Değişim Fişi</title>
        <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.6/dist/JsBarcode.all.min.js"><\/script>
        <style>
          @page { size: 80mm auto; margin: 5mm; }
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body {
            font-family: Arial, Helvetica, sans-serif;
            font-size: 12px;
            font-weight: 600;
            width: 80mm;
            padding: 5mm;
            background: #fff;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .header { text-align: center; padding-bottom: 10px; margin-bottom: 10px; }
          .subtitle { font-size: 14px; font-weight: bold; margin-top: 5px; margin-bottom: 8px; letter-spacing: 1px; }
          .date-line { text-align: center; font-size: 13px; color: #000; font-weight: 600; margin-bottom: 5px; }
          .divider { border-top: 1px dashed #333; margin: 8px 0; }
          .item { display: flex; justify-content: space-between; align-items: center; padding: 4px 0; border-bottom: 1px dotted #ddd; }
          .item:last-child { border-bottom: none; }
          .item-name { font-size: 11px; font-weight: bold; flex: 1; }
          .item-qty { font-size: 11px; color: #000; font-weight: bold; white-space: nowrap; margin-left: 8px; }
          .customer-section { font-size: 11px; margin: 5px 0; }
          .cashier-section { font-size: 11px; margin: 5px 0; }
          .barcode-section { text-align: center; margin: 10px 0; }
          .barcode-number { font-size: 10px; color: #000; margin-top: 2px; }
          .footer { text-align: center; margin-top: 10px; padding-top: 10px; font-size: 10px; color: #000; line-height: 1.6; }
          @media print { body { width: 80mm; } .no-print { display: none; } }
        </style>
      </head>
      <body>
        <div class="header">
          <div style="margin: 0 auto 2px auto; text-align: center;">
            <img src="/akikfisligi.png" alt="Akik Gümüş" style="width: 170px; height: auto; filter: contrast(3);" />
          </div>
          <div style="font-family: Arial, sans-serif; font-size: 11px; font-weight: bold; color: #000; margin-bottom: 3px;">${data.storeId === 3 ? '(YEŞİLSU ŞUBESİ)' : '(OSMANGAZİ ŞUBESİ)'}</div>
          <div style="font-family: Arial, sans-serif; font-size: 10px; color: #000; line-height: 1.4; margin-bottom: 5px;">${data.storeId === 3 ? 'Bey Mah. Atatürk Blv. 43/A<br/>Şahinbey / Gaziantep' : 'Osmangazi Mah. Ahmet Şireci Bulvarı No: 16/F<br/>Şehitkamil / Gaziantep'}<br/>Tel: 0342 230 00 64</div>
          <div class="subtitle">HEDİYE / DEĞİŞİM FİŞİ</div>
        </div>

        <div class="date-line">${formatDate(data.date)}</div>

        <div class="divider"></div>

        ${data.customer ? `
        <div class="customer-section">
          <span style="color: #000; font-weight: bold;">Müşteri:</span> ${data.customer.name}
        </div>
        ` : ''}

        ${cashierDisplay ? `
        <div class="cashier-section">
          <span style="color: #000; font-weight: bold;">Kasiyer:</span> ${cashierDisplay}
        </div>
        ` : ''}

        ${customerRefundAmount > 0 ? `
        <div class="divider"></div>
        <div style="font-size: 12px; font-weight: bold; text-align: center; padding: 6px 0; color: #000;">
          Müşteriye Nakit İade: ₺${formatMoney(customerRefundAmount)}
        </div>
        ` : ''}

        <div class="divider"></div>

        ${data.receiptNumber ? `
        <div class="barcode-section">
          <svg id="barcode-gift"></svg>
          <div class="barcode-number">${data.receiptNumber}</div>
        </div>
        ` : ''}

        <div class="footer">
          <div style="font-weight: bold;">TEŞEKKÜR EDERİZ YİNE BEKLERİZ</div>
          <div style="font-weight: bold;">ÜRÜNLERDE İADE YOKTUR</div>
          <div style="font-weight: bold;">BİLGİ FİŞİDİR MALİ DEĞERİ YOKTUR</div>
          <div style="font-weight: bold;">DEĞİŞİM SÜRECİ 7 GÜN İÇERİSİNDE YAPILMAKTADIR</div>
        </div>

        <script>
          window.onload = function() {
            ${data.receiptNumber ? `
            try {
              JsBarcode("#barcode-gift", "${data.receiptNumber}", {
                format: "CODE128", width: 1.5, height: 40,
                displayValue: false, margin: 0, background: "transparent"
              });
            } catch(e) { console.error('Barkod hatası:', e); }
            ` : ''}
            var directPrint = localStorage.getItem('directPrintEnabled') === 'true';
            setTimeout(function() {
              window.print();
              if (directPrint) { setTimeout(function() { window.close(); }, 500); }
            }, 300);
          }
        <\/script>
      </body>
      </html>
    `;
    handlePrint(giftSilverHtml, 'receipt');
  };

  // Router: storeId'ye göre doğru hediye fişi fonksiyonunu çağır
  const printGiftExchangeReceipt = (data) => {
    if (data.storeId === 1) {
      printGiftExchangeReceiptGold(data);
    } else {
      printGiftExchangeReceiptSilver(data);
    }
  };

  // Mevcut satış kaydından fiş yazdır (son satışlar listesinden)
  const reprintSaleReceipt = async (sale) => {
    try {
      // Satış detayını API'den çek (items dahil)
      let saleDetail = sale;
      if (!sale.items || sale.items.length === 0) {
        const response = await fetch(`${API_URL}/sales/${sale.id}`);
        saleDetail = await response.json();
      }

      // adjustment_items API'den gelebilir veya receipt_data içinde olabilir
      const adjItems = saleDetail.adjustment_items || (saleDetail.receipt_data && saleDetail.receipt_data.adjustment_items) || null;
      const adjTotal = saleDetail.adjustment_total || (saleDetail.receipt_data && saleDetail.receipt_data.adjustment_total) || 0;

      // Hurda mahsubu: scrap_discount kolonu yok, scrap_total_amount JOIN'den gelir
      const scrapDiscountVal = Number(saleDetail.scrap_discount || saleDetail.scrap_total_amount || 0);

      // İndirim düzeltmesi: total == subtotal ise indirim total'e uygulanmamış demektir
      const isRefundSale = saleDetail.sale_type === 'refund' || Number(saleDetail.total) < 0;
      let saleTotal = Math.abs(Number(saleDetail.total || 0));
      const saleSubtotal = Math.abs(Number(saleDetail.subtotal || 0));
      const saleDiscount = Number(saleDetail.discount || 0);
      if (saleDiscount > 0 && saleSubtotal > 0 && Math.abs(saleTotal - saleSubtotal) < 1) {
        saleTotal = saleSubtotal - saleDiscount - scrapDiscountVal + (Number(adjTotal) || 0);
      }

      // Ürün bazlı indirim — DB'deki discount_amount zaten sepet indirimi payını içeriyor,
      // tekrar dağıtmaya gerek yok; doğrudan DB değerlerini kullan.
      const receiptItems = (saleDetail.items || []).map(item => {
        const absQty = Math.abs(item.quantity);
        const unitPrice = Number(item.unit_price || item.price || 0);
        const originalPrice = Number(item.original_unit_price || item.original_price || unitPrice);
        const discountedPrice = Number(item.discounted_unit_price || item.discounted_price || item.discountedUnitPrice || unitPrice);
        const totalItemDiscount = Number(item.discount_amount || item.item_discount || item.itemDiscount || 0);
        const originalTotal = originalPrice * absQty;
        const rawPct = originalTotal > 0 ? (totalItemDiscount / originalTotal) * 100 : 0;
        const discountPct = rawPct > 0.01 ? (Number.isInteger(rawPct) ? rawPct : parseFloat(rawPct.toFixed(2))) : 0;
        return {
          name: item.product_name || item.name,
          barcode: item.barcode || null,
          quantity: absQty,
          price: originalPrice,
          discountedUnitPrice: discountedPrice,
          totalDiscountPercent: discountPct,
          note: item.note || null
        };
      });

      // Ödeme detaylarında eksik açık hesap var mı kontrol et
      // (eski satışlarda açık hesap payment_details'a kaydedilmemiş olabilir)
      let finalPaymentDetails = saleDetail.payment_details || null;
      if (finalPaymentDetails && Array.isArray(finalPaymentDetails) && saleDetail.customer_id) {
        // Zaten Açık Hesap varsa tekrar ekleme
        const alreadyHasAcikHesap = finalPaymentDetails.some(p => (p.method || '').includes('Açık Hesap'));
        const pdTotal = finalPaymentDetails.reduce((sum, p) => sum + (Number(p.amount_tl || p.amountTL || p.amount) || 0), 0);
        const saleTotalAbs = Math.abs(Number(saleDetail.total || 0));
        const missingAmount = saleTotalAbs - pdTotal;
        if (missingAmount > 1 && !alreadyHasAcikHesap) {
          // Satışa bağlı borç kaydı var mı kontrol et
          try {
            const debtResp = await fetch(`${API_URL}/customers/${saleDetail.customer_id}/debts`);
            const debtData = await debtResp.json();
            const saleDebts = (debtData.debts || debtData || []).filter(d => d.sale_id === saleDetail.id || d.sale_id === Number(saleDetail.id));
            // Ödenmiş borçları da kontrol et
            const allDebtsResp = await fetch(`${API_URL}/customers/${saleDetail.customer_id}/debts?include_paid=1`);
            let allSaleDebts = saleDebts;
            try {
              const allDebtsData = await allDebtsResp.json();
              const allDebts = allDebtsData.debts || allDebtsData || [];
              allSaleDebts = allDebts.filter(d => d.sale_id === saleDetail.id || d.sale_id === Number(saleDetail.id));
            } catch(e) {}
            if (allSaleDebts.length > 0) {
              const debtTotal = allSaleDebts.reduce((sum, d) => sum + (Number(d.amount_tl) || Number(d.amount) || 0), 0);
              const debtType = allSaleDebts[0].debt_type || 'TL';
              const debtLabel = debtType === 'GRAM' ? 'Açık Hesap (Gram)' : debtType === 'GUMUS' ? 'Açık Hesap (Gümüş)' : 'Açık Hesap (TL)';
              // Orijinal borç tutarını kullan (ödenmişse bile)
              const originalDebtAmount = Math.max(debtTotal, missingAmount);
              finalPaymentDetails = [...finalPaymentDetails, {
                method: debtLabel,
                currency: 'TRY',
                amount: originalDebtAmount,
                amount_tl: originalDebtAmount,
                amountTL: originalDebtAmount
              }];
            } else if (missingAmount > 1) {
              // Borç kaydı bulunamazsa bile eksik tutarı açık hesap olarak göster
              finalPaymentDetails = [...finalPaymentDetails, {
                method: 'Açık Hesap (TL)',
                currency: 'TRY',
                amount: missingAmount,
                amount_tl: missingAmount,
                amountTL: missingAmount
              }];
            }
          } catch(e) {
            // Hata olursa eksik tutarı açık hesap olarak ekle
            finalPaymentDetails = [...finalPaymentDetails, {
              method: 'Açık Hesap (TL)',
              currency: 'TRY',
              amount: missingAmount,
              amount_tl: missingAmount,
              amountTL: missingAmount
            }];
          }
        }
      }

      // Ara toplam ve indirim: kalemlerden hesapla (DB.subtotal/discount stale kalabilir)
      const computedItemsList = (saleDetail.items || []).reduce((sum, item) => {
        const listPrice = Number(item.original_unit_price) || Number(item.unit_price || item.price) || 0;
        return sum + listPrice * Math.abs(Number(item.quantity || 1));
      }, 0);
      const computedItemsTotal = (saleDetail.items || []).reduce((sum, item) => {
        const totalPrice = Number(item.total_price);
        if (totalPrice > 0) return sum + Math.abs(totalPrice);
        const unitPrice = Number(item.unit_price || item.price || 0);
        return sum + Math.abs(unitPrice) * Math.abs(Number(item.quantity || 1));
      }, 0);
      const dbSubtotal = Number(saleDetail.subtotal || 0);
      const dbDiscount = Number(saleDetail.discount || 0);
      const itemsBasedDiscount = Math.max(0, computedItemsList - computedItemsTotal);
      const receiptSubtotal = computedItemsList > 0 ? computedItemsList : dbSubtotal;
      const receiptDiscount = computedItemsList > 0 ? Math.max(dbDiscount, itemsBasedDiscount) : dbDiscount;

      const receiptData = {
        saleId: saleDetail.id,
        receiptNumber: saleDetail.receipt_number || saleDetail.store_sale_number || saleDetail.id,
        items: receiptItems,
        customer: saleDetail.customer_name ? { name: saleDetail.customer_name } : null,
        staffNames: saleDetail.staff_name || '',
        subtotal: receiptSubtotal,
        totalDiscount: receiptDiscount,
        campaignDiscount: Number(saleDetail.campaign_discount || 0),
        giftCardDiscount: Number(saleDetail.gift_card_discount || 0),
        scrapDiscount: scrapDiscountVal,
        scrapDebtDiscount: Number(saleDetail.scrap_debt_discount || 0),
        customerGiftCardDiscount: Number(saleDetail.customer_gift_card_discount || 0),
        total: saleTotal,
        paymentMethod: saleDetail.payment_method,
        paymentDetails: finalPaymentDetails,
        adjustmentItems: adjItems ? adjItems.map(adj => ({
          description: adj.description || adj.reason || 'Fark',
          reason: adj.reason || '',
          amount: parseFloat(adj.amount || adj.refund_amount || adj.original_amount) || 0,
          discountShare: Number(adj.discountShare || 0)
        })) : null,
        adjustmentTotal: Number(adjTotal) || 0,
        date: saleDetail.created_at || saleDetail.date || new Date(),
        storeId: Number(saleDetail.store_id) || currentStoreId,
        receiptNote: saleDetail.notes || ''
      };

      printSaleReceipt(receiptData);
    } catch (error) {
      console.error('Fiş yazdırma hatası:', error);
      toast.error('Fiş yazdırılamadı!');
    }
  };

  // Mevcut satış kaydından hediye/değişim fişi yazdır (son satışlar listesinden)
  const reprintGiftExchangeReceipt = async (sale) => {
    try {
      let saleDetail = sale;
      if (!sale.items || sale.items.length === 0) {
        const response = await fetch(`${API_URL}/sales/${sale.id}`);
        saleDetail = await response.json();
      }

      // Hurda mahsubu
      const scrapDiscountVal = Number(saleDetail.scrap_discount || saleDetail.scrap_total_amount || 0);

      const receiptData = {
        saleId: saleDetail.id,
        receiptNumber: saleDetail.receipt_number || saleDetail.store_sale_number || saleDetail.id,
        items: (saleDetail.items || []).map(item => ({
          name: item.product_name || item.name,
          barcode: item.barcode || null,
          quantity: item.quantity
        })),
        customer: saleDetail.customer_name ? { name: saleDetail.customer_name } : null,
        staffNames: saleDetail.staff_name || '',
        subtotal: Number(saleDetail.subtotal || 0),
        totalDiscount: Number(saleDetail.discount || 0),
        scrapDiscount: scrapDiscountVal,
        adjustmentTotal: Number(saleDetail.adjustment_total || (saleDetail.receipt_data && saleDetail.receipt_data.adjustment_total) || 0),
        total: Number(saleDetail.total || 0),
        date: saleDetail.created_at || saleDetail.date || new Date(),
        storeId: Number(saleDetail.store_id) || currentStoreId
      };

      printGiftExchangeReceipt(receiptData);
    } catch (error) {
      console.error('Hediye fişi yazdırma hatası:', error);
      toast.error('Hediye fişi yazdırılamadı!');
    }
  };

  // Müşteri bakiye raporu yazdır (Customers sayfasındaki ile aynı format)
  const printCustomerBalanceReport = async (customer) => {
    if (!customer?.id) { toast.warning('Müşteri seçili değil'); return; }
    try {
      const res = await fetch(`${API_URL}/customers/debts/summary?customer_id=${customer.id}`);
      const data = await res.json();
      const entry = Array.isArray(data) ? data.find(d => d.customer_id === customer.id) : null;

      const tlDebt = entry ? Number(entry.total_tl_debt) : 0;
      const tlCredit = entry ? Number(entry.total_tl_credit) : 0;
      const goldDebt = entry ? Number(entry.total_gold_debt || 0) : 0;
      const silverDebt = entry ? Number(entry.total_silver_debt || 0) : 0;
      const netBalance = tlDebt - tlCredit;

      const now = new Date();
      const dateStr = now.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });
      const timeStr = now.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
      const storeId = currentStoreId;

      const html = `<!DOCTYPE html>
<html><head>
  <title>Bakiye Raporu - ${customer.name}</title>
  <style>
    @page { size: 80mm auto; margin: 5mm; }
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: Arial, Helvetica, sans-serif; font-size: 12px; font-weight: 600; width: 80mm; padding: 5mm; background: #fff; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .receipt-top-space { height: 8mm; }
    /* Fişin altına boşluk. Termal yazıcı 1-bit basar: açık gri (#f2f2f2) beyaza dönüyor ve sürücü
       sondaki "boş" alanı kağıt tasarrufu için kesiyordu. Bu yüzden boşluğun EN ALTINA gerçekten
       siyah küçük bir işaret koyuyoruz; yazıcı ona kadar basmak zorunda, aradaki boşluk korunur. */
    .receipt-bottom-space { height: 15mm; }
    .receipt-end-mark { text-align: center; font-size: 10px; color: #000; line-height: 1; }
    .header { text-align: center; border-bottom: 2px dashed #000; padding-bottom: 10px; margin-bottom: 10px; }
    .logo-img { width: 120px; height: auto; margin-bottom: 8px; filter: invert(1); }
    .receipt-title { font-size: 14px; font-weight: bold; margin-top: 8px; padding: 4px 0; border-top: 1px solid #000; }
    .divider { border-top: 1px dashed #333; margin: 8px 0; }
    .row { display: flex; justify-content: space-between; padding: 4px 0; font-size: 11px; }
    .row-grand-total { display: flex; justify-content: space-between; font-size: 15px; font-weight: bold; padding: 8px 0; border-top: 2px solid #000; border-bottom: 2px solid #000; margin: 8px 0; }
    .section-label { font-size: 12px; font-weight: bold; margin-bottom: 4px; padding-bottom: 2px; border-bottom: 1px solid #333; }
    .footer { text-align: center; margin-top: 15px; padding-top: 10px; border-top: 2px dashed #000; font-size: 10px; color: #000; }
    @media print { body { width: 80mm; } }
  </style>
</head><body>
  <div class="receipt-top-space"></div>
  <div class="header">
    ${storeId === 1
      ? `<img src="/Siyah - Beyaz - 1.png" alt="Akik Gold Jewellery" class="logo-img" />`
      : `<div style="margin:0 auto 2px auto;text-align:center;"><img src="/akikfisligi.png" alt="Akik Gümüş" style="width:170px;height:auto;filter:contrast(3);" /></div>`}
    ${storeId === 1
      ? `<div style="font-size:10px;color:#000;line-height:1.4;margin-bottom:2px;">Osmangazi Mah. Ahmet Şireci Bulvarı No: 14D<br/>Şehitkamil / Gaziantep<br/>Tel: 0342 230 00 64<br/>www.akikjewellery.com</div>`
      : `<div style="font-size:11px;font-weight:bold;color:#000;margin-bottom:3px;">${storeId === 3 ? '(YEŞİLSU ŞUBESİ)' : '(OSMANGAZİ ŞUBESİ)'}</div><div style="font-size:10px;color:#000;line-height:1.4;margin-bottom:2px;">${storeId === 3 ? 'Bey Mah. Atatürk Blv. 43/A<br/>Şahinbey / Gaziantep' : 'Osmangazi Mah. Ahmet Şireci Bulvarı No: 16/F<br/>Şehitkamil / Gaziantep'}<br/>Tel: 0342 230 00 64</div>`}
    <div class="receipt-title">BAKİYE RAPORU</div>
  </div>
  <div class="section-label">Müşteri Bilgileri</div>
  <div class="row"><span>Müşteri:</span><span>${customer.name || '-'}</span></div>
  <div class="row"><span>Telefon:</span><span>${customer.phone || '-'}</span></div>
  <div class="row"><span>Tarih:</span><span>${dateStr} ${timeStr}</span></div>
  <div class="divider"></div>
  ${netBalance !== 0 ? `<div class="row-grand-total"><span>Bakiye:</span><span>₺${netBalance.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></div>` : ''}
  ${goldDebt > 0 ? `<div class="row-grand-total"><span>Altın Borcu:</span><span>${goldDebt.toFixed(2)} gr</span></div>` : ''}
  ${silverDebt > 0 ? `<div class="row-grand-total"><span>Gümüş Borcu:</span><span>${silverDebt.toFixed(2)} gr</span></div>` : ''}
  ${netBalance === 0 && goldDebt === 0 && silverDebt === 0 ? `<div class="row-grand-total"><span>Bakiye:</span><span>₺0,00</span></div>` : ''}
  <div class="receipt-bottom-space"></div>
  <div class="receipt-end-mark">.</div>
  <script>
    window.onload = function() {
      var directPrint = localStorage.getItem('directPrintEnabled') === 'true';
      setTimeout(function() {
        window.print();
        if (directPrint) { setTimeout(function() { window.close(); }, 500); }
      }, 300);
    }
  </script>
</body></html>`;
      handlePrint(html, 'receipt');
    } catch (error) {
      console.error('Bakiye raporu yazdırma hatası:', error);
      toast.error('Bakiye raporu yazdırılamadı!');
    }
  };

  // Borç detay modalını kapat ve müşteri listesine dön
  const closeCollectionDebtModal = () => {
    setShowCollectionDebtModal(false);
    setCollectionSelectedCustomer(null);
    setCollectionCustomerDebts([]);
    setShowCollectionHistory(false);
    setShowCollectionModal(true);
  };

  // ===== TAHSİLAT MODÜLÜ SONU =====

  // ===== POS LOG GÖRÜNTÜLEME (ADMIN) =====

  // POS loglarını getir
  const fetchPosLogs = async (page = 1, filters = posLogsFilter) => {
    if (user?.role !== 'admin') return;

    setPosLogsLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '50'
        // Tüm modüllerdeki logları getir
      });

      if (filters.action) params.append('action', filters.action);
      if (filters.status) params.append('status', filters.status);
      if (filters.user_id) params.append('user_id', filters.user_id);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      if (selectedStore?.id) params.append('store_id', selectedStore.id.toString());

      const response = await fetch(`${API_URL}/logs?${params}`);
      const data = await response.json();

      setPosLogs(data.logs || []);
      setPosLogsTotalPages(data.totalPages || 1);
      setPosLogsPage(page);
    } catch (error) {
      console.error('Log getirme hatası:', error);
      toast.error('Loglar yüklenemedi');
    } finally {
      setPosLogsLoading(false);
    }
  };

  // Log istatistiklerini getir
  const fetchPosLogsStats = async () => {
    if (user?.role !== 'admin') return;

    try {
      const params = new URLSearchParams();
      if (posLogsFilter.start_date) params.append('start_date', posLogsFilter.start_date);
      if (posLogsFilter.end_date) params.append('end_date', posLogsFilter.end_date);

      const response = await fetch(`${API_URL}/logs/stats?${params}`);
      const data = await response.json();
      setPosLogsStats(data);
    } catch (error) {
      console.error('Log istatistik hatası:', error);
    }
  };

  // Kullanıcı listesini getir (filtre için)
  const fetchPosLogsUsers = async () => {
    try {
      const response = await fetch(`${API_URL}/users`);
      const data = await response.json();
      setPosLogsUsers(data || []);
    } catch (error) {
      console.error('Kullanıcı listesi hatası:', error);
    }
  };

  // Log modalını aç
  const openPosLogsModal = () => {
    if (user?.role !== 'admin') {
      toast.error('Bu özellik sadece admin kullanıcılar içindir');
      return;
    }
    setShowPosLogsModal(true);
    fetchPosLogs(1);
    fetchPosLogsStats();
    fetchPosLogsUsers();
  };

  // Log filtreleme
  const handlePosLogsFilter = () => {
    fetchPosLogs(1, posLogsFilter);
    fetchPosLogsStats();
  };

  // Filtre temizle
  const clearPosLogsFilter = () => {
    const clearedFilter = {
      action: '',
      status: '',
      user_id: '',
      start_date: '',
      end_date: ''
    };
    setPosLogsFilter(clearedFilter);
    fetchPosLogs(1, clearedFilter);
    fetchPosLogsStats();
  };

  // Log detayını görüntüle
  const viewLogDetail = (log) => {
    setSelectedLogDetail(log);
  };

  // Log aksiyonu için Türkçe açıklama
  const getActionLabel = (action) => {
    const labels = {
      // Sepet işlemleri
      'cart_add': 'Sepete Ekleme',
      'cart_remove': 'Sepetten Çıkarma',
      'cart_clear': 'Sepet Temizleme',
      'cart_quantity_change': 'Miktar Değişikliği',
      'cart_price_edit': 'Fiyat Düzenleme',
      // Satış işlemleri
      'sale': 'Satış Yapıldı',
      'sale_complete': 'Satış Yapıldı',
      'sale_cancel': 'Satış İptali',
      'sale_edit': 'Satış Düzenleme',
      'sale_delete': 'Satış Silme',
      'refund': 'İade',
      'exchange': 'Değişim',
      'hold_sale': 'Satış Bekletme',
      'resume_sale': 'Bekleyen Satış',
      // İndirim ve kampanya
      'apply_discount': 'İndirim Uygulama',
      'apply_campaign': 'Kampanya Uygulama',
      'price_change': 'Fiyat Değişikliği',
      // Ödeme işlemleri
      'payment_start': 'Ödeme Başlatma',
      'payment_cancel': 'Ödeme İptali',
      'partial_payment': 'Kısmi Ödeme',
      // Müşteri ve personel
      'customer_select': 'Müşteri Seçimi',
      'customer_remove': 'Müşteri Kaldırma',
      'staff_select': 'Personel Seçimi',
      // Bakiye
      'gift_card_apply': 'Bakiye Uygulama',
      'gift_card_remove': 'Bakiye Kaldırma',
      'gift_card_created': 'Bakiye Oluşturma',
      // Hurda işlemleri
      'scrap_discount_apply': 'Hurda İndirimi',
      'scrap_debt_created': 'Hurda Alacağı Oluşturma',
      // Borç işlemleri
      'debt_sale': 'Açık Hesap Satışı',
      'debt_add': 'Borç Ekleme',
      'debt_payment': 'Borç Tahsilatı',
      // Genel CRUD
      'create': 'Oluşturma',
      'update': 'Güncelleme',
      'delete': 'Silme',
      'read': 'Görüntüleme',
      'view': 'Görüntüleme',
      // Auth
      'login': 'Giriş',
      'logout': 'Çıkış',
      'login_failed': 'Başarısız Giriş',
      // Stok
      'stock_in': 'Stok Girişi',
      'stock_out': 'Stok Çıkışı',
      'stock_adjust': 'Stok Düzeltme',
      'inventory_count': 'Sayım',
      // Tamir
      'repair_status_change': 'Tamir Durumu Değişikliği',
      // Diğer
      'export': 'Dışa Aktarma',
      'import': 'İçe Aktarma',
      'backup': 'Yedekleme',
      'restore': 'Geri Yükleme',
      'print': 'Yazdırma',
      'search': 'Arama',
      'filter': 'Filtreleme'
    };
    return labels[action] || action;
  };

  // Status için renk
  const getStatusColor = (status) => {
    const colors = {
      'success': '#10b981',
      'warning': '#f59e0b',
      'error': '#ef4444',
      'info': '#3b82f6'
    };
    return colors[status] || '#64748b';
  };

  // Status için Türkçe
  const getStatusLabel = (status) => {
    const labels = {
      'success': 'Başarılı',
      'warning': 'Uyarı',
      'error': 'Hata',
      'info': 'Bilgi'
    };
    return labels[status] || status;
  };

  // ===== POS LOG GÖRÜNTÜLEME SONU =====

  // Ayar katsayıları (altın fiyat hesaplaması için)
  const karatMultipliers = {
    '8': 333,
    '9': 375,
    '10': 417,
    '14': 585,
    '18': 750,
    '21': 875,
    '22': 916,
    '24-995': 995,
    '24-999': 999
  };

  // Altın fiyat hesaplama
  const calculateGoldPrice = (product) => {
    // Sabit fiyatlı ürünler için para birimi dönüşümü yap
    if (product.pricing_type === 'fixed') {
      const price = Number(product.sell_price) || 0;
      if (product.currency === 'USD') {
        const usdRate = getSatisRate('USD');
        return price * usdRate;
      } else if (product.currency === 'EUR') {
        const eurRate = getSatisRate('EUR');
        return price * eurRate;
      }
      return price; // TRY ise direkt döndür
    }

    if (product.metal_type !== 'gold' || !product.weight || !product.gold_karat) {
      const price = Number(product.sell_price) || 0;
      if (product.currency === 'USD') return price * getSatisRate('USD');
      if (product.currency === 'EUR') return price * getSatisRate('EUR');
      return price;
    }

    // Dinamik kur hesaplama (makas farkı veya sabit kur)
    const goldGramPrice = getSatisRate('GOLD');
    if (goldGramPrice <= 0) {
      if (!priceWarningShown.current.gold) {
        priceWarningShown.current.gold = true;
        toast.warning('Altın fiyatı alınamadı, kayıtlı fiyat kullanılıyor');
      }
      return Number(product.sell_price) || 0;
    }

    const gram = parseFloat(product.weight) || 0;
    const labor = parseFloat(product.labor_cost) || 0;
    const multiplier = karatMultipliers[product.gold_karat] || 585; // Varsayılan 14 ayar

    // Formül: ((ayar_katsayısı + işçilik) × gram / 1000) × altın_gram_fiyatı
    const laboredGram = (multiplier + labor) * gram / 1000;
    const calculatedPrice = laboredGram * goldGramPrice;

    return calculatedPrice;
  };

  // Gümüş fiyat hesaplama (Gümüş Aksesuar kategorisi de dahil)
  const calculateSilverPrice = (product) => {
    // Gümüş Aksesuar kategorisi kontrolü
    const isGumushAksesuar = product.metal_type === 'aksesuar' && product.category === 'Gümüş Aksesuar';
    const useSilverPricing = product.metal_type === 'silver' || isGumushAksesuar;

    // Sabit fiyatlı ürünler için para birimi dönüşümü yap
    if (product.pricing_type === 'fixed') {
      const price = Number(product.sell_price) || 0;
      if (product.currency === 'USD') {
        const usdRate = getSatisRate('USD');
        return price * usdRate;
      } else if (product.currency === 'EUR') {
        const eurRate = getSatisRate('EUR');
        return price * eurRate;
      }
      return price; // TRY ise direkt döndür
    }

    if (!useSilverPricing || !product.weight) {
      const price = Number(product.sell_price) || 0;
      if (product.currency === 'USD') return price * getSatisRate('USD');
      if (product.currency === 'EUR') return price * getSatisRate('EUR');
      return price;
    }

    // Dinamik kur hesaplama
    const silverGramPrice = getSatisRate('SILVER');
    const usdRate = getSatisRate('USD');

    const gram = parseFloat(product.weight) || 0;
    const purity = parseInt(product.silver_purity) || 925;
    const profitMargin = parseFloat(product.profit_margin) || 3;
    const laborPerGram = parseFloat(product.labor_cost) || 0;

    // Has gümüş gramı: (ürün gramı × ayar) / 1000
    const pureGram = (gram * purity) / 1000;

    // Gümüş maliyeti: has gram × gümüş kuru
    const silverCost = pureGram * silverGramPrice;

    // GÜMÜŞ AKSESUAR için ayrı formül (dolar kuru YOK, kar oranı sadece işçiliğe)
    if (isGumushAksesuar) {
      if (silverGramPrice <= 0) {
        if (!priceWarningShown.current.silver) {
          priceWarningShown.current.silver = true;
          toast.warning('Gümüş fiyatı alınamadı, kayıtlı fiyat kullanılıyor');
        }
        return Number(product.sell_price) || 0;
      }

      // Gümüş Aksesuar Formülü:
      // Satış Fiyatı = (Has Gümüş Gramı × Gümüş Kuru) + (Ürün Gramı × İşçilik × Kar Oranı)
      const laborCost = gram * laborPerGram * profitMargin;
      const salePrice = silverCost + laborCost;

      return salePrice;
    }

    // NORMAL GÜMÜŞ formülü
    if (silverGramPrice <= 0 || usdRate <= 0) {
      if (!priceWarningShown.current.silverUsd) {
        priceWarningShown.current.silverUsd = true;
        toast.warning('Gümüş veya dolar fiyatı alınamadı, kayıtlı fiyat kullanılıyor');
      }
      return Number(product.sell_price) || 0;
    }

    // İşçilik maliyeti (TL): ürün gramı × gram başı işçilik × dolar kuru
    const laborCostTL = gram * laborPerGram * usdRate;

    // Toplam maliyet (TL): gümüş maliyeti + işçilik maliyeti
    const totalCostTL = silverCost + laborCostTL;

    // Satış fiyatı (TL): toplam maliyet × kar oranı
    const salePriceTL = totalCostTL * profitMargin;

    // Dolar bazlı yuvarlama: TL → USD → yukarı yuvarla → TL
    const salePriceUSD = salePriceTL / usdRate;
    const roundedUSD = Math.ceil(salePriceUSD);
    return roundedUSD * usdRate;
  };

  // Sepet ses bildirimi
  const playCartSound = (type) => {
    try {
      const soundEnabled = localStorage.getItem('soundEnabled');
      if (soundEnabled === 'false') return;
    } catch (e) {}

    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      if (type === 'success') {
        oscillator.frequency.value = 1000;
        oscillator.type = 'sine';
        gainNode.gain.value = 0.2;
        oscillator.start();
        oscillator.stop(audioContext.currentTime + 0.1);
      } else if (type === 'notification') {
        // Terminal sepeti bildirimi - dikkat çekici çift bip sesi
        gainNode.gain.value = 0.3;
        oscillator.type = 'sine';
        // İlk bip
        oscillator.frequency.setValueAtTime(880, audioContext.currentTime);
        oscillator.frequency.setValueAtTime(0, audioContext.currentTime + 0.15);
        // İkinci bip
        oscillator.frequency.setValueAtTime(1100, audioContext.currentTime + 0.2);
        oscillator.frequency.setValueAtTime(0, audioContext.currentTime + 0.35);
        // Üçüncü bip (yüksek)
        oscillator.frequency.setValueAtTime(1320, audioContext.currentTime + 0.4);
        oscillator.start();
        oscillator.stop(audioContext.currentTime + 0.55);
      } else {
        oscillator.frequency.value = 300;
        oscillator.type = 'square';
        gainNode.gain.value = 0.15;
        oscillator.start();
        oscillator.stop(audioContext.currentTime + 0.2);
      }
    } catch (error) {
      console.error('Ses çalma hatası:', error);
    }
  };

  // Mağaza stoğunu getir
  const getStoreStock = async (productId) => {
    const storeId = user?.role === 'cashier' ? user?.store_id : (selectedStore?.id || user?.store_id);
    if (!storeId) return { myStock: 999, otherStores: [] }; // storeId yoksa sınırsız kabul et

    try {
      const response = await fetch(`${API_URL}/store-stocks/${productId}`);
      const storeStocks = await response.json();
      const myStock = storeStocks.find(s => Number(s.store_id) === Number(storeId))?.stock || 0;
      // Sadece aynı tip mağazaları göster (altın→altın, gümüş→gümüş)
      const goldStoreIds = [1];
      const silverStoreIds = [2, 3];
      const isGoldStore = goldStoreIds.includes(Number(storeId));
      const sameTypeStoreIds = isGoldStore ? goldStoreIds : silverStoreIds;
      const otherStores = storeStocks.filter(s => {
        const sid = Number(s.store_id);
        return sid !== Number(storeId) && s.stock > 0 && sameTypeStoreIds.includes(sid);
      });
      return { myStock, otherStores, storeId };
    } catch (error) {
      console.error('Stok getirme hatası:', error);
      return { myStock: 0, otherStores: [] };
    }
  };

  // Ürün türünün bu mağazada satılıp satılamayacağını kontrol et
  const isProductTypeAllowedForStore = (productType) => {
    const storeId = selectedStore?.id || user?.store_id;

    // Altın mağazası (ID=1) - sadece gold ve sarf
    if (storeId === 1) {
      return ['gold', 'sarf'].includes(productType);
    }

    // Gümüş mağazaları (ID=2, ID=3) - altın hariç satabilir
    if (storeId === 2 || storeId === 3) {
      return ['silver', 'steel', 'zippo', 'brands', 'aksesuar', 'sarf'].includes(productType);
    }

    return true;
  };

  // Kişiye özel ürün modalını aç
  const addCustomProduct = () => {
    if (isCartLockedByApproval) {
      toast.warning('Onaylı indirim varken sepete ürün eklenemez!');
      return;
    }
    setCustomProductPrice('');
    setCustomProductNote('');
    setShowCustomProductModal(true);
  };

  // Kişiye özel ürünü sepete ekle (modal onayından)
  const confirmAddCustomProduct = async () => {
    const price = parseFloat(customProductPrice) || 0;
    if (price <= 0) {
      toast.warning('Lütfen bir fiyat girin!');
      return;
    }
    try {
      // Önce mevcut "Kişiye Özel" ürünü ara
      const searchRes = await fetch(`${API_URL}/products?search=${encodeURIComponent('Kişiye Özel')}&limit=5&include_custom=1`);
      const searchData = await searchRes.json();
      const productsList = searchData?.products || (Array.isArray(searchData) ? searchData : []);
      let customProduct = productsList.find(p => p.name === 'Kişiye Özel');

      // Yoksa oluştur
      if (!customProduct) {
        const createRes = await fetch(`${API_URL}/products`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: 'Kişiye Özel',
            category: 'Kişiye Özel',
            sell_price: 0,
            currency: 'TRY',
            metal_type: 'aksesuar',
            active: 1,
            no_discount: 0,
            store_stocks: [
              { store_id: 1, stock: 9999 },
              { store_id: 2, stock: 9999 },
              { store_id: 3, stock: 9999 },
              { store_id: 4, stock: 9999 }
            ]
          })
        });
        const createData = await createRes.json();
        if (createData.error) {
          toast.error('Kişiye özel ürün oluşturulamadı: ' + createData.error);
          return;
        }
        customProduct = { id: createData.id, name: 'Kişiye Özel', category: 'Kişiye Özel', sell_price: 0, currency: 'TRY', metal_type: 'other', barcode: '' };
      }

      const cartItem = {
        id: `custom-${Date.now()}`,
        productId: customProduct.id,
        name: 'Kişiye Özel',
        barcode: customProduct.barcode || '',
        category: 'Kişiye Özel',
        price: price,
        currency: 'TRY',
        quantity: 1,
        stock: 9999,
        storeStock: 9999,
        isGift: false,
        isGoldProduct: false,
        isSilverProduct: false,
        weight: 0,
        image: null,
        isSpecialDefined: false,
        no_discount: false,
        note: customProductNote.trim(),
        isCustomProduct: true
      };

      setCart(prev => [...prev, cartItem]);
      setShowCustomProductModal(false);
      setCustomProductPrice('');
      setCustomProductNote('');
    } catch (error) {
      console.error('Kişiye özel ürün hatası:', error);
      toast.error('Bir hata oluştu!');
    }
  };

  // Sepete ürün ekle
  const addToCart = async (product, customWeight = null) => {
    if (isCartLockedByApproval) {
      toast.warning('Onaylı indirim varken sepete ürün eklenemez! Önce ödemeyi tamamlayın veya indirimi kaldırın.');
      return;
    }
    // Ürün türü kontrolü - bu mağazada satılabilir mi?
    if (!isProductTypeAllowedForStore(product.metal_type)) {
      playCartSound('error');
      toast.error('Barkod bulunamadı!', { autoClose: 4500 });
      return;
    }

    // Özel tanımlı ürün kontrolü - gramaj giriş modalı aç
    if (product.is_special_defined && product.metal_type === 'gold' && customWeight === null) {
      setSpecialWeightProduct(product);
      setSpecialWeightInput('');
      setShowSpecialWeightModal(true);
      return;
    }

    // Özel tanımlı ürünlerde her ekleme benzersiz bir sepet kalemi oluşturur
    const isSpecialDefined = product.is_special_defined && product.metal_type === 'gold';

    // Normal (hediye olmayan) ürün ara - özel tanımlı değilse
    const existingItem = !isSpecialDefined
      ? cart.find(item => item.id === product.id && !item.isGift && !item.isSpecialDefined)
      : null;

    // Mağaza stoğunu kontrol et
    const { myStock, otherStores } = await getStoreStock(product.id);
    const currentQty = existingItem ? existingItem.quantity : 0;
    const newQty = currentQty + 1;

    // Stok yetersizse uyarı göster
    if (newQty > myStock) {
      if (otherStores.length > 0) {
        const storeList = otherStores
          .sort((a, b) => b.stock - a.stock)
          .map(s => `${s.store_name}'da ${s.stock} adet`)
          .join(', ');
        toast.warning(`Ürün stokta yok! ${storeList} mevcut.`, { autoClose: 7500 });
      } else {
        toast.warning('Bu üründen hiçbir mağazada stok yok!', { autoClose: 7500 });
      }
    }

    if (existingItem && !isSpecialDefined) {
      setCart(cart.map(item =>
        item.id === product.id && !item.isGift && !item.isSpecialDefined
          ? { ...item, quantity: item.quantity + 1, storeStock: myStock }
          : item
      ));
      // Aynı ürün 2./3. kez okutulduğunda da büyük önizleme son okutulan ürünü göstersin
      // (büyük görsel selectedPreviewItem || cart[son] mantığıyla çalışıyor; bu kalem
      // sepetin sonunda olmayabileceği için açıkça önizlemeye atıyoruz).
      setSelectedPreviewItem({ ...existingItem, quantity: existingItem.quantity + 1, storeStock: myStock });
      playCartSound('success');

      if (product.no_discount === 1 || product.no_discount === true) {
        toast.warning(`${product.barcode} barkodlu ürüne ürün bazlı indirim uygulanamaz, sepet indirimi kullanabilirsiniz.`, { autoClose: 7500 });
      }

      // Yuvarla indirimi varsa sıfırla (miktar artırıldı)
      if (appliedDiscounts.some(d => d.type === 'round')) {
        setAppliedDiscounts(prev => prev.filter(d => d.type !== 'round'));
        toast.info('Sepet güncellendiği için yuvarla indirimi kaldırıldı', { autoClose: 4500 });
      }
    } else {
      // Metal türüne göre fiyat hesapla
      const isGoldProduct = product.metal_type === 'gold';
      const isGumushAksesuar = product.metal_type === 'aksesuar' && product.category === 'Gümüş Aksesuar';
      const isSilverProduct = product.metal_type === 'silver' || isGumushAksesuar;

      let finalPrice;
      let usedWeight = customWeight || product.weight;

      if (isGoldProduct) {
        // Özel tanımlı ürünse girilen gramajı kullan
        if (isSpecialDefined && customWeight) {
          finalPrice = calculateGoldPriceWithWeight(product, customWeight);
        } else {
          finalPrice = calculateGoldPrice(product);
        }
      } else if (isSilverProduct) {
        finalPrice = calculateSilverPrice(product);
      } else {
        let price = Number(product.sell_price) || 0;
        if (product.currency === 'USD') {
          price = price * getSatisRate('USD');
        } else if (product.currency === 'EUR') {
          price = price * getSatisRate('EUR');
        }
        finalPrice = price;
      }

      // Tüm fiyatlar TL'ye çevrildiği için currency her zaman TRY
      const finalCurrency = 'TRY';

      const newCartItem = {
        id: isSpecialDefined ? `${product.id}_${Date.now()}` : product.id, // Özel tanımlı için benzersiz ID
        productId: product.id, // Gerçek ürün ID'si (stok düşürme için)
        name: product.name,
        barcode: product.barcode,
        category: product.category,
        metal_type: product.metal_type, // Kampanya filtresi için
        brand: product.brand, // Kampanya filtresi için
        price: finalPrice,
        currency: finalCurrency,
        quantity: 1,
        stock: Number(product.stock),
        storeStock: myStock, // Mağaza stoğu
        isGift: false,
        isGoldProduct: isGoldProduct,
        isSilverProduct: isSilverProduct,
        goldKarat: product.gold_karat,
        silverPurity: product.silver_purity,
        profitMargin: product.profit_margin,
        weight: product.weight, // Ürünün orijinal toplam gramajı
        customWeight: isSpecialDefined ? customWeight : null, // Satış için girilen gramaj
        laborCost: product.labor_cost,
        image: product.image,
        isSpecialDefined: isSpecialDefined,
        no_discount: product.no_discount === 1 || product.no_discount === true,
        note: ''
      };
      const newCart = [...cart, newCartItem];
      setCart(newCart);
      setSelectedPreviewItem(null); // Son eklenen ürünün resmi gösterilsin
      playCartSound('success');

      // İndirim yapılamaz uyarısı
      if (product.no_discount === 1 || product.no_discount === true) {
        toast.warning(`${product.barcode} barkodlu ürüne ürün bazlı indirim uygulanamaz, sepet indirimi kullanabilirsiniz.`, { autoClose: 7500 });
      }

      // Yuvarla indirimi varsa sıfırla (yeni ürün eklendi)
      if (appliedDiscounts.some(d => d.type === 'round')) {
        setAppliedDiscounts(prev => prev.filter(d => d.type !== 'round'));
        toast.info('Yeni ürün eklendiği için yuvarla indirimi kaldırıldı', { autoClose: 4500 });
      }
    }
    barcodeInputRef.current?.focus();
  };

  // Özel tanımlı ürün için gramaj bazlı fiyat hesapla
  const calculateGoldPriceWithWeight = (product, weight) => {
    const liveSatis = getSatisRate('GOLD');
    if (!liveSatis) return 0;

    const karatMultipliers = {
      '8': 333, '9': 375, '10': 417, '14': 585, '18': 750,
      '21': 875, '22': 916, '24-995': 995, '24-999': 999
    };

    const gram = parseFloat(weight) || 0;
    const labor = parseFloat(product.labor_cost) || 0;
    const multiplier = karatMultipliers[product.gold_karat] || 585;

    const calculatedPrice = ((multiplier + labor) * gram / 1000) * liveSatis;
    return Math.round(calculatedPrice);
  };

  // Özel tanımlı ürün gramaj onaylama
  const confirmSpecialWeight = async () => {
    const weight = parseFloat(specialWeightInput);
    if (!weight || weight <= 0) {
      toast.error('Geçerli bir gramaj giriniz!');
      return;
    }

    // Toplam gramajı kontrol et
    if (weight > parseFloat(specialWeightProduct.weight || 0)) {
      toast.error(`Girilen gramaj (${weight} gr), toplam gramajdan (${specialWeightProduct.weight} gr) fazla olamaz!`);
      return;
    }

    await addToCart(specialWeightProduct, weight);
    setShowSpecialWeightModal(false);
    setSpecialWeightProduct(null);
    setSpecialWeightInput('');
    toast.success(`${weight} gr ile sepete eklendi`);
  };

  // Ürün bazlı indirim modalını aç
  const openItemDiscountModal = (item) => {
    // Kasiyer seçimi kontrolü (admin hariç)
    if (user?.role !== 'admin' && selectedStaff.length === 0) {
      toast.error('İndirim uygulayabilmek için önce kasiyer seçmelisiniz!', { autoClose: 6000 });
      return;
    }
    // Cart'taki orijinal item'ı bul (customItemDiscount değerine erişmek için)
    const cartItem = cart.find(c => c.id === item.id);
    setSelectedItemForDiscount(item);
    // Mevcut özel indirim varsa yükle
    if (cartItem?.customItemDiscount > 0) {
      setItemDiscountType('amount');
      setItemDiscountAmount(cartItem.customItemDiscount);
    } else {
      setItemDiscountType('percent');
      setItemDiscountAmount(0);
    }
    setShowItemDiscountModal(true);
  };

  // Ürün notu modalını aç
  const openItemNoteModal = (item) => {
    const cartItem = cart.find(c => c.id === item.id);
    setSelectedItemForNote(item);
    setItemNoteText(cartItem?.note || '');
    setShowItemNoteModal(true);
  };

  // Ürün notunu kaydet
  const saveItemNote = () => {
    if (!selectedItemForNote) return;
    setCart(cart.map(item => {
      if (item.id === selectedItemForNote.id) {
        return { ...item, note: itemNoteText.trim() };
      }
      return item;
    }));
    setShowItemNoteModal(false);
    setSelectedItemForNote(null);
    setItemNoteText('');
  };

  // Ürün notunu kaldır
  const removeItemNote = () => {
    if (!selectedItemForNote) return;
    setCart(cart.map(item => {
      if (item.id === selectedItemForNote.id) {
        return { ...item, note: '' };
      }
      return item;
    }));
    setShowItemNoteModal(false);
    setSelectedItemForNote(null);
    setItemNoteText('');
  };

  // Ürün bazlı indirim uygula
  const applyItemDiscount = () => {
    if (!selectedItemForDiscount) return;

    const isUserAdmin = user?.role === 'admin';

    // Kasiyer seçimi kontrolü (admin hariç)
    if (!isUserAdmin && selectedStaff.length === 0) {
      toast.error('İndirim uygulayabilmek için önce kasiyer seçmelisiniz!', { autoClose: 6000 });
      return;
    }
    // Önce indirim değerini hesapla
    const targetItem = cart.find(item => item.id === selectedItemForDiscount.id);
    if (!targetItem) return;

    const originalTotal = convertToTRY(targetItem.price, targetItem.currency) * targetItem.quantity;
    let discountValue = 0;

    if (itemDiscountType === 'percent') {
      const percent = Math.min(100, Math.max(0, itemDiscountAmount));
      discountValue = originalTotal * percent / 100;
    } else if (itemDiscountType === 'amount') {
      discountValue = Math.min(originalTotal, Math.max(0, itemDiscountAmount));
    } else if (itemDiscountType === 'round') {
      const targetPrice = Math.max(0, itemDiscountAmount);
      discountValue = Math.max(0, originalTotal - targetPrice);
    }

    // Maksimum indirim limiti kontrolü (admin hariç) - toplam manuel indirim bazlı
    if (!isUserAdmin && maxDiscountLimit !== null && discountBase > 0) {
      const base = Math.max(0, discountBase);
      const cartDiscountPercent = appliedDiscounts.reduce((sum, d) => {
        if (d.type === 'percent') return sum + d.value;
        if (d.type === 'round') return sum + (d.value > 0 && totalBeforeRound > 0 ? ((totalBeforeRound - d.value) / base) * 100 : 0);
        return sum + (d.value / base) * 100;
      }, 0);
      const otherItemDiscounts = cart.filter(ci => !ci.isRefund && ci.id !== targetItem.id).reduce((sum, ci) => sum + (ci.customItemDiscount || 0), 0);
      const otherItemPercent = (otherItemDiscounts / base) * 100;
      const thisItemPercent = (discountValue / base) * 100;
      const totalPercent = cartDiscountPercent + otherItemPercent + thisItemPercent;
      const effectiveLimit = approvedDiscountOverride || maxDiscountLimit;
      if (totalPercent > effectiveLimit) {
        // Onay varsa ama aşılıyorsa sıfırla
        if (approvedDiscountOverride) {
          setApprovedDiscountOverride(null);
          setDiscountApprovalId(null);
          setDiscountApprovalStatus(null);
        }
        toast.warning(`Maksimum indirim limiti %${effectiveLimit}'dir. Limit üzeri indirim için sepet indirimi bölümünden onay isteyiniz.`, { autoClose: 7500, toastId: 'discount-limit' });
        return;
      }
    }

    // Limit dahilinde, indirimi uygula
    setCart(cart.map(item => {
      if (item.id === selectedItemForDiscount.id) {
        return { ...item, customItemDiscount: discountValue };
      }
      return item;
    }));

    setShowItemDiscountModal(false);
    setSelectedItemForDiscount(null);
    toast.success('Ürün indirimi uygulandı');
  };

  // Ürün indirimini kaldır
  const removeItemDiscount = () => {
    if (!selectedItemForDiscount) return;

    setCart(cart.map(item => {
      if (item.id === selectedItemForDiscount.id) {
        return {
          ...item,
          customItemDiscount: 0
        };
      }
      return item;
    }));

    setShowItemDiscountModal(false);
    setSelectedItemForDiscount(null);
    toast.info('Ürün indirimi kaldırıldı');
  };

  // Miktar güncelle
  const updateQuantity = async (productId, change) => {
    if (isCartLockedByApproval) {
      toast.warning('Onaylı indirim varken miktar değiştirilemez!');
      return;
    }
    const item = cart.find(i => i.id === productId);
    if (!item) return;

    const oldQuantity = item.quantity;
    const newQuantity = item.quantity + change;
    if (newQuantity <= 0) {
      setCart(cart.filter(i => i.id !== productId));
      return;
    }

    // Mağaza stoğunu kontrol et
    const { myStock, otherStores } = await getStoreStock(productId);

    // Stok yetersizse uyarı göster ama eklemeye izin ver
    if (newQuantity > myStock) {
      if (otherStores.length > 0) {
        const storeList = otherStores
          .sort((a, b) => b.stock - a.stock)
          .map(s => `${s.store_name}'da ${s.stock} adet`)
          .join(', ');
        toast.warning(`Ürün stokta yok! ${storeList} mevcut.`, { autoClose: 7500 });
      } else {
        toast.warning('Bu üründen hiçbir mağazada stok yok!', { autoClose: 7500 });
      }
    }

    setCart(cart.map(i =>
      i.id === productId ? { ...i, quantity: newQuantity, storeStock: myStock } : i
    ));
  };

  // Fiyat değiştirme yetkisi kontrolü
  const canEditPrice = () => {
    if (user?.role === 'admin') return true;
    if (user?.role === 'cashier') {
      return user?.permissions?.canEditPrice !== false;
    }
    return true;
  };

  const canRefund = () => {
    if (user?.role === 'admin') return true;
    if (user?.role === 'cashier') {
      return user?.permissions?.canRefund !== false;
    }
    return true;
  };

  // Onaylı indirim varsa sepet kilitli mi?
  const isCartLockedByApproval = user?.role !== 'admin' && approvedDiscountOverride !== null;

  // Sepetteki ürün fiyatını değiştirme başlat
  const startEditingPrice = (item) => {
    if (!canEditPrice()) {
      toast.warning('Fiyat değiştirme yetkiniz yok!');
      return;
    }
    setEditingPriceItemId(item.id);
    setEditingPriceValue(item.price.toString());
  };

  // Fiyat değişikliğini kaydet
  const saveEditedPrice = (itemId) => {
    const newPrice = parseFloat(editingPriceValue);
    if (isNaN(newPrice) || newPrice < 0) {
      toast.error('Geçersiz fiyat!');
      return;
    }
    setCart(cart.map(item =>
      item.id === itemId ? { ...item, price: newPrice } : item
    ));
    setEditingPriceItemId(null);
    setEditingPriceValue('');
  };

  // Fiyat değişikliğini iptal et
  const cancelEditingPrice = () => {
    setEditingPriceItemId(null);
    setEditingPriceValue('');
  };

  // Manuel adet girişini kaydet
  const saveEditedQty = (itemId) => {
    const newQty = parseInt(editingQtyValue);
    if (isNaN(newQty) || newQty < 1) {
      toast.error('Geçersiz adet!');
      return;
    }
    setCart(cart.map(item =>
      item.id === itemId ? { ...item, quantity: newQty } : item
    ));
    setEditingQtyItemId(null);
    setEditingQtyValue('');
  };

  // Sepetten kaldır
  const removeFromCart = (productId) => {
    if (isCartLockedByApproval) {
      toast.warning('Onaylı indirim varken sepetten ürün çıkarılamaz!');
      return;
    }
    // Silinen ürün önizleme seçiliyse, önizlemeyi sıfırla
    if (selectedPreviewItem?.id === productId) {
      setSelectedPreviewItem(null);
    }
    setCart(cart.filter(item => item.id !== productId));
  };

  // Sepeti temizle
  // Tüm aktif satış state'lerini sıfırla (hard refresh etkisi)
  const resetAllSaleState = () => {
    // Sepet
    setCart([]);
    setSelectedPreviewItem(null);

    // Müşteri
    setSelectedCustomer(null);
    setSelectedCustomerDebt(null);
    setPreRefundCustomer(null);
    setCustomerSearchQuery('');
    setCustomerSearchResults([]);
    setPhoneMatchCustomers([]);

    // Kasiyer
    setSelectedStaff([]);

    // İndirimler
    setDiscountAmount(0);
    setDiscountType('percent');
    setAppliedDiscounts([]);
    setApprovedDiscountOverride(null);
    setDiscountApprovalId(null);
    setDiscountApprovalStatus(null);
    setIsRequestingApproval(false);
    setPendingDiscountRequest(null);
    setEditDiscount(0);

    // Ürün bazlı indirim
    setShowItemDiscountModal(false);
    setSelectedItemForDiscount(null);
    setItemDiscountAmount(0);
    setItemDiscountType('percent');

    // Hızlı fiyat
    setShowQuickPriceModal(false);
    setQuickTargetPrice('');

    // Kampanya
    setAppliedCampaign(null);
    setCampaignDiscount(0);
    setCampaignSuggestions([]);
    setBirthdayCampaign(null);
    setSpecialDayCampaign(null);
    setShowGiftCampaignModal(false);
    setEligibleGiftCampaign(null);
    setGiftCampaignProduct(null);

    // Bakiye / Hediye kartı
    setAppliedGiftCard(null);
    setGiftCardDiscount(0);
    setGiftCardCode('');
    setGiftCardInfo(null);
    setGiftCardError('');

    // Hurda borcu
    setCustomerScrapDebtCards([]);
    setAppliedScrapDebtCard(null);

    // Müşteri hediye kartları
    setCustomerGiftCards([]);
    setAppliedCustomerGiftCard(null);

    // Hurda sepeti
    clearScrapCart();
    setScrapFormData({
      metal_type: isGoldStore ? 'gold' : 'silver',
      purity: isGoldStore ? '22' : '925',
      gross_weight: '',
      waste_percent: '',
      waste_gram: '',
      unit_price: '',
      description: ''
    });
    setScrapBonusPercent(0);
    setShowScrapOptionsModal(false);
    setShowScrapFormModal(false);
    setShowScrapBalanceModal(false);
    setScrapBalanceInfo(null);

    // Farklar (adjustment)
    setAdjustmentItems([]);
    setAdjustmentForm({ amount: '', reason: '', description: '' });
    setEditingAdjustmentIndex(null);

    // Ürün notu
    setShowItemNoteModal(false);
    setSelectedItemForNote(null);
    setItemNoteText('');

    // Özel gramaj
    setShowSpecialWeightModal(false);
    setSpecialWeightProduct(null);
    setSpecialWeightInput('');

    // Fiş notu
    setReceiptNote('');
    setShowReceiptNoteInput(false);

    // Ödeme state'leri
    setShowPaymentModal(false);
    setShowPaymentCreditOptions(false);
    setReceivedAmount('');
    setSelectedCurrency('TRY');
    setPartialPayments([]);
    setShowCreditOptions(false);
    setPendingHavalePayment(null);
    setShowNakitModal(false);
    setNakitReceivedAmount('');
    setShowCardModal(false);
    setShowVirtualPosModal(false);
    setShowCashModal(false);
    setShowCashRefundConfirmModal(false);
    setShowTransferRefundConfirmModal(false);
    setShowScrapDebtTypeModal(false);
    setPendingPaymentMethod(null);
    setPendingDebtPayments([]);
    setShowStoreSelectModal(false);
    setSelectedStoreId(null);
    setPendingPaymentData(null);

    // Arama
    setSearchQuery('');
    setSearchResults([]);
    setShowSearchResults(false);

    // Sepet içi düzenleme
    setEditingPriceItemId(null);
    setEditingPriceValue('');
    setEditingQtyItemId(null);
    setEditingQtyValue('');

    // Resim popup
    setShowImagePopup(false);
    setPopupImageUrl('');
    setPopupImageName('');

    setTimeout(() => barcodeInputRef.current?.focus(), 100);
  };

  const clearCart = () => {
    resetAllSaleState();
  };

  // Çift ESC tuşu ile sepeti temizle
  const lastEscRef = useRef(0);
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        const now = Date.now();
        if (now - lastEscRef.current < 500) {
          clearCart();
          lastEscRef.current = 0;
        } else {
          lastEscRef.current = now;
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [cart, scrapCart]); // eslint-disable-line react-hooks/exhaustive-deps

  // Döviz kurunu TL'ye çevir (dinamik kur hesaplama)
  const convertToTRY = (amount, currency) => {
    if (!currency || currency === 'TRY') return amount;
    const rate = getSatisRate(currency);
    if (rate > 0) {
      return amount * rate;
    }
    return amount; // Kur bulunamazsa olduğu gibi döndür
  };

  // Sepet item'ının TL değerini hesapla
  const getItemTotalTRY = (item) => {
    return convertToTRY(item.price * item.quantity, item.currency);
  };

  // Hesaplamalar (tüm tutarlar TL cinsinden)
  // Ürün bazlı özel indirimleri de hesaba kat
  const newItemsOriginalTotal = cart.filter(item => !item.isRefund).reduce((sum, item) => {
    return sum + getItemTotalTRY(item);
  }, 0);
  const totalItemDiscounts = cart.filter(item => !item.isRefund).reduce((sum, item) => {
    return sum + (item.customItemDiscount || 0);
  }, 0);
  const newItemsTotal = newItemsOriginalTotal - totalItemDiscounts;
  const refundItemsTotal = cart.filter(item => item.isRefund).reduce((sum, item) => sum + Math.abs(getItemTotalTRY(item)), 0);
  const subtotal = newItemsTotal - refundItemsTotal;

  // Fark kaldırıldı - Kişiye Özel ürün olarak sepete ekleniyor
  const adjustmentTotal = 0;
  const positiveAdjustmentTotal = 0;

  // İndirim ürünlere uygulanır
  const discountBase = newItemsTotal;
  // Zincirleme indirim hesaplama: her indirim bir öncekinin sonucuna uygulanır
  // Örn: 1200 → %10 → 1080 → %10 → 972 (toplam indirim = 228)
  const calcChainedDiscount = (discounts, base, excludeRound = false) => {
    let remaining = Math.max(0, base);
    for (const d of discounts) {
      if (d.type === 'round' && excludeRound) continue;
      if (d.type === 'percent') {
        remaining -= remaining * d.value / 100;
      } else if (d.type === 'amount') {
        remaining -= d.value;
      } else if (d.type === 'round' && d.value > 0) {
        remaining = Math.min(remaining, d.value);
      }
      remaining = Math.max(0, remaining);
    }
    return Math.max(0, base) - remaining;
  };

  const manualDiscountWithoutRound = Math.min(
    Math.max(0, discountBase),
    calcChainedDiscount(appliedDiscounts, discountBase, true)
  );
  const totalBeforeRound = subtotal - manualDiscountWithoutRound - campaignDiscount - giftCardDiscount - effectiveScrapTotal + adjustmentTotal;
  const manualDiscount = Math.min(
    Math.max(0, discountBase),
    calcChainedDiscount(appliedDiscounts, discountBase, false)
  );

  // Hurda indirimi: Aktif edilmişse sepet tutarına göre hesapla
  // Gram bazlı kartlar için güncel kurla TL'ye çevir - debt_type kontrolü
  const calculateCurrentScrapDebtTL = () => {
    if (!appliedScrapDebtCard || !appliedScrapDebtCard.cards) return 0;
    let totalTL = 0;
    for (const card of appliedScrapDebtCard.cards) {
      const debtType = card.debt_type || 'TL';
      if (debtType === 'GRAM') {
        // Altın gram - remaining_amount gram cinsinden, güncel satış kuruyla çarp
        const currentGoldRate = getSatisRate('GOLD');
        totalTL += Number(card.remaining_amount) * currentGoldRate;
      } else if (debtType === 'GUMUS') {
        // Gümüş gram - remaining_amount gram cinsinden, güncel satış kuruyla çarp
        const currentSilverRate = getSatisRate('SILVER');
        totalTL += Number(card.remaining_amount) * currentSilverRate;
      } else {
        // TL bazlı - direkt remaining_amount kullan
        totalTL += Number(card.remaining_amount);
      }
    }
    return totalTL;
  };
  const currentScrapDebtTL = calculateCurrentScrapDebtTL();
  const calculatedScrapDebtDiscount = (appliedScrapDebtCard && currentScrapDebtTL > 0)
    ? Math.min(currentScrapDebtTL, Math.max(0, subtotal - manualDiscount - campaignDiscount - giftCardDiscount))
    : 0;

  // Müşteri bakiye indirimi (customer_balance_transactions üzerinden)
  const customerGiftCardDiscount = (() => {
    if (!appliedCustomerGiftCard || !appliedCustomerGiftCard.creditAmount) return 0;
    const remainingSubtotal = Math.max(0, subtotal - manualDiscount - campaignDiscount - giftCardDiscount - calculatedScrapDebtDiscount);
    return Math.min(appliedCustomerGiftCard.creditAmount, remainingSubtotal);
  })();

  // Hurda bonusu: Sadece sepet tutarı hurda tutarından büyükse uygulanır
  const scrapBonus = (subtotal > effectiveScrapTotal && appliedScrap?.bonus_amount > 0)
    ? appliedScrap.bonus_amount
    : 0;

  // Kampanya + manuel indirim + bakiye toplamda ürün bedelinin %100'ünü geçemez.
  // (İade/değişim sepetlerinde iade edilen üründen dolayı bakiye negatife dönmesi normal olduğundan bu sınır uygulanmaz.)
  const hasRefundItemsInCart = cart.some(item => item.isRefund);
  const rawProductDiscount = manualDiscount + campaignDiscount + giftCardDiscount;
  const productDiscount = hasRefundItemsInCart ? rawProductDiscount : Math.min(rawProductDiscount, Math.max(0, discountBase));

  const totalDiscount = productDiscount + scrapBonus + calculatedScrapDebtDiscount; // Kampanya + manuel + bakiye (sınırlı) + hurda bonusu + hurda indirimi (müşteri bakiyesi ödeme yöntemi olarak ayrı)
  const afterDiscount = subtotal - totalDiscount;

  const total = afterDiscount - effectiveScrapTotal + adjustmentTotal; // Hurda mahsubu ayrı düşülüyor + fark ekleniyor

  // Toplam manuel indirim yüzdesini hesapla (item + cart indirimleri)
  const getExistingManualDiscountPercent = () => {
    const base = Math.max(0, discountBase);
    if (base <= 0) return 0;
    // Sepet indirimleri (appliedDiscounts)
    const cartPercent = appliedDiscounts.reduce((sum, d) => {
      if (d.type === 'percent') return sum + d.value;
      if (d.type === 'round') return sum + (d.value > 0 && totalBeforeRound > 0 ? ((totalBeforeRound - d.value) / base) * 100 : 0);
      return sum + (d.value / base) * 100;
    }, 0);
    // Ürün bazlı özel indirimler
    const itemCustomTotal = cart.filter(item => !item.isRefund).reduce((sum, item) => sum + (item.customItemDiscount || 0), 0);
    const itemPercent = (itemCustomTotal / base) * 100;
    return cartPercent + itemPercent;
  };

  // İndirim değerini ayarla ve otomatik uygula
  const applyDiscountValue = (rawVal) => {
    // Yüzde indirimi %100'ü geçemez (iade/değişim sepetlerinde bu kısıtlama toplam hesaplamada ayrıca gözetiliyor)
    const val = discountType === 'percent' ? Math.min(100, Math.max(0, rawVal)) : rawVal;
    setDiscountAmount(val);
    if (val > 0) {
      const label = discountType === 'percent' ? `%${val}` : discountType === 'round' ? `Yuvarla → ₺${Number(val).toLocaleString('tr-TR')}` : `₺${Number(val).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}`;
      const newDiscount = { id: Date.now(), type: discountType, value: val, label };
      // Aynı tipte ve aynı değerde varsa tekrar ekleme, değer değiştiyse güncelle
      setAppliedDiscounts(prev => {
        const lastSameType = [...prev].reverse().find(d => d.type === discountType);
        if (lastSameType) {
          // Son aynı tipteki indirimi güncelle
          return prev.map(d => d.id === lastSameType.id ? newDiscount : d);
        }
        return [...prev, newDiscount];
      });
    } else {
      // Değer 0 ise son aynı tipteki indirimi kaldır
      setAppliedDiscounts(prev => {
        const lastIdx = prev.map(d => d.type).lastIndexOf(discountType);
        if (lastIdx >= 0) return prev.filter((_, i) => i !== lastIdx);
        return prev;
      });
    }
  };

  // Sepet indirimi limit kontrolü (kasiyerler için) - input değerini ayarlar
  const handleSetCartDiscount = (value) => {
    if (user?.role === 'admin') {
      applyDiscountValue(value);
      return;
    }
    if (selectedStaff.length === 0) {
      applyDiscountValue(value);
      return;
    }
    if (maxDiscountLimit === null) {
      applyDiscountValue(value);
      return;
    }
    const base = Math.max(0, discountBase);
    if (base <= 0) { applyDiscountValue(value); return; }

    // Aynı anda sadece bir indirim tipi olduğu için mevcut sepet indirimi sıfır
    const existingCartPercent = 0;
    // Ürün indirimleri yüzdesi
    const itemCustomTotal = cart.filter(item => !item.isRefund).reduce((sum, item) => sum + (item.customItemDiscount || 0), 0);
    const itemPercent = (itemCustomTotal / base) * 100;

    let newEffectivePercent = 0;
    if (discountType === 'percent') {
      newEffectivePercent = value;
    } else if (discountType === 'amount') {
      newEffectivePercent = (value / base) * 100;
    } else if (discountType === 'round') {
      newEffectivePercent = value > 0 && totalBeforeRound > 0 ? ((totalBeforeRound - value) / base) * 100 : 0;
    }

    const totalPercent = existingCartPercent + newEffectivePercent + itemPercent;
    // Onay varsa onaylanan yüzde, yoksa kasiyer limiti
    const effectiveLimit = approvedDiscountOverride || maxDiscountLimit;

    if (totalPercent <= effectiveLimit) {
      applyDiscountValue(value);
      return;
    }

    // Limit aşıldı - onay varsa sıfırla, yeni onay gerekecek
    if (approvedDiscountOverride) {
      setApprovedDiscountOverride(null);
      setDiscountApprovalId(null);
      setDiscountApprovalStatus(null);
    }

    // Kasiyer base limitine kısıtla
    const remainingPercent = Math.max(0, maxDiscountLimit - existingCartPercent - itemPercent);
    let cappedValue;
    if (discountType === 'percent') {
      cappedValue = Math.round(remainingPercent * 100) / 100;
    } else if (discountType === 'amount') {
      cappedValue = Math.round(base * remainingPercent / 100 * 100) / 100;
    } else if (discountType === 'round') {
      cappedValue = Math.round(totalBeforeRound - (base * remainingPercent / 100));
    }
    // Sepete sınırlı değeri uygula
    const label = discountType === 'percent' ? `%${cappedValue}` : discountType === 'round' ? `Yuvarla → ₺${Number(cappedValue).toLocaleString('tr-TR')}` : `₺${Number(cappedValue).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}`;
    const newDiscount = { id: Date.now(), type: discountType, value: cappedValue, label };
    setAppliedDiscounts(cappedValue > 0 ? [newDiscount] : []);
    // Input'ta orijinal değeri koru - böylece onay istenebilir
    setDiscountAmount(value);
    toast.warning(`Maksimum indirim limiti %${maxDiscountLimit}'dir. Limit üzeri indirim için onay isteyiniz.`, { autoClose: 6000, toastId: 'discount-limit' });
  };

  // İndirim ekle (appliedDiscounts dizisine)
  const addDiscount = () => {
    if (!discountAmount || discountAmount <= 0) return;
    // Kasiyer seçimi kontrolü (admin hariç)
    if (user?.role !== 'admin' && selectedStaff.length === 0) {
      toast.error('İndirim uygulayabilmek için önce kasiyer seçmelisiniz!', { autoClose: 6000 });
      return;
    }
    const label = discountType === 'percent' ? `%${discountAmount}` : discountType === 'round' ? `Yuvarla → ₺${Number(discountAmount).toLocaleString('tr-TR')}` : `₺${Number(discountAmount).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}`;
    const newDiscount = { id: Date.now(), type: discountType, value: discountAmount, label };
    // Mevcut indirimlerin üstüne ekle (kümülatif/zincirleme)
    setAppliedDiscounts(prev => [...prev, newDiscount]);
    setDiscountAmount(0);
  };

  // Tek bir indirimi sil
  const removeDiscount = (discountId) => {
    setAppliedDiscounts(prev => prev.filter(d => d.id !== discountId));
  };

  // İndirim onay isteği gönder
  const requestDiscountApproval = async (requestedPercent, requestedAmount, originalTotal, discountedTotal, context = {}) => {
    if (isRequestingApproval) return;
    setIsRequestingApproval(true);

    const requestSource = context.source === 'item' ? 'item' : 'cart';
    const requestItemId = requestSource === 'item' ? (context.itemId ?? selectedItemForDiscount?.id ?? null) : null;
    const requestType = requestSource === 'item' ? itemDiscountType : discountType;
    const requestValue = requestSource === 'item' ? itemDiscountAmount : discountAmount;

    try {
      let storeId = null;
      if (user?.role === 'admin') {
        storeId = selectedStore?.id || user?.store_id;
      } else {
        storeId = user?.store_id;
      }

      const staffNames = selectedStaff.map(s => s.name).join(', ');

      // Her ürün için detaylı bilgi: fiyat, kampanya indirimi, manuel indirim
      const cartItems = cartWithDiscounts.filter(item => !item.isRefund).map(item => {
        const originalPrice = convertToTRY(item.price, item.currency);
        const originalTotal = originalPrice * item.quantity;
        const campaignItemDiscount = item.itemDiscount || 0;
        const manualItemDiscount = item.customItemDiscount || 0;

        // Mobil app artık metal_type/gold_karat/weight/labor_cost alanlarını
        // doğrudan okuyup gösteriyor, name alanına gömmeye gerek yok.
        let displayName = item.name;
        const itemNote = (item.note || '').trim();

        return {
          product_id: item.productId || item.id,
          barcode: item.barcode || '',
          name: displayName,
          image_url: item.image || null,
          quantity: item.quantity,
          unit_price: originalPrice,
          original_total: originalTotal,
          campaign_discount: campaignItemDiscount,
          manual_discount: manualItemDiscount,
          discounted_total: item.discountedTotal || (originalTotal - campaignItemDiscount - manualItemDiscount),
          is_campaign_item: item.isCampaignItem || false,
          is_free_item: item.isFreeItem || false,
          is_special_defined: item.isSpecialDefined || false,
          // İleride mobil app güncellenirse doğrudan okunabilsin
          metal_type: item.metal_type || null,
          gold_karat: item.goldKarat || null,
          weight: item.customWeight || item.weight || null,
          labor_cost: item.laborCost ?? null,
          note: itemNote || null
        };
      });

      // Notlara detaylı özet ekle
      const notesLines = [`Kasiyer limiti: %${maxDiscountLimit}, İstenen: %${requestedPercent.toFixed(1)}`];
      if (campaignDiscount > 0 && appliedCampaign) {
        notesLines.push(`Kampanya: ${appliedCampaign.name || appliedCampaign.campaign_type} (-₺${campaignDiscount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })})`);
      }

      const response = await fetch(`${API_URL}/discount-approvals`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          store_id: storeId,
          requester_user_id: user?.id,
          requester_user_name: user?.name,
          staff_id: selectedStaff[0]?.id || null,
          staff_name: staffNames || user?.name,
          customer_id: selectedCustomer?.id || null,
          customer_name: selectedCustomer?.name || null,
          items: cartItems,
          original_total: originalTotal,
          requested_discount: requestedAmount,
          requested_discount_percent: requestedPercent,
          discounted_total: discountedTotal,
          max_allowed_percent: maxDiscountLimit,
          campaign_discount: campaignDiscount || 0,
          campaign_name: appliedCampaign?.name || null,
          manual_discount: manualDiscount || 0,
          gold_rate: getSatisRate('GOLD') || null,
          silver_rate: getSatisRate('SILVER') || null,
          notes: notesLines.join('\n')
        })
      });

      const data = await response.json();
      if (data.success && data.id) {
        setDiscountApprovalId(data.id);
        setDiscountApprovalStatus('pending');
        // Onay geldiğinde otomatik uygulamak için bekleyen indirim bilgisini kaydet
        setPendingDiscountRequest({ source: requestSource, itemId: requestItemId, type: requestType, value: requestValue, percent: requestedPercent });
        toast.info('İndirim onay isteği gönderildi. Yönetici onayı bekleniyor...', { autoClose: 7500 });
      } else {
        toast.error('Onay isteği gönderilemedi: ' + (data.error || 'Bilinmeyen hata'));
      }
    } catch (error) {
      console.error('Discount approval request error:', error);
      toast.error('Onay isteği gönderilirken hata oluştu');
    }

    setIsRequestingApproval(false);
  };

  // İndirim onay durumunu kontrol et (polling)
  useEffect(() => {
    if (!discountApprovalId || discountApprovalStatus !== 'pending') return;

    const pollInterval = setInterval(async () => {
      try {
        const response = await fetch(`${API_URL}/discount-approvals/${discountApprovalId}`);
        const data = await response.json();
        if (data.status === 'approved') {
          setDiscountApprovalStatus('approved');
          // Onaylanan yüzdeyi kaydet (bu yüzdeye kadar izin verilir)
          setApprovedDiscountOverride(pendingDiscountRequest?.percent || null);
          // Onay gelince bekleyen indirim bilgisiyle indirimi otomatik uygula
          if (pendingDiscountRequest && pendingDiscountRequest.value > 0) {
            const pType = pendingDiscountRequest.type;
            const pValue = pendingDiscountRequest.value;
            if (pendingDiscountRequest.source === 'item' && pendingDiscountRequest.itemId != null) {
              // Ürün bazlı indirim: sadece ilgili sepet kalemine uygula
              const itemId = pendingDiscountRequest.itemId;
              setCart(prevCart => prevCart.map(item => {
                if (item.id !== itemId) return item;
                const itemTotal = convertToTRY(item.price, item.currency) * item.quantity;
                let discountValue = 0;
                if (pType === 'percent') discountValue = itemTotal * Math.min(100, Math.max(0, pValue)) / 100;
                else if (pType === 'amount') discountValue = Math.min(itemTotal, Math.max(0, pValue));
                else if (pType === 'round') discountValue = Math.max(0, itemTotal - Math.max(0, pValue));
                return { ...item, customItemDiscount: discountValue };
              }));
              setShowItemDiscountModal(false);
              setSelectedItemForDiscount(null);
              setItemDiscountAmount(0);
            } else {
              const label = pType === 'percent' ? `%${pValue}` : pType === 'round' ? `Yuvarla → ₺${Number(pValue).toLocaleString('tr-TR')}` : `₺${Number(pValue).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}`;
              const newDiscount = { id: Date.now(), type: pType, value: pValue, label };
              setAppliedDiscounts([newDiscount]);
              setDiscountAmount(pValue);
              setShowDiscountModal(false);
            }
            setPendingDiscountRequest(null);
          }
          toast.success('İndirim onaylandı ve uygulandı!', { autoClose: 7500 });
          clearInterval(pollInterval);
        } else if (data.status === 'rejected') {
          setDiscountApprovalStatus('rejected');
          setPendingDiscountRequest(null);
          toast.error('İndirim talebi reddedildi.', { autoClose: 7500 });
          clearInterval(pollInterval);
          // 5 saniye sonra durumu sıfırla
          setTimeout(() => {
            setDiscountApprovalId(null);
            setDiscountApprovalStatus(null);
          }, 5000);
        }
      } catch (err) {
        console.error('Polling error:', err);
      }
    }, 3000);

    return () => clearInterval(pollInterval);
  }, [discountApprovalId, discountApprovalStatus, pendingDiscountRequest]);

  // Sepet temizlendiğinde onay durumunu sıfırla
  useEffect(() => {
    if (cart.length === 0) {
      setDiscountApprovalId(null);
      setDiscountApprovalStatus(null);
      setApprovedDiscountOverride(null);
      setPendingDiscountRequest(null);
    }
  }, [cart.length]);

  // Ürün bazlı indirim dağıtımı - kampanya ve manuel indirimler ayrı ayrı uygulanır
  const cartWithDiscounts = (() => {
    const cartItems = cart.filter(item => !item.isRefund);
    const refundItems = cart.filter(item => item.isRefund);

    if (cartItems.length === 0) {
      return cart.map(item => {
        const priceTRY = convertToTRY(item.price, item.currency);
        return {
          ...item,
          originalTotal: priceTRY * item.quantity,
          itemDiscount: 0,
          discountedTotal: priceTRY * item.quantity,
          discountedUnitPrice: priceTRY,
          discountPercent: 0,
          isCampaignItem: false,
          isFreeItem: false
        };
      });
    }

    // Tüm ürün birimlerini fiyatlarıyla listele (kampanya hesaplaması için)
    // Kişiye özel ürünler kampanyalara dahil edilmez (allUnits'a eklenmez)
    let allUnits = [];
    cartItems.forEach((item, itemIndex) => {
      if (item.isCustomProduct) return;
      const priceTRY = convertToTRY(item.price, item.currency);
      for (let i = 0; i < item.quantity; i++) {
        allUnits.push({
          itemIndex,
          itemId: item.id,
          price: priceTRY,
          unitIndex: i
        });
      }
    });

    // Orijinal sırayı sakla (kademeli indirim için)
    const originalOrderUnits = [...allUnits];

    // Fiyata göre sırala (en ucuzdan en pahalıya - buy_x_pay_y için)
    allUnits.sort((a, b) => a.price - b.price);

    // Kampanya türüne göre hangi birimlerin ücretsiz/indirimli olduğunu belirle
    let freeUnitIndices = new Set(); // Tamamen ücretsiz birimler
    let campaignUnitIndices = new Set(); // Kampanya kapsamındaki birimler
    let campaignDiscountPerUnit = {}; // Her birim için kampanya indirimi

    // Özel gün ve doğum günü kampanyaları için campaignDiscount > 0 koşulu aranmaz
    const isSpecialCampaign = appliedCampaign && (appliedCampaign.campaign_type === 'special_day' || appliedCampaign.campaign_type === 'birthday');

    if (appliedCampaign && (campaignDiscount > 0 || isSpecialCampaign)) {
      const conditions = typeof appliedCampaign.conditions === 'string'
        ? JSON.parse(appliedCampaign.conditions || '{}')
        : appliedCampaign.conditions || {};
      const rewards = typeof appliedCampaign.rewards === 'string'
        ? JSON.parse(appliedCampaign.rewards || '{}')
        : appliedCampaign.rewards || {};

      if (appliedCampaign.campaign_type === 'buy_x_pay_y') {
        // X al Y öde: En ucuz ürünler ücretsiz
        const buyCount = conditions.buy_quantity || conditions.buy_count || 3;
        const freeQty = rewards.free_quantity || 1;
        const repeatEnabled = rewards.repeat || false;

        // Seçili kategori/tür/marka varsa kampanya YALNIZCA kapsam içi ürünleri sayar (boşsa tüm sepet)
        const bxMetalTypes = conditions.metal_types || [];
        const bxCategories = conditions.categories || [];
        const bxBrands = conditions.brands || [];
        const hasBxFilter = bxMetalTypes.length > 0 || bxCategories.length > 0 || bxBrands.length > 0;
        const bxUnits = !hasBxFilter ? allUnits : allUnits.filter(unit => {
          const item = cartItems[unit.itemIndex];
          return (bxMetalTypes.length === 0 || bxMetalTypes.includes(item.metal_type))
            && (bxCategories.length === 0 || bxCategories.includes(item.category))
            && (bxBrands.length === 0 || bxBrands.includes(item.brand));
        });
        const totalUnits = bxUnits.length;

        // Tekrarlama kontrolü: Kaç grup oluşturulabilir?
        const maxGroups = repeatEnabled ? Math.floor(totalUnits / buyCount) : Math.min(1, Math.floor(totalUnits / buyCount));
        const freeCount = maxGroups * freeQty;

        // En ucuz birimleri ücretsiz yap (yalnızca kapsam içi birimler arasından)
        for (let i = 0; i < freeCount && i < bxUnits.length; i++) {
          const unit = bxUnits[i];
          const key = `${unit.itemIndex}-${unit.unitIndex}`;
          freeUnitIndices.add(key);
          campaignUnitIndices.add(key);
          campaignDiscountPerUnit[key] = unit.price; // Tam fiyat indirim
        }

        // Kampanya kapsamındaki diğer ürünleri de işaretle (ücretli olanlar)
        const campaignGroupCount = maxGroups * buyCount;
        for (let i = freeCount; i < campaignGroupCount && i < bxUnits.length; i++) {
          const unit = bxUnits[i];
          const key = `${unit.itemIndex}-${unit.unitIndex}`;
          campaignUnitIndices.add(key);
          campaignDiscountPerUnit[key] = 0; // İndirim yok ama kampanya kapsamında
        }
      } else if (appliedCampaign.campaign_type === 'percentage' || appliedCampaign.campaign_type === 'fixed_amount' || appliedCampaign.campaign_type === 'special_day' || appliedCampaign.campaign_type === 'birthday') {
        // Yüzde, sabit tutar, özel gün veya doğum günü indirimi: Tüm ürünlere orantılı dağıt
        // Kişiye özel ürünler hariç (kampanyaya dahil değil)
        const cartTotal = cartItems.reduce((sum, item) => sum + (item.isCustomProduct ? 0 : convertToTRY(item.price, item.currency) * item.quantity), 0);

        // Özel gün ve doğum günü kampanyaları için her ürüne yüzde/sabit indirim uygula
        if (appliedCampaign.campaign_type === 'special_day' || appliedCampaign.campaign_type === 'birthday') {
          const discountType = rewards.discount_type || 'percentage';
          const discountPercent = rewards.discount_percent || 0;
          const discountAmount = rewards.discount_amount || 0;
          const repeatEnabled = rewards.repeat || false;

          // Kapsam filtresi: seçili tür/kategori/marka varsa SADECE onlara indirim uygula (hiçbiri seçili değilse tümü)
          const spMetalTypes = conditions.metal_types || [];
          const spCategories = conditions.categories || [];
          const spBrands = conditions.brands || [];
          const hasSpFilter = spMetalTypes.length > 0 || spCategories.length > 0 || spBrands.length > 0;
          const isUnitEligible = (unit) => {
            if (!hasSpFilter) return true;
            const item = cartItems[unit.itemIndex];
            const metalMatch = spMetalTypes.length === 0 || spMetalTypes.includes(item.metal_type);
            const catMatch = spCategories.length === 0 || spCategories.includes(item.category);
            const brandMatch = spBrands.length === 0 || spBrands.includes(item.brand);
            return metalMatch && catMatch && brandMatch;
          };
          // Sabit tutar dağıtımı için kapsam içi toplam (kapsam dışı ürünler hariç)
          const eligibleTotal = allUnits.filter(isUnitEligible).reduce((sum, u) => sum + u.price, 0);

          // Minimum tutar kontrolü
          const minPurchase = conditions.min_purchase || 0;
          const meetsMinimum = minPurchase === 0 || cartTotal >= minPurchase;

          // Tekrarlama: Min. tutar kaç kez karşılandı?
          const repeatCount = repeatEnabled && minPurchase > 0
            ? Math.floor(cartTotal / minPurchase)
            : 1;

          if (meetsMinimum) {
            allUnits.forEach((unit, idx) => {
              // Kapsam dışı (seçilmeyen kategori/tür/marka) ürünlere indirim uygulanmaz
              if (!isUnitEligible(unit)) return;
              const key = `${unit.itemIndex}-${unit.unitIndex}`;
              campaignUnitIndices.add(key);

              if (discountType === 'fixed') {
                // Sabit tutar: Kapsam içi ürünler arasında orantılı dağıt (tekrar sayısı ile çarp)
                const unitShare = eligibleTotal > 0 ? unit.price / eligibleTotal : 0;
                campaignDiscountPerUnit[key] = discountAmount * repeatCount * unitShare;
              } else {
                // Yüzde: Her ürüne aynı yüzde
                campaignDiscountPerUnit[key] = unit.price * discountPercent / 100;
              }
            });
          }
        } else {
          // Normal yüzde/sabit tutar kampanyası — seçili kategori/tür/marka varsa YALNIZCA o ürünlere
          const npMetalTypes = conditions.metal_types || [];
          const npCategories = conditions.categories || [];
          const npBrands = conditions.brands || [];
          const hasNpFilter = npMetalTypes.length > 0 || npCategories.length > 0 || npBrands.length > 0;
          const npEligible = (unit) => {
            if (!hasNpFilter) return true;
            const item = cartItems[unit.itemIndex];
            return (npMetalTypes.length === 0 || npMetalTypes.includes(item.metal_type))
              && (npCategories.length === 0 || npCategories.includes(item.category))
              && (npBrands.length === 0 || npBrands.includes(item.brand));
          };
          // İndirim kapsam içi ürünlerin fiyatına orantılı dağıtılır
          const npEligibleTotal = allUnits.filter(npEligible).reduce((sum, u) => sum + u.price, 0);
          allUnits.forEach((unit, idx) => {
            // Kapsam dışı (seçilmeyen kategori/tür/marka) ürünlere indirim uygulanmaz
            if (!npEligible(unit)) return;
            const key = `${unit.itemIndex}-${unit.unitIndex}`;
            campaignUnitIndices.add(key);
            const unitShare = npEligibleTotal > 0 ? unit.price / npEligibleTotal : 0;
            campaignDiscountPerUnit[key] = campaignDiscount * unitShare;
          });
        }
      } else if (appliedCampaign.campaign_type === 'progressive') {
        // Kademeli indirim: Fiyata göre sıralı, %10 → %20 → %30 → %10 → %20 → %30...
        const progressiveTiers = rewards.tiers || [];
        const repeatTiers = rewards.repeat || false;
        const tierCount = progressiveTiers.length;

        // Kategori/tür/marka filtresi - sadece seçili kapsamdaki ürünlere uygula
        const progMetalTypes = conditions.metal_types || [];
        const progCategories = conditions.categories || [];
        const progBrands = conditions.brands || [];
        const hasProgFilter = progMetalTypes.length > 0 || progCategories.length > 0 || progBrands.length > 0;

        // Uygun birimleri filtrele ve fiyata göre azalan sırala (en pahalı önce)
        // Repeat aktifse: ürünler kademelere eşit bölünür (en pahalı grup ilk kademe, en ucuz grup son kademe)
        // Repeat kapalıysa: ilk T ürüne tier[0..T-1], kalan ürünlere indirim yok
        const eligibleProgUnits = originalOrderUnits.filter(unit => {
          if (!hasProgFilter) return true;
          const item = cartItems[unit.itemIndex];
          const metalMatch = progMetalTypes.length === 0 || progMetalTypes.includes(item.metal_type);
          const catMatch = progCategories.length === 0 || progCategories.includes(item.category);
          const brandMatch = progBrands.length === 0 || progBrands.includes(item.brand);
          return metalMatch && catMatch && brandMatch;
        });
        const priceOrderedUnits = [...eligibleProgUnits].sort((a, b) => b.price - a.price);
        const progN = priceOrderedUnits.length;
        const progItemsPerTier = tierCount > 0 ? Math.ceil(progN / tierCount) : 0;

        priceOrderedUnits.forEach((unit, idx) => {
          const key = `${unit.itemIndex}-${unit.unitIndex}`;
          campaignUnitIndices.add(key);

          let tierIdx;
          if (repeatTiers && tierCount > 0 && progItemsPerTier > 0) {
            tierIdx = Math.min(Math.floor(idx / progItemsPerTier), tierCount - 1);
          } else {
            tierIdx = idx;
          }

          if (progressiveTiers[tierIdx]) {
            const discountPercent = progressiveTiers[tierIdx].discount_percent || progressiveTiers[tierIdx].percentage || 0;
            campaignDiscountPerUnit[key] = unit.price * discountPercent / 100;
          } else {
            campaignDiscountPerUnit[key] = 0;
          }
        });
      } else if (appliedCampaign.campaign_type === 'bundle') {
        // Set/Bundle kampanyası: Sadece seçili kategorilerdeki ürünlere indirim uygula
        const bundleCategories = conditions.categories || [];
        const bundleDiscountPercent = rewards.discount_percent || rewards.discount_percentage || 0;
        const bundleDiscountFixed = rewards.discount_amount || 0;

        // Sadece seçili kategorilerdeki ürünlerin toplam tutarı
        const bundleCategoryTotal = cartItems
          .filter(item => bundleCategories.includes(item.category))
          .reduce((sum, item) => sum + (convertToTRY(item.price, item.currency) * item.quantity), 0);

        allUnits.forEach((unit, idx) => {
          const item = cartItems[unit.itemIndex];
          const key = `${unit.itemIndex}-${unit.unitIndex}`;

          // Sadece seçili kategorilerdeki ürünlere indirim uygula
          if (bundleCategories.includes(item.category)) {
            campaignUnitIndices.add(key);

            if (bundleDiscountPercent > 0) {
              // Yüzde indirim: Her ürüne aynı yüzde
              campaignDiscountPerUnit[key] = unit.price * bundleDiscountPercent / 100;
            } else if (bundleDiscountFixed > 0) {
              // Sabit tutar: Orantılı dağıt
              const unitShare = unit.price / bundleCategoryTotal;
              campaignDiscountPerUnit[key] = bundleDiscountFixed * unitShare;
            }
          }
        });
      } else if (appliedCampaign.campaign_type === 'category_discount') {
        // Kategori indirimi: Sadece seçili kategorilerdeki ürünlere yüzde indirim
        const catDiscountCategories = conditions.categories || [];
        const catDiscountPercent = rewards.discount_percent || 0;

        allUnits.forEach((unit, idx) => {
          const item = cartItems[unit.itemIndex];
          const key = `${unit.itemIndex}-${unit.unitIndex}`;

          // Sadece seçili kategorilerdeki ürünlere indirim uygula
          if (catDiscountCategories.length === 0 || catDiscountCategories.includes(item.category)) {
            campaignUnitIndices.add(key);
            campaignDiscountPerUnit[key] = unit.price * catDiscountPercent / 100;
          }
        });
      } else if (appliedCampaign.campaign_type === 'product_discount') {
        // Ürün indirimi: Sadece seçili ürünlere yüzde indirim
        const prodDiscountProducts = conditions.products || [];
        const prodDiscountPercent = rewards.discount_percent || 0;

        allUnits.forEach((unit, idx) => {
          const item = cartItems[unit.itemIndex];
          const key = `${unit.itemIndex}-${unit.unitIndex}`;

          // Sadece seçili ürünlere indirim uygula
          if (prodDiscountProducts.length === 0 || prodDiscountProducts.includes(item.id)) {
            campaignUnitIndices.add(key);
            campaignDiscountPerUnit[key] = unit.price * prodDiscountPercent / 100;
          }
        });
      }
    }

    // Her ürün için indirim hesapla
    const result = cartItems.map((item, itemIndex) => {
      let itemCampaignDiscount = 0;
      let itemManualDiscount = 0;
      let freeUnitsCount = 0;
      let campaignUnitsCount = 0;
      let nonCampaignUnitsCount = 0;

      // Bu ürünün birimlerini kontrol et
      for (let i = 0; i < item.quantity; i++) {
        const key = `${itemIndex}-${i}`;
        if (freeUnitIndices.has(key)) {
          freeUnitsCount++;
          itemCampaignDiscount += convertToTRY(item.price, item.currency);
        } else if (campaignUnitIndices.has(key)) {
          campaignUnitsCount++;
          itemCampaignDiscount += campaignDiscountPerUnit[key] || 0;
        } else {
          nonCampaignUnitsCount++;
        }
      }

      // Manuel indirim ve bakiye indirimi artık ürünlere dağıtılmıyor
      // Sadece toplam sepet indirimi olarak gösteriliyor
      itemManualDiscount = 0;

      // Ürün bazlı özel indirim (customItemDiscount)
      const customDiscount = item.customItemDiscount || 0;

      const priceTRY = convertToTRY(item.price, item.currency);
      const itemTotal = priceTRY * item.quantity;
      const totalItemDiscount = itemCampaignDiscount + itemManualDiscount + customDiscount;
      const discountedTotal = Math.max(0, itemTotal - totalItemDiscount);
      const discountedUnitPrice = item.quantity > 0 ? discountedTotal / item.quantity : 0;
      const discountPercent = itemTotal > 0 ? (totalItemDiscount / itemTotal) * 100 : 0;

      return {
        ...item,
        originalTotal: itemTotal,
        itemDiscount: totalItemDiscount,
        campaignDiscount: itemCampaignDiscount,
        manualDiscount: itemManualDiscount,
        customDiscount: customDiscount,
        discountedTotal: discountedTotal,
        discountedUnitPrice: discountedUnitPrice,
        discountPercent: discountPercent,
        isCampaignItem: campaignUnitsCount > 0 || freeUnitsCount > 0,
        isFreeItem: freeUnitsCount === item.quantity,
        freeUnitsCount: freeUnitsCount,
        campaignUnitsCount: campaignUnitsCount + freeUnitsCount,
        nonCampaignUnitsCount: nonCampaignUnitsCount
      };
    });

    // İade ürünlerini de ekle
    const refundResult = refundItems.map(item => {
      const priceTRY = convertToTRY(item.price, item.currency);
      return {
        ...item,
        originalTotal: priceTRY * item.quantity,
        itemDiscount: 0,
        campaignDiscount: 0,
        manualDiscount: 0,
        discountedTotal: priceTRY * item.quantity,
        discountedUnitPrice: priceTRY,
        discountPercent: 0,
        isCampaignItem: false,
        isFreeItem: false,
        freeUnitsCount: 0,
        campaignUnitsCount: 0,
        nonCampaignUnitsCount: item.quantity
      };
    });

    // Sepet bazlı indirimi (manualDiscount) ürünlere + farklara fiyata oranla dağıt
    const itemsTotal = result.reduce((sum, item) => sum + item.discountedTotal, 0);
    const totalBeforeCartDiscount = itemsTotal + positiveAdjustmentTotal;
    result.forEach(item => {
      if (manualDiscount > 0 && totalBeforeCartDiscount > 0) {
        item.cartDiscountShare = (item.discountedTotal / totalBeforeCartDiscount) * manualDiscount;
      } else {
        item.cartDiscountShare = 0;
      }
      const totalItemAndCartDiscount = item.itemDiscount + item.cartDiscountShare;
      item.finalTotal = Math.max(0, item.originalTotal - totalItemAndCartDiscount);
      item.finalUnitPrice = item.quantity > 0 ? item.finalTotal / item.quantity : 0;
      item.totalDiscountPercent = item.originalTotal > 0 ? (totalItemAndCartDiscount / item.originalTotal) * 100 : 0;
    });
    refundResult.forEach(item => {
      item.cartDiscountShare = 0;
      item.finalTotal = item.discountedTotal;
      item.finalUnitPrice = item.discountedUnitPrice;
      item.totalDiscountPercent = 0;
    });

    // %100 indirimli (hediye/ücretsiz) satırlar: kampanya veya ürün bazlı indirim
    // satırın TÜM tutarını götürdüyse bu satır "tamamen ücretsiz" sayılır. Böyle
    // satırlar fişte kendi orijinal fiyatıyla, indirimsiz görünsün; indirim tutarı
    // ücretsiz olmayan diğer ürünlere (kalan) fiyatlarına oranla dağıtılsın.
    const FREE_ROW_EPS = 0.01;
    const freeRows = result.filter(item =>
      item.originalTotal > FREE_ROW_EPS && item.itemDiscount >= item.originalTotal - FREE_ROW_EPS
    );
    if (freeRows.length > 0) {
      // Paylaşım SADECE aynı kampanya kapsamındaki ödemeli ürünlere yapılmalı
      // (ör. "3 al 2 öde" Pandora kampanyasında ücretsiz ürünün bedeli kampanya
      // dışı/farklı markalı ürünlere yansımasın). Kapsamda başka ödemeli ürün
      // yoksa (beklenmeyen durum), eski davranışa (tüm sepete dağıt) geri düş.
      const campaignPaidRows = result.filter(item =>
        !freeRows.includes(item) && item.finalTotal > FREE_ROW_EPS && item.isCampaignItem
      );
      const paidRows = campaignPaidRows.length > 0
        ? campaignPaidRows
        : result.filter(item => !freeRows.includes(item) && item.finalTotal > FREE_ROW_EPS);
      const freeAmountToRedistribute = freeRows.reduce((sum, item) => sum + item.originalTotal, 0);
      const paidBase = paidRows.reduce((sum, item) => sum + item.finalTotal, 0);

      freeRows.forEach(item => {
        item.itemDiscount = 0;
        item.cartDiscountShare = 0;
        item.finalTotal = item.originalTotal;
        item.finalUnitPrice = item.quantity > 0 ? item.finalTotal / item.quantity : 0;
        item.totalDiscountPercent = 0;
        item.isHiddenFreeItem = true; // hediye ürün, fişte indirimsiz gösteriliyor
      });

      if (paidBase > 0) {
        paidRows.forEach(item => {
          const extraShare = (item.finalTotal / paidBase) * freeAmountToRedistribute;
          item.cartDiscountShare = (item.cartDiscountShare || 0) + extraShare;
          item.finalTotal = Math.max(0, item.finalTotal - extraShare);
          item.finalUnitPrice = item.quantity > 0 ? item.finalTotal / item.quantity : 0;
          item.totalDiscountPercent = item.originalTotal > 0
            ? ((item.itemDiscount + item.cartDiscountShare) / item.originalTotal) * 100
            : 0;
        });
      }
    }

    return [...result, ...refundResult];
  })();

  // Mağaza stok kontrolü - sepetteki ürünlerin mağaza stoğunu kontrol et
  const checkCartStoreStock = async () => {
    const storeId = user?.role === 'cashier' ? user?.store_id : (selectedStore?.id || user?.store_id);
    if (!storeId) {
      return { hasShortage: false, shortageItems: [] };
    }

    const shortageItems = [];

    for (const item of cart) {
      // İade/değişim ve Kişiye Özel ürünlerini stok kontrolünden atla
      if (item.isRefund || item.isCustomProduct) continue;

      // Her ürün için mağaza stoklarını getir
      try {
        const response = await fetch(`${API_URL}/store-stocks/${item.id}`);
        const storeStocks = await response.json();

        // store_id'leri integer olarak karşılaştır
        const myStock = storeStocks.find(s => Number(s.store_id) === Number(storeId))?.stock || 0;
        const shortage = item.quantity - myStock;

        if (shortage > 0) {
          // Başka mağazalarda stok var mı? (sadece aynı tip mağazalar)
          const goldStoreIds = [1];
          const silverStoreIds = [2, 3];
          const isGoldStore = goldStoreIds.includes(Number(storeId));
          const sameTypeStoreIds = isGoldStore ? goldStoreIds : silverStoreIds;
          const otherStores = storeStocks.filter(s => {
            const sid = Number(s.store_id);
            return sid !== Number(storeId) && s.stock > 0 && sameTypeStoreIds.includes(sid);
          });

          if (otherStores.length > 0) {
            // En fazla stoğu olan mağazayı bul
            const bestStore = otherStores.reduce((best, current) =>
              (current.stock > best.stock) ? current : best, otherStores[0]);

            shortageItems.push({
              productId: item.id,
              name: item.name,
              barcode: item.barcode,
              quantity: item.quantity,
              myStock: myStock,
              shortage: shortage,
              availableStoreId: bestStore.store_id,
              availableStoreName: bestStore.store_name,
              availableStock: bestStore.stock,
              shouldRequest: true // varsayılan olarak talep edilsin
            });
          } else {
            // Hiçbir mağazada yok
            shortageItems.push({
              productId: item.id,
              name: item.name,
              barcode: item.barcode,
              quantity: item.quantity,
              myStock: myStock,
              shortage: shortage,
              availableStoreId: null,
              availableStoreName: null,
              availableStock: 0,
              shouldRequest: false
            });
          }
        }
      } catch (error) {
        console.error(`Stok kontrolü hatası (${item.name}):`, error);
      }
    }

    return { hasShortage: shortageItems.length > 0, shortageItems };
  };

  // Transfer talebi oluştur
  const createStockRequests = async (shortageItems) => {
    const storeId = user?.role === 'cashier' ? user?.store_id : (selectedStore?.id || user?.store_id);
    const requestsToCreate = shortageItems.filter(item => item.shouldRequest && item.availableStoreId);

    if (requestsToCreate.length === 0) return true;

    const customerName = selectedCustomer?.name || 'Genel Müşteri';

    // Mağazalara göre grupla (talep edilecek mağazaya göre)
    const groupedByStore = requestsToCreate.reduce((acc, item) => {
      const key = item.availableStoreId;
      if (!acc[key]) {
        acc[key] = {
          to_store_id: key, // Ürünün olduğu mağaza (talep EDİLEN)
          to_store_name: item.availableStoreName,
          items: []
        };
      }
      acc[key].items.push({
        product_id: item.productId,
        quantity: item.shortage,
        notes: `MÜŞTERİYE SATILDI - ${customerName} - ${item.name}`
      });
      return acc;
    }, {});

    // Her mağaza için ayrı talep oluştur
    for (const storeData of Object.values(groupedByStore)) {
      try {
        const response = await fetch(`${API_URL}/transfer-requests`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            from_store_id: storeId, // Talep EDEN mağaza (benim mağazam)
            to_store_id: storeData.to_store_id, // Talep EDİLEN mağaza (ürünün olduğu)
            items: storeData.items,
            notes: `MÜŞTERİYE SATILDI - ${customerName} - Perakende satış sırasında otomatik oluşturuldu`,
            created_by: user?.id,
            staff_id: selectedStaff[0]?.id || null,
            staff_name: [...selectedStaff].sort((a, b) => a.name.localeCompare(b.name, 'tr')).map(s => s.name).join(', ') || null,
            is_sold_to_customer: true // Müşteriye satıldı işareti
          })
        });

        const data = await response.json();
        console.log('Transfer talebi yanıtı:', data);
        if (data.success) {
          // NOT: Stok düşümü burada YAPILMAZ - satış endpoint'i (/api/sales) tüm miktarı zaten düşürür.
          // Eski kodda burada store-stocks/decrement çağrılıyordu ama bu çift düşüme neden oluyordu.
          toast.success(`${storeData.to_store_name} mağazasından transfer talebi oluşturuldu`);
        } else {
          toast.error(`Talep oluşturulamadı: ${data.message || 'Bilinmeyen hata'}`);
        }
      } catch (error) {
        console.error('Transfer talebi hatası:', error);
        toast.error('Transfer talebi oluşturulamadı');
      }
    }

    return true;
  };

  // Stok talebi ile ödemeye devam et
  const handleProceedWithStockRequest = async () => {
    if (!pendingPaymentData) return;

    // Seçili ürünler için transfer talebi oluştur
    await createStockRequests(pendingStockRequests);

    // Modalı kapat
    setShowStockRequestModal(false);

    // Ödeme modalını aç veya processPayment çağır
    if (pendingPaymentData.openModalFn) {
      pendingPaymentData.openModalFn();
    } else {
      const { method, debtPayments, storeId, paymentDetails } = pendingPaymentData;
      await processPayment(method, debtPayments, storeId, paymentDetails);
    }

    // Temizle
    setPendingPaymentData(null);
    setPendingStockRequests([]);
  };

  // Talep olmadan ödemeye devam et
  const handleProceedWithoutRequest = async () => {
    if (!pendingPaymentData) return;

    // Modalı kapat
    setShowStockRequestModal(false);

    // Ödeme modalını aç veya processPayment çağır
    if (pendingPaymentData.openModalFn) {
      pendingPaymentData.openModalFn();
    } else {
      const { method, debtPayments, storeId, paymentDetails } = pendingPaymentData;
      await processPayment(method, debtPayments, storeId, paymentDetails);
    }

    // Temizle
    setPendingPaymentData(null);
    setPendingStockRequests([]);
  };

  // Ödeme işlemi
  const handlePayment = async (method, debtPayments = [], paymentDetails = []) => {
    if (cart.length === 0 && adjustmentItems.length === 0) {
      toast.warning('Sepet boş!');
      return;
    }

    // Değişim modunda ödeme yöntemi kontrolü
    if (isExchangeMode) {
      const allowedMethods = ['Nakit', 'Kredi Kartı', 'Havale/EFT', 'Açık Hesap (TL)'];
      // Müşteriye fark ödenecekse (total < 0) bakiye de kullanılabilir
      if (total < 0) {
        allowedMethods.push('Bakiye İadesi');
      }
      if (!allowedMethods.includes(method)) {
        toast.warning('Değişim işleminde sadece Nakit, Kredi Kartı, Havale/EFT veya Açık Hesap ile ödeme yapılabilir!');
        return;
      }
    }

    if (!selectedCustomer) {
      toast.warning('Lütfen önce müşteri seçin!');
      setShowCustomerSearch(true);
      return;
    }

    if (selectedStaff.length === 0) {
      toast.warning('Lütfen önce satış sorumlusu seçin!');
      setShowStaffModal(true);
      return;
    }

    // Mağaza ID belirle - öncelik sırası: selectedStore > user.store_id
    let storeId = null;
    if (user?.role === 'admin') {
      storeId = selectedStore?.id || user?.store_id;
    } else {
      storeId = user?.store_id;
    }

    // Mağaza ID kontrolü - NULL olmamalı
    if (!storeId) {
      toast.error('Mağaza bilgisi bulunamadı! Lütfen mağaza seçin veya yeniden giriş yapın.');
      return;
    }

    // Hurda alacağı kalan bakiye kontrolü
    if (appliedScrapDebtCard && currentScrapDebtTL > calculatedScrapDebtDiscount) {
      const remainingBalance = currentScrapDebtTL - calculatedScrapDebtDiscount;
      // Kalan bakiye varsa kullanıcıya sor
      setScrapBalanceInfo({
        totalCredit: currentScrapDebtTL,
        usedAmount: calculatedScrapDebtDiscount,
        remainingBalance: remainingBalance,
        cards: appliedScrapDebtCard.cards
      });
      setPendingPaymentData({
        method,
        debtPayments,
        storeId,
        paymentDetails
      });
      setShowScrapBalanceModal(true);
      return;
    }

    // Stok kontrolü openPaymentWithStockCheck'te yapılıyor, burada tekrar yapmıyoruz
    // Ödemeye devam et
    await processPayment(method, debtPayments, storeId, paymentDetails);
  };

  // Hurda alacağı kalan bakiye kararı
  const handleScrapBalanceDecision = async (decision) => {
    if (!pendingPaymentData || !scrapBalanceInfo) return;

    setShowScrapBalanceModal(false);
    const { method, debtPayments, storeId, paymentDetails } = pendingPaymentData;

    try {
      if (decision === 'nakit' || decision === 'havale') {
        // Tüm kartları kapat ve kalan tutarı öde (tek expense oluştur)
        const cardIds = scrapBalanceInfo.cards.map(c => c.id);
        await fetch(`${API_URL}/gift-cards/close-balance-batch`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            card_ids: cardIds,
            expense_amount: scrapBalanceInfo.remainingBalance,
            payment_method: decision === 'nakit' ? 'Nakit' : 'Havale/EFT',
            customer_id: selectedCustomer?.id,
            customer_name: selectedCustomer?.name,
            store_id: storeId,
            notes: `Hurda alacağı kalan bakiye ödemesi - Satış işlemi`
          })
        });
        toast.success(`Kalan ₺${scrapBalanceInfo.remainingBalance.toFixed(2)} tutarı ${decision === 'nakit' ? 'nakit' : 'havale'} olarak ödendi.`);
      }
      // 'hurda' seçeneği için kartlar normal şekilde işlenecek (sadece kullanılan miktar düşülecek)

      // Satışı tamamla
      await processPayment(method, debtPayments, storeId, paymentDetails, decision);
    } catch (error) {
      console.error('Hurda bakiye işlemi hatası:', error);
      toast.error('Hurda bakiye işlemi başarısız!');
    }

    // State'leri temizle
    setScrapBalanceInfo(null);
    setPendingPaymentData(null);
  };

  // Ödeme butonuna basılmadan önce stok kontrolü yap
  const openPaymentWithStockCheck = async (openModalFn) => {
    // Temel kontroller
    if (cart.length === 0 && adjustmentItems.length === 0) {
      toast.warning('Sepet boş!');
      return;
    }
    if (!selectedCustomer) {
      toast.warning('Lütfen önce müşteri seçin!');
      setShowCustomerSearch(true);
      return;
    }
    if (selectedStaff.length === 0) {
      toast.warning('Lütfen önce satış sorumlusu seçin!');
      setShowStaffModal(true);
      return;
    }

    // Mağaza ID belirle - öncelik sırası: selectedStore > user.store_id
    let storeId = null;
    if (user?.role === 'admin') {
      storeId = selectedStore?.id || user?.store_id;
    } else {
      storeId = user?.store_id;
    }

    // Mağaza ID kontrolü - NULL olmamalı
    if (!storeId) {
      toast.error('Mağaza bilgisi bulunamadı! Lütfen mağaza seçin veya yeniden giriş yapın.');
      return;
    }

    // Stok kontrolü yap - sadece sepette ürün varsa
    if (storeId && cart.length > 0) {
      const { hasShortage, shortageItems } = await checkCartStoreStock();

      if (hasShortage) {
        // Hiçbir mağazada stoğu olmayan ürünler var mı?
        const noStockAnywhere = shortageItems.filter(item => !item.availableStoreId);
        if (noStockAnywhere.length > 0) {
          const productNames = noStockAnywhere.map(item => item.name).join(', ');
          toast.error(`Satış yapılamaz! Şu ürünlerden hiçbir mağazada stok yok: ${productNames}`, { autoClose: 9000 });
          return;
        }

        // Başka mağazalarda stok var - transfer talebi modalı göster
        const requestableItems = shortageItems.filter(item => item.availableStoreId);
        requestableItems.forEach(item => { item.shouldRequest = true; });
        setPendingStockRequests(requestableItems);
        setPendingPaymentData({ openModalFn }); // Modal açma fonksiyonunu sakla
        setShowStockRequestModal(true);
        return;
      }
    }

    // Stok yeterli - ödeme modalını aç
    openModalFn();
  };

  // İade/değişim sonucu müşteri bakiyesine alacak ekle
  const handleRefundAsGiftCard = async () => {
    if (total >= 0) {
      toast.warning('Bakiye sadece müşteriye para iadesi gerektiğinde oluşturulabilir!');
      return;
    }

    if (!selectedCustomer?.id) {
      toast.warning('Bakiye eklemek için müşteri seçilmelidir!');
      return;
    }

    try {
      const refundAmount = Math.abs(total);

      // Müşteri bakiyesine alacak olarak ekle (negatif = alacak)
      const response = await fetch(`${API_URL}/customers/${selectedCustomer.id}/debts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          debt_type: 'TL',
          amount: -refundAmount,
          amount_tl: -refundAmount,
          description: `İade/Değişim bakiye iadesi - ${selectedCustomer.name}`
        })
      });

      const data = await response.json();

      if (data.success) {
        const customerInfo = ` (${selectedCustomer.name})`;

        logService.log(
          'sales',
          'balance_credit',
          `İade sonucu bakiye eklendi${customerInfo}: ₺${refundAmount.toFixed(2)}`,
          {
            details: {
              amount: refundAmount,
              reason: 'refund',
              customer: selectedCustomer.name
            },
            entity_type: 'customer',
            entity_id: selectedCustomer.id,
            status: 'success'
          }
        );

        toast.success(`₺${refundAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} bakiye müşteriye eklendi${customerInfo}`);

        // Normal iade işlemini tamamla
        await handlePayment('Bakiye İadesi');
      } else {
        toast.error('Bakiye oluşturulamadı!');
      }
    } catch (error) {
      console.error('Bakiye oluşturma hatası:', error);
      toast.error('Bakiye oluşturulurken hata oluştu!');
    }
  };

  // İade/değişim sonucu hurda borcu oluştur (müşterinin hurda alacağı)
  // debtType: 'TL' | 'GRAM' (altın) | 'GUMUS' (gümüş)
  const handleRefundAsScrapDebt = async (debtType = 'TL') => {
    if (total >= 0) {
      toast.warning('Hurda borcu sadece müşteriye para iadesi gerektiğinde oluşturulabilir!');
      return;
    }

    if (!selectedCustomer || selectedCustomer?.isAnonymous) {
      toast.warning('Hurda borcu için kayıtlı müşteri seçilmelidir!');
      return;
    }

    try {
      const refundAmountTL = Math.abs(total);
      let amount = refundAmountTL;
      let amountGram = null;
      let rateAtCreation = null;
      let amountUnit = 'TL';
      let displayText = `₺${refundAmountTL.toFixed(2)}`;

      // Gram bazlı borç oluşturulacaksa TL'yi grama çevir
      if (debtType === 'GRAM') {
        // Altın satış kuru ile hesapla
        const goldRate = getSatisRate('GOLD');
        if (goldRate <= 0) {
          toast.error('Altın kuru alınamadı!');
          return;
        }
        amountGram = refundAmountTL / goldRate;
        amount = amountGram; // remaining_amount gram cinsinden
        rateAtCreation = goldRate;
        amountUnit = 'GRAM';
        displayText = `${amountGram.toFixed(4)} gr Altın (₺${refundAmountTL.toFixed(2)})`;
      } else if (debtType === 'GUMUS') {
        // Gümüş satış kuru ile hesapla
        const silverRate = getSatisRate('SILVER');
        if (silverRate <= 0) {
          toast.error('Gümüş kuru alınamadı!');
          return;
        }
        amountGram = refundAmountTL / silverRate;
        amount = amountGram; // remaining_amount gram cinsinden
        rateAtCreation = silverRate;
        amountUnit = 'GUMUS';
        displayText = `${amountGram.toFixed(4)} gr Gümüş (₺${refundAmountTL.toFixed(2)})`;
      }

      // Hurda ile satış sonucu oluşan müşteri alacağı doğrudan bakiye sistemine (customer_balance_transactions
      // — bakiyenin TEK kaynağı) yazılır. Eskiden burada ayrı bir gift_cards kaydı/kodu oluşturuluyordu; bu,
      // müşterinin ana bakiye ekranında/geçmişinde hiç görünmüyordu (sadece ayrı "Alacak Raporu" üzerinden
      // görülebiliyordu) ve personel kodu unutup krediyi kaybedebiliyordu. Negatif amount/amount_tl = alacak.
      const amountTlForBalance = debtType === 'TL' ? refundAmountTL : amountGram * rateAtCreation;
      const response = await fetch(`${API_URL}/customers/${selectedCustomer.id}/debts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          debt_type: debtType,
          amount: -amount,
          amount_tl: -amountTlForBalance,
          gold_rate: debtType === 'GRAM' ? rateAtCreation : null,
          silver_rate: debtType === 'GUMUS' ? rateAtCreation : null,
          description: `Hurda ile satış - Kalan tutar müşteri alacağı olarak kaydedildi (${debtType === 'TL' ? 'Nakit' : debtType === 'GRAM' ? 'Altın Gram' : 'Gümüş Gram'})`
        })
      });

      const data = await response.json();

      if (data.success) {
        // Log kaydı
        logService.log(
          'campaigns',
          'scrap_debt_created',
          `Hurda alacağı bakiyeye eklendi: ${displayText} - ${selectedCustomer.name}`,
          {
            details: {
              amount: amount,
              amount_tl: refundAmountTL,
              debt_type: debtType,
              reason: 'scrap_sale_balance',
              customer: selectedCustomer.name,
              customer_id: selectedCustomer.id
            },
            entity_type: 'customer',
            entity_id: selectedCustomer.id,
            status: 'success'
          }
        );

        toast.success(`Müşteri bakiyesine eklendi: ${displayText}`);

        // Normal satış işlemini tamamla
        await handlePayment('Hurda Borca Al');
      } else {
        toast.error('Bakiye oluşturulamadı!');
      }
    } catch (error) {
      console.error('Hurda alacağı oluşturma hatası:', error);
      toast.error('Hurda alacağı oluşturulurken hata oluştu!');
    }
  };

  // Gerçek ödeme işlemi (store_id ile)
  const processPayment = async (method, debtPayments = [], storeId = null, paymentDetails = [], scrapBalanceDecision = null) => {
    try {
      // Müşteri bakiyesi varsa ödeme detaylarına ekle
      if (customerGiftCardDiscount > 0 && appliedCustomerGiftCard && !isExchangeMode) {
        const balancePayment = {
          method: 'Müşteri Bakiyesi',
          currency: 'TRY',
          amount: customerGiftCardDiscount,
          amountTL: customerGiftCardDiscount,
          amount_tl: customerGiftCardDiscount,
          rate: 1
        };

        // Bakiye tüm tutarı karşılamıyorsa, kalan kısmı orijinal ödeme yöntemi olarak ekle
        const remainingAfterBalance = Math.max(0, total - customerGiftCardDiscount);
        if (remainingAfterBalance > 0.01 && paymentDetails.length === 0) {
          // Orijinal ödeme yöntemini de ekle (Nakit, Kredi Kartı vs.)
          paymentDetails = [{
            method: method,
            currency: 'TRY',
            amount: remainingAfterBalance,
            amountTL: remainingAfterBalance,
            amount_tl: remainingAfterBalance,
            rate: 1
          }, balancePayment];
          method = 'Karışık';
        } else {
          paymentDetails = [...paymentDetails, balancePayment];
          if (paymentDetails.length > 1 || debtPayments.length > 0) {
            method = 'Karışık';
          } else {
            method = total <= customerGiftCardDiscount ? 'Müşteri Bakiyesi' : 'Karışık';
          }
        }
      }

      // Açık Hesap varsa ödeme detaylarına ekle
      // (paymentDetails'da zaten Açık Hesap varsa tekrar ekleme - çoklu ödeme modalından gelmiş olabilir)
      if (debtPayments.length > 0) {
        const hasAcikHesapInDetails = paymentDetails.some(p => (p.method || '').includes('Açık Hesap'));
        if (!hasAcikHesapInDetails) {
          const debtDetail = debtPayments.map(d => ({
            method: d.creditType === 'GRAM' ? 'Açık Hesap (Gram)' : d.creditType === 'GUMUS' ? 'Açık Hesap (Gümüş)' : 'Açık Hesap (TL)',
            currency: d.creditType === 'GRAM' ? 'GOLD' : d.creditType === 'GUMUS' ? 'SILVER' : 'TRY',
            amount: d.creditType === 'TL' ? d.amountTL : d.amount,
            amountTL: d.amountTL,
            amount_tl: d.amountTL,
            rate: d.goldRate || d.silverRate || 1
          }));
          paymentDetails = [...paymentDetails, ...debtDetail];
        }
        // Açık hesap dışında başka ödeme yöntemi varsa 'Karışık', yoksa açık hesap türünü kullan
        const hasOtherPayment = paymentDetails.some(p => !(p.method || '').includes('Açık Hesap'));
        if (hasOtherPayment) {
          method = 'Karışık';
        } else {
          const acikHesapMethods = [...new Set(paymentDetails.map(p => p.method))];
          method = acikHesapMethods.length === 1 ? acikHesapMethods[0] : 'Karışık';
        }
      }

      // Sepette iade ürünleri var mı kontrol et
      const refundItems = cartWithDiscounts.filter(item => item.isRefund);
      const newItemsWithDiscounts = cartWithDiscounts.filter(item => !item.isRefund);
      const newItems = cart.filter(item => !item.isRefund); // Eski referanslar için
      const hasExchange = refundItems.length > 0;

      // Yeni ürünler veya fark varsa satış kaydı oluştur
      // DEĞİŞİM DURUMUNDA SATIŞ KAYDI OLUŞTURMA - sadece /api/exchanges kullanılacak
      let newSaleId = null;
      let storeSaleNumber = null;
      let receiptNumber = null;
      if ((newItemsWithDiscounts.length > 0 || adjustmentItems.length > 0) && !hasExchange) {
        // DEBUG: Sepet ve gönderilecek ürünleri logla
        console.log('=== FRONTEND SATIŞ DEBUG ===');
        console.log('Cart (sepet):', cart.map(i => ({ id: i.id, name: i.name, quantity: i.quantity })));
        console.log('cartWithDiscounts:', cartWithDiscounts.map(i => ({ id: i.id, name: i.name, quantity: i.quantity })));
        console.log('newItemsWithDiscounts (gönderilecek):', newItemsWithDiscounts.map(i => ({ id: i.id, name: i.name, quantity: i.quantity })));
        console.log('============================');

        // GÜVENLİK KONTROLÜ: Sepet ile gönderilecek ürünler eşleşmeli
        const cartItemCount = cart.filter(i => !i.isRefund).length;
        const sendItemCount = newItemsWithDiscounts.length;
        if (cartItemCount !== sendItemCount) {
          console.warn('⚠️ UYARI: Sepet ürün sayısı ile gönderilecek ürün sayısı farklı!', { cartItemCount, sendItemCount });
        }

        const saleResponse = await fetch(`${API_URL}/sales`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            customer_id: selectedCustomer?.id,
            staff_id: selectedStaff[0]?.id || null,
            staff_name: [...selectedStaff].sort((a, b) => a.name.localeCompare(b.name, 'tr')).map(s => s.name).join(', '),
            staff_ids: selectedStaff.map(s => s.id),
            items: newItemsWithDiscounts.map(item => {
              // Dövizli ürünlerde fiyatı TL'ye çevir
              const priceTRY = item.currency && item.currency !== 'TRY'
                ? convertToTRY(item.price, item.currency)
                : item.price;
              // finalUnitPrice = tüm indirimler dahil (kampanya + sepet indirimi payı)
              const discountedPriceTRY = item.finalUnitPrice || item.discountedUnitPrice;
              const totalDiscountAmount = (item.itemDiscount || 0) + (item.cartDiscountShare || 0);
              return {
              ...item,
              id: item.productId || item.id, // Özel tanımlı ürünlerde gerçek ürün ID'si
              original_price: priceTRY,
              price: priceTRY,
              discount_amount: totalDiscountAmount,
              discounted_price: discountedPriceTRY,
              discount_percent: item.totalDiscountPercent || item.discountPercent || 0,
              custom_weight: item.customWeight || null, // Özel tanımlı ürün gramajı
              is_special_defined: item.isSpecialDefined || false,
              // Hesaplama detayları
              weight: item.customWeight || item.weight || null,
              gold_karat: item.goldKarat || null,
              labor_cost: item.laborCost || null,
              rate_used: item.isGoldProduct ? getSatisRate('GOLD') : (item.isSilverProduct ? getSatisRate('SILVER') : null),
              metal_type: item.isGoldProduct ? 'GOLD' : (item.isSilverProduct ? 'SILVER' : null),
              note: item.note || null
            }}),
            subtotal: Math.max(0, subtotal),
            tax: 0,
            discount: totalDiscount,
            scrap_discount: effectiveScrapTotal,
            scrap_info: appliedScrap ? {
              metal_type: appliedScrap.metal_type,
              purity: appliedScrap.purity,
              pure_weight: appliedScrap.pure_weight,
              total: appliedScrap.total
            } : null,
            campaign_discount: campaignDiscount,
            campaign_id: appliedCampaign?.id || null,
            campaign_name: appliedCampaign?.name || null,
            scrap_debt_discount: calculatedScrapDebtDiscount > 0 ? calculatedScrapDebtDiscount : null,
            scrap_debt_codes: appliedScrapDebtCard?.codes || null,
            customer_gift_card_discount: customerGiftCardDiscount > 0 ? customerGiftCardDiscount : null,
            customer_gift_card_codes: appliedCustomerGiftCard?.codes || null,
            adjustment_items: adjustmentItems.length > 0 ? (() => {
              const posAdjTotal = adjustmentItems.reduce((s, a) => s + (parseFloat(a.amount) > 0 ? parseFloat(a.amount) : 0), 0);
              const cartItemsTotal = cartWithDiscounts.filter(i => !i.isRefund).reduce((s, i) => s + i.discountedTotal, 0);
              const pool = cartItemsTotal + posAdjTotal;
              return adjustmentItems.map(adj => {
                const amt = parseFloat(adj.amount);
                const share = (manualDiscount > 0 && pool > 0 && amt > 0)
                  ? (amt / pool) * manualDiscount : 0;
                return { ...adj, discountShare: Math.round(share * 100) / 100, netAmount: Math.round((amt - share) * 100) / 100 };
              });
            })() : null,
            adjustment_total: adjustmentTotal > 0 ? adjustmentTotal : null,
            total: Math.max(0, total),
            customer_refund_amount: total < 0 ? Math.abs(total) : undefined,
            payment_method: method,
            payment_details: paymentDetails.length > 0 ? paymentDetails.map(p => ({
              method: p.method,
              currency: p.currency,
              amount: p.amount,
              amount_tl: p.amountTL,
              rate: p.rate,
              bank_account: p.bankAccount || null
            })) : null,
            store_id: storeId, // Stok düşülecek mağaza
            gold_rate: isGoldStore ? (getSatisRate('GOLD') || null) : null, // Altın mağazasında altın kuru
            silver_rate: !isGoldStore ? (getSatisRate('SILVER') || null) : null, // Gümüş mağazasında gümüş kuru
            notes: receiptNote || ''
          })
        });

        const saleData = await saleResponse.json();
        if (saleData.success) {
          newSaleId = saleData.sale_id;
          storeSaleNumber = saleData.store_sale_number;
          receiptNumber = saleData.receipt_number;

          // Açık Hesap ödemeleri varsa ve müşteri seçiliyse, borcu kaydet
          if (debtPayments.length > 0 && selectedCustomer?.id) {
            for (const debt of debtPayments) {
              // Borç tipini belirle: GRAM (altın), GUMUS (gümüş) veya TL
              let debtType = 'TL';
              if (debt.creditType === 'GRAM') debtType = 'GRAM';
              else if (debt.creditType === 'GUMUS') debtType = 'GUMUS';

              const debtData = {
                sale_id: newSaleId,
                debt_type: debtType,
                amount: (debtType === 'GRAM' || debtType === 'GUMUS') ? debt.amount : debt.amountTL,
                amount_tl: debt.amountTL,
                gold_rate: debt.goldRate || null,
                silver_rate: debt.silverRate || null,
                description: `Satış #${newSaleId} - ${cart.map(item => item.name).join(', ')}`
              };

              await fetch(`${API_URL}/customers/${selectedCustomer.id}/debts`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(debtData)
              });
            }
          }

          // Otomatik E-Arsiv/E-Fatura olustur
          try {
            const efaturaResponse = await fetch(`${API_URL}/efatura/auto-invoice`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                sale_id: newSaleId,
                customer_info: selectedCustomer ? {
                  vkn: selectedCustomer.tax_number || '11111111111',
                  name: selectedCustomer.name || 'Nihai Tuketici',
                  address: selectedCustomer.address || '',
                  phone: selectedCustomer.phone || '',
                  email: selectedCustomer.email || ''
                } : null
              })
            });
            const efaturaData = await efaturaResponse.json();
            if (efaturaData.success && efaturaData.invoice_number) {
              console.log('E-Arsiv fatura olusturuldu:', efaturaData.invoice_number);
            }
          } catch (efaturaError) {
            console.error('E-Fatura olusturma hatasi:', efaturaError);
            // Fatura hatasi satis islemini engellemez
          }
        } else {
          // Satış başarısız
          toast.error(saleData.message || saleData.error || 'Satış oluşturulamadı!');
          return;
        }
      }

      // Değişim/iade kaydı oluştur
      if (hasExchange) {
        // İade ürünlerini originalSaleId'ye göre grupla
        const refundItemsBySaleId = {};
        refundItems.forEach(item => {
          const saleId = item.originalSaleId;
          if (!refundItemsBySaleId[saleId]) {
            refundItemsBySaleId[saleId] = [];
          }
          refundItemsBySaleId[saleId].push(item);
        });
        const originalSaleIds = Object.keys(refundItemsBySaleId);

        // NOT: Stok güncellemesi /api/exchanges endpoint'inde yapılıyor
        // Ayrıca /api/sales/:id/refund çağırmaya gerek yok

        // İade edilen ürünlerin detayları (tümü)
        const returnItemsData = refundItems.map(item => {
          // "İADE: Ürün Adı" formatından ürün adını çıkar
          const productName = item.name.replace('İADE: ', '');
          // refund_ prefix'ini kaldırarak product_id'yi al
          const productId = typeof item.id === 'string' ? parseInt(item.id.split('_')[1]) : item.id;
          return {
            product_id: productId,
            sale_item_id: item.sale_item_id || null,
            product_name: productName,
            quantity: item.quantity,
            unit_price: Math.abs(item.price),
            total_price: Math.abs(item.price * item.quantity),
            barcode: item.barcode || item.originalBarcode || null,
            image: item.image || null // Ürün fotoğrafı
          };
        });

        // Yeni verilen ürünlerin detayları (indirimli fiyatlarla)
        const newItemsData = newItemsWithDiscounts.map(item => ({
          product_id: item.productId || item.id,
          product_name: item.name,
          quantity: item.quantity,
          unit_price: item.discountedUnitPrice || convertToTRY(item.price, item.currency),
          original_unit_price: convertToTRY(item.price, item.currency),
          total_price: item.discountedTotal || (convertToTRY(item.price, item.currency) * item.quantity),
          original_total: item.originalTotal || (convertToTRY(item.price, item.currency) * item.quantity),
          item_discount: item.itemDiscount || 0,
          discount_percent: item.discountPercent || 0,
          barcode: item.barcode || null,
          image: item.image || null
        }));

        // Değişim mi sadece iade mi?
        const isExchange = newItemsWithDiscounts.length > 0;

        // Farkın alınma/verilme türüne göre iade fişi ödeme yöntemini belirle
        // Not: debtPayments varsa method 'Karışık' olarak değişmiş olabilir, orijinal yöntemi tespit et
        let effectiveMethod = method;
        if (method === 'Karışık' && debtPayments.length > 0 && paymentDetails.length <= debtPayments.length) {
          effectiveMethod = 'Açık Hesap (TL)';
        }
        const refundMethodMap = {
          'Nakit': 'Nakit İade',
          'Kredi Kartı': 'Kredi Kartı İade',
          'Havale/EFT': 'Havale İade',
          'Açık Hesap (TL)': 'Açık Hesap',
        };
        const exchangeRefundMethod = refundMethodMap[effectiveMethod] || effectiveMethod;

        // DEĞİŞİM İŞLEMİ: Her orijinal satış için ayrı exchange kaydı oluştur
        // 1. ADIM: Her originalSaleId için ayrı iade fişi oluştur
        let refundExchangeId = null; // İlk exchange ID'si fiş gösterimi için kullanılır
        const allExchangeIds = [];

        for (const saleId of originalSaleIds) {
          const groupItems = refundItemsBySaleId[saleId];
          const groupReturnItemsData = groupItems.map(item => {
            const productName = item.name.replace('İADE: ', '');
            const productId = typeof item.id === 'string' ? parseInt(item.id.split('_')[1]) : item.id;
            return {
              product_id: productId,
              sale_item_id: item.sale_item_id || null,
              product_name: productName,
              quantity: item.quantity,
              unit_price: Math.abs(item.price),
              total_price: Math.abs(item.price * item.quantity),
              barcode: item.barcode || item.originalBarcode || null,
              image: item.image || null
            };
          });
          const groupReturnTotal = groupItems.reduce((sum, item) => sum + Math.abs(item.price * item.quantity), 0);

          // Bu orijinal satışa ait iade fark kalemleri (adjustmentItems'dan isRefund olanlar)
          const groupRefundAdjs = adjustmentItems.filter(a => a.isRefund && String(a.originalSaleId) === String(saleId));
          const groupAdjTotal = groupRefundAdjs.reduce((s, a) => s + Math.abs(parseFloat(a.amount)), 0);
          const totalReturnWithAdj = groupReturnTotal + groupAdjTotal;

          // Orijinal fişin müşteri bilgisini iade ürünlerinden al
          const groupCustomerId = groupItems[0]?.originalCustomerId || selectedCustomer?.id;
          const groupCustomerName = groupItems[0]?.originalCustomerName || selectedCustomer?.name;

          const refundResponse = await fetch(`${API_URL}/exchanges`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              original_sale_id: saleId,
              new_sale_id: null,
              customer_id: groupCustomerId,
              customer_name: groupCustomerName,
              staff_id: selectedStaff[0]?.id || null,
              staff_name: [...selectedStaff].sort((a, b) => a.name.localeCompare(b.name, 'tr')).map(s => s.name).join(', '),
              type: 'refund', // Her zaman iade fişi
              return_items: groupReturnItemsData,
              new_items: [], // İade fişinde yeni ürün yok
              return_total: totalReturnWithAdj,
              new_items_total: 0,
              difference: -totalReturnWithAdj, // İade tutarı negatif
              payment_method: exchangeRefundMethod,
              store_id: storeId,
              notes: `İade işlemi - Orijinal Fiş #${saleId}`,
              adjustment_items: groupRefundAdjs.length > 0 ? groupRefundAdjs.map(a => ({
                description: a.description,
                reason: a.reason,
                amount: Math.abs(parseFloat(a.amount)),
                originalSaleId: a.originalSaleId
              })) : null,
              adjustment_total: groupAdjTotal > 0 ? groupAdjTotal : null
            })
          });

          const refundData = await refundResponse.json();
          if (!refundData.success) {
            toast.error(refundData.error || refundData.message || `İade fişi oluşturulamadı! (Fiş #${saleId})`);
            return;
          }

          allExchangeIds.push(refundData.exchange_id);
          if (!refundExchangeId) {
            refundExchangeId = refundData.exchange_id;
          }
        }

        toast.success(`İade fişi oluşturuldu! İade Tutarı: ₺${refundItemsTotal.toFixed(2)}`);

        const originalSaleIdsStr = originalSaleIds.join(', #');

        // 2. ADIM: Eğer yeni ürün varsa, ayrı satış fişi oluştur
        let newSaleStoreSaleNumber = null;
        let newSaleIdForExchange = null;
        let newSaleReceiptNumber = null;
        if (isExchange && newItemsWithDiscounts.length > 0) {
          const saleResponse = await fetch(`${API_URL}/sales`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              customer_id: selectedCustomer?.id,
              staff_id: selectedStaff[0]?.id || null,
              staff_name: [...selectedStaff].sort((a, b) => a.name.localeCompare(b.name, 'tr')).map(s => s.name).join(', '),
              staff_ids: selectedStaff.map(s => s.id),
              items: newItemsWithDiscounts.map(item => {
                // Dövizli ürünlerde fiyatı TL'ye çevir
                const priceTRY = item.currency && item.currency !== 'TRY'
                  ? convertToTRY(item.price, item.currency)
                  : item.price;
                // finalUnitPrice = tüm indirimler dahil (kampanya + sepet indirimi payı)
                const discountedPriceTRY = item.finalUnitPrice || item.discountedUnitPrice;
                const totalDiscountAmount = (item.itemDiscount || 0) + (item.cartDiscountShare || 0);
                return {
                ...item,
                id: item.productId || item.id,
                original_price: priceTRY,
                price: priceTRY,
                discount_amount: totalDiscountAmount,
                discounted_price: discountedPriceTRY,
                discount_percent: item.totalDiscountPercent || item.discountPercent || 0,
                custom_weight: item.customWeight || null,
                is_special_defined: item.isSpecialDefined || false,
                weight: item.customWeight || item.weight || null,
                gold_karat: item.goldKarat || null,
                labor_cost: item.laborCost || null,
                rate_used: item.isGoldProduct ? getSatisRate('GOLD') : (item.isSilverProduct ? getSatisRate('SILVER') : null),
                metal_type: item.isGoldProduct ? 'GOLD' : (item.isSilverProduct ? 'SILVER' : null),
                note: item.note || null
              }}),
              subtotal: newItemsTotal,
              tax: 0,
              discount: totalDiscount,
              adjustment_items: (() => {
                // Değişimde iade farkları ayrı iade fişinde zaten var, yeni satış fişine dahil etme
                const saleAdjItems = adjustmentItems.filter(a => !a.isRefund);
                if (saleAdjItems.length === 0) return null;
                const posAdjTotal = saleAdjItems.reduce((s, a) => s + (parseFloat(a.amount) > 0 ? parseFloat(a.amount) : 0), 0);
                const cartItemsTotal = cartWithDiscounts.filter(i => !i.isRefund).reduce((s, i) => s + i.discountedTotal, 0);
                const pool = cartItemsTotal + posAdjTotal;
                return saleAdjItems.map(adj => {
                  const amt = parseFloat(adj.amount);
                  const share = (manualDiscount > 0 && pool > 0 && amt > 0)
                    ? (amt / pool) * manualDiscount : 0;
                  return { ...adj, discountShare: Math.round(share * 100) / 100, netAmount: Math.round((amt - share) * 100) / 100 };
                });
              })(),
              adjustment_total: (() => {
                const saleAdjTotal = adjustmentItems.filter(a => !a.isRefund).reduce((s, a) => s + parseFloat(a.amount), 0);
                return saleAdjTotal > 0 ? saleAdjTotal : null;
              })(),
              total: (() => {
                const saleAdjTotal = adjustmentItems.filter(a => !a.isRefund).reduce((s, a) => s + parseFloat(a.amount), 0);
                return Math.max(0, newItemsTotal - totalDiscount + saleAdjTotal);
              })(),
              payment_method: method,
              payment_details: paymentDetails.length > 0 ? paymentDetails.map(p => ({
                method: p.method,
                currency: p.currency,
                amount: p.amount,
                amount_tl: p.amountTL,
                rate: p.rate,
                bank_account: p.bankAccount || null
              })) : null,
              store_id: storeId,
              gold_rate: isGoldStore ? (getSatisRate('GOLD') || null) : null,
              silver_rate: !isGoldStore ? (getSatisRate('SILVER') || null) : null,
              notes: `Değişim satışı - İade Fiş ID: ${allExchangeIds.join(', ')} - Orijinal Fiş #${originalSaleIdsStr}`
            })
          });

          const saleData = await saleResponse.json();
          if (saleData.success) {
            newSaleIdForExchange = saleData.sale_id;
            newSaleStoreSaleNumber = saleData.store_sale_number;
            newSaleReceiptNumber = saleData.receipt_number;
            toast.success(`Satış fişi oluşturuldu! Fiş No: ${newSaleStoreSaleNumber} - Tutar: ₺${Math.max(0, newItemsTotal - totalDiscount).toFixed(2)}`);

            // E-Arşiv fatura oluştur
            try {
              await fetch(`${API_URL}/efatura/auto-invoice`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  sale_id: newSaleIdForExchange,
                  customer_info: selectedCustomer ? {
                    vkn: selectedCustomer.tax_number || '11111111111',
                    name: selectedCustomer.name || 'Nihai Tuketici',
                    address: selectedCustomer.address || '',
                    phone: selectedCustomer.phone || '',
                    email: selectedCustomer.email || ''
                  } : null
                })
              });
            } catch (efaturaError) {
              console.error('E-Fatura olusturma hatasi:', efaturaError);
            }
          } else {
            toast.error(saleData.message || saleData.error || 'Satış fişi oluşturulamadı!');
          }
        }

        // Fark hesapla (yeni satış toplam - iade) - indirim ve ayar kalemleri dahil
        const newSaleActualTotal = Math.max(0, newItemsTotal - totalDiscount + adjustmentTotal);
        const finalDifference = newSaleActualTotal - refundItemsTotal;
        const actionText = isExchange ? 'Değişim' : 'İade';
        if (isExchange) {
          if (finalDifference >= 0) {
            toast.info(`${actionText} tamamlandı! Müşteriden alınacak fark: ₺${finalDifference.toFixed(2)}`);
          } else {
            toast.info(`${actionText} tamamlandı! Müşteriye verilecek fark: ₺${Math.abs(finalDifference).toFixed(2)}`);
          }

          // Açık Hesap seçildiyse farkı müşteri hesabına yaz
          // Not: debtPayments varsa method 'Karışık' olarak değişiyor, bu yüzden hem method hem debtPayments kontrol edilir
          const isAcikHesapExchange = debtPayments.length > 0 || method.includes('Açık Hesap') || paymentDetails.some(p => (p.method || '').includes('Açık Hesap'));
          if (isAcikHesapExchange && selectedCustomer?.id && finalDifference !== 0) {
            const debtDescription = finalDifference > 0
              ? `Değişim farkı (borç) - İade Fiş: ${allExchangeIds.join(', ')}${newSaleStoreSaleNumber ? ` - Satış Fiş: ${newSaleStoreSaleNumber}` : ''}`
              : `Değişim farkı (alacak) - İade Fiş: ${allExchangeIds.join(', ')}`;
            try {
              await fetch(`${API_URL}/customers/${selectedCustomer.id}/debts`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  sale_id: newSaleIdForExchange || null,
                  debt_type: 'TL',
                  amount: finalDifference,
                  amount_tl: finalDifference,
                  description: debtDescription
                })
              });
              if (finalDifference > 0) {
                toast.info(`₺${finalDifference.toFixed(2)} müşteri açık hesabına borç olarak eklendi.`);
              } else {
                toast.info(`₺${Math.abs(finalDifference).toFixed(2)} müşteri açık hesabına alacak olarak eklendi.`);
              }
            } catch (debtError) {
              console.error('Açık hesap borç kaydetme hatası:', debtError);
              toast.error('Fark açık hesaba kaydedilemedi!');
            }
          }
        }

        // Değişim/İade fişini göster
        const newItemsSubtotal = newItemsData.reduce((sum, item) => sum + item.original_total, 0);
        const newItemsTotalItemDiscount = newItemsData.reduce((sum, item) => sum + item.item_discount, 0);
        setExchangeReceiptData({
          type: isExchange ? 'exchange' : 'refund',
          date: new Date().toLocaleString('tr-TR'),
          originalSaleId: originalSaleIds.length === 1 ? originalSaleIds[0] : originalSaleIds,
          refundExchangeId: refundExchangeId, // İlk exchange ID'si
          allExchangeIds: allExchangeIds, // Tüm exchange ID'leri
          newSaleId: newSaleStoreSaleNumber, // Yeni satış fiş numarası
          newSaleDbId: newSaleIdForExchange, // Yeni satış DB ID'si
          customerName: selectedCustomer?.name || 'Genel Müşteri',
          staffName: [...selectedStaff].sort((a, b) => a.name.localeCompare(b.name, 'tr')).map(s => s.name).join(', '),
          returnItems: returnItemsData,
          returnTotal: refundItemsTotal,
          newItems: newItemsData,
          newItemsSubtotal: newItemsSubtotal,
          newItemsTotal: newSaleActualTotal,
          totalDiscount: totalDiscount + newItemsTotalItemDiscount,
          campaignDiscount: campaignDiscount,
          manualDiscount: manualDiscount - campaignDiscount - giftCardDiscount - calculatedScrapDebtDiscount,
          scrapDiscount: effectiveScrapTotal,
          difference: finalDifference,
          paymentMethod: effectiveMethod
        });
        setShowExchangeReceiptModal(true);

        // Değişim satışı için otomatik fiş yazdırma verisi hazırla (satış ile aynı şekilde)
        if (isExchange && newSaleIdForExchange) {
          var savedReceiptData = {
            saleId: newSaleIdForExchange,
            receiptNumber: newSaleReceiptNumber || newSaleStoreSaleNumber || newSaleIdForExchange,
            items: newItemsWithDiscounts.map(item => ({
              name: item.name,
              barcode: item.barcode || null,
              quantity: item.quantity,
              price: convertToTRY(item.price, item.currency),
              discountedUnitPrice: item.finalUnitPrice || item.discountedUnitPrice || convertToTRY(item.price, item.currency),
              itemDiscount: item.itemDiscount || 0,
              totalDiscountPercent: item.totalDiscountPercent || 0,
              note: item.note || null
            })),
            customer: selectedCustomer ? { id: selectedCustomer.id, name: selectedCustomer.name, phone: selectedCustomer.phone } : null,
            staffNames: [...selectedStaff].sort((a, b) => a.name.localeCompare(b.name, 'tr')).map(s => s.name).join(', '),
            // Ara toplam liste fiyatından, indirim = ürün bazlı + sepet indirimi (Fişler'den yeniden yazdırmayla aynı)
            subtotal: newItemsOriginalTotal,
            totalDiscount: totalDiscount + totalItemDiscounts,
            campaignDiscount: campaignDiscount,
            giftCardDiscount: giftCardDiscount,
            scrapDiscount: effectiveScrapTotal,
            scrapDebtDiscount: calculatedScrapDebtDiscount,
            customerGiftCardDiscount: customerGiftCardDiscount,
            total: (() => {
              const saleAdjTotal = adjustmentItems.filter(a => !a.isRefund).reduce((s, a) => s + parseFloat(a.amount), 0);
              return Math.max(0, newItemsTotal - totalDiscount + saleAdjTotal);
            })(),
            paymentMethod: method,
            paymentDetails: paymentDetails.length > 0 ? paymentDetails.map(p => ({
              method: p.method,
              currency: p.currency,
              amount: p.amount,
              amountTL: p.amountTL,
              rate: p.rate,
              bankAccount: p.bankAccount || null,
              bank_account: p.bank_account || p.bankAccount || null
            })) : null,
            adjustmentItems: (() => {
              const saleAdjItems = adjustmentItems.filter(a => !a.isRefund);
              if (saleAdjItems.length === 0) return null;
              const posAdjTotal = saleAdjItems.reduce((s, a) => s + (parseFloat(a.amount) > 0 ? parseFloat(a.amount) : 0), 0);
              const cItemsTotal = cartWithDiscounts.filter(i => !i.isRefund).reduce((s, i) => s + i.discountedTotal, 0);
              const pool = cItemsTotal + posAdjTotal;
              return saleAdjItems.map(adj => {
                const amt = parseFloat(adj.amount || adj.refund_amount || adj.original_amount) || 0;
                const share = (manualDiscount > 0 && pool > 0 && amt > 0) ? (amt / pool) * manualDiscount : 0;
                return {
                  description: adj.description || adj.reason || 'Fark',
                  reason: adj.reason || '',
                  amount: amt,
                  discountShare: Math.round(share * 100) / 100
                };
              });
            })(),
            adjustmentTotal: (() => {
              const saleAdjTotal = adjustmentItems.filter(a => !a.isRefund).reduce((s, a) => s + parseFloat(a.amount), 0);
              return saleAdjTotal > 0 ? saleAdjTotal : 0;
            })(),
            date: new Date(),
            storeId: currentStoreId,
            receiptNote: receiptNote || ''
          };
        }

        // Değişim/İade logu - Detaylı
        const returnItemsDetailed = refundItems.map(item => ({
          product_id: typeof item.id === 'string' ? parseInt(item.id.split('_')[1]) : item.id,
          barcode: item.barcode || item.originalBarcode || null,
          name: item.name.replace('İADE: ', ''),
          quantity: item.quantity,
          unit_price: Math.abs(item.price),
          total_price: Math.abs(item.price * item.quantity)
        }));

        const newItemsDetailed = newItems.map(item => ({
          product_id: item.productId || item.id,
          barcode: item.barcode || null,
          name: item.name,
          quantity: item.quantity,
          unit_price: convertToTRY(item.price, item.currency),
          total_price: convertToTRY(item.price, item.currency) * item.quantity,
          weight: item.customWeight || item.weight || null,
          gold_karat: item.goldKarat || null
        }));

        if (isExchange) {
          logService.log(
            LOG_MODULES.SALES,
            LOG_ACTIONS.EXCHANGE,
            `Değişim - Orijinal Satış #${originalSaleIdsStr} - İade Fişler: ${allExchangeIds.join(', ')} - Satış Fiş: ${newSaleStoreSaleNumber} - Fark: ₺${finalDifference.toFixed(2)}`,
            {
              entity_type: 'sale',
              entity_id: originalSaleIds[0],
              status: 'info',
              details: {
                original_sale_ids: originalSaleIds,
                exchange_ids: allExchangeIds,
                refund_exchange_id: refundExchangeId,
                new_sale_id: newSaleIdForExchange,
                new_sale_store_number: newSaleStoreSaleNumber,
                return_total: refundItemsTotal,
                new_items_total: newItemsTotal,
                difference: finalDifference,
                payment_method: method,
                customer: selectedCustomer ? {
                  id: selectedCustomer.id,
                  name: selectedCustomer.name,
                  phone: selectedCustomer.phone
                } : null,
                staff: selectedStaff.map(s => ({ id: s.id, name: s.name })),
                return_items: returnItemsDetailed,
                new_items: newItemsDetailed
              }
            }
          );
        } else {
          logService.log(
            LOG_MODULES.SALES,
            LOG_ACTIONS.REFUND,
            `İade - Orijinal Satış #${originalSaleIdsStr} - İade Fişler: ${allExchangeIds.join(', ')} - Toplam: ₺${refundItemsTotal.toFixed(2)}`,
            {
              entity_type: 'sale',
              entity_id: originalSaleIds[0],
              status: 'warning',
              details: {
                original_sale_ids: originalSaleIds,
                exchange_ids: allExchangeIds,
                refund_exchange_id: refundExchangeId,
                refund_total: refundItemsTotal,
                payment_method: method,
                customer: selectedCustomer ? {
                  id: selectedCustomer.id,
                  name: selectedCustomer.name,
                  phone: selectedCustomer.phone
                } : null,
                staff: selectedStaff.map(s => ({ id: s.id, name: s.name })),
                return_items: returnItemsDetailed
              }
            }
          );
        }
      } else {
        // Normal satış
        let message = `Satış tamamlandı! Fiş No: ${storeSaleNumber || newSaleId} - Toplam: ₺${total.toFixed(2)}`;
        if (appliedCampaign) {
          message += ` (Kampanya: ${appliedCampaign.name})`;
        }
        toast.success(message);

        // Satış logu - Detaylı
        logService.log(
          LOG_MODULES.SALES,
          LOG_ACTIONS.SALE_COMPLETE,
          `Satış #${storeSaleNumber || newSaleId} - ${newItemsWithDiscounts.length} ürün - ₺${total.toFixed(2)}`,
          {
            entity_type: 'sale',
            entity_id: newSaleId,
            status: 'success',
            details: {
              sale_id: newSaleId,
              store_sale_number: storeSaleNumber,
              total: total,
              subtotal: subtotal,
              discount: totalDiscount,
              campaign_discount: campaignDiscount,
              scrap_discount: effectiveScrapTotal,
              gift_card_discount: giftCardDiscount,
              scrap_debt_discount: calculatedScrapDebtDiscount,
              customer_gift_card_discount: customerGiftCardDiscount,
              payment_method: method,
              payment_details: paymentDetails.length > 0 ? paymentDetails.map(p => ({
                method: p.method,
                currency: p.currency,
                amount: p.amount,
                amount_tl: p.amountTL,
                rate: p.rate,
                bank_account: p.bankAccount || null
              })) : [{ method: method, amount_tl: total }],
              customer: selectedCustomer ? {
                id: selectedCustomer.id,
                name: selectedCustomer.name,
                phone: selectedCustomer.phone
              } : null,
              staff: selectedStaff.map(s => ({ id: s.id, name: s.name })),
              campaign: appliedCampaign ? { id: appliedCampaign.id, name: appliedCampaign.name } : null,
              items: newItemsWithDiscounts.map(item => ({
                product_id: item.productId || item.id,
                barcode: item.barcode || null,
                name: item.name,
                quantity: item.quantity,
                unit_price: convertToTRY(item.price, item.currency),
                discounted_unit_price: item.discountedUnitPrice || convertToTRY(item.price, item.currency),
                item_discount: item.itemDiscount || 0,
                total_price: (item.discountedUnitPrice || convertToTRY(item.price, item.currency)) * item.quantity,
                weight: item.customWeight || item.weight || null,
                gold_karat: item.goldKarat || null,
                is_gold: item.isGoldProduct || false,
                is_silver: item.isSilverProduct || false
              }))
            }
          }
        );

        // Fiş yazdırma için satış verilerini kaydet (state temizlenmeden önce)
        var savedReceiptData = {
          saleId: newSaleId,
          receiptNumber: receiptNumber || storeSaleNumber || newSaleId,
          items: newItemsWithDiscounts.map(item => ({
            name: item.name,
            barcode: item.barcode || null,
            quantity: item.quantity,
            price: convertToTRY(item.price, item.currency),
            discountedUnitPrice: item.finalUnitPrice || item.discountedUnitPrice || convertToTRY(item.price, item.currency),
            itemDiscount: item.itemDiscount || 0,
            totalDiscountPercent: item.totalDiscountPercent || 0,
            note: item.note || null
          })),
          customer: selectedCustomer ? { id: selectedCustomer.id, name: selectedCustomer.name, phone: selectedCustomer.phone } : null,
          staffNames: [...selectedStaff].sort((a, b) => a.name.localeCompare(b.name, 'tr')).map(s => s.name).join(', '),
          // Ara toplam liste fiyatından, indirim = ürün bazlı + sepet indirimi (Fişler'den yeniden yazdırmayla aynı)
          subtotal: subtotal + totalItemDiscounts,
          totalDiscount: totalDiscount + totalItemDiscounts,
          campaignDiscount: campaignDiscount,
          giftCardDiscount: giftCardDiscount,
          scrapDiscount: effectiveScrapTotal,
          scrapDebtDiscount: calculatedScrapDebtDiscount,
          customerGiftCardDiscount: customerGiftCardDiscount,
          total: total,
          paymentMethod: method,
          paymentDetails: paymentDetails.length > 0 ? paymentDetails.map(p => ({
            method: p.method,
            currency: p.currency,
            amount: p.amount,
            amountTL: p.amountTL,
            rate: p.rate,
            bankAccount: p.bankAccount || null,
            bank_account: p.bank_account || p.bankAccount || null
          })) : null,
          adjustmentItems: adjustmentItems.length > 0 ? (() => {
            const posAdjTotal = adjustmentItems.reduce((s, a) => s + (parseFloat(a.amount) > 0 ? parseFloat(a.amount) : 0), 0);
            const cItemsTotal = cartWithDiscounts.filter(i => !i.isRefund).reduce((s, i) => s + i.discountedTotal, 0);
            const pool = cItemsTotal + posAdjTotal;
            return adjustmentItems.map(adj => {
              const amt = parseFloat(adj.amount || adj.refund_amount || adj.original_amount) || 0;
              const share = (manualDiscount > 0 && pool > 0 && amt > 0) ? (amt / pool) * manualDiscount : 0;
              return {
                description: adj.description || adj.reason || 'Fark',
                reason: adj.reason || '',
                amount: amt,
                discountShare: Math.round(share * 100) / 100
              };
            });
          })() : null,
          adjustmentTotal: adjustmentTotal > 0 ? adjustmentTotal : 0,
          date: new Date(),
          storeId: currentStoreId,
          receiptNote: receiptNote || ''
        };
      }

      // Kampanya kullanımını kaydet
      if (newSaleId && appliedCampaign) {
        await recordCampaignUsage(newSaleId);
      }

      // Bakiye kullanımını kaydet (ödeme tamamlandıktan sonra)
      if (appliedGiftCard && giftCardDiscount > 0) {
        try {
          await fetch(`${API_URL}/gift-cards/use`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              code: appliedGiftCard.code,
              amount: giftCardDiscount,
              customer_id: selectedCustomer?.id,
              customer_name: selectedCustomer?.name,
              sale_id: newSaleId
            })
          });
        } catch (error) {
          console.error('Bakiye kullanım kaydı hatası:', error);
        }
      }

      // Hurda alacağı (scrap debt) kullanımını kaydet
      // Nakit veya Havale seçildiyse kartlar zaten kapatıldı, bu adımı atla
      if (appliedScrapDebtCard && calculatedScrapDebtDiscount > 0 && scrapBalanceDecision !== 'nakit' && scrapBalanceDecision !== 'havale') {
        try {
          // Her bir hurda alacak kartını kullanıldı olarak işaretle
          let remainingTLToUse = calculatedScrapDebtDiscount;
          for (const card of appliedScrapDebtCard.cards) {
            if (remainingTLToUse <= 0) break;

            const debtType = card.debt_type || 'TL';
            let useFromThisCard;
            let cardTLValue;

            if (debtType === 'GRAM') {
              // Altın gram bazlı kart
              const currentGoldRate = getSatisRate('GOLD');
              const remainingGrams = Number(card.remaining_amount); // remaining_amount gram cinsinden
              cardTLValue = remainingGrams * currentGoldRate;
              const tlToUse = Math.min(cardTLValue, remainingTLToUse);
              // TL'den grama çevir
              useFromThisCard = tlToUse / currentGoldRate;
              remainingTLToUse -= tlToUse;
              console.log(`Hurda alacak kartı kullanıldı: ${card.code}, ${useFromThisCard.toFixed(4)} gr Altın (₺${tlToUse.toFixed(2)})`);
            } else if (debtType === 'GUMUS') {
              // Gümüş gram bazlı kart
              const currentSilverRate = getSatisRate('SILVER');
              const remainingGrams = Number(card.remaining_amount); // remaining_amount gram cinsinden
              cardTLValue = remainingGrams * currentSilverRate;
              const tlToUse = Math.min(cardTLValue, remainingTLToUse);
              // TL'den grama çevir
              useFromThisCard = tlToUse / currentSilverRate;
              remainingTLToUse -= tlToUse;
              console.log(`Hurda alacak kartı kullanıldı: ${card.code}, ${useFromThisCard.toFixed(4)} gr Gümüş (₺${tlToUse.toFixed(2)})`);
            } else {
              // TL bazlı kart
              const cardAmount = Number(card.remaining_amount);
              useFromThisCard = Math.min(cardAmount, remainingTLToUse);
              remainingTLToUse -= useFromThisCard;
              console.log(`Hurda alacak kartı kullanıldı: ${card.code}, Tutar: ₺${useFromThisCard.toFixed(2)}`);
            }

            await fetch(`${API_URL}/gift-cards/use`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                code: card.code,
                amount: useFromThisCard, // Gram bazlı: gram miktarı, TL bazlı: TL miktarı
                customer_id: selectedCustomer?.id,
                customer_name: selectedCustomer?.name,
                sale_id: newSaleId
              })
            });
          }
        } catch (error) {
          console.error('Hurda alacak kartı kullanım kaydı hatası:', error);
        }
      }

      // Müşteri bakiye kullanımı - customer_balance_transactions üzerinden
      if (appliedCustomerGiftCard && customerGiftCardDiscount > 0 && selectedCustomer?.id) {
        try {
          await fetch(`${API_URL}/customers/${selectedCustomer.id}/debts`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              debt_type: 'TL',
              amount: customerGiftCardDiscount,
              amount_tl: customerGiftCardDiscount,
              sale_id: newSaleId,
              description: `Bakiye kullanımı - Satış #${newSaleId}`
            })
          });
          console.log(`Müşteri bakiyesi kullanıldı: ₺${customerGiftCardDiscount.toFixed(2)}`);
        } catch (error) {
          console.error('Müşteri bakiye kullanım kaydı hatası:', error);
        }
      }

      // Hurda alımını kaydet (satış tamamlandıktan sonra) - her hurda sepet öğesi için ayrı kayıt
      if (scrapCart && scrapCart.length > 0) {
        try {
          // Manuel düzenleme yapıldıysa her kalemin tutarını orantılı güncelle
          const originalScrapTotal = scrapCart.reduce((sum, item) => sum + (item.total || 0), 0);
          const scrapRatio = (originalScrapTotal > 0 && effectiveScrapTotal !== originalScrapTotal)
            ? effectiveScrapTotal / originalScrapTotal
            : 1;

          for (const scrapItem of scrapCart) {
            const metalTuru = scrapItem.metal_type === 'gold' ? 'Altın' : 'Gümüş';
            // Fire (waste) hesaplama - gram cinsinden
            const wasteGram = scrapItem.waste_gram || 0;
            const grossWeight = scrapItem.gross_weight || 0;
            const wastePercent = grossWeight > 0 ? (wasteGram / grossWeight) * 100 : 0;
            // Manuel düzenleme varsa orantılı tutar, yoksa orijinal
            const adjustedTotal = scrapItem.total * scrapRatio;

            const scrapResponse = await fetch(`${API_URL}/scrap-purchases`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                customer_id: selectedCustomer?.id || null,
                customer_name: selectedCustomer?.name || 'Satış Müşterisi',
                customer_phone: selectedCustomer?.phone || '',
                staff_id: selectedStaff[0]?.id || null,
                staff_name: [...selectedStaff].sort((a, b) => a.name.localeCompare(b.name, 'tr')).map(s => s.name).join(', ') || null,
                metal_type: scrapItem.metal_type,
                purity: scrapItem.purity,
                gross_weight: parseFloat(scrapItem.gross_weight) * (scrapItem.quantity || 1),
                waste_percent: wastePercent,
                waste_gram: wasteGram * (scrapItem.quantity || 1),
                pure_weight: parseFloat(scrapItem.pure_weight) * (scrapItem.quantity || 1),
                unit_price: parseFloat(scrapItem.price_per_gram),
                total_amount: adjustedTotal,
                sale_id: newSaleId,
                store_id: storeId,
                payment_method: method,
                notes: `Satış #${newSaleId} ile birlikte alındı - ${scrapItem.name || `${metalTuru} ${scrapItem.purity} Ayar`}${scrapItem.quantity > 1 ? ` (${scrapItem.quantity} adet)` : ''}`
              })
            });
            const scrapData = await scrapResponse.json();
            if (scrapData.success) {
              // Log kaydı - Hurda alımı (Detaylı)
              logService.log(
                LOG_MODULES.SCRAP,
                LOG_ACTIONS.CREATE,
                `Hurda Alımı - ${metalTuru} ${scrapItem.purity} Ayar - ${parseFloat(scrapItem.pure_weight) * (scrapItem.quantity || 1)} gr - ₺${adjustedTotal.toFixed(2)}`,
                {
                  entity_type: 'scrap',
                  entity_id: scrapData.id,
                  status: 'success',
                  details: {
                    scrap_id: scrapData.id,
                    sale_id: newSaleId,
                    metal_type: scrapItem.metal_type,
                    metal_type_label: metalTuru,
                    purity: scrapItem.purity,
                    gross_weight: parseFloat(scrapItem.gross_weight) * (scrapItem.quantity || 1),
                    pure_weight: parseFloat(scrapItem.pure_weight) * (scrapItem.quantity || 1),
                    waste_percent: wastePercent,
                    price_per_gram: parseFloat(scrapItem.price_per_gram),
                    total_amount: scrapItem.total,
                    quantity: scrapItem.quantity || 1,
                    customer: selectedCustomer ? {
                      id: selectedCustomer.id,
                      name: selectedCustomer.name,
                      phone: selectedCustomer.phone
                    } : null,
                    staff: selectedStaff.map(s => ({ id: s.id, name: s.name })),
                    payment_method: method,
                    notes: `Satış #${newSaleId} ile birlikte alındı`
                  }
                }
              );
            }
          }
        } catch (error) {
          console.error('Hurda alımı kayıt hatası:', error);
        }
      }

      // Satış sonrası stok uyarıları: sunucu kontrolü (depleted + critical)
      if (storeId && newItems.length > 0) {
        try {
          const productIds = newItems
            .filter(it => !it.isGift && !it.isRefund && it.id)
            .map(it => it.id);
          if (productIds.length > 0) {
            await fetch(`${API_URL}/stock-alerts/check`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ store_id: storeId, product_ids: productIds })
            });
          }
        } catch (error) {
          console.error('Stok uyarı kontrolü hatası:', error);
        }
      }

      // Satış sonrası formu tamamen sıfırla (hard refresh etkisi)
      resetAllSaleState();
      // Ekstra güvenlik: 100ms sonra sepeti tekrar kontrol et
      setTimeout(() => {
        setCart(prevCart => {
          if (prevCart.length > 0) {
            console.warn('⚠️ Sepet satış sonrası temizlenmemişti, şimdi temizleniyor:', prevCart);
            return [];
          }
          return prevCart;
        });
      }, 100);

      // Satış tamamlandıktan sonra fiş yazdırma seçeneği modalı göster
      if (savedReceiptData) {
        setReceiptChoiceData(savedReceiptData);
        setShowReceiptChoiceModal(true);
      }

      // Son satışlar listesini güncelle
      fetchRecentSales(storeId);
      if (showAllSalesModal) fetchAllSales(false);
    } catch (error) {
      console.error('Satış hatası:', error);
      toast.error('Satış işlemi başarısız!');
    }
  };

  // Admin mağaza seçimi tamamlandığında
  const handleStoreSelect = async (storeId) => {
    setShowStoreSelectModal(false);
    setSelectedStoreId(storeId);
    await processPayment(pendingPaymentMethod, pendingDebtPayments, storeId);
    setPendingPaymentMethod(null);
    setPendingDebtPayments([]);
  };

  // Bekletilen satışları yükle (sadece aktif mağazaya ait olanlar)
  const fetchHeldSales = async () => {
    try {
      const storeId = user?.role === 'admin' ? (selectedStore?.id || user?.store_id) : user?.store_id;
      const url = storeId ? `${API_URL}/held-sales?store_id=${storeId}` : `${API_URL}/held-sales`;
      const response = await fetch(url);
      const data = await response.json();
      // Array olduğundan emin ol
      if (Array.isArray(data)) {
        setHeldSales(data);
      } else {
        setHeldSales([]);
      }
    } catch (error) {
      console.error('Bekletilen satışlar yüklenemedi:', error);
      setHeldSales([]);
    }
  };

  // Terminal sepetlerini yükle (kasiyerin mağazasına gönderilmiş)
  const fetchTerminalCarts = async () => {
    // Admin için selectedStore, kasiyer için user.store_id kullan
    const storeId = user?.role === 'admin' ? (selectedStore?.id || user?.store_id) : user?.store_id;
    if (!storeId) return;
    try {
      const response = await fetch(`${API_URL}/terminal-carts/store/${storeId}`);
      const data = await response.json();
      if (Array.isArray(data)) {
        const pendingCarts = data.filter(cart => cart.status === 'pending');
        const prevCount = prevTerminalCartsCountRef.current;
        const newCount = pendingCarts.length;

        // Yeni terminal sepeti geldiyse bildirim sesi çal ve toast göster
        // prevCount === -1 ise ilk yükleme, bildirim çalma
        if (newCount > prevCount && prevCount !== -1) {
          const newCartsCount = newCount - prevCount;
          playCartSound('notification');
          toast.info(`📱 ${newCartsCount} yeni sepet geldi!`, {
            position: 'top-center',
            autoClose: 7500,
            style: { background: '#0ea5e9', color: 'white', fontWeight: 'bold' }
          });
        }

        prevTerminalCartsCountRef.current = newCount;
        setTerminalCarts(pendingCarts);
      } else {
        setTerminalCarts([]);
      }
    } catch (error) {
      console.error('Terminal sepetleri yüklenemedi:', error);
      setTerminalCarts([]);
    }
  };

  // Sayfa yüklendiğinde bekletilen satışları ve son satışları getir
  useEffect(() => {
    const currentStore = user?.role === 'admin' ? (selectedStore?.id || user?.store_id) : user?.store_id;
    fetchHeldSales();
    fetchTerminalCarts();
    fetchRecentSales(currentStore);
  }, [user?.store_id, selectedStore?.id]);

  // Terminal sepetlerini otomatik güncelle (her 5 saniyede bir) - modal açıkken durdur
  useEffect(() => {
    const storeId = user?.role === 'admin' ? (selectedStore?.id || user?.store_id) : user?.store_id;
    if (!storeId || showAllSalesModal) return; // Modal açıkken polling yapma

    const interval = setInterval(() => {
      fetchTerminalCarts();
    }, 5000);

    return () => clearInterval(interval);
  }, [user?.store_id, selectedStore?.id, showAllSalesModal]);

  // Satışı beklet
  const holdSale = async () => {
    if (cart.length === 0) return;
    if (isCartLockedByApproval) {
      toast.warning('Onaylı indirim varken satış bekletilemez! Önce ödemeyi tamamlayın veya indirimi kaldırın.');
      return;
    }

    try {
      const totalAmount = cart.reduce((sum, item) => sum + (convertToTRY(item.price, item.currency) * item.quantity), 0);

      const currentStoreId = user?.role === 'admin' ? (selectedStore?.id || user?.store_id) : user?.store_id;
      const response = await fetch(`${API_URL}/held-sales`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer: selectedCustomer,
          staff: selectedStaff,
          cart: cart,
          discount: discountAmount,
          discountType: discountType,
          appliedDiscounts: appliedDiscounts,
          store_id: currentStoreId || null
        })
      });

      const data = await response.json();
      if (data.success) {
        fetchHeldSales();
        resetAllSaleState();
        toast.success('Satış bekletildi');
      }
    } catch (error) {
      console.error('Satış bekletilemedi:', error);
      toast.error('Satış bekletilemedi!');
    }
  };

  // Bekletilen satışı geri al
  const recallSale = async (heldSale) => {
    try {
      // Önce veritabanından sil
      await fetch(`${API_URL}/held-sales/${heldSale.id}`, {
        method: 'DELETE'
      });

      // Sonra sepete ekle
      setCart(heldSale.cart);
      setSelectedCustomer(heldSale.customer);
      setDiscountAmount(heldSale.discount || 0);
      setDiscountType(heldSale.discountType || 'amount');
      // Eski format uyumu: tek indirim varsa appliedDiscounts'a çevir
      if (heldSale.appliedDiscounts && heldSale.appliedDiscounts.length > 0) {
        setAppliedDiscounts(heldSale.appliedDiscounts);
      } else if (heldSale.discount > 0) {
        const oldType = heldSale.discountType || 'amount';
        const oldVal = heldSale.discount;
        const label = oldType === 'percent' ? `%${oldVal}` : oldType === 'round' ? `Yuvarla → ₺${Number(oldVal).toLocaleString('tr-TR')}` : `₺${Number(oldVal).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}`;
        setAppliedDiscounts([{ id: Date.now(), type: oldType, value: oldVal, label }]);
      } else {
        setAppliedDiscounts([]);
      }
      // Satış sorumlusu bilgisini geri yükle
      if (heldSale.staff && heldSale.staff.length > 0) {
        setSelectedStaff(Array.isArray(heldSale.staff) ? heldSale.staff : [heldSale.staff]);
      }
      setShowHoldModal(false);

      // Listeyi yenile
      fetchHeldSales();
    } catch (error) {
      console.error('Satış geri alınamadı:', error);
    }
  };

  // Bekletilen satışı sil
  const deleteHeldSale = (e, heldSale) => {
    e.stopPropagation();
    showConfirmation({
      title: 'Bekleyen Sepeti Sil',
      message: 'Bu bekleyen sepeti silmek istediğinize emin misiniz?',
      confirmText: 'Evet, Sil',
      cancelText: 'İptal',
      type: 'danger',
      onConfirm: async () => {
        try {
          await fetch(`${API_URL}/held-sales/${heldSale.id}`, {
            method: 'DELETE'
          });

          toast.success('Bekleyen sepet silindi');
          fetchHeldSales();
        } catch (error) {
          console.error('Sepet silinemedi:', error);
          toast.error('Sepet silinemedi');
        }
      }
    });
  };

  // Terminal sepetini sil
  const deleteTerminalCart = (e, terminalCart) => {
    e.stopPropagation();
    showConfirmation({
      title: 'Terminal Sepetini Sil',
      message: 'Bu terminal sepetini silmek istediğinize emin misiniz?',
      confirmText: 'Evet, Sil',
      cancelText: 'İptal',
      type: 'danger',
      onConfirm: async () => {
        try {
          await fetch(`${API_URL}/terminal-carts/${terminalCart.id}`, {
            method: 'DELETE'
          });
          toast.success('Terminal sepeti silindi');
          fetchTerminalCarts();
        } catch (error) {
          console.error('Terminal sepeti silinemedi:', error);
          toast.error('Terminal sepeti silinemedi');
        }
      }
    });
  };

  // Terminal sepetini kasaya yükle
  const recallTerminalCart = async (terminalCart) => {
    try {
      // Durumu 'processing' olarak güncelle
      await fetch(`${API_URL}/terminal-carts/${terminalCart.id}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'processing' })
      });

      // Sepete ekle - items JSON olarak geldiği için parse et
      const parsedItems = typeof terminalCart.items === 'string'
        ? JSON.parse(terminalCart.items)
        : terminalCart.items;

      // Yeni format: { products: [...], sales_staff_id, sales_staff_name, discount_amount, campaign_id, campaign_name }
      // Eski format: [...]
      const items = parsedItems.products || parsedItems;
      const salesStaffId = parsedItems.sales_staff_id;
      const salesStaffName = parsedItems.sales_staff_name;
      const terminalDiscount = parsedItems.discount_amount || 0;
      const terminalCampaignId = parsedItems.campaign_id;
      const terminalCampaignName = parsedItems.campaign_name;

      // Kasiyerin normal sepet formatına dönüştür (addToCart ile aynı format)
      // Terminal sadece bir kısım alan gönderiyor; barkod/stok/kategori vb. için
      // her ürünü backend'den çekip eksik alanları tamamlıyoruz.
      const cartItems = await Promise.all(items.map(async (item) => {
        let product = null;
        try {
          const productRes = await fetch(`${API_URL}/products/${item.id}`);
          if (productRes.ok) product = await productRes.json();
        } catch (e) {
          // Ürün çekilemezse terminal verisiyle yetin
        }

        const metalType = product?.metal_type;
        const isGumushAksesuar = metalType === 'aksesuar' && product?.category === 'Gümüş Aksesuar';
        const isGoldProduct = item.isGoldProduct ?? (metalType === 'gold');
        const isSilverProduct = item.isSilverProduct ?? (metalType === 'silver' || isGumushAksesuar);

        const { myStock } = await getStoreStock(item.id);

        return {
          id: item.id,
          productId: item.id,
          name: item.name,
          barcode: item.barcode || product?.barcode,
          category: product?.category,
          metal_type: metalType,
          brand: product?.brand,
          price: Number(item.sell_price) || 0,
          currency: 'TRY',
          quantity: item.quantity || 1,
          stock: Number(product?.stock) || 0,
          storeStock: myStock,
          isGift: false,
          isGoldProduct,
          isSilverProduct,
          goldKarat: item.goldKarat ?? product?.gold_karat,
          silverPurity: product?.silver_purity,
          profitMargin: product?.profit_margin,
          weight: item.weight ?? product?.weight,
          customWeight: null,
          laborCost: item.laborCost ?? product?.labor_cost,
          image: item.image || product?.image,
          isSpecialDefined: false,
          no_discount: product ? (product.no_discount === 1 || product.no_discount === true) : false,
          note: ''
        };
      }));

      setCart(cartItems);

      // Satış sorumlusu bilgisi varsa otomatik seç (tek kişi olarak array'e ekle)
      if (salesStaffId && salesStaffName) {
        setSelectedStaff([{ id: salesStaffId, name: salesStaffName }]);
      } else {
        // Terminal sorumlu seçmediyse kasada önceki satıştan kalan seçim taşınmasın
        setSelectedStaff([]);
      }

      // İndirim bilgisi varsa uygula
      if (terminalDiscount > 0) {
        setDiscountAmount(terminalDiscount);
        setDiscountType('amount');
        setAppliedDiscounts([{ id: Date.now(), type: 'amount', value: terminalDiscount, label: `₺${Number(terminalDiscount).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}` }]);

        // Kampanya bilgisi varsa göster
        if (terminalCampaignId && terminalCampaignName) {
          // Kampanyayı aktif kampanyalardan bul ve uygula
          const campaign = activeCampaigns.find(c => c.id === terminalCampaignId);
          if (campaign) {
            setAppliedCampaign(campaign);
            setCampaignDiscount(terminalDiscount);
            setDiscountAmount(0); // Manuel indirim sıfırla, kampanya indirimi kullan
            setAppliedDiscounts([]); // Kampanya varsa manuel indirimleri sıfırla
          }
          toast.success(`Terminal sepeti yüklendi - Kampanya: ${terminalCampaignName} (₺${terminalDiscount.toFixed(2)} indirim)`);
        } else {
          toast.success(`Terminal sepeti yüklendi - İndirim: ₺${terminalDiscount.toFixed(2)}`);
        }
      } else if (salesStaffId && salesStaffName) {
        toast.success(`Terminal sepeti yüklendi - Satış: ${salesStaffName}`);
      } else {
        toast.success(`Terminal sepeti yüklendi (${terminalCart.terminal_user_name})`);
      }

      setShowHoldModal(false);

      // Listeyi yenile
      fetchTerminalCarts();
    } catch (error) {
      console.error('Terminal sepeti yüklenemedi:', error);
      toast.error('Terminal sepeti yüklenemedi!');
    }
  };

  // Son satışları getir (mağazaya göre filtrelenmiş, sayfalanmış)
  const fetchRecentSales = async (storeIdParam = null, append = false, customOffset = null) => {
    try {
      const storeId = storeIdParam || (user?.role === 'admin' ? (selectedStore?.id || user?.store_id) : user?.store_id);
      const currentOffset = customOffset !== null ? customOffset : (append ? recentSalesOffset : 0);

      // Birleşik endpoint kullan
      let url = `${API_URL}/all-receipts?store_id=${storeId}&limit=${RECENT_SALES_PAGE_SIZE}&offset=${currentOffset}`;
      if (recentSalesFilters.receiptType) {
        url += `&type_filter=${recentSalesFilters.receiptType}`;
      }
      if (recentSalesFilters.startDate) {
        url += `&start_date=${recentSalesFilters.startDate}`;
      }
      if (recentSalesFilters.endDate) {
        url += `&end_date=${recentSalesFilters.endDate}`;
      }
      if (recentSalesFilters.paymentMethod) {
        url += `&payment_method=${recentSalesFilters.paymentMethod}`;
      }
      if (recentSalesFilters.minAmount) {
        url += `&min_amount=${recentSalesFilters.minAmount}`;
      }
      if (recentSalesFilters.maxAmount) {
        url += `&max_amount=${recentSalesFilters.maxAmount}`;
      }
      if (recentSalesFilters.exactAmount) {
        url += `&exact_amount=${recentSalesFilters.exactAmount}`;
      }

      const response = await fetch(url);
      const data = await response.json();
      const receipts = data.receipts || [];

      // Backend'in SQL LIMIT sayısı kadar satır döndüğünü söylediğinde devam et
      // (tahsilat grupları client-side yeniden filtrelendiği için receipts.length güvenilir değil)
      const backendHasMore = typeof data.hasMore === 'boolean'
        ? data.hasMore
        : receipts.length >= RECENT_SALES_PAGE_SIZE;
      setHasMoreRecentSales(backendHasMore);

      if (append) {
        setRecentSales(prev => {
          const existingKeys = new Set(prev.map(s => `${s.record_type}_${s.id}`));
          const newReceipts = receipts.filter(r => !existingKeys.has(`${r.record_type}_${r.id}`));
          return [...prev, ...newReceipts].sort((a, b) =>
            new Date(b.created_at) - new Date(a.created_at)
          );
        });
        // Offset backend'in SQL OFFSET'i ile hizalı kalmalı: istenen sayfa boyutu kadar ilerle
        setRecentSalesOffset(currentOffset + RECENT_SALES_PAGE_SIZE);
      } else {
        setRecentSales(receipts);
        // İlk yüklemede bir sonraki çağrı için offset = sayfa boyutu
        setRecentSalesOffset(RECENT_SALES_PAGE_SIZE);
      }
    } catch (error) {
      console.error('Satış yükleme hatası:', error);
      if (!append) setRecentSales([]);
    }
  };

  // Daha fazla satış yükle (infinite scroll)
  const loadMoreRecentSales = async () => {
    if (loadingMoreSales || !hasMoreRecentSales) return;
    setLoadingMoreSales(true);
    try {
      const storeId = user?.role === 'admin' ? (selectedStore?.id || user?.store_id) : user?.store_id;
      await fetchRecentSales(storeId, true);
    } finally {
      setLoadingMoreSales(false);
    }
  };

  // Fişler infinite scroll - IntersectionObserver
  useEffect(() => {
    const sentinel = recentSalesSentinelRef.current;
    if (!sentinel || !showLastSaleModal) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMoreRecentSales && !loadingMoreSales && !recentSalesSearch.trim()) {
          loadMoreRecentSales();
        }
      },
      { root: recentSalesListRef.current, threshold: 0.1 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [showLastSaleModal, hasMoreRecentSales, loadingMoreSales, recentSalesSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  // Arama değiştiğinde sunucu tarafında arama yap (debounced)
  // /all-receipts kullanılır → satış + tahsilat + iade + değişim + hurda hepsi gelir
  useEffect(() => {
    const searchTerm = recentSalesSearch.trim();
    if (searchTerm.length < 3) return; // 3 karakterden kısa ise arama yapma

    const timer = setTimeout(async () => {
      try {
        const storeId = user?.role === 'admin' ? (selectedStore?.id || user?.store_id) : user?.store_id;
        const response = await fetch(`${API_URL}/all-receipts?store_id=${storeId}&search=${encodeURIComponent(searchTerm)}&exclude_staff=1&limit=50&offset=0`);
        const data = await response.json();
        const serverReceipts = Array.isArray(data?.receipts) ? data.receipts : [];

        // Sunucudan gelen sonuçları mevcut listeye ekle (tekrar olmayanları)
        setRecentSales(prev => {
          const existingKeys = new Set(prev.map(s => `${s.record_type}_${s.id}`));
          const newReceipts = serverReceipts.filter(r => !existingKeys.has(`${r.record_type}_${r.id}`));
          if (newReceipts.length === 0) return prev;
          return [...prev, ...newReceipts].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        });
      } catch (err) {
        console.error('Arama hatası:', err);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [recentSalesSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  // Select/tarih filtreleri değiştiğinde hemen yeniden yükle
  useEffect(() => {
    if (showLastSaleModal) {
      const storeId = user?.role === 'admin' ? (selectedStore?.id || user?.store_id) : user?.store_id;
      fetchRecentSales(storeId);
    }
  }, [recentSalesFilters.receiptType, recentSalesFilters.startDate, recentSalesFilters.endDate, recentSalesFilters.paymentMethod]); // eslint-disable-line react-hooks/exhaustive-deps

  // Tutar filtreleri değiştiğinde debounce ile yeniden yükle
  useEffect(() => {
    if (!showLastSaleModal) return;
    const timer = setTimeout(() => {
      const storeId = user?.role === 'admin' ? (selectedStore?.id || user?.store_id) : user?.store_id;
      fetchRecentSales(storeId);
    }, 500);
    return () => clearTimeout(timer);
  }, [recentSalesFilters.minAmount, recentSalesFilters.maxAmount, recentSalesFilters.exactAmount]); // eslint-disable-line react-hooks/exhaustive-deps

  // Tüm fişleri sayfalayarak getir (infinite scroll)
  // append=true ise mevcut listeye ekle, false ise sıfırdan yükle
  const fetchAllSales = async (append = false) => {
    if (append && (loadingMoreAllSales || !hasMoreAllSales)) return;
    if (!append && allSalesLoading) return;

    if (append) {
      setLoadingMoreAllSales(true);
    } else {
      setAllSalesLoading(true);
    }

    try {
      const currentOffset = append ? allSalesOffset : 0;
      // Admin olmayan kullanıcılar sadece kendi mağazasını görebilir
      const isAdmin = user?.role === 'admin';
      const effectiveFilter = isAdmin ? allSalesFilter : String(user?.store_id || '');
      const storeParam = effectiveFilter && effectiveFilter !== 'all' ? `&store_id=${effectiveFilter}` : '';
      const searchParam = allSalesSearch.trim() ? `&search=${encodeURIComponent(allSalesSearch.trim())}` : '';
      const dateParam = allSalesDateFilter && allSalesDateFilter !== 'all' ? `&date_filter=${allSalesDateFilter}` : '';
      const typeParam = allSalesTypeFilter && allSalesTypeFilter !== 'all' ? `&type_filter=${allSalesTypeFilter}` : '';
      const response = await fetch(`${API_URL}/all-receipts?limit=${ALL_SALES_PAGE_SIZE}&offset=${currentOffset}${storeParam}${searchParam}${dateParam}${typeParam}`);
      const data = await response.json();

      if (data.error) {
        throw new Error(data.error);
      }

      const { receipts, counts, hasMore } = data;

      setAllSalesTotalCounts(counts);
      setHasMoreAllSales(hasMore);

      if (append) {
        setAllSales(prev => [...prev, ...receipts]);
        setAllSalesOffset(currentOffset + receipts.length);
      } else {
        setAllSales(receipts);
        setAllSalesOffset(receipts.length);
      }
    } catch (error) {
      console.error('Tüm satışlar yükleme hatası:', error);
      if (!append) {
        setAllSales([]);
      }
    } finally {
      if (append) {
        setLoadingMoreAllSales(false);
      } else {
        setAllSalesLoading(false);
      }
    }
  };

  // Daha fazla fiş yükle (infinite scroll)
  const loadMoreAllSales = () => {
    if (loadingMoreAllSales || !hasMoreAllSales) return;
    fetchAllSales(true);
  };

  // Mağaza filtresi veya tarih filtresi değiştiğinde tüm fişleri yeniden yükle
  useEffect(() => {
    if (showAllSalesModal) {
      setAllSales([]);
      setAllSalesOffset(0);
      setHasMoreAllSales(true);
      fetchAllSales(false);
    }
  }, [allSalesFilter, allSalesDateFilter, allSalesTypeFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // Arama değiştiğinde debounce ile yeniden yükle (ilk açılışta değil — liste zaten yükleniyor)
  const allSalesSearchMountedRef = useRef(false);
  useEffect(() => {
    if (!allSalesSearchMountedRef.current) { allSalesSearchMountedRef.current = true; return; }
    if (!showAllSalesModal) return;
    if (allSalesSearchTimeout.current) clearTimeout(allSalesSearchTimeout.current);
    allSalesSearchTimeout.current = setTimeout(() => {
      setAllSales([]);
      setAllSalesOffset(0);
      setHasMoreAllSales(true);
      fetchAllSales(false);
    }, 400);
    return () => { if (allSalesSearchTimeout.current) clearTimeout(allSalesSearchTimeout.current); };
  }, [allSalesSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  // Satış silme izni kontrolü
  const fetchSaleDeletePermission = async () => {
    try {
      const response = await fetch(`${API_URL}/settings/sale-delete-permission`);
      const data = await response.json();
      setSaleDeleteAllowed(data.allowed);
    } catch (error) {
      console.error('Satış silme izni kontrol hatası:', error);
      setSaleDeleteAllowed(false);
    }
  };

  // Sayfa yüklendiğinde silme iznini kontrol et
  useEffect(() => {
    if (user?.role === 'admin') {
      fetchSaleDeletePermission();
    }
  }, [user?.role]);

  // ===== SARF MALZEMELERİ FONKSİYONLARI =====
  const consumableCategories = [
    { id: 'Sarf Hediye Paketi', name: 'Hediye Paketi', icon: <Gift size={24} /> },
    { id: 'Sarf Kutu', name: 'Kutu', icon: <Box size={24} /> },
    { id: 'Sarf Çanta', name: 'Çanta', icon: <ShoppingBag size={24} /> },
    { id: 'Sarf Poşet', name: 'Poşet', icon: <ShoppingCart size={24} /> },
    { id: 'Sarf Kurdele', name: 'Kurdele', icon: <Ribbon size={24} /> },
    { id: 'Sarf Etiket', name: 'Etiket', icon: <Tag size={24} /> },
    { id: 'Sarf Diğer', name: 'Diğer', icon: <MoreHorizontal size={24} /> },
  ];

  const openConsumablesModal = async (sale, editMode = false) => {
    setSelectedSaleForConsumable(sale);
    setConsumableCategory(null);
    setConsumableProducts([]);
    setConsumablesEditMode(editMode);
    setExistingConsumables([]);

    if (editMode && sale.consumables_count > 0) {
      // Mevcut sarfları yükle
      try {
        const response = await fetch(`${API_URL}/sales/${sale.id}/consumables`);
        const data = await response.json();
        setExistingConsumables(data);

        // Seçili olarak ayarla
        const selected = {};
        data.forEach(item => {
          selected[item.product_id] = {
            product: {
              id: item.product_id,
              name: item.product_name,
              image: item.image,
              category: item.category,
              store_stocks: [] // Stok bilgisi ayrıca yüklenecek
            },
            quantity: item.quantity,
            existingRecord: true
          };
        });
        setSelectedConsumables(selected);
      } catch (error) {
        console.error('Sarf malzemeleri yüklenemedi:', error);
        toast.error('Sarf malzemeleri yüklenemedi!');
      }
    } else {
      setSelectedConsumables({});
    }

    setShowConsumablesModal(true);
  };

  const fetchConsumablesByCategory = async (category) => {
    setLoadingConsumables(true);
    setConsumableCategory(category);
    try {
      const response = await fetch(`${API_URL}/products?metal_type=sarf&category=${encodeURIComponent(category)}&limit=100`);
      const data = await response.json();
      const products = data.products || [];
      // Sadece stoku olan ürünleri göster
      const storeId = selectedStore?.id || user?.store_id || 1;
      const productsWithStock = products.filter(p => {
        const stock = p.store_stocks?.find(s => s.store_id === storeId)?.stock || p.stock || 0;
        return stock > 0;
      });
      // Stok bilgisini stocks alanına ekle (deductConsumableFromStock için)
      productsWithStock.forEach(p => {
        p.stocks = p.store_stocks;
      });
      setConsumableProducts(productsWithStock);
    } catch (error) {
      console.error('Sarf malzemeleri yüklenemedi:', error);
      toast.error('Sarf malzemeleri yüklenemedi!');
      setConsumableProducts([]);
    } finally {
      setLoadingConsumables(false);
    }
  };

  // Sarf malzemesi miktarını artır
  const increaseConsumableQty = (product) => {
    const storeId = selectedStore?.id || user?.store_id || 1;
    const currentStock = product.store_stocks?.find(s => s.store_id === storeId)?.stock || product.stock || 0;
    const currentSelected = selectedConsumables[product.id]?.quantity || 0;

    if (currentSelected >= currentStock) {
      toast.warning('Stok yetersiz!');
      return;
    }

    setSelectedConsumables(prev => ({
      ...prev,
      [product.id]: {
        product,
        quantity: currentSelected + 1
      }
    }));
  };

  // Sarf malzemesi miktarını azalt
  const decreaseConsumableQty = (productId) => {
    setSelectedConsumables(prev => {
      const current = prev[productId];
      if (!current || current.quantity <= 1) {
        // Sil
        const newState = { ...prev };
        delete newState[productId];
        return newState;
      }
      return {
        ...prev,
        [productId]: {
          ...current,
          quantity: current.quantity - 1
        }
      };
    });
  };

  // Sarf malzemesini listeden kaldır
  const removeConsumable = (productId) => {
    setSelectedConsumables(prev => {
      const newState = { ...prev };
      delete newState[productId];
      return newState;
    });
  };

  // Tüm sarf malzemelerini kaydet
  const saveConsumables = async () => {
    if (!selectedSaleForConsumable) {
      toast.warning('Satış seçilmedi!');
      return;
    }

    const items = Object.values(selectedConsumables);
    if (items.length === 0 && !consumablesEditMode) {
      toast.warning('Sarf malzemesi seçilmedi!');
      return;
    }

    setSavingConsumables(true);
    const storeId = selectedStore?.id || user?.store_id || 1;

    try {
      // Düzenleme modunda stok farkını hesapla
      if (consumablesEditMode) {
        // Mevcut kayıtları haritala
        const existingMap = {};
        existingConsumables.forEach(item => {
          existingMap[item.product_id] = item.quantity;
        });

        // Her ürün için stok değişikliğini hesapla
        for (const item of items) {
          const { product, quantity } = item;
          const oldQty = existingMap[product.id] || 0;
          const diff = quantity - oldQty;

          if (diff !== 0) {
            const response = await fetch(`${API_URL}/products/${product.id}/stock`, {
              method: 'PATCH',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                store_id: storeId,
                quantity: Math.abs(diff),
                type: diff > 0 ? 'subtract' : 'add'
              })
            });

            if (!response.ok) {
              throw new Error(`${product.name} stok güncellenemedi`);
            }
          }
          delete existingMap[product.id];
        }

        // Silinen ürünlerin stokunu geri ekle
        for (const [productId, qty] of Object.entries(existingMap)) {
          await fetch(`${API_URL}/products/${productId}/stock`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              store_id: storeId,
              quantity: qty,
              type: 'add'
            })
          });
        }
      } else {
        // Yeni ekleme - sadece stoktan düş
        for (const item of items) {
          const { product, quantity } = item;

          const response = await fetch(`${API_URL}/products/${product.id}/stock`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              store_id: storeId,
              quantity: quantity,
              type: 'subtract'
            })
          });

          if (!response.ok) {
            throw new Error(`${product.name} stok güncellenemedi`);
          }
        }
      }

      // Veritabanına kaydet
      const dbItems = items.map(i => ({
        product_id: i.product.id,
        product_name: i.product.name,
        quantity: i.quantity
      }));

      await fetch(`${API_URL}/sales/${selectedSaleForConsumable.id}/consumables`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: dbItems,
          store_id: storeId,
          user_id: user?.id
        })
      });

      // Log kaydet
      const itemsSummary = items.map(i => `${i.product.name} x${i.quantity}`).join(', ');
      logService.log(
        LOG_MODULES.SALES,
        LOG_ACTIONS.UPDATE,
        `Satış #${selectedSaleForConsumable.id} için sarf malzemeleri ${consumablesEditMode ? 'güncellendi' : 'eklendi'}: ${itemsSummary}`,
        { sale_id: selectedSaleForConsumable.id, items: dbItems }
      );

      toast.success(consumablesEditMode ? 'Sarf malzemeleri güncellendi' : `${items.length} kalem sarf malzemesi eklendi`);
      setShowConsumablesModal(false);
      setSelectedConsumables({});
      fetchRecentSales(storeId); // Listeyi güncelle
      if (showAllSalesModal) fetchAllSales(false);
    } catch (error) {
      console.error('Sarf kaydetme hatası:', error);
      toast.error(error.message || 'Sarf malzemeleri kaydedilemedi!');
    } finally {
      setSavingConsumables(false);
    }
  };

  // ===== MASRAFLAR FONKSİYONLARI =====
  const fetchExpenses = async () => {
    setLoadingExpenses(true);
    try {
      const storeId = selectedStore?.id || user?.store_id;
      const now = new Date();
      const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      const response = await fetch(`${API_URL}/expenses?store_id=${storeId}&date=${today}`);
      const data = await response.json();
      if (data.success) {
        setExpenses(data.expenses || []);
      }
    } catch (error) {
      console.error('Masraflar yüklenirken hata:', error);
    } finally {
      setLoadingExpenses(false);
    }
  };

  const openExpensesModal = async () => {
    await fetchExpenses();
    setShowExpensesModal(true);
  };

  const addExpense = async () => {
    if (!newExpense.description.trim()) {
      toast.warning('Açıklama giriniz!');
      return;
    }
    if (!newExpense.amount || parseFloat(newExpense.amount) <= 0) {
      toast.warning('Geçerli bir tutar giriniz!');
      return;
    }

    try {
      const storeId = selectedStore?.id || user?.store_id;
      const response = await fetch(`${API_URL}/expenses`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...newExpense,
          amount: parseFloat(newExpense.amount),
          store_id: storeId,
          user_id: user?.id
        })
      });

      const data = await response.json();
      if (data.success) {
        toast.success('Masraf eklendi!');

        // Log kaydı - Masraf oluşturma
        logService.log(
          LOG_MODULES.EXPENSES,
          LOG_ACTIONS.CREATE,
          `Masraf eklendi - ${newExpense.description} - ₺${parseFloat(newExpense.amount).toLocaleString('tr-TR')}`,
          {
            entity_type: 'expense',
            entity_id: data.expense?.id || data.id,
            status: 'success',
            details: {
              description: newExpense.description,
              amount: parseFloat(newExpense.amount),
              category: newExpense.category,
              payment_method: newExpense.payment_method,
              notes: newExpense.notes || null
            }
          }
        );

        setNewExpense({ description: '', amount: '', category: 'Genel', payment_method: 'Nakit', notes: '' });
        setShowAddExpenseForm(false);
        await fetchExpenses();
      } else {
        toast.error(data.error || 'Masraf eklenemedi!');
      }
    } catch (error) {
      console.error('Masraf ekleme hatası:', error);
      toast.error('Sunucu hatası!');
    }
  };

  const deleteExpense = (id) => {
    showConfirmation({
      title: 'Masrafı Sil',
      message: 'Bu masrafı silmek istediğinize emin misiniz?',
      confirmText: 'Evet, Sil',
      cancelText: 'İptal',
      type: 'danger',
      onConfirm: async () => {
        try {
          const response = await fetch(`${API_URL}/expenses/${id}`, {
            method: 'DELETE'
          });
          const data = await response.json();
          if (data.success) {
            toast.success('Masraf silindi!');
            await fetchExpenses();
          } else {
            toast.error('Silme başarısız!');
          }
        } catch (error) {
          console.error('Masraf silme hatası:', error);
          toast.error('Sunucu hatası!');
        }
      }
    });
  };

  const getTodayExpensesTotal = () => {
    return expenses.reduce((sum, exp) => sum + parseFloat(exp.amount || 0), 0);
  };

  // Admin: Masraf düzenleme modalını aç
  const openEditExpenseModal = (expense) => {
    // created_at'tan tarih ve saat bilgisini ayır
    let expense_date = '';
    let expense_time = '';
    if (expense.created_at) {
      const date = new Date(expense.created_at);
      expense_date = date.toISOString().split('T')[0];
      expense_time = date.toTimeString().slice(0, 5);
    }
    setEditingExpense({ ...expense, expense_date, expense_time });
    setShowEditExpenseModal(true);
  };

  // Admin: Masraf güncelle
  const updateExpense = async () => {
    if (!editingExpense) return;

    // Tarih ve saati birleştir
    let created_at = editingExpense.created_at;
    if (editingExpense.expense_date && editingExpense.expense_time) {
      created_at = `${editingExpense.expense_date} ${editingExpense.expense_time}:00`;
    } else if (editingExpense.expense_date) {
      created_at = `${editingExpense.expense_date} 12:00:00`;
    }

    try {
      const response = await fetch(`${API_URL}/expenses/${editingExpense.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          description: editingExpense.description,
          amount: parseFloat(editingExpense.amount),
          category: editingExpense.category,
          payment_method: editingExpense.payment_method,
          notes: editingExpense.notes,
          created_at: created_at
        })
      });

      const data = await response.json();
      if (data.success) {
        toast.success('Masraf güncellendi!');
        setShowEditExpenseModal(false);
        setEditingExpense(null);
        await fetchExpenses();
      } else {
        toast.error('Güncelleme başarısız!');
      }
    } catch (error) {
      console.error('Masraf güncelleme hatası:', error);
      toast.error('Sunucu hatası!');
    }
  };

  // Admin: Masrafı sil (onaylı)
  const confirmDeleteExpense = async () => {
    if (!deletingExpenseId) return;

    try {
      const response = await fetch(`${API_URL}/expenses/${deletingExpenseId}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Masraf silindi!');
        setShowDeleteExpenseConfirm(false);
        setDeletingExpenseId(null);
        setShowEditExpenseModal(false);
        await fetchExpenses();
      } else {
        toast.error('Silme başarısız!');
      }
    } catch (error) {
      console.error('Masraf silme hatası:', error);
      toast.error('Sunucu hatası!');
    }
  };

  // Geçmiş masrafları getir (admin)
  const fetchPastExpenses = async (filters = pastExpenseFilters) => {
    if (user?.role !== 'admin') return;
    setLoadingPastExpenses(true);
    try {
      const params = new URLSearchParams();
      if (filters.store_id) params.append('store_id', filters.store_id);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      const response = await fetch(`${API_URL}/expenses?${params.toString()}`);
      const data = await response.json();
      if (data.success) {
        setPastExpenses(data.expenses || []);
      }
    } catch (error) {
      console.error('Geçmiş masraflar yüklenirken hata:', error);
      toast.error('Masraflar yüklenemedi!');
    } finally {
      setLoadingPastExpenses(false);
    }
  };

  // ===== BANKA HESAPLARI FONKSİYONLARI =====
  const fetchBankAccounts = async () => {
    try {
      const response = await fetch(`${API_URL}/bank-accounts?active_only=true`);
      const data = await response.json();
      if (data.success) {
        // Sıralama: Şirket → Şahıs → E-Ticaret (her grupta orijinal sıra korunur)
        const typeOrder = { company: 0, ecommerce: 2 };
        const accounts = (data.accounts || []).slice().sort((a, b) => {
          const ra = typeOrder[a.account_type] ?? 1;
          const rb = typeOrder[b.account_type] ?? 1;
          return ra - rb;
        });
        setBankAccounts(accounts);
      }
    } catch (error) {
      console.error('Banka hesapları yüklenirken hata:', error);
    }
  };

  const openBankAccountsModal = async () => {
    await fetchBankAccounts();
    setShowBankAccountsModal(true);
  };

  const selectBankAccount = (account) => {
    setSelectedBankAccount(account);
    // Basit onay modalını göster
    setShowBankAccountDetails(true);
  };

  const addBankAccount = async () => {
    if (!newBankAccount.account_name.trim()) {
      toast.warning('Hesap adı giriniz!');
      return;
    }
    if (!newBankAccount.holder_name.trim()) {
      toast.warning('Hesap sahibi adı giriniz!');
      return;
    }
    if (!newBankAccount.iban.trim()) {
      toast.warning('IBAN giriniz!');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/bank-accounts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newBankAccount)
      });

      const data = await response.json();
      if (data.success) {
        toast.success('Hesap eklendi!');
        resetBankAccountForm();
        setShowAddBankAccountForm(false);
        await fetchBankAccounts();
      } else {
        toast.error(data.error || 'Hesap eklenemedi!');
      }
    } catch (error) {
      console.error('Hesap ekleme hatası:', error);
      toast.error('Sunucu hatası!');
    }
  };

  const updateBankAccount = async () => {
    if (!editingBankAccount) return;

    try {
      const response = await fetch(`${API_URL}/bank-accounts/${editingBankAccount.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...newBankAccount,
          active: true
        })
      });

      const data = await response.json();
      if (data.success) {
        toast.success('Hesap güncellendi!');
        resetBankAccountForm();
        setEditingBankAccount(null);
        setShowAddBankAccountForm(false);
        await fetchBankAccounts();
      } else {
        toast.error(data.error || 'Hesap güncellenemedi!');
      }
    } catch (error) {
      console.error('Hesap güncelleme hatası:', error);
      toast.error('Sunucu hatası!');
    }
  };

  const deleteBankAccount = (id) => {
    showConfirmation({
      title: 'Banka Hesabını Sil',
      message: 'Bu hesabı silmek istediğinize emin misiniz?',
      confirmText: 'Evet, Sil',
      cancelText: 'İptal',
      type: 'danger',
      onConfirm: async () => {
        try {
          const response = await fetch(`${API_URL}/bank-accounts/${id}`, {
            method: 'DELETE'
          });
          const data = await response.json();
          if (data.success) {
            toast.success('Hesap silindi!');
            resetBankAccountForm();
            setShowAddBankAccountForm(false);
            await fetchBankAccounts();
          }
        } catch (error) {
          console.error('Hesap silme hatası:', error);
          toast.error('Sunucu hatası!');
        }
      }
    });
  };

  const startEditBankAccount = (account) => {
    setEditingBankAccount(account);
    setNewBankAccount({
      account_name: account.account_name,
      holder_name: account.holder_name,
      bank_name: account.bank_name || '',
      iban: account.iban,
      account_type: account.account_type,
      currency: account.currency || 'TRY',
      qr_code_image: account.qr_code_image || '',
      notes: account.notes || ''
    });
    setShowAddBankAccountForm(true);
  };

  const resetBankAccountForm = () => {
    setNewBankAccount({
      account_name: '',
      holder_name: '',
      bank_name: '',
      iban: '',
      account_type: 'company',
      currency: 'TRY',
      qr_code_image: '',
      notes: ''
    });
    setEditingBankAccount(null);
  };

  const handleQRCodeUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.warning('QR kod görseli 2MB\'dan küçük olmalıdır!');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setNewBankAccount({ ...newBankAccount, qr_code_image: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const formatIBAN = (iban) => {
    if (!iban) return '';
    const clean = iban.replace(/\s/g, '');
    return clean.match(/.{1,4}/g)?.join(' ') || clean;
  };


  const parseSaleItems = (sale) => {
    if (!sale || !sale.items) return [];
    try {
      const items = typeof sale.items === 'string' ? JSON.parse(sale.items) : sale.items;
      return Array.isArray(items) ? items : [];
    } catch (e) { return []; }
  };

  const proceedWithBankTransfer = (account = null) => {
    const bankAccount = account || selectedBankAccount;
    if (!bankAccount) {
      toast.warning('Lütfen bir hesap seçin!');
      return;
    }
    setShowBankAccountDetails(false);
    setShowBankAccountsModal(false);

    // Tahsilat havale için bekleyen ödeme varsa
    if (pendingCollectionHavalePayment) {
      const accountType = bankAccount.account_type === 'company' ? 'Şirket' : 'Şahsi';
      const newPayment = {
        id: Date.now(),
        method: 'Havale/EFT',
        amount: pendingCollectionHavalePayment.amount,
        gramAmount: pendingCollectionHavalePayment.gramAmount,
        debtType: pendingCollectionHavalePayment.debtType,
        bankAccount: `${bankAccount.account_name} (${accountType})`
      };
      setCollectionMixedPayments(prev => [...prev, newPayment]);
      setCollectionPaymentAmount('');
      setPendingCollectionHavalePayment(null);

      if (pendingCollectionHavalePayment.gramAmount) {
        toast.success(`${pendingCollectionHavalePayment.gramAmount.toFixed(pendingCollectionHavalePayment.debtType === 'GUMUS' ? 2 : 3)} gr (₺${pendingCollectionHavalePayment.amount.toLocaleString('tr-TR', {minimumFractionDigits: 2})}) Havale/EFT (${bankAccount.account_name}) eklendi`);
      } else {
        toast.success(`₺${pendingCollectionHavalePayment.amount.toLocaleString('tr-TR', {minimumFractionDigits: 2})} Havale/EFT (${bankAccount.account_name}) eklendi`);
      }
      return;
    }

    // Karışık ödemede havale için bekleyen ödeme varsa
    if (pendingHavalePayment) {
      const accountType = bankAccount.account_type === 'company' ? 'Şirket' : 'Şahsi';
      const newPayment = {
        id: Date.now(),
        method: 'Havale/EFT',
        currency: pendingHavalePayment.currency,
        amount: pendingHavalePayment.amount,
        amountTL: pendingHavalePayment.amountTL,
        rate: pendingHavalePayment.rate,
        bankAccount: `${bankAccount.account_name} (${accountType})`
      };
      setPartialPayments(prev => [...prev, newPayment]);
      setReceivedAmount('');
      setPendingHavalePayment(null);
      return;
    }

    // Normal Havale/EFT ile satışı tamamla
    // Mağaza ID belirle
    const storeId = user?.role === 'admin' ? (selectedStore?.id || user?.store_id) : user?.store_id;
    if (!storeId) {
      toast.error('Mağaza bilgisi bulunamadı! Lütfen mağaza seçin veya yeniden giriş yapın.');
      return;
    }

    // Banka hesap bilgisini payment_details'a ekle
    const accountType = bankAccount.account_type === 'company' ? 'Şirket' : 'Şahsi';
    const paymentDetailsForHavale = [{
      method: 'Havale/EFT',
      currency: 'TRY',
      amount: total,
      amountTL: total,
      rate: 1,
      bankAccount: `${bankAccount.account_name} (${accountType})`
    }];
    processPayment('Havale/EFT', [], storeId, paymentDetailsForHavale);
  };

  // Son satış modalını aç
  const openLastSaleModal = async (mode = 'view') => {
    console.log('DEBUG - User role:', user?.role, 'Full user:', user); // DEBUG
    const currentStore = user?.role === 'admin' ? (selectedStore?.id || user?.store_id) : user?.store_id;
    await fetchRecentSales(currentStore);
    setSalesModalMode(mode);
    setShowLastSaleModal(true);
  };

  // Başka mağazadan fiş arama
  const searchCrossStoreSale = async () => {
    const searchId = crossStoreReceiptId.trim();
    if (!searchId) return;
    setCrossStoreLoading(true);
    setCrossStoreError('');
    setCrossStoreSale(null);
    try {
      // Önce fiş numarası ile ara
      const searchResponse = await fetch(`${API_URL}/sales/search-by-receipt?receipt_number=${encodeURIComponent(searchId)}`);
      if (searchResponse.ok) {
        const searchData = await searchResponse.json();
        if (searchData && searchData.id) {
          const detailResponse = await fetch(`${API_URL}/sales/${searchData.id}`);
          if (detailResponse.ok) {
            const detailData = await detailResponse.json();
            if (detailData && detailData.id) {
              setCrossStoreSale(detailData);
              return;
            }
          }
        }
      }
      // Fiş numarasıyla bulunamadıysa ID ile dene
      const idResponse = await fetch(`${API_URL}/sales/${searchId}`);
      if (idResponse.ok) {
        const data = await idResponse.json();
        if (data && data.id) {
          setCrossStoreSale(data);
          return;
        }
      }
      setCrossStoreError('Fiş bulunamadı');
    } catch (err) {
      setCrossStoreError('Fiş bulunamadı');
    } finally {
      setCrossStoreLoading(false);
    }
  };

  // İade modalını aç
  const openRefundModal = async (sale) => {
    // Satış detaylarını getir
    try {
      const response = await fetch(`${API_URL}/sales/${sale.id}`);
      const data = await response.json();
      setSelectedSale(data);

      // Fark kalemlerini önceden parse et (indirim oranı hesabı için gerekli)
      let refundAdjItems = [];
      if (data.adjustment_items) {
        refundAdjItems = typeof data.adjustment_items === 'string' ? JSON.parse(data.adjustment_items) : data.adjustment_items;
      }
      if ((!refundAdjItems || refundAdjItems.length === 0) && data.receipt_data) {
        try {
          const rd = typeof data.receipt_data === 'string' ? JSON.parse(data.receipt_data) : data.receipt_data;
          refundAdjItems = rd.adjustmentItems || rd.adjustment_items || [];
        } catch (e) {}
      }

      // effective_unit_price: kalemin indirim uygulanmış birim fiyatı. Yazdırılan fiş, Fiş
      // Detayı modalı, Değişim ve İade ekranı AYNI kaynaktan (computeSaleItemPricing)
      // hesaplasın diye ortak yardımcıya taşındı (vaka: fiş #022617101 — iade tutarları
      // fiş detayıyla tutmuyordu).
      const { itemUnitPrice: refundItemUnitPrice, adjAmount: refundAdjEffective } = computeSaleItemPricing(data, refundAdjItems);

      const itemsWithAvailability = (data.items || []).map(item => ({
        ...item,
        returned_quantity: item.returned_quantity || 0,
        availableQty: item.quantity - (item.returned_quantity || 0),
        refundQty: 0,
        effective_unit_price: refundItemUnitPrice(item)
      }));
      setRefundItems(itemsWithAvailability);

      // Fark kalemlerini iade edilebilir şekilde hazırla - aynı hesaplama uygulanır
      const adjustmentsWithRefund = (refundAdjItems || []).map((adj, idx) => ({
        ...adj,
        id: idx,
        refundAmount: 0,
        effective_amount: refundAdjEffective(adj),
        isRefunded: adj.is_refunded || false
      }));
      setRefundAdjustments(adjustmentsWithRefund);

      // Orijinal ödeme detaylarını parse et
      let paymentDetails = [];
      if (data.payment_details) {
        paymentDetails = typeof data.payment_details === 'string'
          ? JSON.parse(data.payment_details)
          : data.payment_details;
      }
      // Eğer payment_details boşsa ama payment_method varsa, toplam tutarla bir detay oluştur
      if ((!paymentDetails || paymentDetails.length === 0) && data.payment_method) {
        paymentDetails = [{
          method: data.payment_method,
          currency: 'TRY',
          amount: data.total,
          amount_tl: data.total
        }];
      }
      // Eğer satışta hurda alımı varsa, ödeme detaylarına hurda ekle
      const scrapAmount = Number(data.scrap_total_amount) || 0;
      if (scrapAmount > 0) {
        paymentDetails.push({
          method: `Hurda (${data.scrap_metal_type === 'gold' ? 'Altın' : 'Gümüş'})`,
          currency: 'TRY',
          amount: scrapAmount,
          amount_tl: scrapAmount,
          is_scrap: true,
          scrap_id: data.scrap_id
        });
      }

      setRefundOriginalPayments(paymentDetails || []);

      // Bu satışa ait önceki iadeleri çek
      let prevRefunds = [];
      try {
        const refundsResponse = await fetch(`${API_URL}/exchanges?original_sale_id=${sale.id}`);
        const refundsData = await refundsResponse.json();
        if (Array.isArray(refundsData)) {
          prevRefunds = refundsData.filter(r => r.type === 'refund');
        }
      } catch (e) {
        console.error('Önceki iadeler alınamadı:', e);
      }
      setPreviousRefunds(prevRefunds);

      // İade tutarlarını sıfırla
      const initialAmounts = {};
      (paymentDetails || []).forEach((p, idx) => {
        initialAmounts[idx] = '';
      });
      setRefundAmounts(initialAmounts);

      // Mevcut müşteriyi sakla (modal iptal edilirse geri yüklemek için)
      setPreRefundCustomer(selectedCustomer || null);

      if (data.customer_id) {
        // Müşterinin borçlarını getir
        try {
          const debtsResponse = await fetch(`${API_URL}/customers/${data.customer_id}/debts`);
          const debtsData = await debtsResponse.json();
          if (debtsData.success) {
            // Sadece ödenmemiş borçları al
            const unpaidDebts = debtsData.debts.filter(d => !d.is_paid);
            setRefundCustomerDebts(unpaidDebts);
          } else {
            setRefundCustomerDebts([]);
          }
        } catch (e) {
          console.error('Borç bilgisi alınamadı:', e);
          setRefundCustomerDebts([]);
        }

        // Müşterinin borç özeti bilgisini çek (modal başlığında göstermek için)
        try {
          const debtSummaryResponse = await fetch(`${API_URL}/customers/debts/summary`);
          const debtSummaryData = await debtSummaryResponse.json();
          const customerDebt = debtSummaryData.find(d => d.customer_id === data.customer_id);
          setSelectedCustomerDebt(customerDebt || null);
        } catch (err) {
          console.error('Borç özet bilgisi çekilemedi:', err);
          setSelectedCustomerDebt(null);
        }
      } else {
        setRefundCustomerDebts([]);
        setSelectedCustomerDebt(null);
      }

      // Varsayılan iade yöntemi - ilk ödeme detayına göre
      if (paymentDetails && paymentDetails.length > 0) {
        const firstPayment = paymentDetails[0];
        setRefundPaymentMethod(`Orijinal: ${firstPayment.method} - ${firstPayment.currency === 'TRY' ? '₺' : firstPayment.currency}${Number(firstPayment.amount).toLocaleString('tr-TR')}`);
      } else {
        setRefundPaymentMethod('Nakit İade');
      }

      setShowLastSaleModal(false);
      setRecentSalesSearch('');
      setShowRefundModal(true);
    } catch (error) {
      console.error('Satış detay hatası:', error);
    }
  };

  // İade miktarını güncelle (availableQty = quantity - returned_quantity)
  // NOT: item.id (sale_items satır id'si) ile eşleştirilir, product_id ile DEĞİL.
  // Aynı üründen (ör. "Kişiye Özel") aynı fişte birden fazla satır olabilir; product_id
  // ile eşleştirme yapılırsa bir satırı düzenlemek tüm aynı-ürünlü satırları etkiler.
  const updateRefundQty = (itemId, qty) => {
    const item = refundItems.find(i => i.id === itemId);
    if (!item) return;

    const availableQty = item.availableQty !== undefined ? item.availableQty : item.quantity;

    // Tamamen iade edilmiş ürün için hata göster
    if (availableQty <= 0 && qty > 0) {
      toast.error(`Bu ürün daha önce tamamen iade edilmiş!`);
      return;
    }

    // Iade edilebilir miktarı aşmaya çalışıyorsa hata göster
    if (qty > availableQty) {
      toast.warning(`Bu üründen en fazla ${availableQty} adet iade edilebilir! (${item.returned_quantity} adet daha önce iade edildi)`);
      // Miktarı maksimuma ayarla
      setRefundItems(refundItems.map(i =>
        i.id === itemId
          ? { ...i, refundQty: availableQty }
          : i
      ));
      return;
    }

    setRefundItems(refundItems.map(i =>
      i.id === itemId
        ? { ...i, refundQty: Math.max(0, qty) }
        : i
    ));
  };

  // İade işlemini gerçekleştir
  const processRefund = async () => {
    const itemsToRefund = refundItems.filter(item => item.refundQty > 0);
    const adjustmentsToRefund = refundAdjustments.filter(adj => adj.refundAmount > 0);

    if (itemsToRefund.length === 0 && adjustmentsToRefund.length === 0) {
      toast.warning('Lütfen iade edilecek ürün veya fark seçin!');
      return;
    }

    // Açık hesap (bakiye) iadesi için müşteri seçili olmalı
    const hediyeKartiVal = useCustomRefundPayments
      ? (parseFloat(customRefundPayments.hediyeKarti) || 0)
      : refundOriginalPayments.reduce((sum, p, idx) => {
          if ((p.method === 'Müşteri Bakiyesi' || p.method === 'Bakiye') && (parseFloat(refundAmounts[idx]) || 0) > 0) {
            return sum + (parseFloat(refundAmounts[idx]) || 0);
          }
          return sum;
        }, 0);
    if (hediyeKartiVal > 0 && (!selectedSale?.customer_id)) {
      toast.warning('Açık hesaba (bakiyeye) aktarım yapabilmek için satışta kayıtlı müşteri olmalıdır!', { autoClose: 6000 });
      return;
    }

    // Ürün bazlı iade tutarını hesapla (gerçek ödenen fiyat ile)
    const productRefundAmount = itemsToRefund.reduce((sum, item) => sum + (item.refundQty * Number(item.effective_unit_price || item.unit_price)), 0);
    // Fark bazlı iade tutarını hesapla
    const adjustmentRefundAmount = adjustmentsToRefund.reduce((sum, adj) => sum + (parseFloat(adj.refundAmount) || 0), 0);

    // Ödeme bazlı iade tutarlarını hesapla
    const refundPaymentDetails = [];
    let totalRefundFromPayments = 0;

    if (useCustomRefundPayments) {
      // Özel ödeme yöntemleri kullanılıyor
      const nakitAmount = parseFloat(customRefundPayments.nakit) || 0;
      const kartAmount = parseFloat(customRefundPayments.krediKarti) || 0;
      const havaleAmount = parseFloat(customRefundPayments.havale) || 0;
      const hediyeKartiAmount = parseFloat(customRefundPayments.hediyeKarti) || 0;

      if (nakitAmount > 0) {
        refundPaymentDetails.push({
          method: 'Nakit',
          currency: 'TRY',
          amount: nakitAmount,
          is_custom: true
        });
        totalRefundFromPayments += nakitAmount;
      }
      if (kartAmount > 0) {
        refundPaymentDetails.push({
          method: 'Kredi Kartı',
          currency: 'TRY',
          amount: kartAmount,
          is_custom: true
        });
        totalRefundFromPayments += kartAmount;
      }
      if (havaleAmount > 0) {
        refundPaymentDetails.push({
          method: 'Havale/EFT',
          currency: 'TRY',
          amount: havaleAmount,
          is_custom: true
        });
        totalRefundFromPayments += havaleAmount;
      }
      if (hediyeKartiAmount > 0) {
        refundPaymentDetails.push({
          method: 'Bakiye',
          currency: 'TRY',
          amount: hediyeKartiAmount,
          is_custom: true
        });
        totalRefundFromPayments += hediyeKartiAmount;
      }
      const hurdaAmount = parseFloat(customRefundPayments.hurda) || 0;
      if (hurdaAmount > 0) {
        refundPaymentDetails.push({
          method: 'Hurda İade',
          currency: 'TRY',
          amount: hurdaAmount,
          is_custom: true,
          is_scrap_refund: true
        });
        totalRefundFromPayments += hurdaAmount;
      }
    } else {
      // Orijinal ödeme yöntemlerinden iade
      refundOriginalPayments.forEach((payment, idx) => {
        const amount = parseFloat(refundAmounts[idx]) || 0;
        if (amount > 0) {
          refundPaymentDetails.push({
            method: payment.method,
            currency: payment.currency || 'TRY',
            amount: amount,
            original_amount: Number(payment.amount),
            original_amount_tl: Number(payment.amount_tl || payment.amount)
          });
          totalRefundFromPayments += amount;
        }
      });
    }

    // Eğer hiçbir ödeme girişi yapılmamışsa hata ver
    if (totalRefundFromPayments === 0) {
      toast.warning('Lütfen en az bir ödeme yönteminden iade tutarı girin!');
      return;
    }

    // Borçtan düşme seçildiyse kontrol et (eski dropdown için geriye uyumluluk)
    let deductFromDebt = null;
    if (refundPaymentMethod.startsWith('Borçtan Düş - ')) {
      const debtType = refundPaymentMethod.replace('Borçtan Düş - ', '');
      const matchingDebts = refundCustomerDebts.filter(d => d.debt_type === debtType);

      deductFromDebt = {
        type: debtType,
        debt_ids: matchingDebts.map(d => d.id)
      };
    }

    try {
      const currentStore = user?.role === 'admin' ? (selectedStore?.id || user?.store_id) : user?.store_id;
      const staffInfo = selectedStaff.length > 0 ? selectedStaff[0] : null;

      const response = await fetch(`${API_URL}/sales/${selectedSale.id}/refund`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: itemsToRefund.filter(item => item.product_id).map(item => ({
            product_id: item.product_id,
            sale_item_id: typeof item.id === 'number' ? item.id : null,
            quantity: item.refundQty,
            refund_unit_price: Number(item.effective_unit_price || item.unit_price)
          })),
          adjustment_refunds: adjustmentsToRefund.map(adj => ({
            reason: adj.reason || 'Fark',
            description: adj.description || '',
            original_amount: adj.amount,
            refund_amount: adj.refundAmount
          })),
          payment_method: refundPaymentMethod,
          refund_payment_details: refundPaymentDetails,
          staff_id: staffInfo?.id || user?.id,
          staff_name: staffInfo?.name || user?.name,
          deduct_from_debt: deductFromDebt,
          refund_amount: totalRefundFromPayments,
          store_id: currentStore
        })
      });

      const data = await response.json();
      if (data.success) {
        // İade detaylarını göster
        const refundSummary = refundPaymentDetails.map(p =>
          `${p.method}: ₺${p.amount.toLocaleString('tr-TR')}`
        ).join(', ');
        toast.success(`İade tamamlandı: ${refundSummary}`);

        // Bakiye iadesi varsa müşteri bakiye kaydı oluştur (customer_balance_transactions)
        const hediyeKartiAmount = useCustomRefundPayments
          ? (parseFloat(customRefundPayments.hediyeKarti) || 0)
          : refundPaymentDetails.filter(p => p.method === 'Müşteri Bakiyesi' || p.method === 'Bakiye').reduce((sum, p) => sum + p.amount, 0);
        if (hediyeKartiAmount > 0 && selectedSale.customer_id) {
          try {
            await fetch(`${API_URL}/customers/${selectedSale.customer_id}/debts`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                debt_type: 'TL',
                amount: -hediyeKartiAmount,
                amount_tl: -hediyeKartiAmount,
                sale_id: selectedSale.id,
                description: `İade bakiyesi - Fiş #${selectedSale.receipt_number || selectedSale.store_sale_number || selectedSale.id}${selectedSale.customer_name ? ` - ${selectedSale.customer_name}` : ''}`
              })
            });
            const customerInfo = selectedSale.customer_name ? ` (${selectedSale.customer_name})` : '';
            toast.success(`₺${hediyeKartiAmount.toLocaleString('tr-TR')} bakiye müşteriye eklendi${customerInfo}`);
          } catch (bakiyeError) {
            console.error('Bakiye oluşturma hatası:', bakiyeError);
            toast.warning('İade tamamlandı ancak bakiye oluşturulamadı!');
          }
        }

        // İade yapılan müşteriyi seç (sonraki işlemler için)
        if (selectedSale.customer_id) {
          selectCustomerFromSale(selectedSale.customer_id, selectedSale.customer_name);
        }

        setShowRefundModal(false);
        setSelectedSale(null);
        setRefundItems([]);
        setRefundAdjustments([]);
        setRefundPaymentMethod('Nakit İade');
        setRefundCustomerDebts([]);
        setRefundOriginalPayments([]);
        setRefundAmounts({});
        setUseCustomRefundPayments(false);
        setCustomRefundPayments({ nakit: '', krediKarti: '', havale: '', hediyeKarti: '', hurda: '' });
        // Son satışlar listesini yenile
        await fetchRecentSales(currentStore);
        if (showAllSalesModal) fetchAllSales(false);
        // Son satışlar modalını aç
        setShowLastSaleModal(true);
      } else {
        toast.error(data.error || 'İade işlemi başarısız!');
      }
    } catch (error) {
      console.error('İade hatası:', error);
      toast.error('İade işlemi başarısız!');
    }
  };

  // İade/Değişim detay modalını aç
  const openRefundDetailModal = (record) => {
    setSelectedRefundDetail(record);
    setShowRefundDetailModal(true);
  };

  // Borç tahsilat detay modalını aç
  const openDebtPaymentDetailModal = (payment) => {
    setSelectedDebtPaymentDetail(payment);
    setDebtPaymentActionsOpen(false);
    setShowDebtPaymentDetailModal(true);
  };

  // Hurda alımı detay modalını aç
  const openScrapPurchaseDetailModal = (scrap) => {
    setSelectedScrapPurchaseDetail(scrap);
    setShowScrapPurchaseDetailModal(true);
  };

  // Masraf detay modalını aç
  const openExpenseDetailModal = (expense) => {
    setSelectedExpenseDetail(expense);
    setShowExpenseDetailModal(true);
  };

  // Satış detay modalını aç
  const openSaleDetailModal = async (sale) => {
    setSaleExchangeInfo(null);
    setSaleDetailActionsOpen(false);
    setShowSaleDetailModal(true);

    const looksLikeExchange = sale.notes && /Değişim satışı - İade Fiş ID:/i.test(sale.notes);

    // Eğer items zaten yüklüyse API çağrısı yapma (hızlı açılsın). Boş liste "yüklü" sayılmaz: bazı
    // satışların receipt_data'sında ürün yok — o zaman /sales/:id (receipt_data + sale_items) getirilir.
    if (sale.items && sale.items.length > 0) {
      setSelectedSaleDetail(sale);
      // Değişim satışıysa exchange_info için arkaplanda fetch et
      if (looksLikeExchange) {
        try {
          const response = await fetch(`${API_URL}/sales/${sale.id}`);
          const data = await response.json();
          if (data.exchange_info) setSaleExchangeInfo(data.exchange_info);
        } catch (e) { /* sessiz geç */ }
      }
    } else {
      // Items yoksa API'den getir
      setSelectedSaleDetail(null);
      try {
        const response = await fetch(`${API_URL}/sales/${sale.id}`);
        const data = await response.json();
        setSelectedSaleDetail(data);
        if (data.exchange_info) setSaleExchangeInfo(data.exchange_info);
      } catch (error) {
        console.error('Satış detay hatası:', error);
        toast.error('Satış bilgileri yüklenemedi!');
        setShowSaleDetailModal(false);
        return;
      }
    }

    // Bu satışın bir değişimin parçası olup olmadığını kontrol et (local state'te ara)
    const localExchange = recentSales.find(
      rec => (rec.record_type === 'exchange' || rec.record_type === 'refund') && rec.new_sale_id === sale.id
    ) || allSales.find(
      rec => (rec.record_type === 'exchange' || rec.record_type === 'refund') && rec.new_sale_id === sale.id
    );

    if (localExchange && !saleExchangeInfo) {
      setSaleExchangeInfo(localExchange);
    }
  };

  // URL'den gelen sale/payment/collection parametresini kontrol et (müşteri yönetiminden yönlendirme)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const saleId = params.get('sale');
    const paymentId = params.get('payment');
    const collectionCustomerId = params.get('collection_customer_id');
    if (saleId) {
      navigate('/retail-sales', { replace: true });
      openSaleDetailModal({ id: parseInt(saleId) });
    } else if (paymentId) {
      navigate('/retail-sales', { replace: true });
      // Tahsilat detayını aç
      (async () => {
        try {
          const res = await fetch(`${API_URL}/debt-payments/${paymentId}`);
          if (res.ok) {
            const data = await res.json();
            setSelectedDebtPaymentDetail({ ...data, total: data.amount });
            setShowDebtPaymentDetailModal(true);
          }
        } catch (e) {
          console.error('Tahsilat detay yüklenemedi:', e);
        }
      })();
    } else if (collectionCustomerId) {
      navigate('/retail-sales', { replace: true });
      // Müşteri yönetiminden gelen tahsilat yönlendirmesi - müşterinin borç detayını direkt aç
      (async () => {
        try {
          const res = await fetch(`${API_URL}/customers/${collectionCustomerId}`);
          if (res.ok) {
            const data = await res.json();
            const customer = data.customer || data;
            await fetchCollectionDebtSummary();
            fetchCollectionCustomerDebts(customer);
          }
        } catch (e) {
          console.error('Tahsilat müşteri yüklenemedi:', e);
          // Fallback: normal tahsilat modalını aç
          openCollectionModal();
        }
      })();
    }
  }, [location.search]);

  // Satış düzenleme modalını kaydetmeden kapat (state temizle)
  const closeEditSaleModal = () => {
    setShowEditSaleModal(false);
    setEditingSale(null);
    setEditingItems([]);
    setEditDiscount(0);
  };

  // Admin düzenleme modalını kaydetmeden kapat
  const closeAdminEditModal = () => {
    setShowAdminEditModal(false);
    setEditingSale(null);
    setEditingItems([]);
    setEditingAdjustments([]);
    setAdminEditSelectedStaff([]);
    setEditDiscount(0);
    setRefundEditItems([]);
    setAdminEditData({
      customer_id: null,
      customer_name: '',
      staff_id: null,
      staff_name: '',
      payment_method: '',
      payment_details: [],
      created_at: '',
      notes: ''
    });
    setAdminCustomerSearch('');
    setAdminCustomerResults([]);
    setShowAdminCustomerDropdown(false);
    setAdminProductSearch('');
    setAdminProductResults([]);
    setShowAdminProductDropdown(false);
  };

  // Değişim düzenleme modalını kaydetmeden kapat
  const closeExchangeEditModal = () => {
    setShowExchangeEditModal(false);
    setEditingExchange(null);
    setEditingReturnItems([]);
    setEditingNewItems([]);
  };

  // Satış düzenleme modalını aç
  const openEditSaleModal = async (sale) => {
    // Önceki düzenleme state'lerini temizle
    setEditModalKey(prev => prev + 1);
    setEditingSale(null);
    setEditingItems([]);
    setEditDiscount(0);
    try {
      const response = await fetch(`${API_URL}/sales/${sale.id}`);
      const data = await response.json();
      setEditingSale(data);
      setEditingItems(data.items.map(item => ({
        ...item,
        newUnitPrice: Number(item.discounted_unit_price || item.unit_price),
        newQuantity: item.quantity
      })));
      // Sepet indirimi = ürün toplamı (indirimli fiyatlarla) - ödenen tutar
      const itemsTotal = data.items.reduce((sum, item) => {
        return sum + Number(item.discounted_unit_price || item.unit_price) * item.quantity;
      }, 0);
      const paidAmount = getSalePaidAmount(data);
      setEditDiscount(Math.max(0, Math.round((itemsTotal - paidAmount) * 100) / 100));
      setShowLastSaleModal(false);
      setRecentSalesSearch('');
      setShowEditSaleModal(true);
    } catch (error) {
      console.error('Satış detay hatası:', error);
      toast.error('Satış bilgileri yüklenemedi!');
    }
  };

  // Düzenleme fiyatını güncelle (birim fiyat değişince indirim oranını yeniden hesapla)
  const updateEditItemPrice = (itemId, newPrice) => {
    setEditingItems(editingItems.map(item => {
      if (item.id !== itemId) return item;
      const origPrice = item.newOriginalPrice || newPrice;
      const discountAmt = Math.max(0, origPrice - newPrice);
      const discountPct = origPrice > 0 ? (discountAmt / origPrice) * 100 : 0;
      return { ...item, newUnitPrice: newPrice, newDiscountAmount: discountAmt, newDiscountPercent: discountPct };
    }));
  };

  // Düzenleme: orijinal fiyat güncellenince indirimli fiyatı yeniden hesapla
  const updateEditItemOriginalPrice = (itemId, newOrigPrice) => {
    setEditingItems(editingItems.map(item => {
      if (item.id !== itemId) return item;
      const pct = item.newDiscountPercent || 0;
      const discountAmt = newOrigPrice * (pct / 100);
      const unitPrice = newOrigPrice - discountAmt;
      return { ...item, newOriginalPrice: newOrigPrice, newDiscountAmount: discountAmt, newUnitPrice: unitPrice };
    }));
  };

  // Düzenleme: indirim yüzdesi güncellenince birim fiyatı yeniden hesapla
  const updateEditItemDiscountPercent = (itemId, newPct) => {
    setEditingItems(editingItems.map(item => {
      if (item.id !== itemId) return item;
      const clampedPct = Math.max(0, Math.min(100, newPct));
      const origPrice = item.newOriginalPrice || item.newUnitPrice;
      const discountAmt = origPrice * (clampedPct / 100);
      const unitPrice = origPrice - discountAmt;
      return { ...item, newDiscountPercent: clampedPct, newDiscountAmount: discountAmt, newUnitPrice: unitPrice };
    }));
  };

  // Düzenleme: indirim tutarı güncellenince birim fiyatı yeniden hesapla
  const updateEditItemDiscountAmount = (itemId, newDiscAmt) => {
    setEditingItems(editingItems.map(item => {
      if (item.id !== itemId) return item;
      const origPrice = item.newOriginalPrice || item.newUnitPrice;
      const clampedAmt = Math.max(0, Math.min(origPrice, newDiscAmt));
      const pct = origPrice > 0 ? (clampedAmt / origPrice) * 100 : 0;
      const unitPrice = origPrice - clampedAmt;
      return { ...item, newDiscountAmount: clampedAmt, newDiscountPercent: pct, newUnitPrice: unitPrice };
    }));
  };

  // Düzenleme miktarını güncelle (stok limiti ile)
  const updateEditItemQuantity = (itemId, newQty) => {
    setEditingItems(editingItems.map(item => {
      if (item.id !== itemId) return item;
      const maxQty = item.maxQuantity || 999;
      const clampedQty = Math.max(1, Math.min(newQty, maxQty));
      if (newQty > maxQty) {
        toast.error(`${item.product_name} için maksimum ${maxQty} adet girilebilir (stokta ${item.currentStock} adet mevcut)`, { autoClose: 4500 });
      }
      return { ...item, newQuantity: clampedQty };
    }));
  };

  // Ödeme detaylarını yeni toplama eşitle
  const syncPaymentToTotal = () => {
    if (!adminEditData.payment_details || adminEditData.payment_details.length === 0) return;
    const newTotal = calculateEditTotal();
    const currentPaymentTotal = getPaymentDetailsTotal();
    if (currentPaymentTotal === 0 || adminEditData.payment_details.length === 1) {
      // Tek ödeme: doğrudan eşitle
      const updated = adminEditData.payment_details.map((p, idx) => {
        if (idx === 0) {
          return { ...p, amount: newTotal, amount_tl: (!p.currency || p.currency === 'TRY') ? newTotal : p.amount_tl };
        }
        return p;
      });
      setAdminEditData({ ...adminEditData, payment_details: updated });
    } else {
      // Çoklu ödeme: oransal dağıt
      const ratio = currentPaymentTotal > 0 ? newTotal / currentPaymentTotal : 1;
      const updated = adminEditData.payment_details.map(p => {
        const newAmt = Math.round((parseFloat(p.amount) || 0) * ratio * 100) / 100;
        return {
          ...p,
          amount: newAmt,
          amount_tl: (!p.currency || p.currency === 'TRY') ? newAmt : p.amount_tl
        };
      });
      setAdminEditData({ ...adminEditData, payment_details: updated });
    }
    toast.success('Ödeme tutarları yeni toplamla eşitlendi');
  };

  // Düzenlenen toplam hesapla
  const calculateEditTotal = () => {
    const itemsTotal = editingItems.reduce((sum, item) => sum + (item.newUnitPrice * item.newQuantity), 0);
    const adjustmentsTotal = editingAdjustments.reduce((sum, adj) => sum + (parseFloat(adj.newAmount) || 0), 0);
    // Sepet indirimi (editDiscount state'inden)
    const discount = parseFloat(editDiscount) || 0;
    // Hurda mahsubu
    const scrapTotal = editingSale ? (Number(editingSale.scrap_total_amount) || 0) : 0;
    return Math.max(0, itemsTotal - discount - scrapTotal + adjustmentsTotal);
  };

  // İade düzenleme: checkbox/adet/fiyat değişince editingItems'ı güncelle
  const syncRefundEditToItems = (updatedRefundItems) => {
    setRefundEditItems(updatedRefundItems);
    // Sadece checkbox'u seçili olan ürünleri editingItems'a aktar
    const newEditingItems = updatedRefundItems
      .filter(ri => ri.checked && ri.refundQty > 0)
      .map(ri => ({
        id: `refund_${ri.product_id}`,
        product_id: ri.product_id,
        product_name: ri.product_name,
        barcode: ri.barcode,
        quantity: ri.refundQty,
        newQuantity: ri.refundQty,
        originalQuantity: ri.refundQty,
        unit_price: ri.refundPrice,
        newUnitPrice: ri.refundPrice,
        maxQuantity: ri.maxRefundable
      }));
    setEditingItems(newEditingItems);
  };

  // Satış düzenlemesini kaydet - gerçek kaydetme işlemi
  const doSaveEditedSale = async () => {
    setSavingEdit(true);
    try {
      const newTotal = calculateEditTotal();
      const response = await fetch(`${API_URL}/sales/${editingSale.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: editingItems.map(item => ({
            id: item.id,
            product_id: item.product_id,
            unit_price: parseFloat(item.newUnitPrice),
            quantity: parseInt(item.newQuantity)
          })),
          total: newTotal,
          discount: parseFloat(editDiscount) || 0
        })
      });

      const data = await response.json();
      if (data.success) {
        // Log kaydı - satış düzenlendi (Detaylı)
        const changedItems = editingItems.filter(item =>
          item.unit_price !== parseFloat(item.newUnitPrice) || item.quantity !== parseInt(item.newQuantity)
        );
        const changeDetails = changedItems.map(item => {
          const parts = [];
          if (item.unit_price !== parseFloat(item.newUnitPrice)) {
            parts.push(`Fiyat: ₺${Number(item.unit_price).toLocaleString('tr-TR', {minimumFractionDigits: 2})} → ₺${parseFloat(item.newUnitPrice).toLocaleString('tr-TR', {minimumFractionDigits: 2})}`);
          }
          if (item.quantity !== parseInt(item.newQuantity)) {
            parts.push(`Adet: ${item.quantity} → ${parseInt(item.newQuantity)}`);
          }
          return `${item.product_name} (${parts.join(', ')})`;
        }).join(' | ');
        const logMessage = `Satış Düzenlendi #${editingSale.store_sale_number || editingSale.id} - Toplam: ₺${Number(editingSale.total).toLocaleString('tr-TR', {minimumFractionDigits: 2})} → ₺${newTotal.toLocaleString('tr-TR', {minimumFractionDigits: 2})}${changeDetails ? ' | Değişiklikler: ' + changeDetails : ''}`;
        logService.log(
          LOG_MODULES.SALES,
          LOG_ACTIONS.SALE_EDIT,
          logMessage,
          {
            entity_type: 'sale',
            entity_id: editingSale.id,
            status: 'warning',
            details: {
              sale_id: editingSale.id,
              store_sale_number: editingSale.store_sale_number,
              old_total: editingSale.total,
              new_total: newTotal,
              difference: newTotal - editingSale.total,
              customer: editingSale.customer_name ? {
                id: editingSale.customer_id,
                name: editingSale.customer_name
              } : null,
              items: editingItems.map(item => ({
                product_id: item.product_id,
                barcode: item.barcode || null,
                product_name: item.product_name,
                old_price: item.unit_price,
                new_price: parseFloat(item.newUnitPrice),
                price_changed: item.unit_price !== parseFloat(item.newUnitPrice),
                old_quantity: item.quantity,
                new_quantity: parseInt(item.newQuantity),
                quantity_changed: item.quantity !== parseInt(item.newQuantity),
                old_total: item.unit_price * item.quantity,
                new_total: parseFloat(item.newUnitPrice) * parseInt(item.newQuantity)
              }))
            }
          }
        );

        toast.success('Satış güncellendi!');
        setShowEditSaleModal(false);
        setEditingSale(null);
        setEditingItems([]);
        // Listeyi güncelle
        const currentStore = user?.role === 'admin' ? (selectedStore?.id || user?.store_id) : user?.store_id;
        await fetchRecentSales(currentStore);
        // Tüm Fişler modalı açıksa yenile
        if (showAllSalesModal) fetchAllSales(false);
        setShowLastSaleModal(true);
      } else {
        toast.error(data.error || 'Güncelleme başarısız!');
      }
    } catch (error) {
      console.error('Satış güncelleme hatası:', error);
      toast.error('Güncelleme başarısız!');
    } finally {
      setSavingEdit(false);
    }
  };

  // Satış düzenlemesini kaydet - onay modalı göster
  const saveEditedSale = () => {
    showConfirmation({
      title: 'Düzenleme Onayı',
      message: 'Stoklar güncellenmeyecek, sadece düzenleme kısmında değişiklik olacaktır. Onaylıyor musunuz?',
      confirmText: 'Onayla',
      cancelText: 'Vazgeç',
      type: 'info',
      onConfirm: doSaveEditedSale
    });
  };

  // Admin: Kapsamlı düzenleme modalını aç
  const openAdminEditModal = async (sale) => {
    // Önceki düzenleme state'lerini temizle
    setEditModalKey(prev => prev + 1);
    setEditingSale(null);
    setEditingItems([]);
    setEditingAdjustments([]);
    setEditDiscount(0);
    setRefundEditItems([]);
    setAdminEditSelectedStaff([]);
    setAdminEditData({
      customer_id: null,
      customer_name: '',
      staff_id: null,
      staff_name: '',
      payment_method: '',
      payment_details: [],
      created_at: '',
      notes: ''
    });
    setAdminCustomerSearch('');
    setAdminCustomerResults([]);
    setShowAdminCustomerDropdown(false);
    setAdminProductSearch('');
    setAdminProductResults([]);
    setShowAdminProductDropdown(false);
    try {
      // Satış sorumlularını yükle
      const staffResponse = await fetch(`${API_URL}/sales-staff${currentStoreId ? `?store_id=${currentStoreId}` : ''}`);
      const staffData = await staffResponse.json();
      setSalesStaffList(staffData);

      const response = await fetch(`${API_URL}/sales/${sale.id}`);
      const data = await response.json();
      const isRefundRecord = data.sale_type === 'refund' || Number(data.total) < 0;
      setEditingSale({ ...data, _isRefund: isRefundRecord });
      // Kalem birim fiyatları: Fiş Detayı / Değişim / İade ile AYNI kaynaktan (computeSaleItemPricing).
      // Böylece fiş düzenleme ekranındaki fiyatlar diğer ekranlarla birebir tutar. İade kayıtlarında
      // (negatif kalemler) mevcut mantık korunur.
      const editPricing = !isRefundRecord ? computeSaleItemPricing(data, []) : null;
      // Her ürün için mevcut mağaza stoğunu çek
      const saleStoreId = data.store_id || user?.store_id;
      const itemsWithStock = await Promise.all((data.items || []).map(async (item) => {
        // İade'de quantity negatif, pozitife çevir
        const absQty = Math.abs(item.quantity);
        let availableStock = 999; // fallback
        try {
          const stockRes = await fetch(`${API_URL}/store-stocks/${item.product_id}`);
          const stockData = await stockRes.json();
          const storeStock = (stockData || []).find(s => s.store_id === saleStoreId);
          availableStock = storeStock ? storeStock.stock : 0;
        } catch (e) { /* stok alınamazsa sınır koyma */ }
        // Satış: maxQuantity = satılan + kalan stok (arttırma sınırı)
        // İade: maxQuantity stok sınırı yok (iade azaltılabilir), ama orijinal satıştaki qty'den fazla iade edemez
        let maxQuantity;
        if (isRefundRecord) {
          // İade düzenlemede ürün adedini artırmak = daha fazla iade = stoğa daha fazla ürün dönecek (stok sınırı yok)
          // Ama orijinal satıştan fazla iade yapılamaz
          if (data.refund_of_sale_id) {
            try {
              const origSaleRes = await fetch(`${API_URL}/sales/${data.refund_of_sale_id}`);
              const origSaleData = await origSaleRes.json();
              const origItem = (origSaleData.items || []).find(i => i.product_id === item.product_id);
              maxQuantity = origItem ? (origItem.quantity - (origItem.returned_quantity || 0) + absQty) : absQty + 99;
            } catch { maxQuantity = absQty + 99; }
          } else {
            maxQuantity = absQty + 99;
          }
        } else {
          maxQuantity = absQty + availableStock;
        }
        const origUnit = Number(item.original_unit_price || item.unit_price) || 0;
        // Birim fiyat ortak hesaplayıcıdan (iade kaydında: eski davranış). İndirim tutarı/oranı
        // bu birim fiyattan türetilir ki modalın kendi iç hesapları tutarlı kalsın.
        const effUnit = editPricing
          ? Math.round(editPricing.itemUnitPrice(item) * 100) / 100
          : Number(item.discounted_unit_price || item.unit_price) || 0;
        const perUnitDiscount = editPricing
          ? Math.max(0, Math.round((origUnit - effUnit) * 100) / 100)
          : (absQty > 0 ? (Number(item.discount_amount) || 0) / absQty : 0);
        const discPercent = editPricing
          ? (origUnit > 0 ? Math.round((perUnitDiscount / origUnit) * 100 * 100) / 100 : 0)
          : Number(item.discount_percent) || 0;
        return {
          ...item,
          quantity: absQty,
          newOriginalPrice: origUnit,
          newDiscountPercent: discPercent,
          newDiscountAmount: perUnitDiscount,
          newUnitPrice: effUnit,
          newQuantity: absQty,
          originalQuantity: absQty,
          currentStock: availableStock,
          maxQuantity: maxQuantity
        };
      }));
      setEditingItems(itemsWithStock);

      // İade düzenleme: orijinal satış ürünlerini yükle (checkbox listesi için)
      if (isRefundRecord && data.refund_of_sale_id) {
        try {
          const origRes = await fetch(`${API_URL}/sales/${data.refund_of_sale_id}`);
          const origData = await origRes.json();
          const origItems = (origData.items || []).map(origItem => {
            // Bu ürün mevcut iade fişinde var mı?
            const refundedItem = itemsWithStock.find(ri => ri.product_id === origItem.product_id);
            const soldQty = Math.abs(origItem.quantity);
            const alreadyReturned = origItem.returned_quantity || 0;
            // Bu iade fişindeki miktar (düzenlenen)
            const currentRefundQty = refundedItem ? refundedItem.newQuantity : 0;
            // Diğer iadelerden dönen miktar (bu fiş hariç)
            const otherReturned = Math.max(0, alreadyReturned - currentRefundQty);
            const maxRefundable = soldQty - otherReturned;
            return {
              product_id: origItem.product_id,
              product_name: origItem.product_name,
              barcode: origItem.barcode,
              image: origItem.image || null,
              soldQuantity: soldQty,
              maxRefundable: maxRefundable,
              checked: !!refundedItem,
              refundQty: currentRefundQty,
              refundPrice: refundedItem ? refundedItem.newUnitPrice : (parseFloat(origItem.unit_price) || 0),
              originalUnitPrice: parseFloat(origItem.unit_price) || 0
            };
          });
          setRefundEditItems(origItems);
        } catch (e) {
          console.error('Orijinal satış yüklenemedi:', e);
          setRefundEditItems([]);
        }
      } else {
        setRefundEditItems([]);
      }

      // Fark kalemlerini yükle (adjustment_items veya receipt_data.adjustmentItems fallback)
      let adjustments = [];
      if (data.adjustment_items) {
        adjustments = typeof data.adjustment_items === 'string'
          ? JSON.parse(data.adjustment_items)
          : data.adjustment_items;
      }
      // Fallback: receipt_data içinden adjustmentItems çek
      if ((!adjustments || adjustments.length === 0) && data.receipt_data) {
        try {
          const receiptData = typeof data.receipt_data === 'string'
            ? JSON.parse(data.receipt_data)
            : data.receipt_data;
          if (receiptData.adjustmentItems && receiptData.adjustmentItems.length > 0) {
            adjustments = receiptData.adjustmentItems;
          } else if (receiptData.adjustment_items && receiptData.adjustment_items.length > 0) {
            adjustments = receiptData.adjustment_items;
          }
        } catch (e) {
          console.error('Receipt data parse hatası:', e);
        }
      }
      setEditingAdjustments((adjustments || []).map((adj, idx) => ({
        ...adj,
        id: idx,
        amount: adj.amount ?? adj.refund_amount ?? adj.original_amount ?? 0,
        newAmount: adj.amount ?? adj.refund_amount ?? adj.original_amount ?? 0
      })));

      // Admin edit data'yı doldur (local timezone ile)
      const saleDate = new Date(data.created_at);
      const year = saleDate.getFullYear();
      const month = String(saleDate.getMonth() + 1).padStart(2, '0');
      const day = String(saleDate.getDate()).padStart(2, '0');
      const hours = String(saleDate.getHours()).padStart(2, '0');
      const minutes = String(saleDate.getMinutes()).padStart(2, '0');
      const formattedDate = `${year}-${month}-${day}T${hours}:${minutes}`; // datetime-local format

      // payment_details'ı parse et
      let paymentDetails = [];
      if (data.payment_details) {
        paymentDetails = typeof data.payment_details === 'string'
          ? JSON.parse(data.payment_details)
          : data.payment_details;
      }

      // İade'de payment_details tutarlarını pozitife çevir
      if (isRefundRecord && paymentDetails.length > 0) {
        paymentDetails = paymentDetails.map(p => ({
          ...p,
          amount: Math.abs(parseFloat(p.amount) || 0),
          amount_tl: Math.abs(parseFloat(p.amount_tl) || 0)
        }));
      }

      // Eğer payment_details boşsa ama payment_method varsa, toplam tutarla bir detay oluştur
      if ((!paymentDetails || paymentDetails.length === 0) && data.payment_method && data.total) {
        paymentDetails = [{
          method: data.payment_method,
          currency: 'TRY',
          amount: Math.abs(parseFloat(data.total)),
          amount_tl: Math.abs(parseFloat(data.total))
        }];
      }

      // Banka hesabını payment_details'dan çek (Havale/EFT ise)
      const existingBankAccount = paymentDetails.find(p => p.bank_account || p.bankAccount);

      setAdminEditData({
        customer_id: data.customer_id || null,
        customer_name: data.customer_name || '',
        staff_id: data.staff_id || null,
        staff_name: data.staff_name || '',
        payment_method: data.payment_method || '',
        payment_details: Array.isArray(paymentDetails) ? paymentDetails : [],
        created_at: formattedDate,
        notes: data.notes || '',
        bank_account: existingBankAccount?.bank_account || existingBankAccount?.bankAccount || ''
      });

      // Banka hesaplarını yükle (Havale/EFT düzenlemesi için)
      await fetchBankAccounts();

      // Mevcut personelleri staffData'dan eşleştir.
      // Önce staff_id ile eşleştir (isimde boşluk/büyük-küçük harf farkı ya da personel
      // adı sonradan değiştiyse isim eşleşmesi sessizce boş dönüp plasiyeri "seçilmemiş"
      // gösterebiliyordu — id doğruysa artık her zaman bulunur). İsim eşleşmesi, staff_id
      // yakalayamadığı ek personelleri (çoklu seçim) veya staff_id geçersizse tek başvuruyu tamamlar.
      if (staffData.length > 0) {
        const byId = data.staff_id != null ? staffData.filter(s => s.id === data.staff_id) : [];
        const existingNames = (data.staff_name || '').split(',').map(n => n.trim().toLowerCase()).filter(Boolean);
        const byName = staffData.filter(s => existingNames.includes(s.name.trim().toLowerCase()));
        const matched = [...byId, ...byName].filter((s, idx, arr) => arr.findIndex(x => x.id === s.id) === idx);
        setAdminEditSelectedStaff(matched);
      } else {
        setAdminEditSelectedStaff([]);
      }

      // Sepet indirimi hesapla (toplam indirim - ürün bazlı indirimler)
      // DB'deki item.discount_amount kalem-toplam olduğu için newQuantity ile çarpmıyoruz.
      const totalSaleDiscount = Number(data.discount) || 0;
      const itemLevelDiscountsSum = itemsWithStock.reduce((sum, item) => sum + (Number(item.discount_amount) || 0), 0);
      const cartDiscount = Math.max(0, Math.round((totalSaleDiscount - itemLevelDiscountsSum) * 100) / 100);
      setEditDiscount(cartDiscount);

      setShowLastSaleModal(false);
      setRecentSalesSearch('');
      setShowAdminEditModal(true);
      setAdminCustomerSearch('');
      setAdminCustomerResults([]);
      setShowAdminCustomerDropdown(false);
    } catch (error) {
      console.error('Satış detay hatası:', error);
      toast.error('Satış bilgileri yüklenemedi!');
    }
  };

  // Admin: Değişim/İade düzenleme modalını aç
  const openExchangeEditModal = async (exchange) => {
    setEditModalKey(prev => prev + 1);
    setEditingExchange(null);
    setEditingReturnItems([]);
    setEditingNewItems([]);
    try {
      // Satış sorumlularını yükle
      const staffResponse = await fetch(`${API_URL}/sales-staff`);
      const staffData = await staffResponse.json();
      setSalesStaffList(staffData);

      // Exchange detaylarını getir (exchange_id kullan, id "E123" formatında olabilir)
      const exchangeId = exchange.exchange_id || exchange.id;
      const response = await fetch(`${API_URL}/exchanges/${exchangeId}`);
      const data = await response.json();

      setEditingExchange(data);

      // Tarih formatla
      const exchangeDate = new Date(data.created_at);
      const year = exchangeDate.getFullYear();
      const month = String(exchangeDate.getMonth() + 1).padStart(2, '0');
      const day = String(exchangeDate.getDate()).padStart(2, '0');
      const hours = String(exchangeDate.getHours()).padStart(2, '0');
      const minutes = String(exchangeDate.getMinutes()).padStart(2, '0');
      const formattedDate = `${year}-${month}-${day}T${hours}:${minutes}`;

      // Staff bilgilerini parse et (virgülle ayrılmış olabilir)
      const staffNames = data.staff_name ? data.staff_name.split(',').map(n => n.trim()).filter(Boolean) : [];
      const matchedStaffIds = staffNames.map(name => {
        const found = staffData.find(s => s.name === name);
        return found ? found.id : null;
      }).filter(Boolean);

      // payment_details'i parse et
      let exchPaymentDetails = [];
      if (data.payment_details) {
        try {
          exchPaymentDetails = typeof data.payment_details === 'string'
            ? JSON.parse(data.payment_details)
            : data.payment_details;
        } catch (e) {
          exchPaymentDetails = [];
        }
      }
      // payment_details yoksa tek satır türet (eski kayıtlar için)
      if (!Array.isArray(exchPaymentDetails) || exchPaymentDetails.length === 0) {
        const fallbackAmount = Math.abs(Number(data.return_total) || Number(data.difference) || 0);
        exchPaymentDetails = [{
          method: data.payment_method || 'Nakit İade',
          currency: 'TRY',
          amount: fallbackAmount,
          amount_tl: fallbackAmount
        }];
      } else {
        // Tutarları pozitife çevir
        exchPaymentDetails = exchPaymentDetails.map(p => ({
          ...p,
          amount: Math.abs(parseFloat(p.amount) || 0),
          amount_tl: Math.abs(parseFloat(p.amount_tl) || 0)
        }));
      }

      setExchangeEditData({
        customer_id: data.customer_id || null,
        customer_name: data.customer_name || '',
        staff_ids: matchedStaffIds.length > 0 ? matchedStaffIds : (data.staff_id ? [data.staff_id] : []),
        staff_names: staffNames.length > 0 ? staffNames : (data.staff_name ? [data.staff_name] : []),
        payment_method: data.payment_method || '',
        payment_details: exchPaymentDetails,
        created_at: formattedDate,
        notes: data.notes || ''
      });

      // Banka hesaplarını yükle (Havale/EFT düzenlemesi için)
      await fetchBankAccounts();

      // Ürün listelerini düzenlenebilir hale getir
      setEditingReturnItems((data.return_items || []).map(item => ({
        ...item,
        newQuantity: item.quantity,
        newUnitPrice: item.unit_price
      })));
      setEditingNewItems((data.new_items || []).map(item => ({
        ...item,
        newQuantity: item.quantity,
        newUnitPrice: item.unit_price
      })));

      setShowRefundDetailModal(false);
      setShowLastSaleModal(false);
      setRecentSalesSearch('');
      setShowExchangeEditModal(true);
      setAdminCustomerSearch('');
      setAdminCustomerResults([]);
      setShowAdminCustomerDropdown(false);
    } catch (error) {
      console.error('Değişim/İade detay hatası:', error);
      toast.error('Kayıt bilgileri yüklenemedi!');
    }
  };

  // Admin: Değişim/İade kaydını kaydet - gerçek kaydetme işlemi
  const doSaveExchangeEdit = async () => {
    if (!editingExchange) return;

    setSavingExchangeEdit(true);
    try {
      // Ürün listelerini hazırla
      const returnItemsData = editingReturnItems.map(item => ({
        product_id: item.product_id,
        product_name: item.product_name,
        quantity: item.newQuantity,
        unit_price: item.newUnitPrice
      }));

      const newItemsData = editingNewItems.map(item => ({
        product_id: item.product_id,
        product_name: item.product_name,
        quantity: item.newQuantity,
        unit_price: item.newUnitPrice
      }));

      // payment_details'i hazırla: iade için tutarları negatife çevir (saklama formatı negatif)
      const isRefundTypeForSave = editingExchange.type === 'refund';
      const paymentDetailsToSave = (exchangeEditData.payment_details || [])
        .filter(p => p && p.method && (parseFloat(p.amount) || 0) > 0)
        .map(p => {
          const amt = parseFloat(p.amount) || 0;
          const amtTl = parseFloat(p.amount_tl) || amt;
          const sign = isRefundTypeForSave ? -1 : 1;
          return {
            method: p.method,
            currency: p.currency || 'TRY',
            amount: sign * Math.abs(amt),
            amount_tl: sign * Math.abs(amtTl),
            ...(p.bank_account || p.bankAccount ? { bank_account: p.bank_account || p.bankAccount } : {})
          };
        });

      // Birden fazla ödeme yöntemi varsa payment_method = "Karışık"
      const computedPaymentMethod = paymentDetailsToSave.length > 1
        ? 'Karışık'
        : (paymentDetailsToSave[0]?.method || exchangeEditData.payment_method);

      const response = await fetch(`${API_URL}/exchanges/${editingExchange.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_id: exchangeEditData.customer_id,
          customer_name: exchangeEditData.customer_name,
          staff_id: exchangeEditData.staff_ids[0] || null,
          staff_name: [...exchangeEditData.staff_names].sort((a, b) => a.localeCompare(b, 'tr')).join(', ') || null,
          payment_method: computedPaymentMethod,
          payment_details: paymentDetailsToSave,
          notes: exchangeEditData.notes,
          created_at: exchangeEditData.created_at,
          return_items: returnItemsData,
          new_items: newItemsData
        })
      });

      const result = await response.json();

      if (result.success) {
        // Log kaydı - Değişim/İade düzenleme (Detaylı)
        const isRefundType = editingExchange.type === 'refund';
        const typeLabel = isRefundType ? 'İade' : 'Değişim';

        // İade edilen ürünlerdeki değişiklikleri tespit et
        const returnChanges = editingReturnItems.filter(item => {
          const orig = (editingExchange.return_items || []).find(o => o.product_id === item.product_id);
          return orig && (orig.quantity !== item.newQuantity || parseFloat(orig.unit_price) !== item.newUnitPrice);
        }).map(item => {
          const orig = editingExchange.return_items.find(o => o.product_id === item.product_id);
          const parts = [];
          if (orig.quantity !== item.newQuantity) parts.push(`Adet: ${orig.quantity} → ${item.newQuantity}`);
          if (parseFloat(orig.unit_price) !== item.newUnitPrice) parts.push(`Fiyat: ₺${Number(orig.unit_price).toLocaleString('tr-TR', {minimumFractionDigits: 2})} → ₺${item.newUnitPrice.toLocaleString('tr-TR', {minimumFractionDigits: 2})}`);
          return `${item.product_name} (${parts.join(', ')})`;
        });

        // Yeni alınan ürünlerdeki değişiklikleri tespit et
        const newChanges = editingNewItems.filter(item => {
          const orig = (editingExchange.new_items || []).find(o => o.product_id === item.product_id);
          return orig && (orig.quantity !== item.newQuantity || parseFloat(orig.unit_price) !== item.newUnitPrice);
        }).map(item => {
          const orig = editingExchange.new_items.find(o => o.product_id === item.product_id);
          const parts = [];
          if (orig.quantity !== item.newQuantity) parts.push(`Adet: ${orig.quantity} → ${item.newQuantity}`);
          if (parseFloat(orig.unit_price) !== item.newUnitPrice) parts.push(`Fiyat: ₺${Number(orig.unit_price).toLocaleString('tr-TR', {minimumFractionDigits: 2})} → ₺${item.newUnitPrice.toLocaleString('tr-TR', {minimumFractionDigits: 2})}`);
          return `${item.product_name} (${parts.join(', ')})`;
        });

        // Genel bilgi değişiklikleri
        const infoParts = [];
        if (exchangeEditData.payment_method !== (editingExchange.payment_method || '')) infoParts.push(`Ödeme: ${editingExchange.payment_method || '-'} → ${exchangeEditData.payment_method}`);
        if ((exchangeEditData.customer_id || null) !== (editingExchange.customer_id || null)) infoParts.push(`Müşteri değişti`);
        const newStaffName = [...exchangeEditData.staff_names].sort((a, b) => a.localeCompare(b, 'tr')).join(', ');
        if (newStaffName !== (editingExchange.staff_name || '')) infoParts.push(`Personel değişti`);

        const allChanges = [...returnChanges.map(c => `İade: ${c}`), ...newChanges.map(c => `Yeni: ${c}`), ...infoParts];
        const logMessage = `${typeLabel} Düzenlendi #${editingExchange.id}${allChanges.length > 0 ? ' | ' + allChanges.join(' | ') : ''}`;

        logService.log(
          LOG_MODULES.SALES,
          LOG_ACTIONS.SALE_EDIT,
          logMessage,
          {
            entity_type: isRefundType ? 'refund' : 'exchange',
            entity_id: editingExchange.id,
            status: 'warning',
            details: {
              exchange_id: editingExchange.id,
              type: editingExchange.type,
              customer: exchangeEditData.customer_name ? { id: exchangeEditData.customer_id, name: exchangeEditData.customer_name } : null,
              return_items: editingReturnItems.map(item => {
                const orig = (editingExchange.return_items || []).find(o => o.product_id === item.product_id);
                return {
                  product_id: item.product_id,
                  product_name: item.product_name,
                  old_quantity: orig ? orig.quantity : item.newQuantity,
                  new_quantity: item.newQuantity,
                  quantity_changed: orig ? orig.quantity !== item.newQuantity : false,
                  old_price: orig ? parseFloat(orig.unit_price) : item.newUnitPrice,
                  new_price: item.newUnitPrice,
                  price_changed: orig ? parseFloat(orig.unit_price) !== item.newUnitPrice : false
                };
              }),
              new_items: editingNewItems.map(item => {
                const orig = (editingExchange.new_items || []).find(o => o.product_id === item.product_id);
                return {
                  product_id: item.product_id,
                  product_name: item.product_name,
                  old_quantity: orig ? orig.quantity : item.newQuantity,
                  new_quantity: item.newQuantity,
                  quantity_changed: orig ? orig.quantity !== item.newQuantity : false,
                  old_price: orig ? parseFloat(orig.unit_price) : item.newUnitPrice,
                  new_price: item.newUnitPrice,
                  price_changed: orig ? parseFloat(orig.unit_price) !== item.newUnitPrice : false
                };
              })
            }
          }
        );

        toast.success('Kayıt güncellendi!');
        setShowExchangeEditModal(false);
        setEditingExchange(null);
        setEditingReturnItems([]);
        setEditingNewItems([]);
        // Son satışları yenile
        fetchRecentSales(selectedStore?.id || user?.store_id);
        if (showAllSalesModal) fetchAllSales(false);
      } else {
        toast.error(result.error || 'Güncelleme başarısız!');
      }
    } catch (error) {
      console.error('Güncelleme hatası:', error);
      toast.error('Güncelleme sırasında hata oluştu!');
    } finally {
      setSavingExchangeEdit(false);
    }
  };

  // Admin: Değişim/İade kaydını kaydet - onay modalı göster
  const saveExchangeEdit = () => {
    if (!editingExchange) return;

    showConfirmation({
      title: 'Düzenleme Onayı',
      message: 'Stoklar güncellenmeyecek, sadece düzenleme kısmında değişiklik olacaktır. Onaylıyor musunuz?',
      confirmText: 'Onayla',
      cancelText: 'Vazgeç',
      type: 'info',
      onConfirm: doSaveExchangeEdit
    });
  };

  // Exchange düzenleme: İade ürün fiyatını güncelle
  const updateReturnItemPrice = (productId, newPrice) => {
    setEditingReturnItems(items =>
      items.map(item =>
        item.product_id === productId
          ? { ...item, newUnitPrice: newPrice }
          : item
      )
    );
  };

  // Exchange düzenleme: İade ürün adedini güncelle
  const updateReturnItemQuantity = (productId, newQty) => {
    if (newQty < 1) return;
    setEditingReturnItems(items =>
      items.map(item =>
        item.product_id === productId
          ? { ...item, newQuantity: newQty }
          : item
      )
    );
  };

  // Exchange düzenleme: Yeni ürün fiyatını güncelle
  const updateNewItemPrice = (productId, newPrice) => {
    setEditingNewItems(items =>
      items.map(item =>
        item.product_id === productId
          ? { ...item, newUnitPrice: newPrice }
          : item
      )
    );
  };

  // Exchange düzenleme: Yeni ürün adedini güncelle
  const updateNewItemQuantity = (productId, newQty) => {
    if (newQty < 1) return;
    setEditingNewItems(items =>
      items.map(item =>
        item.product_id === productId
          ? { ...item, newQuantity: newQty }
          : item
      )
    );
  };

  // Exchange düzenleme: İade toplamını hesapla
  const calculateReturnTotal = () => {
    return editingReturnItems.reduce((sum, item) => sum + (item.newUnitPrice * item.newQuantity), 0);
  };

  // Exchange düzenleme: Yeni ürün toplamını hesapla
  const calculateNewItemsTotal = () => {
    return editingNewItems.reduce((sum, item) => sum + (item.newUnitPrice * item.newQuantity), 0);
  };

  // Admin: Müşteri listesini yükle
  const loadAdminCustomerList = async () => {
    try {
      const response = await fetch(`${API_URL}/customers?limit=500`);
      const data = await response.json();
      setAdminCustomerResults(data.customers || data || []);
      setShowAdminCustomerDropdown(true);
    } catch (error) {
      console.error('Müşteri listesi yükleme hatası:', error);
    }
  };

  // Admin: Müşteri arama
  const searchAdminCustomer = async (query) => {
    setAdminCustomerSearch(query);
    if (query.length < 1) {
      loadAdminCustomerList();
      return;
    }
    try {
      const response = await fetch(`${API_URL}/customers/search/${encodeURIComponent(query)}`);
      const data = await response.json();
      setAdminCustomerResults(Array.isArray(data) ? data : (data.customers || []));
      setShowAdminCustomerDropdown(true);
    } catch (error) {
      console.error('Müşteri arama hatası:', error);
    }
  };

  // Admin: Müşteri seç
  const selectAdminCustomer = (customer) => {
    setAdminEditData({
      ...adminEditData,
      customer_id: customer.id,
      customer_name: customer.name
    });
    setAdminCustomerSearch('');
    setShowAdminCustomerDropdown(false);
  };

  // Admin: Müşteriyi kaldır
  const clearAdminCustomer = () => {
    setAdminEditData({
      ...adminEditData,
      customer_id: null,
      customer_name: ''
    });
    setAdminCustomerSearch('');
  };

  // Admin: Ürün arama
  const searchAdminProduct = async (query) => {
    setAdminProductSearch(query);
    if (query.length < 2) {
      setAdminProductResults([]);
      setShowAdminProductDropdown(false);
      return;
    }
    try {
      const storeId = editingSale?.store_id || user?.store_id;
      const response = await fetch(`${API_URL}/products/search/${encodeURIComponent(query)}?store_id=${storeId}`);
      const data = await response.json();
      // Zaten sepette olan ürünleri filtrele
      const existingProductIds = editingItems.map(item => item.product_id);
      const filteredProducts = (data.products || data || []).filter(p => !existingProductIds.includes(p.id));
      setAdminProductResults(filteredProducts);
      setShowAdminProductDropdown(true);
    } catch (error) {
      console.error('Ürün arama hatası:', error);
    }
  };

  // Admin: Ürün ekle (stok kontrolü ile)
  const addAdminProduct = async (product) => {
    // Mevcut stoku kontrol et
    const saleStoreId = editingSale?.store_id || user?.store_id;
    let availableStock = 999;
    try {
      const stockRes = await fetch(`${API_URL}/store-stocks/${product.id}`);
      const stockData = await stockRes.json();
      const storeStock = (stockData || []).find(s => s.store_id === saleStoreId);
      availableStock = storeStock ? storeStock.stock : 0;
    } catch (e) { /* stok alınamazsa sınır koyma */ }

    if (availableStock <= 0) {
      toast.error(`${product.name} için stokta ürün bulunmuyor!`, { autoClose: 4500 });
      return;
    }

    // Metal türüne göre fiyat hesapla (gramaj/formül bazlı ürünler için sell_price 0 olabilir)
    const isGoldProduct = product.metal_type === 'gold';
    const isGumushAksesuar = product.metal_type === 'aksesuar' && product.category === 'Gümüş Aksesuar';
    const isSilverProduct = product.metal_type === 'silver' || isGumushAksesuar;

    let productPrice;
    if (isGoldProduct) {
      productPrice = calculateGoldPrice(product);
    } else if (isSilverProduct) {
      productPrice = calculateSilverPrice(product);
    } else {
      productPrice = Number(product.sell_price || product.price || 0);
      // Para birimi dönüşümü yap (USD veya EUR ise TL'ye çevir)
      if (product.currency === 'USD') {
        productPrice = productPrice * getSatisRate('USD');
      } else if (product.currency === 'EUR') {
        productPrice = productPrice * getSatisRate('EUR');
      }
    }

    const newItem = {
      id: `new-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      product_id: product.id,
      product_name: product.name,
      barcode: product.barcode || null,
      image: product.image || null,
      unit_price: productPrice,
      quantity: 1,
      newOriginalPrice: productPrice,
      newDiscountPercent: 0,
      newDiscountAmount: 0,
      newUnitPrice: productPrice,
      newQuantity: 1,
      isNew: true,
      originalQuantity: 0,
      currentStock: availableStock,
      maxQuantity: availableStock
    };
    setEditingItems([...editingItems, newItem]);
    setAdminProductSearch('');
    setAdminProductResults([]);
    setShowAdminProductDropdown(false);
  };

  // Admin: Barkod ile ürün ekle (Enter tuşu)
  const handleAdminProductKeyDown = async (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const query = adminProductSearch.trim();
      if (!query) return;

      // Önce dropdown'daki sonuçlarda tam barkod eşleşmesi ara
      const exactMatch = adminProductResults.find(p => p.barcode === query);
      if (exactMatch) {
        addAdminProduct(exactMatch);
        return;
      }

      // Dropdown'da sonuç varsa ilkini ekle
      if (adminProductResults.length > 0) {
        addAdminProduct(adminProductResults[0]);
        return;
      }

      // Henüz arama yapılmadıysa barkod ile ara
      try {
        const storeId = editingSale?.store_id || user?.store_id;
        const response = await fetch(`${API_URL}/products/search/${encodeURIComponent(query)}?store_id=${storeId}`);
        const data = await response.json();
        const products = data.products || data || [];

        // Tam barkod eşleşmesi
        const barcodeMatch = products.find(p => p.barcode === query);
        if (barcodeMatch) {
          addAdminProduct(barcodeMatch);
        } else if (products.length > 0) {
          addAdminProduct(products[0]);
        } else {
          toast.error('Ürün bulunamadı!');
        }
      } catch (error) {
        console.error('Ürün arama hatası:', error);
        toast.error('Ürün aranamadı!');
      }
    }
  };

  // Admin: Ürün sil — sepet kalemi id'si üzerinden sil (product_id kişiye özel ürünlerde tekrar edebilir)
  const removeAdminProduct = (itemId) => {
    setEditingItems(editingItems.filter(item => item.id !== itemId));
  };

  // Admin: Ödeme detayları toplamını hesapla
  const getPaymentDetailsTotal = () => {
    if (!adminEditData.payment_details || adminEditData.payment_details.length === 0) return 0;
    return adminEditData.payment_details.reduce((sum, p) => sum + (parseFloat(p.amount_tl) || parseFloat(p.amount) || 0), 0);
  };

  // Admin: Stok değişikliklerini hesapla (önizleme için)
  const getStockChanges = () => {
    if (!editingSale || !editingItems) return [];
    const changes = [];
    const originalItems = editingSale.items || [];
    const isRefund = editingSale._isRefund;

    // Miktar değişen ve kaldırılan ürünler
    originalItems.forEach(origItem => {
      const origAbsQty = Math.abs(origItem.quantity);
      const newItem = editingItems.find(i => i.product_id === origItem.product_id);
      if (!newItem) {
        if (isRefund) {
          // İade kaydında ürün kaldırılınca stok AZALIR (iade geri alınıyor)
          changes.push({ name: origItem.product_name, diff: origAbsQty, type: 'düşüm', detail: `kaldırıldı, -${origAbsQty} adet stoktan düşülecek (iade geri alındı)` });
        } else {
          changes.push({ name: origItem.product_name, diff: origAbsQty, type: 'iade', detail: `kaldırıldı, +${origAbsQty} adet stoğa iade` });
        }
      } else {
        const qtyDiff = origAbsQty - newItem.newQuantity;
        if (qtyDiff > 0) {
          if (isRefund) {
            // İade azaldı → stok azalır
            changes.push({ name: origItem.product_name, diff: qtyDiff, type: 'düşüm', detail: `${origAbsQty} → ${newItem.newQuantity}, -${qtyDiff} adet stoktan düşülecek` });
          } else {
            changes.push({ name: origItem.product_name, diff: qtyDiff, type: 'iade', detail: `${origAbsQty} → ${newItem.newQuantity}, +${qtyDiff} adet stoğa iade` });
          }
        } else if (qtyDiff < 0) {
          if (isRefund) {
            // İade arttı → stok artar
            changes.push({ name: origItem.product_name, diff: Math.abs(qtyDiff), type: 'iade', detail: `${origAbsQty} → ${newItem.newQuantity}, +${Math.abs(qtyDiff)} adet stoğa iade` });
          } else {
            changes.push({ name: origItem.product_name, diff: Math.abs(qtyDiff), type: 'düşüm', detail: `${origAbsQty} → ${newItem.newQuantity}, -${Math.abs(qtyDiff)} adet stoktan düşülecek` });
          }
        }
      }
    });

    // Yeni eklenen ürünler
    editingItems.filter(i => i.isNew).forEach(newItem => {
      if (isRefund) {
        // İade'de yeni ürün eklenmesi → stok artar (daha fazla iade)
        changes.push({ name: newItem.product_name, diff: newItem.newQuantity, type: 'iade', detail: `yeni eklendi, +${newItem.newQuantity} adet stoğa iade` });
      } else {
        changes.push({ name: newItem.product_name, diff: newItem.newQuantity, type: 'düşüm', detail: `yeni eklendi, -${newItem.newQuantity} adet stoktan düşülecek` });
      }
    });

    return changes;
  };

  // Admin: Kapsamlı düzenlemeyi kaydet - gerçek kaydetme işlemi
  const doSaveAdminEdit = async (debtUpdate = null) => {
    setSavingEdit(true);
    try {
      const newTotal = calculateEditTotal();
      // Fark kalemlerini hazırla
      const adjustmentItemsToSave = editingAdjustments.length > 0
        ? editingAdjustments.map(adj => ({
            reason: adj.reason || 'Fark',
            description: adj.description || '',
            amount: parseFloat(adj.newAmount) || 0
          }))
        : null;
      const adjustmentTotalToSave = editingAdjustments.reduce((sum, adj) => sum + (parseFloat(adj.newAmount) || 0), 0);

      const response = await fetch(`${API_URL}/sales/${editingSale.id}/admin`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_id: adminEditData.customer_id,
          customer_name: adminEditData.customer_name,
          staff_id: adminEditSelectedStaff[0]?.id || null,
          staff_name: adminEditSelectedStaff.length > 0
            ? [...adminEditSelectedStaff].sort((a, b) => a.name.localeCompare(b.name, 'tr')).map(s => s.name).join(', ')
            : null,
          payment_method: adminEditData.payment_method,
          payment_details: adminEditData.payment_details,
          created_at: adminEditData.created_at,
          notes: adminEditData.notes,
          items: editingItems.map(item => ({
            // Satır id'si: aynı product_id'li (kişiye özel) kalemleri backend'de
            // satır bazlı eşleştirebilmek için gönderiyoruz (yeni kalemlerde 'new-...' string).
            id: item.id,
            product_id: item.product_id,
            unit_price: parseFloat(item.newUnitPrice),
            original_unit_price: parseFloat(item.newOriginalPrice || item.newUnitPrice),
            // newDiscountAmount per-unit; DB kolonu kalem-toplam olduğu için adetle çarpıyoruz.
            discount_amount: (parseFloat(item.newDiscountAmount) || 0) * parseInt(item.newQuantity),
            discounted_unit_price: parseFloat(item.newUnitPrice),
            discount_percent: parseFloat(item.newDiscountPercent) || 0,
            quantity: parseInt(item.newQuantity)
          })),
          subtotal: editingItems.reduce((sum, item) => sum + ((item.newOriginalPrice || item.newUnitPrice) * item.newQuantity), 0),
          discount: (() => {
            const itemDiscounts = editingItems.reduce((sum, item) => sum + ((item.newDiscountAmount || 0) * item.newQuantity), 0);
            const cartDiscountVal = parseFloat(editDiscount) || 0;
            return itemDiscounts + cartDiscountVal;
          })(),
          adjustment_items: adjustmentItemsToSave,
          adjustment_total: adjustmentTotalToSave > 0 ? adjustmentTotalToSave : null,
          total: newTotal,
          debt_update: debtUpdate
        })
      });

      const data = await response.json();
      if (data.success) {
        toast.success('Satış başarıyla güncellendi! (Stok, müşteri ve borçlar güncellendi)');
        setShowAdminEditModal(false);
        setEditingSale(null);
        setEditingItems([]);
        setEditingAdjustments([]);
        setShowDebtUpdateModal(false);
        setPendingAdminSave(null);
        const currentStore = user?.role === 'admin' ? (selectedStore?.id || user?.store_id) : user?.store_id;
        await fetchRecentSales(currentStore);
        // Tüm Fişler modalı açıksa yenile
        if (showAllSalesModal) fetchAllSales(false);
      } else {
        toast.error(data.error || 'Güncelleme başarısız!');
      }
    } catch (error) {
      console.error('Admin satış güncelleme hatası:', error);
      toast.error('Güncelleme başarısız!');
    } finally {
      setSavingEdit(false);
    }
  };

  // Admin: Kapsamlı düzenlemeyi kaydet - onay modalı göster
  const saveAdminEdit = async () => {
    const newTotal = calculateEditTotal();
    const paymentTotal = getPaymentDetailsTotal();

    // 1. GRAM/GUMUS borç kontrolü - bu satışa bağlı metal borç var mı?
    const newPaymentMethod = adminEditData.payment_method || '';
    const oldTotal = parseFloat(editingSale.total) || 0;
    const totalChanged = Math.abs(oldTotal - newTotal) > 0.01;
    const isAcikHesap = newPaymentMethod.includes('Açık Hesap');
    const hasAcikHesapInDetails = adminEditData.payment_details?.some(p => (p.method || '').includes('Açık Hesap'));

    if (isAcikHesap || hasAcikHesapInDetails) {
      // Satışa bağlı borç bilgisini kontrol et
      try {
        const debtResponse = await fetch(`${API_URL}/customers/${adminEditData.customer_id || editingSale.customer_id}/debts`);
        const debtData = await debtResponse.json();
        const saleDebts = (debtData || []).filter(d => d.sale_id === editingSale.id && !d.is_paid);

        // Metal borç var mı? (GRAM veya GUMUS)
        const metalDebt = saleDebts.find(d => d.debt_type === 'GRAM' || d.debt_type === 'GUMUS');
        if (metalDebt) {
          // Modal sadece ödeme HÂLÂ aynı metal cinsinden açık hesapsa anlamlı (admin tutar/kuru
          // elden düzeltsin diye). Ödeme TL'ye ya da tamamen nakit/karta çevrildiyse burada
          // sormaya gerek yok - backend eski metal borcunu otomatik kapatıp yeni durumu yazacak.
          const metalWords = metalDebt.debt_type === 'GRAM' ? ['gram', 'altın'] : ['gümüş'];
          const details = adminEditData.payment_details || [];
          const stillSameMetalAcikHesap = details.length > 0
            ? details.some(p => {
                const m = (p.method || '').toLowerCase();
                return m.includes('açık') && metalWords.some(k => m.includes(k));
              })
            : (isAcikHesap && metalWords.some(k => newPaymentMethod.toLowerCase().includes(k)));

          if (stillSameMetalAcikHesap) {
            // Metal borç hâlâ geçerli - manuel güncelleme modalı göster
            setDebtUpdateData({
              amount: metalDebt.amount || '',
              amount_tl: metalDebt.amount_tl || '',
              gold_rate: metalDebt.gold_rate || '',
              debt_type: metalDebt.debt_type
            });
            setPendingAdminSave(true);
            setShowDebtUpdateModal(true);
            return;
          }
        }
      } catch (err) {
        console.error('Borç kontrol hatası:', err);
      }
    }

    // 3. Değişikliklerin özetini oluştur
    const changes = [];
    const stockChanges = getStockChanges();

    if (totalChanged) {
      changes.push(`Toplam: ₺${oldTotal.toLocaleString('tr-TR')} → ₺${newTotal.toLocaleString('tr-TR')}`);
    }
    if (editingSale.customer_id !== adminEditData.customer_id) {
      changes.push(`Müşteri değiştirildi`);
    }
    if (stockChanges.length > 0) {
      changes.push(`Stok Değişiklikleri:\n${stockChanges.map(c => `  • ${c.name}: ${c.detail}`).join('\n')}`);
    }

    const summaryMessage = changes.length > 0
      ? `Aşağıdaki değişiklikler uygulanacak:\n\n${changes.join('\n\n')}\n\nStoklar, müşteri istatistikleri ve borçlar otomatik güncellenecektir. Onaylıyor musunuz?`
      : 'Değişiklikleri kaydetmek istediğinize emin misiniz?';

    showConfirmation({
      title: 'Düzenleme Onayı',
      message: summaryMessage,
      confirmText: 'Onayla ve Güncelle',
      cancelText: 'Vazgeç',
      type: 'info',
      onConfirm: () => doSaveAdminEdit(null)
    });
  };

  // Admin: Borç güncelleme modalından onayla
  const confirmDebtUpdateAndSave = () => {
    const debtUpdate = {
      action: 'update',
      amount: parseFloat(debtUpdateData.amount) || 0,
      amount_tl: parseFloat(debtUpdateData.amount_tl) || 0,
      gold_rate: parseFloat(debtUpdateData.gold_rate) || 0,
      debt_type: debtUpdateData.debt_type
    };
    setShowDebtUpdateModal(false);
    doSaveAdminEdit(debtUpdate);
  };

  // Admin: Satışı sil
  const deleteSale = async (saleId) => {
    // Silinecek satışın detaylarını bul (log için)
    const saleToDelete = recentSales.find(s => s.id === saleId) || allSales.find(s => s.id === saleId);
    try {
      const response = await fetch(`${API_URL}/sales/${saleId}`, {
        method: 'DELETE'
      });

      const data = await response.json();
      if (data.success) {
        toast.success('Fiş iptal edildi!');
        // Log kaydet
        logService.saleDelete(saleId, {
          total: saleToDelete?.total || saleToDelete?.grand_total || 0,
          customer_name: saleToDelete?.customer_name,
          items_count: saleToDelete?.items_count || saleToDelete?.item_count,
          payment_method: saleToDelete?.payment_method,
          sale_date: saleToDelete?.created_at
        });
        setShowDeleteConfirm(false);
        setDeletingSale(null);
        setShowAdminEditModal(false);
        setShowLastSaleModal(false);
        setRecentSalesSearch('');
        const currentStore = user?.role === 'admin' ? (selectedStore?.id || user?.store_id) : user?.store_id;
        await fetchRecentSales(currentStore);
        // Tüm Fişler modalı açıksa yenile
        if (showAllSalesModal) fetchAllSales(false);
      } else {
        toast.error(data.error || 'İptal başarısız!');
        logService.log(LOG_MODULES.SALES, LOG_ACTIONS.SALE_DELETE, `Fiş iptal edilemedi: #${saleId}`, {
          status: 'error',
          details: { error: data.error }
        });
      }
    } catch (error) {
      console.error('Fiş iptal hatası:', error);
      toast.error('İptal başarısız!');
      logService.log(LOG_MODULES.SALES, LOG_ACTIONS.SALE_DELETE, `Fiş iptal hatası: #${saleId}`, {
        status: 'error',
        details: { error: error.message }
      });
    }
  };

  // Değişim/İade kaydını sil
  const deleteExchange = async (exchangeId) => {
    showConfirmation({
      title: 'Değişim/İade Silme',
      message: 'Bu değişim/iade kaydını silmek istediğinize emin misiniz?\n\n⚠️ Not: Bu işlem sadece kaydı listeden kaldırır. Stok ve ödeme bilgilerinde herhangi bir değişiklik yapılmaz.',
      confirmText: 'Evet, Sil',
      cancelText: 'İptal',
      type: 'warning',
      onConfirm: async () => {
        try {
          const response = await fetch(`${API_URL}/exchanges/${exchangeId}`, {
            method: 'DELETE'
          });

          const data = await response.json();
          if (data.success) {
            toast.success('Kayıt silindi (stok değişmedi)');
            // Log kaydet
            logService.log(LOG_MODULES.SALES, LOG_ACTIONS.DELETE, `Değişim/iade kaydı silindi: #${exchangeId}`, {
              entity_type: 'exchange',
              entity_id: exchangeId,
              status: 'warning'
            });
            setShowRefundDetailModal(false);
            setSelectedRefundDetail(null);
            const currentStore = user?.role === 'admin' ? (selectedStore?.id || user?.store_id) : user?.store_id;
            await fetchRecentSales(currentStore);
            // Tüm Fişler modalı açıksa yenile
            if (showAllSalesModal) fetchAllSales(false);
          } else {
            toast.error(data.error || 'Silme başarısız!');
          }
        } catch (error) {
          console.error('Değişim/iade silme hatası:', error);
          toast.error('Silme başarısız!');
        }
      }
    });
  };

  // Hurda alımı kaydını sil
  const deleteScrapPurchase = async (scrapId) => {
    const scrapToDelete = selectedScrapPurchaseDetail;
    showConfirmation({
      title: 'Hurda Alımı Silme',
      message: 'Bu hurda alımı kaydını silmek istediğinize emin misiniz?',
      confirmText: 'Evet, Sil',
      cancelText: 'İptal',
      type: 'danger',
      onConfirm: async () => {
        try {
          const response = await fetch(`${API_URL}/scrap-purchases/${scrapId}`, {
            method: 'DELETE'
          });

          const data = await response.json();
          if (data.success) {
            toast.success('Hurda alımı kaydı başarıyla silindi!');
            // Log kaydet
            logService.log(LOG_MODULES.SCRAP, LOG_ACTIONS.DELETE, `Hurda alımı silindi: #${scrapId}`, {
              entity_type: 'scrap',
              entity_id: scrapId,
              status: 'warning',
              details: {
                total_price: scrapToDelete?.total_price,
                weight: scrapToDelete?.weight,
                metal_type: scrapToDelete?.metal_type
              }
            });
            setShowScrapPurchaseDetailModal(false);
            setSelectedScrapPurchaseDetail(null);
            const currentStore = user?.role === 'admin' ? (selectedStore?.id || user?.store_id) : user?.store_id;
            await fetchRecentSales(currentStore);
            if (showAllSalesModal) fetchAllSales(false);
          } else {
            toast.error(data.error || 'Silme başarısız!');
          }
        } catch (error) {
          console.error('Hurda alımı silme hatası:', error);
          toast.error('Silme başarısız!');
        }
      }
    });
  };

  // Hurda alımı düzenleme modalını aç
  const openEditScrapPurchaseModal = (scrap) => {
    setEditingScrapPurchase(scrap);
    setScrapPurchaseEditData({
      metal_type: scrap.metal_type || 'gold',
      purity: scrap.purity || '22',
      gross_weight: scrap.gross_weight || '',
      waste_percent: scrap.waste_percent || '0',
      pure_weight: scrap.pure_weight || '',
      unit_price: scrap.unit_price || '',
      total: scrap.total || '',
      payment_method: scrap.payment_method || 'Nakit',
      notes: scrap.notes || '',
      created_at: scrap.created_at ? new Date(scrap.created_at).toISOString().slice(0, 16) : ''
    });
    setShowScrapPurchaseDetailModal(false);
    setShowEditScrapPurchaseModal(true);
  };

  // Hurda alımı düzenlemeyi kaydet
  const saveScrapPurchaseEdit = async () => {
    try {
      const scrapId = editingScrapPurchase.scrap_purchase_id || editingScrapPurchase.id;
      const response = await fetch(`${API_URL}/scrap-purchases/${scrapId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...scrapPurchaseEditData,
          created_at: scrapPurchaseEditData.created_at ? new Date(scrapPurchaseEditData.created_at).toISOString() : undefined
        })
      });

      const data = await response.json();
      if (data.success || response.ok) {
        toast.success('Hurda alımı kaydı güncellendi!');
        setShowEditScrapPurchaseModal(false);
        setEditingScrapPurchase(null);
        const currentStore = user?.role === 'admin' ? (selectedStore?.id || user?.store_id) : user?.store_id;
        await fetchRecentSales(currentStore);
        if (showAllSalesModal) fetchAllSales(false);
      } else {
        toast.error(data.error || 'Güncelleme başarısız!');
      }
    } catch (error) {
      console.error('Hurda alımı güncelleme hatası:', error);
      toast.error('Güncelleme başarısız!');
    }
  };

  // Tahsilat kaydını sil
  const deleteDebtPayment = async (paymentIdInput) => {
    // ID'yi doğru şekilde al (DP123 formatından numeric ID'yi çıkar, virgülle ayrılmış grup ID'lerini destekle)
    const idStr = String(paymentIdInput);
    const paymentIds = idStr.split(',').map(s => {
      const trimmed = s.trim();
      return trimmed.startsWith('DP') ? trimmed.substring(2) : trimmed;
    }).filter(s => s);

    showConfirmation({
      title: 'Tahsilat Silme',
      message: 'Bu tahsilat kaydını silmek istediğinize emin misiniz?\n\nBorç tutarı geri yüklenecektir.',
      confirmText: 'Evet, Sil',
      cancelText: 'İptal',
      type: 'danger',
      onConfirm: async () => {
        try {
          let data;
          for (const pid of paymentIds) {
            const response = await fetch(`${API_URL}/debt-payments/${pid}`, {
              method: 'DELETE'
            });
            data = await response.json();
          }
          if (data.success) {
            toast.success('Tahsilat kaydı silindi ve borç geri yüklendi!');
            setShowDebtPaymentDetailModal(false);
            setSelectedDebtPaymentDetail(null);
            const currentStore = user?.role === 'admin' ? (selectedStore?.id || user?.store_id) : user?.store_id;
            await fetchRecentSales(currentStore);
            // Tüm Fişler modalı açıksa yenile
            if (showAllSalesModal) fetchAllSales(false);
            // Tahsilat geçmişini ve borç özetini de yenile
            fetchCollectionHistory();
            fetchCollectionDebtSummary();
          } else {
            toast.error(data.error || 'Silme başarısız!');
          }
        } catch (error) {
          console.error('Tahsilat silme hatası:', error);
          toast.error('Silme başarısız!');
        }
      }
    });
  };

  // Tahsilat düzenleme modalını aç (sadece admin)
  const openEditDebtPaymentModal = async (payment) => {
    if (user?.role !== 'admin') { toast.error('Bu işlem sadece admin yetkisiyle yapılabilir'); return; }
    // Tarih formatını güvenli şekilde parse et
    let formattedDate = '';
    try {
      if (payment.created_at) {
        const date = new Date(payment.created_at);
        if (!isNaN(date.getTime())) {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          const hours = String(date.getHours()).padStart(2, '0');
          const minutes = String(date.getMinutes()).padStart(2, '0');
          formattedDate = `${year}-${month}-${day}T${hours}:${minutes}`;
        }
      }
    } catch (e) {
      console.error('Tarih parse hatası:', e);
    }

    setEditingDebtPayment(payment);
    // payment_details'ı parse et — her ödeme kalemi ayrı düzenlenecek
    let parsedDetails = [];
    if (payment.payment_details) {
      try {
        parsedDetails = typeof payment.payment_details === 'string' ? JSON.parse(payment.payment_details) : payment.payment_details;
        if (!Array.isArray(parsedDetails)) parsedDetails = [];
      } catch (e) { parsedDetails = []; }
    }
    // Eğer payment_details boşsa, tek kalem olarak göster
    if (parsedDetails.length === 0) {
      parsedDetails = [{ method: payment.payment_method || 'Nakit', amount: parseFloat(payment.amount || payment.total || 0) }];
    }
    setDebtPaymentEditData({
      amount: payment.amount || payment.total || '',
      payment_method: payment.payment_method || 'Nakit',
      notes: payment.notes || '',
      created_at: formattedDate,
      bank_account: '',
      details: parsedDetails.map(d => ({ ...d, amount: parseFloat(d.amount) || 0 }))
    });
    await fetchBankAccounts();
    setShowDebtPaymentDetailModal(false);
    setShowEditDebtPaymentModal(true);
  };

  // Tahsilat düzenlemeyi kaydet
  const saveDebtPaymentEdit = async () => {
    try {
      // ID'yi doğru şekilde al (DP123 formatından numeric ID'yi çıkar)
      let paymentId = editingDebtPayment.debt_payment_id;
      if (!paymentId && editingDebtPayment.id) {
        // id "DP123" formatındaysa numeric kısmını al
        const idStr = String(editingDebtPayment.id);
        paymentId = idStr.startsWith('DP') ? idStr.substring(2) : idStr;
      }

      const oldAmount = parseFloat(editingDebtPayment.total || editingDebtPayment.amount || 0);
      // Detaylardan toplam tutarı hesapla
      const updatedDetails = debtPaymentEditData.details || [];
      const newAmount = updatedDetails.reduce((sum, d) => sum + (parseFloat(d.amount) || 0), 0);
      // Ödeme yöntemi: tek kalemse direkt, birden fazlaysa Karışık
      const uniqueMethods = [...new Set(updatedDetails.map(d => d.method))];
      const newPaymentMethod = uniqueMethods.length === 1 ? uniqueMethods[0] : 'Karışık';

      // Tutar gerçekten kullanıcı tarafından değiştirildi mi? Yalnızca o zaman backend'e
      // amount/old_amount gönder ki "Tahsilat düzeltme" satırı eklensin. Aksi halde
      // grup tahsilatlarda fazla ödeme nedeniyle group.total ile payment_details toplamı
      // ayrı olabildiği için sahte düzeltme kaydı oluşur.
      const amountChanged = !!debtPaymentEditData._amountChanged;

      const body = {
        payment_method: newPaymentMethod,
        payment_details: JSON.stringify(updatedDetails),
        notes: debtPaymentEditData.notes,
        created_at: debtPaymentEditData._dateChanged ? new Date(debtPaymentEditData.created_at).toISOString() : undefined
      };
      if (amountChanged) {
        body.amount = newAmount;
        body.old_amount = oldAmount;
      }

      // Backend'e güncelleme isteği gönder
      const response = await fetch(`${API_URL}/debt-payments/${paymentId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      const data = await response.json();
      if (data.success || response.ok) {
        toast.success('Tahsilat kaydı güncellendi!');
        setShowEditDebtPaymentModal(false);
        setEditingDebtPayment(null);
        // Detay modalını da kapat
        setShowDebtPaymentDetailModal(false);
        setSelectedDebtPaymentDetail(null);
        const currentStore = user?.role === 'admin' ? (selectedStore?.id || user?.store_id) : user?.store_id;
        await fetchRecentSales(currentStore);
        // Tüm Fişler modalı açıksa yenile
        if (showAllSalesModal) fetchAllSales(false);
      } else {
        toast.error(data.error || 'Güncelleme başarısız!');
      }
    } catch (error) {
      console.error('Tahsilat güncelleme hatası:', error);
      toast.error('Güncelleme başarısız!');
    }
  };

  // Fişteki müşteriyi seçer: satış kaydında sadece id ve isim var, iletişim bilgileri cari kartından gelir.
  // Yanıt gelene kadar kullanıcı başka müşteri seçtiyse onun üzerine yazılmaz.
  const selectCustomerFromSale = async (customerId, customerName) => {
    setSelectedCustomer({ id: customerId, name: customerName });
    try {
      const response = await fetch(`${API_URL}/customers/${customerId}`);
      if (!response.ok) return;
      const data = await response.json();
      const full = data.customer || data;
      setSelectedCustomer(prev => (prev?.id === customerId ? {
        ...prev,
        phone: full.phone || '',
        email: full.email || '',
        address: full.address || '',
        tax_number: full.tax_number || ''
      } : prev));
    } catch (error) {
      console.error('Müşteri bilgisi alınamadı:', error);
    }
  };

  // Değişim modalını aç
  const openExchangeModal = async (sale) => {
    try {
      // Sepeti temizleme - mevcut ürünler korunsun, değişim ürünleri eklenecek

      const response = await fetch(`${API_URL}/sales/${sale.id}`);
      const data = await response.json();

      // Satış tarihinden bu yana geçen gün sayısına göre değişim kuralı
      const daysSinceSale = Math.floor((new Date() - new Date(data.created_at)) / (1000 * 60 * 60 * 24));

      if (daysSinceSale > 30 && user?.role !== 'admin') {
        toast.error('14 gün geçti, iade alamazsınız!');
        return;
      }

      if (daysSinceSale > 14) {
        const proceed = await new Promise((resolve) => {
          showConfirmation({
            title: 'Değişim Süresi Geçti',
            message: '14 gün geçtiği için değişim yapamazsınız.\n\nYine de devam etmek istiyor musunuz?',
            confirmText: 'Tamam',
            cancelText: 'İptal',
            type: 'warning',
            onConfirm: () => resolve(true),
            onCancel: () => resolve(false)
          });
        });
        if (!proceed) return;
      }

      setExchangeSaleInfo(data);

      // Fark kalemlerini yükle
      let adjustments = [];
      if (data.adjustment_items) {
        adjustments = typeof data.adjustment_items === 'string'
          ? JSON.parse(data.adjustment_items) : data.adjustment_items;
      }
      if ((!adjustments || adjustments.length === 0) && data.receipt_data) {
        try {
          const rd = typeof data.receipt_data === 'string' ? JSON.parse(data.receipt_data) : data.receipt_data;
          adjustments = rd.adjustmentItems || rd.adjustment_items || [];
        } catch (e) {}
      }

      // effective_unit_price: kalemin indirim uygulanmış birim fiyatı. Yazdırılan fiş, Fiş
      // Detayı modalı ve bu ekran AYNI kaynaktan (computeSaleItemPricing) hesaplasın diye
      // ortak yardımcıya taşındı — DB'de kalem bazında saklı indirimli değerler satış
      // toplamıyla örtüşüyorsa doğrudan onlar, aksi halde orantılı dağıtım kullanılır.
      const { itemUnitPrice, adjAmount: adjEffectiveAmount } = computeSaleItemPricing(data, adjustments);

      const itemsWithAvailability = data.items.map(item => ({
        ...item,
        returned_quantity: item.returned_quantity || 0,
        availableQty: item.quantity - (item.returned_quantity || 0),
        exchangeQty: 0,
        effective_unit_price: itemUnitPrice(item)
      }));

      // Fark kalemlerini ürün gibi ekle - aynı hesaplama uygulanır
      const adjustmentExchangeItems = (adjustments || []).map((adj, idx) => {
        const adjGrossAmount = parseFloat(adj.amount || adj.refund_amount || adj.original_amount) || 0;
        return {
          product_id: `adj_${idx}`,
          product_name: adj.description || adj.reason || 'Fark',
          unit_price: adjGrossAmount,
          quantity: 1,
          returned_quantity: adj.is_refunded ? 1 : 0,
          availableQty: adj.is_refunded ? 0 : 1,
          exchangeQty: 0,
          effective_unit_price: adjEffectiveAmount(adj),
          isAdjustment: true,
          adjustmentData: adj
        };
      });
      setExchangeItems([...itemsWithAvailability, ...adjustmentExchangeItems]);

      // Mevcut müşteriyi sakla (modal iptal edilirse geri yüklemek için)
      setPreRefundCustomer(selectedCustomer || null);

      if (data.customer_id) {
        // Müşterinin borç bilgisini çek
        try {
          const debtResponse = await fetch(`${API_URL}/customers/debts/summary`);
          const debtData = await debtResponse.json();
          const customerDebt = debtData.find(d => d.customer_id === data.customer_id);
          setSelectedCustomerDebt(customerDebt || null);
        } catch (err) {
          console.error('Borç bilgisi çekilemedi:', err);
          setSelectedCustomerDebt(null);
        }
      } else {
        setSelectedCustomerDebt(null);
      }

      // Değişimde mevcut kasiyer seçimi korunsun, orijinal satışın kasiyeri getirilmesin

      setShowLastSaleModal(false);
      setRecentSalesSearch('');
      setShowExchangeModal(true);
    } catch (error) {
      console.error('Satış detay hatası:', error);
    }
  };

  // Değişim miktarını güncelle (availableQty = quantity - returned_quantity)
  // NOT: item.id (sale_items satır id'si) ile eşleştirilir, product_id ile DEĞİL - aynı
  // fişte aynı üründen (ör. "Kişiye Özel") birden fazla satır varsa product_id eşleşmesi
  // bir satırı düzenlerken diğerini de değiştiriyordu. Fark kalemlerinde (isAdjustment)
  // gerçek id olmadığından product_id (adj_${idx}, zaten benzersiz) fallback olarak kullanılır.
  const updateExchangeQty = (itemKey, qty) => {
    const item = exchangeItems.find(i => (i.id ?? i.product_id) === itemKey);
    if (!item) return;

    const availableQty = item.availableQty !== undefined ? item.availableQty : item.quantity;

    // Tamamen iade edilmiş ürün için hata göster
    if (availableQty <= 0 && qty > 0) {
      toast.error(`Bu ürün daha önce tamamen iade edilmiş!`);
      return;
    }

    // İade edilebilir miktarı aşmaya çalışıyorsa hata göster
    if (qty > availableQty) {
      toast.warning(`Bu üründen en fazla ${availableQty} adet değişim yapılabilir! (${item.returned_quantity} adet daha önce iade edildi)`);
      // Miktarı maksimuma ayarla
      setExchangeItems(exchangeItems.map(i =>
        (i.id ?? i.product_id) === itemKey
          ? { ...i, exchangeQty: availableQty }
          : i
      ));
      return;
    }

    setExchangeItems(exchangeItems.map(i =>
      (i.id ?? i.product_id) === itemKey
        ? { ...i, exchangeQty: Math.max(0, qty) }
        : i
    ));
  };

  // Değişim onayı - iade ürünlerini sepete eksi olarak ekle (stok henüz güncellenmez)
  const confirmExchange = async () => {
    const selectedItems = exchangeItems.filter(item => item.exchangeQty > 0);
    if (selectedItems.length === 0) {
      toast.warning('Lütfen değişim yapılacak ürün seçin!');
      return;
    }

    // Müşteriyi seç
    if (exchangeSaleInfo.customer_id) {
      selectCustomerFromSale(exchangeSaleInfo.customer_id, exchangeSaleInfo.customer_name);
    }

    // Normal ürünleri sepete eksi fiyatla ekle
    const productItems = selectedItems.filter(item => !item.isAdjustment);
    const adjustmentItemsSelected = selectedItems.filter(item => item.isAdjustment);

    const refundCartItems = productItems.map(item => ({
      id: `refund_${item.product_id}_${Date.now()}`,
      product_id: item.product_id,
      sale_item_id: typeof item.id === 'number' ? item.id : null,
      name: `İADE: ${item.product_name}`,
      price: -Number(item.effective_unit_price || item.unit_price),
      quantity: item.exchangeQty,
      stock: 999,
      isRefund: true,
      originalSaleId: exchangeSaleInfo.id,
      originalCustomerId: exchangeSaleInfo.customer_id || null,
      originalCustomerName: exchangeSaleInfo.customer_name || null,
      pendingRefund: true,
      barcode: item.barcode || null,
      originalBarcode: item.barcode || null,
      image: item.image || null
    }));

    // Fark kalemlerini adjustmentItems olarak ekle
    const refundAdjItems = adjustmentItemsSelected.map(item => ({
      id: Date.now() + Math.random(),
      description: `İADE: ${item.product_name}`,
      reason: item.adjustmentData?.reason || item.product_name,
      amount: -Number(item.effective_unit_price) * item.exchangeQty,
      isRefund: true,
      originalSaleId: exchangeSaleInfo.id
    }));

    setCart([...cart, ...refundCartItems]);
    if (refundAdjItems.length > 0) {
      setAdjustmentItems(prev => [...prev, ...refundAdjItems]);
    }
    // Değişim modunda mevcut kasiyer seçimi korunsun
    setSelectedPreviewItem(null);
    setShowExchangeModal(false);
    setExchangeSaleInfo(null);
    setExchangeItems([]);
    // Değişim modunda hurda alacağı ve müşteri bakiyesi kullanılamaz - temizle
    if (appliedScrapDebtCard) {
      setAppliedScrapDebtCard(null);
    }
    if (appliedCustomerGiftCard) {
      removeCustomerGiftCard();
    }
    toast.info('İade ürünleri sepete eklendi. Değişim modunda Nakit, Kredi Kartı, Havale/EFT veya Açık Hesap ile ödeme yapılabilir.');
    barcodeInputRef.current?.focus();
  };

  // Satış sorumlularını getir
  const fetchSalesStaff = async () => {
    try {
      const response = await fetch(`${API_URL}/sales-staff${currentStoreId ? `?store_id=${currentStoreId}` : ''}`);
      const data = await response.json();
      setSalesStaffList(data);
    } catch (error) {
      console.error('Satış sorumlusu yükleme hatası:', error);
    }
  };

  // Satış sorumlusu seç
  const selectStaffWithLog = (staff) => {
    setSelectedStaff([staff]);
    setShowStaffModal(false);
  };

  // Seçili satış sorumlularını onayla
  const confirmStaffSelection = () => {
    setShowStaffModal(false);
  };

  // Modal açıldığında satış sorumlularını yükle
  useEffect(() => {
    if (showStaffModal || showTransfersModal || showCollectionPaymentModal) {
      fetchSalesStaff();
    }
  }, [showStaffModal, showTransfersModal, showCollectionPaymentModal]);


  // Gün Sonu Raporu getir
  const fetchEndOfDayReport = async (targetDate) => {
    setEndOfDayLoading(true);
    try {
      const currentStoreId = selectedStore?.id || user?.store_id;
      const localDate = targetDate || new Date();
      const dateParam = `${localDate.getFullYear()}-${String(localDate.getMonth() + 1).padStart(2, '0')}-${String(localDate.getDate()).padStart(2, '0')}`;
      const response = await fetch(`${API_URL}/daily-report?store_id=${currentStoreId}&date=${dateParam}`);
      const report = await response.json();
      report.date = localDate;
      report.storeName = selectedStore?.name || user?.store_name || 'Mağaza';
      setEndOfDayReport(report);
    } catch (error) {
      console.error('Gün sonu raporu yüklenemedi:', error);
    } finally {
      setEndOfDayLoading(false);
    }
  };
  // Gün sonu modal açıldığında raporu getir
  const openEndOfDayReport = () => {
    setEndOfDayTab('genel');
    setExpandedCashiers({});
    setShowPrintMenu(false);
    setExpandedPaymentType(null);
    setExpandedExpenseItem(null);
    setEodCashierFilter([]);
    setEodDate(new Date());
    fetchEndOfDayReport();
    setShowEndOfDayModal(true);
  };

  // Tarih navigasyonu
  const handleEodDateChange = (direction) => {
    const newDate = new Date(eodDate);
    if (direction === 'prev') {
      newDate.setDate(newDate.getDate() - 1);
    } else {
      newDate.setDate(newDate.getDate() + 1);
    }
    // Gelecek tarihe gitmeyi engelle
    const now = new Date();
    now.setHours(23, 59, 59, 999);
    if (newDate > now) return;

    setEodDate(newDate);
    fetchEndOfDayReport(newDate);
  };

  const isEodToday = () => {
    const now = new Date();
    return eodDate.getDate() === now.getDate() &&
           eodDate.getMonth() === now.getMonth() &&
           eodDate.getFullYear() === now.getFullYear();
  };

  // Günlük rapor bildirimini admin'lere gönder
  const sendDailyReportNotification = async () => {
    if (!endOfDayReport || sendingDailyReport) return;

    setSendingDailyReport(true);
    try {
      const reportDate = eodDate.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });

      const response = await fetch(`${API_URL}/push-notifications/daily-report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          store_id: selectedStore?.id,
          store_name: endOfDayReport.storeName || selectedStore?.name || 'Mağaza',
          report_date: reportDate,
          total_sales: endOfDayReport.grandTotal || 0,
          sale_count: (endOfDayReport.totalSales || 0) + (endOfDayReport.exchanges?.count || 0),
          report_summary: {
            cash: endOfDayReport.cash?.total || 0,
            card: endOfDayReport.card?.total || 0,
            transfer: endOfDayReport.transfer?.total || 0
          }
        })
      });

      const data = await response.json();

      if (data.success) {
        if (data.sent > 0) {
          toast.success(`Günlük rapor bildirimi ${data.sent} cihaza gönderildi`);
        } else {
          toast.warning('Bildirim gönderilecek admin cihazı bulunamadı');
        }
      } else {
        toast.error('Bildirim gönderilemedi: ' + (data.error || 'Bilinmeyen hata'));
      }
    } catch (error) {
      console.error('Günlük rapor bildirimi hatası:', error);
      toast.error('Bildirim gönderilirken hata oluştu');
    } finally {
      setSendingDailyReport(false);
    }
  };

  // Gün sonu raporu yazdır
  const printEndOfDayReport = () => {
    if (!endOfDayReport) return;

    const reportDate = eodDate.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });

    const logoHtml = endOfDayReport.storeId === 1
      ? `<img src="/Siyah - Beyaz - 1.png" alt="Akik Gold Jewellery" class="logo-img" />`
      : `<div class="logo-wrap-silver"><img src="/akikfisligi.png" alt="Akik Gümüş" /></div>`;

    const commonStyles = `
      * { margin: 0; padding: 0; box-sizing: border-box; }
      body { font-family: 'Segoe UI', Arial, sans-serif; padding: 20px; max-width: 400px; margin: 0 auto; font-weight: 600; color: #000; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .header { text-align: center; margin-bottom: 20px; padding-bottom: 15px; border-bottom: 2px solid #000; }
      .header h1 { font-size: 18px; margin-bottom: 5px; }
      .header .logo-img { max-width: 160px; height: auto; margin-bottom: 5px; filter: invert(1); }
      .header .logo-wrap-silver { margin: 0 auto 2px auto; text-align: center; }
      .header .logo-wrap-silver img { width: 140px; height: auto; filter: contrast(3); }
      .header .date { font-size: 14px; color: #000; }
      .total-section { background: #fff; border: 1px solid #000; padding: 15px; border-radius: 8px; margin-bottom: 20px; text-align: center; }
      .total-section .label { font-size: 12px; color: #000; }
      .total-section .amount { font-size: 28px; font-weight: bold; color: #000; }
      .total-section .count { font-size: 12px; color: #000; margin-top: 5px; }
      .section { margin-bottom: 20px; }
      .section-title { font-size: 14px; font-weight: bold; margin-bottom: 10px; padding-bottom: 5px; border-bottom: 1px solid #000; }
      .payment-row { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px dashed #999; }
      .payment-row:last-child { border-bottom: none; }
      .payment-row.refund { color: #000; font-weight: bold; }
      .transactions-header { display: flex; justify-content: space-between; font-size: 12px; color: #000; margin-bottom: 10px; }
      .transaction { display: flex; justify-content: space-between; padding: 6px 0; border-bottom: 1px dashed #999; font-size: 12px; }
      .transaction:last-child { border-bottom: none; }
      .transaction-left { display: flex; gap: 10px; }
      .transaction-time { color: #000; min-width: 45px; }
      .transaction-method { color: #000; font-size: 11px; }
      .transaction-amount { font-weight: 700; }
      .footer { text-align: center; margin-top: 20px; padding-top: 15px; border-top: 1px solid #000; font-size: 11px; color: #000; }
      .cashier-block { margin-bottom: 15px; page-break-inside: avoid; }
      .cashier-header { display: flex; justify-content: space-between; align-items: center; padding: 6px 0; border-bottom: 1px solid #000; margin-bottom: 4px; }
      .cashier-name { font-size: 13px; font-weight: bold; color: #000; }
      .cashier-total { font-size: 13px; font-weight: bold; color: #000; }
      .products-table { width: 100%; border-collapse: collapse; font-size: 11px; margin-top: 10px; }
      .products-table th { background: #fff; padding: 6px 8px; text-align: left; font-weight: 700; border-bottom: 2px solid #000; }
      .products-table td { padding: 5px 8px; border-bottom: 1px dashed #999; }
      .products-table th:last-child, .products-table td:last-child { text-align: right; }
      .page-break { page-break-before: always; }
      @media print { body { padding: 10px; font-weight: 600; color: #000; -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
    `;

    // Sayfa 1: Genel Rapor
    const page1 = `
        <div class="header">
          ${logoHtml}
          <div style="font-size:14px;font-weight:bold;margin:5px 0;">${endOfDayReport.storeName}</div>
          <div class="date">${reportDate}</div>
        </div>

        <div class="total-section">
          <div class="label">Toplam Ciro</div>
          <div class="amount">₺${endOfDayReport.grandTotal.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
          <div class="count">${endOfDayReport.totalSales + (endOfDayReport.exchanges?.count || 0)} işlem</div>
        </div>

        <div class="section">
          <div class="section-title">Nakit</div>
          <div class="payment-row">
            <span>Toplam Nakit</span>
            <span>₺${endOfDayReport.cash.total.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
          ${endOfDayReport.cash.usd > 0 ? `
          <div class="payment-row" style="padding-left:15px;font-size:12px;color:#000;">
            <span>└ Dolar (USD)</span>
            <span>$${endOfDayReport.cash.usd.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
          ` : ''}
          ${endOfDayReport.cash.eur > 0 ? `
          <div class="payment-row" style="padding-left:15px;font-size:12px;color:#000;">
            <span>└ Euro (EUR)</span>
            <span>€${endOfDayReport.cash.eur.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
          ` : ''}
          ${endOfDayReport.cashRefund.count > 0 ? `
          <div class="payment-row refund">
            <span>Nakit İade</span>
            <span>-₺${endOfDayReport.cashRefund.total.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
          ` : ''}
          ${(endOfDayReport.scrapCashRefund?.count || 0) > 0 ? `
          <div class="payment-row refund">
            <span>Hurda Nakit İadesi</span>
            <span>-₺${endOfDayReport.scrapCashRefund.total.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
          ` : ''}
          <div class="payment-row" style="background:#fff;border:1px solid #000;padding:8px;font-weight:bold;color:#000;">
            <span>Nakit Kasa Toplamı</span>
            <span>₺${endOfDayReport.expectedCash.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
        </div>

        <div class="section">
          <div class="section-title">Kart & Transfer</div>
          <div class="payment-row">
            <span>Kredi Kartı</span>
            <span>₺${endOfDayReport.card.total.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
          <div class="payment-row">
            <span>Havale/EFT Toplam</span>
            <span>₺${endOfDayReport.transfer.total.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
          ${endOfDayReport.transfer.byAccount && Object.keys(endOfDayReport.transfer.byAccount).length > 0 ?
            Object.entries(endOfDayReport.transfer.byAccount).map(([accountName, data]) => `
            <div class="payment-row" style="padding-left:15px;font-size:12px;color:#000;">
              <span>└ ${stripBankAccountType(accountName)}</span>
              <span>₺${data.total.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} (${data.count})</span>
            </div>
            `).join('') : ''}
          ${endOfDayReport.openAccount.count > 0 ? `
          <div class="payment-row">
            <span>Veresiye</span>
            <span>₺${endOfDayReport.openAccount.total.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
          ` : ''}
          ${endOfDayReport.mixed.count > 0 ? `
          <div class="payment-row">
            <span>Karma Ödeme</span>
            <span>₺${endOfDayReport.mixed.total.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
          ` : ''}
          ${endOfDayReport.voucher.count > 0 ? `
          <div class="payment-row">
            <span>Bakiye</span>
            <span>₺${endOfDayReport.voucher.total.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
          ` : ''}
        </div>

        ${endOfDayReport.collections && endOfDayReport.collections.count > 0 ? `
        <div class="section">
          <div class="section-title">Tahsilatlar</div>
          <div class="payment-row" style="background:#fff;border:1px solid #000;padding:8px;font-weight:bold;color:#000;">
            <span>Tahsilat Toplamı (${endOfDayReport.collections.count} adet)</span>
            <span>+₺${endOfDayReport.collections.total.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
          ${endOfDayReport.collections.items.map(col => `
          <div class="payment-row" style="padding-left:15px;font-size:12px;color:#000;">
            <span>└ ${col.customer_name || 'Müşteri'}</span>
            <span>₺${(parseFloat(col.amount) || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
          `).join('')}
        </div>
        ` : ''}

        ${endOfDayReport.scrap && endOfDayReport.scrap.count > 0 ? `
        <div class="section">
          <div class="section-title">Hurda Alımları</div>
          ${endOfDayReport.scrap.standaloneTotal > 0 ? `
          <div class="payment-row" style="background:#fff;border:1px solid #000;padding:8px;font-weight:bold;color:#000;">
            <span>Hurda Alım (Nakit Ödenen)</span>
            <span>-₺${endOfDayReport.scrap.standaloneTotal.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
          ` : ''}
          ${endOfDayReport.scrap.goldTotal > 0 ? `
          <div class="payment-row" style="padding-left:15px;font-size:12px;color:#000;">
            <span>└ Altın Hurda (${endOfDayReport.scrap.goldGrossGram?.toFixed(2) || '0.00'} gr has / ${endOfDayReport.scrap.goldGram?.toFixed(2) || '0.00'} gr saf)</span>
            <span>₺${endOfDayReport.scrap.goldTotal.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
          ` : ''}
          ${endOfDayReport.scrap.silverTotal > 0 ? `
          <div class="payment-row" style="padding-left:15px;font-size:12px;color:#000;">
            <span>└ Gümüş Hurda (${endOfDayReport.scrap.silverGrossGram?.toFixed(2) || '0.00'} gr has / ${endOfDayReport.scrap.silverGram?.toFixed(2) || '0.00'} gr saf)</span>
            <span>₺${endOfDayReport.scrap.silverTotal.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
          ` : ''}
          ${endOfDayReport.scrap.items && endOfDayReport.scrap.items.length > 0 ?
            endOfDayReport.scrap.items.map(scr => `
          <div class="payment-row" style="padding-left:15px;font-size:11px;color:#000;border-top:1px dashed #999;padding-top:5px;">
            <span>└ ${scr.customer_name || 'Müşteri'}: ${parseFloat(scr.gross_weight || 0).toFixed(2)}gr ${scr.metal_type || ''} - ${scr.payment_method === 'Tahsilat Mahsup' ? 'Tahsilat' : scr.payment_method === 'Havale' && scr.bank_account_name ? scr.bank_account_name : (scr.payment_method || 'Nakit')}</span>
            <span>${scr.payment_method === 'Tahsilat Mahsup' ? '+' : '-'}₺${parseFloat(scr.total_amount || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
          `).join('') : ''}
          ${endOfDayReport.scrap.refundTotal > 0 ? endOfDayReport.scrap.refundItems.map(ri => `
          <div class="payment-row" style="padding-left:15px;font-size:11px;color:#000;border-top:1px dashed #999;padding-top:5px;">
            <span>└ ${ri.customer_name} - Hurda İade (Fiş #${ri.receipt_number})</span>
            <span>+₺${ri.amount.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
          `).join('') : ''}
          ${endOfDayReport.scrap.refundTotal > 0 ? `
          <div class="payment-row" style="background:#fff;border:1px solid #000;padding:8px;font-weight:bold;color:#000;">
            <span>Net Hurda</span>
            <span>-₺${endOfDayReport.scrap.netTotal.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
          ` : ''}
        </div>
        ` : ''}

        ${endOfDayReport.exchanges && endOfDayReport.exchanges.count > 0 ? `
        <div class="section">
          <div class="section-title">Değişim & İade</div>
          <div class="payment-row">
            <span>Toplam İşlem</span>
            <span>${endOfDayReport.exchanges.count} adet</span>
          </div>
          ${endOfDayReport.exchanges.exchangeCount > 0 ? `
          <div class="payment-row" style="padding-left:15px;font-size:12px;color:#000;">
            <span>└ Değişim</span>
            <span>${endOfDayReport.exchanges.exchangeCount} adet</span>
          </div>
          ` : ''}
          ${endOfDayReport.exchanges.refundCount > 0 ? `
          <div class="payment-row" style="padding-left:15px;font-size:12px;color:#000;">
            <span>└ İade</span>
            <span>${endOfDayReport.exchanges.refundCount} adet</span>
          </div>
          ` : ''}
          ${endOfDayReport.exchanges.cashRefundTotal > 0 ? `
          <div class="payment-row" style="background:#fff;border:1px solid #000;padding:8px;font-weight:bold;color:#000;">
            <span>Nakit Verilen (Kasadan)</span>
            <span>-₺${endOfDayReport.exchanges.cashRefundTotal.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
          ` : ''}
          ${endOfDayReport.exchanges.voucherRefundTotal > 0 ? `
          <div class="payment-row" style="padding-left:15px;font-size:12px;color:#000;">
            <span>└ Hediye Çeki Verilen</span>
            <span>₺${endOfDayReport.exchanges.voucherRefundTotal.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
          ` : ''}
          ${endOfDayReport.exchanges.items && endOfDayReport.exchanges.items.length > 0 ?
            endOfDayReport.exchanges.items.map(exc => `
          <div class="payment-row" style="padding-left:15px;font-size:11px;color:#000;border-top:1px dashed #999;padding-top:5px;">
            <span>└ ${exc.type === 'exchange' ? 'Değişim' : 'İade'}: ${exc.customer_name || 'Müşteri'}</span>
            <span>${exc.payment_method} ${exc.difference ? (parseFloat(exc.difference) >= 0 ? '+' : '') + '₺' + parseFloat(exc.difference).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : ''}</span>
          </div>
          `).join('') : ''}
        </div>
        ` : ''}

        ${endOfDayReport.expenses && endOfDayReport.expenses.count > 0 ? `
        <div class="section">
          <div class="section-title">Masraflar</div>
          <div class="payment-row" style="background:#fff;border:1px solid #000;padding:8px;font-weight:bold;color:#000;">
            <span>Masraf Toplamı (${endOfDayReport.expenses.count} adet)</span>
            <span>-₺${endOfDayReport.expenses.total.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
          ${endOfDayReport.expenses.cashTotal > 0 ? `
          <div class="payment-row" style="padding-left:15px;font-size:12px;color:#000;">
            <span>└ Nakit Masraf (Kasadan)</span>
            <span>-₺${endOfDayReport.expenses.cashTotal.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
          ` : ''}
          ${endOfDayReport.expenses.items.map(exp => `
          <div class="payment-row" style="padding-left:15px;font-size:12px;color:#000;">
            <span>└ ${exp.description}</span>
            <span>₺${(parseFloat(exp.amount) || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
          `).join('')}
        </div>
        ` : ''}

    `;

    // Kasa Özeti hesaplamaları
    const gelirler = [];
    const nakitOdemeTutar = endOfDayReport.cash.salesTotal ?? ((endOfDayReport.cash.total || 0) - (endOfDayReport.cash.collectionTotal || 0) - (endOfDayReport.cash.repairTotal || 0));
    if (nakitOdemeTutar > 0) gelirler.push({ name: 'Nakit Ödeme', amount: nakitOdemeTutar });
    if ((endOfDayReport.cash.collectionTotal || 0) > 0) gelirler.push({ name: 'Nakit Tahsilat', amount: endOfDayReport.cash.collectionTotal });
    if ((endOfDayReport.cash.repairTotal || 0) > 0) gelirler.push({ name: 'Nakit Tamirat', amount: endOfDayReport.cash.repairTotal });
    eTicaretGelirRows(endOfDayReport.sanalPos).forEach(([name, amount]) => gelirler.push({ name, amount }));
    if ((endOfDayReport.card.salesTotal ?? endOfDayReport.card.total) > 0) gelirler.push({ name: 'Kredi Kartı', amount: endOfDayReport.card.salesTotal ?? endOfDayReport.card.total });
    if ((endOfDayReport.card.collectionTotal || 0) > 0) gelirler.push({ name: 'Kredi Kartı Tahsilat', amount: endOfDayReport.card.collectionTotal });
    if ((endOfDayReport.card.repairTotal || 0) > 0) gelirler.push({ name: 'Kredi Kartı Tamirat', amount: endOfDayReport.card.repairTotal });
    // Havale/EFT satış gelirlerini hesap bazlı göster
    const transferSalesTotal = endOfDayReport.transfer.salesTotal ?? endOfDayReport.transfer.total;
    if (transferSalesTotal > 0) {
      const byAccount = endOfDayReport.transfer.byAccount || {};
      const accountNames = Object.keys(byAccount);
      if (accountNames.length > 0) {
        accountNames.forEach(name => {
          if (byAccount[name].total > 0) gelirler.push({ name: `Havale/EFT ${stripBankAccountType(name).replace('HAVALE ', '')}`, amount: byAccount[name].total });
        });
        // byAccount toplamı salesTotal'dan düşükse farkı "Havale/EFT Diğer" olarak ekle
        const byAccountTotal = accountNames.reduce((s, n) => s + (byAccount[n].total || 0), 0);
        if (transferSalesTotal > byAccountTotal + 0.01) {
          gelirler.push({ name: 'Havale/EFT Diğer', amount: transferSalesTotal - byAccountTotal });
        }
      } else {
        gelirler.push({ name: 'Havale/EFT', amount: transferSalesTotal });
      }
    }
    // Havale/EFT tahsilat gelirlerini hesap bazlı göster
    const transferCollTotal = endOfDayReport.transfer.collectionTotal || 0;
    if (transferCollTotal > 0) {
      const collByAccount = endOfDayReport.transfer.collectionByAccount || {};
      const collAccountNames = Object.keys(collByAccount);
      if (collAccountNames.length > 0) {
        collAccountNames.forEach(name => {
          if (collByAccount[name].total > 0) gelirler.push({ name: `Tahsilat ${stripBankAccountType(name).replace('HAVALE ', '')}`, amount: collByAccount[name].total });
        });
        const collByAccountTotal = collAccountNames.reduce((s, n) => s + (collByAccount[n].total || 0), 0);
        if (transferCollTotal > collByAccountTotal + 0.01) {
          gelirler.push({ name: 'Havale/EFT Tahsilat Diğer', amount: transferCollTotal - collByAccountTotal });
        }
      } else {
        gelirler.push({ name: 'Havale/EFT Tahsilat', amount: transferCollTotal });
      }
    }
    // Havale/EFT tamirat gelirlerini hesap bazlı göster
    const transferRepairTotal = endOfDayReport.transfer.repairTotal || 0;
    if (transferRepairTotal > 0) {
      const repairByAccount = endOfDayReport.transfer.repairByAccount || {};
      const repairAccountNames = Object.keys(repairByAccount);
      if (repairAccountNames.length > 0) {
        repairAccountNames.forEach(name => {
          if (repairByAccount[name].total > 0) gelirler.push({ name: `Havale/EFT Tamirat ${stripBankAccountType(name).replace('HAVALE ', '')}`, amount: repairByAccount[name].total });
        });
        const repairByAccountTotal = repairAccountNames.reduce((s, n) => s + (repairByAccount[n].total || 0), 0);
        if (transferRepairTotal > repairByAccountTotal + 0.01) {
          gelirler.push({ name: 'Havale/EFT Tamirat Diğer', amount: transferRepairTotal - repairByAccountTotal });
        }
      } else {
        gelirler.push({ name: 'Havale/EFT Tamirat', amount: transferRepairTotal });
      }
    }
    if (endOfDayReport.voucher && endOfDayReport.voucher.total > 0) gelirler.push({ name: 'Bakiye', amount: endOfDayReport.voucher.total });
    if (endOfDayReport.scrap?.saleLinkedGoldTotal > 0) gelirler.push({ name: 'Altın Hurda', amount: endOfDayReport.scrap.saleLinkedGoldTotal });
    if (endOfDayReport.scrap?.saleLinkedSilverTotal > 0) gelirler.push({ name: 'Gümüş Hurda', amount: endOfDayReport.scrap.saleLinkedSilverTotal });
    if ((endOfDayReport.collections?.hurdaTotal || 0) > 0) gelirler.push({ name: 'Hurda ile Tahsilat', amount: endOfDayReport.collections.hurdaTotal });
    const gelirToplam = gelirler.reduce((s, g) => s + g.amount, 0);

    const giderler = [];
    if (endOfDayReport.cardRefund && endOfDayReport.cardRefund.total > 0) giderler.push({ name: 'Kredi Kartı İade', amount: endOfDayReport.cardRefund.total });
    if (endOfDayReport.cashRefund && endOfDayReport.cashRefund.total > 0) giderler.push({ name: 'Nakit İade', amount: endOfDayReport.cashRefund.total });
    if (endOfDayReport.scrapCashRefund && endOfDayReport.scrapCashRefund.total > 0) giderler.push({ name: 'Hurda Nakit İadesi', amount: endOfDayReport.scrapCashRefund.total });
    if (endOfDayReport.sanalPosRefund && endOfDayReport.sanalPosRefund.total > 0) giderler.push({ name: 'Sanal Pos İade', amount: endOfDayReport.sanalPosRefund.total });
    if (endOfDayReport.havaleRefund && endOfDayReport.havaleRefund.total > 0) giderler.push({ name: 'Havale İade', amount: endOfDayReport.havaleRefund.total });
    if (endOfDayReport.exchanges && endOfDayReport.exchanges.cashRefundTotal > 0) giderler.push({ name: 'Değişim Nakit İade', amount: endOfDayReport.exchanges.cashRefundTotal });
    if (endOfDayReport.exchanges && endOfDayReport.exchanges.cardRefundTotal > 0) giderler.push({ name: 'Değişim Kredi Kartı İade', amount: endOfDayReport.exchanges.cardRefundTotal });
    if (endOfDayReport.exchanges && endOfDayReport.exchanges.havaleRefundTotal > 0) giderler.push({ name: 'Değişim Havale İade', amount: endOfDayReport.exchanges.havaleRefundTotal });
    if (endOfDayReport.expenses && endOfDayReport.expenses.total > 0) giderler.push({ name: 'Masraflar', amount: endOfDayReport.expenses.total });
    if (endOfDayReport.scrap && endOfDayReport.scrap.standaloneTotal > 0) giderler.push({ name: 'Hurda Alımları', amount: endOfDayReport.scrap.standaloneTotal });
    const giderToplam = giderler.reduce((s, g) => s + g.amount, 0);

    const kasaGenel = gelirToplam - giderToplam;
    const fmt = (v) => v.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    const kasaOzeti = `
        <div style="border-top: 3px double #000; margin-top: 25px; padding-top: 15px;">
          <div style="text-align:center;font-size:16px;font-weight:bold;margin-bottom:18px;letter-spacing:1px;">KASA ÖZETİ</div>
        </div>

        <div style="margin-bottom:18px;">
          <div style="background:#fff;padding:8px 12px;font-weight:bold;font-size:13px;color:#000;border:1px solid #000;border-radius:4px;margin-bottom:8px;">(+) GELİRLER</div>
          <div style="display:flex;justify-content:space-between;padding:4px 12px;font-size:12px;color:#000;border-bottom:1px solid #000;margin-bottom:4px;">
            <span style="font-weight:700;">İŞLEM ADI</span>
            <span style="font-weight:700;">TUTAR</span>
          </div>
          ${gelirler.map(g => `
          <div style="display:flex;justify-content:space-between;padding:5px 12px;font-size:13px;">
            <span>${g.name}</span>
            <span>${fmt(g.amount)}</span>
          </div>
          `).join('')}
          <div style="border-top:1px dashed #999;margin-top:6px;padding-top:6px;display:flex;justify-content:flex-end;padding-right:12px;font-size:13px;font-weight:bold;">
            TOPLAM : ${fmt(gelirToplam)} (+)
          </div>
        </div>

        <div style="margin-bottom:18px;">
          <div style="background:#fff;padding:8px 12px;font-weight:bold;font-size:13px;color:#000;border:1px solid #000;border-radius:4px;margin-bottom:8px;">(-) GİDERLER</div>
          <div style="display:flex;justify-content:space-between;padding:4px 12px;font-size:12px;color:#000;border-bottom:1px solid #000;margin-bottom:4px;">
            <span style="font-weight:700;">İŞLEM ADI</span>
            <span style="font-weight:700;">TUTAR</span>
          </div>
          ${giderler.length > 0 ? giderler.map(g => `
          <div style="display:flex;justify-content:space-between;padding:5px 12px;font-size:13px;">
            <span>${g.name}</span>
            <span>-${fmt(g.amount)}</span>
          </div>
          `).join('') : `
          <div style="padding:5px 12px;font-size:13px;color:#000;">Gider bulunmuyor</div>
          `}
          <div style="border-top:1px dashed #999;margin-top:6px;padding-top:6px;display:flex;justify-content:flex-end;padding-right:12px;font-size:13px;font-weight:bold;">
            TOPLAM : ${fmt(giderToplam)} (-)
          </div>
        </div>

        <div style="border-top:3px double #000;border-bottom:3px double #000;padding:12px 0;margin:20px 0;text-align:center;">
          <div style="font-size:15px;font-weight:bold;letter-spacing:0.5px;">KASA GENEL TOPLAM : ${fmt(kasaGenel)} ${kasaGenel >= 0 ? '(+)' : '(-)'}</div>
        </div>

        <div style="margin-top:10px;">
          <div style="display:flex;justify-content:flex-end;padding:6px 12px;font-size:13px;font-weight:bold;border-bottom:1px dashed #999;">
            NAKİT KASA TOPLAM : ${fmt(endOfDayReport.expectedCash)} ${endOfDayReport.expectedCash >= 0 ? '(+)' : '(-)'}
          </div>
          <div style="display:flex;justify-content:flex-end;padding:6px 12px;font-size:13px;border-bottom:1px dashed #999;">
            AÇIK HESAP SATIŞLAR : ${fmt(endOfDayReport.openAccount?.total || 0)}
          </div>
          <div style="display:flex;justify-content:flex-end;padding:6px 12px;font-size:13px;border-bottom:1px dashed #999;">
            MÜŞTERİ BAKİYESİ SATIŞLAR : ${fmt(endOfDayReport.musteriBakiyesiSales?.total || 0)}
          </div>
          <div style="display:flex;justify-content:flex-end;padding:6px 12px;font-size:13px;border-bottom:1px dashed #999;">
            AÇIK HESABA İADELER : ${fmt((endOfDayReport.exchanges?.voucherRefundTotal || 0) + (endOfDayReport.acikHesapSalesRefund?.total || 0) + (endOfDayReport.bakiyeRefund?.total || 0))}
          </div>
          <div style="display:flex;justify-content:flex-end;padding:6px 12px;font-size:13px;border-bottom:1px dashed #999;">
            İNDİRİMLER TOPLAM : ${fmt(endOfDayReport.totalDiscount || 0)}
          </div>
        </div>
    `;

    // Sayfa 2: Kasiyer Raporu
    const page2 = `
        <div style="border-top: 3px solid #000; margin-top: 20px; padding-top: 15px;">
          <div style="text-align:center;font-size:14px;font-weight:bold;margin-bottom:15px;">Kasiyer Raporu</div>
        </div>

        ${(endOfDayReport.cashierList || []).map(cashier => `
          <div class="cashier-block">
            <div class="cashier-header">
              <span class="cashier-name">${cashier.name} (${cashier.salesCount})</span>
              <span class="cashier-total">₺${cashier.total.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            </div>
            ${cashier.sales.map(sale => {
              const printAmt = sale.sale_type === 'exchange_return' ? Number(sale.total) : getSaleDisplayAmount(sale);
              return `
              <div class="transaction">
                <div class="transaction-left">
                  <span class="transaction-time">${new Date(sale.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}</span>
                  <span>${sale.customer_name || '-'}</span>
                </div>
                <div style="text-align:right;">
                  <div class="transaction-amount" ${printAmt < 0 ? 'style="color:#000;font-weight:700"' : ''}>${printAmt < 0 ? '-' : ''}₺${Math.abs(printAmt).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                  <div class="transaction-method">${sale.sale_type === 'exchange_return' ? 'Değişim İade' : formatPaymentMethod(sale)}</div>
                </div>
              </div>
            `}).join('')}
          </div>
        `).join('')}

        <div class="footer">
          Yazdırma: ${new Date().toLocaleString('tr-TR')}
        </div>
    `;

    const eodHtml = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Günlük Rapor - ${reportDate}</title>
        <style>${commonStyles}</style>
      </head>
      <body>${page1}${kasaOzeti}${page2}
        <script>
          window.onload = function() {
            var directPrint = localStorage.getItem('directPrintEnabled') === 'true';
            setTimeout(function() {
              window.print();
              if (directPrint) { setTimeout(function() { window.close(); }, 500); }
            }, 250);
          }
        </script>
      </body>
      </html>
    `;
    handlePrint(eodHtml, 'receipt');
  };

  // Kasa Özeti yazdır
  const printKasaOzeti = () => {
    if (!endOfDayReport) return;
    const reportDate = eodDate.toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric' });
    const weekday = eodDate.toLocaleDateString('tr-TR', { weekday: 'long' });
    const fmt = (v) => (v || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    // Hesaplama iOS WebView sayfası (MobileKasaOzeti) ile ortak: src/utils/kasaOzeti.js
    const kasa = computeKasaOzeti(endOfDayReport);
    const { gelirTop, giderTop, kasaGenel } = kasa;
    const gelirItems = kasa.gelirItems.map(i => [i.label, fmt(i.amount)]);
    const giderItems = kasa.giderItems.length > 0
      ? kasa.giderItems.map(i => [i.label, '-' + fmt(i.amount)])
      : [['Gider bulunmuyor', '0,00']];

    const kasaHtml = `<!DOCTYPE html><html><head><title>Kasa Özeti - ${reportDate}</title>
    <style>
      * { margin: 0; padding: 0; box-sizing: border-box; }
      body { font-family: 'Courier New', Courier, monospace; padding: 20px; max-width: 500px; margin: 0 auto; color: #000; font-size: 13px; font-weight: 600; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .header { border: 1px solid #000; padding: 10px 14px; margin-bottom: 18px; }
      .header-title { font-size: 13px; font-weight: 800; letter-spacing: 0.3px; padding-bottom: 8px; margin-bottom: 8px; border-bottom: 1px dashed #999; }
      .header-meta-row { display: flex; justify-content: space-between; align-items: baseline; gap: 16px; padding: 3px 0; }
      .header-meta-label { font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.3px; color: #000; white-space: nowrap; }
      .header-meta-value { font-size: 12px; font-weight: 700; text-align: right; }
      .store-bar { font-weight: bold; padding: 5px 8px; background: #fff; border: 1px solid #000; margin-bottom: 5px; }
      .section { margin-bottom: 16px; }
      .section-title { font-weight: bold; padding: 4px 8px; border: 1px solid #000; margin-bottom: 8px; }
      .section-title.gelir { background: #fff; border-color: #000; }
      .section-title.gider { background: #fff; border-color: #000; }
      .table-header { display: flex; justify-content: space-between; font-weight: bold; padding: 3px 12px; border-bottom: 1px dotted #000; margin-bottom: 3px; font-size: 12px; }
      .row { display: flex; justify-content: space-between; padding: 2px 12px; }
      .section-total { text-align: right; font-weight: bold; padding: 5px 12px 0; margin-top: 4px; border-top: 1px dashed #999; }
      .genel-toplam { text-align: center; font-weight: bold; font-size: 14px; padding: 12px 0; margin: 20px 0 8px; border-top: 3px double #000; border-bottom: 3px double #000; }
      .alt-row { text-align: right; font-weight: bold; padding: 4px 0; border-bottom: 1px dashed #999; }
      @media print { body { padding: 10px; font-weight: 600; color: #000; -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
    </style></head><body>
      <div class="header">
        <div class="header-title">KASA DEFTERİ RAPORU DETAYLI</div>
        <div class="header-meta-row"><span class="header-meta-label">Tarih / Saat</span><span class="header-meta-value">${reportDate}, ${weekday}</span></div>
      </div>
      <div class="store-bar">${endOfDayReport.storeName || 'Merkez'}</div>
      <div class="section">
        <div class="section-title gelir">(+) GELİRLER</div>
        <div class="table-header"><span>İŞLEM ADI</span><span>TUTAR</span></div>
        ${gelirItems.map(([n, v]) => `<div class="row"><span>${n}</span><span>${v}</span></div>`).join('')}
        <div class="section-total">TOPLAM : ${fmt(gelirTop)} (+)</div>
      </div>
      <div class="section">
        <div class="section-title gider">(-) GİDERLER</div>
        <div class="table-header"><span>İŞLEM ADI</span><span>TUTAR</span></div>
        ${giderItems.map(([n, v]) => `<div class="row"><span>${n}</span><span>${v}</span></div>`).join('')}
        <div class="section-total">TOPLAM : ${fmt(giderTop)} (-)</div>
      </div>
      <div class="genel-toplam">KASA GENEL TOPLAM : &nbsp; ${fmt(kasaGenel)} ${kasaGenel >= 0 ? '(+)' : '(-)'}</div>
      ${kasa.summaryRows.map(row => '<div class="alt-row">' + row.label + ' : ' + formatKasaSummaryValue(row) + '</div>').join('')}
    <script>
      window.onload = function() {
        var directPrint = localStorage.getItem('directPrintEnabled') === 'true';
        setTimeout(function() {
          window.print();
          if (directPrint) { setTimeout(function() { window.close(); }, 500); }
        }, 250);
      }
    </script>
    </body></html>`;
    handlePrint(kasaHtml, 'receipt');
  };

  // Bir kanvası, verilen "kesilmemesi gereken" sınırlara (satır/blok altları, canvas px) göre
  // sayfa yüksekliğine sığacak parçalara böler; hiçbir satır/blok ortadan kesilmez.
  const sliceCanvasIntoPages = (canvas, boundariesCanvasPx, pageHeightPx) => {
    const slices = [];
    let cursor = 0;
    const total = canvas.height;
    while (cursor < total - 1) {
      let sliceEnd = Math.min(cursor + pageHeightPx, total);
      if (sliceEnd < total) {
        let snapped = 0;
        for (const b of boundariesCanvasPx) {
          if (b <= sliceEnd && b > cursor) snapped = b;
          if (b >= sliceEnd) break;
        }
        if (snapped > cursor) sliceEnd = snapped;
      }
      slices.push([cursor, sliceEnd]);
      cursor = sliceEnd;
    }
    return slices;
  };

  const addCanvasSliceToPdf = (pdf, canvas, sliceStart, sliceEnd, marginMm, imgWidthMm, pxPerMm) => {
    const sliceHeightPx = sliceEnd - sliceStart;
    const sliceCanvas = document.createElement('canvas');
    sliceCanvas.width = canvas.width;
    sliceCanvas.height = sliceHeightPx;
    sliceCanvas.getContext('2d').drawImage(canvas, 0, sliceStart, canvas.width, sliceHeightPx, 0, 0, canvas.width, sliceHeightPx);
    const imgData = sliceCanvas.toDataURL('image/jpeg', 0.98);
    const sliceHeightMm = sliceHeightPx / pxPerMm;
    pdf.addImage(imgData, 'JPEG', marginMm, marginMm, imgWidthMm, sliceHeightMm);
  };

  const exportEndOfDayPdf = async () => {
    if (!endOfDayReport) return;

    const reportDate = eodDate.toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric' });
    const weekday = eodDate.toLocaleDateString('tr-TR', { weekday: 'long' });
    const dateStr = eodDate.toISOString().split('T')[0];
    const fmt = (v) => (v || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    const logoHtml = endOfDayReport.storeId === 1
      ? `<img src="/Siyah - Beyaz - 1.png" alt="Akik Gold Jewellery" class="logo-img" />`
      : `<div class="logo-wrap-silver"><img src="/akikfisligi.png" alt="Akik Gümüş" /></div>`;

    // Kasa Özeti hesaplamaları (printKasaOzeti ile birebir aynı mantık - tab'dan bağımsız, her zaman gösterilir)
    const gelirItems = [];
    const pNakitOdeme = endOfDayReport.cash.salesTotal ?? ((endOfDayReport.cash.total || 0) - (endOfDayReport.cash.collectionTotal || 0) - (endOfDayReport.cash.repairTotal || 0));
    if (pNakitOdeme > 0) gelirItems.push(['Nakit Ödeme', fmt(pNakitOdeme)]);
    if ((endOfDayReport.cash.collectionTotal || 0) > 0) gelirItems.push(['Nakit Tahsilat', fmt(endOfDayReport.cash.collectionTotal)]);
    if ((endOfDayReport.cash.repairTotal || 0) > 0) gelirItems.push(['Nakit Tamirat', fmt(endOfDayReport.cash.repairTotal)]);
    eTicaretGelirRows(endOfDayReport.sanalPos).forEach(([label, amount]) => gelirItems.push([label, fmt(amount)]));
    if ((endOfDayReport.card.salesTotal ?? endOfDayReport.card.total) > 0) gelirItems.push(['Kredi Kartı', fmt(endOfDayReport.card.salesTotal ?? endOfDayReport.card.total)]);
    if ((endOfDayReport.card.collectionTotal || 0) > 0) gelirItems.push(['Kredi Kartı Tahsilat', fmt(endOfDayReport.card.collectionTotal)]);
    if ((endOfDayReport.card.repairTotal || 0) > 0) gelirItems.push(['Kredi Kartı Tamirat', fmt(endOfDayReport.card.repairTotal)]);
    const pTSalesTotal = endOfDayReport.transfer.salesTotal ?? endOfDayReport.transfer.total ?? 0;
    const pByAcc = endOfDayReport.transfer.byAccount || {};
    const pAccNames = Object.keys(pByAcc);
    if (pTSalesTotal > 0 && pAccNames.length > 0) {
      pAccNames.filter(n => pByAcc[n].total > 0).forEach(name => {
        gelirItems.push([`Havale/EFT ${stripBankAccountType(name).replace('HAVALE ', '')}`, fmt(pByAcc[name].total)]);
      });
      const pByAccTotal = pAccNames.reduce((s, n) => s + (pByAcc[n].total || 0), 0);
      if (pTSalesTotal > pByAccTotal + 0.01) gelirItems.push(['Havale/EFT Diğer', fmt(pTSalesTotal - pByAccTotal)]);
    } else if (pTSalesTotal > 0) {
      gelirItems.push(['Havale/EFT', fmt(pTSalesTotal)]);
    }
    const pCollTotal = endOfDayReport.transfer.collectionTotal || 0;
    const pCollByAcc = endOfDayReport.transfer.collectionByAccount || {};
    const pCollAccNames = Object.keys(pCollByAcc);
    if (pCollTotal > 0 && pCollAccNames.length > 0) {
      pCollAccNames.filter(n => pCollByAcc[n].total > 0).forEach(name => {
        gelirItems.push([`Tahsilat ${stripBankAccountType(name).replace('HAVALE ', '')}`, fmt(pCollByAcc[name].total)]);
      });
      const pCollByAccTotal = pCollAccNames.reduce((s, n) => s + (pCollByAcc[n].total || 0), 0);
      if (pCollTotal > pCollByAccTotal + 0.01) gelirItems.push(['Havale/EFT Tahsilat Diğer', fmt(pCollTotal - pCollByAccTotal)]);
    } else if (pCollTotal > 0) {
      gelirItems.push(['Havale/EFT Tahsilat', fmt(pCollTotal)]);
    }
    // Havale/EFT tamirat gelirlerini hesap bazlı göster
    const pRepairTotal = endOfDayReport.transfer.repairTotal || 0;
    const pRepairByAcc = endOfDayReport.transfer.repairByAccount || {};
    const pRepairAccNames = Object.keys(pRepairByAcc);
    if (pRepairTotal > 0 && pRepairAccNames.length > 0) {
      pRepairAccNames.filter(n => pRepairByAcc[n].total > 0).forEach(name => {
        gelirItems.push([`Havale/EFT Tamirat ${stripBankAccountType(name).replace('HAVALE ', '')}`, fmt(pRepairByAcc[name].total)]);
      });
      const pRepairByAccTotal = pRepairAccNames.reduce((s, n) => s + (pRepairByAcc[n].total || 0), 0);
      if (pRepairTotal > pRepairByAccTotal + 0.01) gelirItems.push(['Havale/EFT Tamirat Diğer', fmt(pRepairTotal - pRepairByAccTotal)]);
    } else if (pRepairTotal > 0) {
      gelirItems.push(['Havale/EFT Tamirat', fmt(pRepairTotal)]);
    }
    if (endOfDayReport.voucher?.total > 0) gelirItems.push(['Bakiye', fmt(endOfDayReport.voucher.total)]);
    if (endOfDayReport.scrap?.saleLinkedGoldTotal > 0) gelirItems.push(['Altın Hurda', fmt(endOfDayReport.scrap.saleLinkedGoldTotal)]);
    if (endOfDayReport.scrap?.saleLinkedSilverTotal > 0) gelirItems.push(['Gümüş Hurda', fmt(endOfDayReport.scrap.saleLinkedSilverTotal)]);
    if ((endOfDayReport.collections?.hurdaTotal || 0) > 0) gelirItems.push(['Hurda ile Tahsilat', fmt(endOfDayReport.collections.hurdaTotal)]);
    const gelirTop = (endOfDayReport.cash.total || 0) + (endOfDayReport.sanalPos?.total || 0) + (endOfDayReport.card.salesTotal ?? endOfDayReport.card.total ?? 0) + (endOfDayReport.card.collectionTotal || 0) + (endOfDayReport.card.repairTotal || 0) + (endOfDayReport.transfer.salesTotal ?? endOfDayReport.transfer.total ?? 0) + (endOfDayReport.transfer.collectionTotal || 0) + (endOfDayReport.transfer.repairTotal || 0) + (endOfDayReport.voucher?.total || 0) + (endOfDayReport.scrap?.saleLinkedTotal || 0) + (endOfDayReport.collections?.hurdaTotal || 0);

    const giderItems = [];
    if (endOfDayReport.cardRefund?.total > 0) giderItems.push(['Kredi Kartı İade', '-' + fmt(endOfDayReport.cardRefund.total)]);
    if (endOfDayReport.cashRefund?.total > 0) giderItems.push(['Nakit İade', '-' + fmt(endOfDayReport.cashRefund.total)]);
    if (endOfDayReport.scrapCashRefund?.total > 0) giderItems.push(['Hurda Nakit İadesi', '-' + fmt(endOfDayReport.scrapCashRefund.total)]);
    if (endOfDayReport.sanalPosRefund?.total > 0) giderItems.push(['Sanal Pos İade', '-' + fmt(endOfDayReport.sanalPosRefund.total)]);
    if (endOfDayReport.havaleRefund?.total > 0) giderItems.push(['Havale İade', '-' + fmt(endOfDayReport.havaleRefund.total)]);
    if (endOfDayReport.exchanges?.cashRefundTotal > 0) giderItems.push(['Değişim Nakit İade', '-' + fmt(endOfDayReport.exchanges.cashRefundTotal)]);
    if (endOfDayReport.exchanges?.cardRefundTotal > 0) giderItems.push(['Değişim Kredi Kartı İade', '-' + fmt(endOfDayReport.exchanges.cardRefundTotal)]);
    if (endOfDayReport.exchanges?.havaleRefundTotal > 0) giderItems.push(['Değişim Havale İade', '-' + fmt(endOfDayReport.exchanges.havaleRefundTotal)]);
    if (endOfDayReport.expenses?.total > 0) giderItems.push(['Masraf Nakit Ödeme', '-' + fmt(endOfDayReport.expenses.total)]);
    if (endOfDayReport.scrap?.standaloneTotal > 0) giderItems.push(['Hurda Alımları', '-' + fmt(endOfDayReport.scrap.standaloneTotal)]);
    const giderTop = (endOfDayReport.cardRefund?.total || 0) + (endOfDayReport.cashRefund?.total || 0) + (endOfDayReport.scrapCashRefund?.total || 0) + (endOfDayReport.sanalPosRefund?.total || 0) + (endOfDayReport.havaleRefund?.total || 0) + (endOfDayReport.exchanges?.cashRefundTotal || 0) + (endOfDayReport.exchanges?.cardRefundTotal || 0) + (endOfDayReport.exchanges?.havaleRefundTotal || 0) + (endOfDayReport.expenses?.total || 0) + (endOfDayReport.scrap?.standaloneTotal || 0);
    if (giderItems.length === 0) giderItems.push(['Gider bulunmuyor', '0,00']);

    const kasaGenel = gelirTop - giderTop;

    // Sayfa 1 stilleri: printKasaOzeti ile birebir aynı tasarım (dikey/portre), tek sayfaya sığacak şekilde küçültülmüş
    // NOT: "body{...}" gerçek dokümanın <body>'sini hedefler, bizim off-screen render kutumuzu değil - bu yüzden ".pdf-root" class'ı kullanılıyor
    const page1Styles = `
      * { margin: 0; padding: 0; box-sizing: border-box; }
      .pdf-root { font-family: 'Courier New', Courier, monospace; color: #000; font-size: 9px; font-weight: 600; width: 420px; line-height: 1.15; }
      .header { border: 1px solid #000; padding: 4px 8px; margin-bottom: 6px; }
      .header-title { font-size: 8.5px; font-weight: 800; letter-spacing: 0.2px; padding-bottom: 3px; margin-bottom: 3px; border-bottom: 1px dashed #999; }
      .header-meta-row { display: flex; justify-content: space-between; align-items: baseline; gap: 8px; padding: 1px 0; }
      .header-meta-label { font-size: 8px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.2px; color: #000; white-space: nowrap; }
      .header-meta-value { font-size: 8px; font-weight: 700; text-align: right; }
      .store-bar { font-weight: bold; padding: 2px 7px; background: #fff; border: 1px solid #000; margin-bottom: 2px; }
      .section { margin-bottom: 4px; }
      .section-title { font-weight: bold; padding: 1.5px 7px; border: 1px solid #000; margin-bottom: 2px; }
      .table-header { display: flex; justify-content: space-between; font-weight: bold; padding: 1px 9px; border-bottom: 1px dotted #000; margin-bottom: 0.5px; font-size: 8px; }
      .row { display: flex; justify-content: space-between; padding: 0 9px; }
      .section-total { text-align: right; font-weight: bold; padding: 1.5px 9px 0; margin-top: 1.5px; border-top: 1px dashed #999; }
      .genel-toplam { text-align: center; font-weight: bold; font-size: 9.5px; padding: 3px 0; margin: 5px 0 2px; border-top: 2px double #000; border-bottom: 2px double #000; }
      .alt-row { text-align: right; font-weight: bold; padding: 1px 0; border-bottom: 1px dashed #999; }
    `;

    // Sayfa 1 içeriği: printKasaOzeti ile birebir aynı markup
    const page1Body = `
      <div class="header">
        <div class="header-title">KASA DEFTERİ RAPORU DETAYLI</div>
        <div class="header-meta-row"><span class="header-meta-label">Tarih / Saat</span><span class="header-meta-value">${reportDate}, ${weekday}</span></div>
      </div>
      <div class="store-bar">${endOfDayReport.storeName || 'Merkez'}</div>
      <div class="section">
        <div class="section-title">(+) GELİRLER</div>
        <div class="table-header"><span>İŞLEM ADI</span><span>TUTAR</span></div>
        ${gelirItems.map(([n, v]) => `<div class="row"><span>${n}</span><span>${v}</span></div>`).join('')}
        <div class="section-total">TOPLAM : ${fmt(gelirTop)} (+)</div>
      </div>
      <div class="section">
        <div class="section-title">(-) GİDERLER</div>
        <div class="table-header"><span>İŞLEM ADI</span><span>TUTAR</span></div>
        ${giderItems.map(([n, v]) => `<div class="row"><span>${n}</span><span>${v}</span></div>`).join('')}
        <div class="section-total">TOPLAM : ${fmt(giderTop)} (-)</div>
      </div>
      <div class="genel-toplam">KASA GENEL TOPLAM : &nbsp; ${fmt(kasaGenel)} ${kasaGenel >= 0 ? '(+)' : '(-)'}</div>
      ${(() => {
        const r = endOfDayReport;
        const netKK = ((r.card?.salesTotal ?? 0) + (r.card?.collectionTotal ?? 0) + (r.card?.repairTotal ?? 0)) - ((r.cardRefund?.total ?? 0) + (r.exchanges?.cardRefundTotal ?? 0) + (r.scrap?.cardTotal ?? 0));
        const tba = r.transfer?.byAccount || {};
        const cba = r.transfer?.collectionByAccount || {};
        const rba = r.transfer?.repairByAccount || {};
        const sba = r.scrap?.transferByAccount || {};
        const allAcc = [...new Set([...Object.keys(tba), ...Object.keys(cba), ...Object.keys(rba), ...Object.keys(sba)])];
        let nhs = 0, nhp = 0, nhe = 0;
        allAcc.forEach(a => { const g = (tba[a]?.total ?? 0) + (cba[a]?.total ?? 0) + (rba[a]?.total ?? 0); const d = sba[a]?.total ?? 0; const al = a.toLocaleLowerCase('tr'); if (al.includes('şahıs') || al.includes('sahis') || a.includes('(Şahıs)')) { nhp += g - d; } else if (al.includes('e-ticaret') || al.includes('eticaret') || al.includes('e ticaret') || a.includes('E-TİCARET')) { nhe += g - d; } else { nhs += g - d; } });
        const nh = r.scrap?.total ?? 0;
        let rows = '';
        if ((r.expectedCash || 0) !== 0) rows += '<div class="alt-row">NAKİT KASA TOPLAM : ' + fmt(r.expectedCash) + ' ' + ((r.expectedCash || 0) >= 0 ? '(+)' : '(-)') + '</div>';
        if (netKK !== 0) rows += '<div class="alt-row">NET KREDİ KARTI : ' + fmt(netKK) + ' ' + (netKK >= 0 ? '(+)' : '(-)') + '</div>';
        if (nhp !== 0) rows += '<div class="alt-row">NET HAVALE ŞAHIS : ' + fmt(nhp) + ' ' + (nhp >= 0 ? '(+)' : '(-)') + '</div>';
        if (nhs !== 0) rows += '<div class="alt-row">NET HAVALE ŞİRKET : ' + fmt(nhs) + ' ' + (nhs >= 0 ? '(+)' : '(-)') + '</div>';
        if (nhe !== 0) rows += '<div class="alt-row">NET HAVALE E-TİCARET : ' + fmt(nhe) + ' ' + (nhe >= 0 ? '(+)' : '(-)') + '</div>';
        eTicaretNetRows(r.sanalPos).forEach(([label, amount]) => { rows += '<div class="alt-row">' + label + ' : ' + fmt(amount) + ' ' + (amount >= 0 ? '(+)' : '(-)') + '</div>'; });
        if (nh !== 0) rows += '<div class="alt-row">TOPLAM HURDA TUTARI : ' + fmt(nh) + '</div>';
        if ((r.openAccount?.total || 0) !== 0) rows += '<div class="alt-row">AÇIK HESAP SATIŞLAR : ' + fmt(r.openAccount?.total) + '</div>';
        if ((r.musteriBakiyesiSales?.total || 0) !== 0) rows += '<div class="alt-row">MÜŞTERİ BAKİYESİ SATIŞLAR : ' + fmt(r.musteriBakiyesiSales?.total) + '</div>';
        if (((r.exchanges?.voucherRefundTotal || 0) + (r.acikHesapSalesRefund?.total || 0) + (r.bakiyeRefund?.total || 0)) !== 0) rows += '<div class="alt-row">AÇIK HESABA İADELER : ' + fmt((r.exchanges?.voucherRefundTotal || 0) + (r.acikHesapSalesRefund?.total || 0) + (r.bakiyeRefund?.total || 0)) + '</div>';
        if ((r.totalDiscount || 0) !== 0) rows += '<div class="alt-row">İNDİRİMLER TOPLAM : ' + fmt(r.totalDiscount) + '</div>';
        return rows;
      })()}
    `;

    // Sayfa 2 stilleri (Yatay - değişmedi, sadece satır hizalaması netleştirildi)
    // NOT: "body{...}" gerçek dokümanın <body>'sini hedefler, bizim off-screen render kutumuzu değil - bu yüzden ".pdf-root" class'ı kullanılıyor
    const commonStyles = `
      * { margin: 0; padding: 0; box-sizing: border-box; }
      .pdf-root { font-family: 'Segoe UI', Arial, sans-serif; padding: 15px; width: 780px; margin: 0 auto; }
      .header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 15px; padding-bottom: 10px; border-bottom: 2px solid #333; }
      .header-left { display: flex; align-items: center; gap: 15px; }
      .header .logo-img { max-width: 120px; height: auto; }
      .header .logo-wrap-silver { text-align: center; }
      .header .logo-wrap-silver img { width: 110px; height: auto; filter: contrast(3); }
      .header-info { text-align: left; }
      .header-info .store-name { font-size: 16px; font-weight: bold; }
      .header-info .date { font-size: 13px; color: #666; }
      .header-right { text-align: right; }
      .header-right .label { font-size: 11px; color: #666; }
      .header-right .amount { font-size: 24px; font-weight: bold; color: #0f766e; }
      .header-right .count { font-size: 11px; color: #666; }
      .section { margin-bottom: 14px; }
      .footer { text-align: center; margin-top: 15px; padding-top: 10px; border-top: 1px solid #ddd; font-size: 11px; color: #999; }
      .products-table { width: 100%; border-collapse: collapse; font-size: 9px; }
      .products-table th { background: #f5f5f5; padding: 4px 4px; text-align: left; vertical-align: middle; font-weight: 600; border-bottom: 2px solid #ddd; font-size: 8px; white-space: nowrap; line-height: 1.4; }
      .products-table td { padding: 3px 4px; vertical-align: middle; border-bottom: 1px dashed #eee; font-size: 8px; line-height: 1.4; }
      .products-table th:last-child, .products-table td:last-child { text-align: right; }
    `;

    // Yardımcı: Ödeme tipi detaylı formatlama
    const formatPdfPayment = (p) => {
      if (p.paymentMethod === 'Karışık' && p.paymentDetails) {
        try {
          const details = typeof p.paymentDetails === 'string' ? JSON.parse(p.paymentDetails) : p.paymentDetails;
          if (Array.isArray(details) && details.length > 0) {
            return details.map(d => d.method || '').filter(Boolean).join(' + ');
          }
        } catch (e) {}
      }
      return p.paymentMethod || '-';
    };

    // Yardımcı: Has gram hesaplama
    const karatRates = { '8': 0.333, '9': 0.375, '10': 0.417, '14': 0.585, '18': 0.750, '21': 0.875, '22': 0.916, '24-995': 0.995, '24-999': 0.999 };
    const calcPureWeight = (p) => {
      if (p.pureWeight) return parseFloat(p.pureWeight);
      const w = parseFloat(p.weight) || 0;
      if (!w) return null;
      if (p.metalType === 'GOLD' || p.metalType === 'gold') {
        const rate = karatRates[p.purity] || 0;
        return rate > 0 ? w * rate : null;
      }
      if (p.metalType === 'SILVER' || p.metalType === 'silver') {
        return w * 0.925;
      }
      return null;
    };

    // Yardımcı: Kur bilgisi
    const getRate = (p) => {
      if (p.rateUsed) return parseFloat(p.rateUsed);
      if ((p.metalType === 'GOLD' || p.metalType === 'gold') && p.goldRate) return parseFloat(p.goldRate);
      if ((p.metalType === 'SILVER' || p.metalType === 'silver') && p.silverRate) return parseFloat(p.silverRate);
      return null;
    };

    // Sayfa 2: Tüm İşlemler Tablosu (Yatay - Tam Genişlik, değişmedi)
    const page2Body = endOfDayReport.allProducts && endOfDayReport.allProducts.length > 0 ? `
        <div class="header">
          <div class="header-left">
            ${logoHtml}
            <div class="header-info">
              <div class="store-name">${endOfDayReport.storeName}</div>
              <div class="date">${reportDate} - Tüm İşlemler</div>
            </div>
          </div>
          <div class="header-right">
            <div class="label">Toplam Ciro</div>
            <div class="amount">₺${endOfDayReport.grandTotal.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
            <div class="count">${endOfDayReport.allProducts.length} ürün / ${endOfDayReport.totalSales + (endOfDayReport.exchanges?.count || 0)} işlem</div>
          </div>
        </div>

        <div class="section">
          <table class="products-table">
            <thead>
              <tr>
                <th>Saat</th>
                <th>Fiş No</th>
                <th>Kasiyer</th>
                <th>Tür</th>
                <th>Barkod</th>
                <th>Ürün</th>
                <th>Ayar</th>
                <th>Gramaj</th>
                <th>Has Gr</th>
                <th>Adet</th>
                <th>Fiyat</th>
                <th>İndirim</th>
                <th>Toplam</th>
                <th>Ödeme</th>
                <th>Kur</th>
              </tr>
            </thead>
            <tbody>
              ${endOfDayReport.allProducts.map(p => {
                const pureW = calcPureWeight(p);
                const rate = getRate(p);
                const purityLabel = p.purity ? (p.metalType === 'SILVER' || p.metalType === 'silver' ? p.purity : p.purity + 'K') : '-';
                return `
              <tr style="background-color:${p.type !== 'Satış' ? '#fef2f2' : 'transparent'};">
                <td style="color:#666;white-space:nowrap;">${p.time ? new Date(p.time).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }) : '-'}</td>
                <td style="color:#666;font-size:9px;">${p.receiptNumber || '-'}</td>
                <td style="font-weight:500;">${p.cashier}</td>
                <td style="color:${p.type === 'Satış' ? '#059669' : p.type === 'İade' ? '#ef4444' : '#f59e0b'};font-weight:500;font-size:0.85em;white-space:nowrap;">${p.type}</td>
                <td style="color:#666;font-size:9px;">${p.barcode || '-'}</td>
                <td>${p.name}</td>
                <td style="color:#b45309;font-weight:500;white-space:nowrap;">${purityLabel}</td>
                <td style="color:#7c3aed;font-weight:500;white-space:nowrap;">${p.weight ? parseFloat(p.weight).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' gr' : '-'}</td>
                <td style="color:#0369a1;font-weight:500;white-space:nowrap;">${pureW ? pureW.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' gr' : '-'}</td>
                <td>${p.quantity}</td>
                <td style="white-space:nowrap;">₺${p.unitPrice.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                <td style="color:${p.discount > 0 ? '#ef4444' : '#ccc'};white-space:nowrap;">${p.discount > 0 ? '-₺' + p.discount.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}</td>
                <td style="font-weight:600;white-space:nowrap;">₺${p.total.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                <td style="font-size:9px;white-space:nowrap;">${formatPdfPayment(p)}</td>
                <td style="color:#666;font-size:9px;white-space:nowrap;">${rate ? '₺' + rate.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}</td>
              </tr>`;
              }).join('')}
            </tbody>
          </table>
        </div>

        <div class="footer">
          Oluşturma: ${new Date().toLocaleString('tr-TR')}
        </div>
    ` : '';

    const fileName = `gunluk-rapor-${dateStr}.pdf`;
    const marginMm = 10;
    const scale = 2;
    const page1El = document.createElement('div');
    page1El.style.cssText = 'position:absolute;left:-99999px;top:0;';
    const page2El = document.createElement('div');
    page2El.style.cssText = 'position:absolute;left:-99999px;top:0;';

    try {
      // --- Sayfa 1: Kasa Özeti (dikey/portre) ---
      page1El.innerHTML = `<div class="pdf-root"><style>${page1Styles}</style>${page1Body}</div>`;
      document.body.appendChild(page1El);
      const page1Root = page1El.firstElementChild;

      const canvas1 = await html2canvas(page1Root, { scale, useCORS: true, letterRendering: true, backgroundColor: '#ffffff' });
      const page1Boundaries = Array.from(page1Root.querySelectorAll(':scope > div')).map(el => {
        const r = el.getBoundingClientRect();
        return Math.round((r.bottom - page1Root.getBoundingClientRect().top) * scale);
      });
      document.body.removeChild(page1El);

      const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
      const imgWidthMmP = 210 - marginMm * 2;
      const pxPerMmP = canvas1.width / imgWidthMmP;
      const pageHeightPxP = (297 - marginMm * 2) * pxPerMmP;
      const slices1 = sliceCanvasIntoPages(canvas1, page1Boundaries, pageHeightPxP);
      slices1.forEach(([s, e], idx) => {
        if (idx > 0) pdf.addPage('a4', 'portrait');
        addCanvasSliceToPdf(pdf, canvas1, s, e, marginMm, imgWidthMmP, pxPerMmP);
      });

      // --- Sayfa 2: Tüm İşlemler (yatay) - satır sınırlarına göre sayfalanır, satır ortadan kesilmez ---
      if (page2Body) {
        page2El.innerHTML = `<div class="pdf-root"><style>${commonStyles}</style>${page2Body}</div>`;
        document.body.appendChild(page2El);
        const page2Root = page2El.firstElementChild;

        const canvas2 = await html2canvas(page2Root, { scale, useCORS: true, letterRendering: true, backgroundColor: '#ffffff' });
        const page2RootTop = page2Root.getBoundingClientRect().top;
        const page2Boundaries = Array.from(page2Root.querySelectorAll('tbody tr')).map(el => {
          const r = el.getBoundingClientRect();
          return Math.round((r.bottom - page2RootTop) * scale);
        });
        document.body.removeChild(page2El);

        const imgWidthMmL = 297 - marginMm * 2;
        const pxPerMmL = canvas2.width / imgWidthMmL;
        const pageHeightPxL = (210 - marginMm * 2) * pxPerMmL;
        const slices2 = sliceCanvasIntoPages(canvas2, page2Boundaries, pageHeightPxL);
        slices2.forEach(([s, e]) => {
          pdf.addPage('a4', 'landscape');
          addCanvasSliceToPdf(pdf, canvas2, s, e, marginMm, imgWidthMmL, pxPerMmL);
        });
      }

      pdf.save(fileName);
      toast.success('PDF indirildi');
    } catch (err) {
      if (page1El.parentNode) document.body.removeChild(page1El);
      if (page2El.parentNode) document.body.removeChild(page2El);
      toast.error('PDF oluşturulamadı');
    }
  };

  // Tarih ve saat
  const [currentTime, setCurrentTime] = useState(new Date());
  useEffect(() => {
    if (showAllSalesModal) return; // Modal açıkken saat güncelleme
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, [showAllSalesModal]);

  // Fiyatları periyodik olarak güncelle
  useEffect(() => {
    const fetchPrices = async () => {
      try {
        const response = await fetch(`${API_URL}/prices`);
        if (response.ok) {
          const data = await response.json();

          // Scraper durumu uyarısını güncelle
          if (data.scraperDown || data.stale) {
            setScraperWarning(data.scraperWarning || 'Canlı fiyat kaynakları erişilemiyor');
          } else {
            setScraperWarning(null);
            // Fiyat geldi, uyarı flag'larını resetle
            priceWarningShown.current = {};
          }

          // Fiyat değişimlerini kontrol et ve animasyon tetikle
          setPrices(prevPrices => {
            if (prevPrices) {
              const animations = {};
              const priceKeys = ['altin_satis', 'altin_alis', 'USD_satis', 'USD_alis', 'EUR_satis', 'EUR_alis', 'GBP_satis', 'GBP_alis', 'gumus_satis', 'gumus_alis', 'gumus_usd_satis', 'gumus_usd_alis'];

              priceKeys.forEach(key => {
                const oldVal = parseFloat(prevPrices[key]) || 0;
                const newVal = parseFloat(data[key]) || 0;
                if (oldVal !== newVal && oldVal > 0) {
                  animations[key] = newVal > oldVal ? 'up' : 'down';
                }
              });

              if (Object.keys(animations).length > 0) {
                setPriceAnimations(animations);
                // 1.5 saniye sonra animasyonu kaldır
                setTimeout(() => setPriceAnimations({}), 1500);
              }
            }
            return data;
          });
        } else {
          setScraperWarning('Fiyat servisi yanıt vermiyor');
        }
      } catch (error) {
        console.error('Fiyat çekme hatası:', error);
        setScraperWarning('Fiyat servisi bağlantı hatası');
      }
    };

    fetchPrices();
    // Modal açıkken fiyat güncellemesini durdur (performans için)
    if (!showAllSalesModal) {
      const priceInterval = setInterval(fetchPrices, 5000);
      return () => clearInterval(priceInterval);
    }
  }, [showAllSalesModal]);

  // Özel kurları getir
  const fetchCustomRates = async () => {
    try {
      const response = await fetch(`${API_URL}/custom-rates`);
      if (response.ok) {
        const data = await response.json();
        const ratesObj = {};
        data.forEach(rate => {
          ratesObj[rate.currency] = rate;
        });
        setCustomRates(ratesObj);
      }
    } catch (error) {
      console.error('Özel kur getirme hatası:', error);
    }
  };

  useEffect(() => {
    fetchCustomRates();
  }, []);

  // Özel kur kaydet
  const saveCustomRate = async () => {
    // Alış kontrolü
    const hasAlisValue = rateModalAlisMode === 'percentage' ? !!rateModalAlisPercentage : !!rateModalAlisValue;
    // Satış kontrolü
    const hasSatisValue = rateModalSatisMode === 'percentage' ? !!rateModalSatisPercentage : !!rateModalSatisValue;

    if (!hasAlisValue && !hasSatisValue) {
      toast.error('En az bir kur değeri giriniz');
      return;
    }

    // USD kaydedilmeden önce mevcut gümüş dolar değerlerini yakala
    const silverBeforeSave = customRates['SILVER'];

    try {
      // Payload hazırla - alış ve satış bağımsız modlarda olabilir
      const payload = {
        currency: rateModalCurrency,
        updated_by: user?.id
      };

      // Alış: moduna göre kaydet
      if (rateModalAlisMode === 'percentage') {
        payload.rate_alis = null;
        payload.alis_percentage = rateModalAlisPercentage ? parseFloat(rateModalAlisPercentage) : null;
      } else {
        payload.rate_alis = rateModalAlisValue ? parseFloat(rateModalAlisValue) : null;
        payload.alis_percentage = null;
      }

      // Satış: moduna göre kaydet
      if (rateModalSatisMode === 'percentage') {
        payload.rate_satis = null;
        payload.satis_percentage = rateModalSatisPercentage ? parseFloat(rateModalSatisPercentage) : null;
      } else {
        payload.rate_satis = rateModalSatisValue ? parseFloat(rateModalSatisValue) : null;
        payload.satis_percentage = null;
      }

      // Gümüş kuru dolar cinsinden girilmişse dolar değerlerini de kaydet
      if (rateModalCurrency === 'SILVER') {
        payload.alis_dolar = rateModalAlisDolar ? parseFloat(rateModalAlisDolar) : null;
        payload.satis_dolar = rateModalSatisDolar ? parseFloat(rateModalSatisDolar) : null;
      }

      const response = await fetch(`${API_URL}/custom-rates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast.success(`${getRateModalLabel(rateModalCurrency)} kurları kaydedildi`);

        // Log kaydı - Kur değişikliği
        logService.log(
          LOG_MODULES.RATES,
          LOG_ACTIONS.UPDATE,
          `${getRateModalLabel(rateModalCurrency)} kuru güncellendi`,
          {
            entity_type: 'rate',
            entity_id: rateModalCurrency,
            status: 'success',
            details: {
              currency: rateModalCurrency,
              currency_label: getRateModalLabel(rateModalCurrency),
              alis_mode: rateModalAlisMode,
              satis_mode: rateModalSatisMode,
              alis_percentage: rateModalAlisMode === 'percentage' && rateModalAlisPercentage ? parseFloat(rateModalAlisPercentage) : null,
              satis_percentage: rateModalSatisMode === 'percentage' && rateModalSatisPercentage ? parseFloat(rateModalSatisPercentage) : null,
              rate_alis: rateModalAlisMode === 'manual' && rateModalAlisValue ? parseFloat(rateModalAlisValue) : null,
              rate_satis: rateModalSatisMode === 'manual' && rateModalSatisValue ? parseFloat(rateModalSatisValue) : null
            }
          }
        );

        fetchCustomRates();
        setShowRateModal(false);
        setRateModalValue('');
        setRateModalAlisValue('');
        setRateModalSatisValue('');
        setRateModalAlisPercentage('');
        setRateModalSatisPercentage('');
        setRateModalAlisDolar('');
        setRateModalSatisDolar('');

        // USD kuru değişince gümüş dolar kuru varsa otomatik TL hesapla ve kaydet
        if (rateModalCurrency === 'USD' && silverBeforeSave &&
            (silverBeforeSave.alis_dolar || silverBeforeSave.satis_dolar)) {
          const newUsdAlis = payload.rate_alis;
          const newUsdSatis = payload.rate_satis;
          const silverPayload = {
            currency: 'SILVER',
            updated_by: user?.id,
            alis_dolar: silverBeforeSave.alis_dolar,
            satis_dolar: silverBeforeSave.satis_dolar,
            alis_percentage: silverBeforeSave.alis_percentage,
            satis_percentage: silverBeforeSave.satis_percentage,
          };
          if (silverBeforeSave.alis_dolar && newUsdAlis) {
            silverPayload.rate_alis = parseFloat((parseFloat(silverBeforeSave.alis_dolar) * newUsdAlis).toFixed(4));
          } else {
            silverPayload.rate_alis = silverBeforeSave.rate_alis;
          }
          if (silverBeforeSave.satis_dolar && newUsdSatis) {
            silverPayload.rate_satis = parseFloat((parseFloat(silverBeforeSave.satis_dolar) * newUsdSatis).toFixed(4));
          } else {
            silverPayload.rate_satis = silverBeforeSave.rate_satis;
          }
          try {
            await fetch(`${API_URL}/custom-rates`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(silverPayload)
            });
            fetchCustomRates();
            toast.info('Gümüş TL kuru, dolar kuruna göre otomatik güncellendi');
          } catch (e) {
            console.error('Gümüş otomatik kur güncelleme hatası:', e);
          }
        }
      } else {
        toast.error('Kur kaydedilemedi');
      }
    } catch (error) {
      console.error('Kur kaydetme hatası:', error);
      toast.error('Kur kaydedilemedi');
    }
  };

  // Özel kuru kaldır
  const removeCustomRate = async (currency) => {
    try {
      const response = await fetch(`${API_URL}/custom-rates/${currency}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        toast.success(`${getRateModalLabel(currency)} özel kuru kaldırıldı`);
        fetchCustomRates();
        setShowRateModal(false);
      }
    } catch (error) {
      console.error('Kur silme hatası:', error);
    }
  };

  // Parite görünürlüğünü toggle et
  const toggleRateVisibility = (rate) => {
    const newVisibleRates = { ...visibleRates, [rate]: !visibleRates[rate] };
    setVisibleRates(newVisibleRates);
    localStorage.setItem('visibleRates', JSON.stringify(newVisibleRates));
  };

  // Kur modalını aç
  const openRateModal = (currency) => {
    if (user?.role !== 'admin') return;
    setRateModalCurrency(currency);

    // Mevcut özel kur varsa yükle
    if (customRates[currency]) {
      setRateModalAlisValue(customRates[currency].rate_alis?.toString() || '');
      setRateModalSatisValue(customRates[currency].rate_satis?.toString() || customRates[currency].rate?.toString() || '');
      // Makas farkı yüzdeliklerini yükle
      const alisPerc = customRates[currency].alis_percentage;
      const satisPerc = customRates[currency].satis_percentage;
      setRateModalAlisPercentage(alisPerc ? alisPerc.toString() : '');
      setRateModalSatisPercentage(satisPerc ? satisPerc.toString() : '');
      // Bağımsız modları belirle
      setRateModalAlisMode(alisPerc ? 'percentage' : 'manual');
      setRateModalSatisMode(satisPerc ? 'percentage' : 'manual');
      setRateModalInputMode((alisPerc || satisPerc) ? 'percentage' : 'manual');
    } else if (prices) {
      setRateModalAlisPercentage('');
      setRateModalSatisPercentage('');
      setRateModalAlisMode('manual');
      setRateModalSatisMode('manual');
      setRateModalInputMode('manual');
      // API'den gelen kurları varsayılan olarak göster
      let alisKey, satisKey;
      if (currency === 'GOLD') {
        alisKey = 'altin_alis';
        satisKey = 'altin_satis';
      } else if (currency === 'SILVER') {
        alisKey = 'gumus_alis';
        satisKey = 'gumus_satis';
      } else {
        alisKey = `${currency}_alis`;
        satisKey = `${currency}_satis`;
      }
      setRateModalAlisValue(prices[alisKey] || '');
      setRateModalSatisValue(prices[satisKey] || '');
    } else {
      setRateModalAlisValue('');
      setRateModalSatisValue('');
      setRateModalAlisPercentage('');
      setRateModalSatisPercentage('');
      setRateModalAlisMode('manual');
      setRateModalSatisMode('manual');
      setRateModalInputMode('manual');
    }
    if (currency === 'SILVER' && customRates[currency]) {
      setRateModalAlisDolar(customRates[currency].alis_dolar != null ? customRates[currency].alis_dolar.toString() : '');
      setRateModalSatisDolar(customRates[currency].satis_dolar != null ? customRates[currency].satis_dolar.toString() : '');
    } else {
      setRateModalAlisDolar('');
      setRateModalSatisDolar('');
    }
    setShowRateModal(true);
  };

  // Anlık piyasa kurlarını al (yardımcı fonksiyon)
  const getBaseRates = () => {
    let baseAlis, baseSatis;
    if (rateModalCurrency === 'GOLD') {
      baseAlis = parseFloat(prices?.altin_alis) || 0;
      baseSatis = parseFloat(prices?.altin_satis) || 0;
    } else if (rateModalCurrency === 'SILVER') {
      baseAlis = parseFloat(prices?.gumus_alis) || 0;
      baseSatis = parseFloat(prices?.gumus_satis) || 0;
    } else if (rateModalCurrency === 'SILVER_USD') {
      // Gümüş/USD paritesi - Nadir Döviz'den doğrudan
      baseAlis = parseFloat(prices?.gumus_usd_alis) || 0;
      baseSatis = parseFloat(prices?.gumus_usd_satis) || 0;
    } else {
      baseAlis = parseFloat(prices?.[`${rateModalCurrency}_alis`]) || 0;
      baseSatis = parseFloat(prices?.[`${rateModalCurrency}_satis`]) || 0;
    }
    return { baseAlis, baseSatis };
  };

  // Dinamik kur hesaplama - makas farkı yüzdesinden veya sabit kurdan
  const getEffectiveRate = (currency, type) => {
    const customRate = customRates[currency];

    // Anlık fiyatları al
    let liveAlis, liveSatis;
    if (currency === 'GOLD') {
      liveAlis = parseFloat(prices?.altin_alis) || 0;
      liveSatis = parseFloat(prices?.altin_satis) || 0;
    } else if (currency === 'SILVER') {
      liveAlis = parseFloat(prices?.gumus_alis) || 0;
      liveSatis = parseFloat(prices?.gumus_satis) || 0;
    } else if (currency === 'SILVER_USD') {
      // Gümüş/USD paritesi - Nadir Döviz'den doğrudan
      liveAlis = parseFloat(prices?.gumus_usd_alis) || 0;
      liveSatis = parseFloat(prices?.gumus_usd_satis) || 0;
    } else {
      liveAlis = parseFloat(prices?.[`${currency}_alis`]) || parseFloat(prices?.[currency]) || 0;
      liveSatis = parseFloat(prices?.[`${currency}_satis`]) || parseFloat(prices?.[currency]) || 0;
    }

    if (type === 'alis') {
      // Alış için: önce yüzdeye bak, yoksa manuel alış kura bak, yoksa anlık
      if (customRate?.alis_percentage != null && customRate.alis_percentage !== '' && !isNaN(customRate.alis_percentage)) {
        return liveAlis * (1 - parseFloat(customRate.alis_percentage) / 100);
      }
      if (customRate?.rate_alis != null && parseFloat(customRate.rate_alis) > 0) {
        return parseFloat(customRate.rate_alis);
      }
      return liveAlis;
    } else {
      // Satış için: önce yüzdeye bak, yoksa manuel satış kura bak, yoksa anlık
      if (customRate?.satis_percentage != null && customRate.satis_percentage !== '' && !isNaN(customRate.satis_percentage)) {
        return liveSatis * (1 + parseFloat(customRate.satis_percentage) / 100);
      }
      if (customRate?.rate_satis != null && parseFloat(customRate.rate_satis) > 0) {
        return parseFloat(customRate.rate_satis);
      }
      return liveSatis;
    }
  };

  // Alış makas farkı değiştiğinde
  const handleAlisPercentageChange = (percentage) => {
    setRateModalAlisPercentage(percentage);
    if (!percentage || isNaN(parseFloat(percentage))) {
      return;
    }
    const pct = parseFloat(percentage);
    const { baseAlis } = getBaseRates();
    // Alış: anlık fiyat - %X (azalt)
    const newAlis = baseAlis * (1 - pct / 100);
    setRateModalAlisValue(newAlis.toFixed(2));
  };

  // Satış makas farkı değiştiğinde
  const handleSatisPercentageChange = (percentage) => {
    setRateModalSatisPercentage(percentage);
    if (!percentage || isNaN(parseFloat(percentage))) {
      return;
    }
    const pct = parseFloat(percentage);
    const { baseSatis } = getBaseRates();
    // Satış: anlık fiyat + %X (artır)
    const newSatis = baseSatis * (1 + pct / 100);
    setRateModalSatisValue(newSatis.toFixed(2));
  };

  // Kur modalı için currency label al
  const getRateModalLabel = (currency) => {
    const labels = {
      'USD': 'Dolar',
      'EUR': 'Euro',
      'GBP': 'Sterlin',
      'GOLD': 'Altın',
      'SILVER': 'Gümüş',
      'SILVER_USD': 'Gümüş/USD Paritesi'
    };
    return labels[currency] || currency;
  };

  // Kur modalı için price key al
  const getRatePriceKeys = (currency) => {
    if (currency === 'GOLD') {
      return { alis: 'altin_alis', satis: 'altin_satis' };
    } else if (currency === 'SILVER') {
      return { alis: 'gumus_alis', satis: 'gumus_satis' };
    } else if (currency === 'SILVER_USD') {
      // Gümüş/USD paritesi için hesaplanmış değerler
      return { alis: 'silver_usd_alis', satis: 'silver_usd_satis', isCalculated: true };
    }
    return { alis: `${currency}_alis`, satis: `${currency}_satis` };
  };

  // Bakiye sorgula
  const checkGiftCard = async (code) => {
    if (!code || code.length < 3) {
      setGiftCardInfo(null);
      setGiftCardError('');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/gift-cards/code/${code}`);
      if (response.ok) {
        const data = await response.json();
        if (data.status === 'active') {
          setGiftCardInfo(data);
          setGiftCardError('');
        } else {
          setGiftCardInfo(null);
          setGiftCardError(data.status === 'used' ? 'Bu kart kullanılmış' : data.status === 'expired' ? 'Kartın süresi dolmuş' : 'Kart iptal edilmiş');
        }
      } else {
        setGiftCardInfo(null);
        setGiftCardError('Bakiye bulunamadı');
      }
    } catch (error) {
      console.error('Bakiye sorgulama hatası:', error);
      setGiftCardInfo(null);
      setGiftCardError('Sorgulama hatası');
    }
  };

  // Bakiye indirim olarak uygula (sadece görüntüleme - ödeme tamamlanınca kullanılacak)
  const applyGiftCard = () => {
    if (!giftCardInfo) return;

    // Mevcut tutarı hesapla (bakiye indirimi hariç)
    const currentTotal = subtotal - manualDiscount - campaignDiscount;
    const useAmount = Math.min(Number(giftCardInfo.remaining_amount), Math.max(0, currentTotal));

    // Sadece state'i güncelle, API çağırma - ödeme tamamlanınca kullanılacak
    const giftCard = {
      code: giftCardInfo.code,
      id: giftCardInfo.id,
      amount: useAmount,
      remaining: giftCardInfo.remaining_amount
    };
    setAppliedGiftCard(giftCard);
    setGiftCardDiscount(useAmount);

    setShowPaymentModal(false);
    setGiftCardCode('');
    setGiftCardInfo(null);
    setGiftCardError('');
  };

  // Bakiye indirimi kaldır
  const removeGiftCard = () => {
    setAppliedGiftCard(null);
    setGiftCardDiscount(0);
  };

  // Hurda hesaplama fonksiyonları
  const calculateScrapPureWeight = () => {
    const grossWeight = parseFloat(scrapFormData.gross_weight) || 0;
    const wasteGram = parseFloat(scrapFormData.waste_gram) || 0;
    const netWeight = grossWeight - wasteGram;
    // Önce custom_purity_rate'i kontrol et (tek seferlik), yoksa varsayılanı kullan
    const purityRate = scrapFormData.custom_purity_rate || scrapPurityRates[scrapFormData.metal_type]?.[scrapFormData.purity] || defaultScrapPurityRates[scrapFormData.metal_type]?.[scrapFormData.purity] || 0;
    return netWeight * purityRate;
  };

  const calculateScrapTotal = () => {
    const pureWeight = calculateScrapPureWeight();
    const pricePerGram = parseFloat(scrapFormData.price_per_gram) || 0;
    return pureWeight * pricePerGram;
  };

  // Eski fonksiyonlar kaldırıldı - artık scrapCart ve ilgili fonksiyonlar kullanılıyor
  // addScrapToCart, updateScrapQuantity, removeScrapFromCart, clearScrapCart

  // Mağazaları getir
  const fetchStores = async () => {
    try {
      const response = await fetch(`${API_URL}/stores`);
      const data = await response.json();
      setStores(data);
    } catch (error) {
      console.error('Mağaza yükleme hatası:', error);
    }
  };

  useEffect(() => {
    fetchStores();
  }, []);

  // Ürün sorgulama arama
  const searchProductLookup = async (query, filters = null) => {
    try {
      // Eğer filtreler parametre olarak verilmişse onları kullan, yoksa state'ten al
      const activeFilters = filters || lookupFilters;

      // Mağaza tipini belirle - arama sonuçlarını filtrelemek için
      const storeId = selectedStore?.id || user?.store_id;
      const isGoldStore = storeId === 1;
      const isSilverStore = storeId === 2 || storeId === 3 || storeId === 4;

      let url;
      if (query && query.length > 0) {
        // Arama sorgusu varsa search endpoint kullan
        // Mağaza tipine göre store_id parametresi ekle
        const searchParams = new URLSearchParams();
        if (isGoldStore) {
          searchParams.append('store_id', '1');
        } else if (isSilverStore) {
          // storeId 4 (Akik Depo) backend'de store_id bazlı filtrede tanımlı değil,
          // storeType göndererek gümüş filtresine düşmesini garantiliyoruz
          searchParams.append('store_id', storeId.toString());
          searchParams.append('storeType', 'Gümüş');
        }
        const queryString = searchParams.toString();
        url = `${API_URL}/products/search/${encodeURIComponent(query)}${queryString ? '?' + queryString : ''}`;
      } else {
        // Filtrelerle ürün getir
        const params = new URLSearchParams();
        params.append('limit', '100'); // Makul limit
        if (activeFilters.metal_type) {
          params.append('metal_type', activeFilters.metal_type);
        }
        if (activeFilters.category) {
          params.append('category', activeFilters.category);
        }
        url = `${API_URL}/products?${params.toString()}`;
      }

      const response = await fetch(url);
      let data = await response.json();

      // API yanıtını kontrol et - { products: [...] } formatı veya direkt array olabilir
      if (data && data.products && Array.isArray(data.products)) {
        data = data.products;
      } else if (!Array.isArray(data)) {
        data = [];
      }

      // Mağaza tipine göre client-side filtreleme (API desteklemiyorsa)
      if (isGoldStore) {
        // Altın mağazası - sadece gold ürünler
        data = data.filter(p => p.metal_type === 'gold');
      } else if (isSilverStore) {
        // Gümüş mağazası - gold hariç tüm ürünler
        data = data.filter(p => p.metal_type !== 'gold');
      }

      // Marka filtresi
      if (activeFilters.brand) {
        data = data.filter(p => p.brand && p.brand.toLowerCase().includes(activeFilters.brand.toLowerCase()));
      }

      setLookupSearchResults(data);

      // Barkod ile arama yapıldıysa ve tek sonuç varsa otomatik seç
      if (query && query.length >= 3 && data.length === 1) {
        const product = data[0];
        selectLookupProduct(product);
        setLookupSearchQuery(''); // Arama kutusunu temizle (sonraki barkod için hazır)
      }
    } catch (error) {
      console.error('Ürün arama hatası:', error);
      setLookupSearchResults([]);
    }
  };

  // Ürün mağaza stoklarını getir
  const fetchProductStoreStocks = async (productId) => {
    try {
      const response = await fetch(`${API_URL}/store-stocks/${productId}`);
      const data = await response.json();
      setProductStoreStocks(data);
    } catch (error) {
      console.error('Stok yükleme hatası:', error);
      setProductStoreStocks([]);
    }
  };

  // Ürün seç ve detayları göster
  const selectLookupProduct = async (product) => {
    setSelectedLookupProduct(product);
    await fetchProductStoreStocks(product.id);
  };

  // Modal kapanırken temizle
  const closeLookupModal = () => {
    setClosingModal('productLookup');
    setTimeout(() => {
      setShowProductLookupModal(false);
      setClosingModal(null);
      setLookupSearchQuery('');
      setLookupSearchResults([]);
      setSelectedLookupProduct(null);
      setLookupFilters({ category: '', metal_type: '', brand: '' });
      setProductStoreStocks([]);
    }, 250);
  };

  // Modal açıldığında mağazaya göre filtreleri ayarla
  useEffect(() => {
    if (showProductLookupModal) {
      // Mağaza tipini belirle
      const storeId = selectedStore?.id || user?.store_id;

      // Akik Gold Jewellery (id=1) = sadece gold, diğerleri (id=2,3) = tür seçimi göster (gold hariç)
      const isGoldStore = storeId === 1;
      const initialFilters = {
        category: '',
        metal_type: isGoldStore ? 'gold' : '', // Gümüş mağazalarında tür seçimi göster
        brand: ''
      };
      setLookupFilters(initialFilters);
      setLookupSearchResults([]);
      setSelectedLookupProduct(null);

      // Altın mağazası ise direkt altın ürünlerini yükle
      if (isGoldStore) {
        searchProductLookup('', initialFilters);
      }
    }
  }, [showProductLookupModal, selectedStore?.id, user?.store_id]);

  // Aktif kampanyaları getir (mağaza tipine göre filtreleme)
  const fetchActiveCampaigns = async () => {
    try {
      // Mağaza tipini belirle (gold veya silver)
      // Kasiyer için her zaman user.store_id kullan - selectedStore localStorage'dan yanlış gelebilir
      const storeId = user?.role === 'admin' ? (selectedStore?.id || user?.store_id) : user?.store_id;
      const storeIdParam = storeId ? `?store_id=${storeId}` : '';
      const response = await fetch(`${API_URL}/campaigns/active${storeIdParam}`);
      const data = await response.json();
      setActiveCampaigns(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Kampanya yükleme hatası:', error);
      setActiveCampaigns([]);
    }
  };

  // Kampanyalar modalını aç (zaten yüklenmiş aktif kampanyaları kullan)
  const openCampaignsModal = () => {
    setShowCampaignsModal(true);
  };

  // Ürün detay modalını aç ve mağaza stoklarını çek
  const openProductDetailModal = async () => {
    const detailItem = selectedPreviewItem || cart[cart.length - 1];
    if (!detailItem) return;
    setShowProductDetailModal(true);
    try {
      const response = await fetch(`${API_URL}/store-stocks/${detailItem.product_id || detailItem.id}`);
      const data = await response.json();
      setDetailStoreStocks(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Mağaza stok bilgisi alınamadı:', error);
      setDetailStoreStocks([]);
    }
  };

  // Ürün resmini büyük popup'ta aç
  const openImagePopup = (imageUrl, productName) => {
    if (imageUrl) {
      setPopupImageUrl(getImageUrl(imageUrl));
      setPopupImageName(productName || 'Ürün');
      setShowImagePopup(true);
    }
  };

  // Kampanya durumunu hesapla
  const getCampaignStatus = (campaign) => {
    if (!campaign.active) return { label: 'Pasif', class: 'inactive' };
    const now = new Date();
    const start = new Date(campaign.start_date);
    const end = new Date(campaign.end_date);
    if (now < start) return { label: 'Beklemede', class: 'pending' };
    if (now > end) return { label: 'Sona Erdi', class: 'expired' };
    return { label: 'Aktif', class: 'active' };
  };

  // Kampanya türü etiketleri
  const CAMPAIGN_TYPE_LABELS = {
    'percentage': 'Yüzde İndirim',
    'fixed': 'Sabit İndirim',
    'buy_x_get_y': 'X Al Y Öde',
    'gift_with_purchase': 'Hediyeli',
    'birthday': 'Doğum Günü',
    'special_day': 'Özel Gün',
    'loyalty': 'Sadakat',
    'bundle': 'Paket',
    'minimum_purchase': 'Minimum Alışveriş'
  };

  useEffect(() => {
    // Mağaza değiştiğinde eski kampanyaları anında temizle (stale kampanya race condition önlemi)
    setActiveCampaigns([]);
    setAppliedCampaign(null);
    setCampaignDiscount(0);
    fetchActiveCampaigns();
  }, [selectedStore?.id, user?.store_id]);

  // Aktif kampanyalarda özel gün kampanyası var mı kontrol et
  useEffect(() => {
    const specialDay = activeCampaigns.find(c => c.campaign_type === 'special_day');
    if (specialDay) {
      setSpecialDayCampaign(specialDay);
      // Eğer başka bir kampanya uygulanmamışsa, özel gün kampanyasını uygula
      if (!appliedCampaign && !birthdayCampaign) {
        setAppliedCampaign(specialDay);
        const rewards = typeof specialDay.rewards === 'string'
          ? JSON.parse(specialDay.rewards)
          : specialDay.rewards || {};
        toast.info(
          `🎉 ${specialDay.name} aktif! ${rewards.discount_type === 'fixed' ? `₺${rewards.discount_amount}` : `%${rewards.discount_percent}`} indirim uygulandı.`,
          { autoClose: 7500 }
        );
      }
    } else {
      setSpecialDayCampaign(null);
    }
  }, [activeCampaigns]);

  // Kampanya koşullarını parse et
  const parseConditions = (campaign) => {
    try {
      return typeof campaign.conditions === 'string'
        ? JSON.parse(campaign.conditions)
        : campaign.conditions || {};
    } catch (e) {
      return {};
    }
  };

  // Kampanya kapsam filtresi: seçili tür/kategori/marka varsa SADECE onları kapsar (hiçbiri seçili değilse tümü)
  // Ürün, seçili her boyutla (tür VE kategori VE marka) eşleşmelidir.
  const makeCampaignScopeFilter = (conditions) => {
    const metalTypes = conditions.metal_types || [];
    const categories = conditions.categories || [];
    const brands = conditions.brands || [];
    const hasFilter = metalTypes.length > 0 || categories.length > 0 || brands.length > 0;
    return (item) => {
      if (!hasFilter) return true;
      const metalMatch = metalTypes.length === 0 || metalTypes.includes(item.metal_type);
      const catMatch = categories.length === 0 || categories.includes(item.category);
      const brandMatch = brands.length === 0 || brands.includes(item.brand);
      return metalMatch && catMatch && brandMatch;
    };
  };

  // Kampanya ödüllerini parse et
  const parseRewards = (campaign) => {
    try {
      return typeof campaign.rewards === 'string'
        ? JSON.parse(campaign.rewards)
        : campaign.rewards || {};
    } catch (e) {
      return {};
    }
  };

  // Sepetteki ürün sayısını hesapla (iade hariç)
  const getCartItemCount = () => {
    return cart.filter(item => !item.isRefund).reduce((sum, item) => sum + item.quantity, 0);
  };

  // Kampanya uygunluğunu kontrol et
  const checkCampaignEligibility = (campaign) => {
    const conditions = parseConditions(campaign);
    const rewards = parseRewards(campaign);
    // Kişiye özel ürünler kampanyalara dahil edilmez
    const cartItems = cart.filter(item => !item.isRefund && !item.isCustomProduct);
    const itemCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);
    const cartTotal = cartItems.reduce((sum, item) => sum + (convertToTRY(item.price, item.currency) * item.quantity), 0);

    switch (campaign.campaign_type) {
      case 'percentage':
        // Minimum tutar kontrolü
        const minAmountPercent = conditions.min_purchase || conditions.min_amount || 0;
        if (minAmountPercent > 0 && cartTotal < minAmountPercent) {
          return { eligible: false, missing: minAmountPercent - cartTotal, type: 'amount' };
        }
        const discountPercent = rewards.discount_percent || rewards.percentage || 0;
        // Seçili kategori/tür/marka varsa indirim YALNIZCA o ürünlere uygulanır (boşsa tüm sepete)
        const pctScopeItems = cartItems.filter(makeCampaignScopeFilter(conditions));
        if (pctScopeItems.length === 0) {
          return { eligible: false, type: 'no_category_items', categories: conditions.categories || [] };
        }
        const pctScopeTotal = pctScopeItems.reduce((sum, item) => sum + (convertToTRY(item.price, item.currency) * item.quantity), 0);
        return { eligible: true, discount: pctScopeTotal * discountPercent / 100 };

      case 'category_discount':
        // Kategori indirimi - sadece seçilen kategorilerdeki ürünlere indirim
        const categoryList = conditions.categories || [];
        const catMinPurchase = conditions.min_purchase || 0;
        const catMinPurchaseType = conditions.min_purchase_type || 'category'; // 'general' veya 'category'

        // Tür, kategori ve markadaki ürünleri filtrele
        const metalTypeList = conditions.metal_types || [];
        const brandList = conditions.brands || [];
        const categoryItems = cartItems.filter(item => {
          const metalMatch = metalTypeList.length === 0 || metalTypeList.includes(item.metal_type);
          const catMatch = categoryList.length === 0 || categoryList.includes(item.category);
          const brandMatch = brandList.length === 0 || brandList.includes(item.brand);
          return metalMatch && catMatch && brandMatch;
        });

        if (categoryItems.length === 0) {
          return { eligible: false, type: 'no_category_items', categories: categoryList };
        }

        // Kategorideki ürünlerin toplamı
        const categoryTotal = categoryItems.reduce((sum, item) => sum + (convertToTRY(item.price, item.currency) * item.quantity), 0);

        // Minimum tutar kontrolü - tip'e göre değişir
        if (catMinPurchase > 0) {
          if (catMinPurchaseType === 'general') {
            // Genel sepet toplamını kontrol et
            if (cartTotal < catMinPurchase) {
              return { eligible: false, missing: catMinPurchase - cartTotal, type: 'amount', categories: categoryList };
            }
          } else {
            // Sadece kategori toplamını kontrol et (varsayılan)
            if (categoryTotal < catMinPurchase) {
              return { eligible: false, missing: catMinPurchase - categoryTotal, type: 'category_amount', categories: categoryList };
            }
          }
        }

        const catDiscountPercent = rewards.discount_percent || 0;
        const catDiscountAmount = categoryTotal * catDiscountPercent / 100;

        return {
          eligible: true,
          discount: catDiscountAmount,
          categoryItems: categoryItems.map(i => i.id),
          categories: categoryList,
          discountPercent: catDiscountPercent
        };

      case 'product_discount':
        // Ürün indirimi - sadece seçilen ürünlere indirim
        const productList = conditions.products || [];
        const prodMinPurchase = conditions.min_purchase || 0;
        const prodMinPurchaseType = conditions.min_purchase_type || 'general'; // 'general' veya 'product'

        // Seçilen ürünleri filtrele
        const productItems = cartItems.filter(item =>
          productList.length === 0 || productList.includes(item.id)
        );

        if (productItems.length === 0) {
          return { eligible: false, type: 'no_product_items', products: productList };
        }

        // Seçilen ürünlerin toplamı
        const productTotal = productItems.reduce((sum, item) => sum + (convertToTRY(item.price, item.currency) * item.quantity), 0);

        // Minimum tutar kontrolü - tip'e göre değişir
        if (prodMinPurchase > 0) {
          if (prodMinPurchaseType === 'product') {
            // Sadece seçilen ürünlerin toplamını kontrol et
            if (productTotal < prodMinPurchase) {
              return { eligible: false, missing: prodMinPurchase - productTotal, type: 'product_amount', products: productList };
            }
          } else {
            // Genel sepet toplamını kontrol et (varsayılan)
            if (cartTotal < prodMinPurchase) {
              return { eligible: false, missing: prodMinPurchase - cartTotal, type: 'amount', products: productList };
            }
          }
        }

        const prodDiscountPercent = rewards.discount_percent || 0;
        const prodDiscountAmount = productTotal * prodDiscountPercent / 100;

        return {
          eligible: true,
          discount: prodDiscountAmount,
          productItems: productItems.map(i => i.id),
          products: productList,
          discountPercent: prodDiscountPercent
        };

      case 'fixed_amount':
        // Minimum tutar kontrolü
        const minAmountFixed = conditions.min_purchase || conditions.min_amount || 0;
        if (minAmountFixed > 0 && cartTotal < minAmountFixed) {
          return { eligible: false, missing: minAmountFixed - cartTotal, type: 'amount' };
        }
        const fixedDiscount = rewards.discount_amount || rewards.amount || 0;
        const repeatFixed = rewards.repeat || false;
        // Tekrarlama: Min. tutar kaç kez karşılandı?
        const repeatCountFixed = repeatFixed && minAmountFixed > 0
          ? Math.floor(cartTotal / minAmountFixed)
          : 1;
        // Seçili kategori/tür/marka varsa indirim YALNIZCA o ürünlere uygulanır (boşsa tüm sepete)
        const fixedScopeItems = cartItems.filter(makeCampaignScopeFilter(conditions));
        if (fixedScopeItems.length === 0) {
          return { eligible: false, type: 'no_category_items', categories: conditions.categories || [] };
        }
        return { eligible: true, discount: fixedDiscount * repeatCountFixed, repeatEnabled: repeatFixed };

      case 'buy_x_pay_y':
        // X al Y öde kampanyası
        const buyCount = conditions.buy_quantity || conditions.buy_count || 3;
        const freeQty = rewards.free_quantity || 1;
        const repeatBuyXPayY = rewards.repeat || false;
        const payCount = buyCount - freeQty;

        // Seçili kategori/tür/marka varsa kampanya YALNIZCA kapsam içi ürünleri sayar (boşsa tüm sepet)
        const bxScopeItems = cartItems.filter(makeCampaignScopeFilter(conditions));
        const bxItemCount = bxScopeItems.reduce((sum, item) => sum + item.quantity, 0);

        if (bxItemCount < buyCount) {
          return { eligible: false, missing: buyCount - bxItemCount, type: 'item', buyCount, payCount, hasScopeItems: bxItemCount > 0 };
        }
        // En ucuz ürünleri bedava yap (yalnızca kapsam içi ürünler)
        const prices = [];
        bxScopeItems.forEach(item => {
          const priceTRY = convertToTRY(item.price, item.currency);
          for (let i = 0; i < item.quantity; i++) {
            prices.push(priceTRY);
          }
        });
        prices.sort((a, b) => a - b);

        // Tekrarlama kontrolü
        const maxGroups = repeatBuyXPayY ? Math.floor(bxItemCount / buyCount) : 1;
        const freeCount = maxGroups * freeQty;
        const freeAmount = prices.slice(0, freeCount).reduce((sum, p) => sum + p, 0);
        return { eligible: true, discount: freeAmount, freeItems: freeCount, repeatEnabled: repeatBuyXPayY };

      case 'progressive':
        // Kademeli indirim: Fiyata göre sırala, en pahalı ürün en düşük kademeyi alır
        const tiers = rewards.tiers || [];
        const repeatTiers = rewards.repeat || false; // Tekrarlama seçeneği
        let progressiveDiscount = 0;
        let appliedTiers = [];

        // Kategori/tür/marka filtresi (boş ise tüm ürünler geçerli)
        const progMetalTypes = conditions.metal_types || [];
        const progCategories = conditions.categories || [];
        const progBrands = conditions.brands || [];
        const hasProgFilter = progMetalTypes.length > 0 || progCategories.length > 0 || progBrands.length > 0;
        const progEligibleItems = cartItems.filter(item => {
          const metalMatch = progMetalTypes.length === 0 || progMetalTypes.includes(item.metal_type);
          const catMatch = progCategories.length === 0 || progCategories.includes(item.category);
          const brandMatch = progBrands.length === 0 || progBrands.includes(item.brand);
          return metalMatch && catMatch && brandMatch;
        });

        if (hasProgFilter && progEligibleItems.length === 0) {
          return { eligible: false, type: 'no_category_items', categories: progCategories };
        }

        // Fiyata göre azalan sırala, ürünler kademelere eşit gruplara bölünür
        const progressiveUnits = [];
        progEligibleItems.forEach((item) => {
          const unitPrice = convertToTRY(item.price, item.currency);
          for (let i = 0; i < item.quantity; i++) {
            progressiveUnits.push(unitPrice);
          }
        });
        progressiveUnits.sort((a, b) => b - a);

        const tierCountProg = tiers.length;
        const totalN = progressiveUnits.length;
        const itemsPerTier = tierCountProg > 0 ? Math.ceil(totalN / tierCountProg) : 0;

        progressiveUnits.forEach((unitPrice, unitIndex) => {
          let tierIdx;
          if (repeatTiers && tierCountProg > 0 && itemsPerTier > 0) {
            tierIdx = Math.min(Math.floor(unitIndex / itemsPerTier), tierCountProg - 1);
          } else {
            tierIdx = unitIndex;
          }

          if (tiers[tierIdx]) {
            const discountPercent = tiers[tierIdx].discount_percent || tiers[tierIdx].percentage || 0;
            progressiveDiscount += unitPrice * discountPercent / 100;
            appliedTiers.push({ item: unitIndex + 1, percentage: discountPercent });
          }
        });

        // Minimum adet şartı: kampanya formundaki "Minimum Ürün Adedi" (min_quantity). Eski kayıtlarda
        // formun gizli varsayılanı buy_quantity=3 kaydediliyordu ve kademeli kampanya 3 ürün olmadan
        // çalışmıyordu; artık buy_quantity kademeli için okunmuyor, min_quantity yoksa 1.
        const progMinQty = parseInt(conditions.min_quantity, 10) || 1;
        if (progMinQty > 0 && totalN < progMinQty) {
          return { eligible: false, missing: progMinQty - totalN, type: 'item_progressive' };
        }
        if (totalN < 1) {
          return { eligible: false, missing: 1 - totalN, type: 'item_progressive' };
        }
        return { eligible: true, discount: progressiveDiscount, appliedTiers, repeatTiers };

      case 'gift_with_purchase':
        // Hediye kampanyası - minimum tutar veya belirli ürünleri al, hediye kazan
        const requiredProducts = conditions.required_products || [];
        const giftProductId = rewards.gift_product_id;
        const giftMinPurchase = conditions.min_purchase || 0;
        const giftCategories = conditions.categories || [];
        const giftRepeatEnabled = rewards.repeat || false;

        // Hediye ürünler hariç toplam hesapla
        const nonGiftItems = cartItems.filter(item => !item.isGift);
        const nonGiftTotal = nonGiftItems.reduce((sum, item) => sum + (convertToTRY(item.price, item.currency) * item.quantity), 0);

        // Minimum tutar kontrolü
        if (giftMinPurchase > 0 && nonGiftTotal < giftMinPurchase) {
          return {
            eligible: false,
            missing: giftMinPurchase - nonGiftTotal,
            type: 'gift_amount',
            giftProductId,
            minPurchase: giftMinPurchase
          };
        }

        // Kategori kontrolü (varsa)
        if (giftCategories.length > 0) {
          const hasRequiredCategory = nonGiftItems.some(item => giftCategories.includes(item.category));
          if (!hasRequiredCategory) {
            return { eligible: false, type: 'gift_category', giftCategories };
          }
        }

        // Gerekli ürünlerin sepette olup olmadığını kontrol et (varsa)
        if (requiredProducts.length > 0) {
          const missingProducts = [];
          requiredProducts.forEach(reqProd => {
            const inCart = nonGiftItems.find(item => item.id === reqProd.product_id);
            if (!inCart || inCart.quantity < (reqProd.quantity || 1)) {
              missingProducts.push(reqProd);
            }
          });

          if (missingProducts.length > 0) {
            return { eligible: false, missingProducts, type: 'gift' };
          }
        }

        // Tekrarlanabilirlik: Kaç adet hediye hak edildi?
        const earnedGiftCount = giftRepeatEnabled && giftMinPurchase > 0
          ? Math.floor(nonGiftTotal / giftMinPurchase)
          : 1;

        // Sepetteki mevcut hediye sayısı
        const currentGiftCount = cartItems.filter(item => item.id === giftProductId && item.isGift)
          .reduce((sum, item) => sum + item.quantity, 0);

        // Hediye ürün sepette yeterli mi kontrol et
        if (currentGiftCount >= earnedGiftCount) {
          const giftInCart = cartItems.find(item => item.id === giftProductId && item.isGift);
          return {
            eligible: true,
            discount: (giftInCart?.originalPrice || 0) * currentGiftCount,
            giftProduct: giftInCart?.name,
            giftAlreadyAdded: true,
            earnedGiftCount,
            currentGiftCount
          };
        }

        // Eksik hediye var
        const remainingGifts = earnedGiftCount - currentGiftCount;
        return {
          eligible: true,
          discount: 0,
          needsGiftAdded: true,
          giftProductId,
          giftProductName: rewards.gift_product_name,
          earnedGiftCount,
          currentGiftCount,
          remainingGifts,
          minPurchase: giftMinPurchase,
          repeatEnabled: giftRepeatEnabled
        };

      case 'bundle':
        // Set kampanyası - kategoriler ile çalışır
        const bundleCategories = conditions.categories || [];
        const bundleMinPurchase = conditions.min_purchase || 0;

        // Her kategoriden en az 1 ürün sepette olmalı
        const missingCategories = [];
        bundleCategories.forEach(category => {
          const hasProduct = cartItems.some(item => item.category === category);
          if (!hasProduct) {
            missingCategories.push(category);
          }
        });

        if (missingCategories.length > 0) {
          return { eligible: false, missingCategories: missingCategories, type: 'bundle' };
        }

        // Set kapsamındaki ürünlerin toplamını hesapla
        const bundleItemsTotal = cartItems
          .filter(item => bundleCategories.includes(item.category))
          .reduce((sum, item) => sum + (convertToTRY(item.price, item.currency) * item.quantity), 0);

        // Minimum tutar kontrolü
        if (bundleMinPurchase > 0 && bundleItemsTotal < bundleMinPurchase) {
          return { eligible: false, missing: bundleMinPurchase - bundleItemsTotal, type: 'amount' };
        }

        // İndirim hesapla
        const bundleDiscountPercent = rewards.discount_percent || rewards.discount_percentage || 0;
        const bundleDiscountFixed = rewards.discount_amount || 0;
        const bundleDiscountAmount = bundleDiscountPercent > 0
          ? bundleItemsTotal * bundleDiscountPercent / 100
          : bundleDiscountFixed;

        return { eligible: true, discount: bundleDiscountAmount, bundleCategories: bundleCategories };

      default:
        return { eligible: false };
    }
  };

  // Kampanya önerilerini hesapla
  const calculateCampaignSuggestions = () => {
    const suggestions = [];

    activeCampaigns.forEach(campaign => {
      const result = checkCampaignEligibility(campaign);

      if (!result.eligible && result.missing) {
        const conditions = parseConditions(campaign);
        const rewards = parseRewards(campaign);

        if (result.type === 'amount') {
          suggestions.push({
            campaign,
            message: `₺${result.missing.toFixed(0)} daha alışveriş yapın, ${campaign.name} kampanyasından faydalanın!`,
            priority: 1
          });
        } else if (result.type === 'item' && result.hasScopeItems) {
          suggestions.push({
            campaign,
            message: `${result.missing} ürün daha ekleyin, ${result.buyCount} al ${result.payCount} öde kampanyasından faydalanın!`,
            priority: 2
          });
        } else if (result.type === 'item_progressive') {
          suggestions.push({
            campaign,
            message: `${result.missing} ürün daha ekleyin, kademeli indirimden faydalanın!`,
            priority: 2
          });
        } else if (result.type === 'bundle' && result.missingCategories) {
          const missingCatNames = result.missingCategories.join(', ');
          suggestions.push({
            campaign,
            message: `Set kampanyası için şu kategorilerden ürün ekleyin: ${missingCatNames}`,
            priority: 2
          });
        } else if (result.type === 'gift_amount') {
          suggestions.push({
            campaign,
            message: `₺${result.missing.toFixed(0)} daha alışveriş yapın, hediye ürün kazanın!`,
            priority: 1
          });
        } else if (result.type === 'category_amount') {
          const catNames = result.categories?.join(', ') || 'seçili kategoriler';
          suggestions.push({
            campaign,
            message: `${catNames} kategorisinden ₺${result.missing.toFixed(0)} daha alın, indirim kazanın!`,
            priority: 1
          });
        } else if (result.type === 'no_category_items') {
          const catNames = result.categories?.join(', ') || 'seçili kategoriler';
          suggestions.push({
            campaign,
            message: `${catNames} kategorisinden ürün ekleyin, %${campaign.rewards?.discount_percent || parseRewards(campaign).discount_percent || 0} indirim kazanın!`,
            priority: 2
          });
        }
      }
    });

    // Önceliğe göre sırala
    suggestions.sort((a, b) => a.priority - b.priority);
    return suggestions.slice(0, 3); // En fazla 3 öneri göster
  };

  // En iyi kampanyayı bul ve uygula
  const findBestCampaign = () => {
    let bestCampaign = null;
    let bestDiscount = 0;
    let bestResult = null;

    activeCampaigns.forEach(campaign => {
      const result = checkCampaignEligibility(campaign);
      if (result.eligible && result.discount > bestDiscount) {
        bestDiscount = result.discount;
        bestCampaign = campaign;
        bestResult = result;
      }
    });

    return { campaign: bestCampaign, discount: bestDiscount, result: bestResult };
  };

  // Sepet değiştiğinde kampanyaları kontrol et
  useEffect(() => {
    if (cart.length === 0) {
      // Doğum günü veya özel gün kampanyası varsa koruyalım ama indirimi sıfırlayalım
      if (!birthdayCampaign && !specialDayCampaign) {
        setAppliedCampaign(null);
      }
      setCampaignDiscount(0);
      setCampaignSuggestions([]);
      return;
    }

    // Sadece normal ürünler varsa kampanya kontrolü yap
    const normalItems = cart.filter(item => !item.isRefund);
    if (normalItems.length === 0) {
      if (!birthdayCampaign && !specialDayCampaign) {
        setAppliedCampaign(null);
      }
      setCampaignDiscount(0);
      setCampaignSuggestions([]);
      return;
    }

    // Doğum günü kampanyası aktifse, indirimini hesapla
    if (birthdayCampaign) {
      const rewards = typeof birthdayCampaign.rewards === 'string'
        ? JSON.parse(birthdayCampaign.rewards)
        : birthdayCampaign.rewards || {};
      const conditions = typeof birthdayCampaign.conditions === 'string'
        ? JSON.parse(birthdayCampaign.conditions || '{}')
        : birthdayCampaign.conditions || {};

      // İndirim yalnızca kampanya kapsamındaki (seçili kategori/tür/marka) ürünlere uygulanır
      const inScope = makeCampaignScopeFilter(conditions);
      const subtotal = normalItems.filter(inScope).reduce((sum, item) => sum + (convertToTRY(item.price, item.currency) * item.quantity), 0);
      let discount = 0;

      if (rewards.discount_type === 'fixed') {
        discount = subtotal > 0 ? (rewards.discount_amount || 0) : 0;
      } else {
        discount = subtotal * ((rewards.discount_percent || 0) / 100);
      }

      // ÖNEMLİ: appliedCampaign'ı set et - cartWithDiscounts buna bakıyor
      setAppliedCampaign(birthdayCampaign);
      setCampaignDiscount(discount);
      setCampaignSuggestions([]);
      return;
    }

    // Özel gün kampanyası aktifse, indirimini hesapla
    if (specialDayCampaign) {
      const conditions = typeof specialDayCampaign.conditions === 'string'
        ? JSON.parse(specialDayCampaign.conditions)
        : specialDayCampaign.conditions || {};
      const rewards = typeof specialDayCampaign.rewards === 'string'
        ? JSON.parse(specialDayCampaign.rewards)
        : specialDayCampaign.rewards || {};

      const subtotal = normalItems.reduce((sum, item) => sum + (convertToTRY(item.price, item.currency) * item.quantity), 0);
      // İndirim yalnızca kampanya kapsamındaki (seçili kategori/tür/marka) ürünlere uygulanır
      const inScope = makeCampaignScopeFilter(conditions);
      const eligibleSubtotal = normalItems.filter(inScope).reduce((sum, item) => sum + (convertToTRY(item.price, item.currency) * item.quantity), 0);

      // Minimum harcama tutarı kontrolü
      const minPurchase = conditions.min_purchase || 0;
      const repeatEnabled = rewards.repeat || false;

      if (minPurchase > 0 && subtotal < minPurchase) {
        setAppliedCampaign(null);
        setCampaignDiscount(0);
        setCampaignSuggestions([{
          campaign: specialDayCampaign,
          message: `₺${(minPurchase - subtotal).toFixed(0)} daha alışveriş yapın, ${specialDayCampaign.name} kampanyasından faydalanın!`,
          priority: 1
        }]);
        return;
      }

      // Tekrarlama: Min. tutar kaç kez karşılandı?
      const repeatCount = repeatEnabled && minPurchase > 0
        ? Math.floor(subtotal / minPurchase)
        : 1;

      let discount = 0;
      if (rewards.discount_type === 'fixed') {
        // Sabit tutar: Tekrar sayısı kadar çarp (kapsam içi ürün yoksa indirim yok)
        discount = eligibleSubtotal > 0 ? (rewards.discount_amount || 0) * repeatCount : 0;
      } else {
        // Yüzde: Kapsam içi ürünlerin toplamına orantılı
        discount = eligibleSubtotal * ((rewards.discount_percent || 0) / 100);
      }

      // ÖNEMLİ: appliedCampaign'ı set et - cartWithDiscounts buna bakıyor
      setAppliedCampaign(specialDayCampaign);
      setCampaignDiscount(discount);
      setCampaignSuggestions([]);
      return;
    }

    // Hediye kampanyası kontrolü
    const giftCampaign = activeCampaigns.find(c => c.campaign_type === 'gift_with_purchase');
    if (giftCampaign) {
      const giftResult = checkCampaignEligibility(giftCampaign);
      if (giftResult.eligible && giftResult.needsGiftAdded && !giftResult.giftAlreadyAdded) {
        // Hediye hak edildi ama henüz eklenmedi - popup göster
        setEligibleGiftCampaign(giftCampaign);
        setGiftCampaignProduct({
          id: giftResult.giftProductId,
          name: giftResult.giftProductName,
          remainingGifts: giftResult.remainingGifts || 1,
          earnedGiftCount: giftResult.earnedGiftCount || 1,
          currentGiftCount: giftResult.currentGiftCount || 0,
          minPurchase: giftResult.minPurchase,
          repeatEnabled: giftResult.repeatEnabled
        });
        setShowGiftCampaignModal(true);
      } else if (giftResult.giftAlreadyAdded) {
        // Hediye zaten eklendi, modal'ı kapat
        setShowGiftCampaignModal(false);
        setEligibleGiftCampaign(null);
      }
    }

    // En iyi kampanyayı bul
    const { campaign, discount, result } = findBestCampaign();

    if (campaign && discount > 0) {
      setAppliedCampaign({ ...campaign, result });
      setCampaignDiscount(discount);
    } else if (!birthdayCampaign && !specialDayCampaign) {
      setAppliedCampaign(null);
      setCampaignDiscount(0);
    }

    // Önerileri hesapla
    const suggestions = calculateCampaignSuggestions();
    setCampaignSuggestions(suggestions);
  }, [cart, activeCampaigns, birthdayCampaign, specialDayCampaign]);

  // Hediye ürünü sepete ekle (quantity: kaç adet eklenecek)
  const addGiftProductToCart = async (productId, quantity = 1) => {
    try {
      // Ürün bilgisini API'den al
      const response = await fetch(`${API_URL}/products/${productId}`);
      const data = await response.json();
      const product = data.product || data;

      if (!product) {
        toast.error('Hediye ürün bulunamadı!');
        return;
      }

      // Sepette bu ürün hediye olarak var mı?
      const existingGift = cart.find(item => item.id === product.id && item.isGift);

      if (existingGift) {
        // Mevcut hediyeye quantity ekle
        setCart(prev => prev.map(item =>
          item.id === product.id && item.isGift
            ? { ...item, quantity: item.quantity + quantity }
            : item
        ));
      } else {
        // Yeni hediye ürün ekle
        const giftItem = {
          id: product.id,
          name: product.name,
          barcode: product.barcode,
          category: product.category,
          price: 0, // Hediye olduğu için 0
          originalPrice: parseFloat(product.sell_price) || 0, // Gerçek fiyat
          quantity: quantity,
          isGift: true, // Hediye ürün işareti
          giftCampaignId: eligibleGiftCampaign?.id
        };
        setCart(prev => [...prev, giftItem]);
        setSelectedPreviewItem(null); // Son eklenen ürünün resmi gösterilsin
      }

      setShowGiftCampaignModal(false);
      setEligibleGiftCampaign(null);
      setGiftCampaignProduct(null);

      if (quantity > 1) {
        toast.success(`🎁 ${quantity} adet ${product.name} hediye olarak sepete eklendi!`);
      } else {
        toast.success(`🎁 ${product.name} hediye olarak sepete eklendi!`);
      }
    } catch (error) {
      console.error('Hediye ürün eklenirken hata:', error);
      toast.error('Hediye ürün eklenemedi!');
    }
  };

  // Hediye ürünü barkod ile ekle
  const addGiftByBarcode = async (barcode) => {
    try {
      const response = await fetch(`${API_URL}/products/barcode/${barcode}`);
      const data = await response.json();
      const product = data.product || data;

      if (!product) {
        toast.error('Ürün bulunamadı!');
        return false;
      }

      // Doğru ürün mü kontrol et
      if (giftCampaignProduct && product.id !== giftCampaignProduct.id) {
        toast.warning(`Bu ürün hediye ürün değil! Hediye: ${giftCampaignProduct.name}`);
        return false;
      }

      await addGiftProductToCart(product.id);
      return true;
    } catch (error) {
      console.error('Barkod okuma hatası:', error);
      toast.error('Ürün bulunamadı!');
      return false;
    }
  };

  // Hediye ürünü ücretli olarak ekle (normal satış)
  const addGiftProductAsPaid = async (productId, quantity = 1) => {
    try {
      const response = await fetch(`${API_URL}/products/${productId}`);
      const data = await response.json();
      const product = data.product || data;

      if (!product) {
        toast.error('Ürün bulunamadı!');
        return;
      }

      // Normal ürün olarak sepete ekle
      const existingItem = cart.find(item => item.id === product.id && !item.isGift);
      if (existingItem) {
        setCart(prev => prev.map(item =>
          item.id === product.id && !item.isGift
            ? { ...item, quantity: item.quantity + quantity }
            : item
        ));
      } else {
        setCart(prev => [...prev, {
          id: product.id,
          name: product.name,
          barcode: product.barcode,
          category: product.category,
          price: Number(product.sell_price),
          quantity: quantity,
          stock: Number(product.stock),
          isGift: false
        }]);
        setSelectedPreviewItem(null); // Son eklenen ürünün resmi gösterilsin
      }

      setShowGiftCampaignModal(false);
      setEligibleGiftCampaign(null);
      setGiftCampaignProduct(null);

      toast.success(`${quantity > 1 ? quantity + ' adet ' : ''}${product.name} ücretli olarak sepete eklendi!`);
    } catch (error) {
      console.error('Ürün eklenirken hata:', error);
      toast.error('Ürün eklenemedi!');
    }
  };

  // Kampanya kullanımını kaydet
  const recordCampaignUsage = async (saleId) => {
    if (!appliedCampaign) return;

    try {
      await fetch(`${API_URL}/campaign-usage`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          campaign_id: appliedCampaign.id,
          campaign_name: appliedCampaign.name,
          campaign_type: appliedCampaign.campaign_type,
          sale_id: saleId,
          customer_id: selectedCustomer?.id,
          customer_name: selectedCustomer?.name,
          customer_phone: selectedCustomer?.phone,
          staff_id: selectedStaff[0]?.id || null,
          staff_name: [...selectedStaff].sort((a, b) => a.name.localeCompare(b.name, 'tr')).map(s => s.name).join(', '),
          staff_ids: selectedStaff.map(s => s.id),
          products: cart.filter(item => !item.isRefund).map(item => ({
            product_id: item.id,
            name: item.name,
            quantity: item.quantity,
            price: item.price
          })),
          original_total: newItemsTotal,
          discount_amount: campaignDiscount,
          final_total: newItemsTotal - campaignDiscount,
          discount_detail: getCampaignDescription(appliedCampaign)
        })
      });
    } catch (error) {
      console.error('Kampanya kullanımı kaydedilemedi:', error);
    }
  };

  // Kampanya açıklaması oluştur
  const getCampaignDescription = (campaign) => {
    if (!campaign) return '';
    const rewards = parseRewards(campaign);
    const conditions = parseConditions(campaign);

    switch (campaign.campaign_type) {
      case 'percentage':
        return `%${rewards.discount_percent || rewards.percentage || 0} indirim`;
      case 'category_discount':
        const catList = conditions.categories || [];
        return `Kategori indirimi %${rewards.discount_percent || 0}${catList.length > 0 ? ` (${catList.join(', ')})` : ''}`;
      case 'product_discount':
        const prodList = conditions.products || [];
        return `Ürün indirimi %${rewards.discount_percent || 0}${prodList.length > 0 ? ` (${prodList.length} ürün)` : ''}`;
      case 'fixed_amount':
        return `₺${rewards.discount_amount || rewards.amount || 0} indirim`;
      case 'buy_x_pay_y':
        const buyCount = conditions.buy_quantity || conditions.buy_count || 3;
        const freeQty = rewards.free_quantity || 1;
        return `${buyCount} al ${buyCount - freeQty} öde`;
      case 'progressive':
        return 'Kademeli indirim';
      case 'gift_with_purchase':
        return 'Hediye kampanyası';
      case 'bundle':
        return 'Set indirimi';
      default:
        return '';
    }
  };

  // iOS WebView: kayda dokununca türüne göre web'deki detay modalı açılır (Tüm Fişler listesiyle aynı);
  // tamirat kayıtları Tamirat sayfasında açılır (geri kaydırınca Fişler'e dönülür)
  const openMobileRecord = (sale) => {
    const type = sale.record_type;
    if (type === 'expense') openExpenseDetailModal(sale);
    else if (type === 'scrap_purchase') openScrapPurchaseDetailModal(sale);
    else if (type === 'debt_payment') openDebtPaymentDetailModal(sale);
    else if (type === 'repair_payment' || type === 'repair_deposit') {
      if (sale.repair_id) window.location.href = `/mobile/tamirat?repair=${sale.repair_id}&from=fisler`;
    } else if (type === 'exchange' || type === 'refund') openRefundDetailModal(sale);
    else openSaleDetailModal(sale);
  };

  const renderMobileFisler = () => (
    <FislerMobileHome
      isAdmin={user?.role === 'admin'}
      receipts={allSales}
      counts={allSalesTotalCounts}
      loading={allSalesLoading}
      loadingMore={loadingMoreAllSales}
      hasMore={hasMoreAllSales}
      onLoadMore={loadMoreAllSales}
      search={allSalesSearch} setSearch={setAllSalesSearch}
      typeFilter={allSalesTypeFilter} setTypeFilter={setAllSalesTypeFilter}
      dateFilter={allSalesDateFilter} setDateFilter={setAllSalesDateFilter}
      storeFilter={allSalesFilter} setStoreFilter={setAllSalesFilter}
      onOpenRecord={openMobileRecord}
    />
  );

  return (
    <div className={mobileApp ? 'rs-mobile m-sheets' : 'pos-container'}>
      {mobileApp ? renderMobileFisler() : (<>
      {/* Header */}
      <header className="pos-header">
        <div className="header-left">
          <button className="back-btn" onClick={() => navigate('/')}>
            <ArrowLeft size={16} /> Ana Sayfa
          </button>
          <h1>Perakende Satış{user?.role === 'admin' && (
            <span style={{ fontSize: '14px', fontWeight: '400', opacity: 0.85 }}>
              {currentStoreId === 1 ? ' Gold' : currentStoreId === 2 ? ' Osmangazi' : currentStoreId === 3 ? ' Yeşilsu' : ' Depo'}
            </span>
          )}</h1>
        </div>
        <div
          className="header-center"
          style={{
            // Mağazaya göre görünen kur sayısını hesapla
            // Altın mağazası: gold, usd, eur, gbp, silverUsd
            // Gümüş mağazası: silver, usd, eur, gbp, silverUsd
            left: (() => {
              const visibleCount = isGoldStore
                ? [visibleRates.gold, visibleRates.usd, visibleRates.eur, visibleRates.gbp, visibleRates.silverUsd].filter(Boolean).length
                : [visibleRates.silver, visibleRates.usd, visibleRates.eur, visibleRates.gbp, visibleRates.silverUsd].filter(Boolean).length;
              // 4 parite: biraz sola, 5 parite: daha fazla sola
              if (visibleCount >= 5) return 'calc(50% - 140px)';
              if (visibleCount === 4) return 'calc(50% - 50px)';
              return '50%';
            })()
          }}
        >
          <img
            src={isGoldStore ? "/Siyah - Beyaz - 1.png" : "/logo.png"}
            alt="Akik"
            className="header-logo"
          />
        </div>
        <div className="header-right">
          <div className="live-prices">
            {scraperWarning && (
              <div className="scraper-warning" title={scraperWarning}>
                <AlertTriangle size={14} />
              </div>
            )}
            {/* Altın mağazası: Altın, USD, EUR göster / Gümüş mağazası: Gümüş, USD, EUR göster */}
            {isGoldStore ? (
              <>
                {/* Altın Mağazası - Altın, USD, EUR sıralaması */}
                {visibleRates.gold && (
                  <div
                    className="price-item-group gold clickable"
                    onClick={() => openRateModal('GOLD')}
                    title={user?.role === 'admin' ? "Tıklayarak özel kur girebilirsiniz" : undefined}
                    style={user?.role !== 'admin' ? { cursor: 'default' } : undefined}
                  >
                    <span className="price-item-label">Altın</span>
                    <div className="price-item-rates vertical">
                      <span className={`rate sell ${priceAnimations.altin_satis ? `price-flash-${priceAnimations.altin_satis}` : ''}`}>{prices?.altin_satis || '--'}</span>
                      <span className={`rate buy ${priceAnimations.altin_alis ? `price-flash-${priceAnimations.altin_alis}` : ''}`}>{prices?.altin_alis || '--'}</span>
                    </div>
                  </div>
                )}
                {visibleRates.usd && (
                  <div
                    className="price-item-group usd clickable"
                    onClick={() => openRateModal('USD')}
                    title={user?.role === 'admin' ? "Tıklayarak özel kur girebilirsiniz" : undefined}
                    style={user?.role !== 'admin' ? { cursor: 'default' } : undefined}
                  >
                    <span className="price-item-label">$ USD</span>
                    <div className="price-item-rates vertical">
                      <span className={`rate sell ${priceAnimations.USD_satis ? `price-flash-${priceAnimations.USD_satis}` : ''}`}>{prices?.USD_satis ? parseFloat(prices.USD_satis).toFixed(2) : '--'}</span>
                      <span className={`rate buy ${priceAnimations.USD_alis ? `price-flash-${priceAnimations.USD_alis}` : ''}`}>{prices?.USD_alis ? parseFloat(prices.USD_alis).toFixed(2) : '--'}</span>
                    </div>
                  </div>
                )}
                {visibleRates.eur && (
                  <div
                    className="price-item-group eur clickable"
                    onClick={() => openRateModal('EUR')}
                    title={user?.role === 'admin' ? "Tıklayarak özel kur girebilirsiniz" : undefined}
                    style={user?.role !== 'admin' ? { cursor: 'default' } : undefined}
                  >
                    <span className="price-item-label">€ EUR</span>
                    <div className="price-item-rates vertical">
                      <span className={`rate sell ${priceAnimations.EUR_satis ? `price-flash-${priceAnimations.EUR_satis}` : ''}`}>{prices?.EUR_satis ? parseFloat(prices.EUR_satis).toFixed(2) : '--'}</span>
                      <span className={`rate buy ${priceAnimations.EUR_alis ? `price-flash-${priceAnimations.EUR_alis}` : ''}`}>{prices?.EUR_alis ? parseFloat(prices.EUR_alis).toFixed(2) : '--'}</span>
                    </div>
                  </div>
                )}
                {visibleRates.gbp && (
                  <div
                    className="price-item-group gbp clickable"
                    onClick={() => openRateModal('GBP')}
                    title={user?.role === 'admin' ? "Tıklayarak özel kur girebilirsiniz" : undefined}
                    style={user?.role !== 'admin' ? { cursor: 'default' } : undefined}
                  >
                    <span className="price-item-label">£ GBP</span>
                    <div className="price-item-rates vertical">
                      <span className={`rate sell ${priceAnimations.GBP_satis ? `price-flash-${priceAnimations.GBP_satis}` : ''}`}>{prices?.GBP_satis ? parseFloat(prices.GBP_satis).toFixed(2) : '--'}</span>
                      <span className={`rate buy ${priceAnimations.GBP_alis ? `price-flash-${priceAnimations.GBP_alis}` : ''}`}>{prices?.GBP_alis ? parseFloat(prices.GBP_alis).toFixed(2) : '--'}</span>
                    </div>
                  </div>
                )}
                {visibleRates.silverUsd && (
                  <div
                    className="price-item-group silver-usd clickable"
                    onClick={() => openRateModal('SILVER_USD')}
                    title={user?.role === 'admin' ? "Tıklayarak özel kur girebilirsiniz" : undefined}
                    style={user?.role !== 'admin' ? { cursor: 'default' } : undefined}
                  >
                    <span className="price-item-label">Ag/USD</span>
                    <div className="price-item-rates vertical">
                      <span className={`rate sell ${priceAnimations.gumus_usd_satis ? `price-flash-${priceAnimations.gumus_usd_satis}` : ''}`}>
                        {prices?.gumus_usd_satis && parseFloat(prices.gumus_usd_satis) > 0
                          ? parseFloat(prices.gumus_usd_satis).toFixed(2)
                          : prices?.gumus_satis && prices?.USD_satis
                            ? (parseFloat(prices.gumus_satis) / parseFloat(prices.USD_satis)).toFixed(2)
                            : '--'}
                      </span>
                      <span className={`rate buy ${priceAnimations.gumus_usd_alis ? `price-flash-${priceAnimations.gumus_usd_alis}` : ''}`}>
                        {prices?.gumus_usd_alis && parseFloat(prices.gumus_usd_alis) > 0
                          ? parseFloat(prices.gumus_usd_alis).toFixed(2)
                          : prices?.gumus_alis && prices?.USD_alis
                            ? (parseFloat(prices.gumus_alis) / parseFloat(prices.USD_alis)).toFixed(2)
                            : '--'}
                      </span>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <>
                {/* Gümüş Mağazaları - Gümüş, USD, EUR sıralaması */}
                {visibleRates.silver && (
                  <div
                    className="price-item-group silver clickable"
                    onClick={() => openRateModal('SILVER')}
                    title={user?.role === 'admin' ? "Tıklayarak özel kur girebilirsiniz" : undefined}
                    style={user?.role !== 'admin' ? { cursor: 'default' } : undefined}
                  >
                    <span className="price-item-label">Gümüş</span>
                    <div className="price-item-rates vertical">
                      <span className={`rate sell ${priceAnimations.gumus_satis ? `price-flash-${priceAnimations.gumus_satis}` : ''}`}>{prices?.gumus_satis || '--'}</span>
                      <span className={`rate buy ${priceAnimations.gumus_alis ? `price-flash-${priceAnimations.gumus_alis}` : ''}`}>{prices?.gumus_alis || '--'}</span>
                    </div>
                  </div>
                )}
                {visibleRates.usd && (
                  <div
                    className="price-item-group usd clickable"
                    onClick={() => openRateModal('USD')}
                    title={user?.role === 'admin' ? "Tıklayarak özel kur girebilirsiniz" : undefined}
                    style={user?.role !== 'admin' ? { cursor: 'default' } : undefined}
                  >
                    <span className="price-item-label">$ USD</span>
                    <div className="price-item-rates vertical">
                      <span className={`rate sell ${priceAnimations.USD_satis ? `price-flash-${priceAnimations.USD_satis}` : ''}`}>{prices?.USD_satis ? parseFloat(prices.USD_satis).toFixed(2) : '--'}</span>
                      <span className={`rate buy ${priceAnimations.USD_alis ? `price-flash-${priceAnimations.USD_alis}` : ''}`}>{prices?.USD_alis ? parseFloat(prices.USD_alis).toFixed(2) : '--'}</span>
                    </div>
                  </div>
                )}
                {visibleRates.eur && (
                  <div
                    className="price-item-group eur clickable"
                    onClick={() => openRateModal('EUR')}
                    title={user?.role === 'admin' ? "Tıklayarak özel kur girebilirsiniz" : undefined}
                    style={user?.role !== 'admin' ? { cursor: 'default' } : undefined}
                  >
                    <span className="price-item-label">€ EUR</span>
                    <div className="price-item-rates vertical">
                      <span className={`rate sell ${priceAnimations.EUR_satis ? `price-flash-${priceAnimations.EUR_satis}` : ''}`}>{prices?.EUR_satis ? parseFloat(prices.EUR_satis).toFixed(2) : '--'}</span>
                      <span className={`rate buy ${priceAnimations.EUR_alis ? `price-flash-${priceAnimations.EUR_alis}` : ''}`}>{prices?.EUR_alis ? parseFloat(prices.EUR_alis).toFixed(2) : '--'}</span>
                    </div>
                  </div>
                )}
                {visibleRates.gbp && (
                  <div
                    className="price-item-group gbp clickable"
                    onClick={() => openRateModal('GBP')}
                    title={user?.role === 'admin' ? "Tıklayarak özel kur girebilirsiniz" : undefined}
                    style={user?.role !== 'admin' ? { cursor: 'default' } : undefined}
                  >
                    <span className="price-item-label">£ GBP</span>
                    <div className="price-item-rates vertical">
                      <span className={`rate sell ${priceAnimations.GBP_satis ? `price-flash-${priceAnimations.GBP_satis}` : ''}`}>{prices?.GBP_satis ? parseFloat(prices.GBP_satis).toFixed(2) : '--'}</span>
                      <span className={`rate buy ${priceAnimations.GBP_alis ? `price-flash-${priceAnimations.GBP_alis}` : ''}`}>{prices?.GBP_alis ? parseFloat(prices.GBP_alis).toFixed(2) : '--'}</span>
                    </div>
                  </div>
                )}
                {visibleRates.silverUsd && (
                  <div
                    className="price-item-group silver-usd clickable"
                    onClick={() => openRateModal('SILVER_USD')}
                    title={user?.role === 'admin' ? "Tıklayarak özel kur girebilirsiniz" : undefined}
                    style={user?.role !== 'admin' ? { cursor: 'default' } : undefined}
                  >
                    <span className="price-item-label">Ag/USD</span>
                    <div className="price-item-rates vertical">
                      <span className={`rate sell ${priceAnimations.gumus_usd_satis ? `price-flash-${priceAnimations.gumus_usd_satis}` : ''}`}>
                        {prices?.gumus_usd_satis && parseFloat(prices.gumus_usd_satis) > 0
                          ? parseFloat(prices.gumus_usd_satis).toFixed(2)
                          : prices?.gumus_satis && prices?.USD_satis
                            ? (parseFloat(prices.gumus_satis) / parseFloat(prices.USD_satis)).toFixed(2)
                            : '--'}
                      </span>
                      <span className={`rate buy ${priceAnimations.gumus_usd_alis ? `price-flash-${priceAnimations.gumus_usd_alis}` : ''}`}>
                        {prices?.gumus_usd_alis && parseFloat(prices.gumus_usd_alis) > 0
                          ? parseFloat(prices.gumus_usd_alis).toFixed(2)
                          : prices?.gumus_alis && prices?.USD_alis
                            ? (parseFloat(prices.gumus_alis) / parseFloat(prices.USD_alis)).toFixed(2)
                            : '--'}
                      </span>
                    </div>
                  </div>
                )}
              </>
            )}
            {/* Üç nokta menüsü */}
            <div className="rates-menu-container">
              <button
                className="rates-menu-btn"
                onClick={() => setShowRatesMenu(!showRatesMenu)}
                title="Pariteleri göster/gizle"
              >
                <MoreVertical size={16} />
              </button>
              {showRatesMenu && (
                <div className="rates-menu-dropdown">
                  <div className="rates-menu-title">Pariteleri Göster/Gizle</div>
                  {currentStoreId === 1 ? (
                    <>
                      <label className="rates-menu-item">
                        <input type="checkbox" checked={visibleRates.gold} onChange={() => toggleRateVisibility('gold')} />
                        <span>Altın</span>
                      </label>
                      <label className="rates-menu-item">
                        <input type="checkbox" checked={visibleRates.usd} onChange={() => toggleRateVisibility('usd')} />
                        <span>$ USD</span>
                      </label>
                      <label className="rates-menu-item">
                        <input type="checkbox" checked={visibleRates.eur} onChange={() => toggleRateVisibility('eur')} />
                        <span>€ EUR</span>
                      </label>
                      <label className="rates-menu-item">
                        <input type="checkbox" checked={visibleRates.gbp} onChange={() => toggleRateVisibility('gbp')} />
                        <span>£ GBP</span>
                      </label>
                      <label className="rates-menu-item">
                        <input type="checkbox" checked={visibleRates.silverUsd} onChange={() => toggleRateVisibility('silverUsd')} />
                        <span>Ag/USD</span>
                      </label>
                    </>
                  ) : (
                    <>
                      <label className="rates-menu-item">
                        <input type="checkbox" checked={visibleRates.silver} onChange={() => toggleRateVisibility('silver')} />
                        <span>Gümüş</span>
                      </label>
                      <label className="rates-menu-item">
                        <input type="checkbox" checked={visibleRates.usd} onChange={() => toggleRateVisibility('usd')} />
                        <span>$ USD</span>
                      </label>
                      <label className="rates-menu-item">
                        <input type="checkbox" checked={visibleRates.eur} onChange={() => toggleRateVisibility('eur')} />
                        <span>€ EUR</span>
                      </label>
                      <label className="rates-menu-item">
                        <input type="checkbox" checked={visibleRates.gbp} onChange={() => toggleRateVisibility('gbp')} />
                        <span>£ GBP</span>
                      </label>
                      <label className="rates-menu-item">
                        <input type="checkbox" checked={visibleRates.silverUsd} onChange={() => toggleRateVisibility('silverUsd')} />
                        <span>Ag/USD</span>
                      </label>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="datetime">
            <span className="date">{currentTime.toLocaleDateString('tr-TR')}</span>
            <span className="time">{currentTime.toLocaleTimeString('tr-TR')}</span>
          </div>
        </div>
      </header>

      {/* 3 Sütunlu Ana İçerik */}
      <div className="pos-main">
        {/* SOL PANEL - Sepet */}
        <div className={`panel left-panel ${mobileActiveTab !== 'cart' ? 'hidden-mobile' : ''}`}>
          <div className="panel-header">
            <h2>Sepet</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span
                className="item-count"
                onClick={() => setShowReceiptNoteInput(true)}
                style={{
                  cursor: 'pointer',
                  background: receiptNote ? 'linear-gradient(135deg, #00bfa6, #0d9488)' : 'linear-gradient(135deg, #94a3b8, #64748b)',
                  fontSize: '0.78rem',
                  fontWeight: 500,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
                title={receiptNote ? `Açıklama: ${receiptNote}` : 'Fiş açıklaması ekle'}
              >
                <FileText size={12} />{receiptNote ? 'Açıklama' : 'Açıklama'}
              </span>
              <span className="item-count">{cart.reduce((sum, item) => sum + item.quantity, 0)} ürün</span>
            </div>
          </div>

          {/* Barkod Arama */}
          <div className="search-section">
            <div style={{ display: 'flex', gap: '6px', width: '100%' }}>
            <div className="search-wrapper" style={{ flex: 1, minWidth: 0 }}>
              <span className="search-icon"><Search size={18} color="#64748b" /></span>
              <input
                ref={barcodeInputRef}
                type="text"
                autoFocus
                placeholder="Barkod veya ürün adı..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
                  searchDebounceRef.current = setTimeout(() => {
                    searchProduct(e.target.value);
                  }, 250);
                }}
                onKeyDown={async (e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    // Bekleyen debounce aramasını iptal et
                    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);

                    const query = e.target.value.trim();
                    if (!query) return;

                    try {
                      // Enter'da doğrudan API çağrısı yap - eski sonuçlara güvenme
                      const storeType = getStoreTypeForCustomer();
                      const storeTypeParam = storeType ? `?storeType=${encodeURIComponent(storeType)}` : '';
                      const response = await fetch(`${API_URL}/products/search/${encodeURIComponent(query)}${storeTypeParam}`);
                      const data = await response.json();

                      if (data.length > 0) {
                        addToCart(data[0]);
                        setSearchQuery('');
                        setSearchResults([]);
                        setShowSearchResults(false);

                      } else {
                        playCartSound('error');
                        toast.error('Ürün bulunamadı!');
                      }
                    } catch (error) {
                      console.error('Barkod arama hatası:', error);
                      playCartSound('error');
                      toast.error('Arama sırasında hata oluştu!');
                    }
                  }
                }}
              />
              {searchQuery && (
                <button className="clear-btn" onClick={() => {
                  setSearchQuery('');
                  setShowSearchResults(false);
                }}><X size={16} /></button>
              )}
              <button
                className="favorites-btn"
                onClick={openFavoritesModal}
                title="Favori Ürünler"
              >
                <Star size={18} color="#eab308" fill="#eab308" />
              </button>
            </div>

            {eskiEtiketScanEnabled && (
              <div className="search-wrapper" style={{ flex: 1 }}>
                <span className="search-icon"><Tag size={18} color="#f59e0b" /></span>
                <input
                  ref={eskiEtiketInputRef}
                  type="text"
                  placeholder="Eski etiket okutun... /27"
                  value={eskiEtiketQuery}
                  onChange={(e) => setEskiEtiketQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleEskiEtiketScan(eskiEtiketQuery);
                    }
                  }}
                  style={{ borderColor: '#f59e0b' }}
                />
                {eskiEtiketQuery && (
                  <button className="clear-btn" onClick={() => setEskiEtiketQuery('')}><X size={16} /></button>
                )}
              </div>
            )}
            </div>

            {showSearchResults && searchResults.length > 0 && (
              <div className="search-dropdown">
                {searchResults.map(product => (
                  <div
                    key={product.id}
                    className="search-item"
                    onClick={() => {
                      addToCart(product);
                      setSearchQuery('');
                      setShowSearchResults(false);
                    }}
                  >
                    <div
                      className={`search-item-image ${product.image ? 'has-image' : ''}`}
                      onClick={(e) => {
                        if (product.image) {
                          e.stopPropagation();
                          openImagePopup(product.image, product.name);
                        }
                      }}
                    >
                      {product.image ? (
                        <img src={`${IMAGE_HOST}${product.image}`} alt={product.name} loading="lazy" />
                      ) : (
                        <div className="search-item-no-image"><Package size={24} color="#94a3b8" /></div>
                      )}
                    </div>
                    <div className="item-info">
                      <span className="item-name">{product.name}</span>
                      <span className="item-barcode">{product.barcode}</span>
                    </div>
                    <span className="item-price">
                      ₺{(product.metal_type === 'gold'
                        ? calculateGoldPrice(product)
                        : (product.metal_type === 'silver' || (product.metal_type === 'aksesuar' && product.category === 'Gümüş Aksesuar'))
                          ? calculateSilverPrice(product)
                          : convertToTRY(Number(product.sell_price), product.currency)
                      ).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Sepet Listesi */}
          <div className="cart-list">
            {cart.length === 0 ? (
              <div className="empty-cart">
                <span className="empty-icon"><ShoppingCart size={48} color="#cbd5e1" /></span>
                <p>Sepet boş</p>
              </div>
            ) : (
              [...cartWithDiscounts].reverse().map(item => (
                <div
                  key={item.id}
                  className={`cart-item ${item.isRefund ? 'refund-item' : ''} ${item.isGift ? 'gift-item' : ''} ${item.isFreeItem ? 'free-item' : item.itemDiscount > 0 ? 'has-discount' : ''} ${item.isCampaignItem ? 'campaign-item' : ''} ${selectedPreviewItem?.id === item.id ? 'selected-preview' : ''}`}
                  onClick={() => setSelectedPreviewItem(item)}
                >
                  <div
                    className={`cart-item-image ${item.image ? 'has-image' : ''}`}
                    onClick={(e) => { e.stopPropagation(); if (item.image) { openImagePopup(item.image, item.name); } else { setSelectedPreviewItem(item); } }}
                  >
                    {item.image ? (
                      <img src={`${IMAGE_HOST}${item.image}`} alt={item.name} loading="lazy" />
                    ) : (
                      <div className="cart-item-no-image"><Package size={20} color="#94a3b8" /></div>
                    )}
                  </div>
                  <div className="cart-item-info" onDoubleClick={(e) => { e.stopPropagation(); openItemNoteModal(item); }}>
                    <div className="cart-item-name-row">
                      <span className="cart-item-name">{item.isGift ? '🎁 ' : ''}{item.name}</span>
                      {item.no_discount && <span className="no-discount-icon"><AlertTriangle size={15} /><span className="no-discount-tooltip">Bu üründe ürün bazlı indirim kısıtlaması var. Lütfen sepet indirimi değil, ürün bazlı indirim kullanınız.</span></span>}
                      {item.isGift && <span className="gift-badge">HEDİYE</span>}
                      {item.isFreeItem && !item.isGift && <span className="free-badge">ÜCRETSİZ</span>}
                      {item.isCampaignItem && !item.isFreeItem && !item.isGift && item.freeUnitsCount > 0 && (
                        <span className="partial-free-badge">{item.freeUnitsCount} adet ücretsiz</span>
                      )}
                    </div>
                    {item.note && (
                      <span className="cart-item-note-badge" title={item.note}>
                        📝 {item.note.length > 30 ? item.note.substring(0, 30) + '...' : item.note}
                      </span>
                    )}
                    {/* Altın ürün detayları - sepette */}
                    {item.isGoldProduct && (
                      <>
                        <div className="cart-gold-details">
                          <span><Medal size={13} /> {item.goldKarat}K</span>
                          <span><Scale size={13} /> {item.customWeight || item.weight}gr</span>
                          <span><Gem size={13} /> Has: {((parseFloat(item.customWeight || item.weight) || 0) * (karatMultipliers[item.goldKarat] || 585) / 1000).toFixed(3)}gr</span>
                          <span><Wrench size={13} /> İşç: {item.laborCost || 0}</span>
                          <span><Scale size={13} /> İşç.Gr: {(((karatMultipliers[item.goldKarat] || 585) + parseFloat(item.laborCost || 0)) * parseFloat(item.customWeight || item.weight || 0) / 1000).toFixed(3)}gr</span>
                        </div>
                        <div className="cart-silver-details">
                          <span>{item.barcode || '-'}</span>
                          <span className="stock-badge"><Package size={13} /> Stok: {item.storeStock ?? item.stock_quantity ?? '-'}</span>
                        </div>
                      </>
                    )}
                    {/* Gümüş ürün detayları - sepette */}
                    {item.isSilverProduct && (
                      <div className="cart-silver-details">
                        <span>{item.barcode || '-'}</span>
                        <span className="stock-badge"><Package size={13} /> Stok: {item.storeStock ?? item.stock_quantity ?? '-'}</span>
                      </div>
                    )}
                    {/* Sarf / diğer ürün detayları - sepette */}
                    {!item.isGoldProduct && !item.isSilverProduct && (
                      <div className="cart-silver-details">
                        <span>{item.barcode || '-'}</span>
                        <span className="stock-badge"><Package size={13} /> Stok: {item.storeStock ?? item.stock_quantity ?? '-'}</span>
                      </div>
                    )}
                  </div>
                  <div className="cart-item-actions">
                    {!item.isRefund && (
                      <>
                        <button className="qty-btn" onClick={() => updateQuantity(item.id, -1)}>−</button>
                        {editingQtyItemId === item.id ? (
                          <input
                            type="number"
                            min="1"
                            className="qty-value qty-edit-input"
                            value={editingQtyValue}
                            onChange={(e) => setEditingQtyValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') saveEditedQty(item.id);
                              if (e.key === 'Escape') { setEditingQtyItemId(null); setEditingQtyValue(''); }
                            }}
                            onBlur={() => saveEditedQty(item.id)}
                            autoFocus
                            style={{ width: '40px', textAlign: 'center', padding: '2px', border: '1px solid #6366f1', borderRadius: '4px', fontSize: '14px', fontWeight: '600' }}
                          />
                        ) : (
                          <span
                            className="qty-value"
                            onDoubleClick={(e) => { e.stopPropagation(); setEditingQtyItemId(item.id); setEditingQtyValue(item.quantity.toString()); }}
                            style={{ cursor: 'pointer' }}
                            title="Çift tıkla: manuel adet gir"
                          >{item.quantity}</span>
                        )}
                        <button className="qty-btn" onClick={() => updateQuantity(item.id, 1)}>+</button>
                      </>
                    )}
                    {item.isRefund && <span className="qty-value">{item.quantity} adet</span>}
                    {!item.isRefund && !item.isGift && !item.isFreeItem && (
                      <button
                        className={`item-discount-btn ${item.customDiscount > 0 ? 'has-discount' : ''}`}
                        onClick={() => openItemDiscountModal(item)}
                        title="Ürün İndirimi"
                      >
                        <Percent size={14} />
                      </button>
                    )}
                    <button className="remove-btn" onClick={() => removeFromCart(item.id)}><Trash2 size={16} /></button>
                  </div>
                  <div className="cart-item-bottom-row">
                    {editingPriceItemId === item.id ? (
                      <div className="cart-item-price-edit">
                        <span className="currency">₺</span>
                        <PriceInput
                          value={editingPriceValue}
                          onChange={(val) => setEditingPriceValue(val)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') saveEditedPrice(item.id);
                            if (e.key === 'Escape') cancelEditingPrice();
                          }}
                          autoFocus
                        />
                        <button className="price-save-btn" onClick={() => saveEditedPrice(item.id)}>✓</button>
                        <button className="price-cancel-btn" onClick={cancelEditingPrice}><X size={14} /></button>
                      </div>
                    ) : (
                      <div className="cart-item-price-wrapper">
                        {item.isGift ? (
                          <>
                            <span className="cart-item-price original-price">
                              ₺{formatPrice(item.originalPrice || 0)}
                            </span>
                            <span className="cart-item-discounted-price gift">₺0,00</span>
                          </>
                        ) : item.isFreeItem ? (
                          <>
                            <span className="cart-item-price original-price">
                              ₺{formatPrice(Math.abs(convertToTRY(item.price, item.currency)))}
                            </span>
                            <span className="cart-item-discounted-price free">₺0,00</span>
                          </>
                        ) : (item.totalDiscountPercent > 0.01) ? (
                          <>
                            <span
                              className={`cart-item-price original-price ${canEditPrice() && !item.isRefund ? 'editable' : ''}`}
                              onClick={() => !item.isRefund && startEditingPrice(item)}
                              title={canEditPrice() && !item.isRefund ? 'Fiyatı değiştirmek için tıklayın' : ''}
                            >
                              ₺{formatPrice(Math.abs(convertToTRY(item.price, item.currency)))}
                            </span>
                            <span style={{ fontSize: '0.82rem', fontWeight: 600, color: '#dc2626', whiteSpace: 'nowrap' }}>
                              %{Number.isInteger(item.totalDiscountPercent) ? item.totalDiscountPercent : parseFloat(item.totalDiscountPercent.toFixed(2))}
                            </span>
                            {item.quantity > 1 && (
                              <span className="cart-item-unit-label">
                                ₺{formatPrice(item.finalUnitPrice)} ×{item.quantity}
                              </span>
                            )}
                            <span className="cart-item-discounted-price">
                              ₺{formatPrice(item.finalUnitPrice * item.quantity)}
                            </span>
                          </>
                        ) : (
                          <>
                            {item.quantity > 1 && (
                              <span className="cart-item-unit-label"
                                onClick={() => !item.isRefund && startEditingPrice(item)}
                              >
                                {item.price < 0 ? '-' : ''}₺{formatPrice(Math.abs(convertToTRY(item.price, item.currency)))} ×{item.quantity}
                              </span>
                            )}
                            <span
                              className={`cart-item-price ${canEditPrice() && !item.isRefund ? 'editable' : ''}`}
                              onClick={() => !item.isRefund && startEditingPrice(item)}
                              title={canEditPrice() && !item.isRefund ? 'Fiyatı değiştirmek için tıklayın' : ''}
                            >
                              {item.price < 0 ? '-' : ''}₺{formatPrice(Math.abs(convertToTRY(item.price, item.currency)) * item.quantity)}
                            </span>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* ORTA PANEL - Ürün Görseli & Fiyat */}
        <div className={`panel center-panel ${mobileActiveTab === 'checkout' ? 'active-mobile' : ''}`}>
          {/* Müşteri Bölümü - yatay bar */}
          <div className="customer-section center-customer">
            {selectedCustomer ? (
              <div className={`customer-card ${selectedCustomer.isAnonymous ? 'anonymous' : ''}`}>
                <div className="customer-avatar" style={selectedCustomer.isAnonymous ? { background: 'linear-gradient(135deg, #64748b 0%, #475569 100%)' } : {}}>
                  {selectedCustomer.isAnonymous ? '?' : selectedCustomer.name.charAt(0)}
                </div>
                <div className="customer-info">
                  <span className="customer-name">{selectedCustomer.name}</span>
                  <span className="customer-phone">{selectedCustomer.phone || '-'}</span>
                </div>
                {!(isCartLockedByApproval && selectedCustomer && !selectedCustomer.isAnonymous) && (
                  <button className="remove-customer-btn" onClick={removeSelectedCustomer}>✕</button>
                )}
              </div>
            ) : (
              <div className="no-customer clickable" onClick={() => setShowCustomerSearch(true)}>
                <User size={16} />
                <span>Müşteri seçilmedi</span>
                <span className="click-hint">Tıklayın</span>
              </div>
            )}
            {selectedCustomerDebt && selectedCustomer && (() => {
              const tlDebt = Math.max(0, Number(selectedCustomerDebt.total_tl_debt || 0) - Number(selectedCustomerDebt.total_tl_credit || 0));
              const goldGram = Number(selectedCustomerDebt.total_gold_debt || 0);
              const silverGram = Number(selectedCustomerDebt.total_silver_debt || 0);
              const goldTL = goldGram * getCurrentGoldPrice();
              const silverTL = silverGram * getCurrentSilverPrice();
              const totalDebt = tlDebt + goldTL + silverTL;
              if (totalDebt < 0.01) return null; // Borç yoksa banner gösterme
              return (
                <div className="customer-debt-banner" onClick={() => fetchCollectionCustomerDebts(selectedCustomer)}>
                  <span className="debt-icon"><Coins size={14} /></span>
                  <div className="debt-info">
                    <span className="debt-title">Bekleyen Tahsilat</span>
                    <span className="debt-amount-text">₺{totalDebt.toLocaleString('tr-TR', {maximumFractionDigits: 0})}</span>
                  </div>
                  <span className="debt-action">→</span>
                </div>
              );
            })()}
            {birthdayCampaign && selectedCustomer && (
              <div className="birthday-campaign-banner">
                <span className="birthday-icon">🎂</span>
                <div className="birthday-info">
                  <span className="birthday-title">Doğum Günü Kampanyası!</span>
                  <span className="birthday-discount">
                    {(() => {
                      const rewards = typeof birthdayCampaign.rewards === 'string' ? JSON.parse(birthdayCampaign.rewards) : birthdayCampaign.rewards || {};
                      return rewards.discount_type === 'fixed' ? `₺${rewards.discount_amount} indirim` : `%${rewards.discount_percent} indirim`;
                    })()}
                    {campaignDiscount > 0 && appliedCampaign?.campaign_type === 'birthday' && ` (-₺${campaignDiscount.toFixed(2)})`}
                  </span>
                </div>
              </div>
            )}
            {specialDayCampaign && !birthdayCampaign && (
              <div className="special-day-campaign-banner">
                <span className="special-day-icon">🎉</span>
                <div className="special-day-info">
                  <span className="special-day-title">{specialDayCampaign.name}</span>
                  <span className="special-day-discount">
                    {(() => {
                      const rewards = typeof specialDayCampaign.rewards === 'string' ? JSON.parse(specialDayCampaign.rewards) : specialDayCampaign.rewards || {};
                      return rewards.discount_type === 'fixed' ? `₺${rewards.discount_amount} indirim` : `%${rewards.discount_percent} indirim`;
                    })()}
                    {campaignDiscount > 0 && appliedCampaign?.campaign_type === 'special_day' && ` (-₺${campaignDiscount.toFixed(2)})`}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Ürün Görseli */}
          {cart.length > 0 && (
            <div
              className="product-preview-card product-preview-large"
              onClick={() => openProductDetailModal()}
              style={{ cursor: 'pointer' }}
              title="Detayları görmek için tıklayın"
            >
              {(selectedPreviewItem?.image || cart[cart.length - 1]?.image) ? (
                <img
                  src={`${IMAGE_HOST}${selectedPreviewItem?.image || cart[cart.length - 1].image}`}
                  alt={selectedPreviewItem?.name || cart[cart.length - 1].name}
                  className="preview-image"
                  style={{ borderRadius: '16px', maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                />
              ) : (
                <div className="preview-no-image">
                  <Package size={80} color="#94a3b8" />
                  <span>{selectedPreviewItem?.name || cart[cart.length - 1]?.name}</span>
                </div>
              )}
              <button
                className="product-detail-btn"
                onClick={(e) => { e.stopPropagation(); openProductDetailModal(); }}
                title="Ürün Detayları"
              >
                <Info size={18} />
              </button>
            </div>
          )}

          {/* Fiyat Detayları */}
          <div className="price-section">
            <div className="price-items-scrollable">
            {/* Kampanya Önerileri */}
            {campaignSuggestions.length > 0 && !appliedCampaign && (
              <div className="campaign-hints">
                {campaignSuggestions.map((suggestion, index) => (
                  <div key={index} className="campaign-hint">
                    <Lightbulb size={14} />
                    <span>{suggestion.message}</span>
                  </div>
                ))}
              </div>
            )}
            </div>
            {/* Toplam Bölümü - price-section içinde sabit alt kısım */}
            <div className="price-totals-wrapper">
              {/* Ara Toplam */}
              <div className="price-row subtotal-diff">
                <span>Ara Toplam</span>
                <span>₺{formatPrice(newItemsOriginalTotal)}</span>
              </div>
              {/* Ürün Bazlı İndirim */}
              {totalItemDiscounts > 0 && (
                <div className="price-row discount">
                  <span>Ürün İndirimi</span>
                  <span>-₺{formatPrice(totalItemDiscounts)}</span>
                </div>
              )}
              {/* İade Ürünleri (varsa) */}
              {refundItemsTotal > 0 && (
                <div className="price-row refund">
                  <span>İade Alınan Ürünler</span>
                  <span>-₺{formatPrice(refundItemsTotal)}</span>
                </div>
              )}
              {/* Ara Toplam (fark varsa göster) */}
              {refundItemsTotal > 0 && (
                <div className="price-row subtotal-diff">
                  <span>Ara Toplam</span>
                  <span>{subtotal < 0 ? '-' : ''}₺{formatPrice(Math.abs(subtotal))}</span>
                </div>
              )}
              {/* Kampanya İndirimi */}
              {campaignDiscount > 0 && appliedCampaign?.campaign_type !== 'birthday' && appliedCampaign?.campaign_type !== 'special_day' && (
                <div className="price-row discount campaign">
                  <span>
                    Kampanya İndirimi
                    {(appliedCampaign?.name || getCampaignDescription(appliedCampaign)) && (
                      <span style={{ display: 'block', fontSize: '11px', opacity: 0.75 }}>
                        {appliedCampaign?.name}{appliedCampaign?.name && getCampaignDescription(appliedCampaign) ? ' — ' : ''}{getCampaignDescription(appliedCampaign)}
                      </span>
                    )}
                  </span>
                  <span>-₺{formatPrice(campaignDiscount)}</span>
                </div>
              )}
              {/* Manuel İndirimler */}
              {appliedDiscounts.map(d => {
                const calcAmount = d.type === 'percent' ? (Math.max(0, discountBase) * d.value / 100)
                  : d.type === 'round' ? (d.value > 0 ? Math.max(0, totalBeforeRound - d.value) : 0)
                  : d.value;
                if (calcAmount <= 0) return null;
                return (
                  <div key={d.id} className="price-row discount">
                    <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      İndirim ({d.label})
                      <button
                        onClick={() => removeDiscount(d.id)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: '14px', fontWeight: 'bold', padding: '0 2px', lineHeight: 1 }}
                        title="İndirimi kaldır"
                      >✕</button>
                    </span>
                    <span>-₺{formatPrice(calcAmount)}</span>
                  </div>
                );
              })}
              {/* Bakiye İndirimi */}
              {giftCardDiscount > 0 && appliedGiftCard && (
                <div className="price-row discount giftcard">
                  <span className="giftcard-label">
                    Bakiye ({appliedGiftCard.code})
                    <button className="remove-giftcard-btn" onClick={removeGiftCard}>✕</button>
                  </span>
                  <span>-₺{formatPrice(giftCardDiscount)}</span>
                </div>
              )}
              {/* Hurda İndirimi - değişim modunda kullanılamaz */}
              {!isExchangeMode && calculatedScrapDebtDiscount > 0 && appliedScrapDebtCard && (
                <div className="price-row discount scrap-debt">
                  <span className="scrap-debt-label">
                    🏷️ Hurda İndirimi
                    <button className="remove-scrap-debt-btn" onClick={removeScrapDebtCard}>✕</button>
                  </span>
                  <span>-₺{formatPrice(calculatedScrapDebtDiscount)}</span>
                </div>
              )}
              {/* Müşteri Bakiyesi (ödeme yöntemi olarak) */}
              {customerGiftCardDiscount > 0 && appliedCustomerGiftCard && (
                <div className="price-row discount customer-gift-card" style={{ background: '#ecfdf5', borderLeft: '3px solid #059669' }}>
                  <span className="customer-gift-card-label">
                    <Gift size={14} style={{ marginRight: '4px' }} /> Müşteri Bakiyesi
                    <button className="remove-customer-gift-card-btn" onClick={removeCustomerGiftCard}>✕</button>
                  </span>
                  <span style={{ color: '#059669' }}>₺{formatPrice(customerGiftCardDiscount)}</span>
                </div>
              )}
              {/* Hurda Mahsubu */}
              {effectiveScrapTotal > 0 && scrapCart.length > 0 && (
                <div className="price-row scrap-deduction">
                  <span className="scrap-label">
                    Hurda ({scrapCart.reduce((sum, item) => sum + ((parseFloat(item.gross_weight) || 0) - (parseFloat(item.waste_gram) || 0)), 0).toFixed(2)} gr)
                    <button className="remove-scrap-btn" onClick={clearScrapCart}>✕</button>
                  </span>
                  <span>-₺{formatPrice(effectiveScrapTotal)}</span>
                </div>
              )}
              {/* Hurda Bonus İndirimi */}
              {scrapBonus > 0 && appliedScrap && (
                <div className="price-row discount scrap-bonus">
                  <span><Percent size={14} style={{ marginRight: '4px' }} /> Hurda Bonus (%{appliedScrap.bonus_percent})</span>
                  <span>-₺{formatPrice(scrapBonus)}</span>
                </div>
              )}
              {/* Hurda + Bonus Toplam */}
              {scrapBonus > 0 && effectiveScrapTotal > 0 && scrapCart.length > 0 && (
                <div className="price-row" style={{ fontWeight: 700, borderTop: '1px dashed #99f6e4', paddingTop: '4px', marginTop: '2px' }}>
                  <span>Hurda + Bonus Toplam</span>
                  <span>-₺{formatPrice(effectiveScrapTotal + scrapBonus)}</span>
                </div>
              )}
              {/* Bonus beklemede - sepet tutarı hurda tutarını aşmıyor */}
              {scrapBonus === 0 && appliedScrap?.bonus_amount > 0 && (
                <div className="price-row scrap-bonus-pending">
                  <span><Percent size={12} style={{ marginRight: '4px' }} /> Bonus (%{appliedScrap.bonus_percent}): Sepet tutarı hurdayı aşınca aktif</span>
                  <span className="pending-amount">+₺{formatPrice(appliedScrap.bonus_amount)}</span>
                </div>
              )}
              {/* Fark kaldırıldı - Kişiye Özel ürün olarak sepete ekleniyor */}
              {/* Toplam */}
              <div
                className={`price-row total ${total < 0 ? 'negative' : ''}`}
              >
                <span>{isExchangeMode && Math.abs(total) < 0.01 ? 'DEĞİŞİM FARKI' : total < 0 ? 'MÜŞTERİYE VERİLECEK' : (customerGiftCardDiscount > 0 ? 'ÖDENECEK' : 'TOPLAM')}</span>
                <span>{total < 0 ? `₺${formatPrice(Math.abs(total))}` : `₺${formatPrice(Math.max(0, total - customerGiftCardDiscount))}`}</span>
              </div>
            </div>
          </div>

          {/* Ödeme Butonları - Toplam durumuna göre değişir */}
          <div className="payment-section">
            {isExchangeMode && Math.abs(total) < 0.01 && cart.some(item => item.isRefund) && cart.some(item => !item.isRefund) ? (
              <>
                <button
                  className="pay-btn cash"
                  onClick={() => openPaymentWithStockCheck(() => {
                    handlePayment('Nakit');
                  })}
                  disabled={cart.length === 0}
                  style={{ flex: 1 }}
                >
                  <Banknote size={20} />
                  <span>NAKİT</span>
                </button>
                <button
                  className="pay-btn card"
                  onClick={() => openPaymentWithStockCheck(() => {
                    handlePayment('Kredi Kartı');
                  })}
                  disabled={cart.length === 0}
                  style={{ flex: 1 }}
                >
                  <CreditCard size={20} />
                  <span>KREDİ KARTI</span>
                </button>
                <button
                  className="pay-btn other"
                  onClick={() => openPaymentWithStockCheck(() => {
                    handlePayment('Havale/EFT');
                  })}
                  disabled={cart.length === 0}
                  style={{ flex: 1 }}
                >
                  <Building2 size={20} />
                  <span>HAVALE</span>
                </button>
              </>
            ) : total >= 0 ? (
              <>
                <button
                  className="pay-btn cash"
                  onClick={() => openPaymentWithStockCheck(() => {
                    setNakitReceivedAmount('');
                    setShowNakitModal(true);
                  })}
                  disabled={cart.length === 0 && adjustmentItems.length === 0}
                >
                  <Banknote size={20} />
                  <span>NAKİT</span>
                </button>
                <button
                  className="pay-btn card"
                  onClick={() => openPaymentWithStockCheck(() => {
                    setShowCardModal(true);
                  })}
                  disabled={cart.length === 0 && adjustmentItems.length === 0}
                >
                  <CreditCard size={20} />
                  <span>KREDİ KARTI</span>
                </button>
                <button
                  className="pay-btn other"
                  onClick={() => openPaymentWithStockCheck(() => {
                    setShowPaymentModal(true);
                  })}
                  disabled={cart.length === 0 && adjustmentItems.length === 0}
                >
                  <Wallet size={20} />
                  <span>DİĞER</span>
                </button>
              </>
            ) : (
              <>
                {!isExchangeMode && (
                  <>
                    <button
                      className="pay-btn refund-cash"
                      onClick={() => {
                        if (!selectedCustomer) {
                          toast.warning('Lütfen önce müşteri seçin!');
                          setShowCustomerSearch(true);
                          return;
                        }
                        if (selectedStaff.length === 0) {
                          toast.warning('Lütfen önce satış sorumlusu seçin!');
                          setShowStaffModal(true);
                          return;
                        }
                        setShowCashRefundConfirmModal(true);
                      }}
                      disabled={cart.length === 0}
                    >
                      <Banknote size={20} />
                      <span>NAKİT VER</span>
                    </button>
                    <button
                      className="pay-btn refund-transfer"
                      onClick={() => {
                        if (!selectedCustomer) {
                          toast.warning('Lütfen önce müşteri seçin!');
                          setShowCustomerSearch(true);
                          return;
                        }
                        if (selectedStaff.length === 0) {
                          toast.warning('Lütfen önce satış sorumlusu seçin!');
                          setShowStaffModal(true);
                          return;
                        }
                        setShowTransferRefundConfirmModal(true);
                      }}
                      disabled={cart.length === 0}
                    >
                      <Building2 size={20} />
                      <span>HAVALE VER</span>
                    </button>
                  </>
                )}
                <button
                  className="pay-btn scrap-debt"
                  style={isExchangeMode ? { flex: 1 } : {}}
                  onClick={() => {
                    if (!selectedCustomer) {
                      toast.warning('Lütfen önce müşteri seçin!');
                      setShowCustomerSearch(true);
                      return;
                    }
                    if (selectedCustomer?.isAnonymous) {
                      toast.warning('Kayıtsız müşteriye bakiye eklenemez!');
                      return;
                    }
                    if (selectedStaff.length === 0) {
                      toast.warning('Lütfen önce satış sorumlusu seçin!');
                      setShowStaffModal(true);
                      return;
                    }
                    if (isExchangeMode) {
                      // Değişim modunda farkı direk müşteri bakiyesine at
                      handleRefundAsGiftCard();
                    } else {
                      setShowScrapDebtTypeModal(true);
                    }
                  }}
                  disabled={cart.length === 0 || (isExchangeMode && !canRefund())}
                >
                  <Coins size={20} />
                  <span>{isExchangeMode ? 'BAKİYEYE AT' : 'BORCA AL'}</span>
                </button>
              </>
            )}
          </div>
        </div>

        {/* SAĞ PANEL - Müşteri & Butonlar */}
        <div className={`panel right-panel ${mobileActiveTab === 'actions' ? 'active-mobile' : ''}`}>
          <div className="corner-btns">
            {user?.role === 'admin' && (
              <button className="corner-btn log-corner" title="İşlem Logları" onClick={openPosLogsModal}><FileText size={14} /></button>
            )}
            <button className="corner-btn help-corner" title="Yardım" onClick={() => setShowHelpModal(true)}>?</button>
          </div>
          <div className="action-grid">
            {/* 1. satır: İade/Değişim, Tahsilat, Transfer */}
            <button className="action-btn" onClick={() => openLastSaleModal(canRefund() ? 'refund_exchange' : 'exchange')}>
              <span className="action-icon" style={{ backgroundColor: canRefund() ? 'rgba(239,68,68,0.1)' : 'rgba(59,130,246,0.1)', color: canRefund() ? '#ef4444' : '#3b82f6' }}>{canRefund() ? <Undo2 size={22} /> : <RefreshCcw size={22} />}</span>
              <span>{canRefund() ? 'İade/Değişim' : 'Değişim'}</span>
            </button>
            <button className="action-btn collection" onClick={openCollectionModal}>
              <span className="action-icon" style={{ backgroundColor: 'rgba(249,115,22,0.1)', color: '#f97316' }}><Coins size={22} /></span>
              <span>Tahsilat</span>
            </button>
            <button className="action-btn transfers" onClick={() => setShowTransfersModal(true)} style={{ position: 'relative' }}>
              {(pendingTransferCount + incomingRequestCount + rejectedRequestCount) > 0 && (
                <span style={{
                  position: 'absolute', top: -6, right: -6, zIndex: 2,
                  background: '#ef4444', color: 'white', borderRadius: '50%',
                  minWidth: 22, height: 22, display: 'flex', alignItems: 'center',
                  justifyContent: 'center', fontSize: 12, fontWeight: 700,
                  boxShadow: '0 2px 6px rgba(239,68,68,0.4)'
                }}>{pendingTransferCount + incomingRequestCount + rejectedRequestCount}</span>
              )}
              <span className="action-icon" style={{ backgroundColor: 'rgba(6,182,212,0.1)', color: '#06b6d4' }}><ArrowLeftRight size={22} /></span>
              <span>Transferler</span>
            </button>
            {/* 2. satır: Hurda Alımı, Bekleyenler, Kampanyalar */}
            <button className="action-btn scrap" onClick={() => {
              if (isExchangeMode) {
                toast.warning('Değişim işleminde hurda alımı yapılamaz!');
                return;
              }
              setShowScrapOptionsModal(true);
            }}>
              <span className="action-icon" style={{ backgroundColor: 'rgba(234,179,8,0.1)', color: '#eab308' }}><Scale size={22} /></span>
              <span>Hurda Alımı</span>
              {effectiveScrapTotal > 0 && <span className="scrap-badge">✓</span>}
            </button>
            {cart.length > 0 ? (
              <button className="action-btn" onClick={holdSale}>
                <span className="action-icon" style={{ backgroundColor: 'rgba(0,191,166,0.1)', color: '#00bfa6' }}><Pause size={22} /></span>
                <span>Beklet</span>
              </button>
            ) : (
              <button className="action-btn" onClick={() => setShowHoldModal(true)} disabled={heldSales.length === 0 && terminalCarts.length === 0}>
                <span className="action-icon" style={{ backgroundColor: 'rgba(6,182,212,0.1)', color: '#06b6d4' }}><ClipboardList size={22} /></span>
                <span>Bekleyenler ({heldSales.length + terminalCarts.length})</span>
              </button>
            )}
            <button className="action-btn" onClick={openCampaignsModal}>
              <span className="action-icon" style={{ backgroundColor: 'rgba(244,63,94,0.1)', color: '#f43f5e' }}><Gift size={22} /></span>
              <span>Kampanyalar</span>
            </button>
            {/* 3. satır: Son Satış, Fark, Ürün & Fiyat Sorgula */}
            <button className="action-btn" onClick={() => openLastSaleModal('view')}>
              <span className="action-icon" style={{ backgroundColor: 'rgba(100,116,139,0.1)', color: '#64748b' }}><ScrollText size={22} /></span>
              <span>Fişler</span>
            </button>
            <button className="action-btn" onClick={addCustomProduct}>
              <span className="action-icon" style={{ backgroundColor: 'rgba(139,92,246,0.1)', color: '#8b5cf6' }}><User size={22} /></span>
              <span>Kişiye Özel</span>
            </button>
            <button className="action-btn primary" onClick={() => setShowProductLookupModal(true)}>
              <span className="action-icon" style={{ backgroundColor: 'rgba(0,191,166,0.1)', color: '#00bfa6' }}><SearchCheck size={22} /></span>
              <span>Ürün & Fiyat Sorgula</span>
            </button>
            {/* 4. satır: Satış Sorumlusu, Tamirat, Masraflar */}
            <button className="action-btn" onClick={() => {
              if (isCartLockedByApproval) {
                toast.warning('Onaylı indirim varken satış sorumlusu değiştirilemez!');
                return;
              }
              if (scrapCart.length > 0) {
                toast.warning('Hurda alımı yapıldıktan sonra satış sorumlusu değiştirilemez!');
                return;
              }
              const hasAnyDiscount = appliedDiscounts.length > 0 || cart.some(item => item.customItemDiscount > 0);
              if (hasAnyDiscount && user?.role !== 'admin') {
                toast.warning('İndirim uygulandıktan sonra satış sorumlusu değiştirilemez! Önce indirimleri kaldırın.');
                return;
              }
              setShowStaffModal(true);
            }}>
              <span className="action-icon" style={{ backgroundColor: 'rgba(99,102,241,0.1)', color: '#6366f1' }}><User size={22} /></span>
              <span>{selectedStaff.length > 0 ? [...selectedStaff].sort((a, b) => a.name.localeCompare(b.name, 'tr')).map(s => s.name).join(', ') : 'Satış Sorumlusu'}</span>
            </button>
            <button className="action-btn repair" style={{ position: 'relative', overflow: 'visible' }} onClick={() => setShowRepairOptionsModal(true)}>
              {repairOverdueCount > 0 && (
                <span style={{
                  position: 'absolute', top: -6, right: -6, zIndex: 2,
                  background: '#ef4444', color: 'white', borderRadius: '50%',
                  minWidth: 22, height: 22, display: 'flex', alignItems: 'center',
                  justifyContent: 'center', fontSize: 12, fontWeight: 700,
                  boxShadow: '0 2px 6px rgba(239,68,68,0.4)',
                  animation: 'cardBadgeBlink 1.1s ease-in-out infinite'
                }}>{repairOverdueCount}</span>
              )}
              <span className="action-icon" style={{ backgroundColor: 'rgba(0,191,166,0.1)', color: '#00bfa6' }}><Wrench size={22} /></span>
              <span>Tamirat</span>
            </button>
            <button className="action-btn" onClick={openExpensesModal}>
              <span className="action-icon" style={{ backgroundColor: 'rgba(236,72,153,0.1)', color: '#ec4899' }}><Banknote size={22} /></span>
              <span>Masraflar</span>
            </button>
            {/* 5. satır: İndirim, Sepeti Temizle, Gün Sonu */}
            <button className="action-btn" style={{ position: 'relative', overflow: 'visible' }} onClick={() => {
              if (user?.role !== 'admin' && selectedStaff.length === 0) {
                toast.error('İndirim uygulayabilmek için önce kasiyer seçmelisiniz!', { autoClose: 6000 });
                return;
              }
              setDiscountAmount(0);
              setShowDiscountModal(true);
            }}>
              <span className="action-icon" style={{ backgroundColor: 'rgba(245,158,11,0.1)', color: '#f59e0b' }}><Tag size={22} /></span>
              <span>İndirim</span>
              {appliedDiscounts.length > 0 && (
                <span style={{
                  position: 'absolute',
                  top: '-6px',
                  right: '-6px',
                  minWidth: '20px',
                  height: '20px',
                  padding: '0 4px',
                  borderRadius: '50%',
                  background: '#ef4444',
                  color: '#fff',
                  fontSize: '13px',
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 2px 6px rgba(239,68,68,0.45)',
                  border: '2px solid #fff',
                  lineHeight: 1,
                  zIndex: 2,
                }}>!</span>
              )}
            </button>
            <button className="action-btn danger" onClick={clearCart}>
              <span className="action-icon" style={{ backgroundColor: 'rgba(239,68,68,0.1)', color: '#ef4444' }}><Trash2 size={22} /></span>
              <span>Sepeti Temizle</span>
            </button>
            <button className="action-btn" onClick={openEndOfDayReport}>
              <span className="action-icon" style={{ backgroundColor: 'rgba(14,165,233,0.1)', color: '#0ea5e9' }}><BarChart3 size={22} /></span>
              <span>Gün Sonu Raporu</span>
            </button>
          </div>
        </div>
      </div>
      </>)}

      {/* MODALS */}

      {/* Müşteri Arama Modal */}
      {showCustomerSearch && (
        <div className={`modal-overlay ${closingModal === 'customerSearch' ? 'closing' : ''}`}>
          <div className="modal customer-search-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2
                onMouseDown={startAnonymousHold}
                onMouseUp={cancelAnonymousHold}
                onMouseLeave={cancelAnonymousHold}
                onTouchStart={startAnonymousHold}
                onTouchEnd={cancelAnonymousHold}
                onTouchCancel={cancelAnonymousHold}
                style={{ userSelect: 'none' }}
              >
                Müşteri Ara
              </h2>
              <button className="close-btn" onClick={() => handleCloseModal('customerSearch', setShowCustomerSearch)}>✕</button>
            </div>
            <div className="modal-body">
              {/* Yeni Müşteri Butonu */}
              <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                <button
                  onClick={() => {
                    setShowCustomerSearch(false);
                    setCustomerSearchQuery('');
                    setShowCustomerModal(true);
                  }}
                  style={{
                    flex: 1,
                    padding: '12px',
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '0.95rem',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                >
                  <Plus size={18} /> Yeni Müşteri
                </button>
              </div>

              <input
                type="text"
                className="modal-input"
                placeholder="İsim veya telefon ile ara..."
                value={customerSearchQuery}
                onChange={(e) => {
                  setCustomerSearchQuery(e.target.value);
                  searchCustomer(e.target.value);
                }}
                autoFocus
              />
              <div className="customer-results">
                {customerSearchResults.map(customer => (
                  <div
                    key={customer.id}
                    className="customer-result"
                    onClick={() => selectCustomerWithDebtCheck(customer)}
                  >
                    <div className="customer-avatar">{customer.name.charAt(0)}</div>
                    <div className="customer-info">
                      <span className="name">{customer.name}</span>
                      <span className="phone">{customer.phone}</span>
                    </div>
                    {(customer._tlBalance || customer._goldBalance || customer._silverBalance) ? (
                      <div style={{ marginLeft: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '3px', flexShrink: 0 }}>
                        {customer._tlBalance !== 0 && customer._tlBalance !== undefined && (
                          <span style={{ fontSize: '0.88rem', fontWeight: 700, color: customer._tlBalance < 0 ? '#dc2626' : '#16a34a', background: customer._tlBalance < 0 ? '#fef2f2' : '#f0fdf4', padding: '2px 10px', borderRadius: '10px' }}>
                            {customer._tlBalance > 0 ? '+' : ''}₺{customer._tlBalance.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                        )}
                        {Math.abs(customer._goldBalance || 0) > 0.0001 && (() => {
                          const goldTL = customer._goldBalance * getSatisRate('GOLD');
                          return (
                            <span style={{ fontSize: '0.82rem', fontWeight: 600, color: customer._goldBalance < 0 ? '#dc2626' : '#d97706' }}>
                              {customer._goldBalance > 0 ? '+' : ''}{customer._goldBalance.toFixed(3)} gr altın <span style={{ fontSize: '0.78rem', color: customer._goldBalance < 0 ? '#dc2626' : '#94a3b8' }}>(₺{Math.abs(goldTL).toLocaleString('tr-TR', { maximumFractionDigits: 0 })})</span>
                            </span>
                          );
                        })()}
                        {Math.abs(customer._silverBalance || 0) > 0.0001 && (() => {
                          const silverTL = customer._silverBalance * getSatisRate('SILVER');
                          return (
                            <span style={{ fontSize: '0.82rem', fontWeight: 600, color: customer._silverBalance < 0 ? '#dc2626' : '#6b7280' }}>
                              {customer._silverBalance > 0 ? '+' : ''}{customer._silverBalance.toFixed(2)} gr gümüş <span style={{ fontSize: '0.78rem', color: customer._silverBalance < 0 ? '#dc2626' : '#94a3b8' }}>(₺{Math.abs(silverTL).toLocaleString('tr-TR', { maximumFractionDigits: 0 })})</span>
                            </span>
                          );
                        })()}
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Yeni Müşteri Modal */}
      {showCustomerModal && (
        <div className={`modal-overlay ${closingModal === 'customer' ? 'closing' : ''}`}>
          <div className="modal customer-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Yeni Müşteri</h2>
              <button className="close-btn" onClick={() => handleCloseModal('customer', setShowCustomerModal, () => setPhoneMatchCustomers([]))}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label><User size={14} style={{ marginRight: '6px' }} />Ad Soyad *</label>
                  <input
                    type="text"
                    value={newCustomer.name}
                    onChange={e => setNewCustomer({...newCustomer, name: e.target.value})}
                    placeholder="Müşteri adı..."
                  />
                </div>
                <div className="form-group phone-search-group">
                  <label><Phone size={14} style={{ marginRight: '6px' }} />Telefon</label>
                  <input
                    type="tel"
                    value={formatPhoneDisplay(newCustomer.phone)}
                    onChange={e => {
                      const digits = normalizePhoneDigits(e.target.value);
                      setNewCustomer({...newCustomer, phone: digits});
                      searchCustomerByPhone(digits);
                    }}
                    placeholder="0 532 XXX XX XX"
                  />
                  {phoneMatchCustomers.length > 0 && (
                    <div className="phone-match-dropdown">
                      <div className="phone-match-header">Mevcut müşteriler:</div>
                      {phoneMatchCustomers.map(customer => (
                        <div
                          key={customer.id}
                          className="phone-match-item"
                          onClick={() => selectPhoneMatchCustomer(customer)}
                        >
                          <span className="match-name">{customer.name}</span>
                          <span className="match-phone">{customer.phone}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label><Mail size={14} style={{ marginRight: '6px' }} />Email</label>
                  <input
                    type="email"
                    value={newCustomer.email}
                    onChange={e => setNewCustomer({...newCustomer, email: e.target.value})}
                    placeholder="email@example.com"
                  />
                </div>
                <div className="form-group">
                  <label><IdCard size={14} style={{ marginRight: '6px' }} />Vergi No / TC</label>
                  <input
                    type="text"
                    value={newCustomer.tax_number}
                    onChange={e => setNewCustomer({...newCustomer, tax_number: e.target.value})}
                    placeholder="Vergi numarası veya TC kimlik no"
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label><MapPin size={14} style={{ marginRight: '6px' }} />Adres</label>
                  <textarea
                    value={newCustomer.address}
                    onChange={e => setNewCustomer({...newCustomer, address: e.target.value})}
                    placeholder="Müşteri adresi"
                    rows="2"
                  />
                </div>
                <div className="form-group">
                  <label><Calendar size={14} style={{ marginRight: '6px' }} />Doğum Tarihi</label>
                  <input
                    type="date"
                    value={newCustomer.birthday}
                    onChange={e => setNewCustomer({...newCustomer, birthday: e.target.value})}
                  />
                </div>
              </div>

              {/* KVKK Onay Checkboxı */}
              <div className="consent-section">
                <div className="consent-checkbox kvkk-checkbox">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={newCustomer.kvkk_consent}
                      onChange={(e) => setNewCustomer({...newCustomer, kvkk_consent: e.target.checked})}
                    />
                    <span className="checkbox-text">
                      <span className="kvkk-link" onClick={(e) => { e.preventDefault(); setShowKvkkModal(true); }}>KVKK Aydınlatma Metni</span>'ni okudum ve kabul ediyorum *
                    </span>
                  </label>
                  {!newCustomer.kvkk_consent && (
                    <span className="kvkk-warning">KVKK onayı zorunludur</span>
                  )}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => { setShowCustomerModal(false); setPhoneMatchCustomers([]); }}>İptal</button>
              <button className="btn-save" onClick={createCustomer} disabled={!newCustomer.kvkk_consent}>Kaydet</button>
            </div>
          </div>
        </div>
      )}

      {/* Kişiye Özel Ürün Modalı */}
      {showCustomProductModal && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: '420px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Kişiye Özel Ürün</h2>
              <button className="close-btn" onClick={() => setShowCustomProductModal(false)}>✕</button>
            </div>
            <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>Fiyat (₺) *</label>
                <PriceInput
                  value={customProductPrice}
                  onChange={val => setCustomProductPrice(val)}
                  placeholder="0,00"
                  autoFocus
                  onKeyDown={e => { if (e.key === 'Enter') confirmAddCustomProduct(); }}
                  style={{ padding: '12px 14px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '1.1rem', fontWeight: 700, width: '100%', boxSizing: 'border-box' }}
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>Not</label>
                <textarea
                  value={customProductNote}
                  onChange={e => setCustomProductNote(e.target.value)}
                  placeholder="Ürün açıklaması..."
                  rows={3}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); confirmAddCustomProduct(); } }}
                  style={{ padding: '10px 14px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '0.9rem', resize: 'vertical' }}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowCustomProductModal(false)}>İptal</button>
              <button className="btn-save" onClick={confirmAddCustomProduct} disabled={!customProductPrice || parseFloat(customProductPrice) <= 0}>
                Sepete Ekle
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Özel Tanımlı Ürün Gramaj Giriş Modalı */}
      {showSpecialWeightModal && specialWeightProduct && (
        <div className="modal-overlay">
          <div className="modal special-weight-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>⚖️ Gramaj Girişi</h2>
              <button className="close-btn" onClick={() => setShowSpecialWeightModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="special-weight-product-info">
                <div className="product-name">{specialWeightProduct.name}</div>
                <div className="product-details">
                  <span className="detail-item">
                    <strong>Toplam Gramaj:</strong> {specialWeightProduct.weight} gr
                  </span>
                  <span className="detail-item">
                    <strong>Ayar:</strong> {specialWeightProduct.gold_karat} Ayar
                  </span>
                  <span className="detail-item">
                    <strong>İşçilik:</strong> {specialWeightProduct.labor_cost || 0}
                  </span>
                </div>
              </div>

              <div className="special-weight-input-section">
                <label>Satılacak Gramaj (gr)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  max={specialWeightProduct.weight}
                  placeholder="Gramaj giriniz..."
                  value={specialWeightInput}
                  onChange={(e) => setSpecialWeightInput(e.target.value)}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      confirmSpecialWeight();
                    }
                  }}
                />
                {specialWeightInput && parseFloat(specialWeightInput) > 0 && (
                  <div className="special-weight-preview">
                    <div className="preview-item">
                      <span className="preview-label">Hesaplanan Fiyat:</span>
                      <span className="preview-value price">
                        ₺{calculateGoldPriceWithWeight(specialWeightProduct, parseFloat(specialWeightInput)).toLocaleString('tr-TR', { maximumFractionDigits: 2 })}
                      </span>
                    </div>
                    <div className="preview-item">
                      <span className="preview-label">Has Gram:</span>
                      <span className="preview-value">
                        {(({
                          '8': 333, '9': 375, '10': 417, '14': 585, '18': 750,
                          '21': 875, '22': 916, '24-995': 995, '24-999': 999
                        }[specialWeightProduct.gold_karat] || 585) * parseFloat(specialWeightInput || 0) / 1000).toFixed(3)} gr
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowSpecialWeightModal(false)}>İptal</button>
              <button
                className="btn-save"
                onClick={confirmSpecialWeight}
                disabled={!specialWeightInput || parseFloat(specialWeightInput) <= 0}
              >
                Sepete Ekle
              </button>
            </div>
          </div>
        </div>
      )}

      {/* İndirim Modal */}
      {showDiscountModal && (
        <div className={`modal-overlay ${closingModal === 'discount' ? 'closing' : ''}`}>
          <div className="modal discount-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>İndirim Uygula</h2>
              <button className="close-btn" onClick={() => { setDiscountAmount(0); handleCloseModal('discount', setShowDiscountModal); }}>✕</button>
            </div>
            <div className="modal-body">
              {/* Mevcut uygulanan indirimler listesi */}
              {appliedDiscounts.length > 0 && (
                <div style={{ marginBottom: '10px', padding: '8px 12px', background: 'rgba(245,158,11,0.08)', borderRadius: '8px', border: '1px solid rgba(245,158,11,0.2)' }}>
                  {(() => {
                    let remaining = Math.max(0, discountBase);
                    return appliedDiscounts.map(d => {
                      let calcAmount = 0;
                      if (d.type === 'percent') {
                        calcAmount = remaining * d.value / 100;
                      } else if (d.type === 'amount') {
                        calcAmount = d.value;
                      } else if (d.type === 'round' && d.value > 0) {
                        calcAmount = Math.max(0, remaining - d.value);
                      }
                      remaining = Math.max(0, remaining - calcAmount);
                      return (
                        <div key={d.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0' }}>
                          <span style={{ fontSize: '0.8rem', color: '#334155' }}>
                            {d.type === 'percent' ? 'Yüzde' : d.type === 'round' ? 'Yuvarla' : 'Tutar'}: {d.label}
                          </span>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{ fontWeight: 600, color: '#dc2626', fontSize: '0.85rem' }}>-₺{formatPrice(calcAmount)}</span>
                            <button
                              onClick={() => removeDiscount(d.id)}
                              style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '4px', cursor: 'pointer', color: '#dc2626', fontSize: '11px', fontWeight: 'bold', padding: '1px 5px', lineHeight: 1 }}
                              title="İndirimi kaldır"
                            >✕</button>
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              )}

              <div className="discount-type-section">
                <div className="discount-type-buttons">
                  <button
                    className={`discount-type-btn ${discountType === 'percent' ? 'active' : ''}`}
                    onClick={() => { setDiscountType('percent'); setDiscountAmount(0); }}
                  >
                    <span className="discount-type-icon">%</span>
                    <span className="discount-type-text">Yüzde</span>
                  </button>
                  <button
                    className={`discount-type-btn ${discountType === 'amount' ? 'active' : ''}`}
                    onClick={() => { setDiscountType('amount'); setDiscountAmount(0); }}
                  >
                    <span className="discount-type-icon">₺</span>
                    <span className="discount-type-text">Tutar</span>
                  </button>
                </div>
              </div>

              <div className="discount-input-section">
                <label className="discount-input-label">
                  {discountType === 'percent' ? 'İndirim Oranı' : discountType === 'round' ? 'Hedef Fiyat' : 'İndirim Tutarı'}
                </label>
                <div className="discount-input-wrapper">
                  <span className="discount-input-prefix">
                    {discountType === 'percent' ? '%' : '₺'}
                  </span>
                  {discountType === 'percent' ? (
                    <input
                      type="number"
                      className="discount-input"
                      value={discountAmount || ''}
                      onChange={e => handleSetCartDiscount(Number(e.target.value) || 0)}
                      min="0"
                      max={100}
                      placeholder=""
                      autoFocus
                    />
                  ) : (
                    <PriceInput
                      className="discount-input"
                      value={discountAmount || 0}
                      onChange={(val) => handleSetCartDiscount(val)}
                      placeholder="0,00"
                      autoFocus
                      hideText
                    />
                  )}
                </div>
                {user?.role !== 'admin' && selectedStaff.length === 0 && (
                  <p style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>
                    İndirim uygulayabilmek için kasiyer seçmelisiniz
                  </p>
                )}
                {user?.role !== 'admin' && maxDiscountLimit !== null && selectedStaff.length > 0 && (() => {
                  const base = Math.max(0, discountBase);
                  let currentPercent = 0;
                  if (discountType === 'percent') currentPercent = discountAmount || 0;
                  else if (discountType === 'amount') currentPercent = base > 0 ? ((discountAmount || 0) / base) * 100 : 0;
                  else if (discountType === 'round') currentPercent = base > 0 && totalBeforeRound > 0 && discountAmount > 0 ? ((totalBeforeRound - discountAmount) / base) * 100 : 0;
                  const effectiveLimit = approvedDiscountOverride || maxDiscountLimit;
                  const exceedsLimit = currentPercent > effectiveLimit;
                  return (
                    <div style={{ marginTop: '4px' }}>
                      {approvedDiscountOverride && (
                        <p style={{ color: '#22c55e', fontSize: '12px', margin: 0 }}>
                          {`İndirim onaylandı: %${Math.round(approvedDiscountOverride * 100) / 100}`}
                        </p>
                      )}
                      {exceedsLimit && discountApprovalStatus !== 'pending' && (
                        <button
                          onClick={() => {
                            let requestedPercent = currentPercent;
                            let requestedAmount = 0;
                            if (discountType === 'percent') requestedAmount = base * discountAmount / 100;
                            else if (discountType === 'amount') requestedAmount = discountAmount;
                            else if (discountType === 'round') requestedAmount = totalBeforeRound > 0 ? totalBeforeRound - discountAmount : 0;
                            if (approvedDiscountOverride) {
                              setApprovedDiscountOverride(null);
                            }
                            const discountedTotal = Math.max(0, base - (campaignDiscount || 0) - requestedAmount);
                            requestDiscountApproval(requestedPercent, requestedAmount, base, discountedTotal, { source: 'cart' });
                          }}
                          disabled={isRequestingApproval}
                          style={{
                            marginTop: '6px', padding: '6px 16px', fontSize: '12px', fontWeight: 600,
                            background: '#3b82f6', color: 'white', border: 'none', borderRadius: '6px',
                            cursor: isRequestingApproval ? 'not-allowed' : 'pointer', opacity: isRequestingApproval ? 0.6 : 1
                          }}
                        >
                          {isRequestingApproval ? 'Gönderiliyor...' : 'Onay İste'}
                        </button>
                      )}
                      {discountApprovalStatus === 'pending' && (
                        <p style={{ color: '#3b82f6', fontSize: '12px', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span style={{ display: 'inline-block', width: '10px', height: '10px', borderRadius: '50%', border: '2px solid #3b82f6', borderTopColor: 'transparent', animation: 'spin 1s linear infinite' }} />
                          Onay bekleniyor...
                        </p>
                      )}
                      {discountApprovalStatus === 'rejected' && (
                        <p style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>
                          İndirim talebi reddedildi.
                        </p>
                      )}
                    </div>
                  );
                })()}
                {discountType === 'amount' && (
                  <div className="round-suggestions">
                    {discountAmount > 0 && numberToText(discountAmount) && (
                      <span className="round-suggestions-label" style={{ fontStyle: 'italic', color: '#64748b', letterSpacing: '0.3px' }}>
                        #{numberToText(discountAmount)}#
                      </span>
                    )}
                    <div className="round-suggestion-buttons">
                      {[100, 200, 500, 1000, 2000, 5000]
                        .filter(val => val <= discountBase)
                        .map((amount, idx) => (
                        <button
                          key={idx}
                          type="button"
                          className={`round-suggestion-btn ${discountAmount === amount ? 'active' : ''}`}
                          onClick={() => handleSetCartDiscount(amount)}
                        >
                          ₺{amount.toLocaleString('tr-TR')}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {discountType === 'percent' && (
                  <div className="round-suggestions">
                    <span className="round-suggestions-label">Hızlı Yüzde:</span>
                    <div className="round-suggestion-buttons">
                      {[3, 5, 7, 10].map((percent, idx) => (
                        <button
                          key={idx}
                          type="button"
                          className={`round-suggestion-btn ${discountAmount === percent ? 'active' : ''}`}
                          onClick={() => handleSetCartDiscount(percent)}
                        >
                          %{percent}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {(() => {
                // Kasiyerin girdiği değeri (cap'lenmemiş hâli) önizlemede göster.
                // Sepete uygulanan asıl indirim cap'lenir; gerçek satış için onay gerekir.
                const inputVal = Number(discountAmount) || 0;
                const base = Math.max(0, discountBase);
                let typedManualDiscount = 0;
                if (inputVal > 0) {
                  if (discountType === 'percent') {
                    typedManualDiscount = base * Math.min(100, inputVal) / 100;
                  } else if (discountType === 'amount') {
                    typedManualDiscount = Math.min(base, inputVal);
                  } else if (discountType === 'round') {
                    typedManualDiscount = Math.max(0, totalBeforeRound - inputVal);
                  }
                } else {
                  typedManualDiscount = manualDiscount;
                }
                const existingTotalDiscount = typedManualDiscount + campaignDiscount;
                const previewTotal = Math.max(0, base - existingTotalDiscount);
                return (
                  <div className="discount-summary">
                    {existingTotalDiscount > 0 && (
                      <div className="discount-summary-row" style={{ opacity: 0.7 }}>
                        <span>Liste Fiyatı</span>
                        <span className="discount-summary-value" style={{ textDecoration: 'line-through' }}>₺{formatPrice(discountBase)}</span>
                      </div>
                    )}
                    {existingTotalDiscount > 0 && (
                      <div className="discount-summary-row discount-row">
                        <span>İndirim</span>
                        <span className="discount-summary-value discount-value">-₺{formatPrice(existingTotalDiscount)} <span style={{ fontSize: '0.85em', opacity: 0.85 }}>(%{base > 0 ? parseFloat((existingTotalDiscount / base * 100).toFixed(2)) : 0})</span></span>
                      </div>
                    )}
                    <div className="discount-summary-row total-row">
                      <span>Toplam</span>
                      <span className="discount-summary-value total-value">₺{formatPrice(previewTotal)}</span>
                    </div>
                  </div>
                );
              })()}
            </div>
            <div className="modal-footer">
              {appliedDiscounts.length > 0 && (
                <button className="btn-cancel" onClick={() => {
                  setAppliedDiscounts([]);
                  setDiscountAmount(0);
                }}>Tümünü Kaldır</button>
              )}
              <button className="btn-save" onClick={() => { setDiscountAmount(0); setShowDiscountModal(false); }}>Tamamla</button>
            </div>
          </div>
        </div>
      )}

      {/* Ürün Bazlı İndirim Modal */}
      {showItemDiscountModal && selectedItemForDiscount && (
        <div className={`modal-overlay ${closingModal === 'itemDiscount' ? 'closing' : ''}`}>
          <div className="modal discount-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Ürün İndirimi</h2>
              <button className="close-btn" onClick={() => { setShowItemDiscountModal(false); setSelectedItemForDiscount(null); }}>✕</button>
            </div>
            <div className="modal-body">
              <div className="item-discount-product-info">
                <span className="item-discount-product-name">{selectedItemForDiscount.name}</span>
                <span className="item-discount-product-price">Birim Fiyat: ₺{formatPrice(convertToTRY(selectedItemForDiscount.price, selectedItemForDiscount.currency))} × {selectedItemForDiscount.quantity} adet</span>
              </div>

              <div className="discount-type-section">
                <p className="discount-type-label">İndirim Türü</p>
                <div className="discount-type-buttons">
                  <button
                    className={`discount-type-btn ${itemDiscountType === 'percent' ? 'active' : ''}`}
                    onClick={() => { setItemDiscountType('percent'); setItemDiscountAmount(0); }}
                  >
                    <span className="discount-type-icon">%</span>
                    <span className="discount-type-text">Yüzde</span>
                  </button>
                  <button
                    className={`discount-type-btn ${itemDiscountType === 'amount' ? 'active' : ''}`}
                    onClick={() => { setItemDiscountType('amount'); setItemDiscountAmount(0); }}
                  >
                    <span className="discount-type-icon">₺</span>
                    <span className="discount-type-text">Tutar</span>
                  </button>
                </div>
              </div>

              <div className="discount-input-section">
                <label className="discount-input-label">
                  {itemDiscountType === 'percent' ? 'İndirim Oranı' : itemDiscountType === 'round' ? 'Hedef Fiyat' : 'İndirim Tutarı'}
                </label>
                <div className="discount-input-wrapper">
                  <span className="discount-input-prefix">
                    {itemDiscountType === 'percent' ? '%' : '₺'}
                  </span>
                  {itemDiscountType === 'percent' ? (
                    <input
                      type="number"
                      className="discount-input"
                      value={itemDiscountAmount || ''}
                      onChange={e => setItemDiscountAmount(Number(e.target.value) || 0)}
                      min="0"
                      max={100}
                      placeholder=""
                      autoFocus
                    />
                  ) : (
                    <PriceInput
                      className="discount-input"
                      value={itemDiscountAmount || 0}
                      onChange={(val) => setItemDiscountAmount(val)}
                      placeholder="0,00"
                      autoFocus
                    />
                  )}
                </div>
                {user?.role !== 'admin' && selectedStaff.length === 0 && (
                  <p style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>
                    İndirim uygulayabilmek için kasiyer seçmelisiniz
                  </p>
                )}
                {user?.role !== 'admin' && maxDiscountLimit !== null && selectedStaff.length > 0 && (() => {
                  const base = Math.max(0, discountBase);
                  const itemTotal = convertToTRY(selectedItemForDiscount.price, selectedItemForDiscount.currency) * selectedItemForDiscount.quantity;
                  let requestedAmount = 0;
                  if (itemDiscountType === 'percent') requestedAmount = itemTotal * (itemDiscountAmount || 0) / 100;
                  else if (itemDiscountType === 'amount') requestedAmount = itemDiscountAmount || 0;
                  else if (itemDiscountType === 'round') requestedAmount = Math.max(0, itemTotal - (itemDiscountAmount || 0));
                  const requestedPercent = base > 0 ? (requestedAmount / base) * 100 : 0;
                  const effectiveLimit = approvedDiscountOverride || maxDiscountLimit;
                  const exceedsLimit = requestedPercent > effectiveLimit;
                  return (
                    <div style={{ marginTop: '4px' }}>
                      {approvedDiscountOverride && (
                        <p style={{ color: '#22c55e', fontSize: '12px', margin: 0 }}>
                          {`İndirim onaylandı: %${Math.round(approvedDiscountOverride * 100) / 100}`}
                        </p>
                      )}
                      {exceedsLimit && discountApprovalStatus !== 'pending' && (
                        <button
                          onClick={() => {
                            if (approvedDiscountOverride) {
                              setApprovedDiscountOverride(null);
                            }
                            const discountedTotal = Math.max(0, base - (campaignDiscount || 0) - requestedAmount);
                            requestDiscountApproval(requestedPercent, requestedAmount, base, discountedTotal, { source: 'item', itemId: selectedItemForDiscount.id });
                          }}
                          disabled={isRequestingApproval}
                          style={{
                            marginTop: '6px', padding: '6px 16px', fontSize: '12px', fontWeight: 600,
                            background: '#3b82f6', color: 'white', border: 'none', borderRadius: '6px',
                            cursor: isRequestingApproval ? 'not-allowed' : 'pointer', opacity: isRequestingApproval ? 0.6 : 1
                          }}
                        >
                          {isRequestingApproval ? 'Gönderiliyor...' : 'Onay İste'}
                        </button>
                      )}
                      {discountApprovalStatus === 'pending' && (
                        <p style={{ color: '#3b82f6', fontSize: '12px', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span style={{ display: 'inline-block', width: '10px', height: '10px', borderRadius: '50%', border: '2px solid #3b82f6', borderTopColor: 'transparent', animation: 'spin 1s linear infinite' }} />
                          Onay bekleniyor...
                        </p>
                      )}
                    </div>
                  );
                })()}

                {/* Hızlı Tutar Butonları */}
                {itemDiscountType === 'amount' && (
                  <div className="round-suggestions">
                    <span className="round-suggestions-label">Hızlı İndirim:</span>
                    <div className="round-suggestion-buttons">
                      {[100, 200, 500, 1000, 2000, 5000].map((amount, idx) => (
                        <button
                          key={idx}
                          type="button"
                          className={`round-suggestion-btn ${itemDiscountAmount === amount ? 'active' : ''}`}
                          onClick={() => setItemDiscountAmount(amount)}
                        >
                          ₺{amount.toLocaleString('tr-TR')}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Hızlı Yüzde Butonları */}
                {itemDiscountType === 'percent' && (
                  <div className="round-suggestions">
                    <span className="round-suggestions-label">Hızlı İndirim:</span>
                    <div className="round-suggestion-buttons">
                      {[3, 5, 7, 10].map((percent, idx) => (
                        <button
                          key={idx}
                          type="button"
                          className={`round-suggestion-btn ${itemDiscountAmount === percent ? 'active' : ''}`}
                          onClick={() => setItemDiscountAmount(percent)}
                        >
                          %{percent}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="discount-summary">
                <div className="discount-summary-row">
                  <span>Ürün Toplamı</span>
                  <span className="discount-summary-value">₺{formatPrice(convertToTRY(selectedItemForDiscount.price, selectedItemForDiscount.currency) * selectedItemForDiscount.quantity)}</span>
                </div>
                {itemDiscountAmount > 0 && (
                  <div className="discount-summary-row discount-row">
                    <span>İndirim</span>
                    <span className="discount-summary-value discount-value">
                      {(() => {
                        const total = convertToTRY(selectedItemForDiscount.price, selectedItemForDiscount.currency) * selectedItemForDiscount.quantity;
                        let discAmount;
                        if (itemDiscountType === 'percent') {
                          discAmount = total * itemDiscountAmount / 100;
                        } else if (itemDiscountType === 'round') {
                          discAmount = Math.max(0, total - itemDiscountAmount);
                        } else {
                          discAmount = Math.min(total, itemDiscountAmount);
                        }
                        const pct = total > 0 ? parseFloat((discAmount / total * 100).toFixed(2)) : 0;
                        return <>-₺{formatPrice(discAmount)} <span style={{ fontSize: '0.85em', opacity: 0.85 }}>(%{pct})</span></>;
                      })()}
                    </span>
                  </div>
                )}
                <div className="discount-summary-row total-row">
                  <span>Yeni Toplam</span>
                  <span className="discount-summary-value total-value">
                    ₺{(() => {
                      const total = convertToTRY(selectedItemForDiscount.price, selectedItemForDiscount.currency) * selectedItemForDiscount.quantity;
                      if (itemDiscountType === 'percent') {
                        return formatPrice(total * (1 - itemDiscountAmount / 100));
                      } else if (itemDiscountType === 'round') {
                        return formatPrice(Math.max(0, itemDiscountAmount));
                      } else {
                        return formatPrice(Math.max(0, total - itemDiscountAmount));
                      }
                    })()}
                  </span>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={removeItemDiscount}>İndirimi Kaldır</button>
              <button className="btn-save" onClick={applyItemDiscount}>Uygula</button>
            </div>
          </div>
        </div>
      )}

      {/* Ürün Notu Modal */}
      {showItemNoteModal && selectedItemForNote && (
        <div className="modal-overlay">
          <div className="modal discount-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Ürün Notu</h2>
              <button className="close-btn" onClick={() => { setShowItemNoteModal(false); setSelectedItemForNote(null); }}>✕</button>
            </div>
            <div className="modal-body">
              <div className="item-discount-product-info">
                <span className="item-discount-product-name">{selectedItemForNote.name}</span>
              </div>
              <div className="discount-input-section">
                <label className="discount-input-label">Not</label>
                <textarea
                  className="item-note-textarea"
                  value={itemNoteText}
                  onChange={e => setItemNoteText(e.target.value)}
                  placeholder="Ürün hakkında not yazın..."
                  rows={4}
                  autoFocus
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={removeItemNote}>Notu Kaldır</button>
              <button className="btn-save" onClick={saveItemNote}>Kaydet</button>
            </div>
          </div>
        </div>
      )}

      {/* Hızlı Fiyat Değiştirme Modal (Toplama çift tıklama) */}
      {showQuickPriceModal && (
        <div className="modal-overlay">
          <div className="modal discount-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Hedef Fiyat Belirle</h2>
              <button className="close-btn" onClick={() => setShowQuickPriceModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="discount-input-section">
                <label className="discount-input-label">Hedef Toplam Fiyat</label>
                <div className="discount-input-wrapper">
                  <span className="discount-input-prefix">₺</span>
                  <PriceInput
                    className="discount-input"
                    value={quickTargetPrice}
                    onChange={val => setQuickTargetPrice(val)}
                    placeholder=""
                    autoFocus
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        if (user?.role !== 'admin' && selectedStaff.length === 0) {
                          toast.error('İndirim uygulayabilmek için önce kasiyer seçmelisiniz!', { autoClose: 6000 });
                          return;
                        }
                        const targetPrice = Number(quickTargetPrice) || 0;
                        if (targetPrice > 0 && targetPrice < total) {
                          let additionalDiscount = total - targetPrice;
                          // Limit kontrolü
                          if (user?.role !== 'admin' && maxDiscountLimit !== null && discountBase > 0) {
                            const effectiveLimit = approvedDiscountOverride || maxDiscountLimit;
                            const existingPercent = getExistingManualDiscountPercent();
                            const additionalPercent = (additionalDiscount / discountBase) * 100;
                            if (existingPercent + additionalPercent > effectiveLimit) {
                              if (approvedDiscountOverride) {
                                setApprovedDiscountOverride(null);
                                setDiscountApprovalId(null);
                                setDiscountApprovalStatus(null);
                              }
                              const remainingPercent = Math.max(0, maxDiscountLimit - existingPercent);
                              additionalDiscount = Math.round(discountBase * remainingPercent / 100 * 100) / 100;
                              toast.warning(`Maksimum indirim limiti %${effectiveLimit}'dir. Limit üzeri indirim için onay isteyiniz.`, { autoClose: 6000, toastId: 'discount-limit' });
                              if (additionalDiscount <= 0) return;
                            }
                          }
                          const finalTarget = Math.round((total - additionalDiscount) * 100) / 100;
                          setAppliedDiscounts(prev => [...prev, { id: Date.now(), type: 'amount', value: additionalDiscount, label: `Yuvarla → ₺${Number(finalTarget).toLocaleString('tr-TR')}` }]);
                          setShowQuickPriceModal(false);
                        }
                      }
                    }}
                  />
                </div>
              </div>

              <div className="discount-summary">
                <div className="discount-summary-row">
                  <span>Mevcut Toplam</span>
                  <span className="discount-summary-value">₺{formatPrice(total)}</span>
                </div>
                {Number(quickTargetPrice) > 0 && Number(quickTargetPrice) < total && (
                  <>
                    <div className="discount-summary-row discount-row">
                      <span>Eklenecek İndirim</span>
                      <span className="discount-summary-value discount-value">-₺{formatPrice(total - Number(quickTargetPrice))}</span>
                    </div>
                    <div className="discount-summary-row total-row">
                      <span>Yeni Toplam</span>
                      <span className="discount-summary-value total-value">₺{formatPrice(Number(quickTargetPrice))}</span>
                    </div>
                  </>
                )}
                {user?.role !== 'admin' && approvedDiscountOverride && selectedStaff.length > 0 && (
                  <div className="discount-summary-row" style={{ color: '#22c55e', fontSize: '12px', marginTop: '4px' }}>
                    <span>İndirim onaylandı: %{Math.round(approvedDiscountOverride * 100) / 100}</span>
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowQuickPriceModal(false)}>İptal</button>
              <button
                className="btn-save"
                disabled={!quickTargetPrice || Number(quickTargetPrice) <= 0 || Number(quickTargetPrice) >= total}
                onClick={() => {
                  if (user?.role !== 'admin' && selectedStaff.length === 0) {
                    toast.error('İndirim uygulayabilmek için önce kasiyer seçmelisiniz!', { autoClose: 6000 });
                    return;
                  }
                  const targetPrice = Number(quickTargetPrice) || 0;
                  if (targetPrice > 0 && targetPrice < total) {
                    let additionalDiscount = total - targetPrice;
                    // Limit kontrolü
                    if (user?.role !== 'admin' && maxDiscountLimit !== null && discountBase > 0) {
                      const effectiveLimit = approvedDiscountOverride || maxDiscountLimit;
                      const existingPercent = getExistingManualDiscountPercent();
                      const additionalPercent = (additionalDiscount / discountBase) * 100;
                      if (existingPercent + additionalPercent > effectiveLimit) {
                        if (approvedDiscountOverride) {
                          setApprovedDiscountOverride(null);
                          setDiscountApprovalId(null);
                          setDiscountApprovalStatus(null);
                        }
                        const remainingPercent = Math.max(0, maxDiscountLimit - existingPercent);
                        additionalDiscount = Math.round(discountBase * remainingPercent / 100 * 100) / 100;
                        toast.warning(`Maksimum indirim limiti %${effectiveLimit}'dir. Limit üzeri indirim için onay isteyiniz.`, { autoClose: 6000, toastId: 'discount-limit' });
                        if (additionalDiscount <= 0) return;
                      }
                    }
                    const finalTarget = Math.round((total - additionalDiscount) * 100) / 100;
                    setAppliedDiscounts(prev => [...prev, { id: Date.now(), type: 'amount', value: additionalDiscount, label: `Yuvarla → ₺${Number(finalTarget).toLocaleString('tr-TR')}` }]);
                    setShowQuickPriceModal(false);
                  }
                }}
              >
                Uygula
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Fark modalı kaldırıldı - Kişiye Özel ürün olarak sepete ekleniyor */}

      {/* Bekleyenler Modal */}
      {showHoldModal && (
        <div className="modal-overlay">
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Bekleyen Satışlar</h2>
              <button className="close-btn" onClick={() => setShowHoldModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              {/* Terminal Sepetleri */}
              {terminalCarts.length > 0 && (
                <>
                  <h3 style={{ marginBottom: '10px', color: '#0ea5e9', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Smartphone size={16} /> El Terminalinden Gelen ({terminalCarts.length})
                  </h3>
                  <div className="held-list" style={{ marginBottom: '20px' }}>
                    {terminalCarts.map(terminalCart => {
                      const parsedItems = typeof terminalCart.items === 'string' ? JSON.parse(terminalCart.items) : terminalCart.items;
                      const items = parsedItems.products || parsedItems;
                      const staffName = parsedItems.sales_staff_name;
                      const discountAmt = parsedItems.discount_amount || 0;
                      const campaignName = parsedItems.campaign_name;
                      const displayTotal = Number(terminalCart.total_amount) || (Array.isArray(items) ? items.reduce((sum, item) => sum + ((parseFloat(item.sell_price) || parseFloat(item.price) || 0) * (item.quantity || 1)), 0) : 0);
                      return (
                        <div key={`terminal-${terminalCart.id}`} className="held-item terminal-cart" onClick={() => recallTerminalCart(terminalCart)} style={{ borderLeft: discountAmt > 0 ? '4px solid #f59e0b' : '4px solid #0ea5e9' }}>
                          <div className="held-info">
                            <span className="held-time" style={{ color: '#0ea5e9' }}>📱 {terminalCart.terminal_user_name}</span>
                            {staffName && <span className="held-customer" style={{ color: '#00bfa6' }}>👤 {staffName}</span>}
                            {discountAmt > 0 && <span className="held-customer" style={{ color: '#f59e0b' }}>🏷️ {campaignName || 'İndirim'}: -₺{discountAmt.toFixed(2)}</span>}
                            <span className="held-customer">{new Date(terminalCart.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}</span>
                            <span className="held-count">{terminalCart.item_count} ürün</span>
                          </div>
                          <span className="held-total">
                            ₺{displayTotal.toFixed(2)}
                          </span>
                          <button className="held-delete-btn" onClick={(e) => deleteTerminalCart(e, terminalCart)} title="Sil"><Trash2 size={14} /></button>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}

              {/* Bekletilen Satışlar */}
              {heldSales.length > 0 && (
                <>
                  <h3 style={{ marginBottom: '10px', color: '#6b7280', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Pause size={16} /> Bekletilen Satışlar ({heldSales.length})
                  </h3>
                  <div className="held-list">
                    {heldSales.map(sale => (
                      <div key={sale.id} className="held-item" onClick={() => recallSale(sale)}>
                        <div className="held-info">
                          <span className="held-time">{sale.time}</span>
                          <span className="held-customer">{sale.customer?.name || 'Müşteri Yok'}</span>
                          <span className="held-count">{sale.cart.length} ürün</span>
                        </div>
                        <span className="held-total">
                          ₺{(() => {
                            const itemsTotal = sale.cart.filter(i => !i.isRefund).reduce((sum, item) => sum + (item.price * item.quantity), 0);
                            const itemDiscounts = sale.cart.filter(i => !i.isRefund).reduce((sum, item) => sum + (item.customItemDiscount || 0), 0);
                            const base = itemsTotal - itemDiscounts;
                            const nonRoundDiscounts = (sale.appliedDiscounts || []).reduce((sum, d) => {
                              if (d.type === 'round') return sum;
                              if (d.type === 'percent') return sum + (base * d.value / 100);
                              return sum + d.value;
                            }, 0);
                            const refundTotal = sale.cart.filter(i => i.isRefund).reduce((sum, item) => sum + Math.abs(item.price * item.quantity), 0);
                            const heldTotalBeforeRound = base - nonRoundDiscounts - refundTotal;
                            const roundDiscounts = (sale.appliedDiscounts || []).reduce((sum, d) => {
                              if (d.type !== 'round') return sum;
                              return sum + (d.value > 0 ? Math.max(0, heldTotalBeforeRound - d.value) : 0);
                            }, 0);
                            return (heldTotalBeforeRound - roundDiscounts).toFixed(2);
                          })()}
                        </span>
                        <button className="held-delete-btn" onClick={(e) => deleteHeldSale(e, sale)} title="Sil"><Trash2 size={14} /></button>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* Hiçbir şey yoksa */}
              {heldSales.length === 0 && terminalCarts.length === 0 && (
                <p className="empty-text">Bekleyen satış yok</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Son Satışlar Modal */}
      {showLastSaleModal && (
        <div className={`modal-overlay ${closingModal === 'lastSale' ? 'closing' : ''}`}>
          <div className="modal large" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                {salesModalMode === 'refund' ? 'İade Yapılacak Satışı Seçin' :
                 salesModalMode === 'exchange' ? 'Değişim Yapılacak Satışı Seçin' :
                 salesModalMode === 'refund_exchange' ? 'İade / Değişim' :
                 'Fişler'}
              </h2>
              <div className="modal-header-actions">
                {salesModalMode === 'view' && (
                  <button
                    className="all-sales-btn"
                    onClick={() => {
                      setShowAllSalesModal(true);
                      setAllSales([]);
                      setAllSalesOffset(0);
                      setHasMoreAllSales(true);
                      setAllSalesFilter('all');
                      fetchAllSales(false);
                    }}
                  >
                    <FileText size={16} />
                    Tüm Fişler
                  </button>
                )}
                {(salesModalMode === 'refund' || salesModalMode === 'exchange' || salesModalMode === 'refund_exchange') && (
                  <button
                    className={`all-sales-btn ${showCrossStoreSearch ? 'active' : ''}`}
                    onClick={() => {
                      if (showCrossStoreSearch) {
                        setShowCrossStoreSearch(false);
                        setCrossStoreReceiptId('');
                        setCrossStoreSale(null);
                        setCrossStoreError('');
                      } else {
                        setShowCrossStoreSearch(true);
                      }
                    }}
                    style={showCrossStoreSearch ? { background: '#ef4444', color: '#fff', borderColor: '#ef4444' } : {}}
                  >
                    <Store size={16} />
                    {showCrossStoreSearch ? 'Kapat' : 'Başka Mağazadan'}
                  </button>
                )}
                <button className="close-btn" onClick={() => handleCloseModal('lastSale', setShowLastSaleModal, () => { setRecentSalesSearch(''); setShowSalesFilters(false); setRecentSalesFilters({ startDate: '', endDate: '', paymentMethod: '', minAmount: '', maxAmount: '', receiptType: '' }); setShowCrossStoreSearch(false); setCrossStoreReceiptId(''); setCrossStoreSale(null); setCrossStoreError(''); })}>✕</button>
              </div>
            </div>
            {/* Başka Mağazadan Fiş Arama Paneli */}
            {showCrossStoreSearch && (
              <div style={{ padding: '12px 16px', background: '#fef3c7', borderBottom: '1px solid #fde68a', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Store size={16} style={{ color: '#92400e', flexShrink: 0 }} />
                  <span style={{ fontSize: '13px', fontWeight: 600, color: '#92400e' }}>Başka Mağazadan Fiş Ara</span>
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <input
                    type="text"
                    placeholder="Fiş numarası girin..."
                    value={crossStoreReceiptId}
                    onChange={(e) => { setCrossStoreReceiptId(e.target.value); setCrossStoreError(''); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') searchCrossStoreSale(); }}
                    style={{ flex: 1, padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', outline: 'none' }}
                  />
                  <button
                    onClick={searchCrossStoreSale}
                    disabled={crossStoreLoading || !crossStoreReceiptId.trim()}
                    style={{ padding: '8px 16px', background: '#f59e0b', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', opacity: (crossStoreLoading || !crossStoreReceiptId.trim()) ? 0.5 : 1 }}
                  >
                    {crossStoreLoading ? 'Aranıyor...' : 'Ara'}
                  </button>
                </div>
                {crossStoreError && (
                  <div style={{ color: '#dc2626', fontSize: '13px', fontWeight: 500 }}>{crossStoreError}</div>
                )}
                {crossStoreSale && (
                  <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '12px 14px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' }}>
                      <div style={{ flex: 1, minWidth: '200px' }}>
                        <div style={{ fontWeight: 700, fontSize: '15px', color: '#1e293b', marginBottom: '4px' }}>
                          Fiş #{crossStoreSale.receipt_number || crossStoreSale.store_sale_number || crossStoreSale.id}
                        </div>
                        <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '2px' }}>
                          {new Date(crossStoreSale.created_at).toLocaleDateString('tr-TR')} {new Date(crossStoreSale.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                        </div>
                        {crossStoreSale.customer_name && (
                          <div style={{ fontSize: '13px', color: '#475569' }}>
                            <User size={12} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                            {crossStoreSale.customer_name}
                          </div>
                        )}
                        <div style={{ fontSize: '13px', color: '#475569', marginTop: '2px' }}>
                          <Store size={12} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                          {stores.find(s => s.id === crossStoreSale.store_id)?.name || `Mağaza #${crossStoreSale.store_id}`}
                        </div>
                        <div style={{ fontSize: '15px', fontWeight: 700, color: '#059669', marginTop: '4px' }}>
                          {Number(crossStoreSale.total).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0 }}>
                        {(salesModalMode === 'refund' || salesModalMode === 'refund_exchange') && canRefund() && (
                          <button
                            className="refund-btn"
                            onClick={() => openRefundModal(crossStoreSale)}
                            style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '8px 14px', fontSize: '13px' }}
                          >
                            <Undo2 size={14} /> İade Al
                          </button>
                        )}
                        {(salesModalMode === 'exchange' || salesModalMode === 'refund_exchange') && (
                          <button
                            className="exchange-btn"
                            onClick={() => openExchangeModal(crossStoreSale)}
                            style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '8px 14px', fontSize: '13px' }}
                          >
                            <RefreshCcw size={14} /> Değişim
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
            <div className="sales-search-bar">
              <input
                type="text"
                placeholder="Fiş no, müşteri adı veya tutar ile ara..."
                value={recentSalesSearch}
                onChange={(e) => setRecentSalesSearch(e.target.value)}
                autoFocus
              />
              {recentSalesSearch && (
                <button className="clear-search-btn" onClick={() => setRecentSalesSearch('')}>✕</button>
              )}
              <button
                className={`filter-toggle-btn ${showSalesFilters ? 'active' : ''}`}
                onClick={() => setShowSalesFilters(!showSalesFilters)}
                title="Filtreler"
              >
                <Filter size={18} />
                {(recentSalesFilters.startDate || recentSalesFilters.endDate || recentSalesFilters.paymentMethod || recentSalesFilters.minAmount || recentSalesFilters.maxAmount || recentSalesFilters.exactAmount || recentSalesFilters.receiptType) && (
                  <span className="filter-badge">!</span>
                )}
              </button>
            </div>
            {/* Filtre Alanları */}
            {showSalesFilters && (
              <div className="sales-filters">
                <div className="filter-row">
                  <div className="filter-group">
                    <label>Başlangıç Tarihi</label>
                    <input
                      type="date"
                      value={recentSalesFilters.startDate}
                      onChange={(e) => setRecentSalesFilters({...recentSalesFilters, startDate: e.target.value})}
                    />
                  </div>
                  <div className="filter-group">
                    <label>Bitiş Tarihi</label>
                    <input
                      type="date"
                      value={recentSalesFilters.endDate}
                      onChange={(e) => setRecentSalesFilters({...recentSalesFilters, endDate: e.target.value})}
                    />
                  </div>
                  <div className="filter-group">
                    <label>Fiş Türü</label>
                    <select
                      value={recentSalesFilters.receiptType}
                      onChange={(e) => setRecentSalesFilters({...recentSalesFilters, receiptType: e.target.value})}
                    >
                      <option value="">Tümü</option>
                      <option value="sale">Satış</option>
                      <option value="refund">İade</option>
                      <option value="exchange">Değişim</option>
                      <option value="debt_payment">Tahsilat</option>
                      <option value="scrap_purchase">Hurda Alımı</option>
                      <option value="repair">Tamirat Fişi</option>
                    </select>
                  </div>
                  <div className="filter-group">
                    <label>Ödeme Yöntemi</label>
                    <select
                      value={recentSalesFilters.paymentMethod}
                      onChange={(e) => setRecentSalesFilters({...recentSalesFilters, paymentMethod: e.target.value})}
                    >
                      <option value="">Tümü</option>
                      <option value="cash">Nakit</option>
                      <option value="card">Kredi Kartı</option>
                      <option value="transfer">Havale/EFT</option>
                      <option value="debt">Açık Hesap</option>
                      <option value="mixed">Karışık</option>
                    </select>
                  </div>
                  <div className="filter-group">
                    <label>Min Tutar (₺)</label>
                    <PriceInput
                      placeholder=""
                      hideText
                      value={recentSalesFilters.minAmount}
                      onChange={(val) => setRecentSalesFilters({...recentSalesFilters, minAmount: val})}
                    />
                  </div>
                  <div className="filter-group">
                    <label>Max Tutar (₺)</label>
                    <PriceInput
                      placeholder="∞"
                      hideText
                      value={recentSalesFilters.maxAmount}
                      onChange={(val) => setRecentSalesFilters({...recentSalesFilters, maxAmount: val})}
                    />
                  </div>
                  <div className="filter-group">
                    <label>Tutar (₺)</label>
                    <PriceInput
                      placeholder="Tam tutar"
                      hideText
                      value={recentSalesFilters.exactAmount}
                      onChange={(val) => setRecentSalesFilters({...recentSalesFilters, exactAmount: val})}
                    />
                  </div>
                  <button
                    className="clear-filters-btn"
                    onClick={() => setRecentSalesFilters({ startDate: '', endDate: '', paymentMethod: '', minAmount: '', maxAmount: '', exactAmount: '', receiptType: '' })}
                  >
                    Temizle
                  </button>
                </div>
              </div>
            )}
            <div className="modal-body" ref={recentSalesListRef} onScroll={(e) => {
              const { scrollTop, scrollHeight, clientHeight } = e.target;
              if (scrollHeight - scrollTop - clientHeight < 200 && hasMoreRecentSales && !loadingMoreSales && !recentSalesSearch.trim()) {
                loadMoreRecentSales();
              }
            }}>
              {(!Array.isArray(recentSales) || recentSales.length === 0) ? (
                <p className="empty-text">Henüz satış kaydı yok</p>
              ) : (() => {
                // Mevcut mağaza ID'sini al
                const activeStoreId = user?.role === 'admin' ? (selectedStore?.id || user?.store_id) : user?.store_id;
                const isCurrentStoreGold = activeStoreId === 1;
                const searchTerm = recentSalesSearch.trim();
                const isSearchingByReceiptNumber = searchTerm && searchTerm.length >= 3; // En az 3 karakter girilirse fiş numarası araması

                // Çapraz mağaza araması: fiş numarası ile arama yapılırsa veya iade/değişim modunda
                const allowCrossStoreSearch = isSearchingByReceiptNumber;

                const filteredSales = recentSales.filter(sale => {
                  // Masraf kayıtlarını son satışlar panelinde gösterme
                  if (sale.record_type === 'expense') return false;
                  const isSaleFromGoldStore = sale.store_id === 1;
                  const isSaleFromCurrentStore = sale.store_id === activeStoreId;

                  // Fiş numarası ile arama yapılıyorsa, tüm mağazaları ara
                  if (isSearchingByReceiptNumber && !isSaleFromCurrentStore) {
                    // Fiş numarası eşleşiyor mu kontrol et
                    const receiptNum = String(sale.receipt_number || '');
                    const storeSaleNum = String(sale.store_sale_number || '');
                    const saleIdStr = String(sale.id);
                    const matchesSearch = receiptNum.includes(searchTerm) ||
                                          storeSaleNum.includes(searchTerm) ||
                                          saleIdStr.includes(searchTerm);
                    if (!matchesSearch) {
                      return false; // Fiş numarası eşleşmiyorsa gösterme
                    }
                    // Fiş numarası eşleşiyorsa devam et (göster)
                  } else if (!isSaleFromCurrentStore) {
                    // Normal modda sadece kendi mağazasının satışlarını göster
                    return false;
                  }

                  // Tarih filtresi
                  const saleDate = new Date(sale.created_at);
                  if (recentSalesFilters.startDate) {
                    const startDate = new Date(recentSalesFilters.startDate);
                    startDate.setHours(0, 0, 0, 0);
                    if (saleDate < startDate) return false;
                  }
                  if (recentSalesFilters.endDate) {
                    const endDate = new Date(recentSalesFilters.endDate);
                    endDate.setHours(23, 59, 59, 999);
                    if (saleDate > endDate) return false;
                  }

                  // Ödeme yöntemi filtresi
                  if (recentSalesFilters.paymentMethod) {
                    const method = (sale.payment_method || '').toLowerCase();
                    const filterMethod = recentSalesFilters.paymentMethod;
                    if (filterMethod === 'cash' && method !== 'cash' && method !== 'nakit') return false;
                    if (filterMethod === 'card' && method !== 'card' && method !== 'kredi kartı' && method !== 'credit_card') return false;
                    if (filterMethod === 'transfer' && method !== 'transfer' && method !== 'havale' && method !== 'eft') return false;
                    if (filterMethod === 'debt' && method !== 'debt' && method !== 'açık hesap' && method !== 'veresiye') return false;
                    if (filterMethod === 'mixed' && method !== 'mixed' && method !== 'karışık') return false;
                  }

                  // Fiş türü filtresi
                  if (recentSalesFilters.receiptType) {
                    const rt = recentSalesFilters.receiptType;
                    if (rt === 'sale' && sale.record_type !== 'sale') return false;
                    if (rt === 'refund' && sale.record_type !== 'refund' && sale.sale_type !== 'refund') return false;
                    if (rt === 'exchange' && sale.record_type !== 'exchange') return false;
                    if (rt === 'debt_payment' && sale.record_type !== 'debt_payment') return false;
                    if (rt === 'scrap_purchase' && sale.record_type !== 'scrap_purchase') return false;
                    if (rt === 'repair' && sale.record_type !== 'repair_payment' && sale.record_type !== 'repair_deposit') return false;
                  }

                  // Tutar filtresi
                  const saleTotal = parseFloat(sale.total) || 0;
                  if (recentSalesFilters.exactAmount) {
                    const exact = parseFloat(recentSalesFilters.exactAmount);
                    if (!isNaN(exact) && Math.abs(saleTotal - exact) > 0.009) return false;
                  }
                  if (recentSalesFilters.minAmount && saleTotal < parseFloat(recentSalesFilters.minAmount)) return false;
                  if (recentSalesFilters.maxAmount && saleTotal > parseFloat(recentSalesFilters.maxAmount)) return false;

                  // Arama filtrelemesi (fiş numarası araması zaten yukarıda kontrol edildi)
                  if (!searchTerm) return true;
                  const searchLower = turkishToLower(searchTerm);
                  const saleId = String(sale.id);
                  const receiptNumber = String(sale.receipt_number || '');
                  const storeSaleNumber = String(sale.store_sale_number || '');
                  const customerName = turkishToLower(sale.customer_name || '');
                  const total = String(Number(sale.total).toLocaleString('tr-TR', { maximumFractionDigits: 2 }));
                  const date = new Date(sale.created_at).toLocaleDateString('tr-TR');
                  const notes = turkishToLower(sale.notes || '');
                  return saleId.includes(searchLower) ||
                         receiptNumber.includes(searchLower) ||
                         storeSaleNumber.includes(searchLower) ||
                         customerName.includes(searchLower) ||
                         total.includes(searchLower) ||
                         date.includes(searchLower) ||
                         notes.includes(searchLower);
                });
                return filteredSales.length === 0 ? (
                  <div className="no-search-results">
                    {recentSalesSearch ? (
                      <p>"<span className="search-term">{recentSalesSearch}</span>" için sonuç bulunamadı</p>
                    ) : (
                      <p>Bu mağaza için satış kaydı bulunamadı</p>
                    )}
                  </div>
                ) : (
                <div className="sales-list">
                  {filteredSales.map(sale => {
                    const isFromOtherStore = sale.store_id !== activeStoreId;
                    const isExchangeRecord = sale.record_type === 'exchange' || sale.sale_type === 'exchange_sale';
                    // İade kaydı: exchanges tablosundan gelen (record_type='refund') VEYA sales tablosundan gelen (sale_type='refund' veya negatif toplam)
                    const isRefundRecord = sale.record_type === 'refund' || sale.sale_type === 'refund' || (sale.record_type === 'sale' && Number(sale.total) < 0);
                    const isDebtPaymentRecord = sale.record_type === 'debt_payment';
                    const isScrapPurchaseRecord = sale.record_type === 'scrap_purchase';
                    const isRepairRecord = sale.record_type === 'repair_payment' || sale.record_type === 'repair_deposit';
                    const isExchangeOrRefund = isExchangeRecord || isRefundRecord;
                    // İade fişi (sales tablosundan) mı yoksa exchange kaydı mı?
                    const isRefundSaleRecord = sale.sale_type === 'refund' || (sale.record_type === 'sale' && Number(sale.total) < 0);
                    const isExchangeTableRecord = sale.record_type === 'exchange' || sale.record_type === 'refund';
                    return (
                    <div key={`${sale.record_type}_${sale.id}`} className={`sale-row ${isFromOtherStore ? 'other-store-sale' : ''} ${isExchangeOrRefund ? 'exchange-refund-row' : ''} ${isDebtPaymentRecord ? 'debt-payment-row' : ''} ${isScrapPurchaseRecord ? 'scrap-purchase-row' : ''} ${isRepairRecord ? 'debt-payment-row' : ''}`} onClick={() => (isScrapPurchaseRecord || isRepairRecord) ? null : (isDebtPaymentRecord ? openDebtPaymentDetailModal(sale) : (isExchangeTableRecord ? openRefundDetailModal(sale) : openSaleDetailModal(sale)))} style={{ cursor: (isScrapPurchaseRecord || isRepairRecord) ? 'default' : 'pointer' }}>
                      <div className="sale-main-info">
                        <span className="sale-id">#{sale.store_scrap_number || sale.receipt_number || sale.store_sale_number || sale.id}</span>
                        {isScrapPurchaseRecord && (
                          <span style={{
                            backgroundColor: '#eab308',
                            color: 'white',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            fontSize: '10px',
                            marginLeft: '8px',
                            fontWeight: 'bold'
                          }}>
                            HURDA ALIMI
                          </span>
                        )}
                        {isDebtPaymentRecord && (
                          <span style={{
                            backgroundColor: '#059669',
                            color: 'white',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            fontSize: '10px',
                            marginLeft: '8px',
                            fontWeight: 'bold'
                          }}>
                            TAHSİLAT
                          </span>
                        )}
                        {isRepairRecord && (
                          <span style={{
                            backgroundColor: '#059669',
                            color: 'white',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            fontSize: '10px',
                            marginLeft: '8px',
                            fontWeight: 'bold'
                          }}>
                            TAMİRAT
                          </span>
                        )}
                        {isRefundRecord && (
                          <>
                            <span style={{
                              backgroundColor: '#ef4444',
                              color: 'white',
                              padding: '2px 8px',
                              borderRadius: '4px',
                              fontSize: '10px',
                              marginLeft: '8px',
                              fontWeight: 'bold'
                            }}>
                              İADE
                            </span>
                            {(sale.refund_of_sale_id || sale.original_sale_id) && (
                              <span style={{
                                backgroundColor: '#fef2f2',
                                color: '#dc2626',
                                padding: '2px 6px',
                                borderRadius: '4px',
                                fontSize: '10px',
                                marginLeft: '4px',
                                fontWeight: '500'
                              }}>
                                Fiş #{sale.original_receipt_number || sale.original_store_sale_number || sale.refund_of_sale_id || sale.original_sale_id}
                              </span>
                            )}
                          </>
                        )}
                        {isFromOtherStore && !isExchangeOrRefund && (
                          <span className="other-store-badge" style={{
                            backgroundColor: '#ff9800',
                            color: 'white',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            fontSize: '10px',
                            marginLeft: '8px'
                          }}>
                            Diğer Mağaza
                          </span>
                        )}
                        <span className="sale-date">
                          {new Date(sale.created_at).toLocaleDateString('tr-TR')} {new Date(sale.created_at).toLocaleTimeString('tr-TR')}
                        </span>
                        <span className="sale-customer">{sale.customer_name || 'Müşteri Yok'}</span>
                        {sale.staff_name && (
                          <span style={{
                            fontSize: '11px',
                            color: '#6366f1',
                            backgroundColor: '#eef2ff',
                            padding: '1px 6px',
                            borderRadius: '4px',
                            marginLeft: '4px',
                            fontWeight: '500'
                          }}>
                            {sale.staff_name}
                          </span>
                        )}
                        {sale.scrap_id && (
                          <span className="sale-scrap-badge" title={`${sale.scrap_metal_type === 'gold' ? 'Altın' : 'Gümüş'} ${sale.scrap_purity} Ayar - ${sale.scrap_pure_weight}gr`}>
                            ⚖️ Hurda
                          </span>
                        )}
                        {!isExchangeOrRefund && !isDebtPaymentRecord && sale.status !== 'refunded' && !sale.consumables_count && (
                          <span className="sale-no-consumables-warning">
                            <AlertTriangle size={14} /> Sarf giriniz
                          </span>
                        )}
                        {user?.role === 'admin' && sale.has_no_discount_item && !isExchangeOrRefund && !isDebtPaymentRecord && !isScrapPurchaseRecord && (
                          <span
                            title="İndirim yapılması kısıtlanmış ürün satışı yapıldı"
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              backgroundColor: '#fee2e2',
                              color: '#b91c1c',
                              border: '1px solid #ef4444',
                              padding: '2px 8px',
                              borderRadius: '4px',
                              fontSize: '11px',
                              fontWeight: 600,
                              marginLeft: '4px'
                            }}
                          >
                            <AlertTriangle size={12} /> İndirim kısıtlı ürün
                          </span>
                        )}
                      </div>
                      <div className="sale-right">
                        {isScrapPurchaseRecord ? (
                          <>
                            <span style={{
                              fontSize: '11px',
                              padding: '2px 6px',
                              borderRadius: '4px',
                              background: '#fefce8',
                              color: '#a16207',
                              fontWeight: '500',
                              marginRight: '8px'
                            }}>
                              {sale.metal_type === 'gold' ? '🥇 Altın' : '🥈 Gümüş'} {sale.purity} Ayar
                            </span>
                            <span style={{
                              fontSize: '11px',
                              padding: '2px 6px',
                              borderRadius: '4px',
                              background: '#fefce8',
                              color: '#a16207',
                              fontWeight: '500'
                            }}>
                              {parseFloat(sale.gross_weight || 0).toFixed(2)}gr
                              {sale.item_count > 1 ? ` (${sale.item_count} kalem)` : ''}
                            </span>
                            <span className="sale-amount" style={{ color: '#a16207' }}>
                              ₺{Number(sale.total).toLocaleString('tr-TR', { maximumFractionDigits: 2 })}
                            </span>
                            <span style={{ fontSize: '11px', color: '#64748b' }}>
                              {sale.payment_method || 'Nakit'}
                            </span>
                          </>
                        ) : isDebtPaymentRecord ? (
                          <>
                            {/* Borç tipi bilgisi */}
                            <span style={{
                              fontSize: '11px',
                              padding: '2px 6px',
                              borderRadius: '4px',
                              background: '#ecfdf5',
                              color: '#059669',
                              fontWeight: '500',
                              marginRight: '8px'
                            }}>
                              {(sale.debt_type || 'TL') === 'TL' ? '₺ TL' : sale.debt_type === 'GRAM' ? '⚖️ Altın' : '⚖️ Gümüş'}
                            </span>
                            <span className="sale-status" style={{ backgroundColor: '#d1fae5', color: '#059669' }}>
                              Tahsilat Tamamlandı
                            </span>
                            <span className="sale-amount" style={{ color: '#059669' }}>
                              {(sale.debt_type || 'TL') === 'TL' ? `₺${Number(sale.total).toLocaleString('tr-TR', { maximumFractionDigits: 2 })}` : `${Number(sale.total).toLocaleString('tr-TR', { maximumFractionDigits: 2 })} gr`}
                            </span>
                            <span style={{ fontSize: '11px', color: '#64748b' }}>
                              {sale.payment_method}
                            </span>
                          </>
                        ) : isRepairRecord ? (
                          <>
                            {/* Tamirat ödemesi / kapora — tahsilat ile birebir aynı tasarım */}
                            <span style={{
                              fontSize: '11px',
                              padding: '2px 6px',
                              borderRadius: '4px',
                              background: '#ecfdf5',
                              color: '#059669',
                              fontWeight: '500',
                              marginRight: '8px'
                            }}>
                              🔧 Tamirat{sale.repair_code ? ` #${sale.repair_code}` : ''}
                            </span>
                            <span className="sale-status" style={{ backgroundColor: '#d1fae5', color: '#059669' }}>
                              Tamirat Ödemesi
                            </span>
                            <span className="sale-amount" style={{ color: '#059669' }}>
                              ₺{Number(sale.total).toLocaleString('tr-TR', { maximumFractionDigits: 2 })}
                            </span>
                            <span style={{ fontSize: '11px', color: '#64748b' }}>
                              {sale.payment_method || 'Nakit'}
                            </span>
                          </>
                        ) : isExchangeOrRefund ? (
                          <>
                            {/* Ürün bilgileri */}
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginRight: '12px' }}>
                              {sale.return_items && sale.return_items.length > 0 && (
                                <span style={{
                                  fontSize: '11px',
                                  padding: '2px 6px',
                                  borderRadius: '4px',
                                  background: '#fef2f2',
                                  color: '#dc2626',
                                  fontWeight: '500'
                                }}>
                                  ↩ {sale.return_items.reduce((sum, item) => sum + item.quantity, 0)} ürün
                                </span>
                              )}
                              {sale.new_items && sale.new_items.length > 0 && (
                                <span style={{
                                  fontSize: '11px',
                                  padding: '2px 6px',
                                  borderRadius: '4px',
                                  background: '#f0fdf4',
                                  color: '#16a34a',
                                  fontWeight: '500'
                                }}>
                                  + {sale.new_items.reduce((sum, item) => sum + item.quantity, 0)} yeni
                                </span>
                              )}
                            </div>
                            <span className={`sale-status ${isRefundRecord ? 'refunded' : 'exchange'}`}>
                              {isRefundRecord ? 'İade Tamamlandı' : 'Değişim Tamamlandı'}
                            </span>
                            <span className="sale-amount" style={{ color: Number(sale.total) < 0 ? '#16a34a' : '#ef4444' }}>
                              {Number(sale.total) < 0 ? '-' : '+'}₺{Math.abs(Number(sale.total)).toLocaleString('tr-TR', { maximumFractionDigits: 2 })}
                            </span>
                            <span style={{ fontSize: '11px', color: '#64748b' }}>
                              {formatPaymentMethod(sale)}
                            </span>
                            {user?.role === 'admin' && (
                              <>
                                <button
                                  className="edit-sale-btn"
                                  onClick={(e) => { e.stopPropagation(); isExchangeTableRecord ? openExchangeEditModal(sale) : openAdminEditModal(sale); }}
                                  style={{
                                    padding: '4px 10px',
                                    fontSize: '12px',
                                    background: 'linear-gradient(135deg, #14b8a6, #0d9488)',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    color: 'white',
                                    fontWeight: '500',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px'
                                  }}
                                >
                                  <Pencil size={12} /> Düzenle
                                </button>
                                {!isRefundRecord && (
                                <button
                                  className="delete-sale-btn"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (isExchangeTableRecord) {
                                      deleteExchange(sale.exchange_id || sale.id);
                                    } else {
                                      setDeletingSale(sale);
                                      setShowDeleteConfirm(true);
                                    }
                                  }}
                                >
                                  <X size={12} /> Fiş Silme
                                </button>
                                )}
                              </>
                            )}
                          </>
                        ) : (
                          <>
                            <span className={`sale-status ${sale.status}`}>
                              {sale.status === 'refunded' ? 'İade Edildi' : sale.status === 'partial_refund' ? 'Kısmi İade' : 'Tamamlandı'}
                            </span>
                            <span className="sale-amount">₺{getSaleDisplayAmount(sale).toLocaleString('tr-TR', { maximumFractionDigits: 2 })}</span>
                            <span style={{ fontSize: '11px', color: '#64748b' }}>
                              {formatPaymentMethod(sale)}
                            </span>
                          </>
                        )}
                        {/* İade edilmiş satışlar için fiş yazdır + admin düzenle/sil butonları */}
                        {isScrapPurchaseRecord && (
                          <button
                            className="print-receipt-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowLastSaleModal(false);
                              navigate('/scrap-purchase', { state: { from: 'retail-sales', openScrapId: sale.scrap_purchase_id } });
                            }}
                            title="Hurda sayfasına git"
                          >
                            Fişe Git
                          </button>
                        )}
                        {isDebtPaymentRecord && (
                          <button
                            className="print-receipt-btn"
                            onClick={async (e) => {
                              e.stopPropagation();
                              setShowLastSaleModal(false);
                              setShowCollectionModal(true);
                              setShowCollectionHistory(true);
                              await fetchCollectionHistory();
                              // Detay modalını aç
                              setSelectedDebtPaymentDetail({ ...sale, total: sale.total || sale.amount });
                              setShowDebtPaymentDetailModal(true);
                            }}
                            title="Tahsilat fişine git"
                          >
                            Fişe Git
                          </button>
                        )}
                        {isRepairRecord && (
                          <button
                            className="print-receipt-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowLastSaleModal(false);
                              navigate('/repairs', { state: { from: 'retail-sales', openRepairId: sale.repair_id } });
                            }}
                            title="Tamirat fişine git"
                          >
                            Fişe Git
                          </button>
                        )}
                        {!isExchangeOrRefund && !isDebtPaymentRecord && !isScrapPurchaseRecord && !isRepairRecord && sale.status === 'refunded' && salesModalMode === 'view' && (
                          <>
                            <button
                              className="print-receipt-btn"
                              onClick={(e) => { e.stopPropagation(); reprintSaleReceipt(sale); }}
                              title="Satış fişi yazdır"
                            >
                              <Printer size={14} /> Fiş
                            </button>
                            <button
                              className="gift-receipt-btn"
                              onClick={(e) => { e.stopPropagation(); reprintGiftExchangeReceipt(sale); }}
                              title="Hediye / Değişim fişi yazdır"
                            >
                              <Gift size={14} /> Hediye Fişi
                            </button>
                            {sale.customer_id && (
                              <button
                                className="print-receipt-btn"
                                onClick={(e) => { e.stopPropagation(); printCustomerBalanceReport({ id: sale.customer_id, name: sale.customer_name, phone: sale.customer_phone }); }}
                                title="Müşteri bakiye fişi yazdır"
                              >
                                <Printer size={14} /> Bakiye Fişi
                              </button>
                            )}
                            {user?.role === 'admin' && (
                              <>
                                <button className="edit-sale-btn" onClick={(e) => { e.stopPropagation(); openAdminEditModal(sale); }}>
                                  <Pencil size={14} /> Düzenle
                                </button>
                                <button className="delete-sale-btn" onClick={(e) => { e.stopPropagation(); setDeletingSale(sale); setShowDeleteConfirm(true); }}>
                                  <X size={14} /> Fiş Silme
                                </button>
                              </>
                            )}
                          </>
                        )}
                        {!isExchangeOrRefund && !isDebtPaymentRecord && !isScrapPurchaseRecord && !isRepairRecord && sale.status !== 'refunded' && (
                          <>
                            {/* Sadece görüntüleme modunda tüm butonları göster */}
                            {salesModalMode === 'view' && (
                              <>
                                <button
                                  className="print-receipt-btn"
                                  onClick={(e) => { e.stopPropagation(); reprintSaleReceipt(sale); }}
                                  title="Satış fişi yazdır"
                                >
                                  <Printer size={14} /> Fiş
                                </button>
                                <button
                                  className={`consumable-btn ${sale.consumables_count > 0 ? 'has-consumables' : ''}`}
                                  onClick={(e) => { e.stopPropagation(); openConsumablesModal(sale, sale.consumables_count > 0); }}
                                >
                                  <Package size={14} /> {sale.consumables_count > 0 ? `Sarflar (${sale.consumables_count})` : 'Sarf Ekle'}
                                </button>
                                <button
                                  className="gift-receipt-btn"
                                  onClick={(e) => { e.stopPropagation(); reprintGiftExchangeReceipt(sale); }}
                                  title="Hediye / Değişim fişi yazdır"
                                >
                                  <Gift size={14} /> Hediye Fişi
                                </button>
                                {sale.customer_id && (
                                  <button
                                    className="print-receipt-btn"
                                    onClick={(e) => { e.stopPropagation(); printCustomerBalanceReport({ id: sale.customer_id, name: sale.customer_name, phone: sale.customer_phone }); }}
                                    title="Müşteri bakiye fişi yazdır"
                                  >
                                    <Printer size={14} /> Bakiye Fişi
                                  </button>
                                )}
                                {user?.role === 'admin' && (
                                  <button className="edit-sale-btn" onClick={(e) => { e.stopPropagation(); openAdminEditModal(sale); }}>
                                    <Pencil size={14} /> Düzenle
                                  </button>
                                )}
                                <button className="exchange-btn" onClick={(e) => { e.stopPropagation(); openExchangeModal(sale); }}>
                                  <RefreshCcw size={14} /> Değişim
                                </button>
                                {canRefund() && (
                                <button className="refund-btn" onClick={(e) => { e.stopPropagation(); openRefundModal(sale); }}>
                                  <Undo2 size={14} /> İade Al
                                </button>
                                )}
                                {user?.role === 'admin' && (
                                  <button className="delete-sale-btn" onClick={(e) => { e.stopPropagation(); setDeletingSale(sale); setShowDeleteConfirm(true); }}>
                                    <X size={14} /> Fiş Silme
                                  </button>
                                )}
                              </>
                            )}
                            {/* İade modunda sadece iade butonu */}
                            {salesModalMode === 'refund' && canRefund() && (
                              <button className="refund-btn" onClick={(e) => { e.stopPropagation(); openRefundModal(sale); }}>
                                <Undo2 size={14} /> İade Al
                              </button>
                            )}
                            {/* Değişim modunda sadece değişim butonu */}
                            {salesModalMode === 'exchange' && (
                              <button className="exchange-btn" onClick={(e) => { e.stopPropagation(); openExchangeModal(sale); }}>
                                <RefreshCcw size={14} /> Değişim Yap
                              </button>
                            )}
                            {/* İade/Değişim modunda her iki buton */}
                            {salesModalMode === 'refund_exchange' && (
                              <>
                                {canRefund() && (
                                <button className="refund-btn" onClick={(e) => { e.stopPropagation(); openRefundModal(sale); }}>
                                  <Undo2 size={14} /> İade
                                </button>
                                )}
                                <button className="exchange-btn" onClick={(e) => { e.stopPropagation(); openExchangeModal(sale); }}>
                                  <RefreshCcw size={14} /> Değişim
                                </button>
                              </>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  );})}
                  {!recentSalesSearch.trim() && (
                    <div ref={recentSalesSentinelRef} style={{ textAlign: 'center', padding: '16px', minHeight: '1px' }}>
                      {loadingMoreSales ? (
                        <span style={{ color: '#64748b', fontSize: '13px' }}>Yükleniyor...</span>
                      ) : hasMoreRecentSales ? (
                        <span style={{ color: '#94a3b8', fontSize: '12px' }}>Kaydırmaya devam edin...</span>
                      ) : null}
                    </div>
                  )}
                </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Satış Detay Modal */}
      {showSaleDetailModal && (
        <div className={`modal-overlay ${closingModal === 'saleDetail' ? 'closing' : ''}`}>
          <div className="modal sale-detail-modal" onClick={e => e.stopPropagation()}>
            {!selectedSaleDetail ? (
              <div style={{ padding: '40px', textAlign: 'center' }}>
                <div className="loading">Yükleniyor...</div>
              </div>
            ) : (
            <>
            <div className="modal-header">
              <h2 style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                <span>Fiş Detayı - #{selectedSaleDetail.receipt_number || selectedSaleDetail.store_sale_number || selectedSaleDetail.id}</span>
                {/* İade fişi badge */}
                {selectedSaleDetail.sale_type === 'refund' && (
                  <span style={{
                    backgroundColor: '#ef4444',
                    color: 'white',
                    padding: '4px 10px',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: 'bold'
                  }}>
                    İADE FİŞİ
                  </span>
                )}
                {/* Ana fiş referansı */}
                {(selectedSaleDetail.original_sale_id || selectedSaleDetail.refund_of_sale_id) && (
                  <span style={{
                    backgroundColor: '#f1f5f9',
                    color: '#475569',
                    padding: '4px 10px',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: '500',
                    border: '1px solid #e2e8f0'
                  }}>
                    Ana Fiş: #{selectedSaleDetail.original_receipt_number || selectedSaleDetail.original_store_sale_number || selectedSaleDetail.original_sale_id || selectedSaleDetail.refund_of_sale_id}
                  </span>
                )}
              </h2>
              <button className="close-btn" onClick={() => handleCloseModal('saleDetail', setShowSaleDetailModal, () => setSaleExchangeInfo(null))}>✕</button>
            </div>
            <div className="modal-body">
              {/* Fiş Bilgileri */}
              <div className="sale-detail-info">
                <div className="sale-detail-row">
                  <span className="sale-detail-label">Tarih</span>
                  <span className="sale-detail-value">
                    {new Date(selectedSaleDetail.created_at).toLocaleDateString('tr-TR')} {new Date(selectedSaleDetail.created_at).toLocaleTimeString('tr-TR')}
                  </span>
                </div>
                <div className="sale-detail-row">
                  <span className="sale-detail-label">Müşteri</span>
                  <span className="sale-detail-value">{selectedSaleDetail.customer_name || 'Müşteri Yok'}</span>
                </div>
                <div className="sale-detail-row">
                  <span className="sale-detail-label">Ödeme Yöntemi</span>
                  <span className="sale-detail-value">
                    {(() => {
                      // payment_details varsa detaylı göster
                      let details = selectedSaleDetail.payment_details;
                      let paymentList = [];

                      if (details && (Array.isArray(details) ? details.length > 0 : true)) {
                        paymentList = Array.isArray(details) ? details :
                          (typeof details === 'string' ? JSON.parse(details) : []);
                      }

                      // payment_details boşsa ama payment_method varsa, toplam tutarla bir detay oluştur
                      if (paymentList.length === 0 && selectedSaleDetail.payment_method) {
                        // Nakit İade durumu: hurda tutarı satış tutarını aştığında müşteriye iade yapılır
                        const saleTotal = parseFloat(selectedSaleDetail.total) || 0;
                        const scrapAmount = Number(selectedSaleDetail.scrap_total_amount || 0);
                        const saleSubtotal = Number(selectedSaleDetail.subtotal || 0);
                        const saleDiscountVal = Number(selectedSaleDetail.discount || 0);
                        const customerRefund = (selectedSaleDetail.payment_method === 'Nakit İade' && scrapAmount > 0)
                          ? Math.max(0, scrapAmount - (saleSubtotal - saleDiscountVal))
                          : 0;

                        if (customerRefund > 0) {
                          paymentList = [{
                            method: 'Müşteriye Nakit İade',
                            currency: 'TRY',
                            amount: customerRefund,
                            amount_tl: customerRefund
                          }];
                        } else if (saleTotal > 0) {
                          paymentList = [{
                            method: selectedSaleDetail.payment_method,
                            currency: 'TRY',
                            amount: saleTotal,
                            amount_tl: saleTotal
                          }];
                        }
                      }

                      if (paymentList.length > 0) {
                        return (
                          <div className="payment-details-list">
                            {paymentList.map((p, idx) => {
                              const currencySymbol = p.currency === 'USD' ? '$' :
                                p.currency === 'EUR' ? '€' :
                                p.currency === 'GOLD' ? 'gr altın' :
                                p.currency === 'SILVER' ? 'gr gümüş' : '₺';
                              const amount = p.currency === 'TRY' ?
                                `₺${Number(p.amount).toLocaleString('tr-TR', {minimumFractionDigits: 2})}` :
                                p.currency === 'GOLD' || p.currency === 'SILVER' ?
                                `${p.amount} ${currencySymbol}` :
                                `${currencySymbol}${Number(p.amount).toLocaleString('tr-TR', {minimumFractionDigits: 2})}`;
                              // Hatalı ödeme yöntemi isimlerini düzelt
                              let methodName = p.method || '';
                              methodName = methodName.replace('Açık Hesap ( L)', 'Açık Hesap (₺)');
                              methodName = methodName.replace('Açık Hesap (L)', 'Açık Hesap (₺)');
                              methodName = methodName.replace('Açık Hesap (TL)', 'Açık Hesap (₺)');
                              methodName = methodName.replace('Açık Hesap ( Gram)', 'Açık Hesap (Gram)');
                              methodName = methodName.replace('Açık Hesap ( Gümüş)', 'Açık Hesap (Gümüş)');
                              return (
                                <div key={idx} className="payment-detail-item">
                                  <span className="payment-method-tag">{methodName}</span>
                                  {p.method === 'Havale/EFT' && p.bank_account && (
                                    <span style={{ fontSize: '11px', color: '#0ea5e9', fontWeight: '500' }}>({stripBankAccountType(p.bank_account)})</span>
                                  )}
                                  <span className="payment-amount">{amount}</span>
                                  {p.currency !== 'TRY' && p.amount_tl && (
                                    <span className="payment-tl-equiv">(₺{Number(p.amount_tl).toLocaleString('tr-TR', {minimumFractionDigits: 2})})</span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        );
                      }

                      // Hiçbir şey yoksa boş göster
                      return '-';
                    })()}
                  </span>
                </div>
                <div className="sale-detail-row">
                  <span className="sale-detail-label">Satış Sorumlusu</span>
                  <span className="sale-detail-value">{selectedSaleDetail.staff_name || '-'}</span>
                </div>
                <div className="sale-detail-row">
                  <span className="sale-detail-label">Durum</span>
                  <span className={`sale-detail-status ${selectedSaleDetail.status}`}>
                    {selectedSaleDetail.status === 'refunded' ? 'İade Edildi' : selectedSaleDetail.status === 'partial_refund' ? 'Kısmi İade' : 'Tamamlandı'}
                  </span>
                </div>
                {/* Satış Anındaki Kur */}
                {(selectedSaleDetail.gold_rate || selectedSaleDetail.silver_rate) && (
                  <div className="sale-detail-rates">
                    <div className="sale-detail-row">
                      <span className="sale-detail-label">Satış Anındaki Kur</span>
                      <span className="sale-detail-value rates-value">
                        {selectedSaleDetail.gold_rate && (
                          <span className="rate-item gold">
                            <span className="rate-label">Altın Gram:</span>
                            <span className="rate-amount">₺{Number(selectedSaleDetail.gold_rate).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</span>
                          </span>
                        )}
                        {selectedSaleDetail.silver_rate && (
                          <span className="rate-item silver">
                            <span className="rate-label">Gümüş Gram:</span>
                            <span className="rate-amount">₺{Number(selectedSaleDetail.silver_rate).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</span>
                          </span>
                        )}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Değişim Bilgisi - Eğer bu satış bir değişimin parçasıysa */}
              {saleExchangeInfo && (
                <div className="exchange-info-section" style={{
                  background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                  border: '2px solid #f59e0b',
                  borderRadius: '12px',
                  padding: '16px',
                  marginBottom: '20px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                    <RefreshCcw size={20} color="#92400e" />
                    <h4 style={{ margin: 0, color: '#92400e' }}>DEĞİŞİM İŞLEMİ</h4>
                    <span style={{
                      background: '#f59e0b',
                      color: 'white',
                      padding: '2px 8px',
                      borderRadius: '4px',
                      fontSize: '11px',
                      fontWeight: 'bold'
                    }}>
                      Orijinal Fiş: #{saleExchangeInfo.original_sale_id}
                    </span>
                  </div>

                  {/* İade Edilen Ürünler */}
                  {saleExchangeInfo.return_items && saleExchangeInfo.return_items.length > 0 && (
                    <div style={{ marginBottom: '12px' }}>
                      <div style={{ fontSize: '13px', fontWeight: '600', color: '#dc2626', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Undo2 size={14} /> İade Edilen Ürünler:
                      </div>
                      {saleExchangeInfo.return_items.map((item, idx) => (
                        <div key={idx} style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          padding: '8px 12px',
                          background: '#fef2f2',
                          borderRadius: '6px',
                          marginBottom: '4px',
                          border: '1px solid #fecaca'
                        }}>
                          {item.image && (
                            <img
                              src={`${IMAGE_HOST}${item.image}`}
                              alt={item.product_name}
                              style={{
                                width: '40px',
                                height: '40px',
                                objectFit: 'cover',
                                borderRadius: '4px',
                                border: '1px solid #fecaca',
                                flexShrink: 0
                              }}
                            />
                          )}
                          <div style={{ flex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                              <span style={{ fontWeight: '500' }}>{item.product_name}</span>
                              {item.barcode && <span style={{ color: '#64748b', marginLeft: '6px', fontSize: '11px' }}>({item.barcode})</span>}
                              <span style={{ color: '#64748b', marginLeft: '8px', fontSize: '13px' }}>
                                {item.quantity} adet × ₺{Number(item.unit_price).toLocaleString('tr-TR')}
                              </span>
                            </div>
                            <span style={{ color: '#dc2626', fontWeight: 'bold' }}>
                              -₺{Number(item.total_price).toLocaleString('tr-TR')}
                            </span>
                          </div>
                        </div>
                      ))}
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        padding: '8px 12px',
                        background: '#fee2e2',
                        borderRadius: '6px',
                        fontWeight: 'bold',
                        marginTop: '8px'
                      }}>
                        <span style={{ color: '#dc2626' }}>İade Toplamı:</span>
                        <span style={{ color: '#dc2626' }}>-₺{Number(saleExchangeInfo.return_total).toLocaleString('tr-TR')}</span>
                      </div>
                    </div>
                  )}

                  {/* Özet */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '10px 12px',
                    background: '#fef9c3',
                    borderRadius: '6px',
                    fontWeight: 'bold',
                    fontSize: '14px',
                    border: '1px solid #facc15'
                  }}>
                    <span>Müşteriden Alınan Fark:</span>
                    <span style={{ color: saleExchangeInfo.difference >= 0 ? '#16a34a' : '#dc2626' }}>
                      ₺{Number(saleExchangeInfo.difference).toLocaleString('tr-TR')}
                    </span>
                  </div>
                </div>
              )}

              {/* Ürünler */}
              <div className="sale-detail-items">
                <h4>{saleExchangeInfo ? 'Yeni Alınan Ürünler' : 'Ürünler'}</h4>
                {(() => {
                  // Birim fiyatlar; yazdırılan fiş + Değişim/İade ekranıyla AYNI kaynaktan
                  // (computeSaleItemPricing) hesaplanır. Böylece fiş detayındaki fiyatlar
                  // değişimdeki fiyatlarla birebir tutar (vaka: fiş #022617101).
                  const itemsList = (selectedSaleDetail.items || []);
                  const detailAdjustments = Array.isArray(selectedSaleDetail.adjustment_items)
                    ? selectedSaleDetail.adjustment_items : [];
                  const { itemUnitPrice: detailItemUnitPrice } = computeSaleItemPricing(selectedSaleDetail, detailAdjustments);
                  return itemsList.map((item, idx) => {
                  const returnedQty = item.returned_quantity || 0;
                  const isFullyReturned = returnedQty >= item.quantity;
                  const isPartiallyReturned = returnedQty > 0 && returnedQty < item.quantity;

                  return (
                    <div
                      key={idx}
                      className="sale-detail-item"
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '12px',
                        ...(isFullyReturned ? {
                          backgroundColor: '#fef2f2',
                          border: '1px solid #fecaca',
                          borderRadius: '8px',
                          padding: '8px',
                          opacity: 0.8
                        } : isPartiallyReturned ? {
                          backgroundColor: '#fffbeb',
                          border: '1px solid #fde68a',
                          borderRadius: '8px',
                          padding: '8px'
                        } : {})
                      }}
                    >
                      {item.image && (
                        <img
                          src={`${IMAGE_HOST}${item.image}`}
                          alt={item.product_name}
                          onClick={(e) => { e.stopPropagation(); openImagePopup(item.image, item.product_name); }}
                          style={{
                            width: '50px',
                            height: '50px',
                            objectFit: 'cover',
                            borderRadius: '6px',
                            border: '1px solid #e2e8f0',
                            flexShrink: 0,
                            cursor: 'pointer',
                            ...(isFullyReturned ? { opacity: 0.5 } : {})
                          }}
                        />
                      )}
                      <div className="sale-detail-item-info" style={{ flex: 1 }}>
                        <span
                          className="sale-detail-item-name"
                          style={isFullyReturned ? { textDecoration: 'line-through', color: '#9ca3af' } : {}}
                        >
                          {item.product_name}
                        </span>
                        {item.barcode && <span className="sale-detail-item-barcode" style={{ fontSize: '11px', color: '#64748b', marginLeft: '8px' }}>({item.barcode})</span>}
                        <span className="sale-detail-item-qty">{Math.abs(item.quantity)} adet</span>

                        {/* Ürün notu - ürün adının hemen altında belirgin biçimde göster */}
                        {item.note && (
                          <div style={{
                            fontSize: '12px',
                            color: '#92400e',
                            marginTop: '6px',
                            padding: '6px 10px',
                            background: '#fef3c7',
                            borderRadius: '6px',
                            borderLeft: '3px solid #f59e0b',
                            fontWeight: '500'
                          }}>
                            📝 {item.note}
                          </div>
                        )}

                        {/* İade durumu bilgisi */}
                        {isFullyReturned && (
                          <div style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            fontSize: '11px',
                            color: '#dc2626',
                            fontWeight: 'bold',
                            marginTop: '4px',
                            padding: '4px 8px',
                            background: '#fee2e2',
                            borderRadius: '4px'
                          }}>
                            <span>↩</span>
                            <span>Tamamen iade edildi ({returnedQty} adet)</span>
                          </div>
                        )}
                        {isPartiallyReturned && (
                          <div style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            fontSize: '11px',
                            color: '#d97706',
                            fontWeight: '500',
                            marginTop: '4px',
                            padding: '4px 8px',
                            background: '#fef3c7',
                            borderRadius: '4px'
                          }}>
                            <span>↩</span>
                            <span>Kısmi iade: {returnedQty}/{item.quantity} adet iade edildi</span>
                          </div>
                        )}

                        {/* Stok bilgisi */}
                        {item.stock_before !== null && item.stock_before !== undefined && (
                          <div style={{
                            display: 'flex',
                            gap: '8px',
                            fontSize: '11px',
                            color: '#64748b',
                            marginTop: '4px',
                            padding: '4px 8px',
                            background: '#f1f5f9',
                            borderRadius: '4px'
                          }}>
                            <span>Önceki: <strong>{item.stock_before}</strong></span>
                            <span>•</span>
                            <span>Satılan: <strong style={{ color: '#ef4444' }}>-{item.quantity}</strong></span>
                            <span>•</span>
                            <span>Kalan: <strong style={{ color: '#16a34a' }}>{item.stock_before - item.quantity}</strong></span>
                          </div>
                        )}
                        {/* Hesaplama detayları */}
                        {item.rate_used && item.weight && (
                          <div className="sale-detail-item-calc">
                            <span className="calc-formula">
                              {item.metal_type === 'GOLD' ? (
                                <>
                                  {item.gold_karat && <span className="calc-karat">{item.gold_karat} Ayar</span>}
                                  <span className="calc-weight">{Number(item.weight).toFixed(2)} gr</span>
                                  <span className="calc-symbol">×</span>
                                  <span className="calc-rate">₺{Number(item.rate_used).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</span>
                                  {item.labor_cost > 0 && <span className="calc-labor">(+{item.labor_cost} işçilik)</span>}
                                </>
                              ) : item.metal_type === 'SILVER' ? (
                                <>
                                  <span className="calc-weight">{Number(item.weight).toFixed(2)} gr</span>
                                  <span className="calc-symbol">×</span>
                                  <span className="calc-rate">₺{Number(item.rate_used).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</span>
                                </>
                              ) : null}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="sale-detail-item-price" style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-end',
                        gap: '2px',
                        ...(isFullyReturned ? { opacity: 0.5 } : {})
                      }}>
                        {(() => {
                          // original_unit_price veritabanından geliyor (indirim öncesi birim fiyat)
                          const isRefundItem = Number(item.quantity) < 0 || selectedSaleDetail.sale_type === 'refund';
                          const absQty = Math.abs(Number(item.quantity || 1));
                          const originalUnitPrice = Number(item.original_unit_price || item.unit_price || item.price || 0);
                          const originalTotal = originalUnitPrice * absQty;
                          // Değişim ekranındaki birim fiyatla birebir aynı olsun diye ortak hesaplayıcıyı kullan
                          const finalTotal = isRefundItem
                            ? Math.abs(Number(item.total_price || 0))
                            : detailItemUnitPrice(item) * absQty;
                          const discountAmount = Number(item.discount_amount || 0);
                          const hasDiscount = discountAmount > 0 || (originalTotal > finalTotal && (originalTotal - finalTotal) >= 1);

                          const displayPrefix = isRefundItem ? '-' : '';
                          if (hasDiscount && !isFullyReturned) {
                            return (
                              <>
                                <span style={{
                                  textDecoration: 'line-through',
                                  color: '#9ca3af',
                                  fontSize: '0.85em'
                                }}>
                                  {displayPrefix}₺{originalTotal.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </span>
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                  <span style={{ color: isRefundItem ? '#dc2626' : '#16a34a', fontWeight: '600' }}>
                                    {displayPrefix}₺{finalTotal.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </span>
                                  {originalTotal > 0 && (
                                    <span style={{ fontSize: '0.75em', color: '#ef4444', fontWeight: '600' }}>
                                      %{((originalTotal - finalTotal) / originalTotal * 100).toFixed(0)}
                                    </span>
                                  )}
                                </span>
                              </>
                            );
                          }
                          return (
                            <span style={isFullyReturned ? { textDecoration: 'line-through', color: '#9ca3af' } : (isRefundItem ? { color: '#dc2626' } : {})}>
                              {displayPrefix}₺{finalTotal.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </span>
                          );
                        })()}
                      </div>
                    </div>
                  );
                  });
                })()}
                {/* Fark kalemleri ürünlerin altında */}
                {selectedSaleDetail.adjustment_items && selectedSaleDetail.adjustment_items.length > 0 && (
                  selectedSaleDetail.adjustment_items.map((adj, idx) => {
                    const originalAmount = Number(adj.amount || adj.refund_amount || adj.original_amount || 0);
                    const discountShare = Number(adj.discountShare || 0);
                    const netAmount = discountShare > 0 ? originalAmount - discountShare : originalAmount;
                    const hasDiscount = discountShare >= 0.01 && originalAmount > 0;
                    return (
                      <div key={`adj-${idx}`} className="sale-detail-item" style={{ alignItems: 'flex-start' }}>
                        <div className="sale-detail-item-info">
                          <span className="sale-detail-item-name">{adj.reason || 'Fark'}</span>
                          {adj.description && <span className="sale-detail-item-qty">{adj.description}</span>}
                        </div>
                        <div className="sale-detail-item-price" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }}>
                          {hasDiscount ? (
                            <>
                              <span style={{ textDecoration: 'line-through', color: '#9ca3af', fontSize: '0.85em' }}>
                                ₺{originalAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </span>
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                <span style={{ color: '#16a34a', fontWeight: '600' }}>
                                  ₺{netAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </span>
                                <span style={{ fontSize: '0.75em', color: '#ef4444', fontWeight: '600' }}>
                                  %{((discountShare / originalAmount) * 100).toFixed(0)}
                                </span>
                              </span>
                            </>
                          ) : (
                            <span>₺{originalAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              {/* Toplam - Perakende satıştaki gibi detaylı görünüm */}
              <div className="sale-detail-total">
                {/* Değişim durumunda detaylı hesaplama */}
                {saleExchangeInfo ? (
                  <>
                    <div className="sale-detail-total-row">
                      <span>Yeni Ürünler Toplamı</span>
                      <span>₺{Number(saleExchangeInfo.new_items_total).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                    <div className="sale-detail-total-row discount">
                      <span>İade Edilen Ürünler</span>
                      <span>-₺{Number(saleExchangeInfo.return_total).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                    {selectedSaleDetail.discount > 0 && (
                      <div className="sale-detail-total-row discount">
                        <span>İndirim</span>
                        <span>-₺{Number(selectedSaleDetail.discount).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      </div>
                    )}
                    <div className="sale-detail-total-row grand-total" style={{ background: '#dcfce7', color: '#16a34a' }}>
                      <span>Müşteriden Alınan</span>
                      <span>₺{Number(saleExchangeInfo.difference).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                  </>
                ) : (
                  <>
                    {/* Perakende satıştaki gibi detaylı fiyat özeti */}
                    {(() => {
                      const itemsDiscountedTotal = selectedSaleDetail.items?.reduce((sum, item) => sum + Number(item.total_price || 0), 0) || 0;
                      // Kalemlerden hesaplanan indirimsiz toplam (original_unit_price var ise en güvenilir kaynak;
                      // DB.subtotal sonradan kalem düzenlemesiyle stale kalabilir)
                      const itemsListTotal = (selectedSaleDetail.items || []).reduce((sum, item) => {
                        const listPrice = Number(item.original_unit_price) || Number(item.unit_price) || 0;
                        return sum + listPrice * Number(item.quantity || 1);
                      }, 0);
                      const saleSubtotal = Number(selectedSaleDetail.subtotal || 0);
                      const adjustmentTotal = selectedSaleDetail.adjustment_total ? Number(selectedSaleDetail.adjustment_total) : 0;
                      const dbDiscount = Number(selectedSaleDetail.discount || 0);
                      const scrapDebtDiscount = Number(selectedSaleDetail.scrap_debt_discount || 0);
                      const scrapAmount = Number(selectedSaleDetail.scrap_total_amount || 0);
                      const hasScrap = scrapAmount > 0;

                      // Ara toplam: kalemlerden hesapla; yoksa (Excel aktarımı vb.) DB subtotal'a düş
                      const itemsTotal = itemsListTotal > 0 ? itemsListTotal : (saleSubtotal > 0 ? saleSubtotal : itemsDiscountedTotal);

                      // İndirim: kalemlerdeki gerçek indirim (original - unit) DB.discount'tan büyükse onu kullan
                      // (stale aggregate durumunda kalemlerdeki indirim doğrudur)
                      const itemsDiscount = Math.max(0, itemsListTotal - itemsDiscountedTotal);
                      const discount = itemsListTotal > 0 ? Math.max(dbDiscount, itemsDiscount) : dbDiscount;

                      // Satış tutarı: kayıt anındaki DB.total kuruşlu ve doğru kaynaktır;
                      // varsa onu kullan (yuvarlama yapma). Yalnızca DB.total yoksa
                      // kalemlerden re-compute et (stale fallback).
                      // Not: Hurda alımı olan satışlarda DB.total, hurda mahsup edilmiş net
                      // ödenecek tutardır (yazdırılan fişteki TOPLAM formülüyle aynı şekilde
                      // hurda tutarı geri eklenerek gerçek satış tutarına ulaşılır).
                      const dbSaleTotal = Number(selectedSaleDetail.total || 0);
                      const computedSaleTotal = itemsTotal + adjustmentTotal - discount - scrapDebtDiscount;
                      const realSaleTotal = hasScrap
                        ? (dbSaleTotal + scrapAmount)
                        : (dbSaleTotal > 0 ? dbSaleTotal : computedSaleTotal);

                      return (
                        <>
                          {/* Ara Toplam (Ürünler + Fark dahil) */}
                          <div className="sale-detail-row" style={{ background: 'linear-gradient(135deg, #14b8a6, #0d9488)', borderRadius: '8px', padding: '12px', margin: '0 -12px 8px' }}>
                            <span style={{ color: 'white', fontWeight: '600' }}>Ara Toplam</span>
                            <span style={{ color: 'white', fontWeight: '700', fontSize: '1rem' }}>₺{(itemsTotal + adjustmentTotal).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                          </div>

                          {/* İndirim */}
                          {discount > 0 && (
                            <div className="sale-detail-row">
                              <span className="sale-detail-label" style={{ color: 'white', display: 'flex', alignItems: 'center', gap: '6px' }}><Tag size={14} color="white" /> İndirim</span>
                              <span className="sale-detail-value" style={{ color: 'white', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                                <span>-₺{discount.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                {(itemsTotal + adjustmentTotal) > 0 && (
                                  <span style={{ fontSize: '0.8em', background: 'rgba(255,255,255,0.25)', padding: '2px 8px', borderRadius: '10px', fontWeight: '600' }}>
                                    %{(discount / (itemsTotal + adjustmentTotal) * 100).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </span>
                                )}
                              </span>
                            </div>
                          )}

                          {/* Hurda Kartı İndirimi */}
                          {scrapDebtDiscount > 0 && (
                            <div className="sale-detail-row">
                              <span className="sale-detail-label" style={{ color: 'white', display: 'flex', alignItems: 'center', gap: '6px' }}><Tag size={14} color="white" /> Hurda Kartı İnd. {selectedSaleDetail.scrap_debt_codes && <small>({selectedSaleDetail.scrap_debt_codes})</small>}</span>
                              <span className="sale-detail-value" style={{ color: 'white' }}>-₺{scrapDebtDiscount.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            </div>
                          )}

                          {/* Hurda Mahsubu (Alınan Hurda) */}
                          {hasScrap && (
                            <div className="sale-detail-row" style={{ flexDirection: 'column', alignItems: 'stretch', gap: '8px', background: 'linear-gradient(135deg, #f59e0b, #d97706)', borderRadius: '8px', padding: '12px', margin: '0 -12px 8px' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ color: 'white', fontWeight: '600' }}>⚖️ Hurda Alımı</span>
                                <span style={{ color: 'white', fontWeight: '700', fontSize: '1rem' }}>-₺{scrapAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                              </div>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', fontSize: '11px' }}>
                                <span style={{ background: 'rgba(255,255,255,0.25)', color: 'white', padding: '3px 10px', borderRadius: '12px', fontWeight: '500' }}>
                                  {selectedSaleDetail.scrap_metal_type === 'gold' ? '🥇 Altın' : '🥈 Gümüş'}
                                </span>
                                <span style={{ background: 'rgba(255,255,255,0.25)', color: 'white', padding: '3px 10px', borderRadius: '12px', fontWeight: '500' }}>
                                  {selectedSaleDetail.scrap_purity} Ayar
                                </span>
                                <span style={{ background: 'rgba(255,255,255,0.25)', color: 'white', padding: '3px 10px', borderRadius: '12px', fontWeight: '500' }}>
                                  Brüt: {Number(selectedSaleDetail.scrap_gross_weight || 0).toFixed(2)} gr
                                </span>
                                {(() => {
                                  const grossW = Number(selectedSaleDetail.scrap_gross_weight || 0);
                                  const wasteG = Number(selectedSaleDetail.scrap_waste_gram || 0);
                                  const wasteP = Number(selectedSaleDetail.scrap_waste_percent || 0);
                                  const fire = wasteG > 0 ? wasteG : (wasteP > 0 ? grossW * wasteP / 100 : 0);
                                  const netW = grossW - fire;
                                  return (
                                    <>
                                      <span style={{ background: 'rgba(255,255,255,0.25)', color: 'white', padding: '3px 10px', borderRadius: '12px', fontWeight: '500' }}>
                                        Fire: {fire.toFixed(2)} gr
                                      </span>
                                      <span style={{ background: 'rgba(255,255,255,0.25)', color: 'white', padding: '3px 10px', borderRadius: '12px', fontWeight: '500' }}>
                                        Net: {netW.toFixed(2)} gr
                                      </span>
                                      <span style={{ background: 'rgba(255,255,255,0.25)', color: 'white', padding: '3px 10px', borderRadius: '12px', fontWeight: '500' }}>
                                        Has: {Number(selectedSaleDetail.scrap_pure_weight || 0).toFixed(2)} gr
                                      </span>
                                    </>
                                  );
                                })()}
                                <span style={{ background: 'rgba(255,255,255,0.25)', color: 'white', padding: '3px 10px', borderRadius: '12px', fontWeight: '500' }}>
                                  ₺{Number(selectedSaleDetail.scrap_unit_price || 0).toLocaleString('tr-TR')}/gr
                                </span>
                              </div>
                            </div>
                          )}

                          {/* TOPLAM (Satış Tutarı) */}
                          <div className="sale-detail-total-row grand-total">
                            <span>SATIŞ TUTARI</span>
                            <span>₺{realSaleTotal.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                          </div>

                          {/* Ödeme Özeti (hurda varsa) */}
                          {hasScrap && (
                            <div className="sale-detail-info" style={{ marginTop: '12px', padding: '12px', borderRadius: '8px' }}>
                              <div className="sale-detail-row">
                                <span className="sale-detail-label"><Scale size={14} style={{ marginRight: '4px', verticalAlign: 'middle' }} /> Hurda ile ödenen</span>
                                <span className="sale-detail-value">₺{scrapAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                              </div>
                              {scrapAmount > realSaleTotal ? (
                                <div className="sale-detail-row" style={{ background: 'linear-gradient(135deg, #22c55e, #16a34a)', borderRadius: '8px', padding: '10px', margin: '4px -12px 0' }}>
                                  <span style={{ color: 'white', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}><Banknote size={16} /> Müşteriye Nakit İade</span>
                                  <span style={{ color: 'white', fontWeight: '700', fontSize: '1.1rem' }}>₺{(scrapAmount - realSaleTotal).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                </div>
                              ) : (
                                <div className="sale-detail-row">
                                  <span className="sale-detail-label" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    {(selectedSaleDetail.payment_method || '').includes('Kredi') || (selectedSaleDetail.payment_method || '').includes('Kart')
                                      ? <><CreditCard size={14} /> Kredi Kartı ile ödenen</>
                                      : (selectedSaleDetail.payment_method || '').includes('Havale') || (selectedSaleDetail.payment_method || '').includes('EFT')
                                        ? <><Building2 size={14} /> Havale/EFT ile ödenen</>
                                        : <><Banknote size={14} /> Nakit ile ödenen</>}
                                  </span>
                                  <span className="sale-detail-value">₺{Number(selectedSaleDetail.total || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                </div>
                              )}
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </>
                )}
              </div>
            </div>
            <div className="modal-footer" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ position: 'relative' }}>
                <button
                  className="btn-primary"
                  onClick={() => setSaleDetailActionsOpen(prev => !prev)}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px', borderRadius: '8px', background: '#0f766e', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' }}
                >
                  <ClipboardList size={15} /> İşlemler <ChevronDown size={14} />
                </button>
                {saleDetailActionsOpen && selectedSaleDetail && (
                  <div style={{
                    position: 'absolute', bottom: '100%', left: 0, marginBottom: '6px',
                    background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px',
                    boxShadow: '0 10px 40px rgba(0,0,0,0.15)', padding: '6px', minWidth: '200px', zIndex: 10
                  }}>
                    {!mobileApp && selectedSaleDetail.status !== 'refunded' && (
                      <>
                        <button onClick={() => { setSaleDetailActionsOpen(false); setShowSaleDetailModal(false); setSaleExchangeInfo(null); openExchangeModal(selectedSaleDetail); }} style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', padding: '10px 12px', border: 'none', background: 'none', cursor: 'pointer', borderRadius: '6px', fontSize: '0.85rem', color: '#1e293b', fontWeight: 500, textAlign: 'left' }} onMouseEnter={e => e.currentTarget.style.background='#f1f5f9'} onMouseLeave={e => e.currentTarget.style.background='none'}>
                          <RefreshCcw size={15} color="#f59e0b" /> Değişim
                        </button>
                        {canRefund() && (
                          <button onClick={() => { setSaleDetailActionsOpen(false); setShowSaleDetailModal(false); setSaleExchangeInfo(null); openRefundModal(selectedSaleDetail); }} style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', padding: '10px 12px', border: 'none', background: 'none', cursor: 'pointer', borderRadius: '6px', fontSize: '0.85rem', color: '#1e293b', fontWeight: 500, textAlign: 'left' }} onMouseEnter={e => e.currentTarget.style.background='#f1f5f9'} onMouseLeave={e => e.currentTarget.style.background='none'}>
                            <Undo2 size={15} color="#dc2626" /> İade Al
                          </button>
                        )}
                      </>
                    )}
                    {!mobileApp && (<>
                    <button onClick={() => { setSaleDetailActionsOpen(false); reprintSaleReceipt(selectedSaleDetail); }} style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', padding: '10px 12px', border: 'none', background: 'none', cursor: 'pointer', borderRadius: '6px', fontSize: '0.85rem', color: '#1e293b', fontWeight: 500, textAlign: 'left' }} onMouseEnter={e => e.currentTarget.style.background='#f1f5f9'} onMouseLeave={e => e.currentTarget.style.background='none'}>
                      <Printer size={15} color="#2563eb" /> Fiş Yazdır
                    </button>
                    <button onClick={() => { setSaleDetailActionsOpen(false); reprintGiftExchangeReceipt(selectedSaleDetail); }} style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', padding: '10px 12px', border: 'none', background: 'none', cursor: 'pointer', borderRadius: '6px', fontSize: '0.85rem', color: '#1e293b', fontWeight: 500, textAlign: 'left' }} onMouseEnter={e => e.currentTarget.style.background='#f1f5f9'} onMouseLeave={e => e.currentTarget.style.background='none'}>
                      <Gift size={15} color="#8b5cf6" /> Hediye Fişi
                    </button>
                    </>)}
                    {user?.role === 'admin' && (
                      <>
                        <div style={{ height: '1px', background: '#e2e8f0', margin: '4px 0' }} />
                        <button onClick={() => { setSaleDetailActionsOpen(false); setShowSaleDetailModal(false); setSaleExchangeInfo(null); openAdminEditModal(selectedSaleDetail); }} style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', padding: '10px 12px', border: 'none', background: 'none', cursor: 'pointer', borderRadius: '6px', fontSize: '0.85rem', color: '#1e293b', fontWeight: 500, textAlign: 'left' }} onMouseEnter={e => e.currentTarget.style.background='#f1f5f9'} onMouseLeave={e => e.currentTarget.style.background='none'}>
                          <Pencil size={15} color="#0d9488" /> Fiş Düzenle
                        </button>
                        <button onClick={() => { setSaleDetailActionsOpen(false); setShowSaleDetailModal(false); setSaleExchangeInfo(null); setDeletingSale(selectedSaleDetail); setShowDeleteConfirm(true); }} style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', padding: '10px 12px', border: 'none', background: 'none', cursor: 'pointer', borderRadius: '6px', fontSize: '0.85rem', color: '#dc2626', fontWeight: 500, textAlign: 'left' }} onMouseEnter={e => e.currentTarget.style.background='#fef2f2'} onMouseLeave={e => e.currentTarget.style.background='none'}>
                          <Trash2 size={15} /> Fiş Sil
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
              <button className="btn-cancel" onClick={() => { setShowSaleDetailModal(false); setSaleExchangeInfo(null); setSaleDetailActionsOpen(false); }}>Kapat</button>
            </div>
            </>
            )}
          </div>
        </div>
      )}

      {/* İade Modal */}
      {showRefundModal && selectedSale && (
        <div className={`modal-overlay ${closingModal === 'refund' ? 'closing' : ''}`}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ width: '900px', maxWidth: '95vw', maxHeight: '90vh' }}>
            {/* Header - Kompakt */}
            <div className="modal-header" style={{ padding: '12px 20px', borderBottom: '2px solid #ef4444' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '20px', flex: 1 }}>
                <h2 style={{ margin: 0, fontSize: '1.1rem', color: '#dc2626' }}>↩ İade - Fiş #{selectedSale.receipt_number || selectedSale.store_sale_number || selectedSale.id}</h2>
                <div style={{ display: 'flex', gap: '16px', fontSize: '0.85rem', color: '#ffffff' }}>
                  <span><strong>Müşteri:</strong> {selectedSale.customer_name || 'Müşteri Yok'}</span>
                  <span><strong>Tarih:</strong> {new Date(selectedSale.created_at).toLocaleDateString('tr-TR')}</span>
                  <span><strong>Toplam:</strong> ₺{(Number(selectedSale.total || 0)).toLocaleString('tr-TR')}</span>
                </div>
              </div>
              <button className="close-btn" onClick={() => handleCloseModal('refund', setShowRefundModal, () => { setSelectedCustomer(preRefundCustomer); })}>✕</button>
            </div>

            {/* Body */}
            <div className="modal-body" style={{ padding: '16px', maxHeight: 'calc(90vh - 130px)', overflow: 'auto' }}>
              {/* Üst Kısım - Ürünler */}
              <div style={{ marginBottom: '16px' }}>
                <h4 style={{ margin: '0 0 10px 0', fontSize: '0.9rem', color: '#374151' }}>İade Edilecek Ürünler</h4>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {refundItems.length > 0 ? refundItems.map(item => {
                    const isFullyRefunded = (item.availableQty || 0) <= 0;
                    return (
                      <div key={item.id} style={{
                        display: 'flex', alignItems: 'center', gap: '10px',
                        padding: '10px 12px', borderRadius: '8px', minWidth: '280px', flex: '1 1 calc(50% - 4px)',
                        backgroundColor: isFullyRefunded ? '#fee2e2' : '#f8fafc',
                        border: isFullyRefunded ? '1px solid #fecaca' : '1px solid #e2e8f0',
                        opacity: isFullyRefunded ? 0.6 : 1
                      }}>
                        {item.image ? (
                          <img src={`${IMAGE_HOST}${item.image}`} alt="" onClick={(e) => { e.stopPropagation(); openImagePopup(item.image, item.product_name || item.name); }} style={{ width: '56px', height: '56px', objectFit: 'cover', borderRadius: '8px', flexShrink: 0, border: '1px solid #e2e8f0', cursor: 'pointer' }} />
                        ) : (
                          <div style={{ width: '56px', height: '56px', borderRadius: '8px', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, border: '1px solid #e2e8f0', color: '#94a3b8', fontSize: '20px' }}>📦</div>
                        )}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: '500', fontSize: '0.9rem', textDecoration: isFullyRefunded ? 'line-through' : 'none' }}>
                            {item.product_name} {item.barcode && <span style={{ color: '#64748b', fontSize: '11px', fontWeight: 'normal' }}>({item.barcode})</span>}
                          </div>
                          <div style={{ fontSize: '0.8rem', color: '#64748b' }}>
                            {isFullyRefunded ? '✓ Tamamen iade edildi' : (
                              <>
                                {item.availableQty} adet x {(() => {
                                  const listPrice = Number(item.original_unit_price || item.unit_price);
                                  const effPrice = Number(item.effective_unit_price || item.unit_price);
                                  return listPrice - effPrice > 0.01 ? (
                                    <>
                                      <span style={{ textDecoration: 'line-through', color: '#94a3b8' }}>₺{listPrice.toLocaleString('tr-TR', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                                      {' '}
                                      <span style={{ color: '#16a34a', fontWeight: '500' }}>₺{effPrice.toLocaleString('tr-TR', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                                      {listPrice > 0 && (
                                        <span style={{ fontSize: '0.85em', color: '#ef4444', fontWeight: '600', marginLeft: '3px' }}>
                                          %{((listPrice - effPrice) / listPrice * 100).toFixed(0)}
                                        </span>
                                      )}
                                    </>
                                  ) : (
                                    <>₺{effPrice.toLocaleString('tr-TR', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</>
                                  );
                                })()}
                              </>
                            )}
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontSize: '0.8rem', color: '#64748b' }}>Adet:</span>
                          <input
                            type="number" min="0" max={item.availableQty || 0} value={item.refundQty}
                            onChange={(e) => updateRefundQty(item.id, parseInt(e.target.value) || 0)}
                            disabled={isFullyRefunded}
                            style={{ width: '55px', padding: '6px 8px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.9rem', textAlign: 'center', backgroundColor: isFullyRefunded ? '#e5e7eb' : 'white' }}
                          />
                        </div>
                      </div>
                    );
                  }) : <p style={{ color: '#64748b', padding: '20px', width: '100%', textAlign: 'center' }}>Ürün yok</p>}
                </div>

                {/* Fark Kalemleri */}
                {refundAdjustments.length > 0 && (
                  <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px dashed #e2e8f0' }}>
                    <h4 style={{ margin: '0 0 8px 0', fontSize: '0.85rem', color: '#16a34a' }}>Fark Kalemleri</h4>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      {refundAdjustments.map((adj, idx) => {
                        const isAlreadyRefunded = adj.isRefunded || adj.is_refunded;
                        return (
                          <div key={idx} onClick={() => {
                            if (isAlreadyRefunded) return;
                            const updated = [...refundAdjustments];
                            updated[idx] = { ...updated[idx], refundAmount: adj.refundAmount > 0 ? 0 : (adj.effective_amount || adj.amount) };
                            setRefundAdjustments(updated);
                          }} style={{
                            display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', borderRadius: '6px',
                            cursor: isAlreadyRefunded ? 'not-allowed' : 'pointer',
                            backgroundColor: isAlreadyRefunded ? '#fee2e2' : adj.refundAmount > 0 ? '#dcfce7' : '#f0fdf4',
                            border: isAlreadyRefunded ? '1px solid #fecaca' : adj.refundAmount > 0 ? '2px solid #16a34a' : '1px solid #86efac',
                            opacity: isAlreadyRefunded ? 0.6 : 1
                          }}>
                            <div>
                              <div style={{ fontWeight: '500', fontSize: '0.85rem', color: isAlreadyRefunded ? '#dc2626' : '#16a34a', textDecoration: isAlreadyRefunded ? 'line-through' : 'none' }}>
                                {adj.reason || adj.description || 'Fark'}
                              </div>
                              <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                                {isAlreadyRefunded ? 'Daha önce iade edildi' : `+₺${Number(adj.effective_amount || adj.amount).toLocaleString('tr-TR', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`}
                              </div>
                            </div>
                            <input type="checkbox" checked={isAlreadyRefunded ? true : adj.refundAmount > 0} readOnly disabled={isAlreadyRefunded} style={{ width: '18px', height: '18px' }} />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* Alt Kısım - Tam İkiye Bölünmüş */}
              <div style={{ display: 'flex', gap: '0', borderTop: '2px solid #e2e8f0' }}>
                {/* Sol Yarı - İade Yöntemi */}
                <div style={{ flex: 1, padding: '16px', borderRight: '2px solid #e2e8f0', backgroundColor: useCustomRefundPayments ? '#f0fdf4' : '#eff6ff' }}>
                  <h4 style={{ margin: '0 0 12px 0', fontSize: '1rem', color: useCustomRefundPayments ? '#166534' : '#1e40af', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    {useCustomRefundPayments ? <><Wallet size={18} /> Özel İade Yöntemi</> : <><CreditCard size={18} /> Orijinal Ödeme Yöntemi</>}
                  </h4>

                  {useCustomRefundPayments ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {[{ key: 'nakit', Icon: Banknote, label: 'Nakit' }, { key: 'krediKarti', Icon: CreditCard, label: 'Kredi Kartı' }, { key: 'havale', Icon: Building2, label: 'Havale/EFT' }, { key: 'hediyeKarti', Icon: BookOpen, label: 'Açık Hesap' }, { key: 'hurda', Icon: Gem, label: 'Hurda İade' }].map(p => (
                        <div key={p.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', backgroundColor: '#dcfce7', borderRadius: '8px', border: '1px solid #86efac' }}>
                          <span style={{ fontWeight: '600', color: '#166534', fontSize: '0.95rem', display: 'flex', alignItems: 'center', gap: '6px' }}><p.Icon size={18} /> {p.label}</span>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <button type="button" onClick={() => { const t = refundItems.reduce((s, i) => s + (i.refundQty * Number(i.effective_unit_price || i.unit_price)), 0) + refundAdjustments.reduce((s, a) => s + (a.refundAmount || 0), 0); const others = Object.entries(customRefundPayments).filter(([k]) => k !== p.key).reduce((s, [, v]) => s + (parseFloat(v) || 0), 0); const remaining = Math.max(0, t - others); setCustomRefundPayments(prev => ({ ...prev, [p.key]: remaining.toFixed(2) })); }} style={{ padding: '6px 12px', fontSize: '0.8rem', backgroundColor: '#22c55e', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: '500' }}>Tümü</button>
                            <span style={{ color: '#166534', fontWeight: '500' }}>₺</span>
                            <PriceInput placeholder="" value={customRefundPayments[p.key]} onChange={(val) => setCustomRefundPayments(prev => ({ ...prev, [p.key]: val }))} style={{ width: '120px', padding: '8px 12px', border: '2px solid #86efac', borderRadius: '6px', fontSize: '1rem', textAlign: 'right', fontWeight: '500' }} />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : refundOriginalPayments.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {refundOriginalPayments.map((payment, idx) => {
                        const IconComponent = payment.method?.toLowerCase().includes('nakit') ? Banknote : payment.method?.toLowerCase().includes('kart') ? CreditCard : payment.method?.toLowerCase().includes('havale') ? Building2 : payment.method?.toLowerCase().includes('hurda') ? Gem : Wallet;
                        // Açık Hesap (Gram/Gümüş) için TL karşılığını kullan
                        const isDebtGramPayment = payment.method?.includes('Açık Hesap (Gram)') || payment.method?.includes('Açık Hesap (Gümüş)');
                        const paymentAmountTL = isDebtGramPayment ? Number(payment.amount_tl || payment.amountTL || 0) : Number(payment.amount);
                        const maxAmount = Math.max(0, paymentAmountTL - (previousRefunds.reduce((sum, r) => { try { const n = r.notes ? JSON.parse(r.notes) : null; return sum + (n?.payment_details?.filter(pd => pd.method === payment.method).reduce((s, pd) => s + (Number(pd.amount_tl || pd.amountTL || pd.amount) || 0), 0) || 0); } catch { return sum; } }, 0)));
                        const isFullyRefunded = maxAmount <= 0;
                        return (
                          <div key={idx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', backgroundColor: isFullyRefunded ? '#f3f4f6' : '#dbeafe', borderRadius: '8px', border: isFullyRefunded ? '1px solid #d1d5db' : '1px solid #93c5fd', opacity: isFullyRefunded ? 0.6 : 1 }}>
                            <div>
                              <div style={{ fontWeight: '600', fontSize: '0.95rem', color: isFullyRefunded ? '#9ca3af' : '#1e3a8a', display: 'flex', alignItems: 'center', gap: '6px' }}><IconComponent size={18} /> {payment.method}</div>
                              <div style={{ fontSize: '0.8rem', color: isFullyRefunded ? '#9ca3af' : '#3b82f6', marginTop: '2px' }}>Max: ₺{maxAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span style={{ color: '#1e3a8a', fontWeight: '500' }}>₺</span>
                              <PriceInput placeholder="" value={refundAmounts[idx] || ''} onChange={(val) => setRefundAmounts(prev => ({ ...prev, [idx]: val }))} disabled={isFullyRefunded} style={{ width: '120px', padding: '8px 12px', border: '2px solid #93c5fd', borderRadius: '6px', fontSize: '1rem', textAlign: 'right', fontWeight: '500', backgroundColor: isFullyRefunded ? '#e5e7eb' : 'white' }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div style={{ padding: '30px', textAlign: 'center', color: '#64748b', fontSize: '0.9rem', backgroundColor: '#f8fafc', borderRadius: '8px' }}>Ödeme bilgisi yok</div>
                  )}
                </div>

                {/* Sağ Yarı - İade Özeti */}
                <div style={{ flex: 1, padding: '16px', backgroundColor: '#fef2f2' }}>
                  <h4 style={{ margin: '0 0 16px 0', fontSize: '1rem', color: '#dc2626' }}>📋 İade Özeti</h4>

                  <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '16px', border: '1px solid #fecaca' }}>
                    {/* Ürün Toplamı */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.95rem', color: '#374151', marginBottom: '10px', paddingBottom: '10px', borderBottom: '1px solid #fee2e2' }}>
                      <span>Ürün İadesi ({refundItems.filter(i => i.refundQty > 0).length} ürün):</span>
                      <span style={{ fontWeight: '600' }}>₺{refundItems.reduce((s, i) => s + (i.refundQty * Number(i.effective_unit_price || i.unit_price)), 0).toLocaleString('tr-TR')}</span>
                    </div>

                    {/* Fark Toplamı */}
                    {refundAdjustments.some(a => a.refundAmount > 0) && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.95rem', color: '#374151', marginBottom: '10px', paddingBottom: '10px', borderBottom: '1px solid #fee2e2' }}>
                        <span>Fark İadesi:</span>
                        <span style={{ fontWeight: '600' }}>₺{refundAdjustments.reduce((s, a) => s + (a.refundAmount || 0), 0).toLocaleString('tr-TR')}</span>
                      </div>
                    )}

                    {/* İade Edilecek */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '1rem', color: '#64748b', marginBottom: '8px' }}>
                      <span>İade Edilecek Tutar:</span>
                      <span style={{ fontWeight: '600', color: '#374151' }}>₺{(refundItems.reduce((s, i) => s + (i.refundQty * Number(i.effective_unit_price || i.unit_price)), 0) + refundAdjustments.reduce((s, a) => s + (a.refundAmount || 0), 0)).toLocaleString('tr-TR')}</span>
                    </div>

                    {/* Kalan Tutar */}
                    {(() => {
                      const iadeEdilecek = refundItems.reduce((s, i) => s + (i.refundQty * Number(i.effective_unit_price || i.unit_price)), 0) + refundAdjustments.reduce((s, a) => s + (a.refundAmount || 0), 0);
                      const girilenTutar = useCustomRefundPayments
                        ? (parseFloat(customRefundPayments.nakit) || 0) + (parseFloat(customRefundPayments.krediKarti) || 0) + (parseFloat(customRefundPayments.havale) || 0) + (parseFloat(customRefundPayments.hediyeKarti) || 0) + (parseFloat(customRefundPayments.hurda) || 0)
                        : Object.values(refundAmounts).reduce((s, v) => s + (parseFloat(v) || 0), 0);
                      const kalanTutar = iadeEdilecek - girilenTutar;

                      if (kalanTutar > 0) {
                        return (
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '1rem', color: '#64748b', marginBottom: '8px' }}>
                            <span>Kalan Tutar:</span>
                            <span style={{ fontWeight: '600', color: '#374151' }}>₺{kalanTutar.toLocaleString('tr-TR')}</span>
                          </div>
                        );
                      }
                      return null;
                    })()}

                    {/* Toplam İade */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '1.3rem', fontWeight: 'bold', color: '#dc2626', padding: '12px', backgroundColor: '#fee2e2', borderRadius: '6px', marginTop: '12px' }}>
                      <span>TOPLAM İADE:</span>
                      <span>₺{(useCustomRefundPayments ? (parseFloat(customRefundPayments.nakit) || 0) + (parseFloat(customRefundPayments.krediKarti) || 0) + (parseFloat(customRefundPayments.havale) || 0) + (parseFloat(customRefundPayments.hediyeKarti) || 0) + (parseFloat(customRefundPayments.hurda) || 0) : Object.values(refundAmounts).reduce((s, v) => s + (parseFloat(v) || 0), 0)).toLocaleString('tr-TR')}</span>
                    </div>
                  </div>

                  {/* İade Yap Butonu */}
                  <button onClick={processRefund} style={{ width: '100%', marginTop: '16px', padding: '14px', backgroundColor: '#dc2626', color: 'white', border: 'none', borderRadius: '8px', fontSize: '1.1rem', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', boxShadow: '0 2px 8px rgba(220, 38, 38, 0.3)' }}>
                    ↩ İADE YAP
                  </button>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="modal-footer" style={{ padding: '10px 20px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <button className="btn-cancel" onClick={() => { setShowRefundModal(false); setSelectedCustomer(preRefundCustomer); setUseCustomRefundPayments(false); setCustomRefundPayments({ nakit: '', krediKarti: '', havale: '', hediyeKarti: '', hurda: '' }); }}>İptal</button>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', color: '#92400e', fontWeight: '500', fontSize: '0.85rem', padding: '6px 12px', backgroundColor: '#fef3c7', borderRadius: '6px', border: '1px solid #f59e0b' }}>
                <input type="checkbox" checked={useCustomRefundPayments} onChange={(e) => { setUseCustomRefundPayments(e.target.checked); if (!e.target.checked) setCustomRefundPayments({ nakit: '', krediKarti: '', havale: '', hediyeKarti: '', hurda: '' }); }} style={{ width: '16px', height: '16px' }} />
                Farklı ödeme yöntemiyle iade et
              </label>
            </div>
          </div>
        </div>
      )}

      {/* İade/Değişim Detay Modal */}
      {showRefundDetailModal && selectedRefundDetail && (
        <div className={`modal-overlay ${closingModal === 'refundDetail' ? 'closing' : ''}`}>
          <div className="modal refund-detail-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                {selectedRefundDetail.record_type === 'refund' ? 'İade Fişi' : 'İade Fişi (Değişim)'} - #{selectedRefundDetail.exchange_id || selectedRefundDetail.id}
              </h2>
              <button className="close-btn" onClick={() => handleCloseModal('refundDetail', setShowRefundDetailModal)}>✕</button>
            </div>
            <div className="modal-body">

              <div className="refund-detail-info">
                <div className="refund-detail-row">
                  <span className="label">Tarih:</span>
                  <span className="value">{new Date(selectedRefundDetail.created_at).toLocaleString('tr-TR')}</span>
                </div>
                <div className="refund-detail-row">
                  <span className="label">Müşteri:</span>
                  <span className="value">{selectedRefundDetail.customer_name || 'Müşteri Yok'}</span>
                </div>
                <div className="refund-detail-row">
                  <span className="label">İşlem Türü:</span>
                  <span className="value" style={{
                    color: '#ef4444',
                    fontWeight: 'bold'
                  }}>
                    İADE
                  </span>
                </div>
                {selectedRefundDetail.record_type === 'refund' && (
                  <div className="refund-detail-row">
                    <span className="label">Ödeme Yöntemi:</span>
                    <span className="value">{selectedRefundDetail.payment_method || '-'}</span>
                  </div>
                )}
                {selectedRefundDetail.staff_name && (
                  <div className="refund-detail-row">
                    <span className="label">İşlemi Yapan:</span>
                    <span className="value">{selectedRefundDetail.staff_name}</span>
                  </div>
                )}
                {selectedRefundDetail.original_sale_id && (
                  <div className="refund-detail-row">
                    <span className="label">Orijinal Fiş:</span>
                    <span className="value">#{selectedRefundDetail.original_sale_id}</span>
                  </div>
                )}
                {selectedRefundDetail.record_type === 'exchange' && selectedRefundDetail.new_sale_id && (
                  <div className="refund-detail-row">
                    <span className="label">Yeni Satış Fişi:</span>
                    <span className="value" style={{ color: '#16a34a', fontWeight: 'bold' }}>#{selectedRefundDetail.new_sale_id}</span>
                  </div>
                )}
              </div>

              {/* İade Edilen Ürünler */}
              {selectedRefundDetail.return_items && selectedRefundDetail.return_items.length > 0 && (
                <div className="refund-items-section">
                  <h4 style={{ color: '#ef4444', marginBottom: '12px' }}>İade Edilen Ürünler</h4>
                  <div className="refund-items-list">
                    {selectedRefundDetail.return_items.map((item, index) => (
                      <div key={index} className="refund-item-row" style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '10px 12px',
                        backgroundColor: '#fef2f2',
                        borderRadius: '8px',
                        marginBottom: '8px',
                        border: '1px solid #fecaca'
                      }}>
                        {item.image && (
                          <img
                            src={`${IMAGE_HOST}${item.image}`}
                            alt={item.product_name}
                            onClick={(e) => { e.stopPropagation(); openImagePopup(item.image, item.product_name); }}
                            style={{
                              width: '45px',
                              height: '45px',
                              objectFit: 'cover',
                              borderRadius: '6px',
                              border: '1px solid #fecaca',
                              flexShrink: 0,
                              cursor: 'pointer'
                            }}
                          />
                        )}
                        <div style={{ flex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div>
                            <div style={{ fontWeight: '500', color: '#1e293b' }}>
                              {item.product_name}
                              {item.barcode && <span style={{ color: '#64748b', marginLeft: '6px', fontSize: '11px', fontWeight: 'normal' }}>({item.barcode})</span>}
                            </div>
                            <div style={{ fontSize: '13px', color: '#64748b' }}>
                              {item.quantity} adet × ₺{Number(item.unit_price).toLocaleString('tr-TR')}
                            </div>
                          </div>
                          <div style={{ fontWeight: 'bold', color: '#ef4444', fontSize: '15px' }}>
                            -₺{Number(item.total_price).toLocaleString('tr-TR')}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '12px',
                    backgroundColor: '#fee2e2',
                    borderRadius: '8px',
                    marginTop: '8px',
                    fontWeight: 'bold'
                  }}>
                    <span>İade Toplamı:</span>
                    <span style={{ color: '#ef4444' }}>
                      -₺{Number(selectedRefundDetail.return_total).toLocaleString('tr-TR')}
                    </span>
                  </div>
                </div>
              )}

              {/* İade Edilen Fark Kalemleri */}
              {selectedRefundDetail.adjustment_items && (() => {
                const adjItems = typeof selectedRefundDetail.adjustment_items === 'string'
                  ? JSON.parse(selectedRefundDetail.adjustment_items)
                  : selectedRefundDetail.adjustment_items;
                return adjItems && adjItems.length > 0 ? (
                  <div className="refund-items-section" style={{ marginTop: '16px' }}>
                    <h4 style={{ color: '#ef4444', marginBottom: '12px' }}>İade Edilen Fark Kalemleri</h4>
                    <div className="refund-items-list">
                      {adjItems.map((adj, index) => (
                        <div key={index} style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '10px 12px',
                          backgroundColor: '#fef2f2',
                          borderRadius: '8px',
                          marginBottom: '8px',
                          border: '1px solid #fecaca'
                        }}>
                          <div>
                            <div style={{ fontWeight: '500', color: '#1e293b' }}>
                              {adj.description || adj.reason || 'Fark Kalemi'}
                            </div>
                          </div>
                          <div style={{ fontWeight: 'bold', color: '#ef4444', fontSize: '15px' }}>
                            -₺{Number(adj.amount).toLocaleString('tr-TR')}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null;
              })()}

              {/* Yeni Alınan Ürünler (Değişim için) */}
              {selectedRefundDetail.new_items && selectedRefundDetail.new_items.length > 0 && (
                <div className="new-items-section" style={{ marginTop: '20px' }}>
                  <h4 style={{ color: '#16a34a', marginBottom: '12px' }}>Yeni Alınan Ürünler</h4>
                  <div className="new-items-list">
                    {selectedRefundDetail.new_items.map((item, index) => (
                      <div key={index} className="new-item-row" style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '10px 12px',
                        backgroundColor: '#f0fdf4',
                        borderRadius: '8px',
                        marginBottom: '8px',
                        border: '1px solid #bbf7d0'
                      }}>
                        {item.image && (
                          <img
                            src={`${IMAGE_HOST}${item.image}`}
                            alt={item.product_name}
                            onClick={(e) => { e.stopPropagation(); openImagePopup(item.image, item.product_name); }}
                            style={{
                              width: '45px',
                              height: '45px',
                              objectFit: 'cover',
                              borderRadius: '6px',
                              border: '1px solid #bbf7d0',
                              flexShrink: 0,
                              cursor: 'pointer'
                            }}
                          />
                        )}
                        <div style={{ flex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div>
                            <div style={{ fontWeight: '500', color: '#1e293b' }}>
                              {item.product_name}
                              {item.barcode && <span style={{ color: '#64748b', marginLeft: '6px', fontSize: '11px', fontWeight: 'normal' }}>({item.barcode})</span>}
                            </div>
                            <div style={{ fontSize: '13px', color: '#64748b' }}>
                              {item.quantity} adet × ₺{Number(item.unit_price).toLocaleString('tr-TR')}
                            </div>
                          </div>
                          <div style={{ fontWeight: 'bold', color: '#16a34a', fontSize: '15px' }}>
                            +₺{Number(item.total_price).toLocaleString('tr-TR')}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '12px',
                    backgroundColor: '#dcfce7',
                    borderRadius: '8px',
                    marginTop: '8px',
                    fontWeight: 'bold'
                  }}>
                    <span>Yeni Ürün Toplamı:</span>
                    <span style={{ color: '#16a34a' }}>
                      +₺{Number(selectedRefundDetail.new_items_total).toLocaleString('tr-TR')}
                    </span>
                  </div>
                </div>
              )}

              {/* Fark/Sonuç */}
              <div style={{
                marginTop: '20px',
                padding: '16px',
                backgroundColor: Number(selectedRefundDetail.total) < 0 ? '#f0fdf4' : '#fef2f2',
                borderRadius: '12px',
                border: `2px solid ${Number(selectedRefundDetail.total) < 0 ? '#16a34a' : '#ef4444'}`
              }}>
                {selectedRefundDetail.record_type === 'refund' ? (
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    fontSize: '18px',
                    fontWeight: 'bold'
                  }}>
                    <span>Müşteriye Nakit İade:</span>
                    <span style={{ color: '#ef4444' }}>
                      ₺{Math.abs(Number(selectedRefundDetail.return_total || selectedRefundDetail.total)).toLocaleString('tr-TR')}
                    </span>
                  </div>
                ) : (
                  <div>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      fontSize: '16px',
                      marginBottom: '8px'
                    }}>
                      <span>İade Tutarı (Yeni Satışa Düşüldü):</span>
                      <span style={{ color: '#ef4444' }}>
                        -₺{Math.abs(Number(selectedRefundDetail.return_total)).toLocaleString('tr-TR')}
                      </span>
                    </div>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      fontSize: '18px',
                      fontWeight: 'bold',
                      paddingTop: '8px',
                      borderTop: '1px solid #fca5a5'
                    }}>
                      <span>Müşteriden Alınan Fark:</span>
                      <span style={{ color: Number(selectedRefundDetail.total) > 0 ? '#16a34a' : '#ef4444' }}>
                        ₺{Math.abs(Number(selectedRefundDetail.total)).toLocaleString('tr-TR')}
                      </span>
                    </div>
                    <div style={{
                      marginTop: '12px',
                      padding: '8px 12px',
                      backgroundColor: '#ecfdf5',
                      borderRadius: '6px',
                      fontSize: '13px',
                      color: '#00897b',
                      textAlign: 'center'
                    }}>
                      ℹ️ Nakit iade yapılmadı - İade tutarı yeni satışa indirim olarak uygulandı
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowRefundDetailModal(false)}>Kapat</button>
            </div>
          </div>
        </div>
      )}

      {/* Borç Tahsilat Detay Modal */}
      {showDebtPaymentDetailModal && selectedDebtPaymentDetail && (
        <div className={`modal-overlay ${closingModal === 'debtPaymentDetail' ? 'closing' : ''}`} style={{ zIndex: 10000 }}>
          <div className="modal debt-payment-detail-modal" style={{ zIndex: 10001 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header" style={{ backgroundColor: '#059669', color: 'white' }}>
              <h2>
                Tahsilat Fişi - #{selectedDebtPaymentDetail.debt_payment_id || selectedDebtPaymentDetail.id}
              </h2>
              <button className="close-btn" onClick={() => handleCloseModal('debtPaymentDetail', setShowDebtPaymentDetailModal)} style={{ color: 'white' }}>✕</button>
            </div>
            <div className="modal-body">
              {/* Tahsilat Başlığı */}
              <div style={{
                backgroundColor: '#059669',
                color: 'white',
                padding: '12px 16px',
                borderRadius: '8px',
                marginBottom: '16px',
                textAlign: 'center',
                fontWeight: 'bold',
                fontSize: '14px'
              }}>
                💰 BORÇ TAHSİLATI - {selectedDebtPaymentDetail.debt_type === 'GRAM' ? 'Altın Borcu' : selectedDebtPaymentDetail.debt_type === 'GUMUS' ? 'Gümüş Borcu' : 'TL Borcu'} Tahsil Edildi
              </div>

              <div className="refund-detail-info">
                <div className="refund-detail-row">
                  <span className="label">Tarih:</span>
                  <span className="value">{new Date(selectedDebtPaymentDetail.created_at).toLocaleString('tr-TR')}</span>
                </div>
                <div className="refund-detail-row">
                  <span className="label">Müşteri:</span>
                  <span className="value">{selectedDebtPaymentDetail.customer_name || 'Müşteri Yok'}</span>
                </div>
                {selectedDebtPaymentDetail.customer_phone && (
                  <div className="refund-detail-row">
                    <span className="label">Telefon:</span>
                    <span className="value">{selectedDebtPaymentDetail.customer_phone}</span>
                  </div>
                )}
                <div className="refund-detail-row">
                  <span className="label">Borç Tipi:</span>
                  <span className="value" style={{
                    color: '#059669',
                    fontWeight: 'bold'
                  }}>
                    {selectedDebtPaymentDetail.debt_type === 'GRAM' ? '⚖️ Altın Borcu' : selectedDebtPaymentDetail.debt_type === 'GUMUS' ? '⚖️ Gümüş Borcu' : '₺ TL Borcu'}
                  </span>
                </div>
                <div className="refund-detail-row">
                  <span className="label">Ödeme Yöntemi:</span>
                  <span className="value">{selectedDebtPaymentDetail.payment_method || '-'}</span>
                </div>
                {selectedDebtPaymentDetail.payment_details && (
                  <div className="refund-detail-row">
                    <span className="label">Ödeme Detayı:</span>
                    <span className="value">
                      {(() => {
                        const details = selectedDebtPaymentDetail.payment_details;
                        const formatDetail = (p, i, arr) => (
                          <span key={i}>
                            {p.method || p.bankAccount || ''}{p.bankAccount ? ` (${stripBankAccountType(p.bankAccount)})` : ''}{p.amount ? `: ₺${Number(p.amount).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}` : ''}
                            {i < arr.length - 1 ? ', ' : ''}
                          </span>
                        );
                        // String ise parse et
                        if (typeof details === 'string') {
                          try {
                            const parsed = JSON.parse(details);
                            if (Array.isArray(parsed)) {
                              return parsed.map((p, i) => formatDetail(p, i, parsed));
                            }
                            return details;
                          } catch {
                            return details;
                          }
                        }
                        // Array ise formatla
                        if (Array.isArray(details)) {
                          return details.map((p, i) => formatDetail(p, i, details));
                        }
                        // Obje ise banka hesabını göster
                        if (typeof details === 'object' && details !== null) {
                          return details.bankAccount || details.method || JSON.stringify(details);
                        }
                        return '-';
                      })()}
                    </span>
                  </div>
                )}
                {selectedDebtPaymentDetail.staff_name && (
                  <div className="refund-detail-row">
                    <span className="label">İşlemi Yapan:</span>
                    <span className="value">{selectedDebtPaymentDetail.staff_name}</span>
                  </div>
                )}
                {selectedDebtPaymentDetail.debt_description && (
                  <div className="refund-detail-row">
                    <span className="label">Borç Açıklaması:</span>
                    <span className="value">{selectedDebtPaymentDetail.debt_description}</span>
                  </div>
                )}
                {selectedDebtPaymentDetail.sale_id && (
                  <div className="refund-detail-row">
                    <span className="label">İlgili Satış:</span>
                    <span className="value" style={{ color: '#0ea5e9', fontWeight: 'bold' }}>Fiş #{selectedDebtPaymentDetail.sale_id}</span>
                  </div>
                )}
                {selectedDebtPaymentDetail.notes && (
                  <div className="refund-detail-row">
                    <span className="label">Not:</span>
                    <span className="value">{selectedDebtPaymentDetail.notes}</span>
                  </div>
                )}
              </div>

              {/* Tahsilat Tutarı */}
              <div style={{
                marginTop: '20px',
                padding: '16px',
                backgroundColor: '#ecfdf5',
                borderRadius: '12px',
                border: '2px solid #059669'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  fontSize: '18px',
                  fontWeight: 'bold'
                }}>
                  <span>Tahsil Edilen Tutar:</span>
                  <span style={{ color: '#059669', fontSize: '22px' }}>
                    {selectedDebtPaymentDetail.debt_type === 'GRAM' || selectedDebtPaymentDetail.debt_type === 'GUMUS'
                      ? `${Number(selectedDebtPaymentDetail.total).toLocaleString('tr-TR')} gr`
                      : `₺${Number(selectedDebtPaymentDetail.total).toLocaleString('tr-TR')}`
                    }
                  </span>
                </div>
              </div>
            </div>
            <div className="modal-footer" style={{ justifyContent: 'space-between' }}>
              <button className="btn-cancel" onClick={() => setShowDebtPaymentDetailModal(false)}>Kapat</button>
              <div style={{ position: 'relative' }}>
                <button
                  className="btn-primary"
                  onClick={() => setDebtPaymentActionsOpen(prev => !prev)}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                >
                  <MoreVertical size={16} /> İşlemler
                </button>
                {debtPaymentActionsOpen && (
                  <div style={{
                    position: 'absolute', bottom: '100%', right: 0, marginBottom: '6px',
                    background: 'white', borderRadius: '10px', boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
                    border: '1px solid #e2e8f0', overflow: 'hidden', zIndex: 10, minWidth: '180px'
                  }}>
                    {!mobileApp && (
                    <button
                      onClick={() => {
                        setDebtPaymentActionsOpen(false);
                        const pd = selectedDebtPaymentDetail;
                        let parsedDetails = null;
                        if (pd.payment_details) {
                          try {
                            parsedDetails = typeof pd.payment_details === 'string' ? JSON.parse(pd.payment_details) : pd.payment_details;
                          } catch (e) {}
                        }
                        printCollectionReceipt({
                          customerName: pd.customer_name,
                          customerPhone: pd.customer_phone,
                          debtDescription: pd.debt_description || pd.notes || 'Borç Tahsilatı',
                          originalAmount: pd.previous_debt ?? (pd.total || pd.amount),
                          paidAmount: pd.total || pd.amount,
                          remainingAmount: Math.max(0, parseFloat(pd.remaining_debt) || 0),
                          isFullyPaid: !(parseFloat(pd.remaining_debt) > 0.01),
                          paymentMethod: pd.payment_method,
                          paymentDetails: Array.isArray(parsedDetails) ? parsedDetails : null,
                          staffName: pd.staff_name,
                          date: pd.created_at
                        });
                      }}
                      style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 16px', width: '100%', border: 'none', background: 'none', cursor: 'pointer', fontSize: '13px', color: '#334155', textAlign: 'left' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#f1f5f9'}
                      onMouseLeave={e => e.currentTarget.style.background = 'none'}
                    >
                      <Printer size={15} color="#059669" /> Yazdır
                    </button>
                    )}
                    {user?.role === 'admin' && (
                      <>
                        <button
                          onClick={() => {
                            setDebtPaymentActionsOpen(false);
                            openEditDebtPaymentModal(selectedDebtPaymentDetail);
                          }}
                          style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 16px', width: '100%', border: 'none', background: 'none', cursor: 'pointer', fontSize: '13px', color: '#334155', textAlign: 'left', borderTop: '1px solid #f1f5f9' }}
                          onMouseEnter={e => e.currentTarget.style.background = '#f1f5f9'}
                          onMouseLeave={e => e.currentTarget.style.background = 'none'}
                        >
                          <Pencil size={15} color="#2563eb" /> Düzenle
                        </button>
                        <button
                          onClick={() => {
                            setDebtPaymentActionsOpen(false);
                            deleteDebtPayment(selectedDebtPaymentDetail.payment_ids || selectedDebtPaymentDetail.debt_payment_id || selectedDebtPaymentDetail.id);
                          }}
                          style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 16px', width: '100%', border: 'none', background: 'none', cursor: 'pointer', fontSize: '13px', color: '#dc2626', textAlign: 'left', borderTop: '1px solid #f1f5f9' }}
                          onMouseEnter={e => e.currentTarget.style.background = '#fef2f2'}
                          onMouseLeave={e => e.currentTarget.style.background = 'none'}
                        >
                          <Trash2 size={15} /> Sil
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tahsilat Düzenleme Modal */}
      {showEditDebtPaymentModal && editingDebtPayment && (
        <div className="modal-overlay" style={{ zIndex: 10002 }}>
          <div className="modal edit-debt-payment-modal" style={{ zIndex: 10003, maxWidth: '450px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header" style={{ backgroundColor: '#059669', color: 'white' }}>
              <h2>Tahsilat Düzenle</h2>
              <button className="close-btn" onClick={() => setShowEditDebtPaymentModal(false)} style={{ color: 'white' }}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Tarih</label>
                <input
                  type="datetime-local"
                  value={debtPaymentEditData.created_at}
                  onChange={(e) => setDebtPaymentEditData({ ...debtPaymentEditData, created_at: e.target.value, _dateChanged: true })}
                  className="form-input"
                />
              </div>

              {/* Ödeme Detayları — her kalem ayrı satır */}
              <div style={{ marginBottom: '12px' }}>
                <label style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: '8px', display: 'block' }}>Ödeme Detayları</label>
                {(debtPaymentEditData.details || []).map((detail, idx) => {
                  const isScrap = detail.isScrap || (detail.method || '').includes('Hurda');
                  const methodLabel = detail.method || 'Ödeme';
                  return (
                    <div key={idx} style={{
                      display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px',
                      padding: '10px 12px', borderRadius: '8px',
                      backgroundColor: isScrap ? '#fef3c7' : '#f8fafc',
                      border: isScrap ? '1px solid #fbbf24' : '1px solid #e2e8f0'
                    }}>
                      <span style={{ flex: 1, fontWeight: 600, fontSize: '0.82rem', color: isScrap ? '#92400e' : '#334155' }}>
                        {methodLabel}
                        {isScrap && detail.scrapDetails && (
                          <span style={{ fontWeight: 400, fontSize: '0.75rem', color: '#b45309', marginLeft: '6px' }}>
                            ({detail.scrapDetails.gross_weight}gr brüt)
                          </span>
                        )}
                      </span>
                      {isScrap ? (
                        <span style={{ fontWeight: 700, fontSize: '0.9rem', color: '#92400e' }}>
                          ₺{Number(detail.amount).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                        </span>
                      ) : (
                        <PriceInput
                          value={parseFloat(detail.amount) || 0}
                          onChange={(val) => {
                            const updated = [...debtPaymentEditData.details];
                            updated[idx] = { ...updated[idx], amount: val, amount_tl: val };
                            setDebtPaymentEditData({ ...debtPaymentEditData, details: updated, _amountChanged: true });
                          }}
                          className="form-input"
                          style={{ width: '140px', textAlign: 'right', fontWeight: 600 }}
                        />
                      )}
                    </div>
                  );
                })}
                {/* Toplam */}
                <div style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '10px 12px', borderRadius: '8px', backgroundColor: '#ecfdf5', border: '2px solid #059669',
                  marginTop: '4px'
                }}>
                  <span style={{ fontWeight: 700, fontSize: '0.9rem', color: '#059669' }}>TOPLAM</span>
                  <span style={{ fontWeight: 700, fontSize: '1.1rem', color: '#059669' }}>
                    ₺{(debtPaymentEditData.details || []).reduce((s, d) => s + (parseFloat(d.amount) || 0), 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                  </span>
                </div>
              </div>

              <div className="form-group">
                <label>Not</label>
                <textarea
                  value={debtPaymentEditData.notes}
                  onChange={(e) => setDebtPaymentEditData({ ...debtPaymentEditData, notes: e.target.value })}
                  className="form-input"
                  rows={2}
                  placeholder="Ek notlar..."
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowEditDebtPaymentModal(false)}>İptal</button>
              <button className="btn-primary" onClick={saveDebtPaymentEdit} style={{ backgroundColor: '#059669' }}>
                Kaydet
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hurda Alımı Detay Modal */}
      {showScrapPurchaseDetailModal && selectedScrapPurchaseDetail && (
        <div className="modal-overlay" style={{ zIndex: 10000 }}>
          <div className="modal scrap-purchase-detail-modal" style={{ zIndex: 10001 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header" style={{ backgroundColor: '#f59e0b', color: 'white' }}>
              <h2>
                ⚖️ Hurda Alım Fişi - #{selectedScrapPurchaseDetail.store_scrap_number || selectedScrapPurchaseDetail.scrap_purchase_id || selectedScrapPurchaseDetail.id}
              </h2>
              <button className="close-btn" onClick={() => setShowScrapPurchaseDetailModal(false)} style={{ color: 'white' }}>✕</button>
            </div>
            <div className="modal-body">
              {/* Hurda Başlığı */}
              <div style={{
                backgroundColor: '#f59e0b',
                color: 'white',
                padding: '12px 16px',
                borderRadius: '8px',
                marginBottom: '16px',
                textAlign: 'center',
                fontWeight: 'bold',
                fontSize: '14px'
              }}>
                ⚖️ HURDA ALIMI {selectedScrapPurchaseDetail.item_count > 1 ? `(${selectedScrapPurchaseDetail.item_count} Kalem)` : `- ${selectedScrapPurchaseDetail.metal_type === 'gold' ? 'Altın' : 'Gümüş'} ${selectedScrapPurchaseDetail.purity} Ayar`}
              </div>

              <div className="refund-detail-info">
                <div className="refund-detail-row">
                  <span className="label">Tarih:</span>
                  <span className="value">{new Date(selectedScrapPurchaseDetail.created_at).toLocaleString('tr-TR')}</span>
                </div>
                <div className="refund-detail-row">
                  <span className="label">Müşteri:</span>
                  <span className="value">{selectedScrapPurchaseDetail.customer_name || 'Müşteri Yok'}</span>
                </div>
                {selectedScrapPurchaseDetail.customer_phone && (
                  <div className="refund-detail-row">
                    <span className="label">Telefon:</span>
                    <span className="value">{selectedScrapPurchaseDetail.customer_phone}</span>
                  </div>
                )}
                {!selectedScrapPurchaseDetail.scrap_items && (
                  <div className="refund-detail-row">
                    <span className="label">Metal Tipi:</span>
                    <span className="value" style={{
                      color: selectedScrapPurchaseDetail.metal_type === 'gold' ? '#f59e0b' : '#6b7280',
                      fontWeight: 'bold'
                    }}>
                      {selectedScrapPurchaseDetail.metal_type === 'gold' ? '🥇 Altın' : '🥈 Gümüş'} - {selectedScrapPurchaseDetail.purity} Ayar
                    </span>
                  </div>
                )}
                {selectedScrapPurchaseDetail.staff_name && (
                  <div className="refund-detail-row">
                    <span className="label">İşlemi Yapan:</span>
                    <span className="value">{selectedScrapPurchaseDetail.staff_name}</span>
                  </div>
                )}
                {selectedScrapPurchaseDetail.payment_method && (
                  <div className="refund-detail-row">
                    <span className="label">Ödeme Yöntemi:</span>
                    <span className="value">{selectedScrapPurchaseDetail.payment_method}</span>
                  </div>
                )}
                {selectedScrapPurchaseDetail.notes && (
                  <div className="refund-detail-row">
                    <span className="label">Not:</span>
                    <span className="value">{selectedScrapPurchaseDetail.notes}</span>
                  </div>
                )}
              </div>

              {/* Çoklu kalem - her birini ayrı göster */}
              {selectedScrapPurchaseDetail.scrap_items && selectedScrapPurchaseDetail.scrap_items.length > 1 && (
                <div style={{ marginTop: '20px' }}>
                  <h4 style={{ color: '#92400e', marginBottom: '12px' }}>Hurda Kalemleri ({selectedScrapPurchaseDetail.scrap_items.length})</h4>
                  {selectedScrapPurchaseDetail.scrap_items.map((item, idx) => (
                    <div key={item.id} style={{
                      padding: '10px 14px',
                      backgroundColor: idx % 2 === 0 ? '#fefce8' : '#fffbeb',
                      borderRadius: '8px',
                      marginBottom: '8px',
                      border: '1px solid #fde68a'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                        <span style={{ fontWeight: '700', fontSize: '0.85rem', color: item.metal_type === 'gold' ? '#b45309' : '#6b7280' }}>
                          {idx + 1}. {item.metal_type === 'gold' ? '🥇 Altın' : '🥈 Gümüş'} - {item.purity} Ayar
                        </span>
                        <span style={{ fontWeight: '700', color: '#b45309', textAlign: 'right' }}>
                          ₺{Number(item.total_price).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                          {item.calculated_amount != null && Math.abs(Number(item.calculated_amount) - Number(item.total_price || 0)) > 0.01 && (
                            <div style={{ fontWeight: 400, fontSize: '0.72rem', color: '#94a3b8' }}>
                              Hesaplanan: ₺{Number(item.calculated_amount).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                            </div>
                          )}
                        </span>
                      </div>
                      <div style={{ display: 'flex', gap: '12px', fontSize: '0.78rem', color: '#78350f', flexWrap: 'wrap' }}>
                        <span>Brüt: {Number(item.gross_weight).toLocaleString('tr-TR')}gr</span>
                        {(item.waste_gram > 0 || item.waste_percent > 0) && <span>Fire: {item.waste_gram > 0 ? `${Number(item.waste_gram).toLocaleString('tr-TR')}gr` : `${(Number(item.gross_weight) * Number(item.waste_percent) / 100).toLocaleString('tr-TR')}gr`}</span>}
                        <span style={{ color: '#16a34a', fontWeight: '600' }}>Net: {(Number(item.gross_weight) - (Number(item.waste_gram) > 0 ? Number(item.waste_gram) : Number(item.gross_weight) * Number(item.waste_percent) / 100)).toLocaleString('tr-TR')}gr</span>
                        <span>Has: {Number(item.pure_weight).toLocaleString('tr-TR')}gr</span>
                        <span>Birim: ₺{Number(item.unit_price).toLocaleString('tr-TR')}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Ağırlık Bilgileri - Toplam (veya tekli kayıt) */}
              <div style={{ marginTop: '20px' }}>
                <h4 style={{ color: '#92400e', marginBottom: '12px' }}>
                  {selectedScrapPurchaseDetail.item_count > 1 ? 'Toplam Ağırlık' : 'Ağırlık Bilgileri'}
                </h4>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '12px'
                }}>
                  <div style={{
                    padding: '12px',
                    backgroundColor: '#fef3c7',
                    borderRadius: '8px',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '12px', color: '#92400e' }}>Brüt Ağırlık</div>
                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#78350f' }}>
                      {Number(selectedScrapPurchaseDetail.gross_weight).toLocaleString('tr-TR')} gr
                    </div>
                  </div>
                  <div style={{
                    padding: '12px',
                    backgroundColor: '#fef3c7',
                    borderRadius: '8px',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '12px', color: '#92400e' }}>Net Ağırlık</div>
                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#78350f' }}>
                      {(Number(selectedScrapPurchaseDetail.gross_weight) - (Number(selectedScrapPurchaseDetail.waste_gram) > 0 ? Number(selectedScrapPurchaseDetail.waste_gram) : Number(selectedScrapPurchaseDetail.gross_weight) * Number(selectedScrapPurchaseDetail.waste_percent || 0) / 100)).toLocaleString('tr-TR')} gr
                    </div>
                  </div>
                  {!selectedScrapPurchaseDetail.scrap_items && (Number(selectedScrapPurchaseDetail.waste_gram) > 0 || Number(selectedScrapPurchaseDetail.waste_percent) > 0) && (
                    <div style={{
                      padding: '12px',
                      backgroundColor: '#fee2e2',
                      borderRadius: '8px',
                      textAlign: 'center'
                    }}>
                      <div style={{ fontSize: '12px', color: '#991b1b' }}>Fire (gr)</div>
                      <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#7f1d1d' }}>
                        {(Number(selectedScrapPurchaseDetail.waste_gram) > 0 ? Number(selectedScrapPurchaseDetail.waste_gram) : Number(selectedScrapPurchaseDetail.gross_weight) * Number(selectedScrapPurchaseDetail.waste_percent) / 100).toLocaleString('tr-TR')} gr
                      </div>
                    </div>
                  )}
                  {!selectedScrapPurchaseDetail.scrap_items && (
                    <div style={{
                      padding: '12px',
                      backgroundColor: '#dbeafe',
                      borderRadius: '8px',
                      textAlign: 'center'
                    }}>
                      <div style={{ fontSize: '12px', color: '#1e40af' }}>Birim Fiyat</div>
                      <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#1e3a8a' }}>
                        ₺{Number(selectedScrapPurchaseDetail.unit_price).toLocaleString('tr-TR')}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Toplam Tutar */}
              <div style={{
                marginTop: '20px',
                padding: '16px',
                backgroundColor: '#fef3c7',
                borderRadius: '12px',
                border: '2px solid #f59e0b'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  fontSize: '18px',
                  fontWeight: 'bold'
                }}>
                  <span>Ödenen Tutar:</span>
                  <span style={{ color: '#b45309', fontSize: '22px' }}>
                    ₺{Number(selectedScrapPurchaseDetail.total).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                  </span>
                </div>
                {/* Tutar fişte elle değiştirildiyse sistemin hesapladığı tutar da görünsün */}
                {selectedScrapPurchaseDetail.calculated_amount != null &&
                  Math.abs(Number(selectedScrapPurchaseDetail.calculated_amount) - Number(selectedScrapPurchaseDetail.total || 0)) > 0.01 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px', fontSize: '13px', color: '#92400e' }}>
                    <span>Hesaplanan Tutar (değiştirildi):</span>
                    <span style={{ textDecoration: 'line-through', color: '#a16207' }}>
                      ₺{Number(selectedScrapPurchaseDetail.calculated_amount).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              {/* Admin için düzenle ve sil butonları */}
              {user?.role === 'admin' && (
                <div style={{ display: 'flex', gap: '8px', marginRight: 'auto' }}>
                  <button
                    className="btn-primary"
                    onClick={() => openEditScrapPurchaseModal(selectedScrapPurchaseDetail)}
                    style={{ backgroundColor: '#f59e0b' }}
                  >
                    <Pencil size={16} /> Düzenle
                  </button>
                  <button
                    className="btn-danger"
                    onClick={() => deleteScrapPurchase(selectedScrapPurchaseDetail.scrap_purchase_id || selectedScrapPurchaseDetail.id)}
                  >
                    <Trash2 size={16} /> Sil
                  </button>
                </div>
              )}
              <button className="btn-cancel" onClick={() => setShowScrapPurchaseDetailModal(false)}>Kapat</button>
            </div>
          </div>
        </div>
      )}

      {/* Hurda Alımı Düzenleme Modal */}
      {showEditScrapPurchaseModal && editingScrapPurchase && (
        <div className="modal-overlay" style={{ zIndex: 10002 }}>
          <div className="modal" style={{ zIndex: 10003, maxWidth: '500px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header" style={{ backgroundColor: '#f59e0b', color: 'white' }}>
              <h2>Hurda Alımı Düzenle</h2>
              <button className="close-btn" onClick={() => setShowEditScrapPurchaseModal(false)} style={{ color: 'white' }}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Tarih</label>
                <input
                  type="datetime-local"
                  value={scrapPurchaseEditData.created_at}
                  onChange={(e) => setScrapPurchaseEditData({ ...scrapPurchaseEditData, created_at: e.target.value })}
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label>Metal Tipi</label>
                <select
                  value={scrapPurchaseEditData.metal_type}
                  onChange={(e) => setScrapPurchaseEditData({ ...scrapPurchaseEditData, metal_type: e.target.value })}
                  className="form-input"
                >
                  <option value="gold">Altın</option>
                  <option value="silver">Gümüş</option>
                </select>
              </div>
              <div className="form-group">
                <label>Ayar</label>
                <select
                  value={scrapPurchaseEditData.purity}
                  onChange={(e) => setScrapPurchaseEditData({ ...scrapPurchaseEditData, purity: e.target.value })}
                  className="form-input"
                >
                  <option value="24">24 Ayar</option>
                  <option value="22">22 Ayar</option>
                  <option value="18">18 Ayar</option>
                  <option value="14">14 Ayar</option>
                  <option value="8">8 Ayar</option>
                  <option value="925">925 Ayar</option>
                </select>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div className="form-group">
                  <label>Brüt Ağırlık (gr)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={scrapPurchaseEditData.gross_weight}
                    onChange={(e) => setScrapPurchaseEditData({ ...scrapPurchaseEditData, gross_weight: e.target.value })}
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label>Fire (%)</label>
                  <input
                    type="number"
                    step="0.1"
                    value={scrapPurchaseEditData.waste_percent}
                    onChange={(e) => setScrapPurchaseEditData({ ...scrapPurchaseEditData, waste_percent: e.target.value })}
                    className="form-input"
                  />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div className="form-group">
                  <label>Net Ağırlık (gr)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={scrapPurchaseEditData.pure_weight}
                    onChange={(e) => setScrapPurchaseEditData({ ...scrapPurchaseEditData, pure_weight: e.target.value })}
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label>Birim Fiyat (₺)</label>
                  <PriceInput
                    value={scrapPurchaseEditData.unit_price}
                    onChange={(val) => setScrapPurchaseEditData({ ...scrapPurchaseEditData, unit_price: val })}
                    className="form-input"
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Toplam Tutar (₺)</label>
                <PriceInput
                  value={scrapPurchaseEditData.total}
                  onChange={(val) => setScrapPurchaseEditData({ ...scrapPurchaseEditData, total: val })}
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label>Ödeme Yöntemi</label>
                <select
                  value={scrapPurchaseEditData.payment_method}
                  onChange={(e) => setScrapPurchaseEditData({ ...scrapPurchaseEditData, payment_method: e.target.value })}
                  className="form-input"
                >
                  <option value="Nakit">Nakit</option>
                  <option value="Havale">Havale</option>
                  <option value="Kredi Kartı">Kredi Kartı</option>
                </select>
              </div>
              <div className="form-group">
                <label>Not</label>
                <textarea
                  value={scrapPurchaseEditData.notes}
                  onChange={(e) => setScrapPurchaseEditData({ ...scrapPurchaseEditData, notes: e.target.value })}
                  className="form-input"
                  rows={2}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowEditScrapPurchaseModal(false)}>İptal</button>
              <button className="btn-primary" onClick={saveScrapPurchaseEdit} style={{ backgroundColor: '#f59e0b' }}>
                Kaydet
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Masraf Detay Modal */}
      {showExpenseDetailModal && selectedExpenseDetail && (
        <div className="modal-overlay" style={{ zIndex: 10000 }}>
          <div className="modal expense-detail-modal" style={{ zIndex: 10001 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header" style={{ backgroundColor: '#dc2626', color: 'white' }}>
              <h2>
                Masraf Fişi - #{selectedExpenseDetail.expense_id || selectedExpenseDetail.id}
              </h2>
              <button className="close-btn" onClick={() => setShowExpenseDetailModal(false)} style={{ color: 'white' }}>✕</button>
            </div>
            <div className="modal-body">
              {/* Masraf Başlığı */}
              <div style={{
                backgroundColor: '#dc2626',
                color: 'white',
                padding: '12px 16px',
                borderRadius: '8px',
                marginBottom: '16px',
                textAlign: 'center',
                fontWeight: 'bold',
                fontSize: '14px'
              }}>
                MASRAF KAYDI
              </div>

              <div className="refund-detail-info">
                <div className="refund-detail-row">
                  <span className="label">Tarih:</span>
                  <span className="value">{new Date(selectedExpenseDetail.created_at).toLocaleString('tr-TR')}</span>
                </div>
                <div className="refund-detail-row">
                  <span className="label">Açıklama:</span>
                  <span className="value" style={{ fontWeight: 'bold' }}>{selectedExpenseDetail.description || '-'}</span>
                </div>
                {selectedExpenseDetail.category && (
                  <div className="refund-detail-row">
                    <span className="label">Kategori:</span>
                    <span className="value" style={{
                      backgroundColor: '#fee2e2',
                      color: '#991b1b',
                      padding: '2px 8px',
                      borderRadius: '4px',
                      fontWeight: 'bold'
                    }}>
                      {selectedExpenseDetail.category}
                    </span>
                  </div>
                )}
                <div className="refund-detail-row">
                  <span className="label">Mağaza:</span>
                  <span className="value">{selectedExpenseDetail.store_name ||
                    (selectedExpenseDetail.store_id === 1 ? 'Akik Gold Jewellery' :
                     selectedExpenseDetail.store_id === 2 ? 'Akik Gümüş Osmangazi' :
                     selectedExpenseDetail.store_id === 3 ? 'Akik Gümüş Yeşil' : 'Bilinmeyen')
                  }</span>
                </div>
                {selectedExpenseDetail.staff_name && (
                  <div className="refund-detail-row">
                    <span className="label">İşlemi Yapan:</span>
                    <span className="value">{selectedExpenseDetail.staff_name}</span>
                  </div>
                )}
                {selectedExpenseDetail.payment_method && (
                  <div className="refund-detail-row">
                    <span className="label">Ödeme Yöntemi:</span>
                    <span className="value">{selectedExpenseDetail.payment_method}</span>
                  </div>
                )}
                {selectedExpenseDetail.notes && (
                  <div className="refund-detail-row">
                    <span className="label">Not:</span>
                    <span className="value">{selectedExpenseDetail.notes}</span>
                  </div>
                )}
              </div>

              {/* Toplam Tutar */}
              <div style={{
                marginTop: '20px',
                padding: '16px',
                backgroundColor: '#fee2e2',
                borderRadius: '12px',
                border: '2px solid #dc2626'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  fontSize: '18px',
                  fontWeight: 'bold'
                }}>
                  <span>Masraf Tutarı:</span>
                  <span style={{ color: '#dc2626', fontSize: '22px' }}>
                    -₺{Number(selectedExpenseDetail.total).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                  </span>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              {/* Admin için düzenle ve sil butonları */}
              {user?.role === 'admin' && (
                <div style={{ display: 'flex', gap: '8px', marginRight: 'auto' }}>
                  <button
                    className="btn-primary"
                    onClick={() => {
                      setShowExpenseDetailModal(false);
                      openEditExpenseModal(selectedExpenseDetail);
                    }}
                    style={{ backgroundColor: '#dc2626' }}
                  >
                    <Pencil size={16} /> Düzenle
                  </button>
                  <button
                    className="btn-danger"
                    onClick={() => {
                      setShowExpenseDetailModal(false);
                      deleteExpense(selectedExpenseDetail.expense_id || selectedExpenseDetail.id);
                    }}
                  >
                    <Trash2 size={16} /> Sil
                  </button>
                </div>
              )}
              <button className="btn-cancel" onClick={() => setShowExpenseDetailModal(false)}>Kapat</button>
            </div>
          </div>
        </div>
      )}

      {/* Satış Düzenleme Modal */}
      {showEditSaleModal && editingSale && (
        <div className="modal-overlay" key={`edit-sale-${editingSale.id}-${editModalKey}`}>
          <div className="modal edit-sale-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Satış Düzenle - Fiş #{editingSale.receipt_number || editingSale.store_sale_number || editingSale.id}</h2>
              <button className="close-btn" onClick={closeEditSaleModal}>✕</button>
            </div>
            <div className="modal-body">
              <div className="edit-sale-info">
                <p><strong>Müşteri:</strong> {editingSale.customer_name || 'Müşteri Yok'}</p>
                <p><strong>Tarih:</strong> {new Date(editingSale.created_at).toLocaleString('tr-TR')}</p>
                <p><strong>Ödeme:</strong> {editingSale.payment_method}</p>
              </div>
              <div className="edit-sale-items">
                <h4>Ürünler</h4>
                <div className="edit-items-header">
                  <span className="col-name">Ürün</span>
                  <span className="col-qty">Adet</span>
                  <span className="col-price">Birim Fiyat</span>
                  <span className="col-total">Toplam</span>
                </div>
                {editingItems.map(item => (
                  <div key={item.id} className="edit-sale-item">
                    <div className="edit-item-name">
                      <span>{item.product_name} {item.barcode && <span style={{ color: '#64748b', fontSize: '11px' }}>({item.barcode})</span>}</span>
                      <span className="original-price">Orijinal: ₺{Number(item.original_unit_price || item.unit_price).toLocaleString('tr-TR', { maximumFractionDigits: 2 })}</span>
                      {Number(item.discount_amount) > 0 && (
                        <span style={{ color: '#ef4444', fontSize: '11px' }}>
                          İndirim: -₺{Number(item.discount_amount).toLocaleString('tr-TR', { maximumFractionDigits: 2 })} {item.discount_percent > 0 && `(%${Number(item.discount_percent).toFixed(0)})`} → ₺{Number(item.discounted_unit_price).toLocaleString('tr-TR', { maximumFractionDigits: 2 })}
                        </span>
                      )}
                    </div>
                    <div className="edit-item-qty">
                      <button
                        className="qty-btn"
                        onClick={() => updateEditItemQuantity(item.id, item.newQuantity - 1)}
                      >−</button>
                      <input
                        type="number"
                        min="1"
                        value={item.newQuantity}
                        onChange={(e) => updateEditItemQuantity(item.id, parseInt(e.target.value) || 1)}
                      />
                      <button
                        className="qty-btn"
                        onClick={() => updateEditItemQuantity(item.id, item.newQuantity + 1)}
                      >+</button>
                    </div>
                    <div className="edit-item-price">
                      <span className="currency">₺</span>
                      <PriceInput
                        value={item.newUnitPrice}
                        onChange={(val) => updateEditItemPrice(item.id, val)}
                        disabled={!canEditPrice()}
                        className={!canEditPrice() ? 'disabled-input' : ''}
                      />
                    </div>
                    <div className="edit-item-total">
                      ₺{(item.newUnitPrice * item.newQuantity).toLocaleString('tr-TR', { maximumFractionDigits: 2 })}
                    </div>
                  </div>
                ))}
              </div>
              {/* Sepet İndirimi */}
              <div style={{ padding: '12px 14px', background: '#fef2f2', borderRadius: '8px', border: '1px solid #fecaca', marginTop: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                  <label style={{ fontWeight: 600, fontSize: '0.95rem', color: '#dc2626' }}>Sepet İndirimi</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <span style={{ color: '#dc2626', fontWeight: 500 }}>₺</span>
                    <PriceInput
                      value={editDiscount}
                      onChange={(val) => setEditDiscount(val)}
                      style={{ width: '130px', padding: '8px 12px', borderRadius: '8px', border: '2px solid #fca5a5', fontSize: '1rem', textAlign: 'right', fontWeight: 600, color: '#dc2626', background: '#fff' }}
                    />
                  </div>
                </div>
              </div>
              <div className="edit-sale-summary">
                {(() => {
                  const itemsSubtotal = editingItems.reduce((sum, item) => sum + (item.newUnitPrice * item.newQuantity), 0);
                  const discountVal = parseFloat(editDiscount) || 0;
                  const scrapAmount = Number(editingSale.scrap_total_amount) || 0;
                  const scrapDebt = Number(editingSale.scrap_debt_discount) || 0;
                  const finalTotal = calculateEditTotal();
                  return (
                    <>
                      <div className="summary-row" style={{ color: '#64748b' }}>
                        <span>Ürün Toplamı:</span>
                        <span>₺{itemsSubtotal.toLocaleString('tr-TR')}</span>
                      </div>
                      {discountVal > 0 && (
                        <div className="summary-row" style={{ color: '#ef4444' }}>
                          <span>Sepet İndirimi:</span>
                          <span>-₺{discountVal.toLocaleString('tr-TR')}</span>
                        </div>
                      )}
                      {scrapAmount > 0 && (
                        <div className="summary-row" style={{ color: '#ef4444', fontSize: '0.85rem', paddingLeft: '12px' }}>
                          <span>Hurda Mahsubu:</span>
                          <span>-₺{scrapAmount.toLocaleString('tr-TR')}</span>
                        </div>
                      )}
                      {scrapDebt > 0 && (
                        <div className="summary-row" style={{ color: '#ef4444', fontSize: '0.85rem', paddingLeft: '12px' }}>
                          <span>Hurda Borç İndirimi:</span>
                          <span>-₺{scrapDebt.toLocaleString('tr-TR')}</span>
                        </div>
                      )}
                      <div className="summary-row original" style={{ fontWeight: 'bold', fontSize: '1.1rem', borderTop: '2px solid #e2e8f0', paddingTop: '8px', marginTop: '4px' }}>
                        <span>Yeni Toplam:</span>
                        <span style={{ color: '#2563eb' }}>₺{finalTotal.toLocaleString('tr-TR')}</span>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={closeEditSaleModal}>İptal</button>
              <button className="btn-save" onClick={saveEditedSale} disabled={savingEdit}>
                {savingEdit ? 'Kaydediliyor...' : 'Değişiklikleri Kaydet'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Admin Kapsamlı Düzenleme Modal */}
      {showAdminEditModal && editingSale && (
        <div className="modal-overlay" style={{ zIndex: 10002 }} key={`admin-edit-${editingSale.id}-${editModalKey}`}>
          <div className="modal admin-edit-sale-modal" style={{ zIndex: 10003 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingSale._isRefund ? 'İade Düzenleme' : 'Admin Düzenleme'} - Fiş #{editingSale.receipt_number || editingSale.store_sale_number || editingSale.id}</h2>
              <button className="close-btn" onClick={closeAdminEditModal}>✕</button>
            </div>
            <div className="modal-body">
              {/* Temel Bilgiler */}
              <div className="admin-edit-section">
                <h4>{editingSale._isRefund ? 'İade Bilgileri' : 'Satış Bilgileri'}</h4>
                <div className="admin-edit-grid">
                  <div className="admin-edit-field">
                    <label>Tarih & Saat</label>
                    <input
                      type="datetime-local"
                      value={adminEditData.created_at}
                      onChange={(e) => setAdminEditData({...adminEditData, created_at: e.target.value})}
                    />
                  </div>
                  <div className="admin-edit-field">
                    <label>Ödeme Tipi</label>
                    <select
                      value={adminEditData.payment_method}
                      onChange={(e) => setAdminEditData({...adminEditData, payment_method: e.target.value})}
                    >
                      <option value="">Seçiniz</option>
                      {editingSale._isRefund ? (
                        <>
                          <option value="Nakit İade">Nakit İade</option>
                          <option value="Kredi Kartı İade">Kredi Kartı İade</option>
                          <option value="Havale İade">Havale İade</option>
                          <option value="Hediye Kartı">Bakiye</option>
                          <option value="Hediye Çeki İadesi">Hediye Çeki İadesi</option>
                          <option value="Açık Hesap">Açık Hesap (Borçtan Düş)</option>
                          <option value="Karışık">Karışık</option>
                          {adminEditData.payment_method && !['', 'Nakit İade', 'Kredi Kartı İade', 'Havale İade', 'Hediye Kartı', 'Hediye Çeki İadesi', 'Açık Hesap', 'Karışık'].includes(adminEditData.payment_method) && (
                            <option value={adminEditData.payment_method}>{adminEditData.payment_method}</option>
                          )}
                        </>
                      ) : (
                        <>
                          <option value="Nakit">Nakit</option>
                          <option value="Kredi Kartı">Kredi Kartı</option>
                          <option value="Havale/EFT">Havale/EFT</option>
                          <option value="Sanal Pos">Sanal Pos</option>
                          <option value="Açık Hesap (TL)">Açık Hesap (₺)</option>
                          <option value="Açık Hesap (Gram)">Açık Hesap (Gram)</option>
                          <option value="Açık Hesap (Gümüş)">Açık Hesap (Gümüş)</option>
                          <option value="Karışık">Karışık</option>
                          <option value="Hurda Borca Al">Hurda Borca Al</option>
                          <option value="Çoklu Ödeme">Çoklu Ödeme</option>
                          {adminEditData.payment_method && !['', 'Nakit', 'Kredi Kartı', 'Havale/EFT', 'Sanal Pos', 'Açık Hesap (TL)', 'Açık Hesap (Gram)', 'Açık Hesap (Gümüş)', 'Karışık', 'Hurda Borca Al', 'Çoklu Ödeme'].includes(adminEditData.payment_method) && (
                            <option value={adminEditData.payment_method}>{adminEditData.payment_method}</option>
                          )}
                        </>
                      )}
                    </select>
                  </div>
                  {(adminEditData.payment_method === 'Havale/EFT' || adminEditData.payment_method === 'Havale İade') && (
                    <div className="admin-edit-field">
                      <label>Banka Hesabı</label>
                      <select
                        value={adminEditData.bank_account || ''}
                        onChange={(e) => setAdminEditData({...adminEditData, bank_account: e.target.value})}
                      >
                        <option value="">Hesap Seçiniz</option>
                        {bankAccounts.map(acc => (
                          <option key={acc.id} value={`${acc.account_name} (${acc.account_type === 'company' ? 'Şirket' : acc.account_type === 'ecommerce' ? 'E-Ticaret' : 'Şahsi'})`}>
                            {acc.account_name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  <div className="admin-edit-field">
                    <label>Satış Sorumlusu {adminEditSelectedStaff.length > 0 && `(${adminEditSelectedStaff.length})`}</label>
                    <div style={{
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      maxHeight: '120px',
                      overflowY: 'auto',
                      padding: '4px'
                    }}>
                      {salesStaffList.length === 0 ? (
                        <div style={{ padding: '8px', color: '#9ca3af', fontSize: '13px', textAlign: 'center' }}>
                          Personel bulunamadı
                        </div>
                      ) : (
                        salesStaffList.map(staff => {
                          const isSelected = adminEditSelectedStaff.some(s => s.id === staff.id);
                          return (
                            <div
                              key={staff.id}
                              onClick={() => {
                                if (isSelected) {
                                  setAdminEditSelectedStaff(adminEditSelectedStaff.filter(s => s.id !== staff.id));
                                } else {
                                  setAdminEditSelectedStaff([...adminEditSelectedStaff, staff]);
                                }
                              }}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '6px 8px',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                backgroundColor: isSelected ? '#eef2ff' : 'transparent',
                                transition: 'background-color 0.15s'
                              }}
                            >
                              <div style={{
                                width: '18px',
                                height: '18px',
                                borderRadius: '4px',
                                border: isSelected ? '2px solid #6366f1' : '2px solid #d1d5db',
                                backgroundColor: isSelected ? '#6366f1' : 'white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                fontSize: '12px',
                                color: 'white',
                                fontWeight: 'bold'
                              }}>
                                {isSelected && '✓'}
                              </div>
                              <span style={{ fontSize: '13px', fontWeight: isSelected ? '600' : '400' }}>{staff.name}</span>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                  <div className="admin-edit-field customer-search-field">
                    <label>Müşteri</label>
                    <div className="customer-input-wrapper">
                      <input
                        type="text"
                        value={adminCustomerSearch || adminEditData.customer_name}
                        onChange={(e) => searchAdminCustomer(e.target.value)}
                        onFocus={() => loadAdminCustomerList()}
                        onBlur={() => setTimeout(() => setShowAdminCustomerDropdown(false), 200)}
                        placeholder="Müşteri seçmek için tıklayın veya arayın..."
                      />
                      {adminEditData.customer_id && (
                        <button type="button" className="clear-customer-btn" onClick={clearAdminCustomer}>×</button>
                      )}
                    </div>
                    {showAdminCustomerDropdown && adminCustomerResults.length > 0 && (
                      <div className="customer-dropdown">
                        {adminCustomerResults.map(customer => (
                          <div
                            key={customer.id}
                            className="customer-option"
                            onClick={() => selectAdminCustomer(customer)}
                          >
                            <span className="customer-name">{customer.name}</span>
                            <span className="customer-phone">{customer.phone}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="admin-edit-field full-width">
                  <label>Notlar</label>
                  <textarea
                    value={adminEditData.notes}
                    onChange={(e) => setAdminEditData({...adminEditData, notes: e.target.value})}
                    placeholder="Satış ile ilgili notlar..."
                    rows={2}
                  />
                </div>

                {/* Ödeme Detayları Düzenleme */}
                <div className="admin-edit-field full-width">
                  <label>Ödeme Detayları</label>
                  <div className="payment-details-edit-section">
                    {adminEditData.payment_details && adminEditData.payment_details.length > 0 ? (
                      <div className="payment-details-edit-list">
                        {adminEditData.payment_details.map((payment, idx) => (
                          <div key={idx} className="payment-detail-edit-row">
                            <select
                              value={payment.method || ''}
                              onChange={(e) => {
                                const updated = [...adminEditData.payment_details];
                                updated[idx] = { ...updated[idx], method: e.target.value };
                                setAdminEditData({ ...adminEditData, payment_details: updated });
                              }}
                              className="payment-method-select"
                            >
                              {editingSale._isRefund ? (
                                <>
                                  <option value="Nakit İade">Nakit İade</option>
                                  <option value="Kredi Kartı">Kredi Kartı İade</option>
                                  <option value="Havale İade">Havale İade</option>
                                  <option value="Açık Hesap">Açık Hesap (Borçtan Düş)</option>
                                  <option value="Açık Hesap (Gram)">Açık Hesap Gram</option>
                                  <option value="Açık Hesap (Gümüş)">Açık Hesap Gümüş</option>
                                </>
                              ) : (
                                <>
                                  <option value="Nakit">Nakit</option>
                                  <option value="Kredi Kartı">Kredi Kartı</option>
                                  <option value="Havale/EFT">Havale/EFT</option>
                                  <option value="Sanal Pos">Sanal Pos</option>
                                  <option value="Açık Hesap (TL)">Açık Hesap (₺)</option>
                                  <option value="Açık Hesap (Gram)">Açık Hesap (Gram)</option>
                                  <option value="Açık Hesap (Gümüş)">Açık Hesap (Gümüş)</option>
                                </>
                              )}
                              <option value="Hurda Borca Al">Hurda Borca Al</option>
                            </select>
                            <select
                              value={payment.currency || 'TRY'}
                              onChange={(e) => {
                                const updated = [...adminEditData.payment_details];
                                updated[idx] = { ...updated[idx], currency: e.target.value };
                                setAdminEditData({ ...adminEditData, payment_details: updated });
                              }}
                              className="payment-currency-select"
                            >
                              <option value="TRY">TL (₺)</option>
                              <option value="USD">USD ($)</option>
                              <option value="EUR">EUR (€)</option>
                              <option value="GOLD">Altın (gr)</option>
                              <option value="SILVER">Gümüş (gr)</option>
                            </select>
                            {(payment.method === 'Havale/EFT' || payment.method === 'Havale İade') && (
                              <select
                                value={payment.bank_account || payment.bankAccount || ''}
                                onChange={(e) => {
                                  const updated = [...adminEditData.payment_details];
                                  updated[idx] = { ...updated[idx], bank_account: e.target.value, bankAccount: e.target.value };
                                  setAdminEditData({ ...adminEditData, payment_details: updated });
                                }}
                                className="payment-currency-select"
                                style={{ minWidth: '120px' }}
                              >
                                <option value="">Hesap Seç</option>
                                {bankAccounts.map(acc => (
                                  <option key={acc.id} value={`${acc.account_name} (${acc.account_type === 'company' ? 'Şirket' : acc.account_type === 'ecommerce' ? 'E-Ticaret' : 'Şahsi'})`}>
                                    {acc.account_name}
                                  </option>
                                ))}
                              </select>
                            )}
                            <PriceInput
                              value={payment.amount || ''}
                              onChange={(val) => {
                                const updated = [...adminEditData.payment_details];
                                const newAmount = val || 0;
                                const updateObj = { ...updated[idx], amount: newAmount };
                                // TRY para biriminde amount_tl'yi de otomatik güncelle
                                if (!updated[idx].currency || updated[idx].currency === 'TRY') {
                                  updateObj.amount_tl = newAmount;
                                }
                                updated[idx] = updateObj;
                                setAdminEditData({ ...adminEditData, payment_details: updated });
                              }}
                              placeholder="Tutar"
                              className="payment-amount-input"
                              hideText
                            />
                            {payment.currency && payment.currency !== 'TRY' && (
                              <PriceInput
                                value={payment.amount_tl || ''}
                                onChange={(val) => {
                                  const updated = [...adminEditData.payment_details];
                                  updated[idx] = { ...updated[idx], amount_tl: val || 0 };
                                  setAdminEditData({ ...adminEditData, payment_details: updated });
                                }}
                                placeholder="TL Karşılığı"
                                className="payment-tl-input"
                                hideText
                              />
                            )}
                            <button
                              type="button"
                              className="remove-payment-btn"
                              onClick={() => {
                                const updated = adminEditData.payment_details.filter((_, i) => i !== idx);
                                setAdminEditData({ ...adminEditData, payment_details: updated });
                              }}
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="no-payment-details">Ödeme detayı bulunmuyor</p>
                    )}
                    <button
                      type="button"
                      className="add-payment-btn"
                      onClick={() => {
                        const newPayment = { method: 'Nakit', currency: 'TRY', amount: 0, amount_tl: 0 };
                        setAdminEditData({
                          ...adminEditData,
                          payment_details: [...(adminEditData.payment_details || []), newPayment]
                        });
                      }}
                    >
                      + Ödeme Ekle
                    </button>
                  </div>
                </div>
              </div>

              {/* Ürünler */}
              <div className="admin-edit-section">
                <h4>{editingSale._isRefund ? 'Satılan Ürünler' : 'Ürünler'}</h4>

                {editingSale._isRefund ? (
                  <>
                    {/* İade düzenleme: orijinal satış ürünleri checkbox listesi */}
                    <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '12px' }}>
                      İade almak istediğiniz ürünleri seçin, adedini ve iade fiyatını belirleyin.
                    </div>
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: '8px',
                      padding: '8px 8px', background: '#e2e8f0', borderRadius: '8px',
                      fontSize: '0.72rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase',
                      letterSpacing: '0.3px', marginBottom: '8px'
                    }}>
                      <div style={{ width: '18px', flexShrink: 0 }}></div>
                      <div style={{ width: '56px', flexShrink: 0 }}></div>
                      <div style={{ flex: 1, minWidth: 0 }}>Ürün</div>
                      <div style={{ width: '60px', textAlign: 'center', flexShrink: 0 }}>Satılan</div>
                      <div style={{ width: '120px', textAlign: 'center', flexShrink: 0 }}>İade Adedi</div>
                      <div style={{ width: '110px', textAlign: 'center', flexShrink: 0 }}>İade Fiyatı</div>
                      <div style={{ width: '90px', textAlign: 'right', flexShrink: 0 }}>Toplam</div>
                    </div>
                    {refundEditItems.map((item, idx) => (
                      <div key={item.product_id} style={{
                        display: 'flex', alignItems: 'center', gap: '8px',
                        padding: '10px 8px', borderBottom: '1px solid #f1f5f9',
                        opacity: item.maxRefundable <= 0 && !item.checked ? 0.5 : 1
                      }}>
                        <input
                          type="checkbox"
                          checked={item.checked}
                          disabled={item.maxRefundable <= 0 && !item.checked}
                          onChange={(e) => {
                            const updated = [...refundEditItems];
                            updated[idx] = {
                              ...updated[idx],
                              checked: e.target.checked,
                              refundQty: e.target.checked ? (updated[idx].refundQty || 1) : 0
                            };
                            syncRefundEditToItems(updated);
                          }}
                          style={{ width: '18px', height: '18px', cursor: 'pointer', flexShrink: 0 }}
                        />
                        {item.image ? (
                          <img src={`${IMAGE_HOST}${item.image}`} alt="" onClick={(e) => { e.stopPropagation(); openImagePopup(item.image, item.product_name || item.name); }} style={{ width: '56px', height: '56px', objectFit: 'cover', borderRadius: '8px', flexShrink: 0, border: '1px solid #e2e8f0', cursor: 'pointer' }} />
                        ) : (
                          <div style={{ width: '56px', height: '56px', borderRadius: '8px', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, border: '1px solid #e2e8f0', color: '#94a3b8', fontSize: '20px' }}>📦</div>
                        )}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: '500', fontSize: '14px' }}>
                            {item.product_name}
                          </div>
                          {item.barcode && (
                            <div style={{ fontSize: '11px', color: '#94a3b8' }}>{item.barcode}</div>
                          )}
                          <div style={{ fontSize: '11px', color: '#64748b' }}>
                            Satış fiyatı: ₺{item.originalUnitPrice.toLocaleString('tr-TR', {minimumFractionDigits: 2})}
                          </div>
                        </div>
                        <div style={{ width: '60px', textAlign: 'center', fontSize: '14px', fontWeight: '500', flexShrink: 0 }}>
                          {item.soldQuantity}
                        </div>
                        <div style={{ width: '120px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', flexShrink: 0 }}>
                          <button
                            className="qty-btn"
                            disabled={!item.checked || item.refundQty <= 1}
                            onClick={() => {
                              const updated = [...refundEditItems];
                              updated[idx] = { ...updated[idx], refundQty: Math.max(1, updated[idx].refundQty - 1) };
                              syncRefundEditToItems(updated);
                            }}
                          >−</button>
                          <input
                            type="number"
                            min="1"
                            max={item.maxRefundable}
                            value={item.checked ? item.refundQty : 0}
                            disabled={!item.checked}
                            onChange={(e) => {
                              const val = Math.min(Math.max(1, parseInt(e.target.value) || 1), item.maxRefundable);
                              const updated = [...refundEditItems];
                              updated[idx] = { ...updated[idx], refundQty: val };
                              syncRefundEditToItems(updated);
                            }}
                            style={{ width: '50px', textAlign: 'center', padding: '4px', border: '1px solid #d1d5db', borderRadius: '6px' }}
                          />
                          <button
                            className="qty-btn"
                            disabled={!item.checked || item.refundQty >= item.maxRefundable}
                            onClick={() => {
                              const updated = [...refundEditItems];
                              updated[idx] = { ...updated[idx], refundQty: Math.min(updated[idx].refundQty + 1, updated[idx].maxRefundable) };
                              syncRefundEditToItems(updated);
                            }}
                          >+</button>
                        </div>
                        <div style={{ width: '110px', flexShrink: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                            <span style={{ fontSize: '13px', color: '#64748b' }}>₺</span>
                            <PriceInput
                              value={item.refundPrice}
                              disabled={!item.checked}
                              onChange={(val) => {
                                const updated = [...refundEditItems];
                                updated[idx] = { ...updated[idx], refundPrice: val || 0 };
                                syncRefundEditToItems(updated);
                              }}
                              style={{ width: '100%', padding: '4px 6px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px' }}
                              hideText
                            />
                          </div>
                        </div>
                        <div style={{ width: '90px', textAlign: 'right', fontWeight: '600', fontSize: '14px', flexShrink: 0 }}>
                          {item.checked ? `₺${(item.refundQty * item.refundPrice).toLocaleString('tr-TR', {minimumFractionDigits: 2})}` : '-'}
                        </div>
                      </div>
                    ))}
                  </>
                ) : (
                  <>
                    {/* Satış düzenleme: mevcut UI */}
                    <div className="admin-product-search-wrapper">
                      <input
                        type="text"
                        value={adminProductSearch}
                        onChange={(e) => searchAdminProduct(e.target.value)}
                        onKeyDown={handleAdminProductKeyDown}
                        placeholder="Barkod veya ürün adı girin, Enter'a basın..."
                        className="admin-product-search-input"
                      />
                      {showAdminProductDropdown && adminProductResults.length > 0 && (
                        <div className="admin-product-dropdown">
                          {adminProductResults.map(product => (
                            <div
                              key={product.id}
                              className="admin-product-option"
                              onClick={() => addAdminProduct(product)}
                            >
                              <span className="product-name">{product.name}</span>
                              <span className="product-barcode">{product.barcode}</span>
                              <span className="product-price">
                                {product.currency === 'USD' ? `$${Number(product.sell_price || 0).toLocaleString('tr-TR', { maximumFractionDigits: 2 })} (₺${(Number(product.sell_price || 0) * getSatisRate('USD')).toLocaleString('tr-TR', { maximumFractionDigits: 2 })})` :
                                 product.currency === 'EUR' ? `€${Number(product.sell_price || 0).toLocaleString('tr-TR', { maximumFractionDigits: 2 })} (₺${(Number(product.sell_price || 0) * getSatisRate('EUR')).toLocaleString('tr-TR', { maximumFractionDigits: 2 })})` :
                                 `₺${Number(product.sell_price || product.price || 0).toLocaleString('tr-TR', { maximumFractionDigits: 2 })}`}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="edit-items-header">
                      <span className="col-name">Ürün</span>
                      <span className="col-qty">Adet</span>
                      <span className="col-price">Liste Fiyatı</span>
                      <span className="col-discount">İndirim</span>
                      <span className="col-price">Satış Fiyatı</span>
                      <span className="col-total">Toplam</span>
                      <span className="col-action"></span>
                    </div>
                    {editingItems.map(item => (
                      <div key={item.id} className={`edit-sale-item ${item.isNew ? 'new-item' : ''}`}>
                        <div className="edit-item-name" style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '10px' }}>
                          {item.image ? (
                            <img src={`${IMAGE_HOST}${item.image}`} alt="" onClick={(e) => { e.stopPropagation(); openImagePopup(item.image, item.product_name || item.name); }} style={{ width: '56px', height: '56px', objectFit: 'cover', borderRadius: '8px', flexShrink: 0, border: '1px solid #e2e8f0', cursor: 'pointer' }} />
                          ) : (
                            <div style={{ width: '56px', height: '56px', borderRadius: '8px', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, border: '1px solid #e2e8f0', color: '#94a3b8', fontSize: '20px' }}>📦</div>
                          )}
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
                            <span>{item.product_name} {item.barcode && <span style={{ color: '#64748b', fontSize: '11px' }}>({item.barcode})</span>} {item.isNew && <span className="new-badge">YENİ</span>}</span>
                            {item.currentStock !== undefined && (
                              <span style={{ color: '#6366f1', fontSize: '11px' }}>
                                Stokta: {item.currentStock} adet | Maks: {item.maxQuantity}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="edit-item-qty">
                          <button className="qty-btn" onClick={() => updateEditItemQuantity(item.id, item.newQuantity - 1)}>−</button>
                          <input
                            type="number"
                            min="1"
                            value={item.newQuantity}
                            onChange={(e) => updateEditItemQuantity(item.id, parseInt(e.target.value) || 1)}
                          />
                          <button className="qty-btn" onClick={() => updateEditItemQuantity(item.id, item.newQuantity + 1)}>+</button>
                        </div>
                        <div className="edit-item-price">
                          <span className="currency">₺</span>
                          <PriceInput
                            value={item.newOriginalPrice || item.newUnitPrice}
                            onChange={(val) => updateEditItemOriginalPrice(item.id, val || 0)}
                            hideText
                          />
                        </div>
                        <div className="edit-item-discount" style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                            <span style={{ fontSize: '11px', color: '#64748b' }}>%</span>
                            <input
                              type="number"
                              min="0"
                              max="100"
                              step="1"
                              value={Math.round(item.newDiscountPercent || 0)}
                              onChange={(e) => updateEditItemDiscountPercent(item.id, parseFloat(e.target.value) || 0)}
                              style={{ width: '45px', padding: '4px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '12px', textAlign: 'center' }}
                            />
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                            <span style={{ fontSize: '11px', color: '#ef4444' }}>₺</span>
                            <PriceInput
                              value={Math.round((item.newDiscountAmount || 0) * 100) / 100}
                              onChange={(val) => updateEditItemDiscountAmount(item.id, val || 0)}
                              style={{ width: '65px', padding: '4px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '12px', textAlign: 'center', color: '#ef4444' }}
                              hideText
                            />
                          </div>
                        </div>
                        <div className="edit-item-price">
                          <span className="currency">₺</span>
                          <PriceInput
                            value={Math.round(item.newUnitPrice * 100) / 100}
                            onChange={(val) => updateEditItemPrice(item.id, val || 0)}
                            hideText
                          />
                        </div>
                        <div className="edit-item-total">
                          ₺{(item.newUnitPrice * item.newQuantity).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                        </div>
                        <div className="edit-item-action">
                          <button className="remove-item-btn" onClick={() => removeAdminProduct(item.id)} title="Ürünü Kaldır">
                            ×
                          </button>
                        </div>
                      </div>
                    ))}
                  </>
                )}

                {/* Fark kalemleri kaldırıldı */}
              </div>

              {/* Sepet İndirimi */}
              {!editingSale._isRefund && (
                <div style={{ padding: '12px 14px', background: '#fef2f2', borderRadius: '8px', border: '1px solid #fecaca', marginTop: '10px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                    <label style={{ fontWeight: 600, fontSize: '0.95rem', color: '#dc2626' }}>Sepet İndirimi</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <span style={{ color: '#dc2626', fontWeight: 500 }}>₺</span>
                      <PriceInput
                        value={editDiscount}
                        onChange={(val) => setEditDiscount(val)}
                        style={{ width: '130px', padding: '8px 12px', borderRadius: '8px', border: '2px solid #fca5a5', fontSize: '1rem', textAlign: 'right', fontWeight: 600, color: '#dc2626', background: '#fff' }}
                        hideText
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Toplam */}
              <div className="edit-sale-summary">
                {(() => {
                  const finalTotal = calculateEditTotal();
                  const originalSubtotal = editingItems.reduce((sum, item) => sum + ((item.newOriginalPrice || item.newUnitPrice) * item.newQuantity), 0);
                  const itemLevelDiscount = editingItems.reduce((sum, item) => sum + ((item.newDiscountAmount || 0) * item.newQuantity), 0);
                  const cartDiscountVal = parseFloat(editDiscount) || 0;
                  const totalDiscount = itemLevelDiscount + cartDiscountVal;
                  const scrapAmount = Number(editingSale.scrap_total_amount) || 0;
                  const scrapDebt = Number(editingSale.scrap_debt_discount) || 0;
                  const stockChanges = getStockChanges();
                  return (
                    <>
                      {!editingSale._isRefund && (
                        <div className="summary-row" style={{ color: '#64748b' }}>
                          <span>Ara Toplam (Liste):</span>
                          <span>₺{originalSubtotal.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</span>
                        </div>
                      )}
                      {!editingSale._isRefund && itemLevelDiscount > 0.5 && (
                        <div className="summary-row" style={{ color: '#ef4444' }}>
                          <span>Ürün İndirimleri:</span>
                          <span>-₺{itemLevelDiscount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</span>
                        </div>
                      )}
                      {!editingSale._isRefund && cartDiscountVal > 0.005 && (
                        <div className="summary-row" style={{ color: '#dc2626' }}>
                          <span>Sepet İndirimi:</span>
                          <span>-₺{cartDiscountVal.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</span>
                        </div>
                      )}
                      {!editingSale._isRefund && scrapAmount > 0 && (
                        <div className="summary-row" style={{ color: '#ef4444', fontSize: '0.85rem', paddingLeft: '12px' }}>
                          <span>Hurda Mahsubu:</span>
                          <span>-₺{scrapAmount.toLocaleString('tr-TR', { maximumFractionDigits: 2 })}</span>
                        </div>
                      )}
                      {!editingSale._isRefund && scrapDebt > 0 && (
                        <div className="summary-row" style={{ color: '#ef4444', fontSize: '0.85rem', paddingLeft: '12px' }}>
                          <span>Hurda Borç İndirimi:</span>
                          <span>-₺{scrapDebt.toLocaleString('tr-TR', { maximumFractionDigits: 2 })}</span>
                        </div>
                      )}
                      {/* Fark kalemleri kaldırıldı */}
                      <div className="summary-row original" style={{ fontWeight: 'bold', fontSize: '1.1rem', borderTop: '2px solid #e2e8f0', paddingTop: '8px', marginTop: '4px' }}>
                        <span>{editingSale._isRefund ? 'Toplam İade:' : 'Yeni Toplam:'}</span>
                        <span>₺{finalTotal.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</span>
                      </div>
                      {stockChanges.length > 0 && (
                        <div style={{
                          marginTop: '12px',
                          padding: '12px 16px',
                          background: '#f0f9ff',
                          border: '1px solid #bae6fd',
                          borderRadius: '8px'
                        }}>
                          <div style={{ fontSize: '13px', fontWeight: '600', color: '#0369a1', marginBottom: '6px' }}>Stok Değişiklikleri:</div>
                          {stockChanges.map((change, idx) => (
                            <div key={idx} style={{ fontSize: '12px', color: change.type === 'iade' ? '#16a34a' : '#dc2626', padding: '2px 0' }}>
                              {change.type === 'iade' ? '+' : '-'} {change.name}: {change.detail}
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
            <div className="modal-footer admin-edit-footer">
              {editingSale?.sale_type !== 'refund' && Number(editingSale?.total) >= 0 && (
              <button className="delete-sale-btn modal-btn" onClick={() => { setDeletingSale(editingSale); setShowDeleteConfirm(true); }}>
                <X size={16} /> Fiş Silme
              </button>
              )}
              <div className="footer-right">
                <button className="btn-cancel" onClick={closeAdminEditModal}>İptal</button>
                <button className="btn-save" onClick={saveAdminEdit} disabled={savingEdit}>
                  {savingEdit ? 'Kaydediliyor...' : 'Değişiklikleri Kaydet'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Borç Güncelleme Modalı (GRAM/GUMUS borçlar için) */}
      {showDebtUpdateModal && (
        <div className="modal-overlay" style={{ zIndex: 10010 }}>
          <div className="modal" style={{ zIndex: 10011, maxWidth: '450px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Metal Borç Güncelleme</h2>
              <button className="close-btn" onClick={() => { setShowDebtUpdateModal(false); setPendingAdminSave(null); }}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{ padding: '16px', background: '#fef3c7', borderRadius: '8px', marginBottom: '16px', fontSize: '13px', color: '#92400e' }}>
                <strong>Dikkat!</strong> Bu satışa bağlı {debtUpdateData.debt_type === 'GRAM' ? 'altın (gram)' : 'gümüş'} tipinde bir borç kaydı var.
                Satış tutarı değiştiği için borç bilgisini manuel olarak güncellemeniz gerekmektedir.
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '4px', color: '#374151' }}>
                    Borç Miktarı ({debtUpdateData.debt_type === 'GRAM' ? 'gram altın' : 'gram gümüş'})
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={debtUpdateData.amount}
                    onChange={(e) => setDebtUpdateData({ ...debtUpdateData, amount: e.target.value })}
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }}
                    placeholder="Örn: 5.25"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '4px', color: '#374151' }}>
                    {debtUpdateData.debt_type === 'GRAM' ? 'Altın' : 'Gümüş'} Kuru (₺/gram)
                  </label>
                  <PriceInput
                    value={debtUpdateData.gold_rate}
                    onChange={(val) => {
                      const rate = val || 0;
                      const amount = parseFloat(debtUpdateData.amount) || 0;
                      setDebtUpdateData({ ...debtUpdateData, gold_rate: val, amount_tl: (amount * rate).toFixed(2) });
                    }}
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }}
                    placeholder="Güncel kur"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '4px', color: '#374151' }}>
                    TL Karşılığı
                  </label>
                  <PriceInput
                    value={debtUpdateData.amount_tl}
                    onChange={(val) => setDebtUpdateData({ ...debtUpdateData, amount_tl: val })}
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }}
                    placeholder="TL karşılığı"
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer" style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button className="btn-cancel" onClick={() => { setShowDebtUpdateModal(false); setPendingAdminSave(null); }}>Vazgeç</button>
              <button
                className="btn-save"
                onClick={confirmDebtUpdateAndSave}
                disabled={!debtUpdateData.amount || !debtUpdateData.amount_tl}
              >
                Onayla ve Kaydet
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Değişim/İade Düzenleme Modal (Admin) */}
      {showExchangeEditModal && editingExchange && (
        <div className="modal-overlay" style={{ zIndex: 10002 }} key={`exchange-edit-${editingExchange.id}-${editModalKey}`}>
          <div className="modal admin-edit-sale-modal" style={{ zIndex: 10003 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                {editingExchange.type === 'refund' ? 'İade' : 'Değişim'} Düzenleme - #{editingExchange.id}
              </h2>
              <button className="close-btn" onClick={closeExchangeEditModal}>✕</button>
            </div>
            <div className="modal-body">
              <div className="admin-edit-section">
                <h4>{editingExchange.type === 'refund' ? 'İade' : 'Değişim'} Bilgileri</h4>
                <div className="admin-edit-grid">
                  <div className="admin-edit-field">
                    <label>Tarih & Saat</label>
                    <input
                      type="datetime-local"
                      value={exchangeEditData.created_at}
                      onChange={(e) => setExchangeEditData({...exchangeEditData, created_at: e.target.value})}
                    />
                  </div>
                  <div className="admin-edit-field">
                    <label>Ödeme Yöntemi</label>
                    <select
                      value={exchangeEditData.payment_method}
                      onChange={(e) => setExchangeEditData({...exchangeEditData, payment_method: e.target.value})}
                    >
                      <option value="">Seçiniz</option>
                      <option value="Nakit">Nakit</option>
                      <option value="Nakit İade">Nakit İade</option>
                      <option value="Kredi Kartı">Kredi Kartı</option>
                      <option value="Kredi Kartı İade">Kredi Kartı İade</option>
                      <option value="Havale/EFT">Havale/EFT</option>
                      <option value="Havale İade">Havale İade</option>
                      <option value="Fark Yok">Fark Yok</option>
                      <option value="Müşteri Lehine Fark">Müşteri Lehine Fark</option>
                      <option value="Mağaza Lehine Fark">Mağaza Lehine Fark</option>
                    </select>
                  </div>
                  <div className="admin-edit-field">
                    <label>İşlemi Yapan {exchangeEditData.staff_ids.length > 0 && `(${exchangeEditData.staff_ids.length})`}</label>
                    <div style={{
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      maxHeight: '120px',
                      overflowY: 'auto',
                      padding: '4px'
                    }}>
                      {salesStaffList.length === 0 ? (
                        <div style={{ padding: '8px', color: '#9ca3af', fontSize: '13px', textAlign: 'center' }}>
                          Personel bulunamadı
                        </div>
                      ) : (
                        salesStaffList.map(staff => {
                          const isSelected = exchangeEditData.staff_ids.includes(staff.id);
                          return (
                            <div
                              key={staff.id}
                              onClick={() => {
                                if (isSelected) {
                                  setExchangeEditData({
                                    ...exchangeEditData,
                                    staff_ids: exchangeEditData.staff_ids.filter(id => id !== staff.id),
                                    staff_names: exchangeEditData.staff_names.filter(n => n !== staff.name)
                                  });
                                } else {
                                  setExchangeEditData({
                                    ...exchangeEditData,
                                    staff_ids: [...exchangeEditData.staff_ids, staff.id],
                                    staff_names: [...exchangeEditData.staff_names, staff.name]
                                  });
                                }
                              }}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '6px 8px',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                backgroundColor: isSelected ? '#eef2ff' : 'transparent',
                                transition: 'background-color 0.15s'
                              }}
                            >
                              <div style={{
                                width: '18px',
                                height: '18px',
                                borderRadius: '4px',
                                border: isSelected ? '2px solid #6366f1' : '2px solid #d1d5db',
                                backgroundColor: isSelected ? '#6366f1' : 'white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                fontSize: '12px',
                                color: 'white',
                                fontWeight: 'bold'
                              }}>
                                {isSelected && '✓'}
                              </div>
                              <span style={{ fontSize: '13px', fontWeight: isSelected ? '600' : '400' }}>{staff.name}</span>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                  <div className="admin-edit-field customer-search-field">
                    <label>Müşteri</label>
                    <div className="customer-input-wrapper">
                      <input
                        type="text"
                        value={adminCustomerSearch || exchangeEditData.customer_name}
                        onChange={(e) => searchAdminCustomer(e.target.value)}
                        onFocus={() => loadAdminCustomerList()}
                        onBlur={() => setTimeout(() => setShowAdminCustomerDropdown(false), 200)}
                        placeholder="Müşteri seçmek için tıklayın..."
                      />
                      {exchangeEditData.customer_id && (
                        <button type="button" className="clear-customer-btn" onClick={() => setExchangeEditData({...exchangeEditData, customer_id: null, customer_name: ''})}>×</button>
                      )}
                    </div>
                    {showAdminCustomerDropdown && adminCustomerResults.length > 0 && (
                      <div className="customer-dropdown">
                        {adminCustomerResults.map(customer => (
                          <div
                            key={customer.id}
                            className="customer-option"
                            onClick={() => {
                              setExchangeEditData({
                                ...exchangeEditData,
                                customer_id: customer.id,
                                customer_name: customer.name
                              });
                              setAdminCustomerSearch('');
                              setShowAdminCustomerDropdown(false);
                            }}
                          >
                            <span className="customer-name">{customer.name}</span>
                            <span className="customer-phone">{customer.phone}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="admin-edit-field full-width">
                  <label>Notlar</label>
                  <textarea
                    value={exchangeEditData.notes}
                    onChange={(e) => setExchangeEditData({...exchangeEditData, notes: e.target.value})}
                    placeholder="İşlem ile ilgili notlar..."
                    rows={2}
                  />
                </div>

                {/* Ödeme Detayları Düzenleme (satış düzenleme modalı ile aynı) */}
                <div className="admin-edit-field full-width">
                  <label>Ödeme Detayları</label>
                  <div className="payment-details-edit-section">
                    {exchangeEditData.payment_details && exchangeEditData.payment_details.length > 0 ? (
                      <div className="payment-details-edit-list">
                        {exchangeEditData.payment_details.map((payment, idx) => (
                          <div key={idx} className="payment-detail-edit-row">
                            <select
                              value={payment.method || ''}
                              onChange={(e) => {
                                const updated = [...exchangeEditData.payment_details];
                                updated[idx] = { ...updated[idx], method: e.target.value };
                                setExchangeEditData({ ...exchangeEditData, payment_details: updated });
                              }}
                              className="payment-method-select"
                            >
                              {editingExchange.type === 'refund' ? (
                                <>
                                  <option value="Nakit İade">Nakit İade</option>
                                  <option value="Kredi Kartı İade">Kredi Kartı İade</option>
                                  <option value="Havale İade">Havale İade</option>
                                  <option value="Açık Hesap">Açık Hesap (Borçtan Düş)</option>
                                  <option value="Açık Hesap (Gram)">Açık Hesap Gram</option>
                                  <option value="Açık Hesap (Gümüş)">Açık Hesap Gümüş</option>
                                  <option value="Müşteri Bakiyesi">Müşteri Bakiyesi</option>
                                  <option value="Hurda Borca Al">Hurda Borca Al</option>
                                </>
                              ) : (
                                <>
                                  <option value="Nakit">Nakit</option>
                                  <option value="Kredi Kartı">Kredi Kartı</option>
                                  <option value="Havale/EFT">Havale/EFT</option>
                                  <option value="Sanal Pos">Sanal Pos</option>
                                  <option value="Açık Hesap (TL)">Açık Hesap (₺)</option>
                                  <option value="Açık Hesap (Gram)">Açık Hesap (Gram)</option>
                                  <option value="Açık Hesap (Gümüş)">Açık Hesap (Gümüş)</option>
                                  <option value="Hurda Borca Al">Hurda Borca Al</option>
                                </>
                              )}
                            </select>
                            <select
                              value={payment.currency || 'TRY'}
                              onChange={(e) => {
                                const updated = [...exchangeEditData.payment_details];
                                updated[idx] = { ...updated[idx], currency: e.target.value };
                                setExchangeEditData({ ...exchangeEditData, payment_details: updated });
                              }}
                              className="payment-currency-select"
                            >
                              <option value="TRY">TL (₺)</option>
                              <option value="USD">USD ($)</option>
                              <option value="EUR">EUR (€)</option>
                              <option value="GOLD">Altın (gr)</option>
                              <option value="SILVER">Gümüş (gr)</option>
                            </select>
                            {(payment.method === 'Havale/EFT' || payment.method === 'Havale İade') && (
                              <select
                                value={payment.bank_account || payment.bankAccount || ''}
                                onChange={(e) => {
                                  const updated = [...exchangeEditData.payment_details];
                                  updated[idx] = { ...updated[idx], bank_account: e.target.value, bankAccount: e.target.value };
                                  setExchangeEditData({ ...exchangeEditData, payment_details: updated });
                                }}
                                className="payment-currency-select"
                                style={{ minWidth: '120px' }}
                              >
                                <option value="">Hesap Seç</option>
                                {bankAccounts.map(acc => (
                                  <option key={acc.id} value={`${acc.account_name} (${acc.account_type === 'company' ? 'Şirket' : acc.account_type === 'ecommerce' ? 'E-Ticaret' : 'Şahsi'})`}>
                                    {acc.account_name}
                                  </option>
                                ))}
                              </select>
                            )}
                            <PriceInput
                              value={payment.amount || ''}
                              onChange={(val) => {
                                const updated = [...exchangeEditData.payment_details];
                                const newAmount = val || 0;
                                const updateObj = { ...updated[idx], amount: newAmount };
                                if (!updated[idx].currency || updated[idx].currency === 'TRY') {
                                  updateObj.amount_tl = newAmount;
                                }
                                updated[idx] = updateObj;
                                setExchangeEditData({ ...exchangeEditData, payment_details: updated });
                              }}
                              placeholder="Tutar"
                              className="payment-amount-input"
                              hideText
                            />
                            {payment.currency && payment.currency !== 'TRY' && (
                              <PriceInput
                                value={payment.amount_tl || ''}
                                onChange={(val) => {
                                  const updated = [...exchangeEditData.payment_details];
                                  updated[idx] = { ...updated[idx], amount_tl: val || 0 };
                                  setExchangeEditData({ ...exchangeEditData, payment_details: updated });
                                }}
                                placeholder="TL Karşılığı"
                                className="payment-tl-input"
                                hideText
                              />
                            )}
                            <button
                              type="button"
                              className="remove-payment-btn"
                              onClick={() => {
                                const updated = exchangeEditData.payment_details.filter((_, i) => i !== idx);
                                setExchangeEditData({ ...exchangeEditData, payment_details: updated });
                              }}
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="no-payment-details">Ödeme detayı bulunmuyor</p>
                    )}
                    <button
                      type="button"
                      className="add-payment-btn"
                      onClick={() => {
                        const defaultMethod = editingExchange.type === 'refund' ? 'Nakit İade' : 'Nakit';
                        const newPayment = { method: defaultMethod, currency: 'TRY', amount: 0, amount_tl: 0 };
                        setExchangeEditData({
                          ...exchangeEditData,
                          payment_details: [...(exchangeEditData.payment_details || []), newPayment]
                        });
                      }}
                    >
                      + Ödeme Ekle
                    </button>
                    {(() => {
                      const totalPaid = (exchangeEditData.payment_details || []).reduce((sum, p) => sum + (parseFloat(p.amount_tl || p.amount) || 0), 0);
                      const expectedTotal = editingExchange.type === 'refund'
                        ? Math.abs(calculateReturnTotal())
                        : Math.abs(calculateNewItemsTotal() - calculateReturnTotal());
                      const diff = totalPaid - expectedTotal;
                      return (
                        <div style={{
                          marginTop: '8px',
                          padding: '8px 12px',
                          background: Math.abs(diff) < 0.01 ? '#f0fdf4' : '#fef3c7',
                          border: `1px solid ${Math.abs(diff) < 0.01 ? '#bbf7d0' : '#fde68a'}`,
                          borderRadius: '6px',
                          fontSize: '12px',
                          color: Math.abs(diff) < 0.01 ? '#166534' : '#92400e',
                          display: 'flex',
                          justifyContent: 'space-between',
                          gap: '12px'
                        }}>
                          <span>Ödeme Toplamı: ₺{totalPaid.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                          <span>Beklenen: ₺{expectedTotal.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                          {Math.abs(diff) >= 0.01 && (
                            <span>Fark: {diff > 0 ? '+' : ''}₺{diff.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>

              {/* İade Edilen Ürünler (Düzenlenebilir) */}
              {editingReturnItems && editingReturnItems.length > 0 && (
                <div className="admin-edit-section">
                  <h4 style={{ color: '#ef4444' }}>İade Edilen Ürünler</h4>
                  <div className="edit-items-header" style={{ display: 'grid', gridTemplateColumns: '56px 2fr 80px 120px 100px', gap: '8px', padding: '8px 12px', backgroundColor: '#f8fafc', borderRadius: '6px', marginBottom: '8px', fontSize: '12px', fontWeight: '600', color: '#64748b' }}>
                    <span></span>
                    <span>Ürün</span>
                    <span style={{ textAlign: 'center' }}>Adet</span>
                    <span style={{ textAlign: 'center' }}>Birim Fiyat</span>
                    <span style={{ textAlign: 'right' }}>Toplam</span>
                  </div>
                  <div className="exchange-items-list">
                    {editingReturnItems.map((item, idx) => (
                      <div key={idx} style={{
                        display: 'grid',
                        gridTemplateColumns: '56px 2fr 80px 120px 100px',
                        gap: '8px',
                        alignItems: 'center',
                        padding: '10px 12px',
                        backgroundColor: '#fef2f2',
                        borderRadius: '6px',
                        marginBottom: '6px',
                        border: '1px solid #fecaca'
                      }}>
                        {item.image ? (
                          <img src={`${IMAGE_HOST}${item.image}`} alt="" onClick={(e) => { e.stopPropagation(); openImagePopup(item.image, item.product_name); }} style={{ width: '56px', height: '56px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #fecaca', cursor: 'pointer' }} />
                        ) : (
                          <div style={{ width: '56px', height: '56px', borderRadius: '8px', background: '#fff5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #fecaca', color: '#f87171', fontSize: '20px' }}>📦</div>
                        )}
                        <span style={{ fontWeight: '500' }}>{item.product_name} {item.barcode && <span style={{ color: '#64748b', fontSize: '11px', fontWeight: 'normal' }}>({item.barcode})</span>}</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <button
                            onClick={() => updateReturnItemQuantity(item.product_id, item.newQuantity - 1)}
                            style={{ width: '24px', height: '24px', border: '1px solid #fecaca', borderRadius: '4px', background: 'white', cursor: 'pointer' }}
                          >−</button>
                          <input
                            type="number"
                            min="1"
                            value={item.newQuantity}
                            onChange={(e) => updateReturnItemQuantity(item.product_id, parseInt(e.target.value) || 1)}
                            style={{ width: '40px', textAlign: 'center', border: '1px solid #fecaca', borderRadius: '4px', padding: '2px' }}
                          />
                          <button
                            onClick={() => updateReturnItemQuantity(item.product_id, item.newQuantity + 1)}
                            style={{ width: '24px', height: '24px', border: '1px solid #fecaca', borderRadius: '4px', background: 'white', cursor: 'pointer' }}
                          >+</button>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                          <span style={{ color: '#64748b' }}>₺</span>
                          <PriceInput
                            value={item.newUnitPrice}
                            onChange={(val) => updateReturnItemPrice(item.product_id, val || 0)}
                            style={{ width: '90px', border: '1px solid #fecaca', borderRadius: '4px', padding: '4px 6px', textAlign: 'right' }}
                            hideText
                          />
                        </div>
                        <span style={{ color: '#ef4444', fontWeight: 'bold', textAlign: 'right' }}>
                          -₺{(item.newUnitPrice * item.newQuantity).toLocaleString('tr-TR', { maximumFractionDigits: 2 })}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Yeni Alınan Ürünler (Düzenlenebilir - Değişim için) */}
              {editingNewItems && editingNewItems.length > 0 && (
                <div className="admin-edit-section">
                  <h4 style={{ color: '#16a34a' }}>Yeni Alınan Ürünler</h4>
                  <div className="edit-items-header" style={{ display: 'grid', gridTemplateColumns: '56px 2fr 80px 120px 100px', gap: '8px', padding: '8px 12px', backgroundColor: '#f8fafc', borderRadius: '6px', marginBottom: '8px', fontSize: '12px', fontWeight: '600', color: '#64748b' }}>
                    <span></span>
                    <span>Ürün</span>
                    <span style={{ textAlign: 'center' }}>Adet</span>
                    <span style={{ textAlign: 'center' }}>Birim Fiyat</span>
                    <span style={{ textAlign: 'right' }}>Toplam</span>
                  </div>
                  <div className="exchange-items-list">
                    {editingNewItems.map((item, idx) => (
                      <div key={idx} style={{
                        display: 'grid',
                        gridTemplateColumns: '56px 2fr 80px 120px 100px',
                        gap: '8px',
                        alignItems: 'center',
                        padding: '10px 12px',
                        backgroundColor: '#f0fdf4',
                        borderRadius: '6px',
                        marginBottom: '6px',
                        border: '1px solid #bbf7d0'
                      }}>
                        {item.image ? (
                          <img src={`${IMAGE_HOST}${item.image}`} alt="" onClick={(e) => { e.stopPropagation(); openImagePopup(item.image, item.product_name); }} style={{ width: '56px', height: '56px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #bbf7d0', cursor: 'pointer' }} />
                        ) : (
                          <div style={{ width: '56px', height: '56px', borderRadius: '8px', background: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #bbf7d0', color: '#86efac', fontSize: '20px' }}>📦</div>
                        )}
                        <span style={{ fontWeight: '500' }}>{item.product_name} {item.barcode && <span style={{ color: '#64748b', fontSize: '11px', fontWeight: 'normal' }}>({item.barcode})</span>}</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <button
                            onClick={() => updateNewItemQuantity(item.product_id, item.newQuantity - 1)}
                            style={{ width: '24px', height: '24px', border: '1px solid #bbf7d0', borderRadius: '4px', background: 'white', cursor: 'pointer' }}
                          >−</button>
                          <input
                            type="number"
                            min="1"
                            value={item.newQuantity}
                            onChange={(e) => updateNewItemQuantity(item.product_id, parseInt(e.target.value) || 1)}
                            style={{ width: '40px', textAlign: 'center', border: '1px solid #bbf7d0', borderRadius: '4px', padding: '2px' }}
                          />
                          <button
                            onClick={() => updateNewItemQuantity(item.product_id, item.newQuantity + 1)}
                            style={{ width: '24px', height: '24px', border: '1px solid #bbf7d0', borderRadius: '4px', background: 'white', cursor: 'pointer' }}
                          >+</button>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                          <span style={{ color: '#64748b' }}>₺</span>
                          <PriceInput
                            value={item.newUnitPrice}
                            onChange={(val) => updateNewItemPrice(item.product_id, val || 0)}
                            style={{ width: '90px', border: '1px solid #bbf7d0', borderRadius: '4px', padding: '4px 6px', textAlign: 'right' }}
                            hideText
                          />
                        </div>
                        <span style={{ color: '#16a34a', fontWeight: 'bold', textAlign: 'right' }}>
                          +₺{(item.newUnitPrice * item.newQuantity).toLocaleString('tr-TR', { maximumFractionDigits: 2 })}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Özet (Dinamik Hesaplama) */}
              <div className="edit-sale-summary">
                <div className="summary-row">
                  <span>İade Toplamı:</span>
                  <span style={{ color: '#ef4444' }}>-₺{calculateReturnTotal().toLocaleString('tr-TR', { maximumFractionDigits: 2 })}</span>
                </div>
                {editingNewItems.length > 0 && (
                  <div className="summary-row">
                    <span>Yeni Ürünler:</span>
                    <span style={{ color: '#16a34a' }}>+₺{calculateNewItemsTotal().toLocaleString('tr-TR', { maximumFractionDigits: 2 })}</span>
                  </div>
                )}
                <div className="summary-row new">
                  <span>Fark:</span>
                  <strong style={{ color: (calculateNewItemsTotal() - calculateReturnTotal()) >= 0 ? '#ef4444' : '#16a34a' }}>
                    {(calculateNewItemsTotal() - calculateReturnTotal()) >= 0 ? '+' : ''}₺{(calculateNewItemsTotal() - calculateReturnTotal()).toLocaleString('tr-TR', { maximumFractionDigits: 2 })}
                  </strong>
                </div>
              </div>
            </div>
            <div className="modal-footer admin-edit-footer">
              <div className="footer-right" style={{ marginLeft: 'auto' }}>
                <button className="btn-cancel" onClick={closeExchangeEditModal}>İptal</button>
                <button className="btn-save" onClick={saveExchangeEdit} disabled={savingExchangeEdit}>
                  {savingExchangeEdit ? 'Kaydediliyor...' : 'Değişiklikleri Kaydet'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Fiş Silme Onay Modal */}
      {showDeleteConfirm && (
        <div className="modal-overlay" style={{ zIndex: 99999 }}>
          <div className="modal delete-confirm-modal" onClick={e => e.stopPropagation()} style={{
            maxWidth: '450px',
            borderRadius: '16px',
            overflow: 'hidden'
          }}>
            <div className="modal-header delete-header" style={{
              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              color: 'white',
              padding: '20px 24px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <AlertTriangle size={28} />
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>Fiş Silme Onayı</h2>
            </div>
            <div className="modal-body" style={{ padding: '24px' }}>
              <p style={{ margin: '0 0 16px 0', fontSize: '15px', color: '#374151' }}>
                <strong>Fiş #{deletingSale?.id}</strong> numaralı kaydı iptal etmek üzeresiniz.
              </p>
              {deletingSale?.refund_of_sale_id ? (
                <p style={{
                  margin: 0,
                  padding: '12px',
                  background: '#fef3c7',
                  borderRadius: '8px',
                  fontSize: '14px',
                  color: '#92400e',
                  lineHeight: '1.5'
                }}>
                  Bu bir iade fişidir. Stoklar güncellenmeyecektir. İşlemi onaylıyor musunuz?
                </p>
              ) : (
                <p style={{
                  margin: 0,
                  padding: '12px',
                  background: '#d1fae5',
                  borderRadius: '8px',
                  fontSize: '14px',
                  color: '#065f46',
                  lineHeight: '1.5'
                }}>
                  Satılan ürünler stoğa geri eklenecektir. İşlemi onaylıyor musunuz?
                </p>
              )}
            </div>
            <div className="modal-footer" style={{
              padding: '16px 24px',
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end',
              borderTop: '1px solid #e5e7eb'
            }}>
              <button
                className="btn-cancel"
                onClick={() => { setShowDeleteConfirm(false); setDeletingSale(null); }}
                style={{ padding: '10px 20px', borderRadius: '8px' }}
              >
                Vazgeç
              </button>
              <button
                className="btn-delete-confirm"
                onClick={() => deleteSale(deletingSale?.id)}
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  background: '#ef4444',
                  color: 'white',
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <X size={16} /> Evet, İptal Et
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Genel Onay Modal (window.confirm yerine) */}
      {showConfirmModal && (
        <div className="modal-overlay" style={{ zIndex: 99999 }}>
          <div className="modal confirm-modal" onClick={e => e.stopPropagation()} style={{
            maxWidth: '420px',
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            <div className="modal-header" style={{
              background: confirmModalConfig.type === 'danger' ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' :
                         confirmModalConfig.type === 'info' ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' :
                         'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              color: 'white',
              padding: '20px 24px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <AlertTriangle size={28} />
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>{confirmModalConfig.title}</h2>
            </div>
            <div className="modal-body" style={{ padding: '24px' }}>
              <p style={{
                margin: 0,
                fontSize: '15px',
                color: '#374151',
                lineHeight: '1.6',
                whiteSpace: 'pre-wrap'
              }}>
                {confirmModalConfig.message}
              </p>
            </div>
            <div className="modal-footer" style={{
              padding: '16px 24px',
              background: '#f8fafc',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px',
              borderTop: '1px solid #e2e8f0'
            }}>
              <button
                onClick={() => {
                  setShowConfirmModal(false);
                  if (confirmModalConfig.onCancel) {
                    confirmModalConfig.onCancel();
                  }
                }}
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  border: '1px solid #d1d5db',
                  background: 'white',
                  color: '#374151',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                {confirmModalConfig.cancelText}
              </button>
              <button
                onClick={() => {
                  setShowConfirmModal(false);
                  if (confirmModalConfig.onConfirm) {
                    confirmModalConfig.onConfirm();
                  }
                }}
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  border: 'none',
                  background: confirmModalConfig.type === 'danger' ? '#ef4444' :
                             confirmModalConfig.type === 'info' ? '#3b82f6' : '#f59e0b',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                {confirmModalConfig.confirmText}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Fiş Yazdırma Seçim Modalı */}
      {showReceiptChoiceModal && receiptChoiceData && (
        <div className="modal-overlay" style={{ zIndex: 99999 }}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{
            maxWidth: '400px',
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            <div className="modal-header" style={{
              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
              color: 'white',
              padding: '20px 24px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <Receipt size={24} />
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>Fiş Yazdır</h2>
            </div>
            <div className="modal-body" style={{ padding: '20px 24px' }}>
              <p style={{ margin: '0 0 16px 0', fontSize: '14px', color: '#555' }}>
                Hangi fişi yazdırmak istersiniz?
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <button
                  onClick={() => {
                    setShowReceiptChoiceModal(false);
                    // 2 adet bilgi fişi yazdır
                    printSaleReceipt(receiptChoiceData);
                    setTimeout(() => {
                      printSaleReceipt(receiptChoiceData);
                      setReceiptChoiceData(null);
                      setTimeout(() => barcodeInputRef.current?.focus(), 100);
                    }, 1500);
                  }}
                  style={{
                    padding: '12px 16px',
                    borderRadius: '10px',
                    border: '1px solid #e2e8f0',
                    background: 'white',
                    color: '#1e293b',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'all 0.2s'
                  }}
                >
                  <Printer size={16} style={{ marginRight: '8px', verticalAlign: 'middle' }} /> Bilgi Fişi Yazdır (2 Adet)
                </button>
                <button
                  onClick={() => {
                    setShowReceiptChoiceModal(false);
                    // 2 adet bilgi fişi + 1 adet hediye/değişim fişi yazdır
                    printSaleReceipt(receiptChoiceData);
                    setTimeout(() => {
                      printSaleReceipt(receiptChoiceData);
                      setTimeout(() => {
                        printGiftExchangeReceipt(receiptChoiceData);
                        setReceiptChoiceData(null);
                        setTimeout(() => barcodeInputRef.current?.focus(), 100);
                      }, 1500);
                    }, 1500);
                  }}
                  style={{
                    padding: '12px 16px',
                    borderRadius: '10px',
                    border: '1px solid #e2e8f0',
                    background: 'white',
                    color: '#1e293b',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'all 0.2s'
                  }}
                >
                  <Gift size={16} style={{ marginRight: '8px', verticalAlign: 'middle' }} /> Hediye / Değişim Fişi Yazdır (2 Bilgi + 1 Hediye)
                </button>
                <button
                  onClick={() => {
                    setShowReceiptChoiceModal(false);
                    // Kapat = 1 adet bilgi fişi yazdır
                    printSaleReceipt(receiptChoiceData);
                    setReceiptChoiceData(null);
                    setTimeout(() => barcodeInputRef.current?.focus(), 100);
                  }}
                  style={{
                    padding: '10px 16px',
                    borderRadius: '10px',
                    border: 'none',
                    background: '#f1f5f9',
                    color: '#64748b',
                    fontSize: '13px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    textAlign: 'center',
                    transition: 'all 0.2s'
                  }}
                >
                  Kapat (1 Adet Bilgi Fişi)
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Masraf Düzenleme Modal */}
      {showEditExpenseModal && editingExpense && (
        <div className="modal-overlay" style={{ zIndex: 10002 }}>
          <div className="modal edit-expense-modal" style={{ zIndex: 10003 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Masraf Düzenle</h2>
              <button className="close-btn" onClick={() => setShowEditExpenseModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="expense-form-grid">
                <div className="form-field">
                  <label>Açıklama</label>
                  <input
                    type="text"
                    value={editingExpense.description}
                    onChange={(e) => setEditingExpense({...editingExpense, description: e.target.value})}
                    placeholder="Masraf açıklaması"
                  />
                </div>
                <div className="form-field">
                  <label>Tutar (₺)</label>
                  <PriceInput
                    value={editingExpense.amount}
                    onChange={(val) => setEditingExpense({...editingExpense, amount: val})}
                  />
                </div>
                <div className="form-field">
                  <label>Kategori</label>
                  <select
                    value={editingExpense.category}
                    onChange={(e) => setEditingExpense({...editingExpense, category: e.target.value})}
                  >
                    {expenseCategories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
                <div className="form-field">
                  <label>Ödeme Tipi</label>
                  <select
                    value={editingExpense.payment_method}
                    onChange={(e) => setEditingExpense({...editingExpense, payment_method: e.target.value})}
                  >
                    <option value="Nakit">Nakit</option>
                    <option value="Kredi Kartı">Kredi Kartı</option>
                    <option value="Havale/EFT">Havale/EFT</option>
                  </select>
                </div>
                <div className="form-field">
                  <label>Tarih</label>
                  <input
                    type="date"
                    value={editingExpense.expense_date || ''}
                    onChange={(e) => setEditingExpense({...editingExpense, expense_date: e.target.value})}
                  />
                </div>
                <div className="form-field">
                  <label>Saat</label>
                  <input
                    type="time"
                    value={editingExpense.expense_time || ''}
                    onChange={(e) => setEditingExpense({...editingExpense, expense_time: e.target.value})}
                  />
                </div>
                <div className="form-field full-width">
                  <label>Notlar</label>
                  <textarea
                    value={editingExpense.notes || ''}
                    onChange={(e) => setEditingExpense({...editingExpense, notes: e.target.value})}
                    placeholder="Ek notlar..."
                    rows={2}
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer admin-edit-footer">
              <button className="btn-delete" onClick={() => { setDeletingExpenseId(editingExpense.id); setShowDeleteExpenseConfirm(true); }}>
                <Trash2 size={16} /> Masrafı Sil
              </button>
              <div className="footer-right">
                <button className="btn-cancel" onClick={() => setShowEditExpenseModal(false)}>İptal</button>
                <button className="btn-save" onClick={updateExpense}>Kaydet</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Masraf Silme Onay Modal */}
      {showDeleteExpenseConfirm && (
        <div className="modal-overlay" style={{ zIndex: 10010 }}>
          <div className="modal delete-confirm-modal" style={{ zIndex: 10011 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header delete-header">
              <AlertTriangle size={24} color="#ef4444" />
              <h2>Masraf Silme Onayı</h2>
            </div>
            <div className="modal-body">
              <p className="delete-warning">Bu masrafı silmek üzeresiniz.</p>
              <p className="delete-info">Bu işlem geri alınamaz.</p>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => { setShowDeleteExpenseConfirm(false); setDeletingExpenseId(null); }}>
                İptal
              </button>
              <button className="btn-delete-confirm" onClick={confirmDeleteExpense}>
                <Trash2 size={16} /> Evet, Sil
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hurda Alacağı Kalan Bakiye Modal */}
      {showScrapBalanceModal && scrapBalanceInfo && (
        <div className="modal-overlay" style={{ zIndex: 10001 }}>
          <div className="modal scrap-balance-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header" style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' }}>
              <h2 style={{ color: 'white', margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Coins size={24} />
                Hurda Alacağı Kalan Bakiye
              </h2>
              <button className="close-btn" style={{ color: 'white' }} onClick={() => setShowScrapBalanceModal(false)}>✕</button>
            </div>
            <div className="modal-body" style={{ padding: '24px' }}>
              <div className="scrap-balance-summary" style={{
                background: '#fffbeb',
                border: '1px solid #fcd34d',
                borderRadius: '12px',
                padding: '20px',
                marginBottom: '24px'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <span style={{ color: '#92400e' }}>Toplam Hurda Alacağı:</span>
                  <strong style={{ color: '#b45309' }}>₺{scrapBalanceInfo.totalCredit.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <span style={{ color: '#92400e' }}>Bu Satışta Kullanılan:</span>
                  <strong style={{ color: '#059669' }}>-₺{scrapBalanceInfo.usedAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                </div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  paddingTop: '12px',
                  borderTop: '2px dashed #fcd34d',
                  fontSize: '1.1rem'
                }}>
                  <span style={{ color: '#92400e', fontWeight: '600' }}>Kalan Bakiye:</span>
                  <strong style={{ color: '#b45309', fontSize: '1.2rem' }}>₺{scrapBalanceInfo.remainingBalance.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                </div>
              </div>

              <p style={{ textAlign: 'center', color: '#64748b', marginBottom: '20px' }}>
                Kalan bakiye için ne yapmak istersiniz?
              </p>

              <div className="scrap-balance-options" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <button
                  className="scrap-balance-option-btn"
                  onClick={() => handleScrapBalanceDecision('nakit')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    padding: '18px 20px',
                    border: '2px solid #e2e8f0',
                    borderRadius: '12px',
                    background: 'white',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '12px',
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Banknote size={24} color="white" />
                  </div>
                  <div style={{ textAlign: 'left', flex: 1 }}>
                    <div style={{ fontWeight: '600', color: '#1e293b', fontSize: '1rem' }}>Nakit Ver</div>
                    <div style={{ color: '#64748b', fontSize: '0.85rem' }}>Kalan tutarı müşteriye nakit olarak öde</div>
                  </div>
                  <ArrowRight size={20} color="#94a3b8" />
                </button>

                <button
                  className="scrap-balance-option-btn"
                  onClick={() => handleScrapBalanceDecision('havale')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    padding: '18px 20px',
                    border: '2px solid #e2e8f0',
                    borderRadius: '12px',
                    background: 'white',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '12px',
                    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Building2 size={24} color="white" />
                  </div>
                  <div style={{ textAlign: 'left', flex: 1 }}>
                    <div style={{ fontWeight: '600', color: '#1e293b', fontSize: '1rem' }}>Havale Ver</div>
                    <div style={{ color: '#64748b', fontSize: '0.85rem' }}>Kalan tutarı müşteriye havale/EFT olarak öde</div>
                  </div>
                  <ArrowRight size={20} color="#94a3b8" />
                </button>

                <button
                  className="scrap-balance-option-btn"
                  onClick={() => handleScrapBalanceDecision('hurda')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    padding: '18px 20px',
                    border: '2px solid #e2e8f0',
                    borderRadius: '12px',
                    background: 'white',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '12px',
                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Coins size={24} color="white" />
                  </div>
                  <div style={{ textAlign: 'left', flex: 1 }}>
                    <div style={{ fontWeight: '600', color: '#1e293b', fontSize: '1rem' }}>Hurda Kalsın</div>
                    <div style={{ color: '#64748b', fontSize: '0.85rem' }}>Bakiyeyi koruyup müşterinin hurda alacağı olarak bırak</div>
                  </div>
                  <ArrowRight size={20} color="#94a3b8" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Diğer Ödeme Seçenekleri Modal */}
      {showPaymentModal && (
        <div className={`modal-overlay ${closingModal === 'payment' ? 'closing' : ''}`}>
          <div className="modal payment-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Diğer Ödeme Seçenekleri</h2>
              <button className="close-btn" onClick={() => handleCloseModal('payment', setShowPaymentModal, () => setShowPaymentCreditOptions(false))}>✕</button>
            </div>
            <div className="modal-body">
              <div className="payment-summary">
                <span>Ödenecek Tutar:</span>
                <strong>₺{total.toFixed(2)}</strong>
              </div>
              <div className="payment-options">
                <button
                  className="payment-option-btn iban"
                  onClick={() => {
                    if (!selectedCustomer) {
                      toast.warning('Lütfen önce müşteri seçin!');
                      setShowPaymentModal(false);
                      setShowCustomerSearch(true);
                      return;
                    }
                    if (selectedStaff.length === 0) {
                      toast.warning('Lütfen önce satış sorumlusu seçin!');
                      setShowPaymentModal(false);
                      setShowStaffModal(true);
                      return;
                    }
                    setShowPaymentModal(false);
                    openBankAccountsModal();
                  }}
                >
                  <span className="payment-icon"><Building2 size={24} /></span>
                  <div className="payment-info">
                    <span className="payment-title">Havale / EFT</span>
                    <span className="payment-desc">Banka hesabı seç ve öde</span>
                  </div>
                </button>
                <button
                  className={`payment-option-btn credit ${showPaymentCreditOptions ? 'active' : ''} ${selectedCustomer?.isAnonymous ? 'disabled' : ''}`}
                  onClick={() => {
                    if (!selectedCustomer) {
                      toast.warning('Lütfen önce müşteri seçin!');
                      setShowPaymentModal(false);
                      setShowCustomerSearch(true);
                      return;
                    }
                    if (selectedCustomer?.isAnonymous) {
                      toast.warning('Kayıtsız müşteriye açık hesap satışı yapılamaz!');
                      return;
                    }
                    if (selectedStaff.length === 0) {
                      toast.warning('Lütfen önce satış sorumlusu seçin!');
                      setShowPaymentModal(false);
                      setShowStaffModal(true);
                      return;
                    }
                    // Değişim modunda: iade yetkisi yoksa ve fark negatifse (müşteriye para dönecekse) engelle
                    if (isExchangeMode && total < 0 && !canRefund()) {
                      toast.warning('İade yetkiniz olmadığı için farkı açık hesaba ekleyemezsiniz. Yeni ürün tutarı iade edilen ürünü geçmelidir.');
                      return;
                    }
                    if (isExchangeMode) {
                      // Değişim modunda direkt TL olarak kaydet
                      const netTotal = total;
                      const debtPayments = [{
                        creditType: 'TL',
                        amount: netTotal,
                        amountTL: netTotal,
                        goldRate: null
                      }];
                      handlePayment('Açık Hesap (TL)', debtPayments);
                      setShowPaymentModal(false);
                      return;
                    }
                    setShowPaymentCreditOptions(!showPaymentCreditOptions);
                  }}
                  disabled={!selectedCustomer || selectedCustomer?.isAnonymous}
                >
                  <span className="payment-icon"><ClipboardList size={24} /></span>
                  <div className="payment-info">
                    <span className="payment-title">Açık Hesap</span>
                    <span className="payment-desc">{!selectedCustomer ? 'Önce müşteri seçilmelidir' : selectedCustomer?.isAnonymous ? 'Kayıtsız müşteri için kullanılamaz' : isExchangeMode ? 'Farkı müşteri hesabına yaz' : 'Müşteri hesabına borç yaz'}</span>
                  </div>
                </button>
                {/* Açık Hesap Seçenekleri (normal modda) */}
                {showPaymentCreditOptions && !isExchangeMode && (
                  <div className="credit-options-inline">
                    <button
                      className="credit-option-btn"
                      onClick={() => {
                        const netTotal = Math.max(0, total - customerGiftCardDiscount);
                        const debtPayments = netTotal > 0 ? [{
                          creditType: 'TL',
                          amount: netTotal,
                          amountTL: netTotal,
                          goldRate: null
                        }] : [];
                        handlePayment(netTotal > 0 ? 'Açık Hesap (TL)' : 'Müşteri Bakiyesi', debtPayments);
                        setShowPaymentModal(false);
                        setShowPaymentCreditOptions(false);
                      }}
                    >
                      <span className="credit-option-icon"><Banknote size={20} style={{ color: '#10b981' }} /></span>
                      <div className="credit-option-info">
                        <span className="credit-option-title">TL Olarak Kaydet</span>
                        <span className="credit-option-value">₺{Math.max(0, total - customerGiftCardDiscount).toFixed(2)} borç</span>
                      </div>
                    </button>
                    <button
                      className="credit-option-btn"
                      onClick={() => {
                        const netTotal = Math.max(0, total - customerGiftCardDiscount);
                        const goldPrice = getSatisRate('GOLD');
                        const goldGram = goldPrice > 0 ? (netTotal / goldPrice).toFixed(3) : 0;
                        const debtPayments = netTotal > 0 ? [{
                          creditType: 'GRAM',
                          amount: parseFloat(goldGram),
                          amountTL: netTotal,
                          goldRate: goldPrice
                        }] : [];
                        handlePayment(netTotal > 0 ? 'Açık Hesap (Gram)' : 'Müşteri Bakiyesi', debtPayments);
                        setShowPaymentModal(false);
                        setShowPaymentCreditOptions(false);
                      }}
                    >
                      <span className="credit-option-icon"><Coins size={20} style={{ color: '#f59e0b' }} /></span>
                      <div className="credit-option-info">
                        <span className="credit-option-title">Gram Altın Olarak</span>
                        <span className="credit-option-value">
                          {(() => {
                            const netTotal = Math.max(0, total - customerGiftCardDiscount);
                            const goldPrice = getSatisRate('GOLD');
                            return goldPrice > 0 ? (netTotal / goldPrice).toFixed(3) : '?';
                          })()} gram borç
                        </span>
                      </div>
                    </button>
                    <button
                      className="credit-option-btn silver"
                      onClick={() => {
                        const netTotal = Math.max(0, total - customerGiftCardDiscount);
                        const silverPrice = getSatisRate('SILVER');
                        const silverGram = silverPrice > 0 ? (netTotal / silverPrice).toFixed(2) : 0;
                        const debtPayments = netTotal > 0 ? [{
                          creditType: 'GUMUS',
                          amount: parseFloat(silverGram),
                          amountTL: netTotal,
                          silverRate: silverPrice
                        }] : [];
                        handlePayment(netTotal > 0 ? 'Açık Hesap (Gümüş)' : 'Müşteri Bakiyesi', debtPayments);
                        setShowPaymentModal(false);
                        setShowPaymentCreditOptions(false);
                      }}
                    >
                      <span className="credit-option-icon"><Coins size={20} style={{ color: '#9ca3af' }} /></span>
                      <div className="credit-option-info">
                        <span className="credit-option-title">Gram Gümüş Olarak</span>
                        <span className="credit-option-value">
                          {(() => {
                            const netTotal = Math.max(0, total - customerGiftCardDiscount);
                            const silverPrice = getSatisRate('SILVER');
                            return silverPrice > 0 ? (netTotal / silverPrice).toFixed(2) : '?';
                          })()} gram borç
                        </span>
                      </div>
                    </button>
                  </div>
                )}
                {!isExchangeMode && (
                <button
                  className="payment-option-btn virtual-pos"
                  onClick={() => {
                    if (selectedStaff.length === 0) {
                      toast.warning('Lütfen önce satış sorumlusu seçin!');
                      setShowPaymentModal(false);
                      setShowStaffModal(true);
                      return;
                    }
                    setShowPaymentModal(false);
                    setShowVirtualPosModal(true);
                  }}
                >
                  <span className="payment-icon"><Smartphone size={24} /></span>
                  <div className="payment-info">
                    <span className="payment-title">Sanal Pos (iyzico)</span>
                    <span className="payment-desc">Online ödeme al</span>
                  </div>
                </button>
                )}
                {!isExchangeMode && (
                <button
                  className="payment-option-btn mixed"
                  onClick={() => {
                    if (!selectedCustomer) {
                      toast.warning('Lütfen önce müşteri seçin!');
                      setShowPaymentModal(false);
                      setShowCustomerSearch(true);
                      return;
                    }
                    if (selectedStaff.length === 0) {
                      toast.warning('Lütfen önce satış sorumlusu seçin!');
                      setShowPaymentModal(false);
                      setShowStaffModal(true);
                      return;
                    }
                    setShowPaymentModal(false);
                    setPartialPayments([]);
                    setReceivedAmount('');
                    setSelectedCurrency('TRY');
                    setShowCashModal(true);
                  }}
                >
                  <span className="payment-icon"><Wallet size={24} /></span>
                  <div className="payment-info">
                    <span className="payment-title">Karışık Ödeme</span>
                    <span className="payment-desc">Birden fazla ödeme yöntemi</span>
                  </div>
                </button>
                )}

                {isExchangeMode && (
                  <div style={{ padding: '12px', textAlign: 'center', color: '#94a3b8', fontSize: '0.85rem', fontStyle: 'italic' }}>
                    Değişim işleminde Nakit, Kredi Kartı, Havale/EFT veya Açık Hesap kullanılabilir.
                  </div>
                )}

                {/* Müşteriye Özel Bakiye */}
                {!isExchangeMode && selectedCustomer && appliedCustomerGiftCard && appliedCustomerGiftCard.creditAmount > 0 && !appliedCustomerGiftCard._applied && (
                  <button
                    className="payment-option-btn customer-giftcard full-width"
                    onClick={() => {
                      setShowPaymentModal(false);
                    }}
                  >
                    <span className="payment-icon"><Gift size={24} style={{ color: '#10b981' }} /></span>
                    <div className="payment-info">
                      <span className="payment-title">Müşteri Bakiyesi</span>
                      <span className="payment-desc">
                        {selectedCustomer.name} - ₺{appliedCustomerGiftCard.creditAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} bakiye uygulandı
                      </span>
                    </div>
                  </button>
                )}
                {!isExchangeMode && appliedCustomerGiftCard && (
                  <div className="payment-option-applied full-width">
                    <Gift size={20} style={{ color: '#10b981' }} />
                    <span>Müşteri bakiyesi uygulandı</span>
                    <button className="remove-applied-btn" onClick={removeCustomerGiftCard}>✕</button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mağaza Seçim Modal (Admin için) */}
      {showStoreSelectModal && (
        <div className="modal-overlay">
          <div className="modal store-select-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Stok Düşülecek Mağazayı Seçin</h2>
              <button className="close-btn" onClick={() => setShowStoreSelectModal(false)}>✕</button>
            </div>
            <div className="modal-content">
              <div className="store-select-list">
                {stores.map(store => (
                  <button
                    key={store.id}
                    className="store-select-item"
                    onClick={() => handleStoreSelect(store.id)}
                  >
                    <span className="store-icon">{store.metal_type === 'gold' ? '🥇' : '🥈'}</span>
                    <span className="store-name">{store.name}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Satış Sorumlusu Modal */}
      {showStaffModal && (
        <div className={`modal-overlay ${closingModal === 'staff' ? 'closing' : ''}`}>
          <div className="modal staff-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Satış Sorumlusu Seç</h2>
              <button className="close-btn" onClick={() => handleCloseModal('staff', setShowStaffModal)}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: '0.78rem', color: '#94a3b8', marginBottom: '10px', fontStyle: 'italic' }}>*Birden fazla kasiyer seçmek için fotoğraflara tıklayınız</p>
              <div className="staff-list">
                {salesStaffList.length === 0 ? (
                  <div className="empty-staff-message">
                    <p className="empty-text">Henüz satış sorumlusu eklenmemiş</p>
                    <p className="empty-hint">Personel Yönetimi sayfasından "Satış Temsilcisi" olarak personel ekleyebilirsiniz.</p>
                  </div>
                ) : (
                  salesStaffList.map(staff => {
                    const isSelected = selectedStaff.some(s => s.id === staff.id && s.source === staff.source);
                    return (
                      <div
                        key={`${staff.source}-${staff.id}`}
                        className={`staff-item ${isSelected ? 'selected' : ''}`}
                        onClick={() => selectStaffWithLog(staff)}
                      >
                        <div
                          className={`staff-avatar ${staff.photo_url ? 'has-photo' : ''}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (isSelected) {
                              setSelectedStaff(selectedStaff.filter(s => !(s.id === staff.id && s.source === staff.source)));
                            } else {
                              setSelectedStaff([...selectedStaff, staff]);
                            }
                          }}
                        >
                          {isSelected ? (
                            <Check size={24} strokeWidth={3} />
                          ) : staff.photo_url ? (
                            <img src={`${IMAGE_HOST}${staff.photo_url}`} alt={staff.name} style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                          ) : (
                            staff.name.charAt(0)
                          )}
                        </div>
                        <div className="staff-info">
                          <span className="staff-name">{staff.name}</span>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
              <div className="staff-modal-actions">
                <button
                  className="confirm-staff-btn"
                  onClick={confirmStaffSelection}
                >
                  Tamam
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tahsilat Düzenleme Modalı */}
      {editingCollectionPayment && (
        <div className="modal-overlay" style={{ zIndex: 10001 }}>
          <div className="modal" style={{ maxWidth: '600px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Tahsilat Düzenle</h2>
              <button className="close-btn" onClick={() => setEditingCollectionPayment(null)}>✕</button>
            </div>
            <div className="modal-body" style={{ padding: '20px' }}>
              {/* Müşteri Bilgisi */}
              <div style={{ marginBottom: '16px', padding: '12px', background: '#f8fafc', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                <div style={{ fontWeight: 600, color: '#1e293b' }}>{editingCollectionPayment.customer_name}</div>
                <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: '2px' }}>
                  {new Date(editingCollectionPayment.created_at).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  {editingCollectionPayment.debt_description && <span> — {editingCollectionPayment.debt_description}</span>}
                </div>
              </div>

              {/* Ödeme Kalemleri */}
              <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, color: '#334155', marginBottom: '8px' }}>Ödeme Kalemleri</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '14px' }}>
                {editCollectionItems.map((item, idx) => {
                  const isHurda = (item.method || '').toLowerCase().includes('hurda');
                  return (
                  <div key={item.id || idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 12px', background: isHurda ? '#f1f5f9' : '#fff', border: `1px solid ${isHurda ? '#cbd5e1' : '#e2e8f0'}`, borderRadius: '10px', opacity: isHurda ? 0.8 : 1 }}>
                    {isHurda ? (
                      <span style={{ padding: '7px 8px', fontSize: '0.82rem', fontWeight: 600, color: '#64748b', minWidth: '110px' }}>{item.method}</span>
                    ) : (
                    <select
                      value={item.method}
                      onChange={(e) => setEditCollectionItems(prev => prev.map((p, i) => i === idx ? { ...p, method: e.target.value } : p))}
                      style={{ padding: '7px 8px', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '0.82rem', fontWeight: 600, color: '#334155', background: '#f8fafc', minWidth: '110px' }}
                    >
                      <option value="Nakit">Nakit</option>
                      <option value="Kredi Kartı">Kredi Kartı</option>
                      <option value="Havale/EFT">Havale/EFT</option>
                    </select>
                    )}
                    {item.method === 'Havale/EFT' && (
                      <select
                        value={item.bank_account || ''}
                        onChange={(e) => setEditCollectionItems(prev => prev.map((p, i) => i === idx ? { ...p, bank_account: e.target.value } : p))}
                        style={{ padding: '7px 8px', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '0.82rem', fontWeight: 600, color: '#334155', background: '#f8fafc', minWidth: '110px' }}
                      >
                        <option value="">Hesap Seç</option>
                        {bankAccounts.map(acc => (
                          <option key={acc.id} value={`${acc.account_name} (${acc.account_type === 'company' ? 'Şirket' : acc.account_type === 'ecommerce' ? 'E-Ticaret' : 'Şahsi'})`}>
                            {acc.account_name}
                          </option>
                        ))}
                      </select>
                    )}
                    <div style={{ position: 'relative', flex: 1 }}>
                      <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: '0.85rem', fontWeight: 600 }}>₺</span>
                      {isHurda ? (
                        <span style={{ display: 'block', width: '100%', padding: '7px 10px 7px 24px', fontSize: '0.95rem', fontWeight: 600, color: '#64748b' }}>
                          {(item.amount || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      ) : (
                      <PriceInput
                        value={item.amount}
                        onChange={(val) => setEditCollectionItems(prev => prev.map((p, i) => i === idx ? { ...p, amount: val || 0 } : p))}
                        style={{ width: '100%', padding: '7px 10px 7px 24px', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '0.95rem', fontWeight: 600, boxSizing: 'border-box' }}
                      />
                      )}
                    </div>
                    {editCollectionItems.length > 1 && !isHurda && (
                      <button
                        onClick={() => setEditCollectionItems(prev => prev.filter((_, i) => i !== idx))}
                        style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', padding: '4px', display: 'flex' }}
                        title="Kalemi sil"
                      >
                        <X size={16} />
                      </button>
                    )}
                  </div>
                  );
                })}
                <button
                  onClick={() => setEditCollectionItems(prev => [...prev, { id: Date.now(), amount: '', method: 'Nakit' }])}
                  style={{ padding: '8px', borderRadius: '8px', border: '1.5px dashed #cbd5e1', background: '#fff', color: '#64748b', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
                >
                  + Ödeme Kalemi Ekle
                </button>
              </div>

              {/* Toplam */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: '#f0fdf4', borderRadius: '8px', border: '1px solid #bbf7d0', marginBottom: '14px' }}>
                <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#166534' }}>Toplam:</span>
                <span style={{ fontSize: '1.1rem', fontWeight: 700, color: '#16a34a' }}>₺{editCollectionItems.reduce((sum, item) => sum + (item.amount || 0), 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>

              {/* Tarih/Saat */}
              <div style={{ marginBottom: '14px' }}>
                <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, color: '#334155', marginBottom: '6px' }}>Tarih / Saat</label>
                <input
                  type="datetime-local"
                  value={editCollectionDate}
                  onChange={(e) => setEditCollectionDate(e.target.value)}
                  style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '0.85rem', boxSizing: 'border-box' }}
                />
              </div>

              {/* Not */}
              <div>
                <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, color: '#334155', marginBottom: '6px' }}>Not</label>
                <input
                  type="text"
                  value={editCollectionNotes}
                  onChange={(e) => setEditCollectionNotes(e.target.value)}
                  placeholder="Opsiyonel not..."
                  style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '0.85rem', boxSizing: 'border-box' }}
                />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px', padding: '16px 20px', borderTop: '1px solid #e2e8f0' }}>
              <button onClick={() => setEditingCollectionPayment(null)} style={{ flex: 1, padding: '10px', borderRadius: '8px', border: '1px solid #e2e8f0', background: '#fff', color: '#64748b', fontWeight: 600, cursor: 'pointer' }}>İptal</button>
              <button onClick={saveEditCollectionPayment} style={{ flex: 1, padding: '10px', borderRadius: '8px', border: 'none', background: '#00bfa6', color: '#fff', fontWeight: 600, cursor: 'pointer' }}>Kaydet</button>
            </div>
          </div>
        </div>
      )}

      {/* Değişim Modal - Basit Satış Detayları */}
      {showExchangeModal && exchangeSaleInfo && (
        <div className={`modal-overlay ${closingModal === 'exchange' ? 'closing' : ''}`}>
          <div className="modal exchange-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Değişim - Fiş #{exchangeSaleInfo.receipt_number || exchangeSaleInfo.store_sale_number || exchangeSaleInfo.id}</h2>
              <button className="close-btn" onClick={() => handleCloseModal('exchange', setShowExchangeModal, () => { setSelectedCustomer(preRefundCustomer); })}>✕</button>
            </div>
            <div className="modal-body">
              {/* Satış Bilgileri */}
              <div className="exchange-sale-info">
                <div className="info-row">
                  <span className="info-label">Müşteri:</span>
                  <span className="info-value">{exchangeSaleInfo.customer_name || 'Belirtilmemiş'}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Tarih:</span>
                  <span className="info-value">{new Date(exchangeSaleInfo.created_at).toLocaleString('tr-TR')}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Toplam Tutar:</span>
                  <span className="info-value">₺{Number(exchangeSaleInfo.total).toLocaleString('tr-TR', { maximumFractionDigits: 2 })}</span>
                </div>
              </div>

              {/* Ürünler - Değişim için seç */}
              <div className="exchange-products-section">
                <h3>Değişim Yapılacak Ürünleri Seçin</h3>
                <div className="exchange-items-list">
                  {exchangeItems.map(item => {
                    const isFullyRefunded = (item.availableQty || 0) <= 0;
                    const exchangeItemKey = item.id ?? item.product_id;
                    return (
                      <div
                        key={exchangeItemKey}
                        className={`exchange-item ${item.exchangeQty > 0 ? 'selected' : ''}`}
                        style={isFullyRefunded ? {
                          opacity: 0.6,
                          backgroundColor: '#fee2e2',
                          border: '1px solid #fecaca'
                        } : {}}
                      >
                        {item.image ? (
                          <img src={`${IMAGE_HOST}${item.image}`} alt="" onClick={(e) => { e.stopPropagation(); openImagePopup(item.image, item.product_name || item.name); }} style={{ width: '56px', height: '56px', objectFit: 'cover', borderRadius: '8px', flexShrink: 0, border: '1px solid #e2e8f0', cursor: 'pointer' }} />
                        ) : (
                          <div style={{ width: '56px', height: '56px', borderRadius: '8px', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, border: '1px solid #e2e8f0', color: '#94a3b8', fontSize: '20px' }}>📦</div>
                        )}
                        <div className="exchange-item-info">
                          <span className="exchange-item-name" style={isFullyRefunded ? { textDecoration: 'line-through', color: '#9ca3af' } : {}}>
                            {item.isAdjustment && <span style={{ background: '#dbeafe', color: '#1d4ed8', fontSize: '10px', fontWeight: 600, padding: '1px 5px', borderRadius: '4px', marginRight: '6px' }}>FARK</span>}
                            {item.product_name} {item.barcode && <span style={{ color: '#64748b', fontSize: '11px', fontWeight: 'normal' }}>({item.barcode})</span>}
                          </span>
                          <span className="exchange-item-detail">
                            {isFullyRefunded ? (
                              <span style={{ color: '#ef4444', fontWeight: 'bold' }}>
                                ✓ Tamamen iade edildi ({item.quantity} adet)
                              </span>
                            ) : (
                              <>
                                Kalan: {item.availableQty} adet | Birim Fiyat: {(() => {
                                  const listPrice = Number(item.original_unit_price || item.unit_price);
                                  const effPrice = Number(item.effective_unit_price || item.unit_price);
                                  return listPrice - effPrice > 0.01 ? (
                                    <>
                                      <span style={{ textDecoration: 'line-through', color: '#94a3b8' }}>₺{listPrice.toLocaleString('tr-TR', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                                      {' '}
                                      <span style={{ color: '#16a34a', fontWeight: '500' }}>₺{effPrice.toLocaleString('tr-TR', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                                      {listPrice > 0 && (
                                        <span style={{ fontSize: '0.85em', color: '#ef4444', fontWeight: '600', marginLeft: '3px' }}>
                                          %{((listPrice - effPrice) / listPrice * 100).toFixed(0)}
                                        </span>
                                      )}
                                    </>
                                  ) : (
                                    <>₺{effPrice.toLocaleString('tr-TR', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</>
                                  );
                                })()}
                                {item.returned_quantity > 0 && (
                                  <span style={{ color: '#f59e0b', marginLeft: '8px', fontSize: '11px', fontWeight: '500' }}>
                                    ({item.returned_quantity}/{item.quantity} iade edildi)
                                  </span>
                                )}
                              </>
                            )}
                          </span>
                        </div>
                        <div className="exchange-item-qty">
                          <button
                            onClick={() => updateExchangeQty(exchangeItemKey, item.exchangeQty - 1)}
                            disabled={isFullyRefunded}
                          >−</button>
                          <input
                            type="number"
                            min="0"
                            max={item.availableQty || 0}
                            value={item.exchangeQty}
                            onChange={(e) => updateExchangeQty(exchangeItemKey, parseInt(e.target.value) || 0)}
                            disabled={isFullyRefunded}
                            style={isFullyRefunded ? { backgroundColor: '#e5e7eb', cursor: 'not-allowed' } : {}}
                          />
                          <button
                            onClick={() => updateExchangeQty(exchangeItemKey, item.exchangeQty + 1)}
                            disabled={isFullyRefunded}
                          >+</button>
                        </div>
                        <span className="exchange-item-total">
                          -₺{(item.exchangeQty * Number(item.effective_unit_price || item.unit_price)).toLocaleString('tr-TR', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Seçilen toplam */}
              <div className="exchange-selected-total">
                <span>Seçilen İade Tutarı:</span>
                <strong>-₺{exchangeItems.reduce((sum, item) => sum + (item.exchangeQty * Number(item.effective_unit_price || item.unit_price)), 0).toLocaleString('tr-TR', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</strong>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => { setShowExchangeModal(false); setSelectedCustomer(preRefundCustomer); }}>İptal</button>
              <button className="btn-save" onClick={confirmExchange} disabled={exchangeItems.filter(item => item.exchangeQty > 0).length === 0}>
                Devam Et
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Gün Sonu Raporu Modal */}
      {showEndOfDayModal && (
        <div className={`modal-overlay ${closingModal === 'endOfDay' ? 'closing' : ''}`}>
          <div className="modal end-of-day-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Günlük Rapor</h2>
              <button className="close-btn" onClick={() => handleCloseModal('endOfDay', setShowEndOfDayModal)}>✕</button>
            </div>

            {/* Tarih Navigasyonu */}
            {endOfDayReport && (
              <div className="eod-date-nav">
                <button className="eod-date-btn" onClick={() => handleEodDateChange('prev')}>
                  <ChevronLeft size={20} />
                </button>
                <div className="eod-date-display eod-date-clickable" onClick={() => document.getElementById('eod-date-picker').showPicker()}>
                  <Calendar size={16} />
                  <span className="eod-date-text">
                    {eodDate.toLocaleDateString('tr-TR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                  </span>
                  {isEodToday() && <span className="eod-today-badge">Bugün</span>}
                  <input
                    type="date"
                    id="eod-date-picker"
                    className="eod-date-hidden-input"
                    value={eodDate.toISOString().split('T')[0]}
                    max={new Date().toISOString().split('T')[0]}
                    onChange={(e) => {
                      if (e.target.value) {
                        const [y, m, d] = e.target.value.split('-');
                        const picked = new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
                        setEodDate(picked);
                        fetchEndOfDayReport(picked);
                      }
                    }}
                  />
                </div>
                <button className="eod-date-btn" onClick={() => handleEodDateChange('next')} disabled={isEodToday()}>
                  <ChevronRight size={20} />
                </button>
              </div>
            )}

            {/* Sekme Navigasyonu */}
            {endOfDayReport && (
              <div className="eod-tabs">
                <div className="eod-tabs-left">
                  <button className={`eod-tab-btn ${endOfDayTab === 'genel' ? 'active' : ''}`} onClick={() => setEndOfDayTab('genel')}>
                    <BarChart3 size={16} /> Genel
                  </button>
                  <button className={`eod-tab-btn ${endOfDayTab === 'kasaOzeti' ? 'active' : ''}`} onClick={() => setEndOfDayTab('kasaOzeti')}>
                    <Wallet size={16} /> Kasa Özeti
                  </button>
                </div>
                <div className="eod-tabs-right">
                  <button
                    className="eod-action-btn send"
                    onClick={sendDailyReportNotification}
                    disabled={sendingDailyReport}
                    title="Admin'lere Bildirim Gönder"
                  >
                    {sendingDailyReport ? <RefreshCw size={15} className="spin" /> : <Send size={15} />}
                  </button>
                  <button className="eod-action-btn print" onClick={() => endOfDayTab === 'kasaOzeti' ? printKasaOzeti() : printEndOfDayReport()} title="Yazdır">
                    <Printer size={15} />
                  </button>
                  <button className="eod-action-btn print" onClick={() => exportEndOfDayPdf()} title="PDF İndir">
                    <Download size={15} />
                  </button>
                </div>
              </div>
            )}

            <div className="modal-body">
              {endOfDayLoading ? (
                <Loading text="Rapor yükleniyor..." size="medium" />
              ) : endOfDayReport ? (
                <div className="eod-report">

                  {/* ========== GENEL SEKMESİ ========== */}
                  {endOfDayTab === 'genel' && (
                    <EndOfDayGenel
                      report={endOfDayReport}
                      expandedPaymentType={expandedPaymentType}
                      setExpandedPaymentType={setExpandedPaymentType}
                      expandedCashiers={expandedCashiers}
                      setExpandedCashiers={setExpandedCashiers}
                      eodCashierFilter={eodCashierFilter}
                      setEodCashierFilter={setEodCashierFilter}
                      expandedSales={expandedSales}
                      setExpandedSales={setExpandedSales}
                    />
                  )}

                  {/* ========== KASA ÖZETİ SEKMESİ ========== */}
                  {endOfDayTab === 'kasaOzeti' && (
                    <div className="kasa-defteri-rapor">
                      {/* Rapor Başlığı */}
                      <div className="kd-header">
                        <table className="kd-header-table">
                          <tbody>
                            <tr><td className="kd-label">Rapor Adı</td><td>: Kasa Defteri Raporu Detaylı</td></tr>
                            <tr><td className="kd-label">Rapor Tarih / Saat</td><td>: {eodDate.toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric' })}, {eodDate.toLocaleDateString('tr-TR', { weekday: 'long' })}</td></tr>
                          </tbody>
                        </table>
                      </div>

                      {/* Merkez / Mağaza */}
                      <div className="kd-store-bar">{endOfDayReport.storeName || 'Merkez'}</div>

                      {/* (+) GELİRLER */}
                      <div className="kd-section">
                        <div className="kd-section-title gelir">(+) GELİRLER</div>
                        <div className="kd-table">
                          <div className="kd-table-header">
                            <span>İŞLEM ADI</span>
                            <span>TUTAR</span>
                          </div>
                          {(endOfDayReport.cash.salesTotal ?? ((endOfDayReport.cash.total || 0) - (endOfDayReport.cash.collectionTotal || 0) - (endOfDayReport.cash.repairTotal || 0))) > 0 && (
                            <div className="kd-row"><span>Nakit Ödeme</span><span>{(endOfDayReport.cash.salesTotal ?? ((endOfDayReport.cash.total || 0) - (endOfDayReport.cash.collectionTotal || 0) - (endOfDayReport.cash.repairTotal || 0))).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></div>
                          )}
                          {(endOfDayReport.cash.collectionTotal || 0) > 0 && (
                            <div className="kd-row"><span>Nakit Tahsilat</span><span>{endOfDayReport.cash.collectionTotal.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></div>
                          )}
                          {(endOfDayReport.cash.repairTotal || 0) > 0 && (
                            <div className="kd-row"><span>Nakit Tamirat</span><span>{endOfDayReport.cash.repairTotal.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></div>
                          )}
                          {eTicaretGelirRows(endOfDayReport.sanalPos).map(([label, amount]) => (
                            <div key={label} className="kd-row"><span>{label}</span><span>{amount.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></div>
                          ))}
                          {(endOfDayReport.card.salesTotal || endOfDayReport.card.total) > 0 && (
                            <div className="kd-row"><span>Kredi Kartı</span><span>{(endOfDayReport.card.salesTotal ?? endOfDayReport.card.total).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></div>
                          )}
                          {(endOfDayReport.card.collectionTotal || 0) > 0 && (
                            <div className="kd-row"><span>Kredi Kartı Tahsilat</span><span>{endOfDayReport.card.collectionTotal.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></div>
                          )}
                          {(endOfDayReport.card.repairTotal || 0) > 0 && (
                            <div className="kd-row"><span>Kredi Kartı Tamirat</span><span>{endOfDayReport.card.repairTotal.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></div>
                          )}
                          {(() => {
                            const tSalesTotal = endOfDayReport.transfer.salesTotal ?? endOfDayReport.transfer.total ?? 0;
                            const byAcc = endOfDayReport.transfer.byAccount || {};
                            const accNames = Object.keys(byAcc);
                            if (tSalesTotal > 0 && accNames.length > 0) {
                              return accNames.filter(n => byAcc[n].total > 0).map(name => (
                                <div key={`ts-${name}`} className="kd-row"><span>Havale/EFT {stripBankAccountType(name).replace('HAVALE ', '')}</span><span>{byAcc[name].total.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></div>
                              ));
                            }
                            if (tSalesTotal > 0) return <div className="kd-row"><span>Havale/EFT</span><span>{tSalesTotal.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></div>;
                            return null;
                          })()}
                          {(() => {
                            const tCollTotal = endOfDayReport.transfer.collectionTotal || 0;
                            const collByAcc = endOfDayReport.transfer.collectionByAccount || {};
                            const collAccNames = Object.keys(collByAcc);
                            if (tCollTotal > 0 && collAccNames.length > 0) {
                              return collAccNames.filter(n => collByAcc[n].total > 0).map(name => (
                                <div key={`tc-${name}`} className="kd-row"><span>Tahsilat {stripBankAccountType(name).replace('HAVALE ', '')}</span><span>{collByAcc[name].total.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></div>
                              ));
                            }
                            if (tCollTotal > 0) return <div className="kd-row"><span>Havale/EFT Tahsilat</span><span>{tCollTotal.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></div>;
                            return null;
                          })()}
                          {(() => {
                            const tRepairTotal = endOfDayReport.transfer.repairTotal || 0;
                            const repairByAcc = endOfDayReport.transfer.repairByAccount || {};
                            const repairAccNames = Object.keys(repairByAcc);
                            if (tRepairTotal > 0 && repairAccNames.length > 0) {
                              return repairAccNames.filter(n => repairByAcc[n].total > 0).map(name => (
                                <div key={`tr-${name}`} className="kd-row"><span>Havale/EFT Tamirat {stripBankAccountType(name).replace('HAVALE ', '')}</span><span>{repairByAcc[name].total.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></div>
                              ));
                            }
                            if (tRepairTotal > 0) return <div className="kd-row"><span>Havale/EFT Tamirat</span><span>{tRepairTotal.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></div>;
                            return null;
                          })()}
                          {endOfDayReport.voucher?.total > 0 && (
                            <div className="kd-row"><span>Bakiye</span><span>{endOfDayReport.voucher.total.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></div>
                          )}
                          {endOfDayReport.scrap?.saleLinkedGoldTotal > 0 && (
                            <div className="kd-row"><span>Altın Hurda</span><span>{endOfDayReport.scrap.saleLinkedGoldTotal.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></div>
                          )}
                          {endOfDayReport.scrap?.saleLinkedSilverTotal > 0 && (
                            <div className="kd-row"><span>Gümüş Hurda</span><span>{endOfDayReport.scrap.saleLinkedSilverTotal.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></div>
                          )}
                          {(endOfDayReport.collections?.hurdaTotal || 0) > 0 && (
                            <div className="kd-row"><span>Hurda ile Tahsilat</span><span>{endOfDayReport.collections.hurdaTotal.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></div>
                          )}
                          <div className="kd-section-total">
                            TOPLAM : {(
                              (endOfDayReport.cash.total || 0) +
                              (endOfDayReport.sanalPos?.total || 0) +
                              (endOfDayReport.card.salesTotal ?? endOfDayReport.card.total ?? 0) +
                              (endOfDayReport.card.collectionTotal || 0) +
                              (endOfDayReport.card.repairTotal || 0) +
                              (endOfDayReport.transfer.salesTotal ?? endOfDayReport.transfer.total ?? 0) +
                              (endOfDayReport.transfer.collectionTotal || 0) +
                              (endOfDayReport.transfer.repairTotal || 0) +
                              (endOfDayReport.voucher?.total || 0) +
                              (endOfDayReport.scrap?.saleLinkedTotal || 0) +
                              (endOfDayReport.collections?.hurdaTotal || 0)
                            ).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} (+)
                          </div>
                        </div>
                      </div>

                      {/* (-) GİDERLER */}
                      <div className="kd-section">
                        <div className="kd-section-title gider">(-) GİDERLER</div>
                        <div className="kd-table">
                          <div className="kd-table-header">
                            <span>İŞLEM ADI</span>
                            <span>TUTAR</span>
                          </div>
                          {endOfDayReport.cardRefund?.total > 0 && (
                            <div className="kd-row"><span>Kredi Kartı İade</span><span>-{endOfDayReport.cardRefund.total.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></div>
                          )}
                          {endOfDayReport.cashRefund?.total > 0 && (
                            <div className="kd-row"><span>Nakit İade</span><span>-{endOfDayReport.cashRefund.total.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></div>
                          )}
                          {endOfDayReport.scrapCashRefund?.total > 0 && (
                            <div className="kd-row"><span>Hurda Nakit İadesi</span><span>-{endOfDayReport.scrapCashRefund.total.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></div>
                          )}
                          {endOfDayReport.sanalPosRefund?.total > 0 && (
                            <div className="kd-row"><span>Sanal Pos İade</span><span>-{endOfDayReport.sanalPosRefund.total.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></div>
                          )}
                          {endOfDayReport.exchanges?.cashRefundTotal > 0 && (
                            <div className="kd-row"><span>Değişim Nakit İade</span><span>-{endOfDayReport.exchanges.cashRefundTotal.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></div>
                          )}
                          {endOfDayReport.exchanges?.cardRefundTotal > 0 && (
                            <div className="kd-row"><span>Değişim Kredi Kartı İade</span><span>-{endOfDayReport.exchanges.cardRefundTotal.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></div>
                          )}
                          {endOfDayReport.havaleRefund?.total > 0 && (
                            <div className="kd-row"><span>Havale İade</span><span>-{endOfDayReport.havaleRefund.total.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></div>
                          )}
                          {endOfDayReport.exchanges?.havaleRefundTotal > 0 && (
                            <div className="kd-row"><span>Değişim Havale İade</span><span>-{endOfDayReport.exchanges.havaleRefundTotal.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></div>
                          )}
                          {endOfDayReport.expenses?.total > 0 && (
                            <div className="kd-row"><span>Masraf Nakit Ödeme</span><span>-{endOfDayReport.expenses.total.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></div>
                          )}
                          {endOfDayReport.scrap?.standaloneTotal > 0 && (
                            <div className="kd-row"><span>Hurda Alımları</span><span>-{endOfDayReport.scrap.standaloneTotal.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></div>
                          )}
                          {(() => {
                            const giderTop = (endOfDayReport.cardRefund?.total || 0) + (endOfDayReport.cashRefund?.total || 0) + (endOfDayReport.scrapCashRefund?.total || 0) + (endOfDayReport.sanalPosRefund?.total || 0) + (endOfDayReport.havaleRefund?.total || 0) + (endOfDayReport.exchanges?.cashRefundTotal || 0) + (endOfDayReport.exchanges?.cardRefundTotal || 0) + (endOfDayReport.exchanges?.havaleRefundTotal || 0) + (endOfDayReport.expenses?.total || 0) + (endOfDayReport.scrap?.standaloneTotal || 0);
                            return giderTop === 0 ? <div className="kd-row"><span style={{ color: '#999' }}>Gider bulunmuyor</span><span>0,00</span></div> : null;
                          })()}
                          <div className="kd-section-total">
                            TOPLAM : {(
                              (endOfDayReport.cardRefund?.total || 0) +
                              (endOfDayReport.cashRefund?.total || 0) +
                              (endOfDayReport.scrapCashRefund?.total || 0) +
                              (endOfDayReport.sanalPosRefund?.total || 0) +
                              (endOfDayReport.havaleRefund?.total || 0) +
                              (endOfDayReport.exchanges?.cashRefundTotal || 0) +
                              (endOfDayReport.exchanges?.cardRefundTotal || 0) +
                              (endOfDayReport.exchanges?.havaleRefundTotal || 0) +
                              (endOfDayReport.expenses?.total || 0) +
                              (endOfDayReport.scrap?.standaloneTotal || 0)
                            ).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} (-)
                          </div>
                        </div>
                      </div>

                      {/* KASA GENEL TOPLAM */}
                      {(() => {
                        const gelirTop = (endOfDayReport.cash.total || 0) + (endOfDayReport.sanalPos?.total || 0) + (endOfDayReport.card.salesTotal ?? endOfDayReport.card.total ?? 0) + (endOfDayReport.card.collectionTotal || 0) + (endOfDayReport.card.repairTotal || 0) + (endOfDayReport.transfer.salesTotal ?? endOfDayReport.transfer.total ?? 0) + (endOfDayReport.transfer.collectionTotal || 0) + (endOfDayReport.transfer.repairTotal || 0) + (endOfDayReport.voucher?.total || 0) + (endOfDayReport.scrap?.saleLinkedTotal || 0) + (endOfDayReport.collections?.hurdaTotal || 0);
                        const giderTop = (endOfDayReport.cardRefund?.total || 0) + (endOfDayReport.cashRefund?.total || 0) + (endOfDayReport.scrapCashRefund?.total || 0) + (endOfDayReport.sanalPosRefund?.total || 0) + (endOfDayReport.havaleRefund?.total || 0) + (endOfDayReport.exchanges?.cashRefundTotal || 0) + (endOfDayReport.exchanges?.cardRefundTotal || 0) + (endOfDayReport.exchanges?.havaleRefundTotal || 0) + (endOfDayReport.expenses?.total || 0) + (endOfDayReport.scrap?.standaloneTotal || 0);
                        const kasaGenel = gelirTop - giderTop;
                        return (
                          <div className="kd-genel-toplam">
                            KASA GENEL TOPLAM : &nbsp; <strong>{kasaGenel.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong> {kasaGenel >= 0 ? '(+)' : '(-)'}
                          </div>
                        );
                      })()}

                      {/* Alt Detaylar */}
                      {(() => {
                        const r = endOfDayReport;
                        const fmt = (v) => (v || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                        const netKrediKarti = ((r.card?.salesTotal ?? 0) + (r.card?.collectionTotal ?? 0) + (r.card?.repairTotal ?? 0)) - ((r.cardRefund?.total ?? 0) + (r.exchanges?.cardRefundTotal ?? 0) + (r.scrap?.cardTotal ?? 0));
                        const transferByAccount = r.transfer?.byAccount || {};
                        const collectionByAccount = r.transfer?.collectionByAccount || {};
                        const repairByAccount = r.transfer?.repairByAccount || {};
                        const scrapByAccount = r.scrap?.transferByAccount || {};
                        const allAccountNames = [...new Set([...Object.keys(transferByAccount), ...Object.keys(collectionByAccount), ...Object.keys(repairByAccount), ...Object.keys(scrapByAccount)])];
                        let netHavaleSirket = 0;
                        let netHavaleSahis = 0;
                        let netHavaleETicaret = 0;
                        allAccountNames.forEach(acc => {
                          const accGelir = (transferByAccount[acc]?.total ?? 0) + (collectionByAccount[acc]?.total ?? 0) + (repairByAccount[acc]?.total ?? 0);
                          const accGider = scrapByAccount[acc]?.total ?? 0;
                          const accNet = accGelir - accGider;
                          const accLower = acc.toLocaleLowerCase('tr');
                          if (accLower.includes('şahıs') || accLower.includes('sahis') || accLower.includes('şahıs') || acc.includes('(Şahıs)')) {
                            netHavaleSahis += accNet;
                          } else if (accLower.includes('e-ticaret') || accLower.includes('eticaret') || accLower.includes('e ticaret') || acc.includes('E-TİCARET')) {
                            netHavaleETicaret += accNet;
                          } else {
                            netHavaleSirket += accNet;
                          }
                        });
                        const toplamHurda = r.scrap?.total ?? 0;
                        return (
                          <div className="kd-alt-detaylar">
                            {(r.expectedCash || 0) !== 0 && <div className="kd-alt-row">NAKİT KASA TOPLAM : {fmt(r.expectedCash)} {(r.expectedCash || 0) >= 0 ? '(+)' : '(-)'}</div>}
                            {netKrediKarti !== 0 && <div className="kd-alt-row">NET KREDİ KARTI : {fmt(netKrediKarti)} {netKrediKarti >= 0 ? '(+)' : '(-)'}</div>}
                            {netHavaleSahis !== 0 && <div className="kd-alt-row">NET HAVALE ŞAHIS : {fmt(netHavaleSahis)} {netHavaleSahis >= 0 ? '(+)' : '(-)'}</div>}
                            {netHavaleSirket !== 0 && <div className="kd-alt-row">NET HAVALE ŞİRKET : {fmt(netHavaleSirket)} {netHavaleSirket >= 0 ? '(+)' : '(-)'}</div>}
                            {netHavaleETicaret !== 0 && <div className="kd-alt-row">NET HAVALE E-TİCARET : {fmt(netHavaleETicaret)} {netHavaleETicaret >= 0 ? '(+)' : '(-)'}</div>}
                            {eTicaretNetRows(r.sanalPos).map(([label, amount]) => <div key={label} className="kd-alt-row">{label} : {fmt(amount)} {amount >= 0 ? '(+)' : '(-)'}</div>)}
                            {toplamHurda !== 0 && <div className="kd-alt-row">TOPLAM HURDA TUTARI : {fmt(toplamHurda)}</div>}
                            {(r.openAccount?.total || 0) !== 0 && <div className="kd-alt-row">AÇIK HESAP SATIŞLAR : {fmt(r.openAccount?.total)}</div>}
                            {(r.musteriBakiyesiSales?.total || 0) !== 0 && <div className="kd-alt-row">MÜŞTERİ BAKİYESİ SATIŞLAR : {fmt(r.musteriBakiyesiSales?.total)}</div>}
                            {((r.exchanges?.voucherRefundTotal || 0) + (r.acikHesapSalesRefund?.total || 0) + (r.bakiyeRefund?.total || 0)) !== 0 && <div className="kd-alt-row">AÇIK HESABA İADELER : {fmt((r.exchanges?.voucherRefundTotal || 0) + (r.acikHesapSalesRefund?.total || 0) + (r.bakiyeRefund?.total || 0))}</div>}
                            {(r.totalDiscount || 0) !== 0 && <div className="kd-alt-row">İNDİRİMLER TOPLAM : {fmt(r.totalDiscount)}</div>}
                          </div>
                        );
                      })()}
                    </div>
                  )}

                </div>
              ) : (
                <p className="eod-empty">Rapor yüklenemedi</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Ödeme Modal */}
      {showCashModal && (() => {
        // Toplam alınan ödeme
        const totalPaid = partialPayments.reduce((sum, p) => sum + p.amountTL, 0);
        // Kalan tutar (müşteri bakiyesi ödeme olarak sayılır)
        const rawRemaining = total - totalPaid - customerGiftCardDiscount;
        const remaining = Math.abs(rawRemaining) < 0.01 ? 0 : rawRemaining;
        // Girilen tutarın TL karşılığı (dinamik kur hesaplama)
        const getExchangeRate = (currency) => {
          if (currency === 'USD') {
            return getAlisRate('USD');
          }
          if (currency === 'EUR') {
            return getAlisRate('EUR');
          }
          return 1;
        };
        const currentAmountTL = selectedCurrency === 'TRY'
          ? Number(receivedAmount) || 0
          : (Number(receivedAmount) || 0) * getExchangeRate(selectedCurrency);

        // Ödeme ekle
        const addPayment = (method) => {
          if (!receivedAmount || currentAmountTL <= 0) {
            toast.warning('Lütfen tutar girin!');
            return;
          }

          if (currentAmountTL > remaining + 0.01) {
            toast.warning(`Girilen tutar kalan tutarı (₺${remaining.toFixed(2)}) aşamaz!`);
            return;
          }

          const newPayment = {
            id: Date.now(),
            method,
            currency: selectedCurrency,
            amount: Number(receivedAmount),
            amountTL: currentAmountTL,
            rate: getExchangeRate(selectedCurrency)
          };

          setPartialPayments([...partialPayments, newPayment]);
          setReceivedAmount('');
          setTimeout(() => {
            const input = document.querySelector('.cash-modal .cash-input');
            if (input) input.focus();
          }, 50);
        };

        // Ödeme sil
        const removePayment = (id) => {
          setPartialPayments(partialPayments.filter(p => p.id !== id));
        };

        // Ödemeyi tamamla
        const completePayment = () => {
          const methods = [...new Set(partialPayments.map(p => p.method))];
          const paymentMethod = methods.length > 1 ? 'Karışık' : methods[0] || 'Nakit';

          // Açık hesap ödemelerini filtrele
          const debtPayments = partialPayments.filter(p =>
            p.method === 'Açık Hesap (TL)' || p.method === 'Açık Hesap (Gram)' || p.method === 'Açık Hesap (Gümüş)'
          );

          // Tüm ödeme detaylarını gönder
          handlePayment(paymentMethod, debtPayments, [...partialPayments]);
          setShowCashModal(false);
          setPartialPayments([]);
        };

        return (
          <div className={`modal-overlay ${closingModal === 'cash' ? 'closing' : ''}`}>
            <div className="modal cash-modal" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Ödeme Al</h2>
                <button className="close-btn" onClick={() => handleCloseModal('cash', setShowCashModal, () => setPendingHavalePayment(null))}>✕</button>
              </div>
              <div className="modal-body">
                {/* Özet Bilgisi - Yan Yana */}
                <div style={{ display: 'flex', gap: '8px', marginBottom: '10px', background: '#f8f9fa', borderRadius: '8px', padding: '8px 10px' }}>
                  <div style={{ flex: 1, textAlign: 'center' }}>
                    <div style={{ fontSize: '0.68rem', color: '#64748b', marginBottom: '2px' }}>Toplam Tutar</div>
                    <div style={{ fontSize: '0.88rem', fontWeight: 700, color: '#1e293b' }}>₺{total.toFixed(2)}</div>
                  </div>
                  <div style={{ width: '1px', background: '#e2e8f0' }} />
                  <div style={{ flex: 1, textAlign: 'center' }}>
                    <div style={{ fontSize: '0.68rem', color: '#64748b', marginBottom: '2px' }}>Toplam Alınan</div>
                    <div style={{ fontSize: '0.88rem', fontWeight: 700, color: totalPaid > 0 ? '#16a34a' : '#1e293b' }}>₺{totalPaid.toFixed(2)}</div>
                  </div>
                  <div style={{ width: '1px', background: '#e2e8f0' }} />
                  <div style={{ flex: 1, textAlign: 'center' }}>
                    <div style={{ fontSize: '0.68rem', color: '#64748b', marginBottom: '2px' }}>{remaining > 0 ? 'Kalan Tutar' : remaining < 0 ? 'Para Üstü' : 'Tamamlandı'}</div>
                    <div style={{ fontSize: '0.88rem', fontWeight: 700, color: remaining > 0 ? '#dc2626' : remaining < 0 ? '#f59e0b' : '#16a34a' }}>{remaining !== 0 ? `₺${Math.abs(remaining).toFixed(2)}` : '✓'}</div>
                  </div>
                </div>

                {/* Alınan Ödemeler - Kompakt Chip */}
                {partialPayments.length > 0 && (
                  <div style={{ marginBottom: '8px', display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center' }}>
                    {partialPayments.map(payment => (
                      <div key={payment.id} style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '3px 10px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '16px', fontSize: '0.78rem' }}>
                        <span style={{ fontWeight: 600, color: '#334155' }}>
                          {payment.method === 'Havale/EFT' && payment.bankAccount
                            ? `Havale (${payment.bankAccount.includes('Şirket') ? 'Ş' : 'K'})`
                            : payment.method}
                        </span>
                        <span style={{ color: '#16a34a', fontWeight: 700 }}>
                          {payment.currency === 'GOLD' ? (
                            <>{payment.amount} gr</>
                          ) : payment.currency !== 'TRY' ? (
                            <>{payment.currency === 'USD' ? '$' : '€'}{payment.amount.toFixed(2)}</>
                          ) : (
                            <>₺{payment.amountTL.toFixed(2)}</>
                          )}
                        </span>
                        <button onClick={() => removePayment(payment.id)} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: '0 0 0 2px', fontSize: '13px', lineHeight: 1 }}>✕</button>
                      </div>
                    ))}
                    <span style={{ fontSize: '0.75rem', color: '#64748b', marginLeft: '4px' }}>
                      Toplam: <strong style={{ color: '#16a34a' }}>₺{totalPaid.toLocaleString('tr-TR', {minimumFractionDigits: 2})}</strong>
                    </span>
                  </div>
                )}

                {/* Yeni Ödeme Ekleme - Sadece kalan varsa göster */}
                {remaining > 0 && (
                  <>
                    {/* Döviz Seçimi */}
                    <div style={{ display: 'flex', background: '#f1f5f9', borderRadius: '6px', padding: '2px', marginBottom: '10px', gap: '2px' }}>
                      {[
                        { key: 'TRY', label: '₺ TL', rate: null },
                        { key: 'USD', label: '$ USD', rate: `1$=₺${getExchangeRate('USD').toLocaleString('tr-TR')}` },
                        { key: 'EUR', label: '€ EUR', rate: `1€=₺${getExchangeRate('EUR').toLocaleString('tr-TR')}` }
                      ].map(c => (
                        <button
                          key={c.key}
                          onClick={() => { setSelectedCurrency(c.key); setReceivedAmount(''); }}
                          style={{
                            flex: 1, padding: '7px 8px', border: 'none', borderRadius: '5px', cursor: 'pointer',
                            background: selectedCurrency === c.key ? '#fff' : 'transparent',
                            color: selectedCurrency === c.key ? '#00897b' : '#64748b',
                            fontWeight: selectedCurrency === c.key ? 600 : 400,
                            fontSize: '0.65rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '3px',
                            boxShadow: selectedCurrency === c.key ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
                            transition: 'all 0.15s'
                          }}
                        >
                          <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>{c.label}</span>
                          {c.rate && <span style={{ fontSize: '0.65rem', opacity: 0.6 }}>{c.rate}</span>}
                        </button>
                      ))}
                    </div>

                    {/* Tutar Girişi */}
                    <div className="cash-input-section">
                      <label>
                        Tutar ({selectedCurrency === 'TRY' ? '₺' : selectedCurrency === 'USD' ? '$' : '€'})
                      </label>
                      <PriceInput
                        className="cash-input"
                        value={receivedAmount}
                        onChange={(val) => setReceivedAmount(val)}
                        placeholder="0,00"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Tab' && !receivedAmount && remaining > 0) {
                            e.preventDefault();
                            if (selectedCurrency === 'TRY') {
                              setReceivedAmount(remaining.toFixed(2));
                            } else {
                              const rate = getExchangeRate(selectedCurrency);
                              setReceivedAmount(rate > 0 ? (remaining / rate).toFixed(2) : '0');
                            }
                          }
                        }}
                      />
                      <span style={{ fontSize: '0.68rem', color: '#94a3b8', marginTop: '3px', display: 'flex', alignItems: 'center', gap: '3px' }}><Lightbulb size={11} color="#00bfa6" /> Tab ile kalan tutarı doldur</span>
                    </div>

                    {/* Döviz ise TL karşılığını göster */}
                    {selectedCurrency !== 'TRY' && receivedAmount && Number(receivedAmount) > 0 && (
                      <div className="tl-equivalent">
                        <span className="tl-label">TL Karşılığı:</span>
                        <span className="tl-amount">₺{currentAmountTL.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      </div>
                    )}

                    {/* Ödeme Yöntemi Butonları */}
                    <div className="payment-method-buttons">
                      <button
                        className="method-btn cash"
                        onClick={() => { addPayment('Nakit'); setShowCreditOptions(false); }}
                        disabled={!receivedAmount || currentAmountTL <= 0}
                      >
                        <Banknote size={16} /> Nakit
                      </button>
                      <button
                        className="method-btn card"
                        onClick={() => { addPayment('Kredi Kartı'); setShowCreditOptions(false); }}
                        disabled={!receivedAmount || currentAmountTL <= 0}
                      >
                        <CreditCard size={16} /> Kart
                      </button>
                      <button
                        className="method-btn transfer"
                        onClick={async () => {
                          if (currentAmountTL > remaining + 0.01) {
                            toast.warning(`Girilen tutar kalan tutarı (₺${remaining.toFixed(2)}) aşamaz!`);
                            return;
                          }
                          setShowCreditOptions(false);
                          // Bekleyen ödeme bilgisini kaydet
                          setPendingHavalePayment({
                            currency: selectedCurrency,
                            amount: Number(receivedAmount),
                            amountTL: currentAmountTL,
                            rate: getExchangeRate(selectedCurrency)
                          });
                          // Banka hesapları modalını aç
                          await openBankAccountsModal();
                        }}
                        disabled={!receivedAmount || currentAmountTL <= 0}
                      >
                        <Building2 size={16} /> Havale
                      </button>
                      <button
                        className={`method-btn credit ${showCreditOptions ? 'active' : ''}`}
                        onClick={() => {
                          if (selectedCustomer?.isAnonymous) {
                            toast.warning('Kayıtsız müşteriye açık hesap kullanılamaz!');
                            return;
                          }
                          setShowCreditOptions(!showCreditOptions);
                        }}
                        disabled={!receivedAmount || currentAmountTL <= 0 || selectedCustomer?.isAnonymous}
                        title={selectedCustomer?.isAnonymous ? 'Kayıtsız müşteri için kullanılamaz' : ''}
                      >
                        <ClipboardList size={16} /> Açık Hesap
                      </button>
                      <button
                        className="method-btn virtual-pos"
                        onClick={() => { addPayment('Sanal Pos'); setShowCreditOptions(false); }}
                        disabled={!receivedAmount || currentAmountTL <= 0}
                      >
                        <Smartphone size={16} /> Sanal Pos
                      </button>
                    </div>

                    {/* Açık Hesap Seçenekleri */}
                    {showCreditOptions && receivedAmount && currentAmountTL > 0 && (
                      <div className="credit-options" ref={creditOptionsRef}>
                        <div className="credit-option" onClick={() => {
                          if (currentAmountTL > remaining + 0.01) {
                            toast.warning(`Girilen tutar kalan tutarı (₺${remaining.toFixed(2)}) aşamaz!`);
                            return;
                          }
                          const newPayment = {
                            id: Date.now(),
                            method: 'Açık Hesap (TL)',
                            currency: 'TRY',
                            amount: currentAmountTL,
                            amountTL: currentAmountTL,
                            creditType: 'TL',
                            rate: 1
                          };
                          setPartialPayments([...partialPayments, newPayment]);
                          setReceivedAmount('');
                          setShowCreditOptions(false);
                        }}>
                          <span className="credit-option-icon">₺</span>
                          <div className="credit-option-info">
                            <span className="credit-option-title">TL Olarak Kaydet</span>
                            <span className="credit-option-value">₺{currentAmountTL.toFixed(2)} borç</span>
                          </div>
                        </div>
                        <div className="credit-option" onClick={() => {
                          if (currentAmountTL > remaining + 0.01) {
                            toast.warning(`Girilen tutar kalan tutarı (₺${remaining.toFixed(2)}) aşamaz!`);
                            return;
                          }
                          const goldPrice = getSatisRate('GOLD');
                          const goldGram = goldPrice > 0 ? (currentAmountTL / goldPrice).toFixed(3) : 0;
                          const newPayment = {
                            id: Date.now(),
                            method: 'Açık Hesap (Gram)',
                            currency: 'GOLD',
                            amount: parseFloat(goldGram),
                            amountTL: currentAmountTL,
                            creditType: 'GRAM',
                            goldRate: goldPrice,
                            rate: goldPrice
                          };
                          setPartialPayments([...partialPayments, newPayment]);
                          setReceivedAmount('');
                          setShowCreditOptions(false);
                        }}>
                          <span className="credit-option-icon">Au</span>
                          <div className="credit-option-info">
                            <span className="credit-option-title">Gram Altın Olarak Kaydet</span>
                            <span className="credit-option-value">
                              {(() => {
                                const goldPrice = getSatisRate('GOLD');
                                return goldPrice > 0 ? (currentAmountTL / goldPrice).toFixed(3) : '?';
                              })()} gram borç
                              <small> (Gram: ₺{getSatisRate('GOLD').toLocaleString('tr-TR')})</small>
                            </span>
                          </div>
                        </div>
                        <div className="credit-option silver" onClick={() => {
                          if (currentAmountTL > remaining + 0.01) {
                            toast.warning(`Girilen tutar kalan tutarı (₺${remaining.toFixed(2)}) aşamaz!`);
                            return;
                          }
                          const silverPrice = getSatisRate('SILVER');
                          const silverGram = silverPrice > 0 ? (currentAmountTL / silverPrice).toFixed(2) : 0;
                          const newPayment = {
                            id: Date.now(),
                            method: 'Açık Hesap (Gümüş)',
                            currency: 'SILVER',
                            amount: parseFloat(silverGram),
                            amountTL: currentAmountTL,
                            creditType: 'GUMUS',
                            silverRate: silverPrice,
                            rate: silverPrice
                          };
                          setPartialPayments([...partialPayments, newPayment]);
                          setReceivedAmount('');
                          setShowCreditOptions(false);
                        }}>
                          <span className="credit-option-icon">Ag</span>
                          <div className="credit-option-info">
                            <span className="credit-option-title">Gram Gümüş Olarak Kaydet</span>
                            <span className="credit-option-value">
                              {(() => {
                                const silverPrice = getSatisRate('SILVER');
                                return silverPrice > 0 ? (currentAmountTL / silverPrice).toFixed(2) : '?';
                              })()} gram borç
                              <small> (Gram: ₺{getSatisRate('SILVER').toLocaleString('tr-TR')})</small>
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn-cancel" onClick={() => {
                  setShowCashModal(false);
                  setPartialPayments([]);
                  setPendingHavalePayment(null);
                }}>İptal</button>
                <button
                  className="btn-save"
                  onClick={completePayment}
                  disabled={remaining > 0}
                >
                  {remaining <= 0 ? 'Ödemeyi Tamamla' : `Kalan: ₺${remaining.toLocaleString('tr-TR', {minimumFractionDigits: 2})}`}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Nakit Ödeme Modal (Para Üstü Hesaplama) */}
      {showNakitModal && (
        <div className={`modal-overlay ${closingModal === 'nakit' ? 'closing' : ''}`}>
          <div className="modal card-payment-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2><Banknote size={20} style={{ marginRight: '8px' }} /> Nakit Ödeme</h2>
              <button className="close-btn" onClick={() => handleCloseModal('nakit', setShowNakitModal)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="card-payment-total">
                <span className="card-payment-label">Ödenecek Tutar</span>
                <span className="card-payment-amount">₺{Math.max(0, total - customerGiftCardDiscount).toFixed(2)}</span>
              </div>
              {customerGiftCardDiscount > 0 && (
                <div style={{ textAlign: 'center', fontSize: '13px', color: '#059669', fontWeight: 600, marginBottom: '8px' }}>
                  Müşteri Bakiyesi: ₺{customerGiftCardDiscount.toFixed(2)}
                </div>
              )}
              <div className="card-payment-info">
                <Banknote size={48} className="card-icon" />
                <p>Nakit ödeme alınacaktır.</p>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowNakitModal(false)}>İptal</button>
              <button
                className="btn-save"
                onClick={() => {
                  handlePayment('Nakit');
                  setShowNakitModal(false);
                }}
              >
                Ödemeyi Tamamla
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Kredi Kartı Ödeme Modal */}
      {showCardModal && (
        <div className={`modal-overlay ${closingModal === 'card' ? 'closing' : ''}`}>
          <div className="modal card-payment-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2><CreditCard size={20} style={{ marginRight: '8px' }} /> Kredi Kartı ile Ödeme</h2>
              <button className="close-btn" onClick={() => handleCloseModal('card', setShowCardModal)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="card-payment-total">
                <span className="card-payment-label">Ödenecek Tutar</span>
                <span className="card-payment-amount">₺{Math.max(0, total - customerGiftCardDiscount).toFixed(2)}</span>
              </div>
              {customerGiftCardDiscount > 0 && (
                <div style={{ textAlign: 'center', fontSize: '13px', color: '#059669', fontWeight: 600, marginBottom: '8px' }}>
                  Müşteri Bakiyesi: ₺{customerGiftCardDiscount.toFixed(2)}
                </div>
              )}
              <div className="card-payment-info">
                <CreditCard size={48} className="card-icon" />
                <p>Kredi kartı ile ödeme alınacaktır.</p>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowCardModal(false)}>İptal</button>
              <button
                className="btn-save"
                onClick={() => {
                  handlePayment('Kredi Kartı');
                  setShowCardModal(false);
                }}
              >
                Ödemeyi Tamamla
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sanal Pos (iyzico) Ödeme Modal */}
      {showVirtualPosModal && (
        <div className="modal-overlay">
          <div className="modal card-payment-modal virtual-pos-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2><Smartphone size={20} style={{ marginRight: '8px' }} /> Sanal Pos (iyzico) ile Ödeme</h2>
              <button className="close-btn" onClick={() => setShowVirtualPosModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="card-payment-total">
                <span className="card-payment-label">Ödenecek Tutar</span>
                <span className="card-payment-amount">₺{total.toFixed(2)}</span>
              </div>

              <div className="card-payment-info virtual-pos-info">
                <Smartphone size={48} className="card-icon" style={{ color: '#0f766e' }} />
                <p>iyzico sanal pos ile ödeme alınacaktır.</p>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowVirtualPosModal(false)}>İptal</button>
              <button
                className="btn-save virtual-pos-btn"
                onClick={() => {
                  handlePayment('Sanal Pos');
                  setShowVirtualPosModal(false);
                }}
              >
                Ödemeyi Onayla
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Ürün ve Fiyat Sorgula Modal */}
      {showProductLookupModal && (
        <div className={`modal-overlay ${closingModal === 'productLookup' ? 'closing' : ''}`}>
          <div className="modal product-lookup-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2><SearchCheck size={20} style={{ marginRight: '8px' }} /> Ürün ve Fiyat Sorgula</h2>
              <button className="close-btn" onClick={closeLookupModal}>✕</button>
            </div>
            <div className="modal-body lookup-body">
              <div className="lookup-container">
                {/* Sol Panel - Arama ve Liste */}
                <div className="lookup-left">
                  {/* Arama */}
                  <div className="lookup-search">
                    <input
                      type="text"
                      placeholder="Barkod veya ürün adı ile ara..."
                      value={lookupSearchQuery}
                      onChange={(e) => {
                        setLookupSearchQuery(e.target.value);
                        searchProductLookup(e.target.value);
                      }}
                      autoFocus
                    />
                  </div>

                  {/* Filtreler - Mağazaya göre kategoriler veya tür seçimi */}
                  {(() => {
                    // Mağaza tipini belirle
                    const storeId = selectedStore?.id || user?.store_id;
                    const isGoldStore = storeId === 1; // Sadece altın mağazası geri butonunu gizler

                    if (lookupFilters.category) {
                      // Kategori seçildi - sadece geri butonu ve seçili kategori
                      return (
                        <div className="lookup-category-cards">
                          <div
                            className="category-back-btn"
                            onClick={() => {
                              const newFilters = { ...lookupFilters, category: '' };
                              setLookupFilters(newFilters);
                              searchProductLookup(lookupSearchQuery, newFilters);
                            }}
                          >
                            ←
                          </div>
                          <div className="category-card active">
                            {lookupFilters.category}
                          </div>
                        </div>
                      );
                    } else if (lookupFilters.metal_type) {
                      // Metal türü seçili - kategorileri göster
                      return (
                        <div className="lookup-category-cards">
                          {!isGoldStore && (
                            <div
                              className="category-back-btn"
                              onClick={() => {
                                const newFilters = { ...lookupFilters, metal_type: '', category: '' };
                                setLookupFilters(newFilters);
                                searchProductLookup(lookupSearchQuery, newFilters);
                              }}
                            >
                              ←
                            </div>
                          )}
                          {(lookupFilters.metal_type === 'gold' ? ['Kolye', 'Yüzük', 'Bileklik', 'Küpe', 'Set', 'Zincir', 'Kelepçe', 'Hediyelik', 'Diğer'] :
                            lookupFilters.metal_type === 'silver' ? ['Kolye', 'Yüzük', 'Bileklik', 'Küpe', 'Set', 'Zincir', 'Diğer'] :
                            lookupFilters.metal_type === 'aksesuar' ? ['Gümüş Aksesuar', 'Çelik Aksesuar', 'Saat', 'Diğer'] :
                            lookupFilters.metal_type === 'steel' ? ['Kolye', 'Bileklik', 'Yüzük', 'Küpe', 'Set', 'Diğer'] :
                            lookupFilters.metal_type === 'zippo' ? ['Zippo Çakmak', 'Zippo Aksesuar', 'Diğer'] :
                            lookupFilters.metal_type === 'brands' ? ['Kolye', 'Bileklik', 'Yüzük', 'Küpe', 'Halhal'] :
                            lookupFilters.metal_type === 'sarf' ? ['Hediyelik', 'Mücevher Kutusu (Işıklı)', 'Tesbih Kutusu', 'Parlatma İlacı', 'Diğer'] :
                            []
                          ).map(cat => (
                            <div
                              key={cat}
                              className="category-card"
                              onClick={() => {
                                const newFilters = { ...lookupFilters, category: cat };
                                setLookupFilters(newFilters);
                                searchProductLookup(lookupSearchQuery, newFilters);
                              }}
                            >
                              {cat}
                            </div>
                          ))}
                        </div>
                      );
                    } else {
                      // Metal türü seçilmedi - tür kartlarını göster
                      // Gümüş mağazaları (id=2,3) için altın hariç göster
                      const isSilverStore = storeId === 2 || storeId === 3;
                      return (
                        <div className="lookup-filter-cards">
                          {!isSilverStore && (
                            <div
                              className="filter-card gold"
                              onClick={() => {
                                const newFilters = { ...lookupFilters, metal_type: 'gold', category: '' };
                                setLookupFilters(newFilters);
                                searchProductLookup(lookupSearchQuery, newFilters);
                              }}
                            >
                              Altın
                            </div>
                          )}
                          <div
                            className="filter-card silver"
                            onClick={() => {
                              const newFilters = { ...lookupFilters, metal_type: 'silver', category: '' };
                              setLookupFilters(newFilters);
                              searchProductLookup(lookupSearchQuery, newFilters);
                            }}
                          >
                            Gümüş
                          </div>
                          <div
                            className="filter-card aksesuar"
                            onClick={() => {
                              const newFilters = { ...lookupFilters, metal_type: 'aksesuar', category: '' };
                              setLookupFilters(newFilters);
                              searchProductLookup(lookupSearchQuery, newFilters);
                            }}
                          >
                            Aksesuar
                          </div>
                          <div
                            className="filter-card steel"
                            onClick={() => {
                              const newFilters = { ...lookupFilters, metal_type: 'steel', category: '' };
                              setLookupFilters(newFilters);
                              searchProductLookup(lookupSearchQuery, newFilters);
                            }}
                          >
                            Çelik
                          </div>
                          {isSilverStore && (
                            <>
                              <div
                                className="filter-card zippo"
                                onClick={() => {
                                  const newFilters = { ...lookupFilters, metal_type: 'zippo', category: '' };
                                  setLookupFilters(newFilters);
                                  searchProductLookup(lookupSearchQuery, newFilters);
                                }}
                              >
                                Zippo
                              </div>
                              <div
                                className="filter-card brands"
                                onClick={() => {
                                  const newFilters = { ...lookupFilters, metal_type: 'brands', category: '' };
                                  setLookupFilters(newFilters);
                                  searchProductLookup(lookupSearchQuery, newFilters);
                                }}
                              >
                                Markalar
                              </div>
                              <div
                                className="filter-card sarf"
                                onClick={() => {
                                  const newFilters = { ...lookupFilters, metal_type: 'sarf', category: '' };
                                  setLookupFilters(newFilters);
                                  searchProductLookup(lookupSearchQuery, newFilters);
                                }}
                                style={{ background: 'linear-gradient(135deg, #6b7280, #4b5563)', color: '#fff' }}
                              >
                                Sarf Malzemeleri
                              </div>
                            </>
                          )}
                        </div>
                      );
                    }
                  })()}

                  {/* Sonuçlar */}
                  <div className="lookup-results">
                    {!Array.isArray(lookupSearchResults) || lookupSearchResults.length === 0 ? (
                      <div className="no-results">{lookupSearchQuery || lookupFilters.category || lookupFilters.metal_type || lookupFilters.brand ? 'Ürün bulunamadı' : 'Lütfen filtre seçin veya arama yapın'}</div>
                    ) : (
                      lookupSearchResults.map(product => (
                        <div
                          key={product.id}
                          className={`lookup-item ${selectedLookupProduct?.id === product.id ? 'selected' : ''}`}
                          onClick={() => selectLookupProduct(product)}
                        >
                          <div className="lookup-item-image">
                            {product.image ? (
                              <img src={`${IMAGE_HOST}${product.image}`} alt={product.name} loading="lazy" />
                            ) : (
                              <div className="no-image-thumb">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#bdc3c7" strokeWidth="1.5">
                                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                                  <circle cx="8.5" cy="8.5" r="1.5"></circle>
                                  <polyline points="21 15 16 10 5 21"></polyline>
                                </svg>
                              </div>
                            )}
                          </div>
                          <div className="lookup-item-info">
                            <span className="lookup-item-name">{product.name}</span>
                            <span className="lookup-item-barcode">{product.barcode}</span>
                          </div>
                          <span className="lookup-item-price">
                            ₺{(product.metal_type === 'gold'
                              ? calculateGoldPrice(product)
                              : (product.metal_type === 'silver' || (product.metal_type === 'aksesuar' && product.category === 'Gümüş Aksesuar'))
                                ? calculateSilverPrice(product)
                                : convertToTRY(Number(product.sell_price), product.currency)
                            ).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Sağ Panel - Detaylar */}
                <div className="lookup-right">
                  {selectedLookupProduct ? (
                    <>
                      {/* Ürün Fotoğrafı */}
                      <div className="lookup-product-image">
                        {selectedLookupProduct.image ? (
                          <img src={`${IMAGE_HOST}${selectedLookupProduct.image}`} alt={selectedLookupProduct.name} loading="lazy" />
                        ) : (
                          <div className="no-image">
                            <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="#bdc3c7" strokeWidth="1.5">
                              <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
                              <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
                              <line x1="12" y1="22.08" x2="12" y2="12"></line>
                            </svg>
                            <span>Fotoğraf Yok</span>
                          </div>
                        )}
                      </div>

                      {/* Ürün Bilgileri */}
                      <div className="lookup-product-info">
                        <h3>{selectedLookupProduct.name}</h3>
                        <div className="lookup-price-tag">
                          ₺{(selectedLookupProduct.metal_type === 'gold'
                            ? calculateGoldPrice(selectedLookupProduct)
                            : (selectedLookupProduct.metal_type === 'silver' || (selectedLookupProduct.metal_type === 'aksesuar' && selectedLookupProduct.category === 'Gümüş Aksesuar'))
                              ? calculateSilverPrice(selectedLookupProduct)
                              : convertToTRY(Number(selectedLookupProduct.sell_price), selectedLookupProduct.currency)
                          ).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>

                        <div className="lookup-details">
                          <div className="detail-row">
                            <span className="label">Barkod:</span>
                            <span className="value">{selectedLookupProduct.barcode || '-'}</span>
                          </div>
                          <div className="detail-row">
                            <span className="label">Kategori:</span>
                            <span className="value">{selectedLookupProduct.category || '-'}</span>
                          </div>
                          <div className="detail-row">
                            <span className="label">Metal:</span>
                            <span className="value">{selectedLookupProduct.metal_type || '-'}</span>
                          </div>
                          <div className="detail-row">
                            <span className="label">Renk:</span>
                            <span className="value">{selectedLookupProduct.color || '-'}</span>
                          </div>
                          <div className="detail-row">
                            <span className="label">Beden:</span>
                            <span className="value">{selectedLookupProduct.size || '-'}</span>
                          </div>
                          <div className="detail-row">
                            <span className="label">Marka:</span>
                            <span className="value">{selectedLookupProduct.brand || '-'}</span>
                          </div>
                          <div className="detail-row">
                            <span className="label">Ağırlık:</span>
                            <span className="value">{selectedLookupProduct.weight ? `${selectedLookupProduct.weight} gr` : '-'}</span>
                          </div>
                          {selectedLookupProduct.metal_type === 'gold' && (
                            <div className="detail-row">
                              <span className="label">Ayar:</span>
                              <span className="value">{selectedLookupProduct.gold_karat ? `${selectedLookupProduct.gold_karat} Ayar` : '-'}</span>
                            </div>
                          )}
                          <div className="detail-row">
                            <span className="label">Tedarikçi:</span>
                            <span className="value">{selectedLookupProduct.supplier || '-'}</span>
                          </div>
                        </div>

                        {/* Mağaza Stokları */}
                        <div className="lookup-stocks">
                          <h4>Mağaza Stokları</h4>
                          <div className="stock-list">
                            {(() => {
                              const metalType = selectedLookupProduct.metal_type;
                              const isGold = metalType === 'gold';
                              const storeStocks = selectedLookupProduct.store_stocks || [];

                              // Altın için store_id=1 (Akik Gold Jewellery), Gümüş için store_id=2,3,4 (Depo dahil)
                              const filteredStocks = storeStocks.filter(stock => {
                                if (isGold) {
                                  return stock.store_id === 1;
                                } else {
                                  return stock.store_id === 2 || stock.store_id === 3 || stock.store_id === 4;
                                }
                              });

                              if (filteredStocks.length > 0) {
                                return filteredStocks.map(stock => (
                                  <div key={stock.store_id} className="stock-item">
                                    <span className="store-name">{stock.store_name}</span>
                                    <span className={`stock-qty ${stock.stock > 0 ? 'in-stock' : 'out-of-stock'}`}>
                                      {stock.stock} adet
                                    </span>
                                  </div>
                                ));
                              } else {
                                // Stok kaydı yoksa metal tipine göre mağaza göster
                                const relevantStores = stores.filter(store => {
                                  if (isGold) {
                                    return store.id === 1;
                                  } else {
                                    return store.id === 2 || store.id === 3 || store.id === 4;
                                  }
                                });
                                return relevantStores.map(store => (
                                  <div key={store.id} className="stock-item">
                                    <span className="store-name">{store.name}</span>
                                    <span className="stock-qty out-of-stock">0 adet</span>
                                  </div>
                                ));
                              }
                            })()}
                          </div>
                          <div className="total-stock">
                            Toplam Stok: <strong>{(() => {
                              const metalType = selectedLookupProduct.metal_type;
                              const isGold = metalType === 'gold';
                              const storeStocks = selectedLookupProduct.store_stocks || [];

                              const filteredStocks = storeStocks.filter(stock => {
                                if (isGold) {
                                  return stock.store_id === 1;
                                } else {
                                  return stock.store_id === 2 || stock.store_id === 3 || stock.store_id === 4;
                                }
                              });

                              return filteredStocks.reduce((sum, s) => sum + (s.stock || 0), 0) || 0;
                            })()} adet</strong>
                          </div>
                        </div>

                        {/* Sepete Ekle Butonu */}
                        <button
                          className="add-to-cart-btn"
                          onClick={() => {
                            addToCart(selectedLookupProduct);
                            closeLookupModal();
                          }}
                          disabled={!selectedLookupProduct.stock || selectedLookupProduct.stock <= 0}
                        >
                          {selectedLookupProduct.stock > 0 ? '🛒 Sepete Ekle' : 'Stok Yok'}
                        </button>
                      </div>
                    </>
                  ) : (
                    <div className="no-selection">
                      <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="#bdc3c7" strokeWidth="1.5">
                        <circle cx="11" cy="11" r="8"></circle>
                        <path d="m21 21-4.35-4.35"></path>
                      </svg>
                      <p>Detayları görmek için bir ürün seçin</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===== TAHSİLAT MODALLARI ===== */}

      {/* Tahsilat - Müşteri Seçim Modal */}
      {showCollectionModal && (
        <div className={`modal-overlay ${closingModal === 'collection' ? 'closing' : ''}`}>
          <div className="modal collection-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2><Coins size={20} style={{ marginRight: '8px' }} /> Tahsilat - Müşteri Seç</h2>
              <button className="close-btn" onClick={() => { setShowCollectionHistory(false); handleCloseModal('collection', setShowCollectionModal); }}>✕</button>
            </div>
            <div className="modal-body">
              <button
                style={{
                  width: '100%',
                  padding: '10px 16px',
                  marginBottom: '12px',
                  border: '1px solid',
                  borderColor: showCollectionHistory ? '#0d9488' : '#cbd5e1',
                  borderRadius: '8px',
                  background: showCollectionHistory ? '#0d9488' : '#f8fafc',
                  color: showCollectionHistory ? '#fff' : '#475569',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px'
                }}
                onClick={() => {
                  const next = !showCollectionHistory;
                  setShowCollectionHistory(next);
                  if (next) fetchCollectionHistory();
                }}
              >
                <Clock size={16} />
                {showCollectionHistory ? 'Müşteri Listesine Dön' : 'Son Tahsilatlar'}
              </button>

              {showCollectionHistory ? (
                loadingCollectionHistory ? (
                  <div className="loading">Yükleniyor...</div>
                ) : collectionHistory.length === 0 ? (
                  <div className="empty-state">
                    <span>📋</span>
                    <p>Henüz tahsilat kaydı bulunmuyor</p>
                  </div>
                ) : (
                  <>
                  <div style={{ marginBottom: '10px', position: 'relative' }}>
                    <Search size={16} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                    <input
                      type="text"
                      className="modal-input"
                      placeholder="Müşteri adı ile ara..."
                      value={collectionHistorySearch}
                      onChange={(e) => setCollectionHistorySearch(e.target.value)}
                      style={{ paddingLeft: '34px' }}
                    />
                  </div>
                  <div className="collection-customer-list" style={{ maxHeight: '55vh', overflowY: 'auto' }}>
                    {collectionHistory.filter(p => !collectionHistorySearch || p.customer_name?.toLowerCase().includes(collectionHistorySearch.toLowerCase())).map((payment, idx) => (
                      <div key={payment.id || idx} className="collection-customer-item" style={{ cursor: 'pointer', flexDirection: 'column', alignItems: 'stretch', gap: '4px', padding: '12px 16px' }} onClick={() => { setSelectedDebtPaymentDetail({ ...payment, total: payment.amount }); setShowDebtPaymentDetailModal(true); }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 'bold', fontSize: '14px', marginBottom: '2px' }}>
                              {payment.customer_name}
                            </div>
                            <div style={{ fontSize: '12px', color: '#64748b' }}>
                              {new Date(payment.created_at).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </div>
                            <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                              {payment.payment_method === 'cash' ? 'Nakit' :
                               payment.payment_method === 'credit_card' ? 'Kredi Kartı' :
                               payment.payment_method === 'havale' ? 'Havale/EFT' :
                               payment.payment_method === 'mixed' ? 'Karışık' :
                               payment.payment_method || '-'}
                              {payment.staff_name ? ` - ${payment.staff_name}` : ''}
                            </div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ fontWeight: 'bold', fontSize: '16px', color: '#16a34a', whiteSpace: 'nowrap' }}>
                              ₺{Number(payment.amount || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </span>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                let pDetails = null;
                                if (payment.payment_details) {
                                  try { pDetails = typeof payment.payment_details === 'string' ? JSON.parse(payment.payment_details) : payment.payment_details; } catch (e) {}
                                }
                                printCollectionReceipt({
                                  customerName: payment.customer_name,
                                  customerPhone: payment.customer_phone,
                                  debtDescription: payment.debt_description || payment.notes || 'Borç Tahsilatı',
                                  originalAmount: payment.previous_debt ?? payment.amount,
                                  paidAmount: payment.amount,
                                  remainingAmount: Math.max(0, parseFloat(payment.remaining_debt) || 0),
                                  isFullyPaid: !(parseFloat(payment.remaining_debt) > 0.01),
                                  paymentMethod: payment.payment_method,
                                  paymentDetails: Array.isArray(pDetails) ? pDetails : null,
                                  staffName: payment.staff_name,
                                  date: payment.created_at
                                });
                              }}
                              style={{
                                background: '#f1f5f9',
                                border: '1px solid #cbd5e1',
                                borderRadius: '6px',
                                padding: '6px 10px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                fontSize: '12px',
                                color: '#475569'
                              }}
                              title="Fiş Yazdır"
                            >
                              <Printer size={14} />
                            </button>
                            {user?.role === 'admin' && (
                            <button
                              onClick={(e) => { e.stopPropagation(); openEditCollectionPayment(payment); }}
                              style={{
                                background: '#eff6ff',
                                border: '1px solid #93c5fd',
                                borderRadius: '6px',
                                padding: '6px 10px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                fontSize: '12px',
                                color: '#2563eb'
                              }}
                              title="Tahsilat Düzenle"
                            >
                              <Pencil size={14} />
                            </button>
                            )}
                            {user?.role === 'admin' && (
                            <button
                              onClick={(e) => { e.stopPropagation(); cancelCollectionPayment(payment); }}
                              style={{
                                background: '#fef2f2',
                                border: '1px solid #fca5a5',
                                borderRadius: '6px',
                                padding: '6px 10px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                fontSize: '12px',
                                color: '#dc2626'
                              }}
                              title="Tahsilat İptal"
                            >
                              <X size={14} />
                            </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  </>
                )
              ) : (
                <>
              <input
                type="text"
                className="modal-input"
                placeholder="Müşteri adı veya telefon ile ara..."
                value={collectionCustomerSearchQuery}
                onChange={(e) => {
                  setCollectionCustomerSearchQuery(e.target.value);
                  searchCollectionCustomer(e.target.value);
                }}
                autoFocus
              />
              <div className="collection-customer-list">
                {collectionCustomerSearchResults.length === 0 ? (
                  <div className="empty-state">
                    <span>🔍</span>
                    <p>Borçlu müşteri bulunamadı</p>
                  </div>
                ) : (
                  collectionCustomerSearchResults
                    .filter(customer => {
                      const s = collectionDebtSummary[customer.id];
                      if (!s) return false;
                      const tlNet = Math.max(0, Number(s.total_tl_debt || 0) - Number(s.total_tl_credit || 0));
                      const total = tlNet + Number(s.total_gold_debt || 0) + Number(s.total_silver_debt || 0);
                      return total > 0.001;
                    })
                    .map(customer => {
                      const summary = collectionDebtSummary[customer.id];
                      const tlDebt = Math.max(0, Number(summary?.total_tl_debt || 0) - Number(summary?.total_tl_credit || 0));
                      const goldGram = Number(summary?.total_gold_debt || 0);
                      const silverGram = Number(summary?.total_silver_debt || 0);
                      const goldTL = goldGram * getCurrentGoldPrice();
                      const silverTL = silverGram * getCurrentSilverPrice();
                      const totalDebt = tlDebt + goldTL + silverTL;

                      return (
                        <div
                          key={customer.id}
                          className="collection-customer-item"
                          onClick={() => fetchCollectionCustomerDebts(customer)}
                        >
                          <div className="customer-avatar">{customer.name?.charAt(0) || '?'}</div>
                          <div className="customer-info">
                            <span className="name">{customer.name}</span>
                            <span className="phone">{customer.phone || '-'}</span>
                          </div>
                          <div className="customer-debt">
                            {tlDebt > 0 && (
                              <span className="debt-tl">₺{tlDebt.toLocaleString('tr-TR', {maximumFractionDigits: 0})}</span>
                            )}
                            {goldGram > 0 && (
                              <span className="debt-gram debt-gold">
                                {goldGram.toFixed(2)} gr = ₺{goldTL.toLocaleString('tr-TR', {maximumFractionDigits: 0})}
                              </span>
                            )}
                            {silverGram > 0 && (
                              <span className="debt-gram debt-silver">
                                {silverGram.toFixed(2)} gr = ₺{silverTL.toLocaleString('tr-TR', {maximumFractionDigits: 0})}
                              </span>
                            )}
                            {(tlDebt > 0 || goldGram > 0 || silverGram > 0) && (
                              <span className="debt-total">Toplam: ₺{totalDebt.toLocaleString('tr-TR', {maximumFractionDigits: 0})}</span>
                            )}
                          </div>
                        </div>
                      );
                    })
                )}
                {collectionCustomerSearchResults.filter(customer => collectionDebtSummary[customer.id]).length === 0 && collectionCustomerSearchResults.length > 0 && (
                  <div className="empty-state">
                    <span>✅</span>
                    <p>Borçlu müşteri bulunamadı</p>
                  </div>
                )}
              </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Tahsilat - Borç Detay Modal */}
      {showCollectionDebtModal && (
        <div className={`modal-overlay ${closingModal === 'collectionDebt' ? 'closing' : ''}`}>
          <div className="modal large collection-debt-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2><Coins size={20} style={{ marginRight: '8px' }} /> {collectionSelectedCustomer?.name} - Borç Detayı</h2>
              <button className="close-btn" onClick={() => handleCloseModal('collectionDebt', setShowCollectionDebtModal)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                <button className="back-to-list-btn" onClick={() => { setShowCollectionHistory(false); closeCollectionDebtModal(); }} style={{ margin: 0 }}>
                  ← Müşteri Listesine Dön
                </button>
                <button
                  className="back-to-list-btn"
                  style={{
                    margin: 0,
                    background: showCollectionHistory ? '#0d9488' : '#f1f5f9',
                    color: showCollectionHistory ? '#fff' : '#475569',
                    borderColor: showCollectionHistory ? '#0d9488' : '#cbd5e1'
                  }}
                  onClick={() => {
                    const next = !showCollectionHistory;
                    setShowCollectionHistory(next);
                    if (next) { setCollectionHistorySearch(''); fetchCollectionHistory(); }
                  }}
                >
                  <Clock size={14} style={{ marginRight: '4px' }} />
                  Son Tahsilatlar
                </button>
              </div>

              {showCollectionHistory ? (
                loadingCollectionHistory ? (
                  <div className="loading">Yükleniyor...</div>
                ) : collectionHistory.length === 0 ? (
                  <div className="empty-state">
                    <span>📋</span>
                    <p>Henüz tahsilat kaydı bulunmuyor</p>
                  </div>
                ) : (
                  <>
                  <div style={{ marginBottom: '10px', position: 'relative' }}>
                    <Search size={16} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                    <input
                      type="text"
                      className="modal-input"
                      placeholder="Müşteri adı ile ara..."
                      value={collectionHistorySearch}
                      onChange={(e) => setCollectionHistorySearch(e.target.value)}
                      style={{ paddingLeft: '34px' }}
                    />
                  </div>
                  <div className="debts-list">
                    {collectionHistory.filter(p => !collectionHistorySearch || p.customer_name?.toLowerCase().includes(collectionHistorySearch.toLowerCase())).map((payment, idx) => (
                      <div key={payment.id || idx} className="debt-card" style={{ padding: '12px 16px', cursor: 'pointer' }} onClick={() => { setSelectedDebtPaymentDetail({ ...payment, total: payment.amount }); setShowDebtPaymentDetailModal(true); }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 'bold', fontSize: '14px', marginBottom: '4px' }}>
                              {payment.customer_name}
                            </div>
                            <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '2px' }}>
                              {new Date(payment.created_at).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </div>
                            <div style={{ fontSize: '12px', color: '#64748b' }}>
                              {payment.payment_method === 'cash' ? '💵 Nakit' :
                               payment.payment_method === 'credit_card' ? '💳 Kredi Kartı' :
                               payment.payment_method === 'havale' ? '🏦 Havale/EFT' :
                               payment.payment_method === 'mixed' ? '🔀 Karışık' :
                               payment.payment_method || '-'}
                              {payment.staff_name ? ` • ${payment.staff_name}` : ''}
                            </div>
                            {payment.debt_description || payment.notes ? (
                              <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>
                                {payment.debt_description || payment.notes}
                              </div>
                            ) : null}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <div style={{ fontWeight: 'bold', fontSize: '16px', color: '#16a34a', whiteSpace: 'nowrap' }}>
                              ₺{Number(payment.amount || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </div>
                            {(() => {
                              const saleId = payment.sale_id || (() => {
                                const desc = payment.debt_description || payment.notes || '';
                                const match = desc.match(/Satış Fiş:\s*(\d+)/);
                                return match ? match[1] : null;
                              })();
                              return saleId ? (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openSaleDetailModal({ id: parseInt(saleId) });
                                  }}
                                  style={{
                                    background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '6px',
                                    padding: '6px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center',
                                    gap: '4px', fontSize: '12px', color: '#2563eb', transition: 'all 0.15s'
                                  }}
                                  title="İlgili Fişi Aç"
                                  onMouseEnter={e => { e.currentTarget.style.background = '#2563eb'; e.currentTarget.style.color = '#fff'; }}
                                  onMouseLeave={e => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.color = '#2563eb'; }}
                                >
                                  <ExternalLink size={14} />
                                </button>
                              ) : null;
                            })()}
                            <button
                              onClick={() => {
                                let pdtls = null;
                                if (payment.payment_details) {
                                  try { pdtls = typeof payment.payment_details === 'string' ? JSON.parse(payment.payment_details) : payment.payment_details; } catch (e) {}
                                }
                                const receiptData = {
                                  customerName: payment.customer_name,
                                  customerPhone: payment.customer_phone,
                                  debtDescription: payment.debt_description || payment.notes || 'Borç Tahsilatı',
                                  originalAmount: payment.previous_debt ?? payment.amount,
                                  paidAmount: payment.amount,
                                  remainingAmount: Math.max(0, parseFloat(payment.remaining_debt) || 0),
                                  isFullyPaid: !(parseFloat(payment.remaining_debt) > 0.01),
                                  paymentMethod: payment.payment_method,
                                  paymentDetails: Array.isArray(pdtls) ? pdtls : null,
                                  staffName: payment.staff_name,
                                  date: payment.created_at
                                };
                                printCollectionReceipt(receiptData);
                              }}
                              style={{
                                background: '#f1f5f9',
                                border: '1px solid #cbd5e1',
                                borderRadius: '6px',
                                padding: '6px 10px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                fontSize: '12px',
                                color: '#475569'
                              }}
                              title="Fiş Yazdır"
                            >
                              <Printer size={14} />
                            </button>
                            <button
                              onClick={() => cancelCollectionPayment(payment)}
                              style={{
                                background: '#fef2f2',
                                border: '1px solid #fca5a5',
                                borderRadius: '6px',
                                padding: '6px 10px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                fontSize: '12px',
                                color: '#dc2626'
                              }}
                              title="Tahsilat İptal"
                            >
                              <X size={14} />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  </>
                )
              ) : loadingCollectionDebts ? (
                <div className="loading">Yükleniyor...</div>
              ) : collectionCustomerDebts.length === 0 ? (
                <div className="empty-state">
                  <span>✅</span>
                  <p>Bu müşterinin borcu bulunmuyor</p>
                </div>
              ) : (
                <div className="debts-list">
                  {/* Gruplanmış borçlar */}
                  {groupedCollectionDebts.map(group => {
                    // Güncel değeri hesapla
                    let currentValue = group.total_amount_tl;
                    if (group.debt_type === 'GRAM') {
                      currentValue = calculateCurrentGramValue(group.total_amount);
                    } else if (group.debt_type === 'GUMUS') {
                      currentValue = calculateCurrentSilverGramValue(group.total_amount);
                    }

                    return (
                    <div key={group.debt_type} className="debt-card grouped">
                      <div className="debt-header">
                        <div className="debt-info">
                          <span className={`debt-type ${group.debt_type === 'GRAM' ? 'gram' : group.debt_type === 'GUMUS' ? 'gumus' : 'tl'}`}>
                            {group.debt_type === 'GRAM' ? '🥇 Altın Borcu' : group.debt_type === 'GUMUS' ? '🥈 Gümüş Borcu' : '💵 TL Borcu'}
                          </span>
                          <span className="debt-count" style={{ marginLeft: '8px', fontSize: '12px', color: '#64748b' }}>
                            ({group.debts.length} adet işlem)
                          </span>
                        </div>
                        <div className="debt-amount" style={{ textAlign: 'right' }}>
                          {(group.debt_type === 'GRAM' || group.debt_type === 'GUMUS') ? (
                            <>
                              <div className="gram-amount" style={{ fontSize: '18px', fontWeight: 'bold', color: group.debt_type === 'GRAM' ? '#f59e0b' : '#6b7280' }}>
                                {group.total_amount.toFixed(group.debt_type === 'GUMUS' ? 2 : 3)} gr
                              </div>
                              <div className="tl-amount current-value" style={{ fontSize: '14px', color: '#64748b' }}>
                                = ₺{currentValue.toLocaleString('tr-TR', {maximumFractionDigits: 2})}
                              </div>
                            </>
                          ) : (
                            <div className="tl-amount main" style={{ fontSize: '20px', fontWeight: 'bold', color: '#16a34a' }}>
                              ₺{group.total_amount_tl.toLocaleString('tr-TR', {minimumFractionDigits: 2})}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Borç detayları - collapse edilebilir */}
                      <details style={{ marginTop: '12px' }}>
                        <summary style={{ cursor: 'pointer', color: '#64748b', fontSize: '13px', marginBottom: '8px' }}>
                          📋 Detayları göster
                        </summary>
                        <div style={{ paddingLeft: '12px', borderLeft: '2px solid #e2e8f0' }}>
                          {group.debts.map((debt, idx) => (
                            <div key={debt.id} style={{
                              padding: '8px 0',
                              borderBottom: idx < group.debts.length - 1 ? '1px dashed #e2e8f0' : 'none',
                              fontSize: '13px'
                            }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ color: '#64748b' }}>
                                  {new Date(debt.created_at).toLocaleDateString('tr-TR')}
                                  {debt.sale_id && ` - Fiş #${debt.sale_id}`}
                                </span>
                                <span style={{ fontWeight: '500' }}>
                                  {(group.debt_type === 'GRAM' || group.debt_type === 'GUMUS')
                                    ? `${Number(debt.amount).toFixed(group.debt_type === 'GUMUS' ? 2 : 3)} gr`
                                    : `₺${Number(debt.amount_tl).toLocaleString('tr-TR')}`
                                  }
                                </span>
                              </div>
                              {debt.description && (
                                <div style={{ color: '#94a3b8', fontSize: '12px', marginTop: '4px' }}>{debt.description}</div>
                              )}
                            </div>
                          ))}
                        </div>
                      </details>

                      <div className="debt-actions" style={{ marginTop: '16px' }}>
                        <button
                          className="btn-paid"
                          onClick={() => openGroupCollectionPaymentModal(group)}
                          style={{ width: '100%', padding: '12px', fontSize: '15px' }}
                        >
                          ✓ Toplam {(group.debt_type === 'GRAM' || group.debt_type === 'GUMUS')
                            ? `${group.total_amount.toFixed(group.debt_type === 'GUMUS' ? 2 : 3)} gr`
                            : `₺${group.total_amount_tl.toLocaleString('tr-TR')}`} Ödeme Al
                        </button>
                      </div>
                    </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Tahsilat - Ödeme Modal */}
      {showCollectionPaymentModal && selectedCollectionDebt && (
        <div className={`modal-overlay ${closingModal === 'collectionPayment' ? 'closing' : ''}`}>
          <div className="modal payment-modal" onClick={(e) => e.stopPropagation()} style={{ maxHeight: '95vh', display: 'flex', flexDirection: 'column' }}>
            <div className="modal-header" style={{ padding: '10px 20px', flexShrink: 0 }}>
              <h2>Ödeme Al</h2>
              <button className="close-btn" onClick={() => handleCloseModal('collectionPayment', setShowCollectionPaymentModal, () => setCollectionScrapData(prev => ({...prev, custom_purity_rate: null})))}>✕</button>
            </div>
            <div className="modal-body" style={{ padding: '10px 20px', overflowY: 'auto', flex: 1 }}>
              <div className="payment-info" style={{ padding: '8px 12px', marginBottom: '8px' }}>
                <div className="payment-info-row" style={{ marginBottom: '2px' }}>
                  <span>Müşteri:</span>
                  <strong>{collectionSelectedCustomer?.name}</strong>
                </div>
                {selectedCollectionDebt.debt_type === 'GRAM' ? (
                  <div className="payment-info-row gram-debt-highlight" style={{ marginBottom: 0 }}>
                    <span>Altın:</span>
                    <strong>{Number(selectedCollectionDebt.amount).toFixed(3)} gr = ₺{calculateCurrentGramValue(selectedCollectionDebt.amount).toLocaleString('tr-TR', {minimumFractionDigits: 2})}</strong>
                  </div>
                ) : selectedCollectionDebt.debt_type === 'GUMUS' ? (
                  <div className="payment-info-row silver-debt-highlight" style={{ marginBottom: 0 }}>
                    <span>Gümüş:</span>
                    <strong>{Number(selectedCollectionDebt.amount).toFixed(2)} gr = ₺{calculateCurrentSilverGramValue(selectedCollectionDebt.amount).toLocaleString('tr-TR', {minimumFractionDigits: 2})}</strong>
                  </div>
                ) : (
                  <div className="payment-info-row" style={{ marginBottom: 0 }}>
                    <span>Borç:</span>
                    <strong>₺{Number(selectedCollectionDebt.amount_tl).toLocaleString('tr-TR', {minimumFractionDigits: 2})}</strong>
                  </div>
                )}
              </div>

              {/* Alınan Ödemeler - kompakt chip */}
              {collectionMixedPayments.length > 0 && (
                <div style={{ marginBottom: '8px', display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center' }}>
                  {collectionMixedPayments.map(payment => (
                    <div key={payment.id} style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '3px 10px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '16px', fontSize: '0.78rem' }}>
                      <span style={{ fontWeight: 600, color: '#334155' }}>{payment.method}</span>
                      <span style={{ color: '#16a34a', fontWeight: 700 }}>
                        {payment.gramAmount
                          ? `${payment.gramAmount.toFixed(payment.debtType === 'GUMUS' ? 2 : 3)} gr`
                          : `₺${payment.amount.toLocaleString('tr-TR', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`}
                      </span>
                      <button onClick={() => removeCollectionPayment(payment.id)} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: '0 0 0 2px', fontSize: '13px', lineHeight: 1 }}>✕</button>
                    </div>
                  ))}
                  <span style={{ fontSize: '0.75rem', color: '#64748b', marginLeft: '4px' }}>
                    Toplam: <strong style={{ color: '#16a34a' }}>₺{getCollectionPaymentsTotal().toLocaleString('tr-TR', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</strong>
                    {getCollectionRemainingDebt() > 0.01 && (
                      <> · Kalan: <strong style={{ color: '#f59e0b' }}>₺{getCollectionRemainingDebt().toLocaleString('tr-TR', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</strong></>
                    )}
                    {getCollectionRemainingDebt() <= 0.01 && <> · <strong style={{ color: '#16a34a' }}>Tamamlandı</strong></>}
                  </span>
                </div>
              )}

              {!collectionScrapMode && getCollectionRemainingDebt() > 0.01 && (
                <div className="form-group">
                  {/* Altın/Gümüş borcu için TL/Gram seçeneği */}
                  {(selectedCollectionDebt?.debt_type === 'GRAM' || selectedCollectionDebt?.debt_type === 'GUMUS') && (
                    <div className="payment-input-mode-toggle" style={{ marginBottom: '10px' }}>
                      <label style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '6px', display: 'block' }}>Giriş Türü:</label>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          type="button"
                          className={`mode-toggle-btn ${collectionPaymentInputMode === 'tl' ? 'active' : ''}`}
                          onClick={() => { setCollectionPaymentInputMode('tl'); setCollectionPaymentAmount(''); }}
                          style={{
                            flex: 1,
                            padding: '8px 12px',
                            border: collectionPaymentInputMode === 'tl' ? '2px solid #3b82f6' : '1px solid #d1d5db',
                            borderRadius: '8px',
                            background: collectionPaymentInputMode === 'tl' ? 'linear-gradient(135deg, #dbeafe, #bfdbfe)' : '#f9fafb',
                            color: collectionPaymentInputMode === 'tl' ? '#1e40af' : '#6b7280',
                            fontWeight: collectionPaymentInputMode === 'tl' ? '600' : '400',
                            cursor: 'pointer',
                            fontSize: '0.9rem'
                          }}
                        >
                          ₺ TL Gir
                        </button>
                        <button
                          type="button"
                          className={`mode-toggle-btn ${collectionPaymentInputMode === 'gram' ? 'active' : ''}`}
                          onClick={() => { setCollectionPaymentInputMode('gram'); setCollectionPaymentAmount(''); }}
                          style={{
                            flex: 1,
                            padding: '8px 12px',
                            border: collectionPaymentInputMode === 'gram' ? '2px solid #f59e0b' : '1px solid #d1d5db',
                            borderRadius: '8px',
                            background: collectionPaymentInputMode === 'gram' ? 'linear-gradient(135deg, #fef3c7, #fde68a)' : '#f9fafb',
                            color: collectionPaymentInputMode === 'gram' ? '#92400e' : '#6b7280',
                            fontWeight: collectionPaymentInputMode === 'gram' ? '600' : '400',
                            cursor: 'pointer',
                            fontSize: '0.9rem'
                          }}
                        >
                          ⚖️ Gram Gir
                        </button>
                      </div>
                    </div>
                  )}
                  <label>
                    {collectionPaymentInputMode === 'gram' && (selectedCollectionDebt?.debt_type === 'GRAM' || selectedCollectionDebt?.debt_type === 'GUMUS')
                      ? `Ödeme Miktarı (gr ${selectedCollectionDebt?.debt_type === 'GRAM' ? 'Altın' : 'Gümüş'})`
                      : 'Ödeme Tutarı (₺)'}
                  </label>
                  {collectionPaymentInputMode === 'gram' && (selectedCollectionDebt?.debt_type === 'GRAM' || selectedCollectionDebt?.debt_type === 'GUMUS') ? (
                    <input
                      className="collection-payment-input"
                      type="number"
                      step="0.001"
                      value={collectionPaymentAmount}
                      onChange={(e) => setCollectionPaymentAmount(e.target.value)}
                      placeholder="Gram miktarı girin..."
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') addCollectionPayment();
                        if (e.key === 'Tab' && !collectionPaymentAmount) {
                          e.preventDefault();
                          const gramRemaining = parseFloat(selectedCollectionDebt.amount) - collectionMixedPayments.reduce((sum, p) => sum + (p.gramAmount || 0), 0);
                          setCollectionPaymentAmount(gramRemaining > 0 ? gramRemaining.toFixed(selectedCollectionDebt?.debt_type === 'GUMUS' ? 2 : 3) : '');
                        }
                      }}
                    />
                  ) : (
                    <PriceInput
                      className="collection-payment-input"
                      value={parseFloat(collectionPaymentAmount) || 0}
                      onChange={(val) => setCollectionPaymentAmount(val)}
                      placeholder="Tutar girin..."
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') addCollectionPayment();
                        if (e.key === 'Tab' && !collectionPaymentAmount) {
                          e.preventDefault();
                          const remaining = getCollectionRemainingDebt();
                          if (remaining > 0) setCollectionPaymentAmount(remaining);
                        }
                      }}
                    />
                  )}
                  <span style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}><Lightbulb size={12} color="#00bfa6" /> Tab ile tüm tutarı doldur</span>
                </div>
              )}

              {/* Gram karşılığı göster (TL modunda) */}
              {!collectionScrapMode && collectionPaymentInputMode === 'tl' && (selectedCollectionDebt?.debt_type === 'GRAM' || selectedCollectionDebt?.debt_type === 'GUMUS') && collectionPaymentAmount && (
                (() => {
                  const rate = selectedCollectionDebt?.debt_type === 'GRAM' ? getCurrentGoldPrice() : getCurrentSilverPrice();
                  if (rate > 0) {
                    const gramEquiv = parseFloat(collectionPaymentAmount) / rate;
                    return (
                      <div className="gram-equivalent-info">
                        <span>Düşülecek Miktar:</span>
                        <strong>{gramEquiv.toFixed(selectedCollectionDebt?.debt_type === 'GUMUS' ? 2 : 3)} gr</strong>
                        <span className="remaining-info">
                          (Kalan: {Math.max(0, Number(selectedCollectionDebt.amount) - gramEquiv).toFixed(selectedCollectionDebt?.debt_type === 'GUMUS' ? 2 : 3)} gr)
                        </span>
                      </div>
                    );
                  }
                  return null;
                })()
              )}

              {/* TL karşılığı göster (Gram modunda) */}
              {!collectionScrapMode && collectionPaymentInputMode === 'gram' && (selectedCollectionDebt?.debt_type === 'GRAM' || selectedCollectionDebt?.debt_type === 'GUMUS') && collectionPaymentAmount && (
                (() => {
                  const rate = selectedCollectionDebt?.debt_type === 'GRAM' ? getCurrentGoldPrice() : getCurrentSilverPrice();
                  if (rate > 0) {
                    const tlEquiv = parseFloat(collectionPaymentAmount) * rate;
                    return (
                      <div className="gram-equivalent-info" style={{ background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)' }}>
                        <span>TL Karşılığı:</span>
                        <strong>₺{tlEquiv.toLocaleString('tr-TR', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</strong>
                        <span className="remaining-info">
                          (Kalan: {Math.max(0, Number(selectedCollectionDebt.amount) - parseFloat(collectionPaymentAmount || 0)).toFixed(selectedCollectionDebt?.debt_type === 'GUMUS' ? 2 : 3)} gr)
                        </span>
                      </div>
                    );
                  }
                  return null;
                })()
              )}

              {getCollectionRemainingDebt() > 0.01 && <div className="form-group">
                <label>Ödeme Yöntemi</label>
                <div className="payment-methods">
                  <button
                    className={`payment-method-btn ${collectionPaymentMethod === 'Nakit' && !collectionScrapMode ? 'active' : ''}`}
                    onClick={() => {
                      const amt = parseFloat(collectionPaymentAmount);
                      if (amt > 0) { addCollectionPayment('Nakit'); }
                      else { setCollectionPaymentMethod('Nakit'); setCollectionScrapMode(false); }
                    }}
                  >
                    <Banknote size={16} /> Nakit
                  </button>
                  <button
                    className={`payment-method-btn ${collectionPaymentMethod === 'Kredi Kartı' && !collectionScrapMode ? 'active' : ''}`}
                    onClick={() => {
                      const amt = parseFloat(collectionPaymentAmount);
                      if (amt > 0) { addCollectionPayment('Kredi Kartı'); }
                      else { setCollectionPaymentMethod('Kredi Kartı'); setCollectionScrapMode(false); }
                    }}
                  >
                    <CreditCard size={16} /> Kredi Kartı
                  </button>
                  <button
                    className={`payment-method-btn ${collectionPaymentMethod === 'Havale/EFT' && !collectionScrapMode ? 'active' : ''}`}
                    onClick={() => { setCollectionPaymentMethod('Havale/EFT'); setCollectionScrapMode(false); }}
                  >
                    <Building2 size={16} /> Havale/EFT
                  </button>
                  <button
                    className={`payment-method-btn ${collectionPaymentMethod === 'Sanal Pos' && !collectionScrapMode ? 'active' : ''}`}
                    onClick={() => {
                      const amt = parseFloat(collectionPaymentAmount);
                      if (amt > 0) { addCollectionPayment('Sanal Pos'); }
                      else { setCollectionPaymentMethod('Sanal Pos'); setCollectionScrapMode(false); }
                    }}
                  >
                    <Smartphone size={16} /> Sanal Pos
                  </button>
                  <button
                    className={`payment-method-btn scrap ${collectionScrapMode ? 'active' : ''}`}
                    onClick={() => {
                      setCollectionScrapMode(true);
                      setCollectionPaymentMethod('Hurda');
                      setCollectionScrapData(prev => ({
                        ...prev,
                        purity: isGoldStore ? '22' : '925',
                        unit_price: getAlisRate(isGoldStore ? 'GOLD' : 'SILVER').toFixed(2)
                      }));
                    }}
                  >
                    <Scale size={16} /> {isGoldStore ? 'Altın Hurda' : 'Gümüş Hurda'}
                  </button>
                </div>
              </div>}

              {/* Havale/EFT Banka Hesapları */}
              {collectionPaymentMethod === 'Havale/EFT' && !collectionScrapMode && (
                <div className="form-group" ref={el => { if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 100); }}>
                  <label>Banka Hesabı Seçin</label>
                  <div className="collection-bank-accounts">
                    {bankAccounts.length === 0 ? (
                      <p className="no-accounts-text">Kayıtlı banka hesabı bulunamadı.</p>
                    ) : (
                      bankAccounts.map(account => (
                        <div
                          key={account.id}
                          className={`bank-account-option ${selectedCollectionBankAccount?.id === account.id ? 'selected' : ''}`}
                          onClick={() => {
                            setSelectedCollectionBankAccount(account);
                            // Tutar girilmişse otomatik ödemeyi ekle
                            const amt = parseFloat(collectionPaymentAmount);
                            if (amt > 0) {
                              addCollectionPayment('Havale/EFT', account);
                            }
                          }}
                        >
                          <span className="account-icon">
                            {account.account_type === 'company' ? <Building2 size={20} strokeWidth={1.75} /> :
                             account.account_type === 'ecommerce' ? <Globe size={20} strokeWidth={1.75} /> :
                             <User size={20} strokeWidth={1.75} />}
                          </span>
                          <span className="account-name">{account.account_name}</span>
                          {account.is_default === 1 && <span className="default-tag">Varsayılan</span>}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}

              {/* Hurda Formu */}
              {collectionScrapMode && (
                <div className="collection-scrap-form">
                  {/* Metal Türü Seçimi (Gümüş mağazalarda) */}
                  {!isGoldStore && (
                    <div style={{ marginBottom: '10px' }}>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <button
                          type="button"
                          onClick={() => {
                            const goldPrice = getAlisRate('GOLD');
                            setCollectionScrapData(prev => ({
                              ...prev,
                              metal_type: 'gold',
                              purity: '22',
                              custom_purity_rate: null,
                              unit_price: goldPrice > 0 ? goldPrice.toString() : ''
                            }));
                          }}
                          style={{
                            flex: 1, padding: '8px', borderRadius: '6px', border: '2px solid',
                            borderColor: collectionScrapData.metal_type === 'gold' ? '#f59e0b' : '#d1d5db',
                            background: collectionScrapData.metal_type === 'gold' ? 'linear-gradient(135deg, #fef3c7, #fde68a)' : '#f9fafb',
                            color: collectionScrapData.metal_type === 'gold' ? '#92400e' : '#6b7280',
                            fontWeight: collectionScrapData.metal_type === 'gold' ? '700' : '500',
                            cursor: 'pointer', fontSize: '0.8rem'
                          }}
                        >
                          🥇 Altın
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            const silverPrice = getAlisRate('SILVER');
                            setCollectionScrapData(prev => ({
                              ...prev,
                              metal_type: 'silver',
                              purity: '925',
                              custom_purity_rate: null,
                              unit_price: silverPrice > 0 ? silverPrice.toString() : ''
                            }));
                          }}
                          style={{
                            flex: 1, padding: '8px', borderRadius: '6px', border: '2px solid',
                            borderColor: collectionScrapData.metal_type === 'silver' ? '#9ca3af' : '#d1d5db',
                            background: collectionScrapData.metal_type === 'silver' ? 'linear-gradient(135deg, #f3f4f6, #e5e7eb)' : '#f9fafb',
                            color: collectionScrapData.metal_type === 'silver' ? '#374151' : '#6b7280',
                            fontWeight: collectionScrapData.metal_type === 'silver' ? '700' : '500',
                            cursor: 'pointer', fontSize: '0.8rem'
                          }}
                        >
                          🥈 Gümüş
                        </button>
                      </div>
                    </div>
                  )}
                  <div className="scrap-form-row">
                    <div className="scrap-form-field">
                      <label>Brüt Gram</label>
                      <input
                        type="number"
                        step="0.01"
                        value={collectionScrapData.gross_weight}
                        onChange={(e) => setCollectionScrapData(prev => ({ ...prev, gross_weight: e.target.value }))}
                        placeholder="0.00"
                      />
                    </div>
                    <div className="scrap-form-field">
                      <label>Fire (gr)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={collectionScrapData.waste_gram}
                        onChange={(e) => setCollectionScrapData(prev => ({ ...prev, waste_gram: e.target.value }))}
                        placeholder="0.00"
                      />
                    </div>
                    <div className="scrap-form-field">
                      <label>Net</label>
                      <input
                        type="text"
                        readOnly
                        value={((parseFloat(collectionScrapData.gross_weight) || 0) - (parseFloat(collectionScrapData.waste_gram) || 0)).toFixed(2)}
                        style={{ background: '#f1f5f9', color: '#334155', fontWeight: '600' }}
                      />
                    </div>
                  </div>
                  <div className="scrap-form-row">
                    <div className="scrap-form-field ayar-field">
                      <label>Ayar</label>
                      <div className="ayar-select-wrapper">
                        <select
                          value={collectionScrapData.purity}
                          onChange={(e) => {
                            setCollectionScrapData(prev => ({ ...prev, purity: e.target.value, custom_purity_rate: null }));
                            setIsEditingPurityRate(false);
                          }}
                        >
                          {((collectionScrapData.metal_type || (isGoldStore ? 'gold' : 'silver')) === 'gold') ? (
                            <>
                              <option value="24">24 Ayar</option>
                              <option value="22">22 Ayar</option>
                              <option value="21">21 Ayar</option>
                              <option value="18">18 Ayar</option>
                              <option value="14">14 Ayar</option>
                              <option value="10">10 Ayar</option>
                              <option value="9">9 Ayar</option>
                              <option value="8">8 Ayar</option>
                            </>
                          ) : (
                            <>
                              <option value="999">999 Ayar</option>
                              <option value="925">925 Ayar</option>
                              <option value="900">900 Ayar</option>
                              <option value="850">850 Ayar</option>
                              <option value="800">800 Ayar</option>
                              <option value="750">750 Ayar</option>
                              <option value="700">700 Ayar</option>
                            </>
                          )}
                        </select>
                        <span className="purity-rate-label">Oran:</span>
                        {isEditingPurityRate ? (
                          <input
                            type="number"
                            className="purity-rate-input"
                            value={editingPurityRateValue}
                            onChange={(e) => setEditingPurityRateValue(e.target.value)}
                            onBlur={() => {
                              updatePurityRate(collectionScrapData.metal_type || storeMetalType, collectionScrapData.purity, editingPurityRateValue, 'collection');
                              setIsEditingPurityRate(false);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                updatePurityRate(collectionScrapData.metal_type || storeMetalType, collectionScrapData.purity, editingPurityRateValue, 'collection');
                                setIsEditingPurityRate(false);
                              }
                              if (e.key === 'Escape') setIsEditingPurityRate(false);
                            }}
                            autoFocus
                          />
                        ) : (
                          <span
                            className="purity-rate-display"
                            onDoubleClick={() => {
                              const collMetalType = collectionScrapData.metal_type || storeMetalType;
                              const currentRate = collectionScrapData.custom_purity_rate || scrapPurityRates[collMetalType]?.[collectionScrapData.purity] || defaultScrapPurityRates[collMetalType]?.[collectionScrapData.purity] || 0;
                              setEditingPurityRateValue(Math.round(currentRate * 1000).toString());
                              setIsEditingPurityRate(true);
                            }}
                            title="Çift tıkla düzenle"
                          >
                            {(() => { const collMetalType = collectionScrapData.metal_type || storeMetalType; return Math.round((collectionScrapData.custom_purity_rate || scrapPurityRates[collMetalType]?.[collectionScrapData.purity] || defaultScrapPurityRates[collMetalType]?.[collectionScrapData.purity] || 0) * 1000); })()}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="scrap-form-field">
                      <label>Gram Fiyatı (₺)</label>
                      <PriceInput
                        value={collectionScrapData.unit_price}
                        onChange={(val) => setCollectionScrapData(prev => ({ ...prev, unit_price: val }))}
                        placeholder="0,00"
                      />
                    </div>
                  </div>
                  {/* Hesaplama Özeti */}
                  {(() => {
                    const calc = calculateCollectionScrapValue();
                    return calc.totalValue > 0 ? (
                      <div className="scrap-calculation-summary">
                        <div className="calc-row">
                          <span>Saf Gram:</span>
                          <strong>{calc.pureWeight.toFixed(2)} gr</strong>
                        </div>
                        <div className="calc-row total">
                          <span>Hurda Değeri:</span>
                          {isEditingCollectionScrapTotal ? (
                            <PriceInput
                              className="edit-total-input"
                              value={editingCollectionScrapTotalValue}
                              onChange={(val) => setEditingCollectionScrapTotalValue(val)}
                              onBlur={() => {
                                const newValue = parseFloat(editingCollectionScrapTotalValue);
                                if (!isNaN(newValue) && newValue >= 0) {
                                  setCustomCollectionScrapTotal(newValue);
                                }
                                setIsEditingCollectionScrapTotal(false);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  const newValue = parseFloat(editingCollectionScrapTotalValue);
                                  if (!isNaN(newValue) && newValue >= 0) {
                                    setCustomCollectionScrapTotal(newValue);
                                  }
                                  setIsEditingCollectionScrapTotal(false);
                                }
                                if (e.key === 'Escape') setIsEditingCollectionScrapTotal(false);
                              }}
                              autoFocus
                            />
                          ) : (
                            <strong
                              className="editable-total"
                              onDoubleClick={() => {
                                setEditingCollectionScrapTotalValue(calc.totalValue.toFixed(2));
                                setIsEditingCollectionScrapTotal(true);
                              }}
                              title="Çift tıkla düzenle"
                              style={{ cursor: 'pointer' }}
                            >
                              ₺{calc.totalValue.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              {customCollectionScrapTotal !== null && <span className="manual-badge">Manuel</span>}
                            </strong>
                          )}
                        </div>
                      </div>
                    ) : null;
                  })()}
                  {/* Hurda Ekle Butonu */}
                  {(() => {
                    const calc = calculateCollectionScrapValue();
                    return calc.totalValue > 0 ? (
                      <button
                        type="button"
                        className="btn-save"
                        style={{ width: '100%', marginTop: '10px', padding: '10px', fontSize: '0.95rem' }}
                        onClick={() => {
                          const scrapCalc = calculateCollectionScrapValue();
                          if (scrapCalc.totalValue <= 0) return;
                          const metalType = collectionScrapData.metal_type || (isGoldStore ? 'gold' : 'silver');
                          const roundedTotal = Math.round(scrapCalc.totalValue * 100) / 100;
                          const newPayment = {
                            id: Date.now(),
                            method: `Hurda (${metalType === 'gold' ? 'Altın' : 'Gümüş'})`,
                            amount: roundedTotal,
                            isScrap: true,
                            scrapDetails: {
                              metal_type: metalType,
                              purity: collectionScrapData.purity,
                              gross_weight: parseFloat(collectionScrapData.gross_weight),
                              waste_percent: parseFloat(collectionScrapData.waste_percent) || 0,
                              waste_gram: parseFloat(collectionScrapData.waste_gram) || 0,
                              pure_weight: scrapCalc.pureWeight,
                              unit_price: parseFloat(collectionScrapData.unit_price),
                              total_amount: roundedTotal
                            }
                          };
                          setCollectionMixedPayments(prev => [...prev, newPayment]);
                          setCollectionScrapMode(false);
                          setCollectionPaymentMethod('');
                          setCollectionScrapData(prev => ({ ...prev, gross_weight: '', waste_percent: '', waste_gram: '', unit_price: prev.unit_price }));
                          setCustomCollectionScrapTotal(null);
                          toast.success(`₺${scrapCalc.totalValue.toLocaleString('tr-TR', {minimumFractionDigits: 2, maximumFractionDigits: 2})} Hurda (${metalType === 'gold' ? 'Altın' : 'Gümüş'}) eklendi`);
                        }}
                      >
                        Hurda Ekle (₺{calculateCollectionScrapValue().totalValue.toLocaleString('tr-TR', {minimumFractionDigits: 2, maximumFractionDigits: 2})})
                      </button>
                    ) : null;
                  })()}
                </div>
              )}
            </div>
            <div className="modal-footer" style={{ padding: '8px 20px', flexShrink: 0 }}>
              <button className="btn-cancel" onClick={() => {
                setShowCollectionPaymentModal(false);
                setCollectionMixedPayments([]);
                setSelectedCollectionBankAccount(null);
                setCollectionScrapData(prev => ({...prev, custom_purity_rate: null}));
              }}>İptal</button>
              <button
                className="btn-save"
                onClick={() => setShowCollectionStaffModal(true)}
                disabled={collectionMixedPayments.length === 0}
              >
                {collectionMixedPayments.length > 0
                  ? `Tahsilatı Tamamla (₺${getCollectionPaymentsTotal().toLocaleString('tr-TR', {minimumFractionDigits: 2, maximumFractionDigits: 2})})`
                  : 'Ödemeyi Kaydet'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tahsilat Kasiyer Seçim Modalı */}
      {showCollectionStaffModal && (
        <div className="modal-overlay" style={{ zIndex: 10001 }}>
          <div className="modal staff-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Tahsilat Kasiyeri</h2>
              <button className="close-btn" onClick={() => setShowCollectionStaffModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="staff-list">
                {salesStaffList.map(staff => {
                  const isSelected = collectionStaffId === staff.id;
                  return (
                    <div key={staff.id} className={`staff-item ${isSelected ? 'selected' : ''}`} onClick={() => { if (collectionProcessing) return; setCollectionStaffId(staff.id); setCollectionStaffName(staff.name); setShowCollectionStaffModal(false); markCollectionDebtAsPaid({ id: staff.id, name: staff.name }); }}>
                      <div className={`staff-avatar ${staff.photo_url ? 'has-photo' : ''}`}>
                        {isSelected ? <Check size={24} strokeWidth={3} /> : staff.photo_url ? <img src={`${IMAGE_HOST}${staff.photo_url}`} alt={staff.name} style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} /> : staff.name.charAt(0)}
                      </div>
                      <div className="staff-info"><span className="staff-name">{staff.name}</span></div>
                    </div>
                  );
                })}
              </div>
              <div className="staff-modal-actions">
                <button className="confirm-staff-btn" disabled={!collectionStaffId || collectionProcessing} onClick={() => { setShowCollectionStaffModal(false); markCollectionDebtAsPaid(); }}>{collectionProcessing ? 'İşleniyor...' : 'Tahsilatı Onayla'}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Özel Kur Modal */}
      {showRateModal && (
        <div className={`modal-overlay ${closingModal === 'rate' ? 'closing' : ''}`}>
          <div className="modal rate-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>💱 {getRateModalLabel(rateModalCurrency)} Kuru</h2>
              <button className="close-btn" onClick={() => handleCloseModal('rate', setShowRateModal)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="rate-comparison">
                <div className="rate-section">
                  <h3>Anlık Piyasa Kuru (Harem Altın)</h3>
                  <div className="rate-display">
                    <div className="rate-item">
                      <span className="rate-label">Alış</span>
                      <span className="rate-value">
                        {rateModalCurrency === 'SILVER_USD'
                          ? (prices?.gumus_alis && prices?.USD_alis
                              ? (parseFloat(prices.gumus_alis) / parseFloat(prices.USD_alis)).toFixed(2)
                              : '--')
                          : prices?.[getRatePriceKeys(rateModalCurrency).alis]
                        } {rateModalCurrency === 'SILVER_USD' ? '$' : '₺'}
                      </span>
                    </div>
                    <div className="rate-item">
                      <span className="rate-label">Satış</span>
                      <span className="rate-value">
                        {rateModalCurrency === 'SILVER_USD'
                          ? (prices?.gumus_satis && prices?.USD_satis
                              ? (parseFloat(prices.gumus_satis) / parseFloat(prices.USD_satis)).toFixed(2)
                              : '--')
                          : prices?.[getRatePriceKeys(rateModalCurrency).satis]
                        } {rateModalCurrency === 'SILVER_USD' ? '$' : '₺'}
                      </span>
                    </div>
                  </div>
                </div>

                {customRates[rateModalCurrency] && (
                  <div className="rate-section custom">
                    <h3>
                      {(customRates[rateModalCurrency].alis_percentage != null && customRates[rateModalCurrency].satis_percentage != null)
                        ? 'Makas Farkı (Dinamik)'
                        : (customRates[rateModalCurrency].alis_percentage != null || customRates[rateModalCurrency].satis_percentage != null)
                          ? 'Kayıtlı Kurlar (Karışık)'
                          : 'Kayıtlı Kurlar (Sabit)'}
                    </h3>
                    <div className="rate-display">
                      <div className="rate-item">
                        <span className="rate-label">Alış</span>
                        {customRates[rateModalCurrency].alis_percentage != null ? (
                          <span className="rate-value custom" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                            <span style={{ fontSize: '0.75rem', color: '#f59e0b' }}>-%{customRates[rateModalCurrency].alis_percentage}</span>
                            <span>{getAlisRate(rateModalCurrency).toLocaleString('tr-TR', { maximumFractionDigits: 2 })} ₺</span>
                          </span>
                        ) : (
                          <span className="rate-value custom">{customRates[rateModalCurrency].rate_alis || '-'} ₺</span>
                        )}
                      </div>
                      <div className="rate-item">
                        <span className="rate-label">Satış</span>
                        {customRates[rateModalCurrency].satis_percentage != null ? (
                          <span className="rate-value custom" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                            <span style={{ fontSize: '0.75rem', color: '#10b981' }}>+%{customRates[rateModalCurrency].satis_percentage}</span>
                            <span>{getSatisRate(rateModalCurrency).toLocaleString('tr-TR', { maximumFractionDigits: 2 })} ₺</span>
                          </span>
                        ) : (
                          <span className="rate-value custom">{customRates[rateModalCurrency].rate_satis || customRates[rateModalCurrency].rate || '-'} ₺</span>
                        )}
                      </div>
                    </div>
                    <p className="rate-info">
                      Son güncelleme: {new Date(customRates[rateModalCurrency].updated_at).toLocaleString('tr-TR')}
                      {customRates[rateModalCurrency].updated_by_name && ` - ${customRates[rateModalCurrency].updated_by_name}`}
                    </p>
                  </div>
                )}

                <div className="rate-section input">
                  <h3>Özel Kur Gir</h3>

                  {/* ALIŞ BÖLÜMÜ */}
                  <div style={{ marginBottom: '16px', padding: '14px', background: 'rgba(220, 38, 38, 0.04)', borderRadius: '10px', border: '1px solid rgba(220, 38, 38, 0.15)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                      <span style={{ fontWeight: '700', fontSize: '0.9rem', color: '#dc2626' }}>Alış Kuru</span>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <button type="button" onClick={() => { setRateModalAlisMode('manual'); setRateModalAlisPercentage(''); }}
                          style={{ padding: '4px 10px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontWeight: '600', fontSize: '0.75rem',
                            background: rateModalAlisMode === 'manual' ? 'linear-gradient(135deg, #3b82f6, #2563eb)' : '#e5e7eb',
                            color: rateModalAlisMode === 'manual' ? 'white' : '#6b7280' }}>
                          Manuel
                        </button>
                        <button type="button" onClick={() => setRateModalAlisMode('percentage')}
                          style={{ padding: '4px 10px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontWeight: '600', fontSize: '0.75rem',
                            background: rateModalAlisMode === 'percentage' ? 'linear-gradient(135deg, #f59e0b, #d97706)' : '#e5e7eb',
                            color: rateModalAlisMode === 'percentage' ? 'white' : '#6b7280' }}>
                          Makas Farkı
                        </button>
                      </div>
                    </div>
                    {rateModalAlisMode === 'percentage' ? (
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontWeight: '600', color: '#dc2626' }}>%</span>
                          <input type="number" step="0.1" value={rateModalAlisPercentage}
                            onChange={(e) => handleAlisPercentageChange(e.target.value)}
                            placeholder="Örn: 2"
                            style={{ flex: 1, padding: '10px 12px', borderRadius: '6px', border: '2px solid #dc2626', fontSize: '1rem', fontWeight: '600', background: '#fef2f2' }} />
                        </div>
                        <p style={{ margin: '4px 0 0', fontSize: '0.7rem', color: '#dc2626' }}>
                          Anlık fiyattan <strong>%{rateModalAlisPercentage || '0'} düşük</strong>
                        </p>
                        {rateModalAlisPercentage && (
                          <div style={{ marginTop: '8px', padding: '6px 10px', background: 'rgba(220,38,38,0.08)', borderRadius: '6px', textAlign: 'center' }}>
                            <span style={{ fontSize: '0.75rem', color: '#991b1b' }}>Hesaplanan: </span>
                            <strong style={{ color: '#b91c1c' }}>₺{((() => { const { baseAlis } = getBaseRates(); return (baseAlis * (1 - parseFloat(rateModalAlisPercentage) / 100)).toLocaleString('tr-TR', { maximumFractionDigits: 2 }); })())}</strong>
                            <span style={{ fontSize: '0.65rem', color: '#991b1b', marginLeft: '4px' }}>(Anlık: ₺{getBaseRates().baseAlis?.toLocaleString('tr-TR')})</span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div>
                        <PriceInput value={rateModalAlisValue}
                          onChange={(val) => { setRateModalAlisValue(val); if (rateModalCurrency === 'SILVER') setRateModalAlisDolar(''); }}
                          placeholder="Alış kuru (₺)..."
                          style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', border: '2px solid #dc2626', fontSize: '1rem', fontWeight: '600' }} />
                        {rateModalCurrency === 'SILVER' && (
                          <div style={{ marginTop: '6px' }}>
                            <label style={{ fontSize: '0.8rem', color: '#64748b' }}>Alış ($)</label>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <PriceInput value={rateModalAlisDolar}
                                onChange={(val) => { const dolarVal = val; setRateModalAlisDolar(dolarVal); if (dolarVal) { const usdRate = getEffectiveRate('USD', 'alis'); setRateModalAlisValue((parseFloat(dolarVal) * usdRate).toFixed(2)); } }}
                                placeholder="Dolar cinsinden..."
                                style={{ flex: 1, padding: '8px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.9rem' }} />
                              <span style={{ fontSize: '0.75rem', color: '#94a3b8', whiteSpace: 'nowrap' }}>× {getEffectiveRate('USD', 'alis')?.toLocaleString('tr-TR')} ₺</span>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* SATIŞ BÖLÜMÜ */}
                  <div style={{ marginBottom: '16px', padding: '14px', background: 'rgba(22, 163, 74, 0.04)', borderRadius: '10px', border: '1px solid rgba(22, 163, 74, 0.15)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                      <span style={{ fontWeight: '700', fontSize: '0.9rem', color: '#16a34a' }}>Satış Kuru</span>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <button type="button" onClick={() => { setRateModalSatisMode('manual'); setRateModalSatisPercentage(''); }}
                          style={{ padding: '4px 10px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontWeight: '600', fontSize: '0.75rem',
                            background: rateModalSatisMode === 'manual' ? 'linear-gradient(135deg, #3b82f6, #2563eb)' : '#e5e7eb',
                            color: rateModalSatisMode === 'manual' ? 'white' : '#6b7280' }}>
                          Manuel
                        </button>
                        <button type="button" onClick={() => setRateModalSatisMode('percentage')}
                          style={{ padding: '4px 10px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontWeight: '600', fontSize: '0.75rem',
                            background: rateModalSatisMode === 'percentage' ? 'linear-gradient(135deg, #f59e0b, #d97706)' : '#e5e7eb',
                            color: rateModalSatisMode === 'percentage' ? 'white' : '#6b7280' }}>
                          Makas Farkı
                        </button>
                      </div>
                    </div>
                    {rateModalSatisMode === 'percentage' ? (
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontWeight: '600', color: '#16a34a' }}>%</span>
                          <input type="number" step="0.1" value={rateModalSatisPercentage}
                            onChange={(e) => handleSatisPercentageChange(e.target.value)}
                            placeholder="Örn: 2"
                            style={{ flex: 1, padding: '10px 12px', borderRadius: '6px', border: '2px solid #16a34a', fontSize: '1rem', fontWeight: '600', background: '#f0fdf4' }} />
                        </div>
                        <p style={{ margin: '4px 0 0', fontSize: '0.7rem', color: '#16a34a' }}>
                          Anlık fiyattan <strong>%{rateModalSatisPercentage || '0'} yüksek</strong>
                        </p>
                        {rateModalSatisPercentage && (
                          <div style={{ marginTop: '8px', padding: '6px 10px', background: 'rgba(22,163,74,0.08)', borderRadius: '6px', textAlign: 'center' }}>
                            <span style={{ fontSize: '0.75rem', color: '#166534' }}>Hesaplanan: </span>
                            <strong style={{ color: '#15803d' }}>₺{((() => { const { baseSatis } = getBaseRates(); return (baseSatis * (1 + parseFloat(rateModalSatisPercentage) / 100)).toLocaleString('tr-TR', { maximumFractionDigits: 2 }); })())}</strong>
                            <span style={{ fontSize: '0.65rem', color: '#166534', marginLeft: '4px' }}>(Anlık: ₺{getBaseRates().baseSatis?.toLocaleString('tr-TR')})</span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div>
                        <PriceInput value={rateModalSatisValue}
                          onChange={(val) => { setRateModalSatisValue(val); if (rateModalCurrency === 'SILVER') setRateModalSatisDolar(''); }}
                          placeholder="Satış kuru (₺)..."
                          style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', border: '2px solid #16a34a', fontSize: '1rem', fontWeight: '600' }} />
                        {rateModalCurrency === 'SILVER' && (
                          <div style={{ marginTop: '6px' }}>
                            <label style={{ fontSize: '0.8rem', color: '#64748b' }}>Satış ($)</label>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <PriceInput value={rateModalSatisDolar}
                                onChange={(val) => { const dolarVal = val; setRateModalSatisDolar(dolarVal); if (dolarVal) { const usdRate = getEffectiveRate('USD', 'satis'); setRateModalSatisValue((parseFloat(dolarVal) * usdRate).toFixed(2)); } }}
                                placeholder="Dolar cinsinden..."
                                style={{ flex: 1, padding: '8px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.9rem' }} />
                              <span style={{ fontSize: '0.75rem', color: '#94a3b8', whiteSpace: 'nowrap' }}>× {getEffectiveRate('USD', 'satis')?.toLocaleString('tr-TR')} ₺</span>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              {customRates[rateModalCurrency] && (
                <button
                  className="btn danger"
                  onClick={() => removeCustomRate(rateModalCurrency)}
                >
                  Kayıtlı Kuru Sil
                </button>
              )}
              <button className="btn secondary" onClick={() => setShowRateModal(false)}>İptal</button>
              <button className="btn primary" onClick={saveCustomRate}>Kaydet</button>
            </div>
          </div>
        </div>
      )}

      {/* Transfer Onay Kasiyer Modal */}
      {showApproveStaffModal && (
        <div className="modal-overlay" style={{ zIndex: 10001 }}>
          <div className="modal staff-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Gönderen Kasiyer Seç</h2>
              <button className="close-btn" onClick={() => setShowApproveStaffModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="staff-list">
                {salesStaffList.map(staff => (
                  <div key={staff.id} className="staff-item" onClick={() => { setShowApproveStaffModal(false); approveIncomingRequest(staff.id, staff.name); }}>
                    <div className={`staff-avatar ${staff.photo_url ? 'has-photo' : ''}`}>
                      {staff.photo_url ? <img src={`${IMAGE_HOST}${staff.photo_url}`} alt={staff.name} style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} /> : staff.name.charAt(0)}
                    </div>
                    <div className="staff-info"><span className="staff-name">{staff.name}</span></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Transfer Talep Reddet Modal */}
      {showRejectRequestModal && (
        <div className="modal-overlay" style={{ zIndex: 10002 }} onClick={() => !processingRequest && setShowRejectRequestModal(false)}>
          <div className="modal" style={{ maxWidth: '560px', width: '92%' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Talebi Reddet</h2>
              <button className="close-btn" onClick={() => setShowRejectRequestModal(false)} disabled={processingRequest}>✕</button>
            </div>
            <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: '6px', color: '#334155' }}>
                  Reddetme Sebebi <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <textarea
                  value={rejectRequestReason}
                  onChange={(e) => setRejectRequestReason(e.target.value)}
                  placeholder="Ör: Stok yok, yanlış istek, ..."
                  rows={4}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px', resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box' }}
                  autoFocus
                />
              </div>
              <div>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: '6px', color: '#334155' }}>
                  Kasiyer <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '10px', maxHeight: '320px', overflowY: 'auto' }}>
                  {salesStaffList.map(staff => {
                    const isSelected = rejectRequestStaffId === staff.id;
                    return (
                      <div
                        key={`${staff.source || 'x'}-${staff.id}`}
                        onClick={() => { setRejectRequestStaffId(staff.id); setRejectRequestStaffName(staff.name); }}
                        style={{
                          cursor: 'pointer',
                          padding: '10px',
                          border: isSelected ? '2px solid #ef4444' : '1px solid #e2e8f0',
                          borderRadius: '10px',
                          background: isSelected ? '#fef2f2' : '#fff',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          transition: 'all 0.15s',
                        }}
                      >
                        <div style={{
                          width: '38px', height: '38px', borderRadius: '50%',
                          background: '#e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontWeight: 600, color: '#475569', overflow: 'hidden', flexShrink: 0
                        }}>
                          {staff.photo_url
                            ? <img src={`${IMAGE_HOST}${staff.photo_url}`} alt={staff.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            : staff.name.charAt(0)}
                        </div>
                        <span style={{ fontSize: '13px', fontWeight: 500, color: '#1e293b' }}>{staff.name}</span>
                      </div>
                    );
                  })}
                </div>
                {salesStaffList.length === 0 && (
                  <p style={{ color: '#94a3b8', fontSize: '13px', margin: '8px 0 0' }}>Kasiyer listesi bulunamadı.</p>
                )}
              </div>
            </div>
            <div className="modal-footer" style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', padding: '12px 20px', borderTop: '1px solid #e2e8f0' }}>
              <button
                onClick={() => setShowRejectRequestModal(false)}
                disabled={processingRequest}
                style={{ padding: '10px 16px', border: '1px solid #cbd5e1', borderRadius: '8px', background: '#fff', color: '#475569', cursor: 'pointer', fontWeight: 500 }}
              >
                Vazgeç
              </button>
              <button
                onClick={confirmRejectIncomingRequest}
                disabled={processingRequest || !rejectRequestReason.trim() || !rejectRequestStaffId}
                style={{
                  padding: '10px 16px', border: 'none', borderRadius: '8px',
                  background: (processingRequest || !rejectRequestReason.trim() || !rejectRequestStaffId) ? '#fca5a5' : '#ef4444',
                  color: '#fff', cursor: (processingRequest || !rejectRequestReason.trim() || !rejectRequestStaffId) ? 'not-allowed' : 'pointer', fontWeight: 600
                }}
              >
                {processingRequest ? 'Gönderiliyor...' : 'Reddetmeyi Onayla'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Masraflar Modal */}
      {showExpensesModal && (
        <div className={`modal-overlay ${closingModal === 'expenses' ? 'closing' : ''}`}>
          <div className="modal expenses-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Masraflar</h2>
              <button className="close-btn" onClick={() => handleCloseModal('expenses', setShowExpensesModal, () => { setShowExpenseForm(false); setShowAddExpenseForm(false); setNewExpense({ description: '', amount: '', category: 'Genel', payment_method: 'Nakit', notes: '' }); })}>✕</button>
            </div>
            <div className="modal-body">
              {/* Yeni Masraf Ekleme Butonu veya Form */}
              <div className="expense-form-section">
                {!showExpenseForm ? (
                  <button
                    className="expense-new-btn"
                    onClick={() => setShowExpenseForm(true)}
                  >
                    + Yeni Masraf Ekle
                  </button>
                ) : (
                  <div className="expense-form-container">
                    <div className="expense-form-grid">
                      <div className="expense-field">
                        <label>Açıklama</label>
                        <input
                          type="text"
                          placeholder="Masraf açıklaması..."
                          value={newExpense.description}
                          onChange={(e) => setNewExpense({...newExpense, description: e.target.value})}
                          className="expense-input"
                        />
                      </div>
                      <div className="expense-field">
                        <label>Tutar (₺)</label>
                        <PriceInput
                          placeholder="0,00"
                          value={newExpense.amount}
                          onChange={(val) => setNewExpense({...newExpense, amount: val})}
                          className="expense-input"
                        />
                      </div>
                      <div className="expense-field">
                        <label>Kategori</label>
                        <select
                          value={newExpense.category}
                          onChange={(e) => setNewExpense({...newExpense, category: e.target.value})}
                          className="expense-select"
                        >
                          {expenseCategories.map(cat => (
                            <option key={cat} value={cat}>{cat}</option>
                          ))}
                        </select>
                      </div>
                      <div className="expense-field">
                        <label>Ödeme Şekli</label>
                        <select
                          value={newExpense.payment_method}
                          onChange={(e) => setNewExpense({...newExpense, payment_method: e.target.value})}
                          className="expense-select"
                        >
                          <option value="Nakit">Nakit</option>
                          <option value="Kredi Kartı">Kredi Kartı</option>
                          <option value="Havale/EFT">Havale/EFT</option>
                          <option value="Kasadan">Kasadan</option>
                        </select>
                      </div>
                    </div>
                    <div className="expense-form-actions">
                      <button
                        className="expense-cancel-btn"
                        onClick={() => {
                          setShowExpenseForm(false);
                          setNewExpense({ description: '', amount: '', category: 'Genel', payment_method: 'Nakit' });
                        }}
                      >
                        İptal
                      </button>
                      <button
                        className="expense-save-btn"
                        onClick={() => {
                          addExpense();
                          setShowExpenseForm(false);
                        }}
                      >
                        Kaydet
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Masraf Listesi */}
              <div className="expense-list-section">
                {loadingExpenses ? (
                  <div className="expense-loading">Yükleniyor...</div>
                ) : expenses.length === 0 ? (
                  <div className="expense-empty">Bugün masraf kaydı yok</div>
                ) : (
                  <>
                    <div className="expense-list-header">
                      <span>Açıklama</span>
                      <span>Kategori</span>
                      <span>Ödeme</span>
                      <span>Tutar</span>
                      <span></span>
                    </div>
                    <div className="expense-list-items">
                      {expenses.map(expense => (
                        <div key={expense.id} className="expense-row">
                          <span className="expense-desc">{expense.description}</span>
                          <span className="expense-cat">{expense.category}</span>
                          <span className="expense-pay">{expense.payment_method}</span>
                          <span className="expense-amt">{parseFloat(expense.amount).toLocaleString('tr-TR')} ₺</span>
                          <div className="expense-actions">
                            {user?.role === 'admin' && (
                              <button className="expense-edit-btn" onClick={() => openEditExpenseModal(expense)}>
                                <Pencil size={12} />
                              </button>
                            )}
                            <button className="expense-del-btn" onClick={() => {
                              if (user?.role === 'admin') {
                                setDeletingExpenseId(expense.id);
                                setShowDeleteExpenseConfirm(true);
                              } else {
                                deleteExpense(expense.id);
                              }
                            }}>×</button>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="expense-total-row">
                      <span>Toplam</span>
                      <span className="expense-total-amt">{getTodayExpensesTotal().toLocaleString('tr-TR')} ₺</span>
                    </div>
                  </>
                )}
              </div>

              {/* Geçmiş Masraflar - Sadece Admin */}
              {user?.role === 'admin' && (
                <div style={{ marginTop: '16px', borderTop: '1px solid #e8ecef', paddingTop: '12px' }}>
                  <button
                    onClick={() => {
                      const next = !showPastExpenses;
                      setShowPastExpenses(next);
                      if (next && pastExpenses.length === 0) fetchPastExpenses();
                    }}
                    style={{
                      width: '100%', padding: '10px', background: showPastExpenses ? '#f0fdfa' : '#f8fafc',
                      border: `1px solid ${showPastExpenses ? '#00bfa6' : '#e2e8f0'}`, borderRadius: '8px',
                      cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600,
                      color: showPastExpenses ? '#00bfa6' : '#64748b', transition: 'all 0.2s',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px'
                    }}
                  >
                    {showPastExpenses ? '▲ Geçmiş Masrafları Gizle' : '▼ Geçmiş Masraflar'}
                  </button>

                  {showPastExpenses && (
                    <div style={{ marginTop: '12px' }}>
                      {/* Filtreler */}
                      <div style={{ display: 'flex', gap: '8px', marginBottom: '10px', flexWrap: 'wrap' }}>
                        <input
                          type="date"
                          value={pastExpenseFilters.start_date}
                          onChange={(e) => setPastExpenseFilters(f => ({ ...f, start_date: e.target.value }))}
                          style={{ flex: 1, minWidth: '120px', padding: '6px 8px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '0.8rem' }}
                          placeholder="Başlangıç"
                        />
                        <input
                          type="date"
                          value={pastExpenseFilters.end_date}
                          onChange={(e) => setPastExpenseFilters(f => ({ ...f, end_date: e.target.value }))}
                          style={{ flex: 1, minWidth: '120px', padding: '6px 8px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '0.8rem' }}
                          placeholder="Bitiş"
                        />
                        <select
                          value={pastExpenseFilters.store_id}
                          onChange={(e) => setPastExpenseFilters(f => ({ ...f, store_id: e.target.value }))}
                          style={{ flex: 1, minWidth: '120px', padding: '6px 8px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '0.8rem' }}
                        >
                          <option value="">Tüm Mağazalar</option>
                          {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                        </select>
                        <select
                          value={pastExpenseFilters.category}
                          onChange={(e) => setPastExpenseFilters(f => ({ ...f, category: e.target.value }))}
                          style={{ flex: 1, minWidth: '100px', padding: '6px 8px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '0.8rem' }}
                        >
                          <option value="">Tüm Kategoriler</option>
                          {expenseCategories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                        </select>
                        <button
                          onClick={() => fetchPastExpenses()}
                          style={{ padding: '6px 14px', background: '#00bfa6', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600, flexShrink: 0 }}
                        >
                          Filtrele
                        </button>
                      </div>

                      {/* Arama */}
                      <input
                        type="text"
                        placeholder="Masraf ara (açıklama, kategori...)"
                        value={pastExpenseSearch}
                        onChange={(e) => setPastExpenseSearch(e.target.value)}
                        style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '0.8rem', marginBottom: '10px' }}
                      />

                      {/* Liste */}
                      {loadingPastExpenses ? (
                        <div style={{ textAlign: 'center', padding: '20px', color: '#94a3b8' }}>Yükleniyor...</div>
                      ) : (
                        <>
                          <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
                              <thead>
                                <tr style={{ background: '#f8fafc', position: 'sticky', top: 0 }}>
                                  <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '2px solid #e2e8f0', fontWeight: 600, color: '#475569' }}>Tarih</th>
                                  <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '2px solid #e2e8f0', fontWeight: 600, color: '#475569' }}>Açıklama</th>
                                  <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '2px solid #e2e8f0', fontWeight: 600, color: '#475569' }}>Kategori</th>
                                  <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '2px solid #e2e8f0', fontWeight: 600, color: '#475569' }}>Mağaza</th>
                                  <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '2px solid #e2e8f0', fontWeight: 600, color: '#475569' }}>Ödeme</th>
                                  <th style={{ padding: '6px 8px', textAlign: 'right', borderBottom: '2px solid #e2e8f0', fontWeight: 600, color: '#475569' }}>Tutar</th>
                                </tr>
                              </thead>
                              <tbody>
                                {pastExpenses
                                  .filter(exp => {
                                    if (pastExpenseFilters.category && exp.category !== pastExpenseFilters.category) return false;
                                    if (!pastExpenseSearch) return true;
                                    const q = pastExpenseSearch.toLowerCase();
                                    return (exp.description || '').toLowerCase().includes(q) ||
                                      (exp.category || '').toLowerCase().includes(q) ||
                                      (exp.store_name || '').toLowerCase().includes(q) ||
                                      (exp.payment_method || '').toLowerCase().includes(q);
                                  })
                                  .map(exp => (
                                    <tr key={exp.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                      <td style={{ padding: '6px 8px', color: '#64748b', whiteSpace: 'nowrap' }}>
                                        {new Date(exp.created_at).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                                      </td>
                                      <td style={{ padding: '6px 8px', color: '#1e293b', fontWeight: 500 }}>{exp.description}</td>
                                      <td style={{ padding: '6px 8px', color: '#64748b' }}>{exp.category}</td>
                                      <td style={{ padding: '6px 8px', color: '#64748b' }}>{exp.store_name || '-'}</td>
                                      <td style={{ padding: '6px 8px', color: '#64748b' }}>{exp.payment_method}</td>
                                      <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 600, color: '#ef4444' }}>
                                        {parseFloat(exp.amount).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
                                      </td>
                                    </tr>
                                  ))}
                              </tbody>
                            </table>
                            {pastExpenses.filter(exp => {
                              if (pastExpenseFilters.category && exp.category !== pastExpenseFilters.category) return false;
                              if (!pastExpenseSearch) return true;
                              const q = pastExpenseSearch.toLowerCase();
                              return (exp.description || '').toLowerCase().includes(q) || (exp.category || '').toLowerCase().includes(q) || (exp.store_name || '').toLowerCase().includes(q);
                            }).length === 0 && (
                              <div style={{ textAlign: 'center', padding: '20px', color: '#94a3b8', fontSize: '0.8rem' }}>
                                {pastExpenses.length === 0 ? 'Tarih aralığı seçip "Filtrele" butonuna basın' : 'Sonuç bulunamadı'}
                              </div>
                            )}
                          </div>
                          {pastExpenses.length > 0 && (
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 8px 0', borderTop: '2px solid #e2e8f0', marginTop: '8px', fontSize: '0.82rem', fontWeight: 700 }}>
                              <span style={{ color: '#475569' }}>
                                Toplam ({pastExpenses.filter(exp => {
                                  if (pastExpenseFilters.category && exp.category !== pastExpenseFilters.category) return false;
                                  if (!pastExpenseSearch) return true;
                                  const q = pastExpenseSearch.toLowerCase();
                                  return (exp.description || '').toLowerCase().includes(q) || (exp.category || '').toLowerCase().includes(q) || (exp.store_name || '').toLowerCase().includes(q);
                                }).length} masraf)
                              </span>
                              <span style={{ color: '#ef4444' }}>
                                {pastExpenses
                                  .filter(exp => {
                                    if (pastExpenseFilters.category && exp.category !== pastExpenseFilters.category) return false;
                                    if (!pastExpenseSearch) return true;
                                    const q = pastExpenseSearch.toLowerCase();
                                    return (exp.description || '').toLowerCase().includes(q) || (exp.category || '').toLowerCase().includes(q) || (exp.store_name || '').toLowerCase().includes(q);
                                  })
                                  .reduce((sum, exp) => sum + parseFloat(exp.amount || 0), 0)
                                  .toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
                              </span>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Kampanyalar Modal */}
      {showCampaignsModal && (
        <div className={`modal-overlay ${closingModal === 'campaigns' ? 'closing' : ''}`}>
          <div className="modal campaigns-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2><Gift size={20} style={{ marginRight: '8px' }} /> Aktif Kampanyalar</h2>
              <div className="modal-header-actions">
                <button
                  className="go-to-campaigns-btn"
                  onClick={() => window.open('/campaigns', '_blank')}
                >
                  Kampanyalar Sayfasına Git →
                </button>
                <button className="close-btn" onClick={() => handleCloseModal('campaigns', setShowCampaignsModal)}>✕</button>
              </div>
            </div>
            <div className="modal-body campaigns-modal-body">
              {activeCampaigns.length === 0 ? (
                <div className="empty-text">Şu anda aktif kampanya bulunmuyor</div>
              ) : (
                <div className="campaigns-list-modal">
                  {activeCampaigns.map(campaign => {
                    const status = getCampaignStatus(campaign);
                    const rewards = typeof campaign.rewards === 'string'
                      ? JSON.parse(campaign.rewards || '{}')
                      : campaign.rewards || {};
                    return (
                      <div key={campaign.id} className={`campaign-item-modal ${status.class}`}>
                        <div className="campaign-item-header">
                          <h3>{campaign.name}</h3>
                          <span className={`campaign-status-badge ${status.class}`}>{status.label}</span>
                        </div>
                        {campaign.description && (
                          <p className="campaign-description">{campaign.description}</p>
                        )}
                        <div className="campaign-item-details">
                          <span className="campaign-type-badge">
                            {CAMPAIGN_TYPE_LABELS[campaign.campaign_type] || campaign.campaign_type}
                          </span>
                          <span className="campaign-dates">
                            {new Date(campaign.start_date).toLocaleDateString('tr-TR')} - {new Date(campaign.end_date).toLocaleDateString('tr-TR')}
                          </span>
                        </div>
                        <div className="campaign-reward-info">
                          {rewards.discount_type === 'percentage' && (
                            <span className="reward-badge">%{rewards.discount_percent} İndirim</span>
                          )}
                          {rewards.discount_type === 'fixed' && (
                            <span className="reward-badge">₺{rewards.discount_amount} İndirim</span>
                          )}
                          {rewards.free_product_id && (
                            <span className="reward-badge">🎁 Hediye Ürün</span>
                          )}
                          {rewards.pay_quantity && rewards.get_quantity && (
                            <span className="reward-badge">{rewards.get_quantity} Al {rewards.pay_quantity} Öde</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Stok Yetersizliği ve Transfer Talebi Modalı */}
      {showStockRequestModal && (
        <div className="modal-overlay">
          <div className="modal stock-request-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2><AlertTriangle size={20} style={{ marginRight: '8px', color: '#f59e0b' }} /> Stok Yetersiz</h2>
              <button className="close-btn" onClick={() => {
                setShowStockRequestModal(false);
                setPendingPaymentData(null);
                setPendingStockRequests([]);
              }}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: '16px', color: '#64748b' }}>
                Aşağıdaki ürünler için mağaza stoğunuz yetersiz. Satışı tamamlamak için transfer talebi oluşturulacaktır.
              </p>
              <table className="stock-request-table">
                <thead>
                  <tr>
                    <th>Ürün</th>
                    <th>Sepet</th>
                    <th>Mağaza Stoğu</th>
                    <th>Eksik</th>
                    <th>Talep Edilecek Mağaza</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingStockRequests.map((item, index) => (
                    <tr key={item.productId}>
                      <td>
                        <div className="stock-product-name">{item.name}</div>
                        {item.barcode && <div className="stock-product-barcode">{item.barcode}</div>}
                      </td>
                      <td className="text-center">{item.quantity}</td>
                      <td className="text-center">{item.myStock}</td>
                      <td className="text-center text-danger">{item.shortage}</td>
                      <td>
                        <div className="available-store">
                          <Store size={14} />
                          <span>{item.availableStoreName}</span>
                          <span className="available-stock">({item.availableStock} adet)</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="modal-footer" style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowStockRequestModal(false);
                  setPendingPaymentData(null);
                  setPendingStockRequests([]);
                }}
              >
                İptal
              </button>
              <button
                className="btn btn-primary"
                onClick={handleProceedWithStockRequest}
              >
                Talep Et ve Satışı Tamamla
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Yardım Modalı */}
      {showHelpModal && (
        <div className={`modal-overlay ${closingModal === 'help' ? 'closing' : ''}`}>
          <div className="modal help-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2><BookOpen size={20} style={{ marginRight: '8px' }} /> Perakende Satış Yardım</h2>
              <button className="close-btn" onClick={() => handleCloseModal('help', setShowHelpModal)}>✕</button>
            </div>
            <div className="help-modal-content">
              {/* Genel Bilgi */}
              <div className="help-section">
                <h3><Store size={18} style={{ marginRight: '6px' }} /> Genel Bilgi</h3>
                <p>Bu sayfa üzerinden satış, iade, değişim ve tahsilat işlemlerini gerçekleştirebilirsiniz. Sol panelde sepet, ortada müşteri ve fiyat bilgileri, sağda ise işlem butonları bulunur.</p>
              </div>

              {/* Satış İşlemi */}
              <div className="help-section">
                <h3><ShoppingCart size={18} style={{ marginRight: '6px' }} /> Satış Nasıl Yapılır?</h3>
                <div className="help-steps">
                  <div className="help-step">
                    <span className="step-num">1</span>
                    <span>Ürünü barkod okuyucu ile okutun veya arama kutusuna yazın</span>
                  </div>
                  <div className="help-step">
                    <span className="step-num">2</span>
                    <span>Gerekirse müşteri seçin (opsiyonel)</span>
                  </div>
                  <div className="help-step">
                    <span className="step-num">3</span>
                    <span>Satış sorumlusu seçin (zorunlu)</span>
                  </div>
                  <div className="help-step">
                    <span className="step-num">4</span>
                    <span>Ödeme yöntemini seçin (Nakit, Kredi Kartı, vb.)</span>
                  </div>
                </div>
              </div>

              {/* İade İşlemi */}
              <div className="help-section">
                <h3><Undo2 size={18} style={{ marginRight: '6px' }} /> İade Nasıl Yapılır?</h3>
                <div className="help-steps">
                  <div className="help-step">
                    <span className="step-num">1</span>
                    <span><strong>İade</strong> butonuna tıklayın</span>
                  </div>
                  <div className="help-step">
                    <span className="step-num">2</span>
                    <span>Açılan listeden iade edilecek satışı bulun</span>
                  </div>
                  <div className="help-step">
                    <span className="step-num">3</span>
                    <span>İade edilecek ürünleri ve miktarları seçin</span>
                  </div>
                  <div className="help-step">
                    <span className="step-num">4</span>
                    <span><strong>Nakit Ver</strong> veya <strong>Hediye Çeki</strong> ile iade tamamlayın</span>
                  </div>
                </div>
                <div className="help-tip"><Lightbulb size={16} style={{ marginRight: '6px', color: '#f59e0b' }} /> Hediye Çeki seçerseniz otomatik bakiye oluşturulur ve yazdırılır.</div>
              </div>

              {/* Değişim İşlemi */}
              <div className="help-section">
                <h3><RefreshCcw size={18} style={{ marginRight: '6px' }} /> Değişim Nasıl Yapılır?</h3>
                <div className="help-steps">
                  <div className="help-step">
                    <span className="step-num">1</span>
                    <span><strong>Değişim</strong> butonuna tıklayın</span>
                  </div>
                  <div className="help-step">
                    <span className="step-num">2</span>
                    <span>Değişim yapılacak satışı seçin</span>
                  </div>
                  <div className="help-step">
                    <span className="step-num">3</span>
                    <span>İade edilecek ürünleri işaretleyin</span>
                  </div>
                  <div className="help-step">
                    <span className="step-num">4</span>
                    <span>Yeni ürünleri sepete ekleyin</span>
                  </div>
                  <div className="help-step">
                    <span className="step-num">5</span>
                    <span>Fark tutarına göre ödeme alın veya iade yapın</span>
                  </div>
                </div>
              </div>

              {/* Butonlar */}
              <div className="help-section">
                <h3><CircleDot size={18} style={{ marginRight: '6px' }} /> Buton Açıklamaları</h3>
                <div className="help-buttons-grid">
                  <div className="help-button-item">
                    <span className="btn-icon"><Search size={18} /></span>
                    <div>
                      <strong>Müşteri Ara</strong>
                      <p>Kayıtlı müşterileri arar</p>
                    </div>
                  </div>
                  <div className="help-button-item">
                    <span className="btn-icon"><Plus size={18} /></span>
                    <div>
                      <strong>Yeni Müşteri</strong>
                      <p>Yeni müşteri kaydı oluşturur</p>
                    </div>
                  </div>
                  <div className="help-button-item">
                    <span className="btn-icon"><User size={18} /></span>
                    <div>
                      <strong>Satış Sorumlusu</strong>
                      <p>Satışı yapan personeli seçer</p>
                    </div>
                  </div>
                  <div className="help-button-item">
                    <span className="btn-icon"><Tag size={18} /></span>
                    <div>
                      <strong>İndirim</strong>
                      <p>Sepete manuel indirim uygular</p>
                    </div>
                  </div>
                  <div className="help-button-item">
                    <span className="btn-icon"><ClipboardList size={18} /></span>
                    <div>
                      <strong>Fişler</strong>
                      <p>Bugünkü satışları listeler</p>
                    </div>
                  </div>
                  <div className="help-button-item">
                    <span className="btn-icon"><Undo2 size={18} /></span>
                    <div>
                      <strong>İade</strong>
                      <p>Satış iadesi başlatır</p>
                    </div>
                  </div>
                  <div className="help-button-item">
                    <span className="btn-icon"><RefreshCcw size={18} /></span>
                    <div>
                      <strong>Değişim</strong>
                      <p>Ürün değişimi başlatır</p>
                    </div>
                  </div>
                  <div className="help-button-item">
                    <span className="btn-icon"><Pause size={18} /></span>
                    <div>
                      <strong>Beklet</strong>
                      <p>Sepeti kaydedip sonra devam eder</p>
                    </div>
                  </div>
                  <div className="help-button-item">
                    <span className="btn-icon"><Smartphone size={18} /></span>
                    <div>
                      <strong>Terminal</strong>
                      <p>El terminalinden gelen sepetleri alır</p>
                    </div>
                  </div>
                  <div className="help-button-item">
                    <span className="btn-icon"><Gift size={18} /></span>
                    <div>
                      <strong>Kampanyalar</strong>
                      <p>Aktif kampanyaları gösterir</p>
                    </div>
                  </div>
                  <div className="help-button-item">
                    <span className="btn-icon"><CreditCard size={18} /></span>
                    <div>
                      <strong>Tahsilat</strong>
                      <p>Müşteri borç tahsilatı yapar</p>
                    </div>
                  </div>
                  <div className="help-button-item">
                    <span className="btn-icon"><SearchCheck size={18} /></span>
                    <div>
                      <strong>Ürün Sorgula</strong>
                      <p>Ürün fiyat ve stok bilgisi sorgular</p>
                    </div>
                  </div>
                  <div className="help-button-item">
                    <span className="btn-icon"><Trash2 size={18} /></span>
                    <div>
                      <strong>Sepeti Temizle</strong>
                      <p>Tüm sepeti sıfırlar</p>
                    </div>
                  </div>
                  <div className="help-button-item">
                    <span className="btn-icon"><BarChart3 size={18} /></span>
                    <div>
                      <strong>Gün Sonu</strong>
                      <p>Günlük satış raporunu gösterir</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Ek Özellikler */}
              <div className="help-section">
                <h3><Star size={18} style={{ marginRight: '6px' }} /> Ek Özellikler</h3>
                <div className="help-buttons-grid">
                  <div className="help-button-item">
                    <span className="btn-icon"><Star size={18} /></span>
                    <div>
                      <strong>Favori Ürünler</strong>
                      <p>Sık satılan ürünlere hızlı erişim sağlar</p>
                    </div>
                  </div>
                  <div className="help-button-item">
                    <span className="btn-icon"><Scale size={18} /></span>
                    <div>
                      <strong>Hurda Alım</strong>
                      <p>Hurda altın/gümüş alım sayfasına gider</p>
                    </div>
                  </div>
                  <div className="help-button-item">
                    <span className="btn-icon"><Wrench size={18} /></span>
                    <div>
                      <strong>Tamirat</strong>
                      <p>Tamirat işlemleri sayfasına gider</p>
                    </div>
                  </div>
                  <div className="help-button-item">
                    <span className="btn-icon"><Gift size={18} /></span>
                    <div>
                      <strong>Bakiye</strong>
                      <p>Ödeme sırasında bakiye kodu girerek bakiye kullanabilirsiniz</p>
                    </div>
                  </div>
                  <div className="help-button-item">
                    <span className="btn-icon"><Users size={18} /></span>
                    <div>
                      <strong>Çoklu Sorumlu</strong>
                      <p>Bir satışa birden fazla sorumlu atayabilirsiniz</p>
                    </div>
                  </div>
                  <div className="help-button-item">
                    <span className="btn-icon"><Pencil size={18} /></span>
                    <div>
                      <strong>Satış Düzenleme</strong>
                      <p>Son satışlardan bir satışı düzenleyebilirsiniz</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* İpuçları */}
              <div className="help-section tips-section">
                <h3><Lightbulb size={18} style={{ marginRight: '6px', color: '#f59e0b' }} /> Faydalı İpuçları</h3>
                <ul className="help-tips-list">
                  <li>Döviz kurlarına tıklayarak özel kur girebilirsiniz</li>
                  <li>Sepetteki ürün fiyatına tıklayarak fiyat değiştirebilirsiniz</li>
                  <li>Ürün resmine tıklayarak büyük görüntüleyebilirsiniz</li>
                  <li>Altın ürünleri otomatik olarak güncel altın fiyatından hesaplanır</li>
                  <li>USD/EUR ürünler otomatik TL'ye çevrilir</li>
                  <li>Kampanya koşulları sağlandığında otomatik indirim uygulanır</li>
                  <li>Müşteri doğum gününe yakınsa özel kampanya uygulanabilir</li>
                  <li>Ürün yıldızına tıklayarak favorilere ekleyebilirsiniz</li>
                  <li>Hurda alım indirimi otomatik olarak sepete yansır</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Ürün Resmi Popup */}
      {showImagePopup && ReactDOM.createPortal(
        <div className="image-popup-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 2147483647, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setShowImagePopup(false)}>
          <div className="image-popup-container" onClick={e => e.stopPropagation()}>
            <button className="image-popup-close" onClick={() => setShowImagePopup(false)}>✕</button>
            {/* iOS WebView: iki parmakla / çift dokunarak yakınlaştırılabilir */}
            {mobileApp
              ? <ZoomablePhoto src={popupImageUrl} alt={popupImageName} onBackgroundClick={() => setShowImagePopup(false)} />
              : <img src={popupImageUrl} alt={popupImageName} className="image-popup-img" />}
            <div className="image-popup-name">{popupImageName}</div>
          </div>
        </div>,
        document.body
      )}

      {/* Değişim/İade Fişi Modal */}
      {showExchangeReceiptModal && exchangeReceiptData && (
        <div className={`modal-overlay ${closingModal === 'exchangeReceipt' ? 'closing' : ''}`}>
          <div className="modal exchange-receipt-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{exchangeReceiptData.type === 'exchange' ? 'Değişim Fişi' : 'İade Fişi'}</h2>
              <button className="close-btn" onClick={() => handleCloseModal('exchangeReceipt', setShowExchangeReceiptModal)}>✕</button>
            </div>
            <div className="exchange-receipt-content">
              {/* Fiş Başlığı */}
              <div className="receipt-header">
                <div className="receipt-logo">{selectedStore?.name || 'AKIK KUYUMCULUK'}</div>
                <div className="receipt-date">{exchangeReceiptData.date}</div>
                <div className="receipt-info">
                  <span>Müşteri: {exchangeReceiptData.customerName}</span>
                  <span>Satış Sorumlusu: {exchangeReceiptData.staffName}</span>
                </div>
              </div>

              {/* İADE BÖLÜMÜ */}
              <div className="receipt-section refund-section">
                <div className="section-header">
                  <Undo2 size={16} />
                  <span className="section-title">İADE FİŞİ</span>
                  <span className="section-ref">
                    {exchangeReceiptData.refundExchangeId && `#${exchangeReceiptData.refundExchangeId}`}
                    {exchangeReceiptData.originalSaleId && ` (Orijinal: #${Array.isArray(exchangeReceiptData.originalSaleId) ? exchangeReceiptData.originalSaleId.join(', #') : exchangeReceiptData.originalSaleId})`}
                  </span>
                </div>
                <div className="receipt-items">
                  {exchangeReceiptData.returnItems.map((item, index) => (
                    <div key={index} className="receipt-item" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      {item.image && (
                        <img
                          src={`${IMAGE_HOST}${item.image}`}
                          alt={item.product_name}
                          style={{
                            width: '40px',
                            height: '40px',
                            objectFit: 'cover',
                            borderRadius: '4px',
                            border: '1px solid #fecaca',
                            flexShrink: 0
                          }}
                        />
                      )}
                      <div className="item-details" style={{ flex: 1 }}>
                        <span className="item-name">{item.product_name} {item.barcode && <span style={{ fontSize: '10px', color: '#666' }}>({item.barcode})</span>}</span>
                        <span className="item-qty">{item.quantity} adet x ₺{item.unit_price.toFixed(2)}</span>
                      </div>
                      <span className="item-total">-₺{item.total_price.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
                <div className="section-total refund-total">
                  <span>İade Toplamı:</span>
                  <span>-₺{exchangeReceiptData.returnTotal.toFixed(2)}</span>
                </div>
              </div>

              {/* YENİ SATIŞ BÖLÜMÜ */}
              {exchangeReceiptData.newItems.length > 0 && (
                <div className="receipt-section sale-section">
                  <div className="section-header">
                    <ShoppingCart size={16} />
                    <span className="section-title">SATIŞ FİŞİ</span>
                    {exchangeReceiptData.newSaleId && (
                      <span className="section-ref">Fiş No: #{exchangeReceiptData.newSaleId}</span>
                    )}
                  </div>
                  <div className="receipt-items">
                    {exchangeReceiptData.newItems.map((item, index) => (
                      <div key={index} className="receipt-item" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        {item.image && (
                          <img
                            src={`${IMAGE_HOST}${item.image}`}
                            alt={item.product_name}
                            style={{
                              width: '40px',
                              height: '40px',
                              objectFit: 'cover',
                              borderRadius: '4px',
                              border: '1px solid #bbf7d0',
                              flexShrink: 0
                            }}
                          />
                        )}
                        <div className="item-details" style={{ flex: 1 }}>
                          <span className="item-name">{item.product_name} {item.barcode && <span style={{ fontSize: '10px', color: '#666' }}>({item.barcode})</span>}</span>
                          <span className="item-qty">{item.quantity} adet x ₺{item.unit_price.toFixed(2)}</span>
                          {item.item_discount > 0 && (
                            <span style={{ fontSize: '11px', color: '#dc2626' }}>İndirim: -₺{item.item_discount.toFixed(2)} (%{item.discount_percent > 0 ? item.discount_percent.toFixed(0) : ((item.item_discount / item.original_total) * 100).toFixed(0)})</span>
                          )}
                        </div>
                        <span className="item-total">₺{item.total_price.toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                  {exchangeReceiptData.totalDiscount > 0 && (
                    <div style={{ padding: '8px 16px', background: '#fef2f2', borderRadius: '6px', margin: '8px 0' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', color: '#dc2626', fontWeight: 500, fontSize: '13px' }}>
                        <span>Toplam İndirim:</span>
                        <span>-₺{exchangeReceiptData.totalDiscount.toFixed(2)}</span>
                      </div>
                    </div>
                  )}
                  <div className="section-total sale-total">
                    <span>Satış Toplamı:</span>
                    <span>₺{exchangeReceiptData.newItemsTotal.toFixed(2)}</span>
                  </div>
                </div>
              )}

              {/* ÖZET */}
              <div className="receipt-summary">
                <div className="summary-row">
                  <span>İade Tutarı:</span>
                  <span className="refund-amount">-₺{exchangeReceiptData.returnTotal.toFixed(2)}</span>
                </div>
                {exchangeReceiptData.newItems.length > 0 && (
                  <>
                    {exchangeReceiptData.totalDiscount > 0 && (
                      <div className="summary-row">
                        <span>Satış Ara Toplam:</span>
                        <span>₺{exchangeReceiptData.newItemsSubtotal.toFixed(2)}</span>
                      </div>
                    )}
                    {exchangeReceiptData.totalDiscount > 0 && (
                      <div className="summary-row" style={{ color: '#dc2626' }}>
                        <span>İndirim:</span>
                        <span>-₺{exchangeReceiptData.totalDiscount.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="summary-row">
                      <span>Yeni Satış Tutarı:</span>
                      <span className="sale-amount">₺{exchangeReceiptData.newItemsTotal.toFixed(2)}</span>
                    </div>
                  </>
                )}
                <div className="summary-divider"></div>
                <div className="summary-row final">
                  <span>{exchangeReceiptData.difference >= 0 ? 'Müşteriden Alınacak:' : 'Müşteriye Verilecek:'}</span>
                  <span className={exchangeReceiptData.difference >= 0 ? 'positive' : 'negative'}>
                    ₺{Math.abs(exchangeReceiptData.difference).toFixed(2)}
                  </span>
                </div>
                <div className="summary-row payment-method">
                  <span>Ödeme Yöntemi:</span>
                  <span>{exchangeReceiptData.paymentMethod}</span>
                </div>
              </div>

              {/* Fiş Alt Bilgi */}
              <div className="receipt-footer">
                <p>Değişim ve iade işleminiz başarıyla tamamlanmıştır.</p>
                <p className="thank-you">Bizi tercih ettiğiniz için teşekkür ederiz!</p>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn secondary" onClick={() => setShowExchangeReceiptModal(false)}>Kapat</button>
            </div>
          </div>
        </div>
      )}

      {/* Hediye Kampanyası Modal */}
      {showGiftCampaignModal && eligibleGiftCampaign && (
        <div className="modal-overlay gift-campaign-overlay">
          <div className="modal gift-campaign-modal">
            <div className="gift-modal-header">
              <div className="gift-icon-large">🎁</div>
              <h2>
                {giftCampaignProduct?.remainingGifts > 1
                  ? `${giftCampaignProduct.remainingGifts} Hediye Kazandınız!`
                  : 'Hediye Kazandınız!'}
              </h2>
              <p className="gift-campaign-name">{eligibleGiftCampaign.name}</p>
            </div>
            <div className="gift-modal-body">
              <div className="gift-product-preview">
                <div className="gift-product-icon">🎀</div>
                <div className="gift-product-details">
                  <span className="gift-label">Hediye Ürün:</span>
                  <span className="gift-name">{giftCampaignProduct?.name || 'Hediye Ürün'}</span>
                  {giftCampaignProduct?.remainingGifts > 1 && (
                    <span className="gift-quantity-info">
                      x{giftCampaignProduct.remainingGifts} adet
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="gift-modal-footer">
              <button
                className="btn-skip-gift"
                onClick={() => {
                  setShowGiftCampaignModal(false);
                  setEligibleGiftCampaign(null);
                  setGiftCampaignProduct(null);
                }}
              >
                Şimdilik Geç
              </button>
              {giftCampaignProduct?.remainingGifts > 1 ? (
                <>
                  <button
                    className="btn-add-gift-one"
                    onClick={() => addGiftProductToCart(giftCampaignProduct?.id, 1)}
                  >
                    🎁 1 Adet Ekle
                  </button>
                  <button
                    className="btn-add-gift"
                    onClick={() => addGiftProductToCart(giftCampaignProduct?.id, giftCampaignProduct?.remainingGifts)}
                  >
                    🎁 Tümünü Ekle ({giftCampaignProduct?.remainingGifts})
                  </button>
                </>
              ) : (
                <button
                  className="btn-add-gift"
                  onClick={() => addGiftProductToCart(giftCampaignProduct?.id)}
                >
                  🎁 Hediyeyi Ekle
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Transferler Modal */}
      {showTransfersModal && (
        <div className={`modal-overlay ${closingModal === 'transfers' ? 'closing' : ''}`} ref={el => {
          if (el) document.body.style.overflow = 'hidden';
          else document.body.style.overflow = '';
        }}>
          <div className={`modal transfers-modal ${transferMode ? 'transfers-modal-large' : ''} ${transferMode === 'receive-transfer' ? 'receive-transfer-mode' : ''}`} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              {transferMode ? (
                <>
                  <button
                    className="back-btn"
                    onClick={() => {
                      const resetTransferState = resetAllTransferState;

                      if (transferMode === 'transfer' && transferItems.length > 0) {
                        showConfirmation({
                          title: 'Kaydedilmemiş Transfer',
                          message: 'Kaydedilmemiş transfer ürünleri var. Geri dönmek istediğinize emin misiniz?',
                          confirmText: 'Evet, Geri Dön',
                          cancelText: 'Hayır, Kal',
                          type: 'warning',
                          onConfirm: resetTransferState
                        });
                        return;
                      }
                      if (transferMode === 'transfer-request' && requestItems.length > 0) {
                        showConfirmation({
                          title: 'Kaydedilmemiş Talep',
                          message: 'Kaydedilmemiş talep ürünleri var. Geri dönmek istediğinize emin misiniz?',
                          confirmText: 'Evet, Geri Dön',
                          cancelText: 'Hayır, Kal',
                          type: 'warning',
                          onConfirm: resetTransferState
                        });
                        return;
                      }
                      resetTransferState();
                    }}
                  >
                    <span style={{ color: '#ffffff', fontSize: '18px', fontWeight: 'bold' }}>←</span>
                  </button>
                  <h2>
                    {transferMode === 'transfer' && <><Send size={20} style={{ marginRight: '8px' }} /> Mağazaya Transfer Yap</>}
                    {transferMode === 'sent-transfers' && <><ArrowRight size={20} style={{ marginRight: '8px' }} /> Gönderilen Transferler</>}
                    {transferMode === 'transfer-request' && <><Plus size={20} style={{ marginRight: '8px' }} /> Transfer Talebi Oluştur</>}
                    {transferMode === 'sent-requests' && <><Send size={20} style={{ marginRight: '8px' }} /> Gönderilen Transfer Talepleri</>}
                    {transferMode === 'incoming-requests' && <><Inbox size={20} style={{ marginRight: '8px' }} /> Gelen Transfer Talepleri</>}
                    {transferMode === 'receive-transfer' && <><Download size={20} style={{ marginRight: '8px' }} /> Transfer Teslim Al</>}
                    {transferMode === 'history' && <><Clock size={20} style={{ marginRight: '8px' }} /> Transfer Geçmişi</>}
                  </h2>
                </>
              ) : (
                <h2><ArrowLeftRight size={20} style={{ marginRight: '8px' }} /> Transferler & Stok İşlemleri</h2>
              )}
              <button className="close-btn" onClick={closeTransferModal}>✕</button>
            </div>
            <div className="modal-body transfers-options-body">
              {!transferMode ? (
                <>
                  <div className="transfers-section">
                    <h3 className="transfers-section-title">Transfer İşlemleri</h3>
                    <div className="transfers-options-grid">
                      <button
                        className="transfer-option-btn"
                        onClick={() => setTransferMode('transfer')}
                      >
                        <span className="option-icon"><Send size={22} /></span>
                        <span className="option-title">Mağazaya Transfer Yap</span>
                      </button>
                      <button
                        className="transfer-option-btn"
                        onClick={() => {
                          setTransferMode('sent-transfers');
                          fetchSentTransfers();
                        }}
                      >
                        <span className="option-icon"><ArrowRight size={22} /></span>
                        <span className="option-title">Gönderilen Transferler</span>
                      </button>
                      <button
                        className="transfer-option-btn"
                        onClick={() => setTransferMode('transfer-request')}
                      >
                        <span className="option-icon"><Plus size={22} /></span>
                        <span className="option-title">Transfer Talebi Oluştur</span>
                      </button>
                      <button
                        className="transfer-option-btn"
                        onClick={() => {
                          setTransferMode('sent-requests');
                          fetchSentRequests();
                          // Reddedilen taleplerin rozeti, kullanıcı listeyi açtığında temizlensin
                          const storeId = selectedStore?.id || user?.store_id;
                          if (storeId && previousRejectedIdsRef.current && previousRejectedIdsRef.current.length > 0) {
                            const ackKey = `ackRejectedTransferRequests_${storeId}`;
                            let ackIds = [];
                            try {
                              const raw = localStorage.getItem(ackKey);
                              if (raw) ackIds = JSON.parse(raw) || [];
                            } catch (e) { ackIds = []; }
                            const merged = Array.from(new Set([...ackIds, ...previousRejectedIdsRef.current]));
                            try { localStorage.setItem(ackKey, JSON.stringify(merged)); } catch (e) {}
                            previousRejectedIdsRef.current = [];
                            setRejectedRequestCount(0);
                          }
                        }}
                        style={{ position: 'relative' }}
                      >
                        {rejectedRequestCount > 0 && (
                          <span style={{
                            position: 'absolute', top: -6, right: -6,
                            background: '#f59e0b', color: 'white', borderRadius: '50%',
                            minWidth: 22, height: 22, display: 'flex', alignItems: 'center',
                            justifyContent: 'center', fontSize: 12, fontWeight: 700,
                            boxShadow: '0 2px 6px rgba(245,158,11,0.4)'
                          }} title="Reddedilen talep">{rejectedRequestCount}</span>
                        )}
                        <span className="option-icon"><Send size={22} /></span>
                        <span className="option-title">Gönderilen Transfer Talepleri</span>
                      </button>
                      <button
                        className="transfer-option-btn"
                        onClick={() => {
                          setTransferMode('incoming-requests');
                          fetchIncomingRequests();
                        }}
                        style={{ position: 'relative' }}
                      >
                        {incomingRequestCount > 0 && (
                          <span style={{
                            position: 'absolute', top: -6, right: -6,
                            background: '#ef4444', color: 'white', borderRadius: '50%',
                            minWidth: 22, height: 22, display: 'flex', alignItems: 'center',
                            justifyContent: 'center', fontSize: 12, fontWeight: 700,
                            boxShadow: '0 2px 6px rgba(239,68,68,0.4)'
                          }}>{incomingRequestCount}</span>
                        )}
                        <span className="option-icon"><Inbox size={22} /></span>
                        <span className="option-title">Gelen Transfer Talepleri</span>
                      </button>
                    </div>
                  </div>
                  <div className="transfers-section">
                    <h3 className="transfers-section-title">Stok İşlemleri</h3>
                    <div className="transfers-options-grid">
                      <button
                        className="transfer-option-btn receive"
                        onClick={() => {
                          setTransferMode('receive-transfer');
                          fetchPendingTransfers();
                        }}
                        style={{ position: 'relative' }}
                      >
                        {pendingTransferCount > 0 && (
                          <span style={{
                            position: 'absolute', top: -6, right: -6,
                            background: '#ef4444', color: 'white', borderRadius: '50%',
                            minWidth: 22, height: 22, display: 'flex', alignItems: 'center',
                            justifyContent: 'center', fontSize: 12, fontWeight: 700,
                            boxShadow: '0 2px 6px rgba(239,68,68,0.4)'
                          }}>{pendingTransferCount}</span>
                        )}
                        <span className="option-icon"><Inbox size={22} /></span>
                        <span className="option-title">Transfer Teslim Al</span>
                      </button>
                      <button
                        className="transfer-option-btn"
                        onClick={() => {
                          setTransferMode('history');
                          fetchTransferHistory();
                        }}
                      >
                        <span className="option-icon"><Clock size={22} /></span>
                        <span className="option-title">Transfer Geçmişi</span>
                      </button>
                    </div>
                  </div>
                </>
              ) : transferMode === 'transfer' && (
                <div className="transfer-split-layout">
                <div className="transfer-left-panel">
                <div className="transfer-form-row-inline">
                  <div className="transfer-form-row">
                    <label>Hedef Mağaza</label>
                    <select
                      value={transferTargetStore}
                      onChange={(e) => setTransferTargetStore(e.target.value)}
                      className="transfer-store-select"
                    >
                      <option value="">-- Mağaza Seçin --</option>
                      {stores
                        .filter(s => s.id !== (selectedStore?.id || user?.store_id))
                        .map(store => (
                          <option key={store.id} value={store.id}>{store.name}</option>
                        ))
                      }
                    </select>
                  </div>

                  <div className="transfer-form-row">
                    <label>Kasiyer</label>
                    <select
                      value={transferStaffId}
                      onChange={(e) => {
                        const staff = salesStaffList.find(s => s.id === parseInt(e.target.value));
                        setTransferStaffId(staff ? staff.id : '');
                        setTransferStaffName(staff ? staff.name : '');
                      }}
                      className="transfer-store-select"
                    >
                      <option value="">-- Kasiyer Seçin --</option>
                      {salesStaffList.map(staff => (
                        <option key={staff.id} value={staff.id}>{staff.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="transfer-form-row">
                    <label>Barkod Okutun</label>
                    <input
                      ref={transferBarcodeRef}
                      type="text"
                      value={transferBarcode}
                      onChange={(e) => setTransferBarcode(e.target.value)}
                      onKeyDown={handleTransferBarcode}
                      placeholder="Barkod okutun veya yazın..."
                      className="transfer-barcode-input"
                      autoFocus
                    />
                  </div>
                </div>
                <div className="transfer-left-scroll">
                <div className="inline-transfer-form">

                  {transferItems.length > 0 && (
                    <div className="transfer-items-section">
                      <h4>Transfer Edilecek Ürünler ({transferItems.length})</h4>
                      <div className="transfer-items-list">
                        {transferItems.map((item, index) => (
                          <div key={item.product_id} className={`transfer-item-row${lastScannedTransferProduct?.product_id === item.product_id ? ' selected' : ''}`}>
                            <div className="transfer-item-image" style={item.image ? { cursor: 'zoom-in' } : {}} onClick={() => item.image && openImagePopup(item.image, item.product_name)}>
                              {item.image ? (
                                <img src={getImageUrl(item.image)} alt={item.product_name} />
                              ) : (
                                <div className="no-image-placeholder"><Package size={24} /></div>
                              )}
                            </div>
                            <div className="transfer-item-info">
                              <span className="transfer-item-name">{item.product_name}</span>
                              <span className="transfer-item-barcode">{item.barcode}</span>
                              <span className="transfer-item-stock">Stok: {item.stock_quantity}</span>
                            </div>
                            <div className="transfer-item-quantity">
                              <button
                                onClick={() => updateTransferItemQuantity(index, item.quantity - 1)}
                                className="qty-btn"
                              >-</button>
                              <span style={{ cursor: 'pointer' }} onDoubleClick={(e) => { const span = e.target; const input = document.createElement('input'); input.type = 'number'; input.min = '1'; input.max = item.stock_quantity; input.value = item.quantity; input.style.cssText = 'width:50px;text-align:center;font-size:inherit;font-weight:inherit;border:2px solid #3b82f6;border-radius:4px;padding:2px;'; span.replaceWith(input); input.focus(); input.select(); const finish = () => { const val = Math.min(Math.max(1, parseInt(input.value) || 1), item.stock_quantity); updateTransferItemQuantity(index, val); }; input.addEventListener('blur', finish); input.addEventListener('keydown', (ev) => { if (ev.key === 'Enter') input.blur(); }); }}>{item.quantity}</span>
                              <button
                                onClick={() => updateTransferItemQuantity(index, item.quantity + 1)}
                                className="qty-btn"
                                disabled={item.quantity >= item.stock_quantity}
                              >+</button>
                            </div>
                            <button
                              className="transfer-item-remove"
                              onClick={() => removeTransferItem(index)}
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="transfer-form-row">
                    <label>Not (Opsiyonel)</label>
                    <textarea
                      value={transferNotes}
                      onChange={(e) => setTransferNotes(e.target.value)}
                      placeholder="Transfer ile ilgili not ekleyin..."
                      className="transfer-notes-input"
                      rows={2}
                    />
                  </div>

                </div>
                </div>
                </div>
                <div className="transfer-right-panel">
                  {lastScannedTransferProduct ? (
                    <>
                      <div className="scanned-product-image" style={lastScannedTransferProduct.image ? { cursor: 'zoom-in' } : {}} onClick={() => lastScannedTransferProduct.image && openImagePopup(lastScannedTransferProduct.image, lastScannedTransferProduct.name)}>
                        {lastScannedTransferProduct.image ? (
                          <img src={getImageUrl(lastScannedTransferProduct.image)} alt={lastScannedTransferProduct.name} />
                        ) : (
                          <div className="scanned-product-placeholder"><Package size={64} /></div>
                        )}
                      </div>
                      <div className="scanned-product-name">{lastScannedTransferProduct.name}</div>
                      <div className="scanned-product-barcode">{lastScannedTransferProduct.barcode}</div>
                    </>
                  ) : (
                    <div className="scanned-product-empty">
                      <Package size={48} />
                      <span>Barkod okutun</span>
                    </div>
                  )}
                  <div className="transfer-form-actions" style={{ marginTop: 'auto', paddingTop: '16px' }}>
                    <button
                      className="transfer-cancel-btn"
                      onClick={() => {
                        const resetTransfer = () => {
                          setTransferMode(null);
                          setTransferItems([]);
                          setTransferBarcode('');
                          setTransferNotes('');
                          setTransferTargetStore('');
                        };
                        if (transferItems.length > 0) {
                          showConfirmation({
                            title: 'Kaydedilmemiş Ürünler',
                            message: 'Kaydedilmemiş ürünler var. İptal etmek istediğinize emin misiniz?',
                            confirmText: 'Evet, İptal Et',
                            cancelText: 'Hayır, Kal',
                            type: 'warning',
                            onConfirm: resetTransfer
                          });
                          return;
                        }
                        resetTransfer();
                      }}
                    >
                      İptal
                    </button>
                    <button
                      className="transfer-submit-btn"
                      onClick={createTransfer}
                      disabled={savingTransfer || transferItems.length === 0 || !transferTargetStore}
                    >
                      {savingTransfer ? 'Gönderiliyor...' : `Transfer Oluştur (${transferItems.length} çeşit toplam ${transferItems.reduce((s, i) => s + (Number(i.quantity) || 0), 0)} ürün)`}
                    </button>
                  </div>
                </div>
                </div>
              )}

              {transferMode === 'sent-transfers' && (
                <div className="sent-transfers-view">
                  {loadingSentTransfers ? (
                    <div className="loading-transfers">
                      <RefreshCw size={24} className="spin" />
                      <span>Transferler yükleniyor...</span>
                    </div>
                  ) : selectedTransferDetail ? (
                    <div className="transfer-detail-view">
                      <button
                        className="detail-back-btn"
                        onClick={() => { setSelectedTransferDetail(null); setEditingTransfer(false); setEditingTransferItems([]); }}
                      >
                        <ArrowLeft size={16} /> Listeye Dön
                      </button>
                      <div className="transfer-summary-card">
                        <div className="transfer-summary-top">
                          <div className="detail-code">
                            <span className="label">Transfer Kodu</span>
                            <span className="value">{selectedTransferDetail.transfer_code}</span>
                          </div>
                          <span className={`status-text ${selectedTransferDetail.status}`}>
                            <span className="status-dot"></span>
                            {selectedTransferDetail.status === 'pending' && 'Beklemede'}
                            {selectedTransferDetail.status === 'in_transit' && 'Yolda'}
                            {selectedTransferDetail.status === 'partially_received' && 'Kısmi Teslim Alındı'}
                            {selectedTransferDetail.status === 'received' && 'Teslim Alındı'}
                            {selectedTransferDetail.status === 'delivered' && 'Teslim Edildi'}
                            {selectedTransferDetail.status === 'cancelled' && 'İptal'}
                          </span>
                        </div>
                        <div className="transfer-summary-meta">
                          <div className="meta-item">
                            <span className="m-label">Hedef Mağaza</span>
                            <span className="m-value">{selectedTransferDetail.to_store_name}</span>
                          </div>
                          <div className="meta-item">
                            <span className="m-label">Tarih</span>
                            <span className="m-value">{new Date(selectedTransferDetail.created_at).toLocaleString('tr-TR')}</span>
                          </div>
                          {selectedTransferDetail.notes && (
                            <div className="meta-item meta-item-note">
                              <span className="m-label">Not</span>
                              <span className="m-value">{selectedTransferDetail.notes}</span>
                            </div>
                          )}
                        </div>
                      </div>
                      {!editingTransfer && ['pending', 'in_transit'].includes(selectedTransferDetail.status) && (
                        <div className="transfer-detail-actions">
                          <button
                            className="td-action-btn edit"
                            onClick={() => startEditingTransfer(selectedTransferDetail)}
                          >
                            <Pencil size={12} /> Düzenle
                          </button>
                          <button
                            className="td-action-btn danger"
                            onClick={() => cancelTransfer(selectedTransferDetail.id)}
                            disabled={cancellingTransfer}
                          >
                            <X size={13} /> {cancellingTransfer ? 'İptal ediliyor...' : 'İptal Et'}
                          </button>
                        </div>
                      )}
                      {editingTransfer && (
                        <div className="transfer-detail-actions">
                          <button
                            className="td-action-btn muted"
                            onClick={() => { setEditingTransfer(false); setEditingTransferItems([]); }}
                            disabled={savingTransferEdit}
                          >
                            Vazgeç
                          </button>
                          <button
                            className="td-action-btn success"
                            onClick={() => saveTransferEdit(selectedTransferDetail.id)}
                            disabled={savingTransferEdit}
                          >
                            <Check size={12} /> {savingTransferEdit ? 'Kaydediliyor...' : 'Kaydet'}
                          </button>
                        </div>
                      )}
                      <div className="transfer-detail-items">
                        <h4>Transfer Edilen Ürünler ({(editingTransfer ? editingTransferItems : selectedTransferDetail.items)?.length || 0})</h4>
                        <div className="detail-items-list">
                          {(editingTransfer ? editingTransferItems : selectedTransferDetail.items)?.map((item, idx) => {
                            const maxQty = editingTransfer && item.source_stock !== null && item.source_stock !== undefined
                              ? item.original_quantity + item.source_stock
                              : undefined;
                            const minQty = item.received_quantity || 0;
                            return (
                            <div key={idx} className="detail-item-row" style={editingTransfer && item.quantity === 0 ? { opacity: 0.4, textDecoration: 'line-through' } : {}}>
                              <div className="detail-item-image" style={item.image ? { cursor: 'zoom-in' } : {}} onClick={() => item.image && openImagePopup(item.image, item.product_name)}>
                                {item.image ? (
                                  <img src={`${IMAGE_HOST}${item.image}`} alt={item.product_name} />
                                ) : (
                                  <div className="no-image-placeholder"><Package size={20} /></div>
                                )}
                              </div>
                              <div className="detail-item-info">
                                <span className="detail-item-name">{item.product_name}</span>
                                <span className="detail-item-barcode">{item.barcode}</span>
                                {editingTransfer && item.source_stock !== null && item.source_stock !== undefined && (
                                  <span style={{ fontSize: '11px', color: item.source_stock > 0 ? '#64748b' : '#dc2626' }}>Stok: {item.source_stock}</span>
                                )}
                              </div>
                              {editingTransfer ? (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
                                  <input
                                    type="number"
                                    min={minQty}
                                    max={maxQty}
                                    value={item.quantity}
                                    onChange={(e) => {
                                      let val = parseInt(e.target.value) || 0;
                                      if (val < minQty) val = minQty;
                                      if (maxQty !== undefined && val > maxQty) val = maxQty;
                                      setEditingTransferItems(prev => prev.map((ei, i) => i === idx ? { ...ei, quantity: val } : ei));
                                    }}
                                    style={{
                                      width: '55px', padding: '4px 6px', border: '1px solid #cbd5e1', borderRadius: '6px',
                                      fontSize: '14px', fontWeight: '600', textAlign: 'center',
                                      ...(item.quantity === 0 ? { borderColor: '#dc2626', background: '#fef2f2', color: '#dc2626' } : {})
                                    }}
                                  />
                                </div>
                              ) : (
                                <div className="detail-item-qty-col">
                                  <span className="detail-item-qty">{item.quantity} adet</span>
                                  {(() => {
                                    const received = item.received_quantity || 0;
                                    const isComplete = received >= item.quantity;
                                    const isPartial = received > 0 && !isComplete;
                                    return (
                                      <span className={`detail-item-received-badge ${isComplete ? 'complete' : isPartial ? 'partial' : 'waiting'}`}>
                                        {isComplete ? '✓' : isPartial ? '◐' : '○'} {received}/{item.quantity} teslim alındı
                                      </span>
                                    );
                                  })()}
                                </div>
                              )}
                            </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  ) : sentTransfers.length === 0 ? (
                    <div className="no-transfers">
                      <Package size={48} />
                      <p>Henüz gönderilen transfer bulunmuyor</p>
                    </div>
                  ) : (
                    <div className="transfers-list">
                      {sentTransfers.map(transfer => (
                        <div
                          key={transfer.id}
                          className="transfer-list-item"
                          onClick={() => fetchTransferDetail(transfer.id)}
                        >
                          <div className="transfer-list-main">
                            <span className="transfer-code">{transfer.transfer_code}</span>
                            <span className="transfer-store">→ {transfer.to_store_name}</span>
                          </div>
                          <div className="transfer-list-meta">
                            <span className="transfer-date">
                              {new Date(transfer.created_at).toLocaleDateString('tr-TR')}
                            </span>
                            <span className={`status-text ${transfer.status}`}>
                              <span className="status-dot"></span>
                              {transfer.status === 'pending' && 'Beklemede'}
                              {transfer.status === 'in_transit' && 'Yolda'}
                              {transfer.status === 'partially_received' && 'Kısmi Teslim Alındı'}
                              {transfer.status === 'received' && 'Teslim Alındı'}
                              {transfer.status === 'delivered' && 'Teslim Edildi'}
                              {transfer.status === 'cancelled' && 'İptal'}
                            </span>
                          </div>
                          <div className="transfer-list-count">
                            {transfer.item_count || transfer.items?.length || '?'} ürün
                            {Number(transfer.received_total) > 0 && (
                              <span className="transfer-list-received">
                                {' '}· {transfer.received_total}/{transfer.total_quantity} teslim alındı
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {transferMode === 'transfer-request' && (
                <div className="transfer-split-layout">
                <div className="transfer-left-panel">
                <div className="transfer-form-row-inline">
                  <div className="transfer-form-row">
                    <label>Kaynak Mağaza</label>
                    <select
                      value={requestSourceStore}
                      onChange={(e) => {
                        setRequestSourceStore(e.target.value);
                        setRequestItems([]);
                        setRequestSearchResults([]);
                      }}
                      className="transfer-store-select"
                    >
                      <option value="">-- Mağaza Seçin --</option>
                      {stores
                        .filter(s => s.id !== (selectedStore?.id || user?.store_id))
                        .map(store => (
                          <option key={store.id} value={store.id}>{store.name}</option>
                        ))
                      }
                    </select>
                  </div>

                  <div className="transfer-form-row">
                    <label>Kasiyer</label>
                    <select
                      value={transferStaffId}
                      onChange={(e) => {
                        const staff = salesStaffList.find(s => s.id === parseInt(e.target.value));
                        setTransferStaffId(staff ? staff.id : '');
                        setTransferStaffName(staff ? staff.name : '');
                      }}
                      className="transfer-store-select"
                    >
                      <option value="">-- Kasiyer Seçin --</option>
                      {salesStaffList.map(staff => (
                        <option key={staff.id} value={staff.id}>{staff.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="transfer-form-row">
                    <label>Ürün Ara</label>
                    <div className="request-search-container">
                      <input
                        type="text"
                        value={requestProductSearch}
                        onChange={(e) => {
                          setRequestProductSearch(e.target.value);
                          searchRequestProducts(e.target.value);
                        }}
                        onKeyDown={handleRequestBarcodeSearch}
                        placeholder={requestSourceStore ? "Barkod okutun veya ürün adı yazın..." : "Önce mağaza seçin"}
                        className="transfer-barcode-input"
                        disabled={!requestSourceStore}
                      />
                      {searchingRequestProducts && (
                        <div className="search-spinner"><RefreshCw size={16} className="spin" /></div>
                      )}
                      {requestSearchResults.length > 0 && (
                        <div className="request-search-results">
                          {requestSearchResults.map(product => (
                            <div
                              key={product.id}
                              className="request-search-item"
                              onClick={() => addRequestItem(product)}
                            >
                              <div className="request-search-image">
                                {product.image ? (
                                  <img src={`${IMAGE_HOST}${product.image}`} alt={product.name} />
                                ) : (
                                  <Package size={20} />
                                )}
                              </div>
                              <div className="request-search-info">
                                <span className="name">{product.name}</span>
                                <span className="barcode">{product.barcode}</span>
                              </div>
                              <span className="stock">Stok: {product.stock_quantity}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="transfer-left-scroll">
                <div className="inline-transfer-form">

                  {requestItems.length > 0 && (
                    <div className="transfer-items-section">
                      <h4>Talep Edilecek Ürünler ({requestItems.length})</h4>
                      <div className="transfer-items-list">
                        {requestItems.map((item, index) => (
                          <div key={item.product_id} className="transfer-item-row">
                            <div className="transfer-item-image" style={item.image ? { cursor: 'zoom-in' } : {}} onClick={() => item.image && openImagePopup(item.image, item.product_name)}>
                              {item.image ? (
                                <img src={getImageUrl(item.image)} alt={item.product_name} />
                              ) : (
                                <div className="no-image-placeholder"><Package size={24} /></div>
                              )}
                            </div>
                            <div className="transfer-item-info">
                              <span className="transfer-item-name">{item.product_name}</span>
                              <span className="transfer-item-barcode">{item.barcode}</span>
                              <span className="transfer-item-stock">Stok: {item.stock_quantity}</span>
                            </div>
                            <div className="transfer-item-quantity">
                              <button
                                onClick={() => updateRequestItemQuantity(index, item.quantity - 1)}
                                className="qty-btn"
                              >-</button>
                              <span>{item.quantity}</span>
                              <button
                                onClick={() => updateRequestItemQuantity(index, item.quantity + 1)}
                                className="qty-btn"
                                disabled={item.quantity >= item.stock_quantity}
                              >+</button>
                            </div>
                            <button
                              className="transfer-item-remove"
                              onClick={() => removeRequestItem(index)}
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="transfer-form-row">
                    <label>Not (Opsiyonel)</label>
                    <textarea
                      value={requestNotes}
                      onChange={(e) => setRequestNotes(e.target.value)}
                      placeholder="Talep ile ilgili not ekleyin..."
                      className="transfer-notes-input"
                      rows={2}
                    />
                  </div>

                </div>
                </div>
                </div>
                <div className="transfer-right-panel">
                  {lastScannedTransferProduct ? (
                    <>
                      <div className="scanned-product-image" style={lastScannedTransferProduct.image ? { cursor: 'zoom-in' } : {}} onClick={() => lastScannedTransferProduct.image && openImagePopup(lastScannedTransferProduct.image, lastScannedTransferProduct.name)}>
                        {lastScannedTransferProduct.image ? (
                          <img src={getImageUrl(lastScannedTransferProduct.image)} alt={lastScannedTransferProduct.name} />
                        ) : (
                          <div className="scanned-product-placeholder"><Package size={64} /></div>
                        )}
                      </div>
                      <div className="scanned-product-name">{lastScannedTransferProduct.name}</div>
                      <div className="scanned-product-barcode">{lastScannedTransferProduct.barcode}</div>
                    </>
                  ) : (
                    <div className="scanned-product-empty">
                      <Package size={48} />
                      <span>Barkod okutun</span>
                    </div>
                  )}
                  <div className="transfer-form-actions" style={{ marginTop: 'auto', paddingTop: '16px' }}>
                    <button
                      className="transfer-cancel-btn"
                      onClick={() => {
                        const resetRequest = () => {
                          setTransferMode(null);
                          setRequestItems([]);
                          setRequestProductSearch('');
                          setRequestSearchResults([]);
                          setRequestNotes('');
                          setRequestSourceStore('');
                        };
                        if (requestItems.length > 0) {
                          showConfirmation({
                            title: 'Kaydedilmemiş Ürünler',
                            message: 'Kaydedilmemiş ürünler var. İptal etmek istediğinize emin misiniz?',
                            confirmText: 'Evet, İptal Et',
                            cancelText: 'Hayır, Kal',
                            type: 'warning',
                            onConfirm: resetRequest
                          });
                          return;
                        }
                        resetRequest();
                      }}
                    >
                      İptal
                    </button>
                    <button
                      className="transfer-submit-btn request"
                      onClick={createTransferRequest}
                      disabled={savingRequest || requestItems.length === 0 || !requestSourceStore}
                    >
                      {savingRequest ? 'Gönderiliyor...' : `Talep Oluştur (${requestItems.length} ürün)`}
                    </button>
                  </div>
                </div>
                </div>
              )}

              {transferMode === 'sent-requests' && (
                <div className="sent-transfers-view">
                  {loadingSentRequests ? (
                    <div className="loading-transfers">
                      <RefreshCw size={24} className="spin" />
                      <span>Talepler yükleniyor...</span>
                    </div>
                  ) : selectedRequestDetail ? (
                    <div className="transfer-detail-view">
                      <button
                        className="detail-back-btn"
                        onClick={() => setSelectedRequestDetail(null)}
                      >
                        <ArrowLeft size={16} /> Listeye Dön
                      </button>
                      <div className="transfer-summary-card">
                        <div className="transfer-summary-top">
                          <div className="detail-code">
                            <span className="label">Talep Kodu</span>
                            <span className="value">{selectedRequestDetail.request_code}</span>
                          </div>
                          <span className={`status-text ${selectedRequestDetail.status}`}>
                            <span className="status-dot"></span>
                            {selectedRequestDetail.status === 'pending' && 'Beklemede'}
                            {selectedRequestDetail.status === 'approved' && 'Onaylandı'}
                            {selectedRequestDetail.status === 'rejected' && 'Reddedildi'}
                            {selectedRequestDetail.status === 'completed' && 'Tamamlandı'}
                            {selectedRequestDetail.status === 'cancelled' && 'İptal'}
                          </span>
                        </div>
                        <div className="transfer-summary-meta">
                          <div className="meta-item">
                            <span className="m-label">Talep Edilen Mağaza</span>
                            <span className="m-value">{selectedRequestDetail.from_store_name}</span>
                          </div>
                          <div className="meta-item">
                            <span className="m-label">Tarih</span>
                            <span className="m-value">{new Date(selectedRequestDetail.created_at).toLocaleString('tr-TR')}</span>
                          </div>
                          {selectedRequestDetail.notes && (
                            <div className="meta-item meta-item-note">
                              <span className="m-label">Not</span>
                              <span className="m-value">{selectedRequestDetail.notes}</span>
                            </div>
                          )}
                        </div>
                      </div>
                      {selectedRequestDetail.status === 'rejected' && (
                        <div style={{ padding: '10px 14px', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '8px', fontSize: '0.85rem', color: '#991b1b', display: 'flex', flexDirection: 'column', gap: '4px', flexShrink: 0 }}>
                          {selectedRequestDetail.rejection_reason && (
                            <div><strong>Red Sebebi:</strong> {selectedRequestDetail.rejection_reason}</div>
                          )}
                          {(selectedRequestDetail.rejected_staff_name || selectedRequestDetail.rejected_by_name) && (
                            <div>
                              <strong>Reddeden:</strong> {selectedRequestDetail.rejected_staff_name || selectedRequestDetail.rejected_by_name}
                            </div>
                          )}
                          {selectedRequestDetail.rejected_at && (
                            <div><strong>Red Tarihi:</strong> {new Date(selectedRequestDetail.rejected_at).toLocaleString('tr-TR')}</div>
                          )}
                        </div>
                      )}
                      {(selectedRequestDetail.status === 'approved' || selectedRequestDetail.status === 'completed') && (selectedRequestDetail.approver_name || selectedRequestDetail.approved_staff_name || selectedRequestDetail.approved_by_name) && (
                        <div style={{ padding: '10px 14px', background: '#ecfdf5', border: '1px solid #86efac', borderRadius: '8px', fontSize: '0.85rem', color: '#065f46', flexShrink: 0 }}>
                          <strong>Onaylayan:</strong> {selectedRequestDetail.approver_name || selectedRequestDetail.approved_staff_name || selectedRequestDetail.approved_by_name || '—'}
                          {selectedRequestDetail.approved_at && ` (${new Date(selectedRequestDetail.approved_at).toLocaleString('tr-TR')})`}
                        </div>
                      )}
                      {selectedRequestDetail.status === 'pending' && (
                        <div className="transfer-detail-actions">
                          <button
                            className="td-action-btn danger"
                            onClick={() => cancelTransferRequest(selectedRequestDetail.id)}
                            disabled={cancellingRequest}
                          >
                            <X size={13} /> {cancellingRequest ? 'İptal ediliyor...' : 'Talebi İptal Et'}
                          </button>
                        </div>
                      )}
                      <div className="transfer-detail-items">
                        <h4>Talep Edilen Ürünler ({selectedRequestDetail.items?.length || 0})</h4>
                        <div className="detail-items-list">
                          {selectedRequestDetail.items?.map((item, idx) => (
                            <div key={idx} className="detail-item-row">
                              <div className="detail-item-image" style={item.image ? { cursor: 'zoom-in' } : {}} onClick={() => item.image && openImagePopup(item.image, item.product_name)}>
                                {item.image ? (
                                  <img src={`${IMAGE_HOST}${item.image}`} alt={item.product_name} />
                                ) : (
                                  <div className="no-image-placeholder"><Package size={20} /></div>
                                )}
                              </div>
                              <div className="detail-item-info">
                                <span className="detail-item-name">{item.product_name}</span>
                                <span className="detail-item-barcode">{item.barcode}</span>
                              </div>
                              <span className="detail-item-qty">{item.quantity} adet</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : sentRequests.length === 0 ? (
                    <div className="no-transfers">
                      <Package size={48} />
                      <p>Henüz gönderilen transfer talebi bulunmuyor</p>
                    </div>
                  ) : (
                    <div className="transfers-list">
                      {sentRequests.map(request => (
                        <div
                          key={request.id}
                          className="transfer-list-item request-item"
                          onClick={() => fetchRequestDetail(request.id)}
                        >
                          <div className="transfer-list-main">
                            <span className="transfer-code">{request.request_code}</span>
                            <span className="transfer-store">← {request.from_store_name}{(request.requester_name || request.staff_name) ? ` (${request.requester_name || request.staff_name})` : ''}</span>
                          </div>
                          <div className="transfer-list-meta">
                            <span className="transfer-date">
                              {new Date(request.created_at).toLocaleDateString('tr-TR')}
                            </span>
                            <span className={`status-text ${request.status}`}>
                              <span className="status-dot"></span>
                              {request.status === 'pending' && 'Beklemede'}
                              {request.status === 'approved' && 'Onaylandı'}
                              {request.status === 'rejected' && 'Reddedildi'}
                              {request.status === 'completed' && 'Tamamlandı'}
                              {request.status === 'cancelled' && 'İptal'}
                            </span>
                          </div>
                          <div className="transfer-list-count">
                            {request.item_count || request.items?.length || '?'} ürün
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {transferMode === 'incoming-requests' && (
                <div className="sent-transfers-view">
                  {/* Tab toggle: Bekleyen / Geçmiş */}
                  <div className="transfer-tab-toggle">
                    <button
                      className={`transfer-tab-btn ${incomingRequestsTab === 'pending' ? 'active' : ''}`}
                      onClick={() => setIncomingRequestsTab('pending')}
                    >
                      <Inbox size={16} /> Bekleyen ({incomingRequests.length})
                    </button>
                    <button
                      className={`transfer-tab-btn ${incomingRequestsTab === 'history' ? 'active' : ''}`}
                      onClick={() => {
                        setIncomingRequestsTab('history');
                        setSelectedHistoryIncomingRequest(null);
                        fetchIncomingRequestsHistory();
                      }}
                    >
                      <Clock size={16} /> Geçmiş
                    </button>
                  </div>

                  {incomingRequestsTab === 'history' ? (
                    loadingIncomingHistory ? (
                      <div className="loading-transfers">
                        <RefreshCw size={24} className="spin" />
                        <span>Geçmiş yükleniyor...</span>
                      </div>
                    ) : incomingRequestsHistory.length === 0 ? (
                      <div className="no-transfers">
                        <Clock size={48} />
                        <p>Geçmiş kayıt bulunamadı</p>
                      </div>
                    ) : (
                      <div className="receive-transfer-content">
                        {/* Sol: Geçmiş Liste */}
                        <div className="transfers-list">
                          <h3>Geçmiş ({incomingRequestsHistory.length})</h3>
                          {incomingRequestsHistory.map(request => {
                            const statusLabels = { approved: 'Onaylandı', rejected: 'Reddedildi', completed: 'Tamamlandı', cancelled: 'İptal' };
                            return (
                              <div
                                key={request.id}
                                className={`transfer-card ${selectedHistoryIncomingRequest?.id === request.id ? 'selected' : ''}`}
                                onClick={() => setSelectedHistoryIncomingRequest(request)}
                              >
                                <div className="transfer-code">{request.request_code}</div>
                                <div className="transfer-from">Talep Eden: {request.from_store_name}{(request.requester_name || request.staff_name) ? ` (${request.requester_name || request.staff_name})` : ''}</div>
                                <div className="transfer-info">
                                  {request.item_count || '?'} ürün, {request.total_quantity || '?'} adet
                                </div>
                                <div className="transfer-date">
                                  {new Date(request.updated_at || request.created_at).toLocaleDateString('tr-TR')}
                                </div>
                                <span className={`status-badge ${request.status}`}>
                                  {statusLabels[request.status] || request.status}
                                </span>
                              </div>
                            );
                          })}
                        </div>

                        {/* Sağ: Seçili Geçmiş Detayı (read-only) */}
                        <div className="transfer-detail">
                          {selectedHistoryIncomingRequest ? (
                            <>
                              <div className="transfer-summary-card">
                                <div className="transfer-summary-top">
                                  <div className="detail-code">
                                    <span className="label">Talep Kodu</span>
                                    <span className="value">{selectedHistoryIncomingRequest.request_code}</span>
                                  </div>
                                  <span className={`status-text ${selectedHistoryIncomingRequest.status}`}>
                                    <span className="status-dot"></span>
                                    {selectedHistoryIncomingRequest.status === 'approved' && 'Onaylandı'}
                                    {selectedHistoryIncomingRequest.status === 'rejected' && 'Reddedildi'}
                                    {selectedHistoryIncomingRequest.status === 'completed' && 'Tamamlandı'}
                                    {selectedHistoryIncomingRequest.status === 'cancelled' && 'İptal'}
                                  </span>
                                </div>
                                <div className="transfer-summary-meta">
                                  <div className="meta-item">
                                    <span className="m-label">Talep Eden</span>
                                    <span className="m-value">{selectedHistoryIncomingRequest.from_store_name}{(selectedHistoryIncomingRequest.requester_name || selectedHistoryIncomingRequest.staff_name) ? ` (${selectedHistoryIncomingRequest.requester_name || selectedHistoryIncomingRequest.staff_name})` : ''}</span>
                                  </div>
                                  <div className="meta-item">
                                    <span className="m-label">Oluşturulma</span>
                                    <span className="m-value">{new Date(selectedHistoryIncomingRequest.created_at).toLocaleString('tr-TR')}</span>
                                  </div>
                                  {selectedHistoryIncomingRequest.notes && (
                                    <div className="meta-item meta-item-note">
                                      <span className="m-label">Not</span>
                                      <span className="m-value">{selectedHistoryIncomingRequest.notes}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                              {selectedHistoryIncomingRequest.status === 'rejected' && (
                                <div style={{ padding: '10px 14px', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '8px', fontSize: '0.85rem', color: '#991b1b', display: 'flex', flexDirection: 'column', gap: '4px', flexShrink: 0 }}>
                                  {selectedHistoryIncomingRequest.rejection_reason && (
                                    <div><strong>Red Sebebi:</strong> {selectedHistoryIncomingRequest.rejection_reason}</div>
                                  )}
                                  {(selectedHistoryIncomingRequest.rejected_staff_name || selectedHistoryIncomingRequest.rejected_by_name) && (
                                    <div>
                                      <strong>Reddeden:</strong> {selectedHistoryIncomingRequest.rejected_staff_name || selectedHistoryIncomingRequest.rejected_by_name}
                                    </div>
                                  )}
                                  {selectedHistoryIncomingRequest.rejected_at && (
                                    <div><strong>Red Tarihi:</strong> {new Date(selectedHistoryIncomingRequest.rejected_at).toLocaleString('tr-TR')}</div>
                                  )}
                                </div>
                              )}
                              {(selectedHistoryIncomingRequest.status === 'approved' || selectedHistoryIncomingRequest.status === 'completed') && (selectedHistoryIncomingRequest.approver_name || selectedHistoryIncomingRequest.approved_staff_name || selectedHistoryIncomingRequest.approved_by_name) && (
                                <div style={{ padding: '10px 14px', background: '#ecfdf5', border: '1px solid #86efac', borderRadius: '8px', fontSize: '0.85rem', color: '#065f46', flexShrink: 0 }}>
                                  <strong>Onaylayan:</strong> {selectedHistoryIncomingRequest.approver_name || selectedHistoryIncomingRequest.approved_staff_name || selectedHistoryIncomingRequest.approved_by_name || '—'}
                                  {selectedHistoryIncomingRequest.approved_at && ` (${new Date(selectedHistoryIncomingRequest.approved_at).toLocaleString('tr-TR')})`}
                                </div>
                              )}
                              <div className="transfer-detail-items">
                                <h4>Ürünler ({(selectedHistoryIncomingRequest.items || []).length})</h4>
                                <div className="detail-items-list">
                                  {(selectedHistoryIncomingRequest.items || []).map(item => (
                                    <div key={item.id} className="detail-item-row">
                                      <div className="detail-item-image" style={item.image ? { cursor: 'zoom-in' } : {}} onClick={() => item.image && openImagePopup(item.image, item.product_name)}>
                                        {item.image ? (
                                          <img src={`${IMAGE_HOST}${item.image}`} alt={item.product_name} />
                                        ) : (
                                          <div className="no-image-placeholder"><Package size={20} /></div>
                                        )}
                                      </div>
                                      <div className="detail-item-info">
                                        <span className="detail-item-name">{item.product_name}</span>
                                        <span className="detail-item-barcode">{item.barcode}</span>
                                      </div>
                                      <span className="detail-item-qty">
                                        {item.approved_quantity || 0} / {item.requested_quantity}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </>
                          ) : (
                            <div className="empty-detail">
                              <p>Detay için bir geçmiş kaydı seçin</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )
                  ) : loadingIncomingRequests ? (
                    <div className="loading-transfers">
                      <RefreshCw size={24} className="spin" />
                      <span>Talepler yükleniyor...</span>
                    </div>
                  ) : incomingRequests.length === 0 ? (
                    <div className="no-transfers">
                      <Package size={48} />
                      <p>Bekleyen transfer talebi bulunmuyor</p>
                    </div>
                  ) : (
                    <div className="receive-transfer-content">
                      {/* Sol: Talep Listesi */}
                      <div className="transfers-list">
                        <h3>Bekleyen Talepler</h3>
                        {incomingRequests.map(request => (
                          <div
                            key={request.id}
                            className={`transfer-card ${selectedIncomingRequestDetail?.id === request.id ? 'selected' : ''}`}
                            onClick={() => fetchIncomingRequestDetail(request)}
                          >
                            <div className="transfer-code">{request.request_code}</div>
                            <div className="transfer-from">Talep Eden: {request.from_store_name || request.to_store_name}{(request.requester_name || request.staff_name) ? ` (${request.requester_name || request.staff_name})` : ''}</div>
                            <div className="transfer-info">
                              {request.item_count || request.items?.length || '?'} ürün, {request.total_quantity || '?'} adet
                            </div>
                            <div className="transfer-date">
                              {new Date(request.created_at).toLocaleDateString('tr-TR')}
                            </div>
                            <span className="status-badge pending">Bekliyor</span>
                          </div>
                        ))}
                      </div>

                      {/* Sağ: Talep Detayı */}
                      <div className="transfer-detail">
                        {selectedIncomingRequestDetail ? (
                          <>
                            <div className="transfer-summary-card">
                              <div className="transfer-summary-top">
                                <div className="detail-code">
                                  <span className="label">Talep Kodu</span>
                                  <span className="value">{selectedIncomingRequestDetail.request_code}</span>
                                </div>
                                <span className="status-text pending">
                                  <span className="status-dot"></span>
                                  Bekliyor
                                </span>
                              </div>
                              <div className="transfer-summary-meta">
                                <div className="meta-item">
                                  <span className="m-label">Talep Eden</span>
                                  <span className="m-value">{selectedIncomingRequestDetail.from_store_name || selectedIncomingRequestDetail.to_store_name}{(selectedIncomingRequestDetail.requester_name || selectedIncomingRequestDetail.staff_name) ? ` (${selectedIncomingRequestDetail.requester_name || selectedIncomingRequestDetail.staff_name})` : ''}</span>
                                </div>
                                <div className="meta-item">
                                  <span className="m-label">Tarih</span>
                                  <span className="m-value">{new Date(selectedIncomingRequestDetail.created_at).toLocaleString('tr-TR')}</span>
                                </div>
                                {selectedIncomingRequestDetail.status === 'completed' && (selectedIncomingRequestDetail.approver_name || selectedIncomingRequestDetail.approved_staff_name || selectedIncomingRequestDetail.approved_by_name) && (
                                  <div className="meta-item">
                                    <span className="m-label">Onaylayan</span>
                                    <span className="m-value">{selectedIncomingRequestDetail.approver_name || selectedIncomingRequestDetail.approved_staff_name || selectedIncomingRequestDetail.approved_by_name}{selectedIncomingRequestDetail.approved_at ? ` (${new Date(selectedIncomingRequestDetail.approved_at).toLocaleString('tr-TR')})` : ''}</span>
                                  </div>
                                )}
                                {selectedIncomingRequestDetail.notes && (
                                  <div className="meta-item meta-item-note">
                                    <span className="m-label">Not</span>
                                    <span className="m-value">{selectedIncomingRequestDetail.notes}</span>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Barkod Okutma (form submit ile) */}
                            <form onSubmit={handleIncomingRequestBarcode} className="barcode-scan-form" style={{ marginBottom: '12px' }}>
                              <div className="barcode-input-wrapper">
                                <Search size={18} />
                                <input
                                  type="text"
                                  ref={incomingRequestBarcodeRef}
                                  value={incomingRequestBarcodeInput}
                                  onChange={(e) => setIncomingRequestBarcodeInput(e.target.value)}
                                  placeholder="Barkod okutun veya yazın..."
                                  autoFocus
                                />
                              </div>
                              <div className="scan-progress">
                                <span className="progress-text">
                                  Onaylanan: {incomingRequestDetailItems.reduce((sum, item) => sum + (item.approved_quantity || 0), 0)} / {incomingRequestDetailItems.reduce((sum, item) => sum + item.requested_quantity, 0)}
                                </span>
                              </div>
                            </form>

                            <div className="transfer-items-list">
                              <table className="transfer-table">
                                <thead>
                                  <tr>
                                    <th></th>
                                    <th style={{ width: '90px' }}>Fotoğraf</th>
                                    <th>Ürün</th>
                                    <th>Talep</th>
                                    <th>Stok</th>
                                    <th>Gönder</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {incomingRequestDetailItems.map((item, idx) => (
                                    <tr key={idx} className={item.approved_quantity >= item.requested_quantity ? 'received-complete' : item.approved_quantity > 0 ? 'received-partial' : ''}>
                                      <td className="status-icon-cell">
                                        {item.approved_quantity >= item.requested_quantity ? (
                                          <span className="check-icon">✓</span>
                                        ) : item.approved_quantity > 0 ? (
                                          <span className="partial-icon">◐</span>
                                        ) : (
                                          <span className="waiting-icon">○</span>
                                        )}
                                      </td>
                                      <td>
                                        <div className="product-image-small" style={item.image ? { cursor: 'zoom-in' } : {}} onClick={() => item.image && openImagePopup(item.image, item.product_name)}>
                                          {item.image ? (
                                            <img src={`${IMAGE_HOST}${item.image}`} alt={item.product_name} />
                                          ) : (
                                            <Package size={16} />
                                          )}
                                        </div>
                                      </td>
                                      <td>
                                        <div className="product-cell">
                                          <span className="product-name">{item.product_name}</span>
                                          <span className="product-barcode">{item.barcode}</span>
                                        </div>
                                      </td>
                                      <td>{item.requested_quantity}</td>
                                      <td>
                                        <span className={`stock-badge ${(item.available_stock || 0) >= item.requested_quantity ? 'sufficient' : 'insufficient'}`} style={{ padding: '2px 8px', borderRadius: '12px', fontSize: '0.85rem', fontWeight: 600, background: (item.available_stock || 0) >= item.requested_quantity ? '#dcfce7' : '#fef2f2', color: (item.available_stock || 0) >= item.requested_quantity ? '#166534' : '#dc2626' }}>
                                          {item.available_stock || 0}
                                        </span>
                                      </td>
                                      <td>
                                        <div className="quantity-control">
                                          <button onClick={() => updateIncomingRequestItemQuantity(idx, (item.approved_quantity || 0) - 1)}>−</button>
                                          <input
                                            type="number"
                                            value={item.approved_quantity || 0}
                                            onChange={(e) => updateIncomingRequestItemQuantity(idx, parseInt(e.target.value) || 0)}
                                            min="0"
                                            max={Math.min(item.requested_quantity, item.available_stock || 0)}
                                            className="qty-input"
                                          />
                                          <button onClick={() => updateIncomingRequestItemQuantity(idx, (item.approved_quantity || 0) + 1)}>+</button>
                                        </div>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>

                            <div className="incoming-request-actions" style={{ display: 'flex', gap: '10px', marginTop: '16px', flexShrink: 0 }}>
                              <button
                                className="transfer-cta-btn muted"
                                onClick={fillAllIncomingRequestApproval}
                              >
                                Tümünü Ekle
                              </button>
                              <button
                                className="transfer-cta-btn danger"
                                onClick={rejectIncomingRequest}
                                disabled={processingRequest}
                              >
                                Reddet
                              </button>
                              <button
                                className="transfer-cta-btn primary"
                                onClick={startApproveIncomingRequest}
                                disabled={processingRequest || incomingRequestDetailItems.every(item => !item.approved_quantity)}
                              >
                                Onayla ve Gönder ({incomingRequestDetailItems.reduce((sum, item) => sum + (item.approved_quantity || 0), 0)} adet)
                              </button>
                            </div>
                          </>
                        ) : (
                          <div className="empty-detail" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#94a3b8', fontSize: '0.95rem' }}>
                            <p>İşlem yapmak için sol taraftan bir talep seçin</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {transferMode === 'history' && (
                <div className="sent-transfers-view">
                  {loadingTransferHistory ? (
                    <div className="loading-transfers">
                      <RefreshCw size={24} className="spin" />
                      <span>Geçmiş yükleniyor...</span>
                    </div>
                  ) : selectedHistoryDetail ? (
                    <div className="transfer-detail-view">
                      <button
                        className="detail-back-btn"
                        onClick={() => setSelectedHistoryDetail(null)}
                      >
                        <ArrowLeft size={16} /> Listeye Dön
                      </button>
                      <div className="transfer-summary-card">
                        <div className="transfer-summary-top">
                          <div className="detail-code">
                            <span className="label">Transfer Kodu</span>
                            <span className="value">{selectedHistoryDetail.transfer_code}</span>
                          </div>
                          <span className={`status-text ${selectedHistoryDetail.status}`}>
                            <span className="status-dot"></span>
                            {selectedHistoryDetail.status === 'received' && 'Teslim Alındı'}
                            {selectedHistoryDetail.status === 'cancelled' && 'İptal'}
                          </span>
                        </div>
                        <div className="transfer-summary-meta">
                          <div className="meta-item">
                            <span className="m-label">Gönderen</span>
                            <span className="m-value">{selectedHistoryDetail.from_store_name}</span>
                          </div>
                          <div className="meta-item">
                            <span className="m-label">Hedef</span>
                            <span className="m-value">{selectedHistoryDetail.to_store_name}</span>
                          </div>
                          <div className="meta-item">
                            <span className="m-label">Gönderen Kasiyer</span>
                            <span className="m-value">{
                              (() => {
                                const m = selectedHistoryDetail.notes?.match(/Kasiyer:\s*(.+)/);
                                return m ? m[1].trim() : (selectedHistoryDetail.created_by_staff || selectedHistoryDetail.created_by_name || '-');
                              })()
                            }</span>
                          </div>
                          <div className="meta-item">
                            <span className="m-label">Gönderim Tarihi</span>
                            <span className="m-value">{new Date(selectedHistoryDetail.created_at).toLocaleString('tr-TR')}</span>
                          </div>
                          {selectedHistoryDetail.status === 'received' && (
                            <>
                              {(selectedHistoryDetail.received_by_staff || selectedHistoryDetail.received_by_name) && (
                                <div className="meta-item">
                                  <span className="m-label">Teslim Alan Kasiyer</span>
                                  <span className="m-value">{selectedHistoryDetail.received_by_staff || selectedHistoryDetail.received_by_name}</span>
                                </div>
                              )}
                              {selectedHistoryDetail.received_at && (
                                <div className="meta-item">
                                  <span className="m-label">Teslim Tarihi</span>
                                  <span className="m-value">{new Date(selectedHistoryDetail.received_at).toLocaleString('tr-TR')}</span>
                                </div>
                              )}
                            </>
                          )}
                          {selectedHistoryDetail.notes && (
                            <div className="meta-item meta-item-note">
                              <span className="m-label">Not</span>
                              <span className="m-value">{selectedHistoryDetail.notes}</span>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="transfer-detail-items">
                        <h4>Ürünler ({selectedHistoryDetail.items?.length || 0})</h4>
                        <div className="detail-items-list">
                          {selectedHistoryDetail.items?.map((item, idx) => (
                            <div key={idx} className="detail-item-row">
                              <div className="detail-item-image" style={item.image ? { cursor: 'zoom-in' } : {}} onClick={() => item.image && openImagePopup(item.image, item.product_name)}>
                                {item.image ? (
                                  <img src={`${IMAGE_HOST}${item.image}`} alt={item.product_name} />
                                ) : (
                                  <div className="no-image-placeholder"><Package size={20} /></div>
                                )}
                              </div>
                              <div className="detail-item-info">
                                <span className="detail-item-name">{item.product_name}</span>
                                <span className="detail-item-barcode">{item.barcode}</span>
                              </div>
                              <span className="detail-item-qty">{item.quantity} adet</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : transferHistory.length === 0 ? (
                    <div className="no-transfers">
                      <Package size={48} />
                      <p>Transfer geçmişi bulunmuyor</p>
                    </div>
                  ) : (
                    <div className="transfers-list">
                      {transferHistory.map(transfer => (
                        <div
                          key={transfer.id}
                          className="transfer-list-item"
                          onClick={() => setSelectedHistoryDetail(transfer)}
                        >
                          <div className="transfer-list-main">
                            <span className="transfer-code">{transfer.transfer_code}</span>
                            <span className="transfer-store">
                              {transfer.from_store_id === (selectedStore?.id || user?.store_id)
                                ? `→ ${transfer.to_store_name}`
                                : `← ${transfer.from_store_name}`
                              }
                            </span>
                          </div>
                          <div className="transfer-list-meta">
                            <span className="transfer-date">
                              {new Date(transfer.created_at).toLocaleDateString('tr-TR')}
                            </span>
                            <span className={`status-text ${transfer.status}`}>
                              <span className="status-dot"></span>
                              {transfer.status === 'received' && 'Teslim Alındı'}
                              {transfer.status === 'cancelled' && 'İptal'}
                            </span>
                          </div>
                          <div className="transfer-list-count">
                            {transfer.item_count || transfer.items?.length || '?'} ürün
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {transferMode === 'receive-transfer' && (
                <>
                  {loadingPendingTransfers ? (
                    <div className="loading-state">Yükleniyor...</div>
                  ) : pendingTransfers.length === 0 ? (
                    <div className="empty-state">
                      <Inbox size={48} />
                      <p>Bekleyen transfer bulunmuyor.</p>
                    </div>
                  ) : (
                    <>
                    <div className="receive-transfer-content">
                      {/* Sol: Transfer Listesi */}
                      <div className="transfers-list">
                        <h3>Bekleyen Transferler</h3>
                        <p style={{ fontSize: '13px', color: '#666', margin: '0 0 12px 0' }}>Teslim almak istediğiniz transferi seçin, ardından barkod okutun.</p>
                        {pendingTransfers.map(transfer => {
                          const receivedTotal = Number(transfer.received_total) || 0;
                          const totalQty = Number(transfer.total_quantity) || 0;
                          const remainingQty = totalQty - receivedTotal;
                          const isPartial = receivedTotal > 0;
                          return (
                          <div
                            key={transfer.id}
                            className={`transfer-card ${selectedTransferForReceive?.id === transfer.id ? 'selected' : ''}`}
                            onClick={() => selectTransferForReceive(transfer)}
                          >
                            <div className="transfer-code">{transfer.transfer_code}</div>
                            <div className="transfer-from">Gönderen: {transfer.from_store_name}</div>
                            {transfer.notes && (
                              <div style={{ fontSize: '12px', color: '#92400e', background: '#fffbeb', padding: '4px 8px', borderRadius: '4px', margin: '4px 0' }}>
                                {transfer.notes}
                              </div>
                            )}
                            <div className="transfer-info">
                              {transfer.item_count} ürün, {isPartial ? (
                                <span style={{ fontWeight: 'bold' }}>
                                  <span style={{ color: '#28a745' }}>{receivedTotal} teslim alındı</span>
                                  {' / '}
                                  <span style={{ color: '#dc3545' }}>{remainingQty} kalan</span>
                                  <span style={{ color: '#999', fontWeight: 'normal' }}> (toplam {totalQty})</span>
                                </span>
                              ) : (
                                <span>{totalQty} adet</span>
                              )}
                            </div>
                            {isPartial && (
                              <div style={{
                                width: '100%',
                                height: '8px',
                                background: '#e9ecef',
                                borderRadius: '4px',
                                margin: '8px 0',
                                overflow: 'hidden'
                              }}>
                                <div style={{
                                  width: `${Math.round((receivedTotal / totalQty) * 100)}%`,
                                  height: '100%',
                                  background: '#28a745',
                                  borderRadius: '4px',
                                  transition: 'width 0.3s ease'
                                }} />
                              </div>
                            )}
                            <div className="transfer-date">
                              {new Date(transfer.created_at).toLocaleDateString('tr-TR')}
                            </div>
                            <span className={`status-badge ${transfer.status}`}>
                              {transfer.status === 'pending' ? 'Bekliyor' : transfer.status === 'partially_received' ? 'Kısmi Teslim' : 'Yolda'}
                            </span>
                          </div>
                          );
                        })}
                      </div>

                      {/* Sağ: Transfer Detayı */}
                      <div className="transfer-detail">
                        {selectedTransferForReceive ? (
                          <>
                            <div className="transfer-summary-card">
                              <div className="transfer-summary-top">
                                <div className="detail-code">
                                  <span className="label">Transfer Kodu</span>
                                  <span className="value">{selectedTransferForReceive.transfer_code}</span>
                                </div>
                                <span className={`status-text ${selectedTransferForReceive.status}`}>
                                  <span className="status-dot"></span>
                                  {selectedTransferForReceive.status === 'pending' && 'Bekliyor'}
                                  {selectedTransferForReceive.status === 'partially_received' && 'Kısmi Teslim'}
                                  {selectedTransferForReceive.status === 'in_transit' && 'Yolda'}
                                </span>
                              </div>
                              <div className="transfer-summary-meta">
                                <div className="meta-item">
                                  <span className="m-label">Gönderen</span>
                                  <span className="m-value">{selectedTransferForReceive.from_store_name}</span>
                                </div>
                                <div className="meta-item">
                                  <span className="m-label">Gönderen Kasiyer</span>
                                  <span className="m-value">{
                                    (() => {
                                      const m = selectedTransferForReceive.notes?.match(/Kasiyer:\s*(.+)/);
                                      return m ? m[1].trim() : (selectedTransferForReceive.created_by_staff || selectedTransferForReceive.created_by_name || '-');
                                    })()
                                  }</span>
                                </div>
                                <div className="meta-item">
                                  <span className="m-label">Tarih</span>
                                  <span className="m-value">{new Date(selectedTransferForReceive.created_at).toLocaleString('tr-TR')}</span>
                                </div>
                                {selectedTransferForReceive.notes && (
                                  <div className="meta-item meta-item-note">
                                    <span className="m-label">Not</span>
                                    <span className="m-value">{selectedTransferForReceive.notes}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="receive-detail-split">
                              <div className="receive-detail-main">
                                <form onSubmit={handleReceiveBarcodeSubmit} className="receive-barcode-form" style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
                                  <span style={{ fontSize: '11px', color: '#64748b', whiteSpace: 'nowrap' }}>
                                    Seans: {transferDetailItems.reduce((sum, item) => sum + (item.received_quantity || 0), 0)} / Kalan: {transferDetailItems.reduce((sum, item) => sum + item.quantity - (item.already_received || 0), 0)}
                                    {transferDetailItems.some(item => (item.already_received || 0) > 0) && (
                                      <> (Önceki: {transferDetailItems.reduce((sum, item) => sum + (item.already_received || 0), 0)})</>
                                    )}
                                  </span>
                                  <div className="barcode-input-wrapper" style={{ flex: 1 }}>
                                    <Search size={16} />
                                    <input
                                      type="text"
                                      ref={receiveBarcodeInputRef}
                                      value={receiveBarcodeInput}
                                      onChange={(e) => setReceiveBarcodeInput(e.target.value)}
                                      placeholder="Barkod okutun..."
                                      autoFocus
                                    />
                                  </div>
                                </form>

                                {/* Eksi stoklu ürün uyarısı */}
                                {transferDetailItems.some(item => item.current_stock_at_destination < 0) && (
                                  <div className="negative-stock-warning">
                                    <AlertTriangle size={24} />
                                    <div>
                                      <strong>Dikkat: Eksi Stoklu Ürünler Var!</strong>
                                      <p>Aşağıdaki ürünler müşteriye satıldı ve stoklarınız eksiye düştü. Teslim alındığında stoklar otomatik sıfırlanacak.</p>
                                    </div>
                                  </div>
                                )}

                                <div className="transfer-items-list">
                                  <table className="transfer-table">
                                    <thead>
                                      <tr>
                                        <th></th>
                                        <th>Foto</th>
                                        <th>Ürün</th>
                                        <th>Barkod</th>
                                        <th>Mevcut Stok</th>
                                        <th>Gönderilen</th>
                                        <th>Önceki Teslim</th>
                                        <th>Kalan</th>
                                        <th>Bu Seans</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {transferDetailItems.map((item, index) => {
                                        const totalReceived = (item.already_received || 0) + (item.received_quantity || 0);
                                        const isComplete = totalReceived >= item.quantity;
                                        const isPartial = totalReceived > 0;
                                        return (
                                        <tr key={index} className={`${isComplete ? 'received-complete' : isPartial ? 'received-partial' : ''} ${item.current_stock_at_destination < 0 ? 'negative-stock-row' : ''}`}>
                                          <td className="status-icon-cell">
                                            {isComplete ? (
                                              <span className="check-icon">✓</span>
                                            ) : isPartial ? (
                                              <span className="partial-icon">◐</span>
                                            ) : (
                                              <span className="waiting-icon">○</span>
                                            )}
                                          </td>
                                          <td style={{ width: '56px', padding: '4px' }}>
                                            {item.image ? (
                                              <img
                                                src={`${IMAGE_HOST}${item.image}`}
                                                alt=""
                                                style={{ width: '52px', height: '52px', objectFit: 'cover', borderRadius: '6px', cursor: 'pointer' }}
                                                onClick={() => openImagePopup(item.image, item.product_name)}
                                              />
                                            ) : (
                                              <div style={{ width: '52px', height: '52px', borderRadius: '6px', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                <Package size={22} color="#94a3b8" />
                                              </div>
                                            )}
                                          </td>
                                          <td className="product-cell">
                                            <span>{item.product_name}</span>
                                            {item.current_stock_at_destination < 0 && (
                                              <span className="sold-badge">MÜŞTERİYE SATILDI</span>
                                            )}
                                          </td>
                                          <td>{item.barcode}</td>
                                          <td className={item.current_stock_at_destination < 0 ? 'negative-stock' : ''}>
                                            {item.current_stock_at_destination}
                                          </td>
                                          <td>{item.quantity}</td>
                                          <td style={{ color: (item.already_received || 0) > 0 ? '#28a745' : '#999', fontWeight: (item.already_received || 0) > 0 ? 'bold' : 'normal' }}>
                                            {item.already_received || 0}
                                          </td>
                                          <td style={{ color: (item.quantity - (item.already_received || 0)) > 0 ? '#dc3545' : '#28a745', fontWeight: 'bold' }}>
                                            {item.quantity - (item.already_received || 0)}
                                          </td>
                                          <td className="received-qty">
                                            <span className={isComplete ? 'complete' : isPartial ? 'partial' : ''} style={{ cursor: 'pointer' }} onDoubleClick={(e) => { const span = e.target; const remaining = item.quantity - (item.already_received || 0); const input = document.createElement('input'); input.type = 'number'; input.min = '0'; input.max = remaining; input.value = item.received_quantity || 0; input.style.cssText = 'width:50px;text-align:center;font-size:inherit;font-weight:inherit;border:2px solid #3b82f6;border-radius:4px;padding:2px;'; span.replaceWith(input); input.focus(); input.select(); const finish = () => { const val = Math.min(Math.max(0, parseInt(input.value) || 0), remaining); const updated = [...transferDetailItems]; updated[index] = { ...item, received_quantity: val }; setTransferDetailItems(updated); }; input.addEventListener('blur', finish); input.addEventListener('keydown', (ev) => { if (ev.key === 'Enter') input.blur(); }); }}>
                                              {item.received_quantity || 0}
                                            </span>
                                          </td>
                                        </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              </div>

                              {/* Sağ: Son okutulan ürünün fotoğrafı (Mağazaya Transfer Yap ekranındaki gibi) */}
                              <div className="receive-scan-panel">
                                {lastScannedTransferProduct ? (
                                  <>
                                    <div className="receive-scan-photo" style={lastScannedTransferProduct.image ? { cursor: 'zoom-in' } : {}} onClick={() => lastScannedTransferProduct.image && openImagePopup(lastScannedTransferProduct.image, lastScannedTransferProduct.name)}>
                                      {lastScannedTransferProduct.image ? (
                                        <img src={`${IMAGE_HOST}${lastScannedTransferProduct.image}`} alt={lastScannedTransferProduct.name} />
                                      ) : (
                                        <div className="receive-scan-placeholder"><Package size={48} /></div>
                                      )}
                                    </div>
                                    <div className="receive-scan-name">{lastScannedTransferProduct.name}</div>
                                    <div className="receive-scan-barcode">{lastScannedTransferProduct.barcode}</div>
                                  </>
                                ) : (
                                  <div className="receive-scan-empty">
                                    <Package size={40} />
                                    <span>Barkod okutun</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </>
                        ) : (
                          <div className="empty-detail">
                            <Inbox size={48} />
                            <p>Teslim almak için sol taraftan bir transfer seçin</p>
                          </div>
                        )}
                      </div>
                    </div>
                    {selectedTransferForReceive && (
                      <div className="receive-actions">
                        <button
                          className="transfer-cta-btn primary"
                          onClick={receiveTransfer}
                          disabled={transferDetailItems.reduce((sum, item) => sum + (item.received_quantity || 0), 0) === 0}
                        >
                          <Check size={14} /> Teslim Al ({transferDetailItems.reduce((sum, item) => sum + (item.received_quantity || 0), 0)})
                        </button>
                      </div>
                    )}
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Hurda Seçenek Modal */}
      {/* Tamirat Seçenek Modal */}
      {showRepairOptionsModal && (
        <div className={`modal-overlay ${closingModal === 'repairOptions' ? 'closing' : ''}`}>
          <div className="modal repair-options-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2><Wrench size={20} style={{ marginRight: '8px' }} /> Tamirat</h2>
              <button className="close-btn" onClick={() => handleCloseModal('repairOptions', setShowRepairOptionsModal)}>✕</button>
            </div>
            <div className="modal-body repair-options-body">
              <p className="repair-options-desc">Tamirat işlemi için bir seçenek belirleyin:</p>
              <div className="repair-options-buttons">
                <button
                  className="repair-option-btn go-repairs"
                  onClick={() => {
                    setShowRepairOptionsModal(false);
                    navigate('/repairs', { state: { from: 'retail-sales' } });
                  }}
                >
                  <span className="option-icon"><ClipboardList size={24} /></span>
                  <span className="option-title">Tamiratlar Sayfasına Git</span>
                  <span className="option-desc">Tüm tamirat kayıtlarını görüntüle ve yönet</span>
                </button>
                <button
                  className="repair-option-btn create-repair"
                  onClick={() => {
                    setShowRepairOptionsModal(false);
                    navigate('/repairs', {
                      state: {
                        from: 'retail-sales',
                        openCreateRepairModal: true,
                        prefillCustomer: selectedCustomer ? {
                          id: selectedCustomer.id,
                          name: selectedCustomer.name,
                          phone: selectedCustomer.phone || ''
                        } : null
                      }
                    });
                  }}
                >
                  <span className="option-icon"><Plus size={24} /></span>
                  <span className="option-title">Tamirat Oluştur</span>
                  <span className="option-desc">Yeni bir tamirat kaydı oluştur</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showScrapOptionsModal && (
        <div className={`modal-overlay ${closingModal === 'scrapOptions' ? 'closing' : ''}`}>
          <div className="modal scrap-options-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2><Scale size={20} style={{ marginRight: '8px' }} /> Hurda Alımı</h2>
              <button className="close-btn" onClick={() => handleCloseModal('scrapOptions', setShowScrapOptionsModal)}>✕</button>
            </div>
            <div className="modal-body scrap-options-body">
              <p className="scrap-options-desc">Hurda alımı için bir seçenek belirleyin:</p>
              <div className="scrap-options-buttons">
                <button
                  className="scrap-option-btn only-scrap"
                  onClick={() => {
                    setShowScrapOptionsModal(false);
                    navigate('/scrap-purchase', { state: { from: 'retail-sales' } });
                  }}
                >
                  <span className="option-icon"><ClipboardList size={20} /></span>
                  <span className="option-title">Yalnızca Hurda Al</span>
                  <span className="option-desc">Hurda alımı sayfasına git ve kayıt oluştur</span>
                </button>
                <button
                  className="scrap-option-btn scrap-with-sale"
                  onClick={() => {
                    if (selectedStaff.length === 0) {
                      setShowScrapOptionsModal(false);
                      toast.warning('Lütfen önce satış sorumlusu seçin!');
                      setShowStaffModal(true);
                      return;
                    }
                    setShowScrapOptionsModal(false);
                    // Mağazaya göre varsayılan metal tipini ve kuru ayarla
                    const metalType = isGoldStore ? 'gold' : 'silver';
                    // Manuel kur veya makas farkından dinamik hesaplanan alış fiyatı
                    const defaultPrice = metalType === 'gold'
                      ? getAlisRate('GOLD')
                      : getAlisRate('SILVER');
                    setScrapFormData(prev => ({
                      ...prev,
                      metal_type: metalType,
                      purity: isGoldStore ? '22' : '925',
                      gross_weight: '',
                      price_per_gram: defaultPrice > 0 ? defaultPrice.toString() : '',
                      waste_percent: 0,
                      waste_gram: '',
                      description: '',
                      bonus_percent: 0
                    }));
                    setCustomScrapTotal(null);
                    setShowScrapFormModal(true);
                  }}
                >
                  <span className="option-icon"><ShoppingCart size={20} /></span>
                  <span className="option-title">Hurda ile Satış Yap</span>
                  <span className="option-desc">Hurda tutarını bu satıştan düş</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Hurda Formu Modal */}
      {showScrapFormModal && (
        <div className={`modal-overlay ${closingModal === 'scrapForm' ? 'closing' : ''}`}>
          <div className="modal scrap-form-modal large" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2><Scale size={20} style={{ marginRight: '8px' }} /> Hurda ile Satış</h2>
              <button className="close-btn" onClick={() => handleCloseModal('scrapForm', setShowScrapFormModal, () => setScrapFormData(prev => ({...prev, custom_purity_rate: null})))}>✕</button>
            </div>
            <div className="modal-body scrap-form-body">
              {/* Metal Türü Seçimi (Gümüş mağazalarda Altın/Gümüş toggle) */}
              {!isGoldStore && (
                <div style={{ marginBottom: '12px' }}>
                  <label className="section-label">Metal Türü</label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      type="button"
                      onClick={() => {
                        const goldPrice = getAlisRate('GOLD');
                        setScrapFormData(prev => ({
                          ...prev,
                          metal_type: 'gold',
                          purity: '22',
                          custom_purity_rate: null,
                          price_per_gram: goldPrice > 0 ? goldPrice.toString() : ''
                        }));
                        setCustomScrapTotal(null);
                      }}
                      style={{
                        flex: 1, padding: '10px', borderRadius: '8px', border: '2px solid',
                        borderColor: scrapFormData.metal_type === 'gold' ? '#f59e0b' : '#d1d5db',
                        background: scrapFormData.metal_type === 'gold' ? 'linear-gradient(135deg, #fef3c7, #fde68a)' : '#f9fafb',
                        color: scrapFormData.metal_type === 'gold' ? '#92400e' : '#6b7280',
                        fontWeight: scrapFormData.metal_type === 'gold' ? '700' : '500',
                        cursor: 'pointer', fontSize: '0.9rem'
                      }}
                    >
                      🥇 Altın Hurdası
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        const silverPrice = getAlisRate('SILVER');
                        setScrapFormData(prev => ({
                          ...prev,
                          metal_type: 'silver',
                          purity: '925',
                          custom_purity_rate: null,
                          price_per_gram: silverPrice > 0 ? silverPrice.toString() : ''
                        }));
                        setCustomScrapTotal(null);
                      }}
                      style={{
                        flex: 1, padding: '10px', borderRadius: '8px', border: '2px solid',
                        borderColor: scrapFormData.metal_type === 'silver' ? '#9ca3af' : '#d1d5db',
                        background: scrapFormData.metal_type === 'silver' ? 'linear-gradient(135deg, #f3f4f6, #e5e7eb)' : '#f9fafb',
                        color: scrapFormData.metal_type === 'silver' ? '#374151' : '#6b7280',
                        fontWeight: scrapFormData.metal_type === 'silver' ? '700' : '500',
                        cursor: 'pointer', fontSize: '0.9rem'
                      }}
                    >
                      🥈 Gümüş Hurdası
                    </button>
                  </div>
                </div>
              )}

              {/* Hızlı Hurda Kartları (Altın seçili olduğunda göster) */}
              {(isGoldStore || scrapFormData.metal_type === 'gold') && (
                <div className="quick-scrap-section" style={{ marginBottom: '12px' }}>
                  <label className="section-label">Hızlı Ekle (Tıkla ekle)</label>
                  <div className="quick-scrap-cards" style={{ display: 'flex', gap: '6px' }}>
                    {quickScrapOptions.map(option => {
                      const cartItem = scrapCart.find(item => item.name === option.name && item.isQuickOption);
                      const quantity = cartItem?.quantity || 0;
                      return (
                        <button
                          key={option.id}
                          type="button"
                          className={`quick-scrap-card ${quantity > 0 ? 'in-cart' : ''}`}
                          onClick={() => handleQuickScrapSelect(option)}
                          style={{ padding: '6px 8px', gap: '2px', borderRadius: '6px' }}
                        >
                          <span className="card-name" style={{ fontSize: '0.7rem' }}>{option.name}</span>
                          <span className="card-weight" style={{ fontSize: '0.75rem' }}>{option.weight} gr</span>
                          {quantity > 0 && <span className="card-qty-badge" style={{ width: '16px', height: '16px', fontSize: '0.6rem', top: '-5px', right: '-5px' }}>{quantity}</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Manuel Hurda Ekleme */}
              <div className="manual-scrap-section">
                <label className="section-label">Manuel Ekle</label>
                <div className="scrap-form-grid-inline">
                  <div className="form-field ayar-field">
                    <label>Ayar</label>
                    <div className="ayar-select-wrapper">
                      <select
                        value={scrapFormData.purity}
                        onChange={(e) => {
                          setScrapFormData({...scrapFormData, purity: e.target.value, custom_purity_rate: null});
                          setIsEditingPurityRate(false);
                        }}
                      >
                        {scrapFormData.metal_type === 'gold' ? (
                          <>
                            <option value="24">24 Ayar</option>
                                                        <option value="22">22 Ayar</option>
                            <option value="21">21 Ayar</option>
                            <option value="18">18 Ayar</option>
                            <option value="14">14 Ayar</option>
                            <option value="10">10 Ayar</option>
                            <option value="9">9 Ayar</option>
                            <option value="8">8 Ayar</option>
                          </>
                        ) : (
                          <>
                            <option value="999">999 Ayar</option>
                            <option value="925">925 Ayar</option>
                            <option value="900">900 Ayar</option>
                            <option value="850">850 Ayar</option>
                            <option value="800">800 Ayar</option>
                            <option value="750">750 Ayar</option>
                            <option value="700">700 Ayar</option>
                          </>
                        )}
                      </select>
                      <span className="purity-rate-label">Oran:</span>
                      {isEditingPurityRate ? (
                        <input
                          type="number"
                          className="purity-rate-input"
                          value={editingPurityRateValue}
                          onChange={(e) => setEditingPurityRateValue(e.target.value)}
                          onBlur={() => {
                            updatePurityRate(scrapFormData.metal_type, scrapFormData.purity, editingPurityRateValue);
                            setIsEditingPurityRate(false);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              updatePurityRate(scrapFormData.metal_type, scrapFormData.purity, editingPurityRateValue);
                              setIsEditingPurityRate(false);
                            }
                            if (e.key === 'Escape') setIsEditingPurityRate(false);
                          }}
                          autoFocus
                        />
                      ) : (
                        <span
                          className="purity-rate-display"
                          onDoubleClick={() => {
                            const currentRate = scrapFormData.custom_purity_rate || scrapPurityRates[scrapFormData.metal_type]?.[scrapFormData.purity] || defaultScrapPurityRates[scrapFormData.metal_type]?.[scrapFormData.purity] || 0;
                            setEditingPurityRateValue(Math.round(currentRate * 1000).toString());
                            setIsEditingPurityRate(true);
                          }}
                          title="Çift tıkla düzenle"
                        >
                          {Math.round((scrapFormData.custom_purity_rate || scrapPurityRates[scrapFormData.metal_type]?.[scrapFormData.purity] || defaultScrapPurityRates[scrapFormData.metal_type]?.[scrapFormData.purity] || 0) * 1000)}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="form-field">
                    <label>Brüt Gram</label>
                    <input
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      value={scrapFormData.gross_weight}
                      onChange={(e) => setScrapFormData({...scrapFormData, gross_weight: e.target.value})}
                    />
                  </div>
                  <div className="form-field">
                    <label>Fire (gr)</label>
                    <input
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      value={scrapFormData.waste_gram || ''}
                      onChange={(e) => setScrapFormData({...scrapFormData, waste_gram: e.target.value})}
                    />
                  </div>
                  <div className="form-field">
                    <label>Net Gram</label>
                    <input
                      type="text"
                      readOnly
                      value={((parseFloat(scrapFormData.gross_weight) || 0) - (parseFloat(scrapFormData.waste_gram) || 0)).toFixed(2)}
                      style={{ backgroundColor: '#f0fdf4', fontWeight: '600', color: '#16a34a', cursor: 'default' }}
                    />
                  </div>
                  <div className="form-field">
                    <label>Has Gr Fiyatı (₺)</label>
                    <PriceInput
                      placeholder="0,00"
                      value={scrapFormData.price_per_gram}
                      onChange={(val) => setScrapFormData({...scrapFormData, price_per_gram: val})}
                    />
                  </div>
                  <div className="form-field wide">
                    <label>Açıklama</label>
                    <input
                      type="text"
                      placeholder="Opsiyonel"
                      value={scrapFormData.description}
                      onChange={(e) => setScrapFormData({...scrapFormData, description: e.target.value})}
                    />
                  </div>
                </div>

                {/* Anlık Hesaplama Önizlemesi + Buton */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  marginTop: '12px',
                  padding: '0 4px'
                }}>
                  {scrapFormData.gross_weight && scrapFormData.price_per_gram ? (() => {
                    const grossWeight = parseFloat(scrapFormData.gross_weight) || 0;
                    const wasteGram = parseFloat(scrapFormData.waste_gram) || 0;
                    const pricePerGram = parseFloat(scrapFormData.price_per_gram) || 0;
                    const netWeight = grossWeight - wasteGram;
                    // Önce custom_purity_rate'i kontrol et (tek seferlik), yoksa varsayılanı kullan
                    const purityRate = scrapFormData.custom_purity_rate || scrapPurityRates[scrapFormData.metal_type]?.[scrapFormData.purity] || defaultScrapPurityRates[scrapFormData.metal_type]?.[scrapFormData.purity] || 0;
                    const pureWeight = netWeight * purityRate;
                    const calculatedTotal = pureWeight * pricePerGram;
                    const total = customScrapTotal !== null ? customScrapTotal : calculatedTotal;

                    return (
                      <div style={{
                        flex: 1,
                        background: 'linear-gradient(135deg, #fef3c7, #fde68a)',
                        padding: '10px 14px',
                        borderRadius: '8px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: '12px'
                      }}>
                        <div style={{ display: 'flex', gap: '12px', fontSize: '0.8rem', color: '#92400e' }}>
                          <span>Brüt: <strong>{grossWeight.toFixed(2)}gr</strong></span>
                          {wasteGram > 0 && <span>Net: <strong>{netWeight.toFixed(2)}gr</strong></span>}
                          <span>Has: <strong>{pureWeight.toFixed(2)}gr</strong></span>
                        </div>
                        {isEditingScrapTotal ? (
                          <PriceInput
                            className="edit-total-input"
                            value={editingScrapTotalValue}
                            onChange={(val) => setEditingScrapTotalValue(val)}
                            onBlur={() => {
                              const newValue = parseFloat(editingScrapTotalValue);
                              if (!isNaN(newValue) && newValue >= 0) {
                                setCustomScrapTotal(newValue);
                              }
                              setIsEditingScrapTotal(false);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                const newValue = parseFloat(editingScrapTotalValue);
                                if (!isNaN(newValue) && newValue >= 0) {
                                  setCustomScrapTotal(newValue);
                                }
                                setIsEditingScrapTotal(false);
                              }
                              if (e.key === 'Escape') {
                                setIsEditingScrapTotal(false);
                                setCustomScrapTotal(null);
                              }
                            }}
                            autoFocus
                            style={{ width: '120px', textAlign: 'right' }}
                          />
                        ) : (
                          <div
                            className="editable-total"
                            onDoubleClick={() => {
                              setEditingScrapTotalValue(total.toFixed(2));
                              setIsEditingScrapTotal(true);
                            }}
                            title="Çift tıkla düzenle"
                            style={{ fontSize: '1.1rem', fontWeight: '700', color: '#78350f', cursor: 'pointer' }}
                          >
                            ₺{total.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            {customScrapTotal !== null && <span className="manual-badge">Manuel</span>}
                          </div>
                        )}
                      </div>
                    );
                  })() : <div style={{ flex: 1 }} />}

                  <button
                    type="button"
                    className="btn-add-scrap"
                    onClick={addScrapToCart}
                    disabled={!scrapFormData.gross_weight || !scrapFormData.price_per_gram}
                    style={{ flexShrink: 0 }}
                  >
                    <Plus size={16} /> Sepete Ekle
                  </button>
                </div>
              </div>

              {/* Hurda Sepeti */}
              <div className="scrap-cart-section">
                <div className="section-header">
                  <label className="section-label">Hurda Sepeti ({scrapCart.length} kalem)</label>
                  {scrapCart.length > 0 && (
                    <button className="btn-clear-cart" onClick={clearScrapCart}>
                      <Trash2 size={14} /> Temizle
                    </button>
                  )}
                </div>
                {scrapCart.length === 0 ? (
                  <div className="scrap-cart-empty">
                    <Scale size={32} style={{ opacity: 0.3 }} />
                    <span>Henüz hurda eklenmedi</span>
                  </div>
                ) : (
                  <div className="scrap-cart-list">
                    {scrapCart.map(item => (
                      <div key={item.id} className="scrap-cart-item">
                        <div className="scrap-item-info">
                          <span className="scrap-item-name">{item.name}</span>
                          <span className="scrap-item-details">
                            {item.purity} Ayar • Brüt: {item.gross_weight.toFixed(2)}gr{item.waste_gram > 0 ? ` • Fire: ${item.waste_gram.toFixed(2)}gr • Net: ${(item.gross_weight - item.waste_gram).toFixed(2)}gr` : ''} • Has: {item.pure_weight.toFixed(2)}gr • ₺{item.price_per_gram.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/gr
                          </span>
                        </div>
                        <div className="scrap-item-qty">
                          <button onClick={() => updateScrapQuantity(item.id, -1)} disabled={item.quantity <= 1}>-</button>
                          <span
                            className="scrap-qty-value"
                            title="Çift tıkla düzenle"
                            onDoubleClick={(e) => {
                              const span = e.currentTarget;
                              const input = document.createElement('input');
                              input.type = 'number';
                              input.min = '1';
                              input.value = item.quantity;
                              input.className = 'scrap-qty-input';
                              input.style.cssText = 'width:40px;text-align:center;font-size:inherit;font-weight:inherit;border:1px solid #3b82f6;border-radius:4px;padding:2px;outline:none;';
                              let committed = false;
                              const commit = () => {
                                if (committed) return;
                                committed = true;
                                const val = parseInt(input.value) || 1;
                                setScrapQuantity(item.id, val);
                                if (input.parentNode) input.parentNode.replaceChild(span, input);
                              };
                              input.onblur = commit;
                              input.onkeydown = (ev) => {
                                if (ev.key === 'Enter') { ev.preventDefault(); commit(); }
                                if (ev.key === 'Escape') { committed = true; if (input.parentNode) input.parentNode.replaceChild(span, input); }
                              };
                              span.parentNode.replaceChild(input, span);
                              input.focus();
                              input.select();
                            }}
                          >{item.quantity}</span>
                          <button onClick={() => updateScrapQuantity(item.id, 1)}>+</button>
                        </div>
                        <div className="scrap-item-total">
                          ₺{item.total.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                        <button className="scrap-item-remove" onClick={() => removeScrapFromCart(item.id)}>
                          <X size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Bonus ve Toplam */}
              {scrapCart.length > 0 && (
                <div className="scrap-summary-section">
                  <div className="scrap-bonus-row">
                    <label><Percent size={14} /> Yüzdelik (Tüm Hurdalar İçin)</label>
                    <div className="bonus-input-group">
                      <input
                        type="number"
                        step="1"
                        min="0"
                        max={(() => {
                          if (user?.role === 'admin' || selectedStaff.length === 0) return 100;
                          const limits = selectedStaff.map(s => s.max_scrap_bonus_percent).filter(v => v !== null && v !== undefined);
                          if (limits.length === 0) return 100;
                          return Math.max(...limits);
                        })()}
                        placeholder=""
                        value={scrapBonusPercent}
                        onChange={(e) => {
                          let val = parseFloat(e.target.value) || 0;
                          if (user?.role !== 'admin' && selectedStaff.length > 0) {
                            const limits = selectedStaff.map(s => s.max_scrap_bonus_percent).filter(v => v !== null && v !== undefined);
                            if (limits.length > 0) {
                              const maxLimit = Math.max(...limits);
                              if (val > maxLimit) {
                                toast.warning(`Hurda yüzdelik limiti %${maxLimit}'dir. Bu limiti aşamazsınız.`, { toastId: 'scrap-bonus-limit' });
                                val = maxLimit;
                              }
                            }
                          }
                          setScrapBonusPercent(val);
                        }}
                      />
                      <span>%</span>
                    </div>
                    <span className="bonus-amount">
                      +₺{(effectiveScrapTotal * scrapBonusPercent / 100).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                  <div className="scrap-total-row">
                    <span>Toplam Hurda Tutarı:</span>
                    {isEditingScrapTotal ? (
                      <PriceInput
                        className="edit-total-input"
                        value={editingScrapTotalValue}
                        onChange={(val) => setEditingScrapTotalValue(val)}
                        onBlur={() => {
                          const newValue = parseFloat(editingScrapTotalValue);
                          if (!isNaN(newValue) && newValue >= 0) {
                            setCustomScrapTotal(newValue);
                          }
                          setIsEditingScrapTotal(false);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            const newValue = parseFloat(editingScrapTotalValue);
                            if (!isNaN(newValue) && newValue >= 0) {
                              setCustomScrapTotal(newValue);
                            }
                            setIsEditingScrapTotal(false);
                          }
                          if (e.key === 'Escape') {
                            setIsEditingScrapTotal(false);
                            setCustomScrapTotal(null);
                          }
                        }}
                        autoFocus
                      />
                    ) : (
                      <span
                        className="scrap-total-amount editable"
                        onDoubleClick={() => {
                          setEditingScrapTotalValue(effectiveScrapTotal.toFixed(2));
                          setIsEditingScrapTotal(true);
                        }}
                        title="Çift tıkla düzenle"
                        style={{ cursor: 'pointer' }}
                      >
                        ₺{effectiveScrapTotal.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    )}
                  </div>
                  {scrapBonusPercent > 0 && (
                    <>
                      <div className="scrap-total-row" style={{ borderTop: '1px dashed #99f6e4', paddingTop: '6px', marginTop: '4px' }}>
                        <span style={{ fontWeight: 700, color: '#00897b' }}>Hurda + Yüzdelik Toplam:</span>
                        <span className="scrap-total-amount" style={{ fontWeight: 700 }}>
                          ₺{(effectiveScrapTotal + (effectiveScrapTotal * scrapBonusPercent / 100)).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </div>
                      <div className="scrap-bonus-info">
                        Sepet tutarı hurdayı aştığında +₺{(effectiveScrapTotal * scrapBonusPercent / 100).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} indirim uygulanacak
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => { setShowScrapFormModal(false); setScrapFormData(prev => ({...prev, custom_purity_rate: null})); }}>İptal</button>
              <button
                className="btn-save scrap-apply"
                onClick={() => setShowScrapFormModal(false)}
                disabled={scrapCart.length === 0}
              >
                <Scale size={16} /> Onayla ({scrapCart.length} kalem)
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tamirat Modal */}
      {showRepairModal && (
        <div className={`modal-overlay ${closingModal === 'repair' ? 'closing' : ''}`}>
          <div className="modal large repair-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2><Wrench size={20} style={{ marginRight: '8px' }} /> Yeni Tamirat Kaydı</h2>
              <button className="close-btn" onClick={() => handleCloseModal('repair', setShowRepairModal)}>✕</button>
            </div>
            <div className="modal-body" style={{ padding: '16px 20px' }}>
              {/* Müşteri Formu — Ad Soyad + Telefon (mevcut müşteri varsa telefondan öneri çıkar) */}
              <div style={{ marginBottom: '14px' }}>
                <div className="new-customer-form">
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
                    <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                      <input
                        type="text"
                        placeholder="Ad Soyad *"
                        value={repairFormData.customer_name}
                        onChange={(e) => setRepairFormData(prev => ({ ...prev, customer_name: e.target.value.toLocaleUpperCase('tr-TR') }))}
                        disabled={!!repairFormData.customer_id}
                      />
                    </div>
                    <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                      <input
                        type="tel"
                        placeholder="0 5XX XXX XX XX"
                        value={formatPhoneDisplay(repairFormData.customer_phone)}
                        onChange={(e) => {
                          const digits = normalizePhoneDigits(e.target.value);
                          setRepairFormData(prev => ({ ...prev, customer_phone: digits }));
                          if (!repairFormData.customer_id) searchRepairCustomerByPhone(digits);
                        }}
                        disabled={!!repairFormData.customer_id}
                      />
                    </div>
                    {repairFormData.customer_name && (
                      <button
                        className="save-new-customer-btn"
                        onClick={createRepairCustomer}
                        disabled={!!repairFormData.customer_id}
                        style={repairFormData.customer_id ? { opacity: 0.4, cursor: 'not-allowed', background: '#94a3b8' } : {}}
                      >
                        {repairFormData.customer_id ? '✓' : '✓ Kaydet'}
                      </button>
                    )}
                    {repairFormData.customer_id && (
                      <button
                        className="clear-customer-btn"
                        onClick={() => {
                          setRepairFormData(prev => ({ ...prev, customer_id: '', customer_name: '', customer_phone: '' }));
                          setRepairPhoneMatchCustomers([]);
                          setRepairCustomerSelectedFromMatch(false);
                        }}
                        title="Müşteriyi temizle"
                      >✕</button>
                    )}
                  </div>
                  {!repairFormData.customer_id && repairFormData.customer_name && (
                    <div style={{ marginTop: '4px', fontSize: '0.7rem', color: '#94a3b8', fontStyle: 'italic' }}>
                      Müşteri kaydederek KVKK onayı alınmış sayılır.
                    </div>
                  )}
                  {repairPhoneMatchCustomers.length > 0 && !repairFormData.customer_id && (
                    <div style={{ marginTop: '6px', background: '#fffbeb', border: '1px solid #fbbf24', borderRadius: '8px', overflow: 'hidden' }}>
                      <div style={{ padding: '6px 10px', fontSize: '0.75rem', fontWeight: 600, color: '#92400e', background: '#fef3c7' }}>
                        Bu numaraya kayıtlı müşteri bulundu:
                      </div>
                      {repairPhoneMatchCustomers.map(c => (
                        <div
                          key={c.id}
                          onClick={() => {
                            setRepairFormData(prev => ({ ...prev, customer_id: c.id, customer_name: c.name, customer_phone: c.phone || '' }));
                            setRepairPhoneMatchCustomers([]);
                            setRepairCustomerSelectedFromMatch(true);
                          }}
                          style={{ padding: '8px 10px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid #fde68a', fontSize: '0.8rem' }}
                          onMouseEnter={e => e.currentTarget.style.background = '#fef9c3'}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                        >
                          <span style={{ fontWeight: 600, color: '#1e293b' }}>{c.name}</span>
                          <span style={{ color: '#64748b' }}>{c.phone}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {repairFormData.customer_id && (
                    <div className="customer-saved-badge" style={{ marginTop: '6px' }}>
                      ✓ {repairCustomerSelectedFromMatch ? 'Müşteri seçildi' : 'Müşteri kaydedildi'}
                    </div>
                  )}
                </div>
              </div>

              {/* Ürünler - Kompakt Liste */}
              <div style={{ marginBottom: '14px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                  <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#475569' }}>Ürünler ({repairFormData.items.filter(i => i.item_description?.trim()).length || repairFormData.items.length})</span>
                  <span style={{ color: '#94a3b8', fontSize: '0.75rem' }}>Yeni ürün eklemek için Enter'a basın</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {repairFormData.items.map((item, itemIndex) => (
                    <div key={itemIndex} style={{ background: '#f8fafc', borderRadius: '8px', padding: '8px 10px', border: '1px solid #e2e8f0' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ color: '#94a3b8', fontWeight: 700, fontSize: '0.8rem', minWidth: '16px' }}>{itemIndex + 1}</span>
                        <input
                          type="text"
                          placeholder="Örn: 22 ayar bilezik - kaynak yapılacak"
                          value={item.item_description}
                          onChange={(e) => updateRepairItem(itemIndex, 'item_description', e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && item.item_description?.trim()) {
                              e.preventDefault();
                              if (itemIndex === repairFormData.items.length - 1) {
                                addRepairItem();
                                setTimeout(() => { const inputs = document.querySelectorAll('.repair-item-input-rs'); if (inputs.length > 0) inputs[inputs.length - 1].focus(); }, 50);
                              }
                            }
                          }}
                          className="repair-item-input-rs"
                          style={{ flex: 1, border: '1px solid #e2e8f0', borderRadius: '6px', padding: '8px 10px', fontSize: '0.9rem', outline: 'none' }}
                        />
                        <div style={{ display: 'flex', gap: '3px', flexShrink: 0 }}>
                          <input type="file" id={`repair-file-${itemIndex}`} accept="image/*" multiple onChange={(e) => handleRepairPhotoUpload(e, itemIndex)} style={{ display: 'none' }} />
                          <input type="file" id={`repair-camera-${itemIndex}`} accept="image/*" capture="environment" onChange={(e) => handleRepairPhotoUpload(e, itemIndex)} style={{ display: 'none' }} />
                          <button type="button" onClick={() => document.getElementById(`repair-camera-${itemIndex}`)?.click()} disabled={repairUploading} style={{ background: 'none', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '6px', cursor: 'pointer', color: '#64748b', display: 'flex', alignItems: 'center' }} title="Kamera"><Camera size={16} /></button>
                          <button type="button" onClick={() => document.getElementById(`repair-file-${itemIndex}`)?.click()} disabled={repairUploading} style={{ background: 'none', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '6px', cursor: 'pointer', color: '#64748b', display: 'flex', alignItems: 'center' }} title="Galeri"><Image size={16} /></button>
                          <button type="button" onClick={() => openRepairTerminalModal(itemIndex)} disabled={repairUploading || repairWaitingForPhoto} style={{ background: 'none', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '6px', cursor: 'pointer', color: '#64748b', display: 'flex', alignItems: 'center' }} title="Terminal"><Smartphone size={16} /></button>
                          {repairFormData.items.length > 1 && (
                            <button type="button" onClick={() => removeRepairItem(itemIndex)} style={{ background: 'none', border: '1px solid #fecaca', borderRadius: '6px', padding: '6px', cursor: 'pointer', color: '#ef4444', display: 'flex', alignItems: 'center' }} title="Sil"><Trash2 size={16} /></button>
                          )}
                        </div>
                      </div>
                      {repairUploading && <p className="uploading-text" style={{ margin: '4px 0 0 24px', fontSize: '0.75rem' }}>Yükleniyor...</p>}
                      {repairWaitingForPhoto && (
                        <div className="waiting-photo-indicator" style={{ margin: '4px 0 0 24px' }}>
                          <span className="spinner"></span>
                          <span>Terminal'den fotoğraf bekleniyor... ({selectedRepairTerminal?.name})</span>
                          <button type="button" className="cancel-wait-btn" onClick={cancelRepairPhotoWait}>İptal</button>
                        </div>
                      )}
                      {item.photos && item.photos.length > 0 && (
                        <div style={{ display: 'flex', gap: '6px', marginTop: '6px', marginLeft: '24px', flexWrap: 'wrap' }}>
                          {item.photos.map((photo, photoIndex) => (
                            <div key={photoIndex} style={{ position: 'relative', width: '48px', height: '48px', borderRadius: '6px', overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                              <img src={`${IMAGE_HOST}${photo}`} alt={`Fotoğraf ${photoIndex + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                              <button type="button" onClick={() => removeRepairPhoto(itemIndex, photoIndex)} style={{ position: 'absolute', top: '-1px', right: '-1px', background: '#ef4444', color: '#fff', border: 'none', borderRadius: '0 6px 0 6px', width: '18px', height: '18px', fontSize: '11px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>✕</button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Ücret, Tarihler - Kompakt Grid */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '14px' }}>
                {/* Ücret Tipi Checkboxlar */}
                <div style={{ gridColumn: '1 / -1', display: 'flex', gap: '8px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', padding: '6px 12px', borderRadius: '6px', border: repairFormData.price_type === 'free' ? '2px solid #10b981' : '1px solid #e2e8f0', background: repairFormData.price_type === 'free' ? '#ecfdf5' : '#fff', fontSize: '0.85rem' }}>
                    <input type="checkbox" checked={repairFormData.price_type === 'free'} onChange={(e) => setRepairFormData(prev => ({...prev, price_type: e.target.checked ? 'free' : 'normal', estimated_price: e.target.checked ? '' : prev.estimated_price}))} style={{ width: '16px', height: '16px', accentColor: '#10b981' }} />
                    <span style={{ fontWeight: 600, color: repairFormData.price_type === 'free' ? '#059669' : '#64748b' }}>Ücretsiz</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', padding: '6px 12px', borderRadius: '6px', border: repairFormData.price_type === 'pending' ? '2px solid #f59e0b' : '1px solid #e2e8f0', background: repairFormData.price_type === 'pending' ? '#fffbeb' : '#fff', fontSize: '0.85rem' }}>
                    <input type="checkbox" checked={repairFormData.price_type === 'pending'} onChange={(e) => setRepairFormData(prev => ({...prev, price_type: e.target.checked ? 'pending' : 'normal', estimated_price: e.target.checked ? '' : prev.estimated_price}))} style={{ width: '16px', height: '16px', accentColor: '#f59e0b' }} />
                    <span style={{ fontWeight: 600, color: repairFormData.price_type === 'pending' ? '#d97706' : '#64748b' }}>Hesaplanacak</span>
                  </label>
                </div>
                {/* Ücret ve Kapora */}
                {repairFormData.price_type === 'normal' && (
                  <>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label style={{ fontSize: '0.8rem', marginBottom: '4px' }}>Ücret (₺)</label>
                      <PriceInput placeholder="Ücret girin" value={repairFormData.estimated_price} onChange={(val) => setRepairFormData(prev => ({...prev, estimated_price: val}))} />
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label style={{ fontSize: '0.8rem', marginBottom: '4px' }}>Alınan Ücret (₺)</label>
                      {Number(repairFormData.deposit_amount) > 0 ? (
                        <div style={{ padding: '8px 10px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', minHeight: '38px' }}>
                          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px', flex: 1, minWidth: 0 }}>
                            <span style={{ fontWeight: 700, color: '#16a34a', fontSize: '0.95rem' }}>
                              ₺{Number(repairFormData.deposit_amount).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                            </span>
                            {(repairFormData.deposit_payment_details || []).map((p, idx) => (
                              <span key={idx} style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', padding: '1px 8px', background: '#fff', border: '1px solid #d1fae5', borderRadius: '12px', fontSize: '0.72rem', color: '#334155' }}>
                                <strong>{p.method}</strong>
                                {p.bank_account && <span style={{ color: '#6d28d9', fontSize: '0.68rem' }}>· {stripBankAccountType(p.bank_account)}</span>}
                                <span style={{ color: '#16a34a', fontWeight: 600 }}>₺{Number(p.amount).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</span>
                              </span>
                            ))}
                          </div>
                          <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
                            <button type="button" onClick={openDepositPaymentModal} style={{ padding: '4px 10px', background: '#fff', border: '1px solid #cbd5e1', borderRadius: '6px', cursor: 'pointer', fontSize: '0.75rem', color: '#475569' }}>Düzenle</button>
                            <button type="button" onClick={clearDepositPayment} style={{ padding: '4px 8px', background: '#fff', border: '1px solid #fecaca', borderRadius: '6px', cursor: 'pointer', fontSize: '0.75rem', color: '#dc2626' }} title="Alınan ücreti kaldır">✕</button>
                          </div>
                        </div>
                      ) : (
                        <button type="button" onClick={openDepositPaymentModal} style={{ padding: '8px 12px', background: '#f0fdf4', color: '#16a34a', border: '1px dashed #86efac', borderRadius: '8px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', minHeight: '38px' }}>
                          <Wallet size={16} /> Ücret Al
                        </button>
                      )}
                    </div>
                  </>
                )}
                {/* Tarihler */}
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label style={{ fontSize: '0.8rem', marginBottom: '4px' }}>Tahmini Teslim</label>
                  <input type="date" value={repairFormData.estimated_delivery} onChange={(e) => setRepairFormData(prev => ({...prev, estimated_delivery: e.target.value}))} />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label style={{ fontSize: '0.8rem', marginBottom: '4px' }}>En Geç Teslim</label>
                  <input type="date" value={repairFormData.deadline} onChange={(e) => setRepairFormData(prev => ({...prev, deadline: e.target.value}))} />
                </div>
              </div>

              {/* Kasiyer ve Notlar - Yan Yana */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label style={{ fontSize: '0.8rem', marginBottom: '4px' }}>Kasiyer</label>
                  <select value={repairFormData.staff_name} onChange={(e) => setRepairFormData(prev => ({...prev, staff_name: e.target.value}))}>
                    <option value="">Kasiyer Seç</option>
                    {salesStaffList.map(staff => (
                      <option key={staff.id} value={staff.name}>{staff.name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label style={{ fontSize: '0.8rem', marginBottom: '4px' }}>Notlar</label>
                  <input type="text" placeholder="Ek notlar..." value={repairFormData.notes} onChange={(e) => setRepairFormData(prev => ({...prev, notes: e.target.value}))} />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowRepairModal(false)}>İptal</button>
              <button className="btn-save" onClick={handleRepairSubmit}>
                <Wrench size={16} /> Tamirat Kaydı Oluştur
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tamirat "Ücret Al" (Kapora) Ödeme Modal */}
      {showRepairPaymentModal && (() => {
        const repairTotal = Number(repairFormData.estimated_price) || 0;
        const totalPaid = getRepairPaymentsTotal();
        const remaining = Math.max(0, repairTotal - totalPaid);
        const showInputs = remaining > 0.01;
        return (
        <div className="modal-overlay">
          <div className="modal payment-modal" onClick={e => e.stopPropagation()} style={{ maxHeight: '95vh', display: 'flex', flexDirection: 'column' }}>
            <div className="modal-header" style={{ padding: '10px 20px', flexShrink: 0 }}>
              <h2>Ücret Al</h2>
              <button className="close-btn" onClick={() => setShowRepairPaymentModal(false)}>✕</button>
            </div>
            <div className="modal-body" style={{ padding: '10px 20px', overflowY: 'auto', flex: 1 }}>
              <div className="payment-info" style={{ padding: '8px 12px', marginBottom: '8px' }}>
                {repairFormData.customer_name && (
                  <div className="payment-info-row" style={{ marginBottom: '2px' }}>
                    <span>Müşteri:</span>
                    <strong>{repairFormData.customer_name}</strong>
                  </div>
                )}
                <div className="payment-info-row" style={{ marginBottom: 0 }}>
                  <span>Tamirat Ücreti:</span>
                  <strong>₺{repairTotal.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</strong>
                </div>
              </div>

              {/* Alınan Ödemeler - kompakt chip */}
              {repairPayments.length > 0 && (
                <div style={{ marginBottom: '8px', display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center' }}>
                  {repairPayments.map(payment => (
                    <div key={payment.id} style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '3px 10px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '16px', fontSize: '0.78rem' }}>
                      <span style={{ fontWeight: 600, color: '#334155' }}>{payment.method}</span>
                      {payment.bankAccount && <span style={{ color: '#6d28d9', fontSize: '0.7rem' }}>{payment.bankAccount.account_name}</span>}
                      <span style={{ color: '#16a34a', fontWeight: 700 }}>₺{payment.amount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</span>
                      <button onClick={() => removeRepairPayment(payment.id)} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: '0 0 0 2px', fontSize: '13px', lineHeight: 1 }}>✕</button>
                    </div>
                  ))}
                  <span style={{ fontSize: '0.75rem', color: '#64748b', marginLeft: '4px' }}>
                    Toplam: <strong style={{ color: '#16a34a' }}>₺{totalPaid.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</strong>
                    {remaining > 0.01
                      ? <> · Kalan: <strong style={{ color: '#f59e0b' }}>₺{remaining.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</strong></>
                      : <> · <strong style={{ color: '#16a34a' }}>Tamamlandı</strong></>}
                  </span>
                </div>
              )}

              {showInputs && (
                <div className="form-group">
                  <label>Alınan Ücret (₺)</label>
                  <PriceInput
                    value={repairPaymentAmount}
                    onChange={(val) => setRepairPaymentAmount(val)}
                    placeholder="Tutar girin..."
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') addRepairPayment();
                      if (e.key === 'Tab' && !repairPaymentAmount) {
                        e.preventDefault();
                        if (remaining > 0) setRepairPaymentAmount(remaining.toFixed(2));
                      }
                    }}
                    autoFocus
                  />
                  <span style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>💡 Tab ile kalan tutarı doldur (en fazla ₺{remaining.toLocaleString('tr-TR', { minimumFractionDigits: 2 })})</span>
                </div>
              )}

              {showInputs && <div className="form-group">
                <label>Ödeme Yöntemi</label>
                <div className="payment-methods">
                  <button
                    className={`payment-method-btn ${repairPaymentMethod === 'Nakit' ? 'active' : ''}`}
                    onClick={() => {
                      const amt = parseFloat(repairPaymentAmount);
                      if (amt > 0) { addRepairPayment('Nakit'); }
                      else { setRepairPaymentMethod('Nakit'); }
                    }}
                  >
                    <Banknote size={16} /> Nakit
                  </button>
                  <button
                    className={`payment-method-btn ${repairPaymentMethod === 'Kredi Kartı' ? 'active' : ''}`}
                    onClick={() => {
                      const amt = parseFloat(repairPaymentAmount);
                      if (amt > 0) { addRepairPayment('Kredi Kartı'); }
                      else { setRepairPaymentMethod('Kredi Kartı'); }
                    }}
                  >
                    <CreditCard size={16} /> Kredi Kartı
                  </button>
                  <button
                    className={`payment-method-btn ${repairPaymentMethod === 'Havale/EFT' ? 'active' : ''}`}
                    onClick={() => setRepairPaymentMethod('Havale/EFT')}
                  >
                    <Building2 size={16} /> Havale/EFT
                  </button>
                  <button
                    className={`payment-method-btn ${repairPaymentMethod === 'Açık Hesap' ? 'active' : ''}`}
                    onClick={() => {
                      const amt = parseFloat(repairPaymentAmount);
                      if (amt > 0) { addRepairPayment('Açık Hesap'); }
                      else { setRepairPaymentMethod('Açık Hesap'); }
                    }}
                  >
                    <Wallet size={16} /> Açık Hesap
                  </button>
                </div>
              </div>}

              {/* Havale/EFT Banka Hesapları */}
              {repairPaymentMethod === 'Havale/EFT' && (
                <div className="form-group">
                  <label>Banka Hesabı Seçin</label>
                  <div className="collection-bank-accounts">
                    {repairBankAccounts.length === 0 ? (
                      <p className="no-accounts-text">Kayıtlı banka hesabı bulunamadı.</p>
                    ) : (
                      repairBankAccounts.map(account => (
                        <div
                          key={account.id}
                          className={`bank-account-option ${selectedRepairBankAccount?.id === account.id ? 'selected' : ''}`}
                          onClick={() => {
                            setSelectedRepairBankAccount(account);
                            const amt = parseFloat(repairPaymentAmount);
                            if (amt > 0) addRepairPayment('Havale/EFT', account);
                          }}
                        >
                          <span className="account-icon">
                            {account.account_type === 'company' ? '🏢' : '👤'}
                          </span>
                          <span className="account-name">{account.account_name}</span>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowRepairPaymentModal(false)}>İptal</button>
              <button
                className="btn-save"
                onClick={completeDepositPayment}
                disabled={totalPaid <= 0}
              >
                Ücreti Kaydet
              </button>
            </div>
          </div>
        </div>
        );
      })()}

      {/* Terminal Seçim Modal (Tamirat Fotoğraf) */}
      {showRepairTerminalModal && (
        <div className={`modal-overlay ${closingModal === 'repairTerminal' ? 'closing' : ''}`}>
          <div className="modal terminal-select-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2><Smartphone size={20} style={{ marginRight: '8px' }} /> Terminal Seç</h2>
              <button className="close-btn" onClick={() => setShowRepairTerminalModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <p className="terminal-modal-desc">
                Fotoğraf çekmek için bir terminal seçin. Seçilen terminale fotoğraf çekim isteği gönderilecek.
              </p>
              {loadingRepairTerminals ? (
                <div className="loading-state">Terminaller yükleniyor...</div>
              ) : repairTerminals.length === 0 ? (
                <div className="empty-state">
                  <span><Smartphone size={48} /></span>
                  <p>Bu mağazaya bağlı aktif terminal bulunamadı.</p>
                  <small>Terminal kullanıcısının sisteme giriş yapmış olması gerekir.</small>
                </div>
              ) : (
                <div className="terminal-list">
                  {repairTerminals.map(terminal => (
                    <div
                      key={terminal.id}
                      className="terminal-item"
                      onClick={() => sendRepairPhotoRequest(terminal)}
                    >
                      <div className="terminal-icon"><Smartphone size={24} /></div>
                      <div className="terminal-info">
                        <span className="terminal-name">{terminal.name}</span>
                        <span className="terminal-email">{terminal.email}</span>
                      </div>
                      <div className="terminal-arrow">→</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Banka Hesapları Modalı */}
      {showBankAccountsModal && (
        <div className="modal-overlay">
          <div className="modal bank-accounts-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Landmark size={20} /> Banka Hesapları</h2>
              <button className="close-btn" onClick={() => { setShowBankAccountsModal(false); resetBankAccountForm(); setShowAddBankAccountForm(false); setPendingHavalePayment(null); setPendingCollectionHavalePayment(null); }}>✕</button>
            </div>
            <div className="modal-body">
              <p className="bank-modal-info">Havale/EFT ödemesi için bir hesap seçin:</p>

              {bankAccounts.length === 0 && !showAddBankAccountForm ? (
                <div className="no-accounts">
                  <p>Henüz kayıtlı hesap bulunmuyor.</p>
                  {user?.role === 'admin' && (
                    <button
                      className="btn-add-account"
                      onClick={() => setShowAddBankAccountForm(true)}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}
                    >
                      <Plus size={16} /> Yeni Hesap Ekle
                    </button>
                  )}
                </div>
              ) : (
                <>
                  <div className="bank-accounts-list">
                    {bankAccounts.map(account => (
                      <div
                        key={account.id}
                        className={`bank-account-card ${selectedBankAccount?.id === account.id ? 'selected' : ''}`}
                        onClick={() => selectBankAccount(account)}
                      >
                        <div className="account-icon">
                          {account.account_type === 'company' ? <Building2 size={32} strokeWidth={1.75} /> :
                           account.account_type === 'ecommerce' ? <Globe size={32} strokeWidth={1.75} /> :
                           <User size={32} strokeWidth={1.75} />}
                        </div>
                        <div className="account-info">
                          <span className="account-name">{account.account_name}</span>
                          <span className="account-holder">{account.holder_name}</span>
                          {account.bank_name && <span className="bank-name">{account.bank_name}</span>}
                        </div>
                        <div className="account-actions">
                          {account.is_default === 1 && <span className="default-badge">Varsayılan</span>}
                          {user?.role === 'admin' && (
                            <button
                              className="edit-account-btn"
                              onClick={(e) => { e.stopPropagation(); startEditBankAccount(account); }}
                              title="Düzenle"
                            >
                              <Pencil size={14} />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {!showAddBankAccountForm && user?.role === 'admin' && (
                    <button
                      className="btn-add-account-small"
                      onClick={() => setShowAddBankAccountForm(true)}
                      style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                    >
                      <Plus size={16} /> Yeni Hesap Ekle
                    </button>
                  )}
                </>
              )}

              {/* Yeni Hesap Ekleme / Düzenleme Formu */}
              {showAddBankAccountForm && user?.role === 'admin' && (
                <div className="add-account-form">
                  <h3>{editingBankAccount ? 'Hesabı Düzenle' : 'Yeni Hesap Ekle'}</h3>
                  <div className="form-grid">
                    <div className="form-row">
                      <label>Hesap Adı *</label>
                      <input
                        type="text"
                        placeholder="Örn: Şirket Hesabı"
                        value={newBankAccount.account_name}
                        onChange={(e) => setNewBankAccount({...newBankAccount, account_name: e.target.value})}
                      />
                    </div>
                    <div className="form-row">
                      <label>Hesap Sahibi *</label>
                      <input
                        type="text"
                        placeholder="Ad Soyad"
                        value={newBankAccount.holder_name}
                        onChange={(e) => setNewBankAccount({...newBankAccount, holder_name: e.target.value})}
                      />
                    </div>
                    <div className="form-row">
                      <label>Banka Adı</label>
                      <input
                        type="text"
                        placeholder="Örn: Ziraat Bankası"
                        value={newBankAccount.bank_name}
                        onChange={(e) => setNewBankAccount({...newBankAccount, bank_name: e.target.value})}
                      />
                    </div>
                    <div className="form-row">
                      <label>Hesap Türü</label>
                      <select
                        value={newBankAccount.account_type}
                        onChange={(e) => setNewBankAccount({...newBankAccount, account_type: e.target.value})}
                      >
                        <option value="company">Şirket Hesabı</option>
                        <option value="personal">Şahsi Hesap</option>
                      </select>
                    </div>
                  </div>
                  <div className="form-row full-width">
                    <label>IBAN *</label>
                    <input
                      type="text"
                      placeholder="TR..."
                      value={newBankAccount.iban}
                      onChange={(e) => setNewBankAccount({...newBankAccount, iban: e.target.value.toUpperCase()})}
                    />
                  </div>
                  <div className="form-row full-width">
                    <label>QR Kod Görseli</label>
                    <div className="qr-upload-section">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleQRCodeUpload}
                        id="qr-upload-input"
                        className="qr-upload-input"
                      />
                      <label htmlFor="qr-upload-input" className="qr-upload-label">
                        📷 Görsel Seç
                      </label>
                      {newBankAccount.qr_code_image && (
                        <div className="qr-preview">
                          <img src={newBankAccount.qr_code_image} alt="QR Preview" />
                          <button
                            type="button"
                            className="remove-qr-btn"
                            onClick={() => setNewBankAccount({...newBankAccount, qr_code_image: ''})}
                          >
                            ✕
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="form-row full-width">
                    <label>Not</label>
                    <input
                      type="text"
                      placeholder="Opsiyonel not..."
                      value={newBankAccount.notes}
                      onChange={(e) => setNewBankAccount({...newBankAccount, notes: e.target.value})}
                    />
                  </div>
                  <div className="form-actions">
                    {editingBankAccount && (
                      <button
                        className="btn-delete"
                        onClick={() => deleteBankAccount(editingBankAccount.id)}
                      >
                        <Trash2 size={14} /> Sil
                      </button>
                    )}
                    <button className="btn-cancel" onClick={() => { resetBankAccountForm(); setShowAddBankAccountForm(false); }}>İptal</button>
                    <button
                      className="btn-save"
                      onClick={editingBankAccount ? updateBankAccount : addBankAccount}
                    >
                      {editingBankAccount ? 'Güncelle' : 'Kaydet'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Banka Hesabı Onay Modalı */}
      {showBankAccountDetails && selectedBankAccount && (
        <div className="modal-overlay">
          <div className="modal bank-account-details-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>🏦 Havale/EFT Onay</h2>
              <button className="close-btn" onClick={() => setShowBankAccountDetails(false)}>✕</button>
            </div>
            <div className="modal-body" style={{ textAlign: 'center', padding: '30px 20px' }}>
              <div style={{
                background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
                borderRadius: '16px',
                padding: '24px',
                marginBottom: '20px',
                color: '#fff'
              }}>
                <div style={{ fontSize: '0.9rem', opacity: 0.9, marginBottom: '8px' }}>Seçilen Hesap</div>
                <div style={{ fontSize: '1.4rem', fontWeight: '700' }}>{selectedBankAccount.account_name}</div>
              </div>

              <div style={{
                background: '#f8fafc',
                borderRadius: '12px',
                padding: '20px',
                border: '2px solid #e2e8f0'
              }}>
                <div style={{ fontSize: '0.9rem', color: '#64748b', marginBottom: '8px' }}>Ödenecek Tutar</div>
                <div style={{ fontSize: '2rem', fontWeight: '700', color: '#0f172a' }}>
                  ₺{(pendingHavalePayment?.amountTL || total).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowBankAccountDetails(false)}>İptal</button>
              <button className="btn-save bank-confirm" onClick={() => proceedWithBankTransfer()}>
                ✓ Ödemeyi Onayla
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Favori Ürünler Modalı */}
      {showFavoritesModal && (
        <div className="modal-overlay">
          <div className="modal favorites-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2><Star size={20} style={{ color: '#eab308', fill: '#eab308', marginRight: '8px' }} /> Favori Ürünler</h2>
              <button className="close-btn" onClick={() => setShowFavoritesModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              {loadingFavorites ? (
                <div className="loading-state">Yükleniyor...</div>
              ) : favoriteProducts.length === 0 ? (
                <div className="empty-state">
                  <span className="empty-icon"><Star size={48} style={{ color: '#eab308' }} /></span>
                  <p>Henüz favori ürün eklenmemiş</p>
                  <small>Ürün Yönetimi sayfasından ürünleri favorilere ekleyebilirsiniz</small>
                </div>
              ) : (
                <div className="favorites-grid">
                  {favoriteProducts.map(product => (
                    <div
                      key={product.id}
                      className="favorite-item"
                    >
                      <div className="favorite-image" onClick={() => {
                        addToCart(product);
                        toast.success(`${product.name} sepete eklendi`);
                        setShowFavoritesModal(false);
                      }} style={{ cursor: 'pointer' }}>
                        {product.image ? (
                          <img src={`${IMAGE_HOST}${product.image}`} alt={product.name} loading="lazy" />
                        ) : (
                          <span className="no-image">📷</span>
                        )}
                      </div>
                      <div className="favorite-info">
                        <span className="favorite-name">{product.name}</span>
                        <span className="favorite-price">
                          ₺{convertToTRY(Number(product.sell_price || 0), product.currency).toLocaleString('tr-TR', { maximumFractionDigits: 2 })}
                        </span>
                        <span className="favorite-stock">Stok: {product.stock || 0}</span>
                      </div>
                      <button
                        className={`favorite-add-cart-btn${cart.find(c => c.id === product.id) ? ' has-items' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          addToCart(product);
                          toast.success(`${product.name} sepete eklendi`);
                        }}
                        title="Sepete ekle"
                      >
                        <ShoppingCart size={16} />
                        {(() => {
                          const cartItem = cart.find(c => c.id === product.id);
                          const qty = cartItem ? cartItem.quantity : 0;
                          return qty > 0 ? <span className="favorite-cart-badge">{qty}</span> : null;
                        })()}
                      </button>
                      <button
                        className="remove-favorite-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleFavoriteFromSales(product);
                        }}
                        title="Favorilerden çıkar"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Sarf Malzemeleri Modal */}
      {showConsumablesModal && (
        <div className="modal-overlay">
          <div className="modal consumables-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                {consumableCategory ? (
                  <>
                    <button className="back-btn" onClick={() => { setConsumableCategory(null); setConsumableProducts([]); }}>
                      ← Geri
                    </button>
                    {consumableCategories.find(c => c.id === consumableCategory)?.name || 'Sarf Malzemeleri'}
                  </>
                ) : consumablesEditMode ? (
                  'Sarf Malzemeleri'
                ) : (
                  'Sarf Malzemesi Ekle'
                )}
              </h2>
              {selectedSaleForConsumable && (
                <span className="consumable-sale-info">Fiş #{selectedSaleForConsumable.receipt_number || selectedSaleForConsumable.store_sale_number || selectedSaleForConsumable.id}</span>
              )}
              <button className="close-btn" onClick={() => setShowConsumablesModal(false)}>✕</button>
            </div>
            <div className="modal-body consumables-body">
              <div className="consumables-content">
                {!consumableCategory ? (
                  // Kategori seçim ekranı
                  <div className="consumable-categories-grid">
                    {consumableCategories.map(cat => (
                      <div
                        key={cat.id}
                        className="consumable-category-card"
                        onClick={() => fetchConsumablesByCategory(cat.id)}
                      >
                        <span className="category-icon">{cat.icon}</span>
                        <span className="category-name">{cat.name}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  // Ürün listesi
                  <div className="consumable-products-grid">
                    {loadingConsumables ? (
                      <div className="loading-consumables">Yükleniyor...</div>
                    ) : consumableProducts.length === 0 ? (
                      <div className="no-consumables">
                        <p>Bu kategoride stokta ürün bulunmuyor</p>
                      </div>
                    ) : (
                      consumableProducts.map(product => {
                        const storeId = selectedStore?.id || user?.store_id || 1;
                        const stock = product.store_stocks?.find(s => s.store_id === storeId)?.stock || product.stock || 0;
                        const selectedQty = selectedConsumables[product.id]?.quantity || 0;
                        return (
                          <div
                            key={product.id}
                            className={`consumable-product-card ${selectedQty > 0 ? 'selected' : ''}`}
                          >
                            <div className="consumable-product-image">
                              {product.image ? (
                                <img src={`${IMAGE_HOST}${product.image}`} alt={product.name} loading="lazy" />
                              ) : (
                                <span className="no-image-icon">📦</span>
                              )}
                            </div>
                            <div className="consumable-product-info">
                              <span className="product-name">{product.name}</span>
                              <span className="product-stock">Stok: {stock}</span>
                            </div>
                            <div className="consumable-qty-controls">
                              {selectedQty > 0 ? (
                                <>
                                  <button
                                    className="qty-btn minus"
                                    onClick={() => decreaseConsumableQty(product.id)}
                                  >
                                    −
                                  </button>
                                  <span className="qty-value">{selectedQty}</span>
                                  <button
                                    className="qty-btn plus"
                                    onClick={() => increaseConsumableQty(product)}
                                    disabled={selectedQty >= stock}
                                  >
                                    +
                                  </button>
                                </>
                              ) : (
                                <button
                                  className="add-consumable-btn"
                                  onClick={() => increaseConsumableQty(product)}
                                >
                                  Ekle
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                )}
              </div>

              {/* Seçilen Sarf Malzemeleri Listesi */}
              {Object.keys(selectedConsumables).length > 0 && (
                <div className="selected-consumables-section">
                  <h4>Seçilen Malzemeler</h4>
                  <div className="selected-consumables-list">
                    {Object.values(selectedConsumables).map(({ product, quantity }) => (
                      <div key={product.id} className="selected-consumable-item">
                        <span className="item-name">{product.name}</span>
                        <div className="item-qty-controls">
                          <button
                            className="qty-btn mini minus"
                            onClick={() => decreaseConsumableQty(product.id)}
                          >
                            −
                          </button>
                          <span className="item-qty">{quantity}</span>
                          <button
                            className="qty-btn mini plus"
                            onClick={() => increaseConsumableQty(product)}
                          >
                            +
                          </button>
                        </div>
                        <button
                          className="remove-item-btn"
                          onClick={() => removeConsumable(product.id)}
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Footer - Kaydet Butonu */}
            {(Object.keys(selectedConsumables).length > 0 || consumablesEditMode) && (
              <div className="modal-footer consumables-footer">
                <div className="consumables-summary">
                  <span>{Object.keys(selectedConsumables).length} kalem</span>
                  <span className="total-qty">
                    Toplam: {Object.values(selectedConsumables).reduce((sum, item) => sum + item.quantity, 0)} adet
                  </span>
                </div>
                <button
                  className="save-consumables-btn"
                  onClick={saveConsumables}
                  disabled={savingConsumables}
                >
                  {savingConsumables ? 'Kaydediliyor...' : consumablesEditMode ? 'Güncelle' : 'Kaydet ve Stoktan Düş'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Log Görüntüleme Modal (Sadece Admin) */}
      {showPosLogsModal && user?.role === 'admin' && (
        <div className="modal-overlay">
          <div className="modal pos-logs-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '1200px', width: '95%', maxHeight: '90vh' }}>
            <div className="modal-header" style={{ background: 'linear-gradient(135deg, #dc2626 0%, #991b1b 100%)' }}>
              <h2 style={{ color: '#fff', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <FileText size={24} /> Log Kayıtları
              </h2>
              <button className="close-btn" onClick={() => setShowPosLogsModal(false)}>×</button>
            </div>
            <div className="modal-body" style={{ padding: '0', display: 'flex', flexDirection: 'column', height: 'calc(90vh - 60px)' }}>
              {/* Filtreler */}
              <div style={{ padding: '16px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                  <div style={{ flex: '1', minWidth: '150px' }}>
                    <label style={{ display: 'block', fontSize: '0.8rem', color: '#64748b', marginBottom: '4px' }}>İşlem Türü</label>
                    <select
                      value={posLogsFilter.action}
                      onChange={(e) => setPosLogsFilter({ ...posLogsFilter, action: e.target.value })}
                      style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.9rem' }}
                    >
                      <option value="">Tümü</option>
                      <option value="cart_add">Sepete Ekleme</option>
                      <option value="cart_remove">Sepetten Çıkarma</option>
                      <option value="cart_clear">Sepet Temizleme</option>
                      <option value="cart_price_edit">Fiyat Düzenleme</option>
                      <option value="sale_complete">Satış Tamamlandı</option>
                      <option value="sale_edit">Satış Düzenleme</option>
                      <option value="refund">İade</option>
                      <option value="exchange">Değişim</option>
                      <option value="apply_discount">İndirim Uygulama</option>
                      <option value="debt_sale">Açık Hesap Satışı</option>
                    </select>
                  </div>
                  <div style={{ flex: '1', minWidth: '150px' }}>
                    <label style={{ display: 'block', fontSize: '0.8rem', color: '#64748b', marginBottom: '4px' }}>Durum</label>
                    <select
                      value={posLogsFilter.status}
                      onChange={(e) => setPosLogsFilter({ ...posLogsFilter, status: e.target.value })}
                      style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.9rem' }}
                    >
                      <option value="">Tümü</option>
                      <option value="success">Başarılı</option>
                      <option value="warning">Uyarı</option>
                      <option value="error">Hata</option>
                      <option value="info">Bilgi</option>
                    </select>
                  </div>
                  <div style={{ flex: '1', minWidth: '150px' }}>
                    <label style={{ display: 'block', fontSize: '0.8rem', color: '#64748b', marginBottom: '4px' }}>Kullanıcı</label>
                    <select
                      value={posLogsFilter.user_id}
                      onChange={(e) => setPosLogsFilter({ ...posLogsFilter, user_id: e.target.value })}
                      style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.9rem' }}
                    >
                      <option value="">Tümü</option>
                      {posLogsUsers.map(u => (
                        <option key={u.id} value={u.id}>{u.name}</option>
                      ))}
                    </select>
                  </div>
                  <div style={{ flex: '1', minWidth: '140px' }}>
                    <label style={{ display: 'block', fontSize: '0.8rem', color: '#64748b', marginBottom: '4px' }}>Başlangıç</label>
                    <input
                      type="date"
                      value={posLogsFilter.start_date}
                      onChange={(e) => setPosLogsFilter({ ...posLogsFilter, start_date: e.target.value })}
                      style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.9rem' }}
                    />
                  </div>
                  <div style={{ flex: '1', minWidth: '140px' }}>
                    <label style={{ display: 'block', fontSize: '0.8rem', color: '#64748b', marginBottom: '4px' }}>Bitiş</label>
                    <input
                      type="date"
                      value={posLogsFilter.end_date}
                      onChange={(e) => setPosLogsFilter({ ...posLogsFilter, end_date: e.target.value })}
                      style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.9rem' }}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={handlePosLogsFilter}
                      style={{ padding: '8px 16px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
                    >
                      <Filter size={16} /> Filtrele
                    </button>
                    <button
                      onClick={clearPosLogsFilter}
                      style={{ padding: '8px 16px', background: '#64748b', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
                    >
                      Temizle
                    </button>
                    <button
                      onClick={() => fetchPosLogs(posLogsPage)}
                      style={{ padding: '8px 16px', background: '#0ea5e9', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
                    >
                      <RefreshCw size={16} /> Yenile
                    </button>
                  </div>
                </div>

                {/* İstatistikler */}
                {posLogsStats && (
                  <div style={{ display: 'flex', gap: '12px', marginTop: '16px', flexWrap: 'wrap' }}>
                    {posLogsStats.statusStats?.map(stat => (
                      <div key={stat.status} style={{
                        padding: '8px 16px',
                        background: getStatusColor(stat.status) + '15',
                        border: `1px solid ${getStatusColor(stat.status)}30`,
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <span style={{ color: getStatusColor(stat.status), fontWeight: '600' }}>{stat.count}</span>
                        <span style={{ color: '#64748b', fontSize: '0.85rem' }}>{getStatusLabel(stat.status)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Log Listesi */}
              <div style={{ flex: 1, overflow: 'auto', padding: '16px' }}>
                {posLogsLoading ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                    <RefreshCw size={32} style={{ animation: 'spin 1s linear infinite' }} />
                    <p>Yükleniyor...</p>
                  </div>
                ) : posLogs.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                    <FileText size={48} style={{ opacity: 0.5 }} />
                    <p>Kayıt bulunamadı</p>
                  </div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: '#f1f5f9' }}>
                        <th style={{ padding: '12px', textAlign: 'left', fontSize: '0.85rem', color: '#64748b', fontWeight: '600' }}>Tarih/Saat</th>
                        <th style={{ padding: '12px', textAlign: 'left', fontSize: '0.85rem', color: '#64748b', fontWeight: '600' }}>Kullanıcı</th>
                        <th style={{ padding: '12px', textAlign: 'left', fontSize: '0.85rem', color: '#64748b', fontWeight: '600' }}>Mağaza</th>
                        <th style={{ padding: '12px', textAlign: 'left', fontSize: '0.85rem', color: '#64748b', fontWeight: '600' }}>İşlem</th>
                        <th style={{ padding: '12px', textAlign: 'left', fontSize: '0.85rem', color: '#64748b', fontWeight: '600' }}>Açıklama</th>
                        <th style={{ padding: '12px', textAlign: 'center', fontSize: '0.85rem', color: '#64748b', fontWeight: '600' }}>Durum</th>
                        <th style={{ padding: '12px', textAlign: 'center', fontSize: '0.85rem', color: '#64748b', fontWeight: '600' }}>Detay</th>
                      </tr>
                    </thead>
                    <tbody>
                      {posLogs.map(log => (
                        <tr key={log.id} style={{ borderBottom: '1px solid #e2e8f0', background: log.status === 'warning' ? '#fef3c715' : log.status === 'error' ? '#fee2e215' : 'transparent' }}>
                          <td style={{ padding: '12px', fontSize: '0.85rem', color: '#475569', whiteSpace: 'nowrap' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <Clock size={14} style={{ color: '#94a3b8' }} />
                              {new Date(log.created_at).toLocaleString('tr-TR')}
                            </div>
                          </td>
                          <td style={{ padding: '12px', fontSize: '0.85rem' }}>
                            <div style={{ fontWeight: '500', color: '#1e293b' }}>{log.user_name || '-'}</div>
                            <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{log.user_role}</div>
                          </td>
                          <td style={{ padding: '12px', fontSize: '0.85rem' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <Store size={14} style={{ color: '#94a3b8' }} />
                              <span style={{ fontWeight: '500', color: '#1e293b' }}>{log.store_name || '-'}</span>
                            </div>
                          </td>
                          <td style={{ padding: '12px' }}>
                            <span style={{
                              display: 'inline-block',
                              padding: '4px 10px',
                              background: '#e2e8f0',
                              borderRadius: '4px',
                              fontSize: '0.8rem',
                              fontWeight: '500',
                              color: '#475569'
                            }}>
                              {getActionLabel(log.action)}
                            </span>
                          </td>
                          <td style={{ padding: '12px', fontSize: '0.85rem', color: '#475569', maxWidth: '300px' }}>
                            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {log.description}
                            </div>
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center' }}>
                            <span style={{
                              display: 'inline-block',
                              padding: '4px 10px',
                              background: getStatusColor(log.status) + '20',
                              color: getStatusColor(log.status),
                              borderRadius: '20px',
                              fontSize: '0.75rem',
                              fontWeight: '600'
                            }}>
                              {getStatusLabel(log.status)}
                            </span>
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center' }}>
                            <button
                              onClick={() => viewLogDetail(log)}
                              style={{
                                padding: '6px 12px',
                                background: '#f1f5f9',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                fontSize: '0.8rem',
                                color: '#475569'
                              }}
                            >
                              <Eye size={14} /> Detay
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Sayfalama */}
              {posLogsTotalPages > 1 && (
                <div style={{ padding: '16px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'center', gap: '8px' }}>
                  <button
                    onClick={() => fetchPosLogs(posLogsPage - 1)}
                    disabled={posLogsPage <= 1}
                    style={{ padding: '8px 16px', background: posLogsPage <= 1 ? '#e2e8f0' : '#dc2626', color: posLogsPage <= 1 ? '#94a3b8' : '#fff', border: 'none', borderRadius: '6px', cursor: posLogsPage <= 1 ? 'not-allowed' : 'pointer' }}
                  >
                    Önceki
                  </button>
                  <span style={{ padding: '8px 16px', color: '#64748b' }}>
                    Sayfa {posLogsPage} / {posLogsTotalPages}
                  </span>
                  <button
                    onClick={() => fetchPosLogs(posLogsPage + 1)}
                    disabled={posLogsPage >= posLogsTotalPages}
                    style={{ padding: '8px 16px', background: posLogsPage >= posLogsTotalPages ? '#e2e8f0' : '#dc2626', color: posLogsPage >= posLogsTotalPages ? '#94a3b8' : '#fff', border: 'none', borderRadius: '6px', cursor: posLogsPage >= posLogsTotalPages ? 'not-allowed' : 'pointer' }}
                  >
                    Sonraki
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Log Detay Modal */}
      {selectedLogDetail && (
        <div className="modal-overlay">
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header" style={{ background: getStatusColor(selectedLogDetail.status) }}>
              <h2 style={{ color: '#fff', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Eye size={20} /> Log Detayı
              </h2>
              <button className="close-btn" onClick={() => setSelectedLogDetail(null)}>×</button>
            </div>
            <div className="modal-body" style={{ padding: '20px' }}>
              <div style={{ display: 'grid', gap: '16px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.8rem', color: '#64748b', marginBottom: '4px' }}>Tarih/Saat</label>
                    <div style={{ fontWeight: '500' }}>{new Date(selectedLogDetail.created_at).toLocaleString('tr-TR')}</div>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.8rem', color: '#64748b', marginBottom: '4px' }}>Durum</label>
                    <span style={{
                      display: 'inline-block',
                      padding: '4px 12px',
                      background: getStatusColor(selectedLogDetail.status) + '20',
                      color: getStatusColor(selectedLogDetail.status),
                      borderRadius: '20px',
                      fontWeight: '600',
                      fontSize: '0.85rem'
                    }}>
                      {getStatusLabel(selectedLogDetail.status)}
                    </span>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.8rem', color: '#64748b', marginBottom: '4px' }}>Kullanıcı</label>
                    <div style={{ fontWeight: '500' }}>{selectedLogDetail.user_name || '-'}</div>
                    <div style={{ fontSize: '0.8rem', color: '#94a3b8' }}>{selectedLogDetail.user_role}</div>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.8rem', color: '#64748b', marginBottom: '4px' }}>Mağaza</label>
                    <div style={{ fontWeight: '500' }}>{selectedLogDetail.store_name || '-'}</div>
                  </div>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', color: '#64748b', marginBottom: '4px' }}>İşlem Türü</label>
                  <span style={{
                    display: 'inline-block',
                    padding: '6px 14px',
                    background: '#e2e8f0',
                    borderRadius: '6px',
                    fontWeight: '500'
                  }}>
                    {getActionLabel(selectedLogDetail.action)}
                  </span>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', color: '#64748b', marginBottom: '4px' }}>Açıklama</label>
                  <div style={{ padding: '12px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                    {selectedLogDetail.description}
                  </div>
                </div>
                {selectedLogDetail.details && (() => {
                  const details = typeof selectedLogDetail.details === 'string'
                    ? JSON.parse(selectedLogDetail.details)
                    : selectedLogDetail.details;
                  const data = details.details && typeof details.details === 'object' ? details.details : details;

                  const InfoRow = ({ label, value, bold, color }) => (
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
                      <span style={{ color: '#64748b', fontSize: '0.9rem' }}>{label}</span>
                      <span style={{ color: color || '#1e293b', fontSize: '0.9rem', fontWeight: bold ? '600' : '400' }}>{value}</span>
                    </div>
                  );

                  // Kur değişikliği logu
                  if (data.currency && (data.rate_alis !== undefined || data.rate_satis !== undefined || data.alis_percentage !== undefined)) {
                    return (
                      <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '16px', marginTop: '8px' }}>
                        <InfoRow label="Döviz" value={data.currency_label || data.currency} bold />
                        <InfoRow label="Mod" value={data.input_mode === 'percentage' ? 'Makas Farkı (%)' : 'Manuel Kur'} />
                        {data.input_mode === 'percentage' ? (
                          <>
                            {data.alis_percentage != null && <InfoRow label="Alış Makas Farkı" value={`%${data.alis_percentage}`} color="#f59e0b" />}
                            {data.satis_percentage != null && <InfoRow label="Satış Makas Farkı" value={`%${data.satis_percentage}`} color="#10b981" />}
                          </>
                        ) : (
                          <>
                            {data.rate_alis != null && <InfoRow label="Alış Kuru" value={`₺${Number(data.rate_alis).toLocaleString('tr-TR')}`} />}
                            {data.rate_satis != null && <InfoRow label="Satış Kuru" value={`₺${Number(data.rate_satis).toLocaleString('tr-TR')}`} />}
                          </>
                        )}
                      </div>
                    );
                  }

                  // Masraf logu
                  if (data.description && data.amount && data.category) {
                    return (
                      <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '16px', marginTop: '8px' }}>
                        <InfoRow label="Açıklama" value={data.description} bold />
                        <InfoRow label="Kategori" value={data.category} />
                        <InfoRow label="Ödeme Yöntemi" value={data.payment_method} />
                        {data.notes && <InfoRow label="Notlar" value={data.notes} />}
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '14px 0', marginTop: '4px', background: '#fef2f2', margin: '12px -16px -16px', padding: '14px 16px', borderRadius: '0 0 12px 12px' }}>
                          <span style={{ color: '#dc2626', fontSize: '1rem', fontWeight: '600' }}>TUTAR</span>
                          <span style={{ color: '#dc2626', fontSize: '1.1rem', fontWeight: '700' }}>₺{Number(data.amount).toLocaleString('tr-TR')}</span>
                        </div>
                      </div>
                    );
                  }

                  // Tahsilat logu
                  if (data.debt && data.payment) {
                    return (
                      <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '16px', marginTop: '8px' }}>
                        {data.customer && <InfoRow label="Müşteri" value={`${data.customer.name}${data.customer.phone ? ` (${data.customer.phone})` : ''}`} />}
                        <InfoRow label="Borç Açıklaması" value={data.debt.description} />
                        <InfoRow label="Borç Tipi" value={data.debt.debt_type === 'TL' ? 'TL Borcu' : data.debt.debt_type === 'GRAM' ? 'Altın Borcu' : 'Gümüş Borcu'} />
                        <InfoRow label="Orijinal Borç" value={`₺${Number(data.debt.original_amount).toLocaleString('tr-TR')}`} />
                        <InfoRow label="Ödeme Yöntemi" value={data.payment.payment_method} bold />
                        {data.payment.is_scrap_payment && data.payment.scrap_details && (
                          <InfoRow label="Hurda Detay" value={`${data.payment.scrap_details.metal_type === 'gold' ? 'Altın' : 'Gümüş'} ${data.payment.scrap_details.purity} Ayar - ${data.payment.scrap_details.pure_weight?.toFixed(2)} gr`} />
                        )}
                        <InfoRow label="Durum" value={data.result?.is_fully_paid ? 'Tamamen Ödendi' : `Kalan: ₺${Number(data.result?.remaining_amount || 0).toLocaleString('tr-TR')}`} color={data.result?.is_fully_paid ? '#10b981' : '#f59e0b'} />
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '14px 0', marginTop: '4px', background: '#dcfce7', margin: '12px -16px -16px', padding: '14px 16px', borderRadius: '0 0 12px 12px' }}>
                          <span style={{ color: '#16a34a', fontSize: '1rem', fontWeight: '600' }}>TAHSİLAT</span>
                          <span style={{ color: '#16a34a', fontSize: '1.1rem', fontWeight: '700' }}>₺{Number(data.payment.amount).toLocaleString('tr-TR')}</span>
                        </div>
                      </div>
                    );
                  }

                  // Satış düzenleme logu
                  if (selectedLogDetail.action === 'sale_edit' && data.items && data.items.some(i => i.price_changed || i.quantity_changed)) {
                    const changedItems = data.items.filter(i => i.price_changed || i.quantity_changed);
                    return (
                      <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '16px', marginTop: '8px' }}>
                        <div style={{ fontSize: '0.8rem', color: '#94a3b8', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Değişiklikler</div>
                        {changedItems.map((item, idx) => (
                          <div key={idx} style={{ padding: '10px 0', borderBottom: idx < changedItems.length - 1 ? '1px dashed #e2e8f0' : 'none' }}>
                            <div style={{ color: '#1e293b', fontSize: '0.9rem', fontWeight: '600', marginBottom: '6px' }}>{item.product_name || '-'}</div>
                            {item.price_changed && (
                              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', marginBottom: '3px' }}>
                                <span style={{ color: '#64748b' }}>Fiyat</span>
                                <span>
                                  <span style={{ color: '#dc2626', textDecoration: 'line-through', marginRight: '8px' }}>₺{Number(item.old_price).toLocaleString('tr-TR', {minimumFractionDigits: 2})}</span>
                                  <span style={{ color: '#16a34a', fontWeight: '600' }}>₺{Number(item.new_price).toLocaleString('tr-TR', {minimumFractionDigits: 2})}</span>
                                </span>
                              </div>
                            )}
                            {item.quantity_changed && (
                              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', marginBottom: '3px' }}>
                                <span style={{ color: '#64748b' }}>Adet</span>
                                <span>
                                  <span style={{ color: '#dc2626', textDecoration: 'line-through', marginRight: '8px' }}>{item.old_quantity}</span>
                                  <span style={{ color: '#16a34a', fontWeight: '600' }}>{item.new_quantity}</span>
                                </span>
                              </div>
                            )}
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
                              <span style={{ color: '#64748b' }}>Tutar</span>
                              <span>
                                <span style={{ color: '#dc2626', textDecoration: 'line-through', marginRight: '8px' }}>₺{Number(item.old_total).toLocaleString('tr-TR', {minimumFractionDigits: 2})}</span>
                                <span style={{ color: '#16a34a', fontWeight: '600' }}>₺{Number(item.new_total).toLocaleString('tr-TR', {minimumFractionDigits: 2})}</span>
                              </span>
                            </div>
                          </div>
                        ))}
                        {data.store_sale_number && <InfoRow label="Fiş No" value={`#${data.store_sale_number}`} />}
                        {data.customer && <InfoRow label="Müşteri" value={data.customer.name} />}
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '14px 0', marginTop: '4px', background: '#fef3c7', margin: '12px -16px -16px', padding: '14px 16px', borderRadius: '0 0 12px 12px' }}>
                          <span style={{ color: '#92400e', fontSize: '1rem', fontWeight: '600' }}>TOPLAM</span>
                          <span>
                            <span style={{ color: '#92400e', fontSize: '0.9rem', textDecoration: 'line-through', marginRight: '8px' }}>₺{Number(data.old_total).toLocaleString('tr-TR', {minimumFractionDigits: 2})}</span>
                            <span style={{ color: '#92400e', fontSize: '1.1rem', fontWeight: '700' }}>₺{Number(data.new_total).toLocaleString('tr-TR', {minimumFractionDigits: 2})}</span>
                          </span>
                        </div>
                      </div>
                    );
                  }

                  // Değişim/İade düzenleme logu
                  if (selectedLogDetail.action === 'sale_edit' && (data.return_items || data.new_items)) {
                    const renderChangeItems = (items, label, color) => {
                      const changed = (items || []).filter(i => i.price_changed || i.quantity_changed);
                      if (changed.length === 0) return null;
                      return (
                        <div style={{ marginBottom: '12px' }}>
                          <div style={{ fontSize: '0.8rem', color: color, marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600' }}>{label}</div>
                          {changed.map((item, idx) => (
                            <div key={idx} style={{ padding: '8px 0', borderBottom: idx < changed.length - 1 ? '1px dashed #e2e8f0' : 'none' }}>
                              <div style={{ color: '#1e293b', fontSize: '0.9rem', fontWeight: '600', marginBottom: '4px' }}>{item.product_name || '-'}</div>
                              {item.price_changed && (
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', marginBottom: '2px' }}>
                                  <span style={{ color: '#64748b' }}>Fiyat</span>
                                  <span>
                                    <span style={{ color: '#dc2626', textDecoration: 'line-through', marginRight: '8px' }}>₺{Number(item.old_price).toLocaleString('tr-TR', {minimumFractionDigits: 2})}</span>
                                    <span style={{ color: '#16a34a', fontWeight: '600' }}>₺{Number(item.new_price).toLocaleString('tr-TR', {minimumFractionDigits: 2})}</span>
                                  </span>
                                </div>
                              )}
                              {item.quantity_changed && (
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
                                  <span style={{ color: '#64748b' }}>Adet</span>
                                  <span>
                                    <span style={{ color: '#dc2626', textDecoration: 'line-through', marginRight: '8px' }}>{item.old_quantity}</span>
                                    <span style={{ color: '#16a34a', fontWeight: '600' }}>{item.new_quantity}</span>
                                  </span>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      );
                    };
                    return (
                      <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '16px', marginTop: '8px' }}>
                        <div style={{ fontSize: '0.8rem', color: '#94a3b8', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Değişiklikler</div>
                        {renderChangeItems(data.return_items, 'İade Edilen Ürünler', '#ef4444')}
                        {renderChangeItems(data.new_items, 'Yeni Alınan Ürünler', '#16a34a')}
                        {data.customer && <InfoRow label="Müşteri" value={data.customer.name} />}
                        {!(data.return_items || []).some(i => i.price_changed || i.quantity_changed) && !(data.new_items || []).some(i => i.price_changed || i.quantity_changed) && (
                          <div style={{ color: '#64748b', fontSize: '0.85rem', textAlign: 'center', padding: '12px 0' }}>Genel bilgiler güncellendi (ödeme yöntemi, müşteri, personel vb.)</div>
                        )}
                      </div>
                    );
                  }

                  // Satış logu (varsayılan)
                  return (
                    <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '16px', marginTop: '8px' }}>
                      {/* Ürünler */}
                      {data.items && data.items.length > 0 && (
                        <div style={{ marginBottom: '16px' }}>
                          <div style={{ fontSize: '0.8rem', color: '#94a3b8', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Ürünler</div>
                          {data.items.map((item, idx) => {
                            const qty = item.quantity || item.qty || 1;
                            const price = item.discounted_unit_price || item.unit_price || item.price || 0;
                            return (
                              <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: idx < data.items.length - 1 ? '1px dashed #e2e8f0' : 'none' }}>
                                <div style={{ flex: 1 }}>
                                  <div style={{ color: '#1e293b', fontSize: '0.9rem', fontWeight: '500' }}>{item.name || item.product_name || '-'}</div>
                                  <div style={{ color: '#94a3b8', fontSize: '0.8rem' }}>{qty} adet × ₺{Number(price).toLocaleString('tr-TR')}</div>
                                </div>
                                <div style={{ color: '#1e293b', fontSize: '0.95rem', fontWeight: '600' }}>₺{Number(price * qty).toLocaleString('tr-TR')}</div>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* Özet Bilgiler */}
                      <div style={{ borderTop: data.items ? '2px solid #e2e8f0' : 'none', paddingTop: data.items ? '12px' : '0' }}>
                        {(data.customer || data.customer_id) && (
                          <InfoRow label="Müşteri" value={data.customer ? `${data.customer.name}${data.customer.phone ? ` (${data.customer.phone})` : ''}` : `#${data.customer_id}`} />
                        )}
                        {data.staff && data.staff.length > 0 && (
                          <InfoRow label="Satış Sorumlusu" value={data.staff.map(s => s.name).join(', ')} />
                        )}
                        {data.store_sale_number && (
                          <InfoRow label="Fiş No" value={`#${data.store_sale_number}`} />
                        )}
                        {data.subtotal !== undefined && data.subtotal !== data.total && (
                          <InfoRow label="Ara Toplam" value={`₺${Number(data.subtotal).toLocaleString('tr-TR')}`} />
                        )}
                        {data.discount > 0 && (
                          <InfoRow label="İndirim" value={`-₺${Number(data.discount).toLocaleString('tr-TR')}`} color="#dc2626" />
                        )}
                        {data.campaign_discount > 0 && (
                          <InfoRow label="Kampanya İndirimi" value={`-₺${Number(data.campaign_discount).toLocaleString('tr-TR')}`} color="#dc2626" />
                        )}
                        {data.scrap_discount > 0 && (
                          <InfoRow label="Hurda İndirimi" value={`-₺${Number(data.scrap_discount).toLocaleString('tr-TR')}`} color="#dc2626" />
                        )}
                        {data.gift_card_discount > 0 && (
                          <InfoRow label="Bakiye" value={`-₺${Number(data.gift_card_discount).toLocaleString('tr-TR')}`} color="#dc2626" />
                        )}
                        {data.scrap_debt_discount > 0 && (
                          <InfoRow label="Hurda Alacağı" value={`-₺${Number(data.scrap_debt_discount).toLocaleString('tr-TR')}`} color="#dc2626" />
                        )}
                        {data.payment_method && (
                          <InfoRow label="Ödeme Yöntemi" value={data.payment_method} bold />
                        )}
                        {data.payment_details && data.payment_details.length > 1 && (
                          <div style={{ padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
                            <div style={{ color: '#64748b', fontSize: '0.85rem', marginBottom: '6px' }}>Ödeme Dağılımı</div>
                            {data.payment_details.map((p, i) => (
                              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', color: '#475569', marginLeft: '12px', marginTop: '4px' }}>
                                <span>{p.method}</span>
                                <span>{p.currency && p.currency !== 'TRY' ? `${p.amount} ${p.currency} = ` : ''}₺{Number(p.amount_tl || p.amount).toLocaleString('tr-TR')}</span>
                              </div>
                            ))}
                          </div>
                        )}
                        {(data.total || data.subtotal) && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '14px 0', marginTop: '4px', background: '#e0f2fe', margin: '12px -16px -16px', padding: '14px 16px', borderRadius: '0 0 12px 12px' }}>
                            <span style={{ color: '#0369a1', fontSize: '1rem', fontWeight: '600' }}>TOPLAM</span>
                            <span style={{ color: '#0369a1', fontSize: '1.1rem', fontWeight: '700' }}>₺{Number(data.total || data.subtotal || 0).toLocaleString('tr-TR')}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}
                {selectedLogDetail.ip_address && (
                  <div>
                    <label style={{ display: 'block', fontSize: '0.8rem', color: '#64748b', marginBottom: '4px' }}>IP Adresi</label>
                    <div style={{ fontFamily: 'monospace', color: '#64748b' }}>{selectedLogDetail.ip_address}</div>
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setSelectedLogDetail(null)}>Kapat</button>
            </div>
          </div>
        </div>
      )}

      {/* Nakit İade Onay Modalı */}
      {showCashRefundConfirmModal && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: '450px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header" style={{ background: 'linear-gradient(135deg, #22c55e, #16a34a)', color: 'white' }}>
              <h2><Banknote size={20} style={{ marginRight: '8px' }} /> Nakit İade Onayı</h2>
              <button className="close-btn" onClick={() => setShowCashRefundConfirmModal(false)} style={{ color: 'white' }}>✕</button>
            </div>
            <div className="modal-body" style={{ padding: '30px', textAlign: 'center' }}>
              <div style={{
                fontSize: '3rem',
                fontWeight: 'bold',
                color: '#16a34a',
                marginBottom: '20px',
                padding: '20px',
                background: '#f0fdf4',
                borderRadius: '12px'
              }}>
                ₺{Math.abs(total).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <p style={{ fontSize: '1.1rem', color: '#374151', marginBottom: '10px' }}>
                Müşteriye yukarıdaki tutarı <strong>NAKİT</strong> olarak vereceksiniz.
              </p>
              <p style={{ fontSize: '0.95rem', color: '#6b7280' }}>
                Bu işlemi onaylıyor musunuz?
              </p>
            </div>
            <div className="modal-footer" style={{ display: 'flex', gap: '10px', justifyContent: 'center', padding: '20px' }}>
              <button
                className="btn-cancel"
                onClick={() => setShowCashRefundConfirmModal(false)}
                style={{ flex: 1, padding: '12px 24px' }}
              >
                İptal
              </button>
              <button
                className="btn-save"
                onClick={() => {
                  setShowCashRefundConfirmModal(false);
                  handlePayment('Nakit İade');
                }}
                style={{
                  flex: 1,
                  padding: '12px 24px',
                  background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                  color: 'white',
                  fontWeight: 'bold'
                }}
              >
                Evet, Onaylıyorum
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Havale İade Onay Modalı */}
      {showTransferRefundConfirmModal && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: '450px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header" style={{ background: 'linear-gradient(135deg, #3b82f6, #2563eb)', color: 'white' }}>
              <h2><Building2 size={20} style={{ marginRight: '8px' }} /> Havale İade Onayı</h2>
              <button className="close-btn" onClick={() => setShowTransferRefundConfirmModal(false)} style={{ color: 'white' }}>✕</button>
            </div>
            <div className="modal-body" style={{ padding: '30px', textAlign: 'center' }}>
              <div style={{
                fontSize: '3rem',
                fontWeight: 'bold',
                color: '#2563eb',
                marginBottom: '20px',
                padding: '20px',
                background: '#eff6ff',
                borderRadius: '12px'
              }}>
                ₺{Math.abs(total).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <p style={{ fontSize: '1.1rem', color: '#374151', marginBottom: '10px' }}>
                Müşteriye yukarıdaki tutarı <strong>HAVALE/EFT</strong> olarak vereceksiniz.
              </p>
              <p style={{ fontSize: '0.95rem', color: '#6b7280' }}>
                Bu işlemi onaylıyor musunuz?
              </p>
            </div>
            <div className="modal-footer" style={{ display: 'flex', gap: '10px', justifyContent: 'center', padding: '20px' }}>
              <button
                className="btn-cancel"
                onClick={() => setShowTransferRefundConfirmModal(false)}
                style={{ flex: 1, padding: '12px 24px' }}
              >
                İptal
              </button>
              <button
                className="btn-save"
                onClick={() => {
                  setShowTransferRefundConfirmModal(false);
                  handlePayment('Havale İade');
                }}
                style={{
                  flex: 1,
                  padding: '12px 24px',
                  background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                  color: 'white',
                  fontWeight: 'bold'
                }}
              >
                Evet, Onaylıyorum
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hurda Borca Al Seçenek Modal (Gram/Nakit) */}
      {showScrapDebtTypeModal && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: '500px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header" style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)', color: 'white' }}>
              <h2><Coins size={20} style={{ marginRight: '8px' }} /> Borca Al - Tür Seçimi</h2>
              <button className="close-btn" onClick={() => setShowScrapDebtTypeModal(false)} style={{ color: 'white' }}>✕</button>
            </div>
            <div className="modal-body" style={{ padding: '24px' }}>
              <div style={{
                fontSize: '2.5rem',
                fontWeight: 'bold',
                color: '#b45309',
                marginBottom: '20px',
                padding: '20px',
                background: '#fffbeb',
                borderRadius: '12px',
                textAlign: 'center'
              }}>
                ₺{Math.abs(total).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <p style={{ textAlign: 'center', color: '#64748b', marginBottom: '20px' }}>
                Müşterinin alacağını hangi türde kaydetmek istersiniz?
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {/* Gram Seçeneği - Mağaza tipine göre Altın veya Gümüş */}
                <button
                  onClick={() => {
                    setShowScrapDebtTypeModal(false);
                    handleRefundAsScrapDebt(isGoldStore ? 'GRAM' : 'GUMUS');
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    padding: '18px 20px',
                    border: '2px solid #e2e8f0',
                    borderRadius: '12px',
                    background: 'white',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  className="scrap-balance-option-btn"
                >
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '12px',
                    background: isGoldStore
                      ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
                      : 'linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Scale size={24} color="white" />
                  </div>
                  <div style={{ textAlign: 'left', flex: 1 }}>
                    <div style={{ fontWeight: '600', color: '#1e293b', fontSize: '1rem' }}>
                      {isGoldStore ? 'Gram Altın' : 'Gram Gümüş'}
                    </div>
                    <div style={{ color: '#64748b', fontSize: '0.85rem' }}>
                      {(() => {
                        const rate = isGoldStore ? getSatisRate('GOLD') : getSatisRate('SILVER');
                        const gram = rate > 0 ? (Math.abs(total) / rate).toFixed(4) : '0';
                        return `≈ ${gram} gr ${isGoldStore ? 'Altın' : 'Gümüş'} (Kur: ₺${rate.toLocaleString('tr-TR')})`;
                      })()}
                    </div>
                  </div>
                  <ArrowRight size={20} color="#94a3b8" />
                </button>

                {/* Nakit (TL) Seçeneği */}
                <button
                  onClick={() => {
                    setShowScrapDebtTypeModal(false);
                    handleRefundAsScrapDebt('TL');
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    padding: '18px 20px',
                    border: '2px solid #e2e8f0',
                    borderRadius: '12px',
                    background: 'white',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  className="scrap-balance-option-btn"
                >
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '12px',
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Banknote size={24} color="white" />
                  </div>
                  <div style={{ textAlign: 'left', flex: 1 }}>
                    <div style={{ fontWeight: '600', color: '#1e293b', fontSize: '1rem' }}>Nakit (TL)</div>
                    <div style={{ color: '#64748b', fontSize: '0.85rem' }}>
                      ₺{Math.abs(total).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} TL olarak kaydet
                    </div>
                  </div>
                  <ArrowRight size={20} color="#94a3b8" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Hızlı Transfer Teslim Özet Modal */}
      {showQuickTransferSummary && quickTransferSummaryData && (
        <div className="modal-overlay" style={{ zIndex: 1100 }}>
          <div className="modal quick-transfer-summary-modal large-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2><Package size={20} style={{ marginRight: '8px' }} /> Teslim Alma Özeti</h2>
              <button className="close-btn" onClick={() => setShowQuickTransferSummary(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="summary-content">
                {quickTransferSummaryData.map((group, idx) => (
                  <div key={idx} className="transfer-group">
                    <div className="transfer-group-header">
                      <div className="transfer-info">
                        <span className="transfer-code" style={{ color: 'white' }}>{group.transfer.code}</span>
                        <span className="transfer-from" style={{ color: 'white', marginLeft: '12px' }}>Gönderen: {group.transfer.from_store}</span>
                      </div>
                    </div>

                    <div className="scanned-items">
                      <h4 className="section-title received">✓ Teslim Alınacak Ürünler ({group.scannedItems.length})</h4>
                      <table className="summary-table">
                        <thead>
                          <tr>
                            <th>Ürün</th>
                            <th>Barkod</th>
                            <th>Miktar</th>
                          </tr>
                        </thead>
                        <tbody>
                          {group.scannedItems.map((item, itemIdx) => (
                            <tr key={itemIdx}>
                              <td className="product-cell">
                                {item.product.image && (
                                  <img src={`${IMAGE_HOST}${item.product.image}`} alt="" className="product-thumb" onClick={() => openImagePopup(item.product.image, item.product.name)} style={{ cursor: 'zoom-in' }} />
                                )}
                                <span>{item.product.name}</span>
                              </td>
                              <td>{item.product.barcode}</td>
                              <td className="qty-cell">{item.scannedQty} / {item.expectedQty}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {group.missingItems && group.missingItems.length > 0 && (
                      <div className="missing-items">
                        <h4 className="section-title missing">⚠ Bu Fişten Okutulmayan Ürünler ({group.missingItems.length})</h4>
                        <table className="summary-table missing-table">
                          <thead>
                            <tr>
                              <th>Ürün</th>
                              <th>Barkod</th>
                              <th>Kalan</th>
                            </tr>
                          </thead>
                          <tbody>
                            {group.missingItems.map((item, itemIdx) => (
                              <tr key={itemIdx}>
                                <td className="product-cell">
                                  {item.image && (
                                    <img src={`${IMAGE_HOST}${item.image}`} alt="" className="product-thumb" onClick={() => openImagePopup(item.image, item.product_name)} style={{ cursor: 'zoom-in' }} />
                                  )}
                                  <span>{item.product_name}</span>
                                </td>
                                <td>{item.barcode}</td>
                                <td className="qty-cell missing">{item.expectedQty}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div className="summary-totals">
                <div className="total-item">
                  <span className="label">Toplam Teslim Alınacak:</span>
                  <span className="value">{quickTransferSummaryData.reduce((sum, g) => sum + g.scannedItems.reduce((s, i) => s + i.scannedQty, 0), 0)} adet</span>
                </div>
                <div className="total-item missing">
                  <span className="label">Toplam Okutulmayan:</span>
                  <span className="value">{quickTransferSummaryData.reduce((sum, g) => sum + (g.missingItems?.reduce((s, i) => s + i.expectedQty, 0) || 0), 0)} adet</span>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn secondary" onClick={() => setShowQuickTransferSummary(false)}>Geri Dön</button>
              <button
                className="btn primary"
                onClick={confirmQuickTransfer}
                disabled={savingQuickTransfer}
                style={{ color: 'white' }}
              >
                {savingQuickTransfer ? 'Kaydediliyor...' : 'Onayla ve Teslim Al'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Teslim Alan Kasiyer Seçim Modalı */}
      {showReceiveStaffModal && (
        <div className="modal-overlay" style={{ zIndex: 10001 }}>
          <div className="modal staff-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Teslim Alan Kasiyer</h2>
              <button className="close-btn" onClick={() => setShowReceiveStaffModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="staff-list">
                {salesStaffList.map(staff => {
                  const isSelected = receiveStaffId === staff.id;
                  return (
                    <div key={staff.id} className={`staff-item ${isSelected ? 'selected' : ''}`} onClick={() => { setReceiveStaffId(staff.id); setReceiveStaffName(staff.name); }}>
                      <div className={`staff-avatar ${staff.photo_url ? 'has-photo' : ''}`}>
                        {isSelected ? <Check size={24} strokeWidth={3} /> : staff.photo_url ? <img src={`${IMAGE_HOST}${staff.photo_url}`} alt={staff.name} style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} /> : staff.name.charAt(0)}
                      </div>
                      <div className="staff-info"><span className="staff-name">{staff.name}</span></div>
                    </div>
                  );
                })}
              </div>
              <div className="staff-modal-actions">
                <button className="confirm-staff-btn" disabled={!receiveStaffId} onClick={handleReceiveStaffConfirm}>Teslim Al</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Ürün Detay Modalı */}
      {showProductDetailModal && (selectedPreviewItem || cart[cart.length - 1]) && (() => {
        const detailItem = selectedPreviewItem || cart[cart.length - 1];
        return (
          <div
            className={`modal-overlay ${closingModal === 'productDetail' ? 'closing' : ''}`}
            style={{
              zIndex: 9999
            }}
            onClick={() => handleCloseModal('productDetail', setShowProductDetailModal, () => setDetailStoreStocks([]))}
          >
            <div
              className="modal"
              onClick={(e) => e.stopPropagation()}
              style={{
                backgroundColor: 'white',
                borderRadius: '16px',
                maxWidth: '900px',
                width: '95%',
                maxHeight: '90vh',
                overflow: 'auto',
                display: 'flex',
                flexDirection: 'row',
                gap: '24px',
                padding: '24px',
                position: 'relative',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
              }}
            >
              <button
                onClick={() => handleCloseModal('productDetail', setShowProductDetailModal, () => setDetailStoreStocks([]))}
                style={{
                  position: 'absolute',
                  top: '12px',
                  right: '12px',
                  background: '#f3f4f6',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <X size={20} />
              </button>

              {/* Sol: Büyük Görsel */}
              <div style={{ flex: '1', minWidth: '280px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <img
                  src={`${IMAGE_HOST}${detailItem.image}`}
                  alt={detailItem.name}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '400px',
                    objectFit: 'contain',
                    borderRadius: '12px',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
                  }}
                />
              </div>

              {/* Sağ: Ürün Bilgileri */}
              <div style={{ flex: '1', minWidth: '280px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <h2 style={{ margin: 0, fontSize: '1.4rem', color: '#1f2937', borderBottom: '2px solid #e5e7eb', paddingBottom: '12px' }}>
                  {detailItem.name}
                </h2>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  {/* Barkod */}
                  <div style={{ background: '#f9fafb', padding: '12px', borderRadius: '8px' }}>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '4px' }}>Barkod</div>
                    <div style={{ fontSize: '0.95rem', fontWeight: '600', color: '#1f2937' }}>
                      {detailItem.barcode || '-'}
                    </div>
                  </div>

                  {/* Kategori */}
                  <div style={{ background: '#f9fafb', padding: '12px', borderRadius: '8px' }}>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '4px' }}>Kategori</div>
                    <div style={{ fontSize: '0.95rem', fontWeight: '600', color: '#1f2937' }}>
                      {detailItem.category || '-'}
                    </div>
                  </div>

                  {/* Fiyat */}
                  <div style={{ background: '#f0fdf4', padding: '12px', borderRadius: '8px', gridColumn: '1 / -1' }}>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '4px' }}>Fiyat</div>
                    <div style={{ fontSize: '1.15rem', fontWeight: '700', color: '#16a34a' }}>
                      ₺{(convertToTRY(detailItem.price, detailItem.currency) || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                    </div>
                  </div>

                </div>

                {/* Altın Ürün Detayları */}
                {detailItem.isGoldProduct && (
                  <div style={{ background: 'linear-gradient(135deg, #fef3c7, #fde68a)', padding: '16px', borderRadius: '12px', marginTop: '8px' }}>
                    <h3 style={{ margin: '0 0 12px 0', fontSize: '1rem', color: '#92400e', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Medal size={18} /> Altın Bilgileri
                    </h3>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                      <div style={{ background: 'rgba(255,255,255,0.7)', padding: '10px', borderRadius: '8px' }}>
                        <div style={{ fontSize: '0.7rem', color: '#92400e', marginBottom: '2px' }}>Ayar</div>
                        <div style={{ fontSize: '1.05rem', fontWeight: '700', color: '#78350f' }}>
                          {detailItem.goldKarat}K
                        </div>
                      </div>
                      <div style={{ background: 'rgba(255,255,255,0.7)', padding: '10px', borderRadius: '8px' }}>
                        <div style={{ fontSize: '0.7rem', color: '#92400e', marginBottom: '2px' }}>Gramaj</div>
                        <div style={{ fontSize: '1.05rem', fontWeight: '700', color: '#78350f' }}>
                          {detailItem.weight} gr
                        </div>
                      </div>
                      <div style={{ background: 'rgba(255,255,255,0.7)', padding: '10px', borderRadius: '8px' }}>
                        <div style={{ fontSize: '0.7rem', color: '#92400e', marginBottom: '2px' }}>İşçilik</div>
                        <div style={{ fontSize: '1.05rem', fontWeight: '700', color: '#78350f' }}>
                          {detailItem.laborCost || 0}
                        </div>
                      </div>
                      <div style={{ background: 'rgba(255,255,255,0.7)', padding: '10px', borderRadius: '8px' }}>
                        <div style={{ fontSize: '0.7rem', color: '#92400e', marginBottom: '2px' }}>İşçilikli Gramaj</div>
                        <div style={{ fontSize: '1.05rem', fontWeight: '700', color: '#78350f' }}>
                          {(((karatMultipliers[detailItem.goldKarat] || 585) + (parseFloat(detailItem.laborCost) || 0)) * (parseFloat(detailItem.weight) || 0) / 1000).toFixed(3)} gr
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Stok Bilgisi */}
                <div style={{ marginTop: 'auto' }}>
                  <div style={{ fontSize: '0.85rem', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>Stok Bilgileri</div>
                  {(() => {
                    const currentStoreId = selectedStore?.id || user?.store_id;
                    const isGoldStore = currentStoreId === 1;
                    if (isGoldStore) {
                      return (
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <div style={{ flex: 1, background: '#f9fafb', padding: '10px', borderRadius: '8px', textAlign: 'center' }}>
                            <div style={{ fontSize: '0.7rem', color: '#6b7280', marginBottom: '4px' }}>Mağaza Stoğu</div>
                            <div style={{ fontSize: '1rem', fontWeight: '600', color: '#1f2937' }}>{detailItem.storeStock ?? '-'}</div>
                          </div>
                        </div>
                      );
                    }
                    return (
                      <>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
                          {detailStoreStocks.filter(s => [2, 3, 4].includes(s.store_id)).map(s => {
                            const isCurrent = s.store_id === currentStoreId;
                            return (
                              <div key={s.store_id} style={{
                                background: isCurrent ? '#eff6ff' : '#f9fafb',
                                border: isCurrent ? '1.5px solid #3b82f6' : '1px solid #e5e7eb',
                                padding: '10px',
                                borderRadius: '8px',
                                textAlign: 'center'
                              }}>
                                <div style={{ fontSize: '0.7rem', color: isCurrent ? '#2563eb' : '#6b7280', marginBottom: '4px' }}>
                                  {s.store_name}{isCurrent ? ' (Bu mağaza)' : ''}
                                </div>
                                <div style={{ fontSize: '1.05rem', fontWeight: '700', color: s.stock > 0 ? '#059669' : '#dc2626' }}>
                                  {s.stock}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Tüm Fişler Modal */}
      {!mobileApp && showAllSalesModal && (
        <div className="modal-overlay">
          <div className="modal extra-large all-sales-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Tüm Satış Fişleri</h2>
              <button className="close-btn" onClick={() => { setShowAllSalesModal(false); setAllSalesFilter('all'); setAllSalesSearch(''); setAllSalesDateFilter('all'); setAllSalesTypeFilter('all'); }}>✕</button>
            </div>
            <div className="modal-body" onScroll={(e) => {
              const { scrollTop, scrollHeight, clientHeight } = e.target;
              if (scrollHeight - scrollTop - clientHeight < 200 && hasMoreAllSales && !loadingMoreAllSales && !allSalesLoading) {
                loadMoreAllSales();
              }
            }}>
              {/* Arama ve Tarih Filtresi */}
              <div style={{ display: 'flex', gap: '8px', padding: '0 0 12px 0', flexWrap: 'wrap', alignItems: 'center' }}>
                <div style={{ flex: 1, minWidth: '200px', position: 'relative' }}>
                  <input
                    type="text"
                    placeholder="Fiş no, müşteri adı, telefon, personel ara..."
                    value={allSalesSearch}
                    onChange={(e) => setAllSalesSearch(e.target.value)}
                    style={{ width: '100%', padding: '10px 12px 10px 36px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', outline: 'none', background: '#f8fafc' }}
                  />
                  <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                  {allSalesSearch && (
                    <button onClick={() => setAllSalesSearch('')} style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: '16px' }}>✕</button>
                  )}
                </div>
                <select
                  value={allSalesTypeFilter}
                  onChange={(e) => setAllSalesTypeFilter(e.target.value)}
                  style={{ padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', background: allSalesTypeFilter !== 'all' ? '#eef2ff' : '#f8fafc', cursor: 'pointer', minWidth: '130px', fontWeight: allSalesTypeFilter !== 'all' ? 600 : 400, color: allSalesTypeFilter !== 'all' ? '#4f46e5' : undefined }}
                >
                  <option value="all">Tüm İşlemler</option>
                  <option value="sale">Satış Fişleri</option>
                  <option value="refund">İade Fişleri</option>
                  <option value="debt_payment">Tahsilat Fişleri</option>
                  <option value="repair">Tamirat Fişleri</option>
                  <option value="scrap_purchase">Hurda Alım</option>
                  <option value="expense">Masraflar</option>
                </select>
                <select
                  value={allSalesDateFilter}
                  onChange={(e) => setAllSalesDateFilter(e.target.value)}
                  style={{ padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', background: allSalesDateFilter !== 'all' ? '#f0fdf4' : '#f8fafc', cursor: 'pointer', minWidth: '130px', fontWeight: allSalesDateFilter !== 'all' ? 600 : 400, color: allSalesDateFilter !== 'all' ? '#16a34a' : undefined }}
                >
                  <option value="all">Tüm Tarihler</option>
                  <option value="today">Bugün</option>
                  <option value="yesterday">Dün</option>
                  <option value="week">Son 7 Gün</option>
                  <option value="month">Son 30 Gün</option>
                </select>
                {(allSalesSearch || allSalesDateFilter !== 'all' || allSalesTypeFilter !== 'all') && (
                  <button
                    onClick={() => { setAllSalesSearch(''); setAllSalesDateFilter('all'); setAllSalesTypeFilter('all'); }}
                    style={{ padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '12px', background: '#fee2e2', color: '#dc2626', cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap' }}
                  >
                    Filtreleri Temizle
                  </button>
                )}
              </div>

              {/* Mağaza Filtre Kartları - Sadece admin görebilir */}
              {user?.role === 'admin' && (
              <div className="store-filter-cards">
                <div
                  className={`store-filter-card all ${allSalesFilter === 'all' ? 'active' : ''}`}
                  onClick={() => { if (allSalesFilter !== 'all') { setAllSalesFilter('all'); } }}
                >
                  <div className="store-filter-icon">
                    <Store size={24} />
                  </div>
                  <div className="store-filter-info">
                    <span className="store-filter-name">Tüm Fişler</span>
                    <span className="store-filter-count">{allSalesTotalCounts.all} fiş</span>
                  </div>
                </div>
                <div
                  className={`store-filter-card gold ${allSalesFilter === '1' ? 'active' : ''}`}
                  onClick={() => { if (allSalesFilter !== '1') { setAllSalesFilter('1'); } }}
                >
                  <div className="store-filter-icon">
                    <Coins size={24} />
                  </div>
                  <div className="store-filter-info">
                    <span className="store-filter-name">Akik Gold Jewellery</span>
                    <span className="store-filter-count">{allSalesTotalCounts.store1} fiş</span>
                  </div>
                </div>
                <div
                  className={`store-filter-card silver ${allSalesFilter === '2' ? 'active' : ''}`}
                  onClick={() => { if (allSalesFilter !== '2') { setAllSalesFilter('2'); } }}
                >
                  <div className="store-filter-icon">
                    <Building2 size={24} />
                  </div>
                  <div className="store-filter-info">
                    <span className="store-filter-name">Akik Gümüş Osmangazi</span>
                    <span className="store-filter-count">{allSalesTotalCounts.store2} fiş</span>
                  </div>
                </div>
                <div
                  className={`store-filter-card silver ${allSalesFilter === '3' ? 'active' : ''}`}
                  onClick={() => { if (allSalesFilter !== '3') { setAllSalesFilter('3'); } }}
                >
                  <div className="store-filter-icon">
                    <Building2 size={24} />
                  </div>
                  <div className="store-filter-info">
                    <span className="store-filter-name">Akik Gümüş Yeşil</span>
                    <span className="store-filter-count">{allSalesTotalCounts.store3} fiş</span>
                  </div>
                </div>
              </div>
              )}

              {/* Satış Listesi */}
              <div className="all-sales-list">
                {allSalesLoading && allSales.length === 0 ? (
                  <div className="loading-spinner">Yükleniyor...</div>
                ) : allSales.length === 0 && !allSalesLoading ? (
                  <div className="no-data">Bu filtreye uygun satış bulunamadı.</div>
                ) : (
                  allSales.map(sale => {
                    const isExchangeRecord = sale.record_type === 'exchange';
                    const isRefundRecord = sale.record_type === 'refund';
                    const isDebtPaymentRecord = sale.record_type === 'debt_payment';
                    const isScrapPurchaseRecord = sale.record_type === 'scrap_purchase';
                    const isExpenseRecord = sale.record_type === 'expense';
                    const isRepairRecord = sale.record_type === 'repair_payment' || sale.record_type === 'repair_deposit';
                    const isExchangeOrRefund = isExchangeRecord || isRefundRecord;

                    return (
                      <div
                        key={`${sale.record_type}_${sale.id}`}
                        className={`all-sales-item ${isDebtPaymentRecord ? 'debt-payment-item' : ''} ${isScrapPurchaseRecord ? 'scrap-purchase-item' : ''} ${isExpenseRecord ? 'expense-item' : ''} ${isExchangeOrRefund ? 'exchange-refund-item' : ''} ${isRepairRecord ? 'debt-payment-item' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (isExpenseRecord) {
                            openExpenseDetailModal(sale);
                          } else if (isScrapPurchaseRecord) {
                            openScrapPurchaseDetailModal(sale);
                          } else if (isDebtPaymentRecord) {
                            openDebtPaymentDetailModal(sale);
                          } else if (isRepairRecord) {
                            setShowAllSalesModal(false);
                            navigate('/repairs', { state: { from: 'retail-sales', openRepairId: sale.repair_id } });
                          } else if (isExchangeOrRefund) {
                            openRefundDetailModal(sale);
                          } else {
                            openSaleDetailModal(sale);
                          }
                        }}
                        style={isExpenseRecord ? { borderLeft: '4px solid #dc2626', cursor: 'pointer' } : isScrapPurchaseRecord ? { borderLeft: '4px solid #f59e0b', cursor: 'pointer' } : (isDebtPaymentRecord || isRepairRecord) ? { borderLeft: '4px solid #059669', cursor: 'pointer' } : isExchangeRecord ? { borderLeft: '4px solid #ef4444', cursor: 'pointer' } : isRefundRecord ? { borderLeft: '4px solid #ef4444', cursor: 'pointer' } : { cursor: 'pointer' }}
                      >
                        <div className="all-sales-item-left">
                          <div className="all-sales-item-header">
                            <span className="all-sales-item-no">#{sale.receipt_number || sale.store_sale_number || sale.id}</span>
                            {isDebtPaymentRecord && (
                              <span style={{
                                backgroundColor: '#059669',
                                color: 'white',
                                padding: '2px 6px',
                                borderRadius: '4px',
                                fontSize: '10px',
                                fontWeight: 'bold',
                                marginLeft: '6px'
                              }}>
                                TAHSİLAT
                              </span>
                            )}
                            {isRepairRecord && (
                              <span style={{
                                backgroundColor: '#059669',
                                color: 'white',
                                padding: '2px 6px',
                                borderRadius: '4px',
                                fontSize: '10px',
                                fontWeight: 'bold',
                                marginLeft: '6px'
                              }}>
                                TAMİRAT
                              </span>
                            )}
                            {isScrapPurchaseRecord && (
                              <span style={{
                                backgroundColor: '#f59e0b',
                                color: 'white',
                                padding: '2px 6px',
                                borderRadius: '4px',
                                fontSize: '10px',
                                fontWeight: 'bold',
                                marginLeft: '6px'
                              }}>
                                ⚖️ HURDA {sale.metal_type === 'gold' ? 'ALTIN' : sale.metal_type === 'silver' ? 'GÜMÜŞ' : sale.metal_type?.includes(',') ? 'ALTIN/GÜMÜŞ' : 'ALTIN'}
                                {sale.item_count > 1 && ` (${sale.item_count})`}
                              </span>
                            )}
                            {isRefundRecord && (
                              <span style={{
                                backgroundColor: '#ef4444',
                                color: 'white',
                                padding: '2px 6px',
                                borderRadius: '4px',
                                fontSize: '10px',
                                fontWeight: 'bold',
                                marginLeft: '6px'
                              }}>
                                İADE
                              </span>
                            )}
                            {isExpenseRecord && (
                              <span style={{
                                backgroundColor: '#dc2626',
                                color: 'white',
                                padding: '2px 6px',
                                borderRadius: '4px',
                                fontSize: '10px',
                                fontWeight: 'bold',
                                marginLeft: '6px'
                              }}>
                                MASRAF
                              </span>
                            )}
                            {user?.role === 'admin' && sale.has_no_discount_item && !isExchangeOrRefund && !isDebtPaymentRecord && !isScrapPurchaseRecord && !isExpenseRecord && (
                              <span
                                title="İndirim yapılması kısıtlanmış ürün satışı yapıldı"
                                style={{
                                  backgroundColor: '#fee2e2',
                                  color: '#b91c1c',
                                  border: '1px solid #ef4444',
                                  padding: '2px 6px',
                                  borderRadius: '4px',
                                  fontSize: '10px',
                                  fontWeight: 600,
                                  marginLeft: '6px'
                                }}
                              >
                                ⚠ İndirim kısıtlı ürün
                              </span>
                            )}
                            <span className={`all-sales-item-store store-${sale.store_id}`}>
                              {sale.store_id === 1 ? 'Akik Gold Jewellery' :
                               sale.store_id === 2 ? 'Akik Gümüş Osmangazi' :
                               sale.store_id === 3 ? 'Akik Gümüş Yeşil' : 'Bilinmeyen'}
                            </span>
                          </div>
                          <div className="all-sales-item-details">
                            <span className="all-sales-item-customer">
                              <User size={14} />
                              {isExpenseRecord ? (sale.description || 'Masraf') : (sale.customer_name || 'Müşteri Yok')}
                            </span>
                            <span className="all-sales-item-date">
                              <Calendar size={14} />
                              {new Date(sale.created_at).toLocaleDateString('tr-TR')} {new Date(sale.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                            </span>
                            {sale.staff_name && (
                              <span style={{
                                fontSize: '11px',
                                color: '#6366f1',
                                backgroundColor: '#eef2ff',
                                padding: '1px 6px',
                                borderRadius: '4px',
                                fontWeight: '500'
                              }}>
                                {sale.staff_name}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="all-sales-item-right">
                          <span className="all-sales-item-total" style={isExpenseRecord ? { color: '#dc2626' } : isScrapPurchaseRecord ? { color: '#f59e0b' } : (isDebtPaymentRecord || isRepairRecord) ? { color: '#059669' } : isExchangeOrRefund ? { color: Number(sale.total) < 0 ? '#16a34a' : '#ef4444' } : {}}>
                            {isExpenseRecord
                              ? `-₺${Number(sale.total).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}`
                              : isScrapPurchaseRecord
                                ? `₺${Number(sale.total).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}`
                                : isDebtPaymentRecord
                                  ? (sale.debt_type === 'GRAM' || sale.debt_type === 'GUMUS' ? `${Number(sale.total).toLocaleString('tr-TR')} gr` : `₺${Number(sale.total).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}`)
                                  : isRepairRecord
                                  ? `₺${Number(sale.total).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}`
                                  : isExchangeOrRefund
                                    ? `${Number(sale.total) < 0 ? '-' : '+'}₺${Math.abs(Number(sale.total)).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}`
                                    : `₺${getSaleDisplayAmount(sale).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}`
                            }
                          </span>
                          {isScrapPurchaseRecord && (
                            <span style={{ fontSize: '11px', color: '#92400e' }}>
                              {(Number(sale.gross_weight || 0) - (Number(sale.waste_gram) > 0 ? Number(sale.waste_gram) : Number(sale.gross_weight || 0) * Number(sale.waste_percent || 0) / 100)).toLocaleString('tr-TR')}gr net
                            </span>
                          )}
                          {isExpenseRecord && sale.category && (
                            <span style={{ fontSize: '11px', color: '#991b1b' }}>
                              {sale.category}
                            </span>
                          )}
                          <span className="all-sales-item-method">{formatPaymentMethod(sale)}</span>
                        </div>
                      </div>
                    );
                  })
                )}
                {/* Infinite scroll: loading göstergesi */}
                {loadingMoreAllSales && (
                  <div className="loading-spinner" style={{ padding: '16px', textAlign: 'center' }}>Daha fazla yükleniyor...</div>
                )}
                {!loadingMoreAllSales && hasMoreAllSales && allSales.length > 0 && (
                  <div style={{ padding: '12px', textAlign: 'center' }}>
                    <button
                      onClick={loadMoreAllSales}
                      style={{ padding: '8px 24px', borderRadius: '8px', border: '1px solid #e2e8f0', background: '#f8fafc', cursor: 'pointer', fontSize: '13px', color: '#64748b' }}
                    >
                      Daha fazla yükle
                    </button>
                  </div>
                )}
                {!hasMoreAllSales && allSales.length > 0 && (
                  <div style={{ padding: '12px', textAlign: 'center', color: '#94a3b8', fontSize: '13px' }}>Tüm fişler yüklendi ({allSales.length} fiş)</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Tab Navigation */}
      {!mobileApp && (
      <nav className="mobile-tab-nav">
        <div className="mobile-tab-nav-inner">
          <button
            className={`mobile-tab-btn ${mobileActiveTab === 'cart' ? 'active' : ''}`}
            onClick={() => setMobileActiveTab('cart')}
          >
            <ShoppingCart size={24} />
            <span>Sepet</span>
            {cart.length > 0 && (
              <span className="badge">{cart.reduce((sum, item) => sum + item.quantity, 0)}</span>
            )}
          </button>
          <button
            className={`mobile-tab-btn ${mobileActiveTab === 'checkout' ? 'active' : ''}`}
            onClick={() => setMobileActiveTab('checkout')}
          >
            <Receipt size={24} />
            <span>Ödeme</span>
          </button>
          <button
            className={`mobile-tab-btn ${mobileActiveTab === 'actions' ? 'active' : ''}`}
            onClick={() => setMobileActiveTab('actions')}
          >
            <MoreHorizontal size={24} />
            <span>İşlemler</span>
          </button>
        </div>
      </nav>
      )}

      {/* KVKK Aydınlatma Metni Modal */}
      {showKvkkModal && (
        <div className="modal-overlay">
          <div className="modal kvkk-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>KVKK Aydınlatma Metni</h2>
              <button className="close-btn" onClick={() => setShowKvkkModal(false)}>✕</button>
            </div>
            <div className="modal-body kvkk-content">
              <h3>KİŞİSEL VERİLERİN KORUNMASI KANUNU KAPSAMINDA AYDINLATMA METNİ</h3>

              <p><strong>Veri Sorumlusu:</strong> AKIK Kuyumculuk</p>

              <h4>1. Kişisel Verilerin İşlenme Amacı</h4>
              <p>Kişisel verileriniz aşağıdaki amaçlarla işlenmektedir:</p>
              <ul>
                <li>Müşteri ilişkileri yönetimi ve iletişim faaliyetlerinin yürütülmesi</li>
                <li>Satış ve satış sonrası hizmetlerin sunulması</li>
                <li>Kampanya, promosyon ve duyuruların iletilmesi (onay verilmesi halinde)</li>
                <li>Yasal yükümlülüklerin yerine getirilmesi</li>
                <li>Fatura ve ödeme işlemlerinin gerçekleştirilmesi</li>
              </ul>

              <h4>2. İşlenen Kişisel Veriler</h4>
              <ul>
                <li>Kimlik bilgileri (ad, soyad, T.C. kimlik numarası)</li>
                <li>İletişim bilgileri (telefon numarası, e-posta adresi, adres)</li>
                <li>Müşteri işlem bilgileri (satın alma geçmişi, ödeme bilgileri)</li>
                <li>Doğum tarihi (özel gün kutlamaları için)</li>
              </ul>

              <h4>3. Kişisel Verilerin Aktarılması</h4>
              <p>Kişisel verileriniz; yasal zorunluluklar kapsamında yetkili kamu kurum ve kuruluşlarına,
              ödeme işlemleri için iş birliği yapılan finans kuruluşlarına ve SMS/e-posta gönderimi için
              hizmet alınan tedarikçilere aktarılabilmektedir.</p>

              <h4>4. Kişisel Veri Toplamanın Yöntemi ve Hukuki Sebebi</h4>
              <p>Kişisel verileriniz, mağazamızda gerçekleştirilen satış işlemleri sırasında,
              sözlü veya yazılı olarak tarafınızdan alınmaktadır. Bu veriler; sözleşmenin kurulması
              ve ifası, yasal yükümlülüklerin yerine getirilmesi ve açık rızanız kapsamında işlenmektedir.</p>

              <h4>5. Kişisel Veri Sahibinin Hakları</h4>
              <p>KVKK'nın 11. maddesi uyarınca aşağıdaki haklara sahipsiniz:</p>
              <ul>
                <li>Kişisel verilerinizin işlenip işlenmediğini öğrenme</li>
                <li>İşlenmişse buna ilişkin bilgi talep etme</li>
                <li>İşlenme amacını ve amaca uygun kullanılıp kullanılmadığını öğrenme</li>
                <li>Yurt içinde veya yurt dışında aktarıldığı üçüncü kişileri bilme</li>
                <li>Eksik veya yanlış işlenmişse düzeltilmesini isteme</li>
                <li>KVKK'nın 7. maddesinde öngörülen şartlar çerçevesinde silinmesini veya yok edilmesini isteme</li>
                <li>İşlenen verilerin münhasıran otomatik sistemler vasıtasıyla analiz edilmesi suretiyle
                aleyhinize bir sonucun ortaya çıkmasına itiraz etme</li>
                <li>Kanuna aykırı olarak işlenmesi sebebiyle zarara uğramanız halinde zararın giderilmesini talep etme</li>
              </ul>

              <h4>6. İletişim</h4>
              <p>Haklarınızı kullanmak için mağazamıza yazılı olarak başvurabilirsiniz.</p>

              <p className="kvkk-date"><em>Son güncelleme: {new Date().toLocaleDateString('tr-TR')}</em></p>
            </div>
            <div className="modal-footer">
              <button className="btn-save" onClick={() => setShowKvkkModal(false)}>Kapat</button>
            </div>
          </div>
        </div>
      )}

      {/* Fiş Açıklaması Modal */}
      {showReceiptNoteInput && (
        <div className="modal-overlay" style={{ zIndex: 9999 }} onClick={() => setShowReceiptNoteInput(false)}>
          <div className="modal" style={{ maxWidth: '520px', width: '90%' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2><FileText size={18} style={{ marginRight: '8px' }} /> Fiş Açıklaması</h2>
              <button className="close-btn" onClick={() => setShowReceiptNoteInput(false)}>✕</button>
            </div>
            <div className="modal-body" style={{ padding: '20px' }}>
              <textarea
                placeholder="Fiş açıklaması yazın..."
                value={receiptNote}
                onChange={(e) => setReceiptNote(e.target.value)}
                autoFocus
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: '8px',
                  border: '1px solid #cbd5e1',
                  fontSize: '14px',
                  outline: 'none',
                  resize: 'vertical',
                  minHeight: '120px',
                  fontFamily: 'inherit',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            <div className="modal-footer" style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              {receiptNote && (
                <button className="btn-cancel" onClick={() => { setReceiptNote(''); }}>
                  Açıklamayı Sil
                </button>
              )}
              <button className="btn-save" onClick={() => setShowReceiptNoteInput(false)}>
                Tamam
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RetailSales;
